import StatusPill, { STATUS_PILL_W } from "./components/StatusPill"; // 117 §2a, 136 §1
import BadgeHoverArea from "./components/BadgeHoverArea"; // 117 §3
import TimeField from "./components/TimeField"; // 117 §5
import { AttendeesPickerSection } from "./components/AttendeesPickerSection"; // B27, brief 84
// 168B §2 — `React` imported for `react/react-in-jsx-scope`, which is 622 of
// this file's eslint warnings, one per JSX element: the filter bar's two new
// selects would otherwise push the file over its ratchet baseline. The JSX
// transform stays automatic ("jsx": "react-jsx"), so nothing emitted moves.
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, lazy, Suspense, type CSSProperties, type Dispatch } from "react";
import { createPortal, flushSync } from "react-dom";
import { getFallbackQuote } from "./data/quotes";
import { resolveScheduleAxis } from "./lib/scheduleAxis";
import { resolveCalendarDisplay } from "./lib/calendarDisplay"; // 133 §4
// 132 §4/§6/§7 — the firm's legal holidays, resolved once for every surface
// that draws a day. PRESENTATIONAL ONLY (the module's own fence): nothing
// downstream may read it to decide a term, a deadline or a date shift.
import {
  holidayDefsFrom,
  makeHolidayLookup,
  observedCountries,
} from "./lib/legalHolidays";
import { useTranslation, tImperative } from "./i18n";
import {
  MessageCircle,
  MessagesSquare,
  Phone,
  AlertTriangle,
  Gavel,
  Info,
  Pencil,
  Trash2,
  Lock,
  Unlock,
  Play,
  Pause,
  Square,
  Search,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Pin,
  PinOff,
  Copy,
  X,
  Plus,
  Clock,
  MapPin,
  Send,
  Undo2,
  Archive,
  TimerOff,
  FileText,
  Share2,
  Mail,
  Video,
  Calendar,
  Users,
  Download,
  Upload,
  ChevronLeft,
  List,
  Grid3X3,
  Grid2x2,
  SquareKanban, // 190 §C — the Bento button beside the matrix
  CalendarClock,
  Bell,
  Volume2,
  VolumeX,
  ExternalLink as ExtLink2,
  LayoutDashboard,
  LayoutTemplate,
  Briefcase,
  LibraryBig,
  BookUser,
  Book,
  Bus,
  ClipboardClock,
  FileSearch,
  UserPlus,
  UserX,
  Scale,
  UserMinus,
  GraduationCap,
  Handshake,
  UserCircle,
  UserRoundPlus,
  UserPen,
  Building2,
  Landmark,
  UsersRound,
  Banknote,
  HandCoins,
  ReceiptEuro,
  BarChart3,
  Settings,
  Menu,
  Palette,
  Sun,
  Moon,
  MessageSquareCheck,
  SearchCheck,
  LogOut,
  Folder,
  Link2,
  Globe,
  SquareArrowOutUpRight,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  ServerCog,
} from "lucide-react";
import "./App.css";
import {
  noteFirmCurrency,
  I,
  SK,
  ld,
  sv,
  DOC_STATUSES,
  FT_INFO,
  detFT,
  ctc,
  fc,
  fq,
  fmin,
  fmtElapsed,
  evDayClr,
  fd,
  resolveEventLocation,
  pname,
  coIni,
  nid,
  pm,
  DEFAULT_EXT_TYPES,
  TASK_STATUSES,
  REMINDER_OPTS,
  reminderDate,
  itemLevelMap,
  provisionalLevel,
  queueScrollToEv,
  queueScrollToTk,
  isNotifRead,
  type NotifRow,
  getInternalRate,
  canEditInternalRates,
  isFeeEarnerCollab,
  billsAtZeroCollab,
  eisenAuto,
  eisenQuadrant,
  EISEN_QUADRANTS,
  normalizeWeeklySchedule,
  weeklyHours,
  weeklyBreakdown,
  SCHED_DAYS,
  SCHED_START,
  SCHED_SLOTS,
  isWeekendDow,
  slotToTime,
  setFirmNameCache,
  dcatLabel,
  dcatTag,
} from "./utils";
import { EisenBadge, EISEN_LABELS, eisenLabel } from "./EisenBadge";
import CaViewComponent from "./views/CaseView";
import ClientMailTemplateModal from "./components/modals/ClientMailTemplateModal"; // 189 §H — one-to-one, with a template
import CollectiveCaseView from "./views/CollectiveCaseView";
import DashComponent from "./views/Dashboard";
import AgViewComponent from "./views/AgendaView";
import SettingsViewComponent from "./views/SettingsView";
import GChatViewComponent, {
  SidebarGenChat,
  SidebarCaseChat,
} from "./views/ChatView";
import LibraryView from "./views/LibraryView";
import CompactColumn from "./views/CompactColumn";
import ExternalView from "./views/ExternalView";
import ScheduleGrid from "./views/ScheduleGrid";
import ScheduleView from "./views/ScheduleView";
import PlacesView from "./views/PlacesView";
import InvoiceView from "./views/InvoiceView";
import ReportsView from "./views/ReportsView";
import ArchiveView from "./views/ArchiveView";
import FleetView from "./components/FleetView"; // BRIEF 162 — the operator cockpit
import AssigneePicker from "./components/AssigneePicker";
import FieldSelect from "./components/FieldSelect";
import MenuSelect from "./components/MenuSelect"; // 154B §2
import { makeFieldRows } from "./lib/fieldRows"; // A3, brief 65
import { makeTimerStops, dropTimerRow } from "./lib/timerStops"; // A5, brief 65
import { timerSubject, timerKey, sameTimer } from "./lib/timerSubject"; // brief 91 §3a, 116 §3
import { firmWebsiteHref } from "./lib/firmWebsite"; // brief 92 §2
import {
  stackCut, // brief 94 §1b
  BADGE_SIZE, // 116 §2 — the stack's own geometry, named
  BADGE_OVERLAP,
  BADGE_AREA_W,
  BADGE_SELF_RING, // 118 §1 — the ring, once
  viewerInRow,
  badgeSections,
  type NameableCo,
  assigneeTip,
} from "./lib/badgeStack";
// 119 §5 — the three named groups, and the ONE population builder the modal
// overview and the badge hover panel both read.
import {
  assignedFromRows,
  attendeeKey,
  eventPeople,
  externalInitials,
  groupPeopleByMatter,
  meetingRoster,
  mergeAttendeeRows,
  teamIdSet,
  PEOPLE_GROUP_KEY,
  type AttendeeRow,
  type MeetingAttendee,
  type OutlookAttendee,
  type PeopleGroupKey,
} from "./lib/meetingAttendees";
// 119 §1c — the matter's cast, from the one producer (§1c's pre-step).
import { matterCast } from "./lib/matterCast";
import { withNewCounselLink, newCcElement } from "./lib/counselLinks"; // 214 §2 — a counsel's parties on the new link
import type { TaxList } from "./lib/taxonomyTypes"; // 214 §4 — the vocabularies typed at the hook
import type { FieldSelectOption } from "./components/FieldSelect";
import { meetingSeed, type SeedableEvent } from "./lib/meetingSeed"; // A6, brief 65 + 119 §1c
import { clientsFamilyKey, resolveMatter, type MatterRow } from "./lib/matterLabel"; // 123 §4, 191 §C (117 §8's matterEventTitle reader here withdrawn by 191 D.0)
import { swapSimulationToken } from "./lib/simulationSwap"; // A7, brief 65
import DemoBanner from "./components/DemoBanner"; // 77 §4a — the lenient floor says so
import { pickRingTargets } from "./lib/ringTargets"; // A17, brief 65
import { isUnreadMention } from "./lib/mentionFilter"; // A21, brief 65
// 75 §4 — the ONE definition of choosable/archived/active; see that file.
import { choosableCo, isActiveCo, isArchivedCo, soleArchived } from "./lib/collaboratorState";
// 57a-i / 67 §3 — the ONE definition of "this task is still open".
import { openStatusCodes, pickableStatusCodes, taskIsOpen } from "./lib/taskCounters";
import { normFormDates } from "./lib/formDates"; // A13, brief 65
import {
  mergeSavedRow,
  mergeAnswerInto,
  appendSavedRow,
  swapCreatedRow,
  toastInjectedSubtasks,
  folderUncheckedWords,
} from "./lib/savedRows"; // A9, brief 65 · swapCreatedRow 207 §1.3 · mergeAnswerInto 207B §5 · folderUncheckedWords 212 §18
import { createSaveQueue } from "./lib/saveQueue.mjs"; // 207B §5 — one save in flight, the latest waiting
import type { SaveQueue } from "./lib/saveQueue.mjs";
import { firmSettingsSaves, firmSettingsTyped } from "./lib/firmSettingsSaves"; // 207B §5 — the firm settings' one queue
import { tempId } from "./lib/tempId"; // 207 §1.2 — a temporary id is never a server id
import { paneOnArrival, type PaneScreen } from "./lib/paneScroll"; // 207C §4 — a section opens at its top
import { storedClientLanguage } from "./lib/docLang.mjs"; // 207C §6 — a client's language as stored
import CallsPanel from "./components/CallsPanel";
import { startRinger, stopRinger } from "./lib/ringer";
// Phase 39 — in-app WebRTC media layer for internal calls.
import { createCallSession, acquireLocalMedia, fetchIceServers } from "./lib/webrtcCall";
// 72 — the share leg's shapes, declared where the sessions are (both this file
// and CallWindow read them, so neither may own them).
import type { ShareLeg, ShareView } from "./lib/webrtcCall";
import { armCallPush, callPushHeld, primeCallPush } from "./lib/webPush";
import { bellToMac } from "./lib/bellToMac"; // 208 §15 — the bell tells the Mac what arrives
import { callPerfMark } from "./lib/callPerf";
import { loadXlsx, onUse } from "./lib/loadOnUse.mjs"; // 208 §1 — the Excel library loads the first time a sheet is written; the parts below, when used
import { xlsxRow } from "./lib/xlsxCells.mjs";
import ProFormaModal from "./components/modals/ProFormaModal";
import JurisdictionImportModal from "./components/modals/JurisdictionImportModal";
import { buildComposeUrl, openCompose } from "./lib/outlookCompose";
// 168B §2b — the Client-status filter offers the five LINK values, in lifecycle
// order, with the labels the roster's own picker resolves.
import { KNOWN_STATUSES } from "./lib/collectiveSegments.mjs";
import { linkStatusLabel } from "./lib/linkStatus";
import {
  SaveButton,
  CancelButton,
  ModalLayout,
  LockButton,
  AddButton,
  InitialsBadge,
  ArchivedHoleBadge,
  ColleagueCheckGrid,
  ScrollHint,
  useScrollHint,
  InfoHint,
  OPEN_MODE_GLOW, OPEN_MODE_ICON, // 190 round 6 [AA] — the lock's glow on the two header icons
  LoadedOnUse, // 208 §1
  ListNotLoaded, // 210 §1
} from "./components/SharedUI";
import { ContentFooterSlot } from "./components/CountersStrip"; // 190 round 8 [AK] — the page's floor
import CddClientSection from "./views/CddClientSection";

/* 208 §1 — WHAT IS NEEDED ONLY WHEN USED, LOADS WHEN USED (src/lib/loadOnUse.mjs):
   each module below is reached only through its loader, so it leaves the main
   chunk and arrives the first time it is used. */
const loadCampaigns = onUse(() => import("./views/CampaignsView")); // 189 §H — newsletters over a segment; when the section opens
/* Phase 39 — the call window. CENSUS (208 §1): an incoming call is heard HERE —
   the live stream's `call` events feed `setIncomingCalls`, the ring card and
   the ringer live in this file and CallsPanel — and the window mounts only
   once a call has a session (`callWin && callView`); nothing in it runs before
   a call arrives. So it loads on use, and a ring starts its load (the ringer's
   effect below), so an answer finds it there. */
const loadCallWindow = onUse(() => import("./components/CallWindow"));

/* 151E §2(b) — ONE copy of the text-size → zoom steps (M+3.6 C14; the apply()
   effect consumes it below). POPOUT_ZOOM pins the pop-out COLUMN at the
   xlarge step regardless of the user's main-app text size: the pop-out's
   documentElement is its own root that the style mirror WRITES (a copied
   string, not a shared node — measured, which is what the brief's "if it can
   be decoupled" asked), so it can carry its own zoom. `_mirrorSrc` caches the
   mirrored source string per document so the cheap change-detection survives
   the zoom override. */
const TEXT_SIZE_ZOOM: Record<string, number> = {
  small: 12 / 13,
  medium: 1,
  large: 14 / 13,
  xlarge: 16 / 13,
};
/* 204 Part Three §3 [RULED — Laurent: "keep the events/tasks' current size in
   lists and timer (purpose is to be easy to click) but make the titles —
   TIMER, TASKS DUE TODAY, TODAY'S EVENTS — follow the current chosen app
   size"]. THE PIN STAYS: the pop-out's rows and its timer render at this
   xlarge step whatever the main app's text size — DELIBERATELY, by that
   ruling; a later reader who finds the pop-out "inconsistent" with the app
   is reading the ruling, not a defect. Only the three section headings
   (`.colSecH`) follow the app's size, through `--col-title-scale` (the
   mirror below writes appZoom / POPOUT_ZOOM on the pop-out's root, so a
   heading under the pinned zoom comes out at the app's own size). */
const POPOUT_ZOOM = TEXT_SIZE_ZOOM.xlarge;
/* 186 A.3a — the popped-out case chat's matter key: a case id, or "cc:N" for a
   collective case (the sidebar rows' own `did` spelling), null when no matter is
   open. */
type ChatPopDid = number | string | null;
const _mirrorSrc = new WeakMap<Document, string>();
/* 206D §5.1 — EVERY WINDOW THE APP OPENS AS A POP-OUT, IN ONE LIST. The four
   surfaces — the compact column (`lx-col-root`), the call (`lx-call-root`), the
   general chat (`lexcrm_gchat`) and the case chat (`lexcrm_cchat`) — all open
   through ONE door, App's `_openPopOutWindow`, and that door writes every window
   it hands out here; the page's unload (App, the `pagehide`/`beforeunload`
   effect) closes whatever is in it. A fifth pop-out is closed on unload without
   anyone remembering to add it — which is how 186's two were forgotten. A
   window the user closed stays in the set until the next open prunes it;
   closing it again at unload is a no-op. */
const POP_OUT_WINDOWS = new Set<Window>();
import SubtaskChecklist from "./components/SubtaskChecklist";
import SelfTick from "./components/SelfTick";
import Combobox from "./components/Combobox";
import { stageAutofill } from "./components/StageLinkField"; // 109 §7d
import type { TaxEntry as StageTaxEntry } from "./components/StageLinkField";

import MultiSelect from "./components/MultiSelect";
import {
  useConfirmAndUndo,
  ConfirmDeleteDialog,
  ConfirmProvider,
  UndoToast,
} from "./components/useConfirmAndUndo";
import LoginView from "./views/LoginView";
import { useTaxonomyList, useTaxonomy } from "./hooks/useTaxonomy";
import { customFieldColumns } from "./lib/customFieldColumns"; // 189 §B
import { pickEntryLabel } from "./utils/taxonomyMap"; // 132 §4
import {
  eisenDefaultsFromEntries,
  eventLocationsFromEntries,
  firstMeetingTypeCode,
  longestTaxonomyCode,
  meetingDefaultModes,
  meetingTypeCodes,
} from "./utils/taxonomyMap";
// BRIEF 123 §3 — the two facts that replace `EV_MEETING_KIND` (see its epitaph
// where the map stood).
// BRIEF 124 §1 — `meetingModeFor` REPLACES every `meetingModeOf` call in this
// file. The old one answers "what mode does this row hold", which reads an
// unanswered row as Physical; the new one asks the TYPE before falling back to
// it. `meetingModeOf` is still exported and still right for a caller that has
// no taxonomy in hand — this file always does.
import {
  DEFAULT_MEETING_MODE,
  eventSaveWords,
  isMeetingType,
  meetingModeFor,
  type MeetingMode,
} from "./lib/meetingMode";
// P20d / ruled 2026-07-25 — the case modal pre-fills from the firm's own ref
// pattern, through the SAME pure grammar the server allocates with.
import { nextRefFrom } from "./lib/caseRefPattern.mjs";
// 137 §4(a) — the .ics composer, extracted so the server can attach the SAME
// bytes to the Outlook draft it composes. `downloadIcs` below is the
// browser-only half and stays here.
import { evToVevent, icsCalendar } from "./lib/icsEvent.mjs";
// BRIEF 05 STEPS 5 + 6 (via brief 124 §5) — the drop layer and the chooser.
import EmailDropLayer, { type EmailDropDescriptor } from "./components/EmailDropLayer";
import { type DropFileVerdict } from "./lib/dropFileKind"; // 129 §1
import EmailDropChooser, { type DocumentDropChoice } from "./components/EmailDropChooser";
import { openFileDestination } from "./components/FileDestinationModal"; // 216 §6 [RULED 75] — the one window a dropped document is filed from
import NotifRowView from "./components/NotifRowView"; // 125 §6
import NotifGroupedList from "./components/NotifGroupedList"; // 128 §4
import { firedPlace, reminderPlace, eventPlace, standingPlace, bellRows, bellNewCount, bellIds, taskIdOf, isLateTaskReminder, joinUrlOf } from "./lib/bellPlace"; // 209 §26; 213B §10 / §6
import { appScreen, rightEdgeLeft, chatLeft, popOutLeftOf } from "./lib/popOutPlace"; // 213B §9 [RULED 42] — a pop-out on the app's own screen
// BRIEF 124 §4 — the firm's own address, replacing a hardcoded WATT literal.
import { firmDefaultLocation } from "./lib/firmLocation";
import { firmCountryCode } from "./lib/firmAddress.mjs"; // 207 §13 — a new client's or party's country
import {
  usePreferences,
  primePreferences,
  seedPreferences,
} from "./hooks/usePreferences";
import {
  useRoleLabels,
  primeRoleLabels,
  roleLabel,
  roleDefaultRatio,
} from "./hooks/useRoleLabels";
import { deriveThemeVars, isDarkTheme, dayThemeVarsOf, deriveDocumentPalette } from "./lib/themeDerivation"; // 203-glance §C.1 — the DAY theme's vars for the two exporting views; 204 §A.1 — and the documents' brand, emitted on the root as `--firm-brand`
import {
  getTaskStatusMeta,
  getInvoicePaymentStatusMeta,
  getWorkLocationMeta,
  workLocationCycle,
  resolveTaxonomyLabel,
  resolveTemplateLabel,
  resolveLongLabel,
  resolveTaxonomyEntry,
} from "./lib/taxonomyHelpers";
import { iconByName } from "./lib/lucideRegistry";
import {
  primePermissions,
  refetchPermissions,
  __resetPermissionsCache,
  usePermissions,
  markSwapping,
} from "./hooks/usePermissions";
import { SURFACE_SECTION_KEY } from "./hooks/sectionMeta";
import { ingestLegacyPrefs } from "./lib/ingestLegacyPrefs";
import { presentedIdentity } from "./lib/presentedIdentity";
// Hotfix-B (Part A) — pure duplicate analysis for the Database cleanup modal.
import { analyzeOrphanClients } from "./lib/orphanDedup.mjs";
// Phase 43 — COI/duplicate name matching (accent+space normalized, item 5) and
// pure per-client matter computation for the Clients filter bar (item 1) + the
// record enrichment (item 3, via ClientCasesCard).
import {
  findNameMatches,
} from "./lib/coiMatch.mjs";
import {
  clientMatterSummary,
  clientRegularCases,
  clientCollectiveCases,
  matterAmountDue,
} from "./lib/clientMatters.mjs";
import ClientCasesCard from "./views/ClientCasesCard";
import NameAdvisor from "./components/NameAdvisor";
import {
  api,
  getAuthUser,
  logout as apiLogout,
  getClients,
  createClient,
  updateClient,
  deleteClient,
  archiveClient,
  unarchiveClient,
  backfillClientFolders,
  getCasesWithLatestInvoice,
  getCollectiveCasesWithLatestInvoice,
  getCaseWithLatestInvoice,
  getCollectiveCaseWithLatestInvoice,
  createCase,
  updateCase,
  deleteCase,
  archiveCase,
  unarchiveCase,
  purgeCase,
  getArchivedCases,
  createCollectiveCase,
  updateCollectiveCase,
  deleteCollectiveCase,
  archiveCollectiveCase,
  unarchiveCollectiveCase,
  purgeCollectiveCase,
  getArchivedCollectiveCases,
  getOpponents,
  createOpponent,
  updateOpponent,
  deleteOpponent,
  getOpponentLawyers,
  createOpponentLawyer,
  updateOpponentLawyer,
  deleteOpponentLawyer,
  getExperts,
  createExpert,
  updateExpert,
  deleteExpert,
  getJurisdictions,
  createJurisdiction,
  updateJurisdiction,
  deleteJurisdiction,
  bulkImportJurisdictions,
  getCollaborators,
  createCollaborator,
  updateCollaborator,
  deleteCollaborator,
  getTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  restoreTimeEntry, // 152 §3 — the single-row undo's other half
  getAgenda,
  createAgendaEvent,
  updateAgendaEvent,
  deleteAgendaEvent,
  archiveAgendaEvent,
  unarchiveAgendaEvent,
  purgeAgendaEvent,
  getArchivedAgenda,
  getTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  archiveTask,
  unarchiveTask,
  purgeTask,
  autoOngoingTask,
  getArchivedTasks,
  getInvoices,
  getBooks,
  createBook,
  updateBook,
  deleteBook,
  getContactPersons,
  createContactPerson,
  updateContactPerson,
  deleteContactPerson,
  getOpponentContacts,
  createOpponentContact,
  updateOpponentContact,
  deleteOpponentContact,
  getMediators,
  createMediator,
  updateMediator,
  deleteMediator,
  getArbiters,
  createArbiter,
  updateArbiter,
  deleteArbiter,
  getAdditionalOpponents,
  createAdditionalOpponent,
  updateAdditionalOpponent,
  deleteAdditionalOpponent,
  getAllChatMessages,
  createChatMessage,
  updateChatMessage,
  deleteChatMessage,
  getChatReadReceipts,
  upsertChatReadReceipt,
  getFirmSettings,
  updateFirmSettings,
  createSubtask,
  updateSubtask,
  createTaskDoc,
  // C.2 — the case/CC financials engine, relocated server-side.
  computeCaseFinancials,
  // 81 §7 — the previous-login line, read once per session.
  getAuthAttemptSummary,
  // 111 §5b — the item-link store's client. 112 §7 adds the single-link
  // delete (the manager's ×).
  createItemLink,
  deleteItemLink,
  unlinkItemLinks,
  // BRIEF 05 STEP 4/5 (via brief 124 §5) — the two resolve calls.
  resolveDroppedEmailFile,
  resolveDroppedEmailById,
  // 129 §3 — the drop files a document through 127 §1's route.
  uploadMatterFile,
  // 184 B.4 — the dropped e-mail's `.eml`, filed under the created task/event.
  postEmlDoc,
} from "./api";
import type { AuthAttemptSummary, DropEml, EmlDocRow, ItemLink, MatterFileConflict, MatterFileUpload, MatterKind } from "./api";
import type { TFn } from "./lib/taxonomyTypes";
/* 184 A.1 — a matter id as a modal form holds it (a number, a numeric string,
   `""` for a cleared picker, null/undefined) → the column's value: an Int or
   null. `""` is what both pickers write for the other kind, and `""` is not a
   value an `Int?` column takes (measured: a 500 on the PUT). */
function matterIdOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}
/* 184 B.4 — the drop's transient key, removed before a row or a request body
   is built from a form (the `_timerHold` class). */
function withoutDropEml<T extends { _dropEml?: unknown }>(o: T): T {
  const rest = { ...o };
  delete rest._dropEml;
  return rest;
}
/* 184 B.4 — a task or event row as the `.eml` filing reads it back: the id and
   its document list. */
interface DocParentRow { id?: number; docs?: unknown[]; [k: string]: unknown }
import MatterFileConflictDialog, { type MatterFileConflictState } from "./components/MatterFileConflictDialog"; // 129 §3
/* 129 §5 — LAZY, exactly as `CaseDocumentsSection.tsx:62` mounts it. A static
   import here would pull the whole billing browser into the main chunk and undo
   the split that file already paid for; this is the second mount of one
   component, not a second copy of it. */
import type { DocumentBillingStart } from "./components/DocumentBillingModal";
const DocumentBillingModal = lazy(() => import("./components/DocumentBillingModal"));
/* 211 §12 — the clients' subfolder check: the propagation window's `fill`, loaded on use */
const FolderPropagationModal = lazy(() => import("./components/FolderPropagationModal"));
import SignInLine from "./components/SignInLine";
import DayQuote from "./components/DayQuote"; // 126 §3
import { emailDocUrl } from "./lib/emailLink"; // 126 §9
import { m365Connected, m365ConnectedNow } from "./lib/m365Status"; // 126 §7/§9
import { resolveSubtaskText } from "./lib/subtaskHelpers";
import { redMatterFin, redEntryFin, redCoFin, redRoleRows,
  redFinInv,
} from "./lib/financialsClient";
import { startLiveStream } from "./lib/liveStream";
import { CT, RAD, FS, Z, POPUP_PAD } from "./lib/tokens";
import { askList, loadLists, LIST_ASKS, ListLoadContext } from "./lib/listLoad"; // 210 §1 — a list that could not load says so
import type { ListAnswer, ListFailure, ListLoadState, ListSource } from "./lib/listLoad";
import { NAV_COUNT_LISTS, SECTION_LISTS } from "./lib/sectionLists"; // 210 §1 — which lists a row counts, a section shows
import { PaneOpenContext, isAbovePane, useAbovePane } from "./lib/paneStacking"; // 204 §1 — a modal over an open pane; 205 §5 — and it owns the Escape; 206 §8.4 — the timer dropdown joins
import { normaliseAppFont, appFontStack } from "./lib/appFonts";
import { _buildTplItems, _reconcileTpl } from "./lib/templatePreview";
import { _LBL_KEYS } from "./lib/modalLabelKeys";
import {
  makeLabelTranslators,
  makeModalFields,
} from "./components/modalFields";
import QuickAddModal from "./components/dialogs/QuickAddModal";
import CaseModal from "./components/modals/CaseModal";
import TimeEntryModal from "./components/modals/TimeEntryModal";
import BulkTimeEntryModal from "./components/modals/BulkTimeEntryModal"; // 144 §4

import AgendaEventModal from "./components/modals/AgendaEventModal";
import TaskModal from "./components/modals/TaskModal";
import CollaboratorModal from "./components/modals/CollaboratorModal";
import EisenhowerDialog from "./components/dialogs/EisenhowerDialog";
import BentoDialog from "./components/dialogs/BentoDialog"; // 190 §C
import { BentoPicker } from "./components/BentoBoard"; // 190 [I] — the picker beside the header icon
import { useBento } from "./hooks/useBento";
import type { EisenCardTask } from "./components/EisenTaskCard"; // 190 §C
import LinksManagerDialog from "./components/dialogs/LinksManagerDialog"; // 112 §7
import PartyDetailDialog from "./components/dialogs/PartyDetailDialog";
import TaskPromptDialog from "./components/dialogs/TaskPromptDialog";
import ClientModal from "./components/modals/ClientModal";
import ContactPersonModal from "./components/modals/ContactPersonModal";
import StageModal from "./components/modals/StageModal";
import AdditionalPartyModal from "./components/modals/AdditionalPartyModal";
import ArbiterModal from "./components/modals/ArbiterModal";
import MediatorModal from "./components/modals/MediatorModal";
import ExpertModal from "./components/modals/ExpertModal";
import OpposingCounselModal from "./components/modals/OpposingCounselModal";
import OpponentModal from "./components/modals/OpponentModal";
import OpponentContactModal from "./components/modals/OpponentContactModal";
import JurisdictionModal from "./components/modals/JurisdictionModal";
import { makeToast, ToastHost } from "./components/Toast";
import { OPEN_MATTER_FORM, setPendingMatterForm, languageRefusalAction, type MatterRef } from "./lib/caseLanguage"; // 209 §5
import { buildEcfg } from "./lib/ecfg";
import { clientArchivedChip, clientStandingPill } from "./lib/clientStandingPill"; // 206C §4.1 — the two marks a client carries
import EntityList from "./components/EntityList";
import { usePopoverAutoClose, useAppHotkeys } from "./hooks/popoverChrome";
import { SideFlyPanel } from "./components/SideFlyPanel";
import { SearchPopover } from "./components/SearchPopover";
import { PasswordField } from "./components/PasswordField";
import { ClientsImportModal } from "./components/ClientsImportModal";
import CoView from "./views/CostsView";
import Table from "./components/Table";
import { withCount } from "./lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// Chrome Document-PiP browser global (guarded at call sites); TS's DOM lib has
// no declaration for it yet. Zero-emit.
declare const documentPictureInPicture: any;

const _pendingCreates = new Set();
// Phase 30 (D-3) — the C94d failed-update cooldown (_failedUpdates map) is
// gone: it reverted re-attempts client-side WITHOUT a network call, so a
// transient failure looked permanent for 60s. A retry now actually retries;
// the engine's rollback handles each failure individually.

/* ── BRIEF 123 §3 — `EV_MEETING_KIND` IS DEAD, AND THIS IS ITS EPITAPH ───────
   It stood here as

       const EV_MEETING_KIND = { REU: "room", VIR: "virtual" };

   — two Belgian event-type codes, hardcoded in the browser, standing in for
   ONE thing the app had no idea about. It was read at eleven sites (the brief
   counted eight; the census in the commit message names all eleven), and every
   one of them was asking one of two questions through the other:

       is this event a meeting?         -> the TYPE's `meta.isMeeting` (§1),
                                           which ANY NUMBER of types may carry
       is it held in person or online?  -> the EVENT's `meetingMode` (§2),
                                           chosen in the modal (§3)

   The cost was measured, not argued: a hearing held by videoconference had no
   home at all, and tenant 4 on this box has `event_type` code REU relabelled
   "Team meeting (renamed by the firm)" — a working server (which asks the
   taxonomy, eventTypeImport.js:28-47) and a modal that could not see its own
   firm's meeting type.

   The two facts now live in src/lib/meetingMode.ts and the fold that produces
   the first is `meetingTypeCodes` (utils/taxonomyMap), beside the M365 import
   fold it must not be confused with.

   THE SERVER'S OWN MIRROR WAS STILL A LITERAL PAIR WHEN THIS WAS WRITTEN, and
   §3 stated that boundary rather than crossing it: `MEETING_KIND = { REU:
   'room', VIR: 'virtual' }` (routes/agenda.js:60@50fb417c, read at :440 and
   :677) still decided whether the ROUTE bound a room or read a platform, and
   widening the census to the server was named the next unit.

   THAT UNIT RAN IMMEDIATELY AFTER, on this branch. Both readers now ask the
   event's own `meetingMode` and the map is gone from the server too, so
   "extending a future type to a machinery is a one-entry change here" — the
   comment that stood above it — is no longer a change anybody has to make.
   The reason it could not wait: :677's room release on "type flipped away" was
   nearly dead code, and §3's toggle makes Physical -> Online an ordinary act. */

/* 05n — COUNTRY_FALLBACK is GONE, and this is why a dead branch is worth
   deleting rather than tolerating. 05m-b hoisted it verbatim and registered
   it: `useTaxonomyList` returns `KIND_TO_DEFAULT[kind] || []` and never null,
   so `countriesList || COUNTRY_FALLBACK` could not fire. Harmless while the
   two lists were the same seven strings. This slice makes them different
   SHAPES — 249 `[code, label]` tuples against seven bare labels — so the
   unreachable branch became an unreachable branch that would render the wrong
   thing, which is a trap rather than clutter. */

// ── 128 §4 — `SEC` LEFT THIS FILE, and it took the partition with it ──────
//
// It is `components/NotifGroupedList.tsx` now — heading, colour, counter and
// 126 §4b's total `groupOf()`, moved as one piece because they are one thing:
// a counter is a fact about a bucket, and a bucket is a fact about the
// partition that filled it. Splitting them across two files is how a heading
// comes to count rows a different rule assigned.
//
// The sweep comment that stood here (SEC was declared inside the overview's
// render body and mounted as <SEC …/>, so every App render minted a new
// component type) travelled with it, and so did 125 §6's ruling that this
// block is the GROUPING and never the ROW.
//
// WHY IT MOVED: the bell was `notifications.slice(0, 20).map(…)` — one flat
// list, no headings, no counters — and the overview was four `SEC`s over a
// partition computed in its own render body. Two renderers of one list, one
// click apart. 125 §6 ended two renderers of a ROW; this ends two renderers of
// the LIST.
/* 128 §3 — one warning per un-rendered notification type per session. Module
   scope on purpose: the persisted branch runs inside App's render, which fires
   on every timer tick and every SSE event, and a per-render warn would bury
   the thing it is trying to say. */
const _untypedSeen = new Set<string>();

/* 04g §2 — what saveEntity forwards to persist(). `createExtra` is merged into
   the CREATE request only, never into the row that enters state; today its one
   field is the collaborator password. Deliberately NOT `any`: this is the union
   of what every call site actually passes, and persist's own onRow/onStart are
   reached directly by their callers rather than through here — widen it when a
   call site needs them, per THE FOUR RULES #2. */
type SaveEntityOpts = { createExtra?: Record<string, unknown> };

/* Brief 42 — the alert payload the engine sends, which the toast and the bell
   row are two renderings of. The field list is the ENGINE's, not a convenient
   subset: `server/src/lib/alertEngine.js:384` builds the common base and the
   reminder scans repeat them. Written rather than `any` because the ratchet
   may never grow — new code gets a type, and a baseline is for a file MOVE
   (SESSION_RULES.md, earned in 04g). Every field is optional because this
   crosses the wire: the type says what MAY be there, and the reads guard.

   BRIEF 46 §4 — THIS TYPE HAD FALLEN BEHIND ITS OWN CLAIM, and the claim is
   what made that a defect: a type that says "the field list is the ENGINE's"
   and then omits five of the engine's fields is worse than one that says
   nothing, because a reader trusts it. `leadMinutes` went missing when #279
   moved the number out of the identity and into the payload; `leadDays`,
   `late`, `shouldHaveFiredOn` and `caughtUpOn` are brief 46's own additions
   (§1 and §2) and were mine to declare here. All five are added, and the
   renderers that read them (the bell branch and the toast) now type-check
   against the thing the engine actually sends. */
type AlertPayload = {
  kind?: string;
  entity?: string;
  entityId?: number | string | null;
  title?: string;
  type?: string | null;
  date?: string | null;
  time?: string | null;
  caseId?: number | null;
  ccId?: number | null;
  joinUrl?: string | null;
  /* The minute lead (#279) and the day lead (brief 46 §1) — each read from
     the payload rather than recomputed, so a renderer can never disagree with
     the engine the way `alerts.t15` did once its constant moved. */
  leadMinutes?: number | null;
  leadDays?: number | null;
  /* Brief 46 §2 — the catch-up's stamp. `late` is what makes the renderers
     show "should have reached you on …" instead of a countdown that may no
     longer be true. */
  late?: boolean;
  shouldHaveFiredOn?: string | null;
  caughtUpOn?: string | null;
};
/* The toast IS that payload plus the client's dedupe identity. `expiresAt` is
   optional and that optionality is brief 42 §0's ruling in the type system:
   an alert toast carries none and is never swept. */
type AlertToast = AlertPayload & { uid: string; expiresAt?: number };
/* What `navAgendaEvent` needs of an agenda event, and no more. `caId` AND
   `caseId` because this tree carries both spellings — see the note at the
   function. */
type AgendaEventLike = {
  id?: number | string | null;
  caId?: number | null;
  caseId?: number | null;
  ccId?: number | null;
};

/* 05k §1 — the groups the collaborator DELETE 409 can name, in display order.
   Keys and order are the SERVER's (routes/collaborators.js REFERENCE_GROUPS);
   this map only says how each renders and in what sequence, so a group the
   server adds without a label here is skipped rather than shown raw.

   The values are the untranslated fallback. They carry "(s)" because a
   `fallback` string is not plural-resolved — only catalog keys are, via
   `collaborator.blocked.<key>_one` / `_other` (seeded en/fr/nl). Same trade
   the client delete banner already makes with `${lk.cases} case(s)`. */
const _BLOCK_GROUP_FALLBACK_EN: Record<string, string> = {
  /* 05k §3 first — these two are the ones whose null would have destroyed
     something (billable time re-priced to zero; an AML verifier's identity),
     so they lead the sentence. */
  timeEntries: "{count} time entr(y/ies)",
  cddVerifications: "{count} AML verification(s)",
  chat: "{count} chat message(s)",
  caseTeam: "{count} case link(s)",
  grants: "{count} access grant(s)",
  documents: "{count} attached document(s)",
  library: "{count} library loan(s)",
  reservations: "{count} room reservation(s)",
};
const _BLOCK_GROUP_ORDER = Object.keys(_BLOCK_GROUP_FALLBACK_EN);

/* 72 — THE SHARE LEG'S SHAPES, DECLARED RATHER THAN ERASED. `: any` and
   `as any` are what scripts/lint/ratchets.js counts, and SESSION_RULES is
   explicit that new code adding one gets a TYPE and not a new baseline. The
   precedent for a shape TypeScript does not carry yet is this tree's own:
   webrtcCall.ts's `TransportStat` and CallWindow.tsx's `userAgentData` sniff
   both write the shape out, because a shape written out is refutable by the
   next engine that changes it and an `any` is not. */

/* `ShareLeg` and `ShareView` are NOT declared here: `CallWindow` reads them too,
   and a type in this file would have to be copied into that one. They live in
   `src/lib/webrtcCall`, which owns call-session shapes, and are imported above.

   `monitorTypeSurfaces` — the ONE exclusion getDisplayMedia actually enforces
   (§3.3, whole-screen sharing is never offered) — is absent from the DOM lib's
   `DisplayMediaStreamOptions` at this TypeScript version, and so is the method
   on some lib targets. Both are declared here instead of cast away. */
type ShareCaptureOptions = { video: { monitorTypeSurfaces: string }; audio: false };
type DisplayCapableMediaDevices = MediaDevices & {
  getDisplayMedia?: (options: ShareCaptureOptions) => Promise<MediaStream>;
};

/* The fields this file reads off a rejected `api` call. `src/api.ts:186-188`
   sets `status`, `body` and `code` on the Error it throws, so anything past
   `code` — here the id of whoever already holds the share — is read from
   `body`, never off the error itself. */
type ShareApiError = { name?: string; code?: string | null; body?: { sharerId?: number } };

/* BRIEF 95 §2c — the two shapes `_evRpIds` reads, and nothing more.
   An agenda event carries BOTH spellings of its case id depending on where it
   came from (`shape()` in routes/agenda.js:195 adds `caId` beside the column's
   own `caseId`), which is the same defensive read `navAgendaEvent` and
   CompactColumn's `navEvent` already make. `MatterRps` is the half of a Case /
   CollectiveCase that answers "who is responsible" — a cast at the two `find`
   sites rather than an `any` at each, so the ratchet stays where it is. */
/* 116 §2 — `subtasks` joins the shape rather than being cast in at the one site
   that reads it: `coAvs`'s second badge asks whether a LIVE subtask of the
   event is the viewer's, and `(ev as any).subtasks` would have been a new `any`
   in new code — which is exactly what `ratchets.js` counts. Only the two fields
   that predicate reads are declared. */
type EvMatterRef = {
  caId?: number | null;
  caseId?: number | null;
  ccId?: number | null;
  subtasks?: { archived?: boolean; assignedCoId?: number | null }[] | null;
  /* 119 §5/§6 — the stored addressees, so the hover panel groups the SAME
     population the modal's overview does. Declared here rather than cast at the
     call site, for 116 §2's reason two lines up: a new `any` in new code is
     exactly what `ratchets.js` counts. */
  outlookAttendees?: OutlookAttendee[] | null;
  outlookAttendeesAt?: string | null;
};
type MatterRps = { id?: number; responsiblePartners?: number[] | null };

/** A matter, as far as the meeting seed needs one: its team, and the nine id
 *  families `matterCast` resolves. */
type MatterWithCast = {
  id?: number;
  caseCollaborators?: { coId?: number }[] | null;
  clIds?: unknown[] | null;
  cpIds?: unknown[] | null;
  opIds?: unknown[] | null;
  ocIds?: unknown[] | null;
  ocuIds?: unknown[] | null;
  exIds?: unknown[] | null;
  meIds?: unknown[] | null;
  arIds?: unknown[] | null;
  op2Ids?: unknown[] | null;
};

/* ── 119 §3 — THE ORPHAN WATCH'S TWO NUMBERS, NAMED ──────────────────────────
   The CADENCE and the FLOOR are different facts and must not be one constant.

   `POLL_MS` is how often the watch asks on its own: twenty minutes. The brief's
   intent is 15-30; an accepted Outlook invitation is not a page anybody is
   waiting on, and one Graph call per signed-in user per twenty minutes is a
   load a tenant does not feel.

   `MIN_GAP_MS` is the floor under EVERY caller, including the ones the watch
   does not control. Window-refocus fires on any alt-tab and would otherwise
   turn a nudge into a poll, so no two searches are made within a minute of each
   other whatever asked for them.

   Module scope, so the callback that reads them has no phantom dependency. */
const OUTLOOK_ORPHAN_POLL_MS = 20 * 60 * 1000;
const OUTLOOK_ORPHAN_MIN_GAP_MS = 60 * 1000;

/* 190 round 8 [AN] — the header's full-screen overlays: ONE owner, at most one
   open. The union is the whole list; a third overlay joins it here and nowhere
   else. `headerOverlayNext` is what each overlay's setter writes: it keeps the
   boolean-or-updater contract the consumers have (`setEisenOpen(false)`,
   `setBentoOpen((v) => !v)`) over the flag's own value, and resolves it
   against the one owner — opening names itself (whatever was open closes),
   closing clears itself only. Pure and module-scope, so the two callbacks
   below have no dependency. */
type HeaderOverlay = "eisen" | "bento" | null;
function headerOverlayNext(cur: HeaderOverlay, name: Exclude<HeaderOverlay, null>, v: unknown): HeaderOverlay {
  const next = typeof v === "function" ? !!(v as (open: boolean) => unknown)(cur === name) : !!v;
  return next ? name : cur === name ? null : cur;
}

/* 195 §B.0 — a row un-archived through the modal joins the live list: merged
   over its own id if the stream already delivered it, appended otherwise.
   Module scope: the save's arm is three closures deep already. */
const mergeRestoredRow = (prev: unknown, row: { id: number }): { id: number }[] => {
  const arr = Array.isArray(prev) ? (prev as { id: number }[]) : [];
  return arr.some((x) => x.id === row.id) ? arr.map((x) => (x.id === row.id ? { ...x, ...row } : x)) : [...arr, row];
};

/* 207B §6 [RULED] — a NEW client's language is French as a real value in the
   form (what the save wrote anyway — the saved result is today's); the form
   shows what is stored, and nothing where nothing is. Module scope and typed
   over `unknown`: the seed arrives as `any`, and is read here without it.
   207C §6 — AND AN EXISTING CLIENT'S FORM READS WHAT IS STORED. The form binds
   `lang` (the API's alias); the server sends `language` and nothing ever
   copied it across, so EVERY stored client opened on « — » and a save made
   without touching it wrote null over its language (207B's form; `main`'s
   wrote « fr » over it). The stored language is the form's `lang` now
   (src/lib/docLang.mjs `storedClientLanguage`); an empty one stays « — ».
   209 §5 [RULED 18] — AND A NEW CLIENT STARTS WITH NONE: « no language is ever
   assumed »; a client without one reads its case's. (It started in French.) */
function withClientFormLang(kind: unknown, isNew: boolean, seed: unknown): unknown {
  if (kind !== "cl" || !seed || typeof seed !== "object") return seed;
  if (!isNew) return { ...seed, lang: storedClientLanguage(seed) || null };
  return seed;
}

/* 208 §15 — how long after the bell's first load a newly seen item still counts as
   unread WHEN THE PAGE OPENED (a source answering late: the Outlook count), and
   stays in the bell rather than reaching the Mac. */
const BELL_SETTLE_MS = 10000;

export default function App() {
  // 81 §7 — `locale` joins the destructuring for the previous-login line: the
  // date is formatted for the READER'S locale (en→en-GB, fr→fr-BE, nl→nl-BE via
  // i18n.tsx's LOCALE_BY_LANG) and never as a hard-coded DD/MM/YYYY.
  const { t, lang, locale, setLang, available: i18nAvailable } = useTranslation();
  const { rows: _roleRows } = useRoleLabels(); /* Phase M+3.7 — load and subscribe to /api/roles/labels so roleLabel() returns active-language strings everywhere. C.2 — the rows also feed the financials-compute payloads. */
  const { confirmAndUndo, confirmDlg, cancelConfirm, undoToast, runUndo } =
    useConfirmAndUndo();
  const fRef = useRef<any>(null);
  /* 04g §2 — THE CREATE PASSWORD LIVES HERE AND NOWHERE ELSE.
     POST /api/collaborators has accepted, floor-checked and hashed a
     `password` since it was written (collaborators.js:116) and no client
     ever sent one, so the create modal produced accounts that could not sign
     in on any box without M365. The value has to reach the POST body, and the
     one thing it must not do is enter `form`: a form-shaped object is what
     gets spread into state, diffed and stringified by the sync engine
     (persist :4235 hashes the row, :4284 POSTs it, and the row sits in
     `collaborators` until the server's answer replaces it). So it goes to a
     REF, read once and synchronously by the save handler and handed to
     persist() as `createExtra`, which merges it into the POST body alone. It
     is never in `form`, never in a state array, never in the create hash,
     never in the localStorage cache, and never in a toast or an error body —
     failures report the server's reason, which for this field is the floor in
     lib/password.js and never the value. */
  const coCreatePwRef = useRef("");
  /* Stable identity: PasswordField reports through an effect, so a fresh
     function each render would re-fire it every render. */
  const _setCoCreatePw = useCallback((v: string) => {
    coCreatePwRef.current = v || "";
  }, []);
  /* The one read. Returns persist()'s `createExtra` or undefined, so a create
     without a password is byte-identical to what this modal sent before. */
  const _coCreatePwPayload = () =>
    coCreatePwRef.current
      ? { createExtra: { password: coCreatePwRef.current } }
      : undefined;
  /* 04g §2 — is a password OPTIONAL on this box? Only where Microsoft sign-in
     is actually configured, which is what /api/auth/config already reports to
     the login page (auth.js:74). Off (staging, any standalone-local instance)
     a collaborator without one cannot log in at all, so creation would be a
     half-operation and the password is REQUIRED. Defaults to false, so a
     failed probe demands a password rather than quietly allowing an account
     nobody can use — the one answer the brief rules out is blank-and-silent. */
  const [_m365Configured, setM365Configured] = useState(false);
  /* 77 §4a — is this tenant on the lenient (demo) floor? The same response
     already being fetched carries it, so the banner costs no extra request.
     Defaults to false: a failed probe shows NO banner rather than crying demo
     at a real firm, which is the safe direction for a claim about the data. */
  const [_demoTenant, setDemoTenant] = useState(false);
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/config", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (alive) setM365Configured(!!c?.m365Configured);
        if (alive) setDemoTenant(c?.demo === true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  // 167 §2d — authed derives from the stored IDENTITY (lx:authUser), never from
  // a token: the browser holds no token now, its session is the httpOnly cookie.
  // A returning user with a live cookie and a stored identity boots straight in;
  // the cookie authenticates every call.
  const [authed, setAuthed] = useState<any>(() => !!getAuthUser());
  // Decode a JWT payload (no signature verification — that's the server's job).
  // Accepts base64url with or without padding; logs to console for diagnosis.
  const _decodeJwt = (tok: any) => {
    try {
      if (!tok || typeof tok !== "string") return null;
      const part = tok.split(".")[1];
      if (!part) return null;
      let b64 = part.replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      const json = decodeURIComponent(
        atob(b64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(json);
    } catch (e) {
      console.error("[lx] JWT decode failed:", e);
      return null;
    }
  };
  // Read the authenticated identity from the dedicated lx:authUser slot
  // (only ever written by login() / main.jsx SSO handler). Falls back to
  // decoding the JWT directly for sessions that predate the dedicated slot.
  // IMPORTANT: never read lx:currentUser here — that key has historically been
  // reused and may hold the simulated "Viewing as" identity, not the real one.
  const _readAuthIdentity = () => {
    try {
      const raw = localStorage.getItem("lx:authUser");
      if (raw) {
        const u = JSON.parse(raw);
        if (u && u.id != null)
          return { id: +u.id, isAdmin: !!u.isAdmin, source: "lx:authUser" };
      }
    } catch {}
    const p = _decodeJwt(localStorage.getItem("lx:token"));
    if (p && p.id != null) {
      try {
        localStorage.setItem(
          "lx:authUser",
          JSON.stringify({
            id: p.id,
            email: p.email || "",
            role: p.role || "",
            isAdmin: !!p.isAdmin,
          }),
        );
      } catch {}
      return { id: +p.id, isAdmin: !!p.isAdmin, source: "jwt" };
    }
    return null;
  };
  // authUserId: the REAL signed-in user's id. Set once at login from the
  // identity above. Never touched by sCurrentUser; "Viewing as" only changes
  // `currentUser`, so the admin retains the switcher and admin-only UI even
  // while impersonating a non-admin.
  const [authUserId, setAuthUserId] = useState<any>(() => {
    const id = _readAuthIdentity();
    if (id) {
      console.log("[lx] authUserId init:", id);
      return id.id;
    }
    return null;
  });
  useEffect(() => {
    const h = () => {
      setAuthed(false);
      setAuthUserId(null);
    };
    window.addEventListener("lx:unauthorized", h);
    return () => window.removeEventListener("lx:unauthorized", h);
  }, []);
  useEffect(() => {
    if (!authed) {
      setAuthUserId(null);
      return;
    }
    const id = _readAuthIdentity();
    if (id) {
      console.log("[lx] authUserId set after auth:", id);
      setAuthUserId(id.id);
    } else {
      console.warn("[lx] authed=true but no identity in lx:currentUser or JWT");
    }
  }, [authed]);
  const doLogout = () => {
    // 05s — apiLogout() now sweeps every `lx:` key (api.ts clearAppStorage), so
    // the hand-written `removeItem("lx:cu")` that used to stand here is gone.
    // It was the evidence for the prefix rule: the call site had already grown
    // a fourth key because the list inside logout() did not cover it.
    apiLogout();
    __resetPermissionsCache();
    sCurrentUser(null);
    setAuthed(false);
    setAuthUserId(null);
    setSimulation(null);
  };
  if (typeof window !== "undefined") window.lxLogout = doLogout;
  // Phase M+2b — admin "View as" simulation. 167 §2d: the simulated token is the
  // httpOnly cookie now, unreadable by JS, so the banner reads a NON-SECRET
  // marker (`lx:sim`) written by startSimulation from the /simulate response —
  // { simulatedBy, simulatedUserId, expiresAt }, the same three values the
  // banner ever showed, none of them a credential. lx:authUser stays the
  // original admin so _authIsAdmin keeps the switcher visible while impersonating.
  const _decodeSimulation = () => {
    try {
      const raw =
        typeof localStorage !== "undefined"
          ? localStorage.getItem("lx:sim")
          : null;
      if (!raw) return null;
      const m = JSON.parse(raw);
      if (m && m.simulatedBy != null)
        return {
          simulatedBy: +m.simulatedBy,
          simulatedUserId: +m.simulatedUserId,
          expiresAt: m.expiresAt || null,
        };
    } catch {
      /* a marker we cannot read is no simulation */
    }
    return null;
  };
  const [simulation, setSimulation] = useState<any>(() => _decodeSimulation());
  const startSimulation = async (targetId: any) => {
    if (!targetId) return;
    // M+3.7.2 §D — Full reload after JWT swap. The previous markSwapping
    // dance only covered TeamAccessPanel; other hooks (useTaxonomy,
    // usePreferences, useRoleLabels, presence ping) ignored the flag and
    // 401-cascaded under the simulated token. Reload re-runs the full boot
    // sequence with the new token; admin-only debug-tier flow, lost
    // in-flight modal state acceptable.
    await swapSimulationToken({
      url: "/api/auth/simulate",
      body: { collaboratorId: targetId },
      onSwapped: (j: any) => {
        sv(SK.cu, targetId);
        // 167 §2d — the banner's source, written here (the token is httpOnly).
        try {
          localStorage.setItem(
            "lx:sim",
            JSON.stringify({
              simulatedBy: j?.simulatedBy,
              simulatedUserId: targetId,
              expiresAt: j?.expiresAt ?? null,
            }),
          );
        } catch {
          /* the swap still reloads under the simulated cookie */
        }
      },
      onError: (e) =>
        toast(
          "⚠ " +
            t("toast.simulate_failed", { fallback: "Simulate failed:" }) +
            " " +
            (e.message || t("common.unknown", { fallback: "unknown" })),
        ),
    });
  };
  const endSimulation = async () => {
    await swapSimulationToken({
      url: "/api/auth/end-simulate",
      onSwapped: (j) => {
        if (j.user && j.user.id != null) sv(SK.cu, j.user.id);
        // 167 §2d — simulation is over; drop the banner marker.
        try {
          localStorage.removeItem("lx:sim");
        } catch {
          /* nothing to clear */
        }
      },
      onError: (e) => {
        toast(
          "⚠ " +
            t("toast.exit_simulate_failed", {
              fallback: "Exit simulation failed:",
            }) +
            " " +
            (e.message || t("common.unknown", { fallback: "unknown" })) +
            " " +
            t("toast.please_sign_in_again", {
              fallback: "— please sign in again",
            }),
        );
        setTimeout(doLogout, 1500);
      },
    });
  };
  // Per-user preferences (Postgres-backed, formerly scattered across lx:* in
  // localStorage). The hook subscribes once; primePreferences() is awaited in
  // the authed boot effect so the singleton is populated before this body
  // renders any consumer. `setPref(path, value)` debounces 500ms.
  const { prefs, setPref } = usePreferences();
  const [loading, setLoading] = useState<any>(true);
  const [section, setSection] = useState<any>("dash");
  const [query, setQuery] = useState<any>("");
  /* 195 §C.3b — the Documents screen's own search text. `DocView` below is
     a render-body closure (the function-call shortcut, no hooks of its own),
     so its state lives here beside `query`; the box is DocView's. */
  const [docsQ, setDocsQ] = useState<string>("");
  const [detailId, setDetailId] = useState<any>(null);
  /* BRIEF 108 §4b — the search's one-shot Settings focus hint: which pane a
     search result chose, consumed by SettingsView (open + scroll) and then
     cleared. { pane, seq } — seq makes a repeat of the same pane re-fire. */
  const [settingsFocus, setSettingsFocus] = useState<{ pane: string; seq: number } | null>(null);
  const [caseFilter, setCaseFilter] = useState<any>("all");
  const caSort = prefs.caseSort || { k: "nextev", d: 1 };
  const setCaSort = (v: any) =>
    setPref("caseSort", typeof v === "function" ? v(caSort) : v);
  /* 189 §C / 190 §D → 190 round 6 [Z] (ruled 2026-09-16) — list or board is
     TRANSIENT state, not a preference. It was `prefs.caseListMode` /
     `prefs.ccListMode`, 189's stage-board inheritance where list-or-board
     was meant to last — so a list "got stuck on bento view even after
     wandering elsewhere in the app". Now the table is the default on every
     arrival and the Bento is a mode you enter and leave, like the
     Eisenhower: `navResetTransients` resets both with the rest of the
     transient state (the union rule — no second mechanism). The two
     preference keys have no writer any more; a stored value goes unread (a
     preference blob is deep-merged, never validated against a key list —
     one staging account carried "board", and it simply stops mattering).
     The board is the list Bento (190 (3)): a move writes a placement, never
     a stage. */
  const [caMode, setCaMode] = useState<"list" | "board">("list");
  const [ccMode, setCcMode] = useState<"list" | "board">("list");
  const [agendaFilter, setAgendaFilter] = useState<any>("all");
  // Phase 43 (item 1) — Clients-scoped structured filter bar. Ephemeral, mirrors
  // cf/af; AND-applied client-side over the already-loaded client list inside
  // EList's `fi` (guarded on et === "cl"). `arch` explicitly governs archived
  // visibility (active | incl | only) — replacing the implicit hide-until-typed
  // rule for the clients list, so archived former clients can be surfaced for a
  // manual conflict check.
  /* 168B §2b (RULING 3) — the axes, in the ruled order. `type` and
     `clientStatus` are new. `email` is GONE: ruling 3 enumerates the bar's
     content and an e-mail box is not in it — §1f made the address a column
     that composes and copies, and the search box above already matches an
     e-mail substring (EntityList joins every string field of the row). */
  const CL_FILTER_DEFAULT = {
    name: "", type: "all", country: "",
    caseLink: "all", caseName: "",
    status: "all", clientStatus: "all", statusFrom: "", statusTo: "",
    arch: "active", due: "all",
  };
  const [clFilter, sClFilter] = useState<any>(CL_FILTER_DEFAULT);
  // Phase 43 CORRECTION (item 2) — app-styled COI/duplicate confirm. Holds
  // { msg, onConfirm } while open; OK runs onConfirm (the deferred save), Cancel
  // clears it (abort). Replaces the native window.confirm.
  const [coiPrompt, setCoiPrompt] = useState<any>(null);
  const [activeModal, setActiveModal] = useState<any>(null);
  const [form, setForm] = useState<any>({});
  const [activeToast, setActiveToast] = useState<any>(null);
  const [confirmDelete, setConfirmDelete] = useState<any>(null);
  // Phase 36 (D) — when a client hard-delete is refused (linked / folder
  // populated), hold { id, body } to offer "archive instead".
  const [clBlocked, setClBlocked] = useState<any>(null);
  const [quickAddModal, setQuickAddModal] = useState<any>(null);
  const [quickAddForm, setQuickAddForm] = useState<any>({});
  const [expCl, sExpCl] = useState<any>(false);
  const [clSrch, sClSrch] = useState<any>("");
  /* BRIEF 125 §3 — THE SENTINEL, WHICH USED TO BE WRONG AT BOTH ENDS.
     `null` = the matter picker's menu is CLOSED; anything else, INCLUDING the
     empty string, is OPEN showing recents — that is the render condition the
     menu is written against (AgendaEventModal.tsx:628 `agCaseQ != null`).
     This was initialised to `""`, and `cm()` reset it to `""`, so the menu was
     painted the instant the field mounted, on EVERY open, over whatever sat
     below it. See the § note on `cm()` for the whole diagnosis; the semantics
     were never in doubt because the line below has stated them since C18, and
     ITS reset is `null`. The comment is now on both, so the next reader of
     either sees the contract rather than inferring it from one of them. */
  const [agCaseQ, sAgCaseQ] = useState<any>(null); // null = picker closed; "" = open (recents)
  const [teCaseQ, sTeCaseQ] = useState<any>(null); // C18: null = picker closed; "" = open (recents)
  const [clients, setClients] = useState<any>([]);
  const [cases, setCases] = useState<any>([]);
  const [collectiveCases, setCollectiveCases] = useState<any>([]);
  const [opponents, setOpponents] = useState<any>([]);
  const [opponentLawyers, setOpponentLawyers] = useState<any>([]);
  const [experts, setExperts] = useState<any>([]);
  const [jurisdictions, setJurisdictions] = useState<any>([]);
  const [collaborators, setCollaborators] = useState<any>([]);
  const [timeEntries, setTimeEntries] = useState<any>([]);
  // C.2 — the case-modal Bruto/Netto success-fee estimate moved server-side
  // (POST /api/financials/compute; same arithmetic, verbatim,
  // recording-verified: recordings/c2-financials/). The modal posts the
  // saved case row + its entries + the live form fields and renders what it
  // is handed — it computes none. Latest-wins; the result is stamped with
  // the owning form id so switching cases mid-flight never shows another
  // case's estimate; fq(null) renders "—" until the first response.
  const [caseModalCalc, setCaseModalCalc] = useState<any>(null);
  const _cmcSeq = useRef(0);
  useEffect(() => {
    if (activeModal !== "ca") {
      setCaseModalCalc(null);
      return;
    }
    const seq = ++_cmcSeq.current;
    const saved = form.id ? cases.find((c: any) => c.id === form.id) : null;
    computeCaseFinancials({
      cases: saved ? [redMatterFin(saved)] : [],
      entries: (form.id
        ? timeEntries.filter((t: any) => t.caId === form.id)
        : []
      ).map(redEntryFin),
      collaborators: (collaborators || []).map(redCoFin),
      roleRows: redRoleRows(_roleRows),
      // Brief 87 §3b — the modal's Bruto/Netto estimate sums the case's entries,
      // so it takes per-entry VAT too. The rate card beside it does not: a card
      // states today's terms (ruled 2026-08-17).
      invoices: (invoices || []).map(redFinInv),
      caseModal: {
        id: form.id === undefined ? null : form.id,
        referenceHourlyRate:
          form.referenceHourlyRate === undefined ? null : form.referenceHourlyRate,
        costsRatio: form.costsRatio === undefined ? null : form.costsRatio,
        claimAmount: form.claimAmount === undefined ? null : form.claimAmount,
        successFeeRate:
          form.successFeeRate === undefined ? null : form.successFeeRate,
      },
    })
      .then((r: any) => {
        if (_cmcSeq.current === seq)
          setCaseModalCalc({ id: form.id ?? null, data: r?.caseModal ?? null });
      })
      .catch(() => {
        if (_cmcSeq.current === seq) setCaseModalCalc(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, form.id, form.referenceHourlyRate, form.costsRatio, form.claimAmount, form.successFeeRate, cases, timeEntries, collaborators, _roleRows]);
  const [agendaEvents, setAgendaEvents] = useState<any>([]);
  const [contactPersons, setContactPersons] = useState<any>([]);
  const [opponentContacts, setOpponentContacts] = useState<any>([]);
  const [mediators, setMediators] = useState<any>([]);
  const [arbiters, setArbiters] = useState<any>([]);
  const [additionalOpponents, setAdditionalOpponents] = useState<any>([]);
  const [books, setBooks] = useState<any>([]);
  const [invoices, setInvoices] = useState<any>([]);
  const evtTypes = useTaxonomyList("event_type");
  const extLinks = useTaxonomyList("external_link");
  /* Phase M+2b: permMx kept as empty state for backward-compat with the dead-code preview block in SettingsView. The Gate 4b Roles UI replaces both. */ const [
    permMx,
    sPermMx,
  ] = useState<any>([]);
  const [etForm, sEtForm] = useState<any>({ code: "", desc: "", color: CT.c_4a7cff });
  const [tasks, setTasks] = useState<any>([]);
  /* 210 §1 — THE LISTS THAT COULD NOT LOAD (src/lib/listLoad.ts has the rule,
     and the census of the local copies the boot's `_apiOr` fell back on). By
     `SK` key: what the last ask left — the status or the error; and the lists
     being asked again right now, so « Réessayer » waits for its answer. */
  const [listsNotLoaded, setListsNotLoaded] = useState<Readonly<Record<string, ListFailure>>>({});
  const [listsAsking, setListsAsking] = useState<ReadonlySet<string>>(() => new Set<string>());
  const _listsAskingRef = useRef(new Set<string>());
  /* Every list the boot loads: its name for the console, its request, its
     setter — all stable, so the table is built once. */
  const _listSources = useMemo(() => {
    const src = (key: string, name: string, fetch: () => Promise<unknown>, set: Dispatch<unknown[]>) => ({ key, name, fetch, set });
    return new Map(
      [
        src(SK.cl, "clients", getClients, setClients),
        src(SK.ca, "cases", getCasesWithLatestInvoice, setCases),
        src(SK.cc, "collective cases", getCollectiveCasesWithLatestInvoice, setCollectiveCases),
        src(SK.op, "opponents", getOpponents, setOpponents),
        src(SK.ocu, "opposing counsel", getOpponentLawyers, setOpponentLawyers),
        src(SK.ex, "experts", getExperts, setExperts),
        src(SK.ju, "jurisdictions", getJurisdictions, setJurisdictions),
        src(SK.co, "collaborators", getCollaborators, setCollaborators),
        src(SK.te, "time entries", getTimeEntries, setTimeEntries),
        src(SK.ag, "agenda events", getAgenda, setAgendaEvents),
        src(SK.cp, "contact persons", getContactPersons, setContactPersons),
        src(SK.oc, "opponent contacts", getOpponentContacts, setOpponentContacts),
        src(SK.me, "mediators", getMediators, setMediators),
        src(SK.ar, "arbiters", getArbiters, setArbiters),
        src(SK.op2, "other persons", getAdditionalOpponents, setAdditionalOpponents),
        src(SK.books, "books", getBooks, setBooks),
        src(SK.inv, "invoices", getInvoices, setInvoices),
        src(SK.tk, "tasks", getTasks, setTasks),
      ].map((x) => [x.key, x] as const),
    );
  }, []);
  /* An ask's answers: a list that came is set and loses its mark; one that did
     not keeps it, or takes it — and nothing takes its place. */
  const settleLists = useCallback(
    (answers: ReadonlyMap<string, ListAnswer>) => {
      const failed: [string, ListFailure][] = [];
      for (const [k, a] of answers) {
        if (a.ok) _listSources.get(k)?.set(a.rows);
        else failed.push([k, a.failure]);
      }
      setListsNotLoaded((prev) => ({
        ...Object.fromEntries(Object.entries(prev).filter(([k]) => !answers.has(k))),
        ...Object.fromEntries(failed),
      }));
    },
    [_listSources],
  );
  /* A whole list read again elsewhere (the live stream's re-reads) is a load too. */
  const markListLoaded = useCallback((key: string) => {
    setListsNotLoaded((prev) => (key in prev ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key)) : prev));
  }, []);
  /* « Réessayer », and the window coming back: those lists alone, by the same
     policy (three asks, a second apart); a list already being asked is not
     asked twice. */
  const retryLists = useCallback(
    (keys: readonly string[]) => {
      const asking = _listsAskingRef.current;
      const sources: ListSource[] = [];
      for (const k of keys) {
        const src = _listSources.get(k);
        if (src && !asking.has(k)) sources.push(src);
      }
      if (!sources.length) return;
      for (const x of sources) asking.add(x.key);
      setListsAsking(new Set(asking));
      void loadLists(sources).then((answers) => {
        settleLists(answers);
        for (const x of sources) asking.delete(x.key);
        setListsAsking(new Set(asking));
      });
    },
    [_listSources, settleLists],
  );
  const _listLoad = useMemo<ListLoadState>(
    () => ({ notLoaded: listsNotLoaded, asking: listsAsking, retry: retryLists }),
    [listsNotLoaded, listsAsking, retryLists],
  );
  /* The focus cycle (below) is dep-less by design and reaches the marks
     through this. */
  const _listsRetryRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    _listsRetryRef.current = () => {
      retryLists(Object.keys(listsNotLoaded));
    };
  }, [listsNotLoaded, retryLists]);
  const tkTypes = useTaxonomyList("activity_code");
  const evtEntries = useTaxonomy("event_type").entries;
  /* P20a — the { code: locationSpec } fold genAgLocation resolves against.
     includeArchived, for the same reason the Eisenhower fold takes it (#191):
     an archived event type still has events pointing at it, and editing one
     of those must keep autofilling what it autofilled before. */
  const evtLocations = eventLocationsFromEntries(
    useTaxonomy("event_type", { includeArchived: true }).entries,
  );
  /* BRIEF 123 §1/§3 — the firm's own meeting types, folded ONCE, from the same
     archived-inclusive list the location fold reads and for the same reason
     (#191): an archived meeting type still has meetings pointing at it, and
     editing one of those must keep offering the mode it was held in. */
  const evMeetingTypes = meetingTypeCodes(
    useTaxonomy("event_type", { includeArchived: true }).entries,
  );
  /* BRIEF 124 §1 — and the mode each of those types PREFERS, from the same
     archived-inclusive list and for the same reason (#191): opening an event on
     an archived meeting type must not change what mode it prefers. */
  const evDefaultModes = meetingDefaultModes(
    useTaxonomy("event_type", { includeArchived: true }).entries,
  );
  /* BRIEF 139 §1 — and the LONGEST code any of them can put on a card, folded
     once for the same "no row re-derives it" reason as the three above. From
     `evtTypes` (:2022) and NOT from an archived-inclusive list, which is the one
     way this fold differs from its siblings: theirs answer questions about an
     event that already exists (#191), this one answers what every event surface
     must RESERVE — and §1's ruling is that archiving a long code narrows the
     column again. It is the same list `etTag` (:9174) resolves the visible chip
     against, so the mirror and what it mirrors cannot drift apart. */
  const evtLongestCode = longestTaxonomyCode(evtTypes);
  /* The two questions, as two functions, so no call site re-derives either. */
  const evIsMeeting = (typeCode: unknown) => isMeetingType(typeCode, evMeetingTypes);
  const evDefaultMode = (typeCode: unknown): MeetingMode | null =>
    evDefaultModes[String(typeCode ?? "")] || null;
  /* 124 §1 — `meetingModeFor`, not `meetingModeOf`: a row that has ANSWERED
     keeps its answer, and only an unanswered one reads the type's preference.
     Every existing meeting was answered by 123 §2's backfill, so this changes
     nothing that is already stored — it changes what a NEW event opens on,
     which is the whole of Laurent's report. */
  const evMode = (row: { type?: unknown; meetingMode?: unknown } | null | undefined) =>
    row && evIsMeeting(row.type)
      ? meetingModeFor(row, evDefaultMode(row.type))
      : null;
  const actEntries = useTaxonomy("activity_code").entries;
  const [tkForm, setTkForm] = useState<any>({ code: "", desc: "" });
  const [tkStatusFilt, setTkStatusFilt] = useState<any>("");
  const [sfMaps, setSfMaps] = useState<any>([]);
  const [extTypeMaps, setExtTypeMaps] = useState<any>(DEFAULT_EXT_TYPES);
  const [sfForm, setSfForm] = useState<any>({
    label: "",
    localBase: "",
    cloudBase: "",
  });
  const [xtForm, setXtForm] = useState<any>({ ext: "", type: "pleading" });
  const caseTypesList = useTaxonomyList("case_type");
  const docTypesList = useTaxonomyList("document_category");
  const titlesList = useTaxonomyList("person_title");
  const clientTypesList = useTaxonomyList("client_type");
  const caseStatusesList = useTaxonomyList("case_status");
  /* Phase M+3.7 Stage 3 round 3 — raw entries (with labelEn/labelFr/labelNl) used by CoView and ReportsView to localise badges. */ const caseStatusEntries =
    useTaxonomy("case_status").entries;
  const caseTypeEntries = useTaxonomy("case_type").entries;
  /* 59 §2 / 45c — the Clients list printed the `client_type` CODE. Raw entries,
     handed to buildEcfg, so the column resolves through 05m step C's matcher. */
  const clientTypeEntries = useTaxonomy("client_type").entries;
  /* 189 §B — the firm's own field definitions; the ones that say "show in
     lists" become the Clients list's trailing columns (buildEcfg). */
  const _customFieldTax = useTaxonomy("custom_field") as { entries: unknown; lang: string };
  const customFieldColsCl = customFieldColumns(_customFieldTax.entries, "client", _customFieldTax.lang);
  /* 05m-b — the vocabularies behind the six label-as-value selects. The lists
     above carry only the ACTIVE LANGUAGE's labels, which is enough to answer
     "does an option carry this value?" and not enough to answer "does the
     vocabulary claim it twice?" — the state that makes a healthy-looking
     control assert an entry the app cannot identify. One subscriber each; the
     hook's cache is module-level, so this costs a fetch nothing. */
  const titleEntries = useTaxonomy("person_title").entries;
  const countryEntries = useTaxonomy("country").entries;
  const barEntries = useTaxonomy("bar").entries;
  const caseStageEntries = useTaxonomy("case_stage").entries;
  const barsList = useTaxonomyList("bar");
  // 214 §4 [RULED 53] — a client's contact's role (the contact_role vocabulary), for the two contact forms and the lists.
  const contactRoleEntries = useTaxonomy("contact_role").entries as TaxList;
  const contactRolesList = useTaxonomyList("contact_role") as readonly FieldSelectOption[];
  const countriesList = useTaxonomyList("country");
  const docStatusesList = useTaxonomyList("document_status");
  const linkTypesList = useTaxonomyList("link_type");
  const stageTypesList = useTaxonomyList("case_stage");
  const taskStatusesList = useTaxonomyList("task_status");
  const taskStatusEntries = useTaxonomy("task_status").entries;
  const itemLevelList = useTaxonomyList("item_level");
  const itemLevelEntries = useTaxonomy("item_level").entries;
  const reminderOptsList = useTaxonomyList("reminder_option");
  /* 136 §3(5) — the firm's event statuses, for the ag modal's picker. Entries
     rather than the tuple list: `FieldSelect` resolves a row's language label
     from them, and the tuple shape carries no meta (taxonomyHelpers.ts:97). */
  const eventStatusEntries = useTaxonomy("event_status").entries;
  /* Phase M+3.7 — hydrate window.__lxEisen so eisenLabel(q) resolves to the active-language label.
   Read raw entries (not the tuple-shaped list) so labelEn/labelFr/labelNl are preserved. */
  const _eisenRaw = useTaxonomy("eisenhower_quadrant");
  const partyRoleTax = useTaxonomy("party_role");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const map: Record<string, any> = {};
    for (const e of _eisenRaw.entries || []) {
      map[e.code] = {
        labelEn: e.labelEn || e.label,
        labelFr: e.labelFr,
        labelNl: e.labelNl,
        label: e.label,
      };
    }
    window.__lxEisen = map;
  }, [_eisenRaw.entries, _eisenRaw.lang]);
  /* Same pattern, other direction — hydrate window.__lxActEisen so
   eisenAuto(type) resolves a task's DEFAULT quadrant from activity_code
   meta.eisen instead of the former hardcoded EISEN_AUTO_* lists.
   includeArchived: an archived code still has tasks pointing at it, and those
   kept their default under the old constants; dropping it would silently
   re-bucket them. Publish nothing until entries exist, so the pre-fetch window
   falls back to the bundled defaults (identical data) rather than to "s". */
  const _actEisenRaw = useTaxonomy("activity_code", { includeArchived: true });
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!(_actEisenRaw.entries || []).length) return;
    window.__lxActEisen = eisenDefaultsFromEntries(
      _actEisenRaw.entries,
    );
  }, [_actEisenRaw.entries]);
  const [themes, setThemes] = useState<any>([]);
  const [firmSettings, _setFirmSettings] = useState<any>(null);
  // 169 R3 — ONE seam: wherever firmSettings lands (boot, the SSE refetch, a
  // save, the field-PUT refresh), the firm's currency is noted for `fc`.
  // 207B §5 — the row the app holds NOW, for `takeFirmSettings`'s guard.
  const firmSettingsNowRef = useRef<{ updatedAt?: string } | null>(null);
  const setFirmSettings = useCallback((next: any) => {
    firmSettingsNowRef.current = next as { updatedAt?: string } | null;
    _setFirmSettings(next);
    noteFirmCurrency(next);
  }, []);
  const [firmLogo, setFirmLogo] = useState<any>(null);
  const [firmName, setFirmName] = useState<any>("Legal Practice Manager");
  // Resolved, never taken raw: a preference naming Inter, Roboto or Source Sans
  // Pro was written before those families were dropped and there is no face on
  // this box for any of them.
  const appFont = normaliseAppFont(prefs.font);
  const setAppFont = (v: any) =>
    setPref("font", typeof v === "function" ? v(appFont) : v);
  /* 204 §2.3 [MERGE — LABELLED, Laurent rules at the glance]: the per-user
     `prefs.themeColor` (« Browser bar colour ») is no longer read or written.
     MEASURED before the merge (drive-204p3-2c.js): it and the firm's
     `windowBarColor` drove ONE output, `<meta name="theme-color">` — and the
     per-user value never survived a reload, because the theme effect below
     writes the theme's `--bs` over it on every run and does not depend on
     it (planted #ff0000 → the meta read the theme's #141820 after boot;
     typed #123456 → tinted at once; reloaded → #141820 again), while the
     firm's value boots and stays and also reaches the PWA manifest
     (server/src/routes/manifest.js:45). The stored pref stays in each
     user's preferences row, unread; `lx:themeColor` stays in the legacy
     ingest list (lib/ingestLegacyPrefs.ts) so an old browser's key is still
     swept into that unread pref rather than left behind. */
  const [todayQuote, setTodayQuote] = useState<any>(() => {
    try {
      const d = localStorage.getItem("lx:quoteDate");
      const today = new Date().toISOString().slice(0, 10);
      if (d === today) {
        const q = localStorage.getItem("lx:quoteToday");
        return q ? JSON.parse(q) : null;
      }
    } catch {}
    return null;
  });
  const [partyDetailDlg, setPartyDetailDlg] = useState<any>(null);
  /* 190 round 8 [AK] — the footer slot's DOM node, handed down through
     `ContentFooterSlot` (CountersStrip.tsx); a ref callback into state so the
     provider re-renders once the node exists. */
  const [contentFooter, setContentFooter] = useState<HTMLDivElement | null>(null);
  /* 190 round 8 [AN] [RULED, Laurent 2026-09-16 — "make them mutually
     exclusive: ticking the one closes the other, in both directions"]: THE
     HEADER'S FULL-SCREEN OVERLAYS HAVE ONE OWNER THAT HOLDS AT MOST ONE.
     `eisenOpen` and `bentoOpen` were two independent booleans (:1426, :1429
     until this round), so both could be true and the later one buried the
     other — "a one way street", both icons glowing. Not cross-wired setters
     (a third overlay later would reintroduce it): ONE state, a union of
     names or null, and the two flags DERIVED from it. Each setter keeps the
     contract its consumers have — a boolean or an updater over the flag's
     own value (the header toggles, the dialogs' close, Escape, the Dashboard
     link) — and writes the one owner: opening names itself (whatever was
     open closes), closing clears itself only. Only one icon glows, because
     only one flag can be true. A third overlay joins the union. */
  const [headerOverlay, setHeaderOverlay] = useState<HeaderOverlay>(null);
  const eisenOpen = headerOverlay === "eisen";
  const setEisenOpen = useCallback((v: unknown) => { setHeaderOverlay((cur) => headerOverlayNext(cur, "eisen", v)); }, []);
  /* 190 §C surface 1 — the user Bento, beside the Eisenhower. Its Escape,
     its templates and its placements live in BentoDialog / useBento. */
  const bentoOpen = headerOverlay === "bento";
  const setBentoOpen = useCallback((v: unknown) => { setHeaderOverlay((cur) => headerOverlayNext(cur, "bento", v)); }, []);
  const closeBento = useCallback(() => { setBentoOpen(false); }, [setBentoOpen]);
  /* 190 [I] — the user Bento's state is App's (the host of the header icon):
     the picker sits in the header row right of the icon while the Bento is
     open, and the dialog takes the state down. */
  const userBento = useBento("user", "task", bentoOpen);
  const [eisenDragId, setEisenDragId] = useState<any>(null);
  const [eisenResetSnap, setEisenResetSnap] = useState<any>(null);
  const [eisenPickerOpen, setEisenPickerOpen] = useState<any>(false);
  const [userMatrix, setUserMatrix] = useState<any>({});
  useEffect(() => {
    if (!eisenOpen) {
      setEisenResetSnap(null);
      return;
    }
    const h = (e: any) => {
      /* 205 §5 — THE ESCAPE GOES TO WHATEVER IS IN FRONT. This listener is on
         `document`, which fires BEFORE every `window` listener — the hotkey
         chain (hooks/popoverChrome.ts) and ModalLayout's own `escToClose` are
         both `window` — so a key meant for a dialog opened over this pane used
         to close the pane FIRST and the dialog after it, on one press. It is a
         phase ordering, not a missing condition, which is why no
         `stopPropagation` anywhere could have fixed it.
         `isAbovePane()` is 204 §1's own order asked at the keypress: every
         shell lifted to `Z.paneModal`/`paneModal2` while this pane is open
         registers itself (lib/paneStacking.ts), and so does Alt+F's
         SearchPopover. While one of them stands, the pane is not the topmost
         thing that can close and does not consume the key. WHO REGISTERS and
         who needs no registration is measured in that file's own header. */
      if (e.key === "Escape" && !isAbovePane()) setEisenOpen(false);
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [eisenOpen]);
  const showDL = prefs.showDeadlines != null ? prefs.showDeadlines : true;
  const sShowDL = (v: any) =>
    setPref("showDeadlines", typeof v === "function" ? v(showDL) : v);
  const lookback = prefs.agendaLookback != null ? prefs.agendaLookback : 10;
  const sLookback = (v: any) =>
    setPref("agendaLookback", typeof v === "function" ? v(lookback) : v);
  const nwh = prefs.weeklyHourTarget != null ? prefs.weeklyHourTarget : 25;
  const sNwh = (v: any) =>
    setPref("weeklyHourTarget", typeof v === "function" ? v(nwh) : v);
  const [showEarlier, sShowEarlier] = useState<any>(false);
  const [collStg, setCollStg] = useState<any>({});
  const [showDesc, sShowDesc] = useState<any>(false);
  const [currentUser, sCurrentUser] = useState<any>(null);
  const [showMyDash, sShowMyDash] = useState<any>(true);
  const [agMy, sAgMy] = useState<any>(false);
  /* Brief 86 §2f — the Agenda's single-collaborator filter. Lifted here
     beside agMy because every other filter that view owns is App state
     (agTypeFilt, agDateRange, agViewMode, af, showEarlier), so the choice
     survives navigating away and back exactly as those do. null = off. */
  const [agCoFilt, sAgCoFilt] = useState<number | null>(null);
  /* Phase 30 (D-2) — the personal Eisenhower matrix is a per-user SERVER
     preference now (prefs.userMatrix, keyed by user id), so it roams across
     devices. One-time legacy ingest: an existing lx:userMatrix:<user> key
     seeds the pref the first time the pref is absent; localStorage stays a
     read-only fallback until then. */
  useEffect(() => {
    if (!currentUser) {
      setUserMatrix({});
      return;
    }
    const all = prefs.userMatrix;
    if (all && typeof all === "object") {
      setUserMatrix(all[currentUser] || {});
      return;
    }
    try {
      const raw = localStorage.getItem(`lx:userMatrix:${currentUser}`);
      const local = raw ? JSON.parse(raw) : {};
      setUserMatrix(local);
      if (raw) setPref("userMatrix", { [currentUser]: local });
    } catch {
      setUserMatrix({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, prefs.userMatrix]);
  // Once collaborators are loaded from the API, make sure currentUser
  // resolves to a real row, otherwise role-based UI (Schedule, Library,
  // etc.) appears dimmed & uneditable.
  useEffect(() => {
    if (!authed || !collaborators || !collaborators.length) return;
    if (currentUser && collaborators.some((c: any) => c.id === currentUser)) return;
    let next = null;
    try {
      const raw = localStorage.getItem("lx:currentUser");
      const liId = raw ? JSON.parse(raw).id : null;
      if (liId && collaborators.some((c: any) => c.id === liId)) next = liId;
    } catch {}
    if (!next) next = collaborators[0].id;
    sCurrentUser(next);
    sv(SK.cu, next);
  }, [authed, collaborators, currentUser]);
  useEffect(() => {
    if (!authed) return;
    let stopped = false;
    const ping = () => {
      api("/presence", { method: "POST" }).catch(() => {});
    };
    ping();
    const t = setInterval(() => {
      if (!stopped) ping();
    }, 30000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [authed]);
  useEffect(() => {
    if (!authed) return;
    const h = async () => {
      try {
        const rows = await getTimeEntries();
        setTimeEntries(rows);
        if (Array.isArray(rows)) markListLoaded(SK.te); // 210 §1 — a whole re-read is a load
      } catch {}
    };
    window.addEventListener("lx:reload-time-entries", h);
    return () => window.removeEventListener("lx:reload-time-entries", h);
  }, [authed]);
  const userMatrixQuadrant = (t: any) => userMatrix[t.id] || eisenQuadrant(t);
  // INVARIANT: Eisenhower drags write a per-user triage preference only — they must NEVER mutate the task record (tk/Task). Matrix placement is a private view; task fields (status/priority/assignee) stay owned by the task.
  const _persistUserMatrix = (next: any) => {
    if (!currentUser) return;
    setPref("userMatrix", {
      ...(prefs.userMatrix && typeof prefs.userMatrix === "object"
        ? prefs.userMatrix
        : {}),
      [currentUser]: next,
    });
  };
  const userMatrixSetQuadrant = (tkId: any, quadrant: any) => {
    const next = { ...userMatrix, [tkId]: quadrant };
    setUserMatrix(next);
    setEisenResetSnap(null);
    _persistUserMatrix(next);
  };
  const userMatrixReset = () => {
    setEisenResetSnap(userMatrix);
    setUserMatrix({});
    _persistUserMatrix({});
  };
  const userMatrixUndoReset = () => {
    if (!eisenResetSnap) return;
    setUserMatrix(eisenResetSnap);
    _persistUserMatrix(eisenResetSnap);
    setEisenResetSnap(null);
  };
  const [partiesLocked, setPartiesLocked] = useState<any>(true);
  const [lastCaseCtx, setLastCaseCtx] = useState<any>(null);
  const [undoEntry, setUndoEntry] = useState<any>(null);
  const undoTRef = useRef<any>(null);
  const [tkCompletePend, setTkCompletePend] = useState<any>(null);
  const [subtaskDoneAsk, setSubtaskDoneAsk] = useState<any>(null);
  const [caTesel, setCaTesel] = useState<any>(new Set());
  const [partiesOpen, setPartiesOpen] = useState<any>({});
  const [tkDelConfirm, setTkDelConfirm] = useState<any>(null);
  const [dTeP, setDTeP] = useState<any>({
    open: true,
    my: true,
    lim: 10,
    all: false,
  });
  const [tePeriod, setTePeriod] = useState<any>("week");
  const [navGrp, setNavGrp] = useState<any>({
    persons: false,
    collabs: false,
    clients: false,
    opponents: false,
    third: false,
  });
  const sideCol =
    prefs.sidebarCollapsed != null
      ? prefs.sidebarCollapsed
      : typeof window !== "undefined" && window.innerWidth < 900;
  const setSideCol = (v: any) =>
    setPref(
      "sidebarCollapsed",
      typeof v === "function"
        ? v(prefs.sidebarCollapsed != null ? prefs.sidebarCollapsed : false)
        : v,
    );
  const theme = Number.isFinite(prefs.theme) ? prefs.theme : null;
  const setTheme = (v: any) =>
    setPref("theme", typeof v === "function" ? v(theme) : v);
  // Phase 44 D6 — durable per-user "Personal" slot pick. Separate from `theme`
  // (the active selection) exactly as the firm's customThemeId is separate from
  // the active slot: selecting Day/Night/Custom/Auto changes what's ACTIVE but
  // never erases this stored pick. Lives in Collaborator.preferences (no schema
  // / authz change — PATCH /me/preferences deep-merges arbitrary keys).
  const personalTheme = Number.isFinite(prefs.personalTheme)
    ? prefs.personalTheme
    : null;
  const setPersonalTheme = (v: any) =>
    setPref("personalTheme", typeof v === "function" ? v(personalTheme) : v);
  const textSize = prefs.textSize || "auto";
  const setTextSize = (v: any) =>
    setPref(
      "textSize",
      typeof v === "function" ? v(prefs.textSize || "auto") : v,
    ); /* M+3.6 C15 — auto-night-switch is per-user again (revert C10's
   firm-wide design). Storage: Collaborator.preferences JSON. */
  const autoNightSwitch = !!prefs.autoNightSwitch;
  const setAutoNightSwitch = (v: any) =>
    setPref(
      "autoNightSwitch",
      typeof v === "function" ? v(autoNightSwitch) : !!v,
    );
  const dayStart =
    typeof prefs.dayStart === "string" ? prefs.dayStart : "07:00";
  const setDayStart = (v: any) =>
    setPref("dayStart", typeof v === "function" ? v(dayStart) : v);
  const nightStart =
    typeof prefs.nightStart === "string" ? prefs.nightStart : "19:00";
  const setNightStart = (v: any) =>
    setPref("nightStart", typeof v === "function" ? v(nightStart) : v);
  const [sessionTheme, setSessionTheme] = useState<any>(null);
  const [sessionAuto, setSessionAuto] = useState<any>(null);
  const [autoTick, setAutoTick] = useState<any>(0);
  useEffect(() => {
    const eff = autoNightSwitch || sessionAuto === true;
    if (!eff) return;
    const t = setInterval(() => setAutoTick((x: any) => x + 1), 60000);
    return () => clearInterval(t);
  }, [
    autoNightSwitch,
    sessionAuto,
  ]); /* M+3.6 C14 — zoom-based whole-app scaling, with viewport heights
   compensated via --ui-zoom so 100dvh chains stay viewport-bounded.
   The bare `zoom: X` approach in C11 broke because body/#root/.A
   all use height:100dvh and rendered to ~X*viewport device px,
   clipping .SF past visible area. Now App.css uses
   height:calc(100dvh / var(--ui-zoom, 1)) so the rendered chain
   exactly fills the viewport at any zoom. --base-font-size stays
   pinned at 13px (medium baseline) so the var-based CSS rules are
   stable; zoom does all the scaling. */
  useEffect(() => {
    const map = TEXT_SIZE_ZOOM; // 151E §2(b) — one copy, shared with POPOUT_ZOOM
    /* Round 13 (C79) — Auto PREFERS FIT: start from the screen-width pick as
       before, then step DOWN one size at a time while the zoomed viewport
       leaves the main lists less than MIN_EFF_H effective CSS px of height
       (innerHeight / zoom < 720 → primary content would hide behind scroll);
       generous viewports keep the larger size, "small" is the floor, and
       manual choices never enter this branch. Deterministic: same screen +
       window size → same answer (recomputed on window resize). */
    const autoPick = () => {
      const sw = (window.screen && window.screen.width) || 0;
      const ORDER = ["small", "medium", "large", "xlarge"];
      const MIN_EFF_H = 720;
      let i = ORDER.indexOf(
        sw >= 1500 ? "medium" : sw >= 1280 ? "large" : "xlarge",
      );
      const ih = window.innerHeight || 0;
      while (i > 0 && ih > 0 && ih / map[ORDER[i]] < MIN_EFF_H) i--;
      return ORDER[i];
    };
    let lastSW = (window.screen && window.screen.width) || 0;
    let lastSX = window.screenX || window.screenLeft || 0;
    let lastEff: any = null;
    const apply = () => {
      const eff = textSize === "auto" ? autoPick() : textSize;
      if (eff === lastEff) return;
      lastEff = eff;
      const z = map[eff] || 1;
      const r = document.documentElement;
      r.style.zoom = z === 1 ? "" : String(z);
      r.style.setProperty("--ui-zoom", String(z));
      r.style.setProperty("--base-font-size", "13px");
    };
    apply();
    if (textSize !== "auto") return;
    const onResize = () => apply();
    window.addEventListener("resize", onResize);
    const poll = setInterval(() => {
      const sw = (window.screen && window.screen.width) || 0;
      const sx = window.screenX || window.screenLeft || 0;
      if (sw !== lastSW || sx !== lastSX) {
        lastSW = sw;
        lastSX = sx;
        apply();
      }
    }, 2000);
    return () => {
      window.removeEventListener("resize", onResize);
      clearInterval(poll);
    };
  }, [textSize]);
  const [sideMob, setSideMob] = useState<any>(false);
  /* Phase 29 Stage-3 C24 — the Persons and Theme popovers are CENTERED
     overlays (the same chrome as the Search popover: blurred .OV backdrop +
     .PopPanel), no longer bar-anchored flyouts. They work identically from
     the expanded bar, the collapsed rail AND the <600px drawer (the old
     drawer-mode vertical fallback is gone). Identical open/close set for
     all three popovers: Escape (hotkey chain), outside-click, and close on
     resize / sidebar collapse / drawer change. */
  const [sideFly, setSideFly] = useState<any>(null); // {id:'theme'|'persons'}
  const openSideFly = (id: any) =>
    setSideFly((p: any) => (p && p.id === id ? null : { id }));
  /* (The shared resize/drawer close effects live below, after searchDlg's
     declaration — its deps reference would hit the TDZ up here.) */
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < 600) {
        setSideCol(true);
        setSideMob(false);
      } else if (w < 900) {
        setSideCol(true);
      }
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    const apply = () => {
      const standalone =
        (window.matchMedia &&
          window.matchMedia("(display-mode: standalone)").matches) ||
        (window.navigator as any).standalone === true;
      const _pn = t("app.product_name", {
        fallback: "Legal Practice Manager",
      });
      document.title = standalone
        ? firmName || _pn
        : firmName
          ? firmName + " — " + _pn
          : _pn;
    };
    apply();
    const mq =
      window.matchMedia && window.matchMedia("(display-mode: standalone)");
    if (!mq) return;
    const l = () => apply();
    try {
      mq.addEventListener("change", l);
    } catch {
      mq.addListener(l);
    }
    return () => {
      try {
        mq.removeEventListener("change", l);
      } catch {
        mq.removeListener(l);
      }
    };
  }, [firmName, lang]);
  useEffect(() => {
    let m = document.querySelector('meta[name="theme-color"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "theme-color");
      document.head.appendChild(m);
    }
    /* Stage-3 C22 — the admin window-bar color override (FirmSettings)
       outranks the theme-derived surface. 204 §2.3 [MERGE]: the per-user
       accent that stood between the two is gone — see `themeColor` above. */
    m.setAttribute(
      "content",
      firmSettings?.windowBarColor || CT.c_4A7CFF,
    );
  }, [firmSettings?.windowBarColor]);
  /* Stage-3 C22 — admin-configurable document title (tab text + installed
     app window bar); falls back to the firm name. The pop-out sync effect
     mirrors it into the pop-out document. */
  useEffect(() => {
    document.title =
      firmSettings?.windowTitle ||
      firmName ||
      t("app.product_name", { fallback: "Legal Practice Manager" });
  }, [firmSettings?.windowTitle, firmName, lang]);
  /* M+3.6 C15 — sidebar 4-toggle (Day/Night/Custom/Auto). Renderer
   combines push, auto-switch, and per-session escape. During push,
   default is customThemeId; user picks override via sessionTheme
   (one of the slots) or sessionAuto (time-based). Outside push,
   prefs.autoNightSwitch + prefs.theme drive everything. */
  useEffect(() => {
    const r = document.documentElement;
    if (!themes.length) return;
    const now = new Date();
    const pushActive =
      firmSettings?.customPushUntil &&
      new Date(firmSettings.customPushUntil) > now;
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Brussels",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(now);
    const hh = parts.find((p) => p.type === "hour")!.value;
    const mm = parts.find((p) => p.type === "minute")!.value;
    const cur = `${hh}:${mm}`;
    const isDay = cur >= (dayStart || "07:00") && cur < (nightStart || "19:00");
    const effAuto =
      pushActive && sessionAuto != null ? sessionAuto : autoNightSwitch;
    let themeId;
    if (pushActive) {
      if (effAuto)
        themeId = isDay ? firmSettings?.dayThemeId : firmSettings?.nightThemeId;
      else
        themeId =
          sessionTheme != null ? sessionTheme : firmSettings?.customThemeId;
    } else if (effAuto) {
      themeId = isDay ? firmSettings?.dayThemeId : firmSettings?.nightThemeId;
    } else {
      themeId = Number.isFinite(prefs.theme)
        ? prefs.theme
        : firmSettings?.nightThemeId;
    }
    const active =
      themes.find((t: any) => t.id === themeId) ||
      themes.find((t: any) => t.id === firmSettings?.nightThemeId) ||
      themes[0];
    if (!active) return;
    const vars = deriveThemeVars(active.vars || {});
    Object.entries(vars).forEach(([k, v]) => r.style.setProperty(k, v));
    /* 204 §A.1 [RULED — Laurent, 2026-09-22: "One firm-level main colour,
       taken from the Day theme, emitted once on the root as its own var, read
       by the wordmark and the documents."] — `--firm-brand`. The vars written
       above are the ACTIVE theme's, and the active theme may be `prefs.theme`
       — a per-user pick — so the `--brand` among them is PERSONAL: 203-glance-2
       §B painting the firm's name from it gave a reader on a personal theme a
       firm colour of their own (and the compact column's `.CColT` read a third
       source, `--ac`). The firm's colour is the value the documents already
       take — `deriveDocumentPalette(dayThemeVarsOf(themes, firmSettings)).brand`,
       the DAY theme's stored `--brand`, else its accent (203-glance §C.1) — the
       very call App hands the two exporting views, so the wordmark and the
       documents cannot drift by construction. Set here, in the same effect as
       the vars, removed in the same cleanup; `:root` carries its baseline
       (App.css, beside `--brand`) for the first paint before this effect runs. */
    const firmBrand = deriveDocumentPalette(dayThemeVarsOf(themes, firmSettings)).brand;
    r.style.setProperty("--firm-brand", firmBrand);
    document.body.classList.toggle(
      "theme-dark",
      isDarkTheme(active.vars || {}),
    );
    /* 109 §6 — no `color-scheme` existed anywhere in the tree, so native
       <select> popups followed the OS: black popups inside the light day theme
       on dark macOS. The theme pipeline now declares it FROM THE THEME'S OWN
       GROUND — the same `isDarkTheme` luminance call the theme-dark class
       reads (themeDerivation.ts:303, --bs relative luminance < 0.5) — so
       day→light, night→dark, and any custom theme follows its own surface.
       This IS the "white by day, black by night" default Laurent described; a
       per-theme manual override would need a column the Theme row does not
       have (vars is the CSS map, there is no meta), so it is not smuggled in
       here — enumerated in the PR. */
    r.style.colorScheme = isDarkTheme(active.vars || {}) ? "dark" : "light";
    const _mtc = document.querySelector('meta[name="theme-color"]');
    /* Stage-3 C22 — theme-derived bar color only when no admin override. */
    if (_mtc && vars["--bs"] && !firmSettings?.windowBarColor)
      _mtc.setAttribute("content", vars["--bs"]);
    return () => {
      Object.keys(vars).forEach((k) => r.style.removeProperty(k));
      r.style.removeProperty("--firm-brand"); // 204 §A.1 — symmetric with the vars
      r.style.removeProperty("color-scheme"); // §6 — symmetric with the vars
    };
  }, [
    prefs.theme,
    themes,
    firmSettings,
    autoNightSwitch,
    sessionTheme,
    sessionAuto,
    dayStart,
    nightStart,
    autoTick,
  ]);
  // The `<link id="lx-font-link">` this used to inject into <head> — a second
  // request to fonts.googleapis.com, on top of the @import at the head of
  // App.css — is gone. Both families are declared in src/fonts.css and served
  // off this box, so choosing one is now nothing but a change of CSS value.
  useEffect(() => {
    const ff = appFontStack(appFont);
    document.documentElement.style.setProperty("--app-font", ff);
    document.body.style.fontFamily = ff;
    const root = document.getElementById("root");
    if (root) root.style.fontFamily = ff;
  }, [appFont]);
  const toggleSideCol = () => {
    const w = window.innerWidth;
    if (w < 600) {
      if (sideMob) {
        setSideMob(false);
      } else {
        setSideMob(true);
      }
      return;
    }
    const nv = !sideCol;
    setSideCol(nv);
  };
  const [chatOpen, setChatOpen] = useState<any>(true);
  const [chatMsgs, setChatMsgs] = useState<any>({});
  const [chatInput, setChatInput] = useState<any>("");
  const [chatType, setChatType] = useState<any>("general");
  const [chatSearch, setChatSearch] = useState<any>("");
  const [chatMention, setChatMention] = useState<any>(false);
  const [chatMentionQ, setChatMentionQ] = useState<any>("");
  const chatRef = useRef<any>(null);
  const [chatRead, setChatRead] = useState<any>({});
  const [gChat, setGChat] = useState<any>([]);
  const [gChatInput, setGChatInput] = useState<any>("");
  const [gChatType, setGChatType] = useState<any>("general");
  const [gChatSearch, setGChatSearch] = useState<any>("");
  const [gChatMention, setGChatMention] = useState<any>(false);
  const [gChatMentionQ, setGChatMentionQ] = useState<any>("");
  const gChatRef = useRef<any>(null);
  const [gChatLinkCase, setGChatLinkCase] = useState<any>("");
  /* 132 §1 — WHICH LIST `gChatLinkCase` came out of. It cannot be inferred:
     Case and CollectiveCase ids come from different sequences (131 §6), so id
     7 may name one of each. `gChatCaseQ` was here and is gone — it was the
     hand-rolled type-ahead's query, this file never wrote it, and MatterPicker
     owns that state for a caller that does not pass it. */
  const [gChatLinkKind, setGChatLinkKind] = useState<any>("");
  const [sideChat, setSideChat] = useState<any>(false);
  const sideChatRef = useRef<any>(null);
  const [sideCaseChat, setSideCaseChat] = useState<any>(true);
  const [mailDlg, setMailDlg] = useState<any>(null);
  const emailClient = prefs.emailClient || "mailto";
  const setEmailClient = (v: any) =>
    setPref("emailClient", typeof v === "function" ? v(emailClient) : v);
  /* 168B §1f (RULING 2) — the two services an entity list's contact cells
     reach for, wired here because this is where the firm's mail-client
     preference and the toast live. Named seams, not direct imports at the
     cell: `recordings/57c-entitylist-freeze` drives every host `onClick` in
     the rendered tree, and neither `window.open` nor `navigator.clipboard`
     is something that harness can stub. */
  const composeTo = (to: string) => openCompose({ to }, emailClient);
  const copyText = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast(t("common.copied", { fallback: "Copied" })))
      .catch(() =>
        toast(
          t("toast.clipboard_denied", {
            fallback: "\u26a0 Clipboard access denied \u2014 paste manually",
          }),
        ),
      );
  };
  const mtgApp = prefs.mtgApp || "ics";
  const setMtgApp = (v: any) =>
    setPref("mtgApp", typeof v === "function" ? v(mtgApp) : v);
  const mtgPlatforms = prefs.mtgPlatforms || {
    teams: true,
    zoom: false,
    zoomUrl: "",
    gmeet: false,
  };
  const setMtgPlatforms = (v: any) =>
    setPref("mtgPlatforms", typeof v === "function" ? v(mtgPlatforms) : v);
  /* BRIEF 124 §4 — RESOLVED FROM THE FIRM'S OWN SETTINGS, FALLING BACK TO
     EMPTY. This read
       `prefs.mtgDefaultLocation || "WATT Legal - Avenue Louise / …"`
     and the literal was the third instance in this slice of one firm's data
     living in the product: a second firm inherited WATT's street as its default
     meeting location on every new event, and it travelled into the invitation.
     The user's own preference still wins; below it the firm's address, composed
     once in `firmDefaultLocation`; below that "", never somebody else's. */
  const mtgDefLoc =
    prefs.mtgDefaultLocation || firmDefaultLocation(firmSettings?.firm, lang); // 207 §12 — the reader's variant
  const setMtgDefLoc = (v: any) =>
    setPref("mtgDefaultLocation", typeof v === "function" ? v(mtgDefLoc) : v);
  const [doodleDlg, setDoodleDlg] = useState<any>(null);
  const pollTool = prefs.pollTool || "doodle";
  const setPollTool = (v: any) =>
    setPref("pollTool", typeof v === "function" ? v(pollTool) : v);
  const pollCustomUrl = prefs.pollUrl || "";
  const setPollCustomUrl = (v: any) =>
    setPref("pollUrl", typeof v === "function" ? v(pollCustomUrl) : v);
  const [notifOpen, setNotifOpen] = useState<any>(false);
  /* 151E §1(b) — the bell panel's scroll affordance (attachKey: the panel
     mounts only while open). */
  const bellHint = useScrollHint(notifOpen);
  /* 81 §7 — THE PREVIOUS SIGN-IN, and the failures since it. Held here because
     it renders in the bell popup below; fetched ONCE per session rather than
     per open, because it is a fact about the moment this session started and
     re-reading it would only make it drift towards "now" — which is the classic
     bug §7 names. */
  const [signInSummary, setSignInSummary] = useState<AuthAttemptSummary | null>(null);
  /* 81 §7 / register 81f — HAS THE ANSWER ARRIVED YET, as distinct from "the
     answer is no". The daily-overview guard below now opens the modal on an
     otherwise-empty morning when `failedSince > 0`, and without this flag a
     null summary is indistinguishable from a zero count: the guard would spend
     the day (`setDailyOverview(false)`, and `lx:dailylast` is stamped at
     :4552 BEFORE the modal ever renders) a moment before the count arrived.
     It would have worked whenever the fetch won the race and failed silently on
     a slow morning — which is likelier to be exactly the morning that matters.
     Set in `finally`, so a FAILED fetch also resolves to "asked, no alarm"
     rather than holding the modal open forever. */
  const [signInChecked, setSignInChecked] = useState(false);
  /* Phase 31 (C2) — internal calls. `calls` is the log (server-shaped rows),
     `incomingCalls` the live ring stack (ephemeral SSE payloads + a local
     deadline for the visual countdown), `callPanelOpen` the phone dropdown.
     prefs.callsSeenAt drives the RED missed-calls badge (opening the panel
     stamps it). */
  const [callPanelOpen, setCallPanelOpen] = useState<any>(false);
  const [calls, setCalls] = useState<any>([]);
  const [incomingCalls, setIncomingCalls] = useState<any>([]);
  const [callBusy, setCallBusy] = useState<any>(false);
  const [callDeclineFor, setCallDeclineFor] = useState<any>(null); // callId showing the decline time picker
  /* 31.1 (item 6) — the caller-side waiting modal: {callId} of MY live
     outgoing call in THIS tab. The modal itself derives everything else
     from the live `calls` row (the same persistent 'call' events that feed
     the log), so abort/decline/timeout/answer all resolve it with no extra
     wiring. Per-tab by design: only the initiating tab waits. */
  const [outgoingCall, setOutgoingCall] = useState<any>(null);
  const [presenceIds, setPresenceIds] = useState<any>([]);
  /* Corr (item 7) — true once the first /presence pull resolves, so the calls
     panel can tell "not loaded yet" from "genuinely offline". */
  const [presenceLoaded, setPresenceLoaded] = useState<any>(false);
  const callBtnRef = useRef<any>(null);
  // Live co[] for SSE handler closures (the stream effect captures its
  // mount-time scope; the ref always points at the current array).
  const coLiveRef = useRef<any>([]);
  /* Phase 39 — in-app WebRTC call. The live media session lives in a ref so the
     SSE handler closures always reach the current one; callWin {callId} marks
     the floating window open; callView is its render snapshot (set by the
     session's onChange); callMediaErr is the camera/mic acquisition failure.
     meRef / callsRef are stable indirections for the same closures. */
  const rtcSessionRef = useRef<any>(null);
  // Phase 42 (Lever C) — callId of the session currently PRE-WARMING during a
  // ring (media-less, not yet accepted); null once accepted or torn down.
  const rtcPrewarmRef = useRef<any>(null);
  // Phase 42 — the ring-time pre-warmed camera (getUserMedia kicked off ON the
  // ring so the device is warm before accept). Held OFF the session stream and
  // OFF every sender until accept — NOT transmitted to the caller and NOT shown
  // in the recipient's self-view. Stores the acquireLocalMedia() promise;
  // consumed by accept, released by endCallSession on decline/timeout/cancel.
  const rtcPrewarmMediaRef = useRef<any>(null);
  const [callWin, setCallWin] = useState<any>(null);
  const [callView, setCallView] = useState<any>(null);
  const [callMediaErr, setCallMediaErr] = useState<any>(null);
  /* 72 — SCREEN SHARE: A SECOND SESSION, DELIBERATELY BESIDE THE FIRST.
     `rtcShareRef` holds the LEG's media session — its own RTCPeerConnection per
     peer, on its own callId. The conversation's session (`rtcSessionRef` above)
     is never touched by any of it: no track is added to it, and it is never
     renegotiated. That is the whole reason §2 rules a share as a second CALL
     rather than a second track — a renegotiation would put the conversation on
     the path whose DTLS failure brief 70 part four fixed with its cause still
     not established. If the share never connects, you still have the call.
     `shareLeg` is the leg as the server published it ({id, sharerId,
     participantIds}); `shareView` is the leg session's render snapshot;
     `shareStreamRef` holds the getDisplayMedia capture so its tracks can be
     stopped — a display track the page forgets keeps the browser's "sharing"
     indicator up over a share that has ended. */
  const rtcShareRef = useRef<ReturnType<typeof createCallSession> | null>(null);
  const shareStreamRef = useRef<MediaStream | null>(null);
  /* 74y — THE PICKER'S OWN MOST RECENT PICK, RECORDED AS A VALUE. Two properties
     matter and both are deliberate. It is a plain track ID, not a track or a
     stream, so a STALE READER CAN BE NAMED rather than followed: a live reference
     would be re-read through whatever object currently holds it and would agree
     with itself. And it is taken at the instant `getDisplayMedia` RESOLVES —
     before any session exists — so it is the one fact in the system that is
     independent of every session object, which is what lets it tell a stale
     session from a wrong track. `sessionTag` is filled in immediately after the
     session for this capture is built, so the pair names what SHOULD be on screen. */
  const sharePickedRef = useRef<{ trackId: string | null; sessionTag: string | null }>({ trackId: null, sessionTag: null });

  /* ── 74y (addendum) — COUNT THE LIVE CAPTURES BEFORE ANYTHING ELSE ───────────
     Laurent's new observation moves the question below the app: Chrome BRINGS THE
     CAPTURED SURFACE FORWARD at capture start, a page cannot do that, and the
     window it raised was the wrong one while the receiver had the right one. A
     page holding one capture cannot produce that. Two live captures can — Chrome
     raises the surface of a capture nobody ended, while the wire carries the new
     one — and then the divergence is in WHAT IS BEING CAPTURED, not in which
     object a surface reads, and no amount of identity work touches it.
     There is no browser API that enumerates a page's display captures, so this
     keeps the register: every track `getDisplayMedia` ever returned in this page's
     life, reported with its `readyState` on demand. **MORE THAN ONE `live` IS THE
     WHOLE SYMPTOM** and the fix is teardown rather than identity.
     Deliberately a REGISTER and not a live query: an ended track still answers
     `readyState`, so a capture that was stopped is visible AS stopped rather than
     absent, and "it was cleaned up" is distinguishable from "it was never here".
     Bounded at 20 so a long session cannot grow it without limit; ended tracks are
     inert and hold no device. Counts only what THIS page obtained since load, so a
     reload resets it — which is worth saying, because a count of one after a
     reload proves nothing about the share before it. */
  const _capturesRef = useRef<{ seq: number; id: string; track: MediaStreamTrack; at: string }[]>([]);
  const _captureCensus = (when: string) => {
    const all = _capturesRef.current;
    const live = all.filter((c) => c.track.readyState === "live");
    const rows = all.map((c) => `#${c.seq} ${c.id} ${c.track.readyState}${c.track.readyState === "live" ? " ←" : ""} (acquired ${c.at})`);
    const head = `[lxcall 74y-census ${when}] ${all.length} display capture(s) this page has taken, ${live.length} still live`;
    if (live.length > 1) {
      console.warn(`${head} — MORE THAN ONE IS LIVE. Chrome raises the surface of a capture nobody `
        + `ended, so this alone explains a wrong window being brought forward while the wire carries `
        + `the right one. This is a TEARDOWN defect, not an identity one.\n  ${rows.join("\n  ")}`);
    } else {
      console.info(`${head}\n  ${rows.join("\n  ") || "(none)"}`);
    }
  };
  const _registerCapture = (stream: MediaStream) => {
    for (const t of stream.getVideoTracks()) {
      _capturesRef.current.push({
        seq: _capturesRef.current.length + 1,
        id: t.id,
        track: t,
        at: new Date().toISOString().slice(11, 23),
      });
    }
    if (_capturesRef.current.length > 20) _capturesRef.current = _capturesRef.current.slice(-20);
  };
  const [shareLeg, setShareLeg] = useState<ShareLeg | null>(null);
  const [shareView, setShareView] = useState<ShareView | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  /* 73 §2 — A LEG THAT WILL NOT CONNECT MUST SAY SO AND STOP PRETENDING.
     `shareLost` is the one part of that a snapshot cannot carry: the transport
     removes a peer that has been `failed` for 9 s, and an empty peer list is
     then indistinguishable from "the sharer left" — which is what #357 assumed
     and silently closed the tile on. It is a FAILURE, kept on screen with a
     retry, and it is cleared only by the server actually ending the leg. The
     other two failing shapes (`stalled`, `mediaSilent`) ride the snapshot and
     need no state here. */
  const [shareLost, setShareLost] = useState(false);
  /* 74l (b) — THE CALL SURFACE'S INTRINSIC SIZE, as it measures itself in the TAB.
     The pop-out opens at this rather than at a constant: the window is sized to the
     surface, never the surface to the window. A ref and not state — nothing renders
     from it, `openCallPopOut` reads it, and putting it in state would re-render the
     call window on every measurement it just reported. */
  const callSurfaceSizeRef = useRef<{ width: number; height: number } | null>(null);
  const meRef = useRef<any>(currentUser);
  meRef.current = currentUser;
  const callsRef = useRef<any>([]);
  /* Corr-3 (item 11) — incoming-call OS desktop notification, CENTRALIZED +
     INSTRUMENTED. The original inline block (in the callRing handler) is
     logically correct and matches the working Pomodoro pattern, so a blind
     rewrite would not help — the failure is in a layer the code can't see from
     here. This helper logs each gate so the FAILING LAYER is visible in the
     backgrounded tab's DevTools console (which keeps running for a backgrounded,
     non-frozen tab):
       • "[ringNotif] … handler reached" missing  → the callRing SSE event never
         ran the handler (tab frozen, or the unbuffered ephemeral was dropped
         during a background-throttled reconnect — it has no replay).
       • backgrounded=false                       → focus/visibility gate.
       • permission!="granted"                    → site permission state.
       • "constructed" logged but no banner shows  → OS-level suppression (e.g.
         macOS System Settings ▸ Notifications ▸ Chrome is OFF even though the
         SITE permission reads "granted").
     It is also called from the DURABLE 'call' create event (which REPLAYS on
     reconnect), so a ring dropped on the ephemeral path still notifies once the
     stream catches up. De-duped by callId + by the notification tag. */
  const ringNotifSentRef = useRef<Set<number>>(new Set());
  const maybeRingNotification = (callId: any, initiatorId: any, startedAt: any) => {
    try {
      if (callId == null) return;
      if (ringNotifSentRef.current.has(Number(callId))) {
        console.debug("[ringNotif] skip: already sent for call", callId);
        return;
      }
      // Stale-replay guard: a durable create event replayed long after the ring
      // ended still says status:ringing — don't notify for a dead call.
      if (startedAt) {
        const age = Date.now() - new Date(startedAt).getTime();
        if (Number.isFinite(age) && age > 45000) {
          console.debug("[ringNotif] skip: stale ring", callId, Math.round(age / 1000) + "s");
          return;
        }
      }
      const hidden = typeof document !== "undefined" && document.hidden;
      const unfocused =
        typeof document !== "undefined" &&
        typeof document.hasFocus === "function" &&
        !document.hasFocus();
      const backgrounded = hidden || unfocused;
      const supported = typeof window !== "undefined" && "Notification" in window;
      const permission = supported ? Notification.permission : "unsupported";
      console.debug(
        `[ringNotif] handler reached: call=${callId} hidden=${hidden} unfocused=${unfocused} backgrounded=${backgrounded} supported=${supported} permission=${permission}`,
      );
      if (!backgrounded) {
        console.debug("[ringNotif] skip: LexCRM is focused (in-app card covers it)");
        return;
      }
      if (!supported || permission !== "granted") {
        console.debug("[ringNotif] skip: notifications not available/granted →", permission);
        return;
      }
      /* 208 §14 — ONE BANNER PER CALL, PER BROWSER: where this browser holds a
         push subscription the server delivers to (src/lib/webPush.ts
         `callPushHeld`, learned at sign-in), the push's banner is the one
         (public/sw.js) and the page posts none; where it holds none, the page
         posts its own, as before. Marked before the answer, so the durable
         event arriving beside the ephemeral one cannot post a second. */
      ringNotifSentRef.current.add(Number(callId));
      void callPushHeld().then((held) => {
        if (held) {
          globalThis.console.debug("[ringNotif] skip: this browser holds a push subscription — the push's banner is the one", callId);
          return;
        }
        try {
          const caller = (coLiveRef.current || []).find(
            (c: any) => Number(c.id) === Number(initiatorId),
          );
          const callerName = caller ? pname(caller) : t("calls.someone", { fallback: "Someone" });
          const ringNotif = new Notification(
            t("calls.ring_notif_title", {
              name: callerName,
              fallback: `${callerName} is calling…`,
            }),
            {
              body: t("calls.ring_notif_body", {
                fallback: "Incoming call — open LexCRM to answer.",
              }),
              tag: `lx-call-${callId}`,
            },
          );
          console.debug("[ringNotif] constructed Notification for call", callId);
          ringNotif.onclick = () => {
            try {
              window.focus();
            } catch {
              /* ignore */
            }
            ringNotif.close();
          };
        } catch (e) {
          ringNotifSentRef.current.delete(Number(callId));
          globalThis.console.warn("[ringNotif] error constructing notification:", e);
        }
      });
    } catch (e) {
      console.warn("[ringNotif] error constructing notification:", e);
    }
  };
  /* Corr-4 (PART B) — DURABLE ring re-present. The in-app ring CARD was added
     only by the ephemeral callRing, which has no replay; a recipient with any
     socket gap at the call instant missed the entire live alert (no card, no
     banner) and the call silently became missed. This re-presents the ring —
     CARD (setIncomingCalls) AND banner (maybeRingNotification) — from any
     durable source: the ring-buffered 'call' event (replayed on reconnect) and
     the /calls fetch (mount / reconnect / push-opened tab). Validity-guarded:
     only a call still RINGING for me and still inside its ~40s window, with the
     deadline computed from the server's startedAt (not receive time), so an
     expired/resolved call is never re-rung. Recipient-correct: the participant
     check + the hub's per-recipient gate mean only the actual callee ever sees
     it. Supersedes the 8b9574b banner-only refire. */
  const RING_WINDOW_MS = 40000; // matches server RING_SECONDS
  const presentRingIfActive = (call: any) => {
    try {
      if (!call || call.status !== "ringing" || call.id == null) return;
      const me = Number(meRef.current);
      const mine = (call.participants || []).find(
        (p: any) => Number(p.collaboratorId) === me,
      );
      if (!mine || mine.outcome !== "ringing") return;
      const started = call.startedAt ? new Date(call.startedAt).getTime() : Date.now();
      const deadline = started + RING_WINDOW_MS;
      if (!(deadline > Date.now())) return; // already expired
      setIncomingCalls((prev: any) =>
        prev.some((c: any) => c.callId === call.id)
          ? prev
          : [
              ...prev,
              {
                callId: call.id,
                initiatorId: call.initiatorId,
                isCollective: call.isCollective,
                subject: call.subject,
                deadline,
              },
            ],
      );
      // Banner too (self-gates: only when backgrounded, deduped by callId+tag).
      maybeRingNotification(call.id, call.initiatorId, call.startedAt);
    } catch (e) {
      console.warn("[ring] presentRingIfActive error:", e);
    }
  };
  const callsSeenAt = prefs.callsSeenAt || "";
  const setCallsSeenAt = (v: any) => setPref("callsSeenAt", v);
  /* Phase 31 (C3) — alert toasts from the server alert engine (T-15 / T-0 /
     reminder fields). Show-once, never replayed (the SSE leg is ephemeral too).
     Brief 35 (stage 3) — "never persisted to the bell" NO LONGER HOLDS and
     that is the point: the engine now writes a durable Notification inside
     its FiredAlert claim, so this toast is an ECHO of a row that exists
     before it fires and still exists after it fades.
     Brief 42 §0 — RULED by Laurent, 2026-08-06, watching stage 3 run: "It is
     an interesting way to force people to check the Bell: the annoyance of the
     popup is on purpose." AN ALERT TOAST NO LONGER EXPIRES. The only way to
     clear one is to open the Bell, which makes the user glance at everything
     in there on the way past. The annoyance IS the mechanism, not a side
     effect of one — do not soften it.
     THE SWEEPER STAYS AND IS NOT VESTIGIAL: it now sweeps only rows that still
     CARRY an `expiresAt`. Alert toasts are pushed without one (App.tsx:2673), so they
     are never swept, and anything that later wants a lifetime gets one by
     setting the field. The `== null` arm is the whole ruling in one operator —
     `a.expiresAt > now` against an undefined field is false, which would have
     dropped every alert toast on the very next tick.
     THE SCOPE, because it is one line's carelessness away (brief 42 §1): this
     is the `alertToasts` state ALONE. The ordinary toasts — saved, error, every
     API failure — are a separate system (`toast()`), keep their own lifetimes
     and MUST keep expiring. A "Saved" that needed the Bell to dismiss would be
     a defect, not this ruling. */
  const [alertToasts, setAlertToasts] = useState<any>([]);
  const dismissAlertToast = (uid: any) =>
    setAlertToasts((prev: any) => prev.filter((a: any) => a.uid !== uid));
  useEffect(() => {
    if (!alertToasts.some((a: AlertToast) => a.expiresAt != null)) return;
    const iv = setInterval(() => {
      const now = Date.now();
      setAlertToasts((prev: any) =>
        prev.filter((a: any) => a.expiresAt == null || a.expiresAt > now),
      );
    }, 1000);
    return () => clearInterval(iv);
  }, [alertToasts.length]);
  /* Brief 42 §1.3 — which toast is currently showing its "why" line, by uid.
     One at a time, and clicking the same Bell again puts it away. Held here
     rather than in a per-toast component because a component declared inside
     this render body is exactly what scripts/lint-inline-components.js fails
     the build over (CLAUDE.md, Bug-A). */
  const [alertTipUid, setAlertTipUid] = useState<string | null>(null);
  const [cddReportData, setCddReportData] = useState<any>(null);
  const notifRead = Array.isArray(prefs.notifRead) ? prefs.notifRead : [];
  const setNotifRead = (v: any) =>
    setPref("notifRead", typeof v === "function" ? v(notifRead) : v);
  // Phase 19 — persisted notifications (first persisted source in the
  // bell aggregator). Fetched on mount + on bell open + on a low-rate
  // poll. Read state lives on Notification.readAt server-side; the
  // legacy `prefs.notifRead` array remains the source of truth for
  // DERIVED notifications (its IDs are derived-side prefixes like
  // "ovd-…", "st-…", never "db-…"). For persisted ones we mirror locally
  // via a separate set so re-fetches reconcile.
  const [persistedNotifs, setPersistedNotifs] = useState<any>([]);
  // 208 §15 — the bell's persisted rows have answered once (success or not): part of the bell's first load
  const [persistedAnswered, setPersistedAnswered] = useState(false);
  const fetchPersistedNotifs = useCallback(async () => {
    try {
      const rows = await api.get("/notifications/me");
      const next = Array.isArray(rows) ? rows : [];
      // Flicker fix (b): the 60s poll previously called setPersistedNotifs with a
      // fresh array every time, re-rendering the whole App (and any open modal)
      // even when nothing changed. Return the SAME reference when the payload is
      // unchanged so React bails out and no re-render fires.
      setPersistedNotifs((prev: any) => {
        if (
          prev.length === next.length &&
          prev.every((p: any, i: any) => {
            const n = next[i];
            return (
              n &&
              p.id === n.id &&
              p.readAt === n.readAt &&
              p.type === n.type
            );
          })
        ) {
          return prev;
        }
        return next;
      });
    } catch {}
    setPersistedAnswered(true);
  }, []);
  /* 147 §1.6 — TWO SWITCHES, BOTH PERSISTED, AND `0` IS A VALUE.
     `!= null` was already right here: it is the guard that lets a stored ZERO
     survive. What defeated it were the `|| 20` fallbacks at the three READERS
     (:below, CompactColumn.tsx and the header ring), which turned zero back
     into twenty and made the loop's own `if (intMs <= 0) return;` unreachable.
     They are gone; this stays.

     `pomMuted` was `useState(false)` with NO SETTER ANYWHERE IN THE TREE —
     dead state, always false, read once by the chime. It is a preference now,
     beside the interval, and it reaches Settings as a prop like every other
     personal setting on that panel. The PATCH is a free-form deep merge
     (`server/src/routes/preferences.js:38`), so a new key needs no server
     change and no migration.

     AND THE TWO DO DIFFERENT THINGS, which is the half the label has to say:
     MUTE silences the CHIME only; the desktop Notification — the thing that
     actually reached the user, with the app not in front — is stopped by
     setting the interval to 0 and by nothing else. */
  const pomInterval =
    prefs.pomodoroInterval != null ? prefs.pomodoroInterval : 20;
  const setPomInterval = (v: any) =>
    setPref("pomodoroInterval", typeof v === "function" ? v(pomInterval) : v);
  const pomMuted = prefs.pomodoroMuted === true;
  const setPomMuted = (v: boolean) => setPref("pomodoroMuted", v);
  // Phase 19 — fetch persisted notifications on mount + 60s poll + on
  // bell open. Short-circuits before auth so we don't 401 the API.
  useEffect(() => {
    if (!authed) return;
    fetchPersistedNotifs();
    const t0 = setInterval(fetchPersistedNotifs, 60000);
    return () => clearInterval(t0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, currentUser]);
  useEffect(() => {
    if (notifOpen && authed) fetchPersistedNotifs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifOpen]);
  /* 81 §7 — the previous sign-in, ONCE per authenticated session.
     Deliberately NOT on `notifOpen` like the poll above: this describes the
     moment the session started, and re-reading it on every open would let the
     "previous" login creep toward the current one as the day goes on — the
     classic bug §7 names in as many words. A failure leaves the line absent
     rather than showing a wrong date. */
  useEffect(() => {
    // `currentUser` is READ here rather than only listed, so this effect needs
    // no exhaustive-deps suppression: the "Viewing as" simulation changes the
    // identity without a reload, and the line must follow it. The first draft
    // suppressed the rule instead, the per-file ratchet refused a 20th
    // suppression in this file, and it was right to — SESSION_RULES: a ratchet
    // is obeyed, not re-recorded.
    if (!authed || !currentUser) { setSignInSummary(null); return; }
    let live = true;
    getAuthAttemptSummary()
      .then((d: AuthAttemptSummary) => { if (live) setSignInSummary(d); })
      .catch(() => { /* no line rather than a wrong one */ })
      .finally(() => { if (live) setSignInChecked(true); });
    return () => { live = false; };
  }, [authed, currentUser]);
  // Phase 30 (F) — deploy signal. The server's buildId is captured on the
  // first version check after load; when a later check (every stream open)
  // or a live 'deploy' event reports a different one, the reload banner
  // shows. Never auto-reloads (D8) — except the dead-tab chunk-failure path
  // in main.jsx.
  const [newVersion, setNewVersion] = useState<any>(null);
  const bootBuildIdRef = useRef<any>(null);
  /* BRIEF 98 §1 — THE OTHER WAY A BUILD AND A SERVER COME APART.
     `newVersion` above answers "the server moved" — it compares the server's
     buildId against the server's OWN previous answer. It is blind by
     construction to "the bundle moved and the server did not", which is the
     state staging sat in for three days while `ci-local`'s build rewrote
     nginx's document root and nothing restarted the API.
     The server now derives that itself (`isApiStale`, server/src/lib/buildId.js)
     and reports it on the same call this already makes, so there is nothing to
     compare here and nothing to remember anywhere. */
  /* BRIEF 106 §2c — AND IT READS `serverStale`, NOT `apiStale`.
     98 §1's comparison is between two views of ONE artefact — the bundle this
     process booted with against the bundle nginx serves now — and neither of
     them is the server. `ci-local` rebuilds `dist/` on every gate run because
     `bundle-ratchet` reads what the build produced, and `dist/` is nginx's
     document root, so `apiStale` went true on a CSS-only unit and asked for a
     restart that changed nothing. A warning that cannot be ignored and is
     usually unnecessary trains the reader to ignore it.
     `serverStale` is true when the code on disk is not the code this process
     loaded (`server/src/lib/buildId.js`), which is the only state a
     `pm2 restart` fixes. The state name keeps the old spelling so every reader
     below is unchanged; what moved is which field fills it. */
  const [apiStale, setApiStale] = useState(false);
  /* 157A follow-up — THE THIRD SKEW, and the one that cost a whole glance.
     `apiStale`/`serverStale` both ask whether this PROCESS is behind something.
     Neither can see a `dist/` built hours before HEAD — which is the state
     staging was in on 2026-08-31, because `.githooks/pre-push` runs
     `ci-local --lints-only` and never builds, so pushing is not a deploy here.
     Laurent glanced, reported six sections "behaving exactly as before", and
     every one had been committed after the last build.
     ITS OWN BANNER, not a widening of the one below, because the REMEDY
     differs: a stale server wants a restart, a stale bundle wants a REBUILD and
     a reload does nothing at all. Telling him to reload would waste the glance
     a second time. Server-derived (`isBundleStale`, server/src/lib/buildId.js);
     `null` means undeterminable and shows nothing — absent is never fresh. */
  const [bundleStale, setBundleStale] = useState(false);
  const [bundleDismissed, setBundleDismissed] = useState(false);
  // BRIEF 162 — is this instance a cockpit (holds OPERATOR_TARGETS)? Server
  // truth from /api/version. The Fleet nav shows only when this is true AND the
  // viewer has fleet.view — a hookless module cannot moduleId-gate the section,
  // so without this an admin of any non-cockpit tenant would see an inert Fleet.
  const [isCockpit, setIsCockpit] = useState(false);
  /* 112 §5 — the banner's per-session dismiss. MEMORY deliberately (not
     sessionStorage): the ruling requires the dismissal NOT to survive a
     reload, and sessionStorage survives every reload of the tab. */
  const [staleDismissed, setStaleDismissed] = useState(false);
  // Phase 28 (item G / CK1) — real-time push. Supplements the boot-once chat
  // load and (near-)replaces the 60s notification poll above (which stays as a
  // low-frequency fallback if the stream drops). The server's per-subscriber
  // access filter guarantees a non-member never receives a case's payload.
  // 163 §B.3 — minimal shapes for the fields these load paths actually read.
  type FirmSettingsShape = { logo?: string | null; firm?: { name?: string | null; currency?: string | null } | null; senderFolderMaps?: unknown; externalTaskTypeMaps?: unknown };
  type ChatBundleShape = { ccChatMsgs?: Record<string, unknown>; chatMsgs?: Record<string, unknown>; gChat?: unknown[] };
  useEffect(() => {
    if (!authed) return;
    const checkVersion = () => {
      (api.get("/version") as Promise<{ buildId?: string; serverStale?: boolean; bundleStale?: boolean; isCockpit?: boolean } | null>)
        .then((v) => {
          if (!v || !v.buildId) return;
          // §1 — read BEFORE the early return below: a process that has been
          // stale since before this tab opened never changes its buildId, so
          // the first answer is the only one that would ever say so.
          setApiStale(v.serverStale === true);
          setBundleStale(v.bundleStale === true);
          setIsCockpit(v.isCockpit === true);
          if (!bootBuildIdRef.current) {
            bootBuildIdRef.current = v.buildId;
            return;
          }
          if (v.buildId !== bootBuildIdRef.current) setNewVersion(v.buildId);
        })
        .catch(() => {});
    };
    checkVersion();
    const addMsg = (prev: any, key: any, m: any) => {
      const arr = prev[key] || [];
      if (arr.some((x: any) => x.id === m.id)) return prev; // dedup by server id
      return { ...prev, [key]: [...arr, m] };
    };
    /* Phase 29 (item 5) — live task/agenda/subtask/read-receipt consumption.
       Upserts are id-keyed and idempotent: the actor's own device receives
       an echo of its mutation (the POST response may even arrive AFTER the
       SSE event — s() dedupes the temp-id/server-id overlap), other devices
       and case members get the live update. archive removes the row from
       live state (boot lists exclude archived rows); unarchive re-inserts. */
    const upsertRow = (prev?: any, row?: any, extraMerge?: any) => {
      const ex = prev.find((x: any) => x.id === row.id);
      const merged = {
        ...(ex || {}),
        ...row,
        // Payloads from include-less routes (archive/unarchive) carry no
        // subtasks array — keep what we have rather than wiping it.
        subtasks: row.subtasks ?? (ex ? ex.subtasks : undefined) ?? [],
        // Round 13 (C72) — same preservation for the document list.
        docs: row.docs ?? (ex ? ex.docs : undefined) ?? [],
        ...(extraMerge || {}),
      };
      return ex
        ? prev.map((x: any) => (x.id === row.id ? merged : x))
        : [...prev, merged];
    };
    const applyEntity = (setter: any) => (p: any) => {
      const row = p && (p.task || p.event);
      if (!row || row.id == null) return;
      if (p.action === "delete" || p.action === "archive") {
        setter((prev: any) => prev.filter((x: any) => x.id !== row.id));
        return;
      }
      setter((prev: any) => upsertRow(prev, row));
    };
    const mergeSub = (list: any, st: any, action: any) => {
      const arr = Array.isArray(list) ? list : [];
      if (action === "delete") return arr.filter((x) => x.id !== st.id);
      const ex = arr.some((x) => x.id === st.id);
      const next = ex
        ? arr.map((x) => (x.id === st.id ? { ...x, ...st } : x))
        : [...arr, st];
      return next.sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.id - b.id,
      );
    };
    /* Round 13 (C71) — orphan-parent robustness for subtask events. The old
       handler could only PATCH a parent already in `tk`; a subtask event
       whose parent row is missing (e.g. a fresh delegation whose parent the
       viewer's list never carried, or an SSE replay race) was silently
       dropped — the delegatee's Dashboard/pop-out then stayed stale until a
       refresh. On a miss we pull the full parent once (subtasks + docs +
       viewer-attached case title ride the GET) and upsert it; per-id
       in-flight dedupe so an event burst fetches once. */
    const _orphanFetches = new Set();
    const fetchTaskIntoState = (taskId: any) => {
      if (_orphanFetches.has(taskId)) return;
      _orphanFetches.add(taskId);
      (getTask(taskId) as Promise<{ id?: unknown } | null>)
        .then((row) => {
          if (row && row.id != null)
            setTasks((prev: any) => upsertRow(prev, row));
        })
        .catch(() => {})
        .finally(() => _orphanFetches.delete(taskId));
    };
    /* Phase 30 (C) — debounced refetchers for poke-style events. The pokes
       are content-free by design (payload-trust rule): the GETs re-apply the
       per-viewer scoping/scrubbing, so nothing sensitive ever rides the
       stream for entities whose GET shape is viewer-dependent. */
    const _debounces = new Map();
    const debounced = (key: any, ms: any, fn: any) => {
      if (_debounces.has(key)) clearTimeout(_debounces.get(key));
      _debounces.set(key, setTimeout(() => { _debounces.delete(key); fn(); }, ms));
    };
    const _refetchList = (fn: any, set: any) => () =>
      fn().then((r: any) => {
        if (!Array.isArray(r)) return;
        set(r);
        // 210 §1 — a whole list read again is a load: a list marked not loaded loses its mark
        for (const x of _listSources.values()) if (x.set === set) markListLoaded(x.key);
      }).catch(() => {});
    const PARTY: Record<string, any> = {
      cl: [getClients, setClients], op: [getOpponents, setOpponents], ocu: [getOpponentLawyers, setOpponentLawyers],
      ex: [getExperts, setExperts], cp: [getContactPersons, setContactPersons], oc: [getOpponentContacts, setOpponentContacts],
      me: [getMediators, setMediators], ar: [getArbiters, setArbiters], op2: [getAdditionalOpponents, setAdditionalOpponents],
      ju: [getJurisdictions, setJurisdictions],
    };
    const upsertById = (set: any) => (row: any, action: any) =>
      set((prev: any) =>
        action === "delete"
          ? prev.filter((x: any) => x.id !== row.id)
          : prev.some((x: any) => x.id === row.id)
            ? prev.map((x: any) => (x.id === row.id ? { ...x, ...row } : x))
            : [...prev, row],
      );
    /* 191B §B.2b/§B.2c — A MATTER'S SERVED MONEY FOLLOWS EVERY EVENT THAT NAMES IT.
       [JUDGEMENT — advisor, 2026-09-17; the method, not a ruling.]

       Laurent: "I really had to hard refresh the app to get the updated strip,
       shouldn't that be immediate?" Measured: the strip's right three and the
       Dossiers row's seven read each matter row's served `recap` (CountersStrip,
       matterListCfg), attached by the LIST reads alone — page open and `access`.
       A payment poked `inv` and the matter; `inv` re-read the invoices only, and
       the matter poke re-read `GET /cases/:id`, which served no recap — merged
       over the held row, so the stale recap survived until a reload.

       Now: an `inv` or `te` event that NAMES a matter (`caseId`/`ccId` on the
       poke, or its `ownRow`'s — timeEntries.js publishTe), and every `case`/`cc`
       poke, re-reads THAT matter through the flagged single read (§B.2a — the
       same function and gates as the list row) and REPLACES the row the page
       holds. ONE DEBOUNCED READ PER MATTER: the `inv` + `case` pair a payment
       sends costs one read. An `inv`/`te` re-read never ADDS a row — the lists
       hold no archived matter (cases.js) while upsertById appends what it does
       not find; `case`/`cc` keep today's add-on-create (`mayAdd`). An event
       naming no matter changes no matter's summary: nothing more than today. */
    const _matterPending = new Map<string, { mayAdd: boolean }>();
    const rereadMatter = (kind: "case" | "cc", id: number, mayAdd: boolean) => {
      if (!Number.isFinite(id)) return;
      const key = kind + ":" + id;
      const pend = _matterPending.get(key) || { mayAdd: false };
      pend.mayAdd = pend.mayAdd || mayAdd;
      _matterPending.set(key, pend);
      debounced(key, 500, () => {
        const flags = _matterPending.get(key) || { mayAdd: false };
        _matterPending.delete(key);
        const read = kind === "case" ? getCaseWithLatestInvoice(id) : getCollectiveCaseWithLatestInvoice(id);
        const set = kind === "case" ? setCases : setCollectiveCases;
        (read as Promise<{ id?: unknown; archived?: unknown } | null>)
          .then((row) => {
            if (!row || row.id == null) return;
            /* 193 §E [JUDGEMENT — advisor] — THE LISTS HOLD LIVE MATTERS ONLY
               (cases.js / collectiveCases.js: the default list excludes
               `archived`), and the single read this re-read makes serves an
               archived row like any other, with its `archived` flag. So the
               flag decides here, whatever the event was: an archived matter
               LEAVES the held list (the `archive` poke a second window sends,
               or any event on a matter archived meanwhile) and is never added
               to it (an `inv`/`te` event naming an archived matter, a poke on
               one). The archive view reads its own rows at mount
               (ArchiveView.tsx `reload`, `?archived=1`); restore adds the row
               back from there, and an `unarchive` poke re-reads a live row and
               adds it as before. Add-on-create for a live matter is unchanged. */
            if (row.archived === true) {
              interface Held { id: unknown }
              const drop = (prev: Held[]) => (prev.some((x) => x.id === row.id) ? prev.filter((x) => x.id !== row.id) : prev);
              set(drop);
              return;
            }
            // 207B §5 — what was typed into this matter and not yet answered stays.
            const kept = keepTyped(kind === "case" ? SK.ca : SK.cc, row);
            set((prev: any) =>
              prev.some((x: any) => x.id === kept.id)
                ? prev.map((x: any) => (x.id === kept.id ? { ...x, ...kept } : x))
                : flags.mayAdd ? [...prev, kept] : prev,
            );
          })
          .catch(() => {});
      });
    };
    // The matter a poke names: the row's own ids, or its `ownRow`'s (the te
    // envelope carries the matter there for the hub's gate).
    const matterOf = (p: any): { kind: "case" | "cc"; id: number } | null => {
      const caseId = p?.caseId ?? p?.ownRow?.caseId ?? null;
      const ccId = p?.ccId ?? p?.ownRow?.ccId ?? null;
      if (caseId != null) return { kind: "case", id: Number(caseId) };
      if (ccId != null) return { kind: "cc", id: Number(ccId) };
      return null;
    };
    const refetchChat = () =>
      (getAllChatMessages() as Promise<ChatBundleShape | null>)
        .then((_ch) => {
          const _cc = _ch?.ccChatMsgs || {};
          const _ccKeyed = Object.fromEntries(
            Object.entries(_cc).map(([k, v]) => ["cc:" + k, v]),
          );
          setChatMsgs({ ...(_ch?.chatMsgs || {}), ..._ccKeyed });
          setGChat(_ch?.gChat || []);
        })
        .catch(() => {});
    const stop = startLiveStream({
      onChat: (p) => {
        const m = p && p.message;
        if (!m || m.id == null) return;
        const key = m.caseId != null ? m.caseId : m.ccId != null ? "cc:" + m.ccId : null;
        // Phase 30 (C) — edits/pins/deletes stream now, not just creates.
        if (p.action === "update" || p.action === "delete") {
          const patchList = (arr: any) =>
            p.action === "delete"
              ? (arr || []).filter((x: any) => x.id !== m.id)
              : (arr || []).map((x: any) => (x.id === m.id ? { ...x, ...m } : x));
          if (key != null) {
            setChatMsgs((prev: any) => ({ ...prev, [key]: patchList(prev[key]) }));
          } else {
            setGChat((prev: any) => patchList(prev));
            // 132 §1 — the fold has a second arm. `linkKey` is the store key
            // App already uses for a collective conversation (`cc:<id>`, the
            // `key` two lines above this block), so an edit or a delete of a
            // cc-linked general message reaches the copy folded into that
            // matter's feed — which is what "one row, two views" costs.
            const linkKey = m.linkedCaseId != null
              ? m.linkedCaseId
              : m.linkedCcId != null
                ? "cc:" + m.linkedCcId
                : null;
            if (linkKey != null) {
              setChatMsgs((prev: any) => ({
                ...prev,
                [linkKey]: patchList(prev[linkKey]),
              }));
            }
          }
          return;
        }
        if (m.caseId != null) {
          setChatMsgs((prev: any) => addMsg(prev, m.caseId, m));
        } else if (m.ccId != null) {
          setChatMsgs((prev: any) => addMsg(prev, "cc:" + m.ccId, m));
        } else {
          setGChat((prev: any) => (prev.some((x: any) => x.id === m.id) ? prev : [...prev, m]));
          if (m.linkedCaseId != null) {
            setChatMsgs((prev: any) => addMsg(prev, m.linkedCaseId, m));
          } else if (m.linkedCcId != null) {
            // 132 §1 — `else if`: the two link ids are exclusive by the route's
            // own invariant, so a live message can never land in two matters.
            setChatMsgs((prev: any) => addMsg(prev, "cc:" + m.linkedCcId, m));
          }
        }
      },
      onNotification: () => { fetchPersistedNotifs(); },
      /* Round 15 (C94a) — same upsert as applyEntity(sTk), plus transition
         detection: a row arriving as "inprogress" over a stored "todo"/null
         is the C82b auto-ongoing signature (timer start / task-bound time
         log). Its timestamp parks in recentAutoOngoingRef so TkStatusTag can
         drop a pill click that raced the flip (the user aimed at "To do" —
         the pill must not jump into the completion prompt). */
      onTask: (p) => {
        const row = p && p.task;
        if (!row || row.id == null) return;
        if (p.action === "delete" || p.action === "archive") {
          setTasks((prev: any) => prev.filter((x: any) => x.id !== row.id));
          return;
        }
        setTasks((prev: any) => {
          const ex = prev.find((x: any) => x.id === row.id);
          if (
            ex &&
            (ex.status === "todo" || ex.status == null) &&
            row.status === "inprogress"
          ) {
            recentAutoOngoingRef.current.set(row.id, Date.now());
            if (recentAutoOngoingRef.current.size > 64) {
              const cut = Date.now() - 60000;
              for (const [k2, v2] of recentAutoOngoingRef.current)
                if (v2 < cut) recentAutoOngoingRef.current.delete(k2);
            }
          }
          return upsertRow(prev, row);
        });
      },
      onAgenda: applyEntity(setAgendaEvents),
      onSubtask: (p) => {
        const st = p && p.subtask;
        if (!st || st.id == null) return;
        if (p.parentTaskId != null) {
          setTasks((prev: any) => {
            // C71 — parent missing from state → fetch-and-upsert fallback
            // (membership recompute follows from the upsert).
            if (!prev.some((x: any) => x.id === p.parentTaskId)) {
              fetchTaskIntoState(p.parentTaskId);
              return prev;
            }
            return prev.map((x: any) =>
              x.id === p.parentTaskId
                ? { ...x, subtasks: mergeSub(x.subtasks, st, p.action) }
                : x,
            );
          });
        } else if (p.parentEventId != null) {
          setAgendaEvents((prev: any) =>
            prev.map((x: any) =>
              x.id === p.parentEventId
                ? { ...x, subtasks: mergeSub(x.subtasks, st, p.action) }
                : x,
            ),
          );
        }
      },
      onChatRead: (p) => {
        if (!p || !p.key) return;
        const ts = p.lastReadAt ? new Date(p.lastReadAt).getTime() : 0;
        if (!ts) return;
        // Max-merge: never regress a newer local read timestamp.
        setChatRead((prev: any) =>
          (prev[p.key] || 0) >= ts ? prev : { ...prev, [p.key]: ts },
        );
      },
      /* Phase 30 (C/F) — the new event families. Row events upsert by id
         (same idempotent pattern as tasks/agenda); poke events refetch
         through the scoping/scrubbing GETs; component-local surfaces
         (places grid, schedule, CDD panels, doc tracking, bulk mailer,
         billed-emails pane) listen for the lx:sse bridge event. */
      handlers: {
        party: (p) => {
          if (!p || !p.entity) return;
          if (p.entity === "co") {
            // Collaborator rows are viewer-scrubbed (rates) → id-only poke.
            debounced("co", 400, _refetchList(getCollaborators, setCollaborators));
            return;
          }
          if (p.entity === "books") {
            if (p.row && p.row.id != null) upsertById(setBooks)(p.row, p.action);
            return;
          }
          const pair = PARTY[p.entity];
          if (!pair) return;
          if (p.action === "bulk" || !p.row || p.row.id == null) {
            debounced("party:" + p.entity, 400, _refetchList(pair[0], pair[1]));
            return;
          }
          upsertById(pair[1])(keepTyped(SK[p.entity as keyof typeof SK], p.row as { id?: unknown }), p.action); // 207B §5
        },
        te: (p) => {
          debounced("te", 600, _refetchList(getTimeEntries, setTimeEntries));
          // 191B §B.2b — the matter this entry belongs to follows (the seven).
          const m = matterOf(p); if (m) rereadMatter(m.kind, m.id, false);
        },
        inv: (p) => {
          debounced("inv", 600, _refetchList(getInvoices, setInvoices));
          // 191B §B.2b — a payment, a send, an edit: the matter's right three follow.
          const m = matterOf(p); if (m) rereadMatter(m.kind, m.id, false);
        },
        case: (p) => {
          if (!p || p.id == null) return;
          if (p.action === "delete") {
            setCases((prev: any) => prev.filter((x: any) => x.id !== p.id));
            return;
          }
          // 191B §B.2b/c — the flagged read, debounced per matter with the
          // `inv` poke a payment sends beside it; add-on-create as before.
          rereadMatter("case", Number(p.id), true);
        },
        cc: (p) => {
          if (!p || p.id == null) return;
          if (p.action === "delete") {
            setCollectiveCases((prev: any) => prev.filter((x: any) => x.id !== p.id));
            return;
          }
          rereadMatter("cc", Number(p.id), true);
        },
        config: (p) => {
          if (!p || !p.domain) return;
          if (p.domain === "themes") {
            debounced("themes", 400, () =>
              api.get("/themes").then((r: any) => Array.isArray(r) && setThemes(r)).catch(() => {}),
            );
          } else if (p.domain === "firmSettings") {
            debounced("firmSettings", 400, () =>
              (getFirmSettings() as Promise<FirmSettingsShape | null>)
                .then((_fs) => {
                  // 207B §5 — the typing tab receives its own echo (measured):
                  // the re-read lands through the same door as a save's answer —
                  // never older than the row held, the typed-meanwhile laid over it.
                  const _m = takeFirmSettings(_fs);
                  if (!_m) return;
                  setFirmLogo(_m.logo || null);
                  const _fn = _m.firm?.name || "Legal Practice Manager";
                  setFirmName(_fn);
                  setFirmNameCache(_fn);
                })
                .catch(() => {}),
            );
          }
          // Panels with component-local config state (taxonomies, rates,
          // doc/bulk-mail templates, places admin, xlsx restore domain:all)
          // converge via the bridge below; "all" additionally suggests a
          // reload through the deploy banner path.
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "config", ...p } }));
        },
        access: () => {
          // Visibility/permissions changed for THIS viewer (recipient poke)
          // or firm-wide (roles / surface scopes). Refetch the permission
          // map and every scoped store. 193 §A — REFETCH, not prime: prime
          // answers the cached map once one has landed, so a roles save never
          // reached a browser already open (measured: the switchboard's proof
          // needed a reload).
          debounced("access", 700, () => {
            Promise.resolve(refetchPermissions()).catch(() => {});
            _refetchList(getCasesWithLatestInvoice, setCases)();
            _refetchList(getCollectiveCasesWithLatestInvoice, setCollectiveCases)();
            _refetchList(getTasks, setTasks)();
            _refetchList(getAgenda, setAgendaEvents)();
            _refetchList(getTimeEntries, setTimeEntries)();
            _refetchList(getInvoices, setInvoices)();
            refetchChat();
            getChatReadReceipts().then((r: any) => r && setChatRead(r)).catch(() => {});
          });
        },
        reservation: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "reservation", ...p } })),
        place: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "place", ...p } })),
        schedule: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "schedule", ...p } })),
        cdd: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "cdd", ...p } })),
        interest: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "interest", ...p } })),
        ccdt: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "ccdt", ...p } })),
        bulkmail: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "bulkmail", ...p } })),
        billedEmails: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "billedEmails", ...p } })),
        prefs: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "prefs", ...p } })),
        /* 175 §3.3 — THE DETACHED CLIENT-FOLDER PROVISION REPORTS.
           `scheduleClientFolder` is fire-and-forget by design (a folder call
           must never reject into the POST), and until now its every outcome —
           including a folder that WAS created whose path was never stored —
           was one `console.warn` on the server. Addressed to the ONE user who
           created the client (recipientId), so a failure is a toast at the
           moment it happens rather than a pane rendering nothing days later,
           and a success repaints their list, which it also never did. */
        clientFolder: (p) => {
          debounced("cl", 400, _refetchList(getClients, setClients));
          if (p && p.action === "failed")
            toast(
              t("clients.folder_failed", {
                fallback:
                  "The client was saved, but its SharePoint folder was not. Repair it from the clients list — Provision missing folders.",
              }),
              "error",
            );
          /* 176 §2.2 — AND SUCCESS SAYS WHAT IT MADE. The list repaint above
             has been here since 175 §3.3, but the one event that CREATES a
             folder was silent about the folder — while the CASE side has named
             its own since Phase 41 (`CaseDocumentsSection.tsx:301-309`). Since
             §1's ruling that name is permanent, so this is the moment it is
             worth saying.

             `toast.folder.created` and NOT a second sentence for the same
             event (§2.2): the case side already seeds "Created new folder:
             {name}" in fr/nl/en and the two events are the same event on two
             entities. `p.name` is `folderLastSegment(out.path)` from the
             server (clients.js), so it is the folder's own last segment and
             not the client's name — the same string §2.1's row prints.

             Guarded on `p.name`: an older server publishing the pre-176
             envelope carries none, and a toast reading "Created new folder: "
             is worse than the silence it replaces. */
          if (p && p.action === "provisioned" && p.name)
            toast(
              t("toast.folder.created", {
                name: String(p.name),
                fallback: "Created new folder: " + String(p.name),
              }),
            );
          /* 211 §11 — made, but a subfolder still missing after its retry: named, then and there */
          const sm = (p ?? {}) as { action?: string; name?: string; missing?: string[] };
          if (sm.action === "subfolders_missing" && Array.isArray(sm.missing) && sm.missing.length)
            toast(
              (t as TFn)("clients.subfolders_missing", {
                name: sm.name ?? "",
                list: sm.missing.join(", "),
                fallback: "The folder of {name} was made without {list}. Check the subfolders from the clients list — Provision missing folders.",
              }),
              "warning",
            );
        },
        /* Phase 41 (D8b) — terminal completion of a fire-and-poll folder
           backfill, addressed to the launching user (recipientId). Re-dispatched
           to the app listener, which re-enables the launch control + toasts the
           real server summary; it filters on recipientId (the hub also delivers
           this to admins by overlay). */
        backfill: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "backfill", ...p } })),
        /* 206 §8.1 — THE CASE / COLLECTIVE-CASE FOLDER PROVISION, NOW
           DETACHED. `cases.js` and `collectiveCases.js` ran their folder call
           AWAITED INSIDE THE POST, so the response was the answer; with the
           bounded retry they now share with the client path, holding the
           request open for up to four backoffs was not an option. They answer
           immediately and report here instead — the same shape `clientFolder`
           has had since 175 §3.3, addressed to the ONE user who created the
           matter. The DURABLE half of the failure is a bell row (206 §7);
           this is the toast at the moment it happens. */
        caseFolder: (p) => {
          /* The same refetch the `access` poke uses for this store — the
             list carries its latest invoice, so the plain `getCases` would
             answer a thinner row. */
          debounced("caFolder", 400, () => {
            _refetchList(getCasesWithLatestInvoice, setCases)();
            _refetchList(getCollectiveCasesWithLatestInvoice, setCollectiveCases)();
          });
          /* 206 §8.1 — NARROWED AT THE SEAM (179's rule for an `any`-typed
             boundary): the poke envelope arrives untyped, so it is read ONCE
             through the shape this channel actually publishes and every field
             below is a real type rather than a member access on `any`. */
          const fp = (p ?? {}) as { action?: string; name?: string };
          if (fp.action === "failed")
            toast(
              t("cases.folder_failed", {
                fallback:
                  "The matter was saved, but its SharePoint folder was not. Create it from the matter's Case file pane.",
              }),
              "error",
            );
          /* The same guard `clientFolder` carries: an older server publishing
             no name would toast "Created new folder: ". */
          const fpName = fp.name ?? "";
          if (fp.action === "provisioned" && fpName)
            toast(
              t("toast.folder.created", {
                name: fpName,
                fallback: "Created new folder: " + fpName,
              }),
            );
          /* 211 §11 — made, but a subfolder still missing after its retry: named, then and there */
          const fpMissing = (p ?? {}) as { missing?: string[] };
          if (fp.action === "subfolders_missing" && Array.isArray(fpMissing.missing) && fpMissing.missing.length)
            toast(
              (t as TFn)("cases.subfolders_missing", {
                name: fpName,
                list: fpMissing.missing.join(", "),
                fallback: "The folder of {name} was made without {list}. Check the subfolders from Settings — Folder templates.",
              }),
              "warning",
            );
        },
        /* Template-delete-propagation — terminal completion of a fire-and-poll
           LIVE folder sweep, addressed to the launching admin (recipientId).
           The propagation modal listens on the lx:sse bridge and matches by
           runId; the poke is non-ephemeral so it replays across a reconnect. */
        folderPropagation: (p) =>
          window.dispatchEvent(new CustomEvent("lx:sse", { detail: { type: "folderPropagation", ...p } })),
        /* Phase 31 (C2) — calls. 'call' = persistent log rows (idempotent
           upsert; also dismisses my ring card once my outcome resolved —
           answered in another tab, or the server sweep landed missed).
           'callRing' / 'callAnswer' are EPHEMERAL (no SSE id, never
           replayed): ring → push onto the incoming stack with a local
           countdown deadline; answer/decline → live toast to the caller. */
        call: (p) => {
          if (!p || !p.call || p.call.id == null) return;
          upsertCallRow(p.call, p.action);
          /* 31.1 (item 7b) — the initiator's OTHER tabs/devices arm the
             waiting card from the create event (the initiating tab already
             armed it from the POST — the functional prev|| keeps it). The
             card resolves everywhere from the same row updates, so Abort
             and Join are available in whichever tab the user is actually
             looking at. */
          if (
            p.action === "create" &&
            p.call.status === "ringing" &&
            Number(p.call.initiatorId) === Number(currentUser)
          ) {
            setOutgoingCall((prev: any) => prev || { callId: p.call.id });
          }
          const mine = (p.call.participants || []).find(
            (x: any) => Number(x.collaboratorId) === Number(currentUser),
          );
          if (mine && mine.outcome !== "ringing") {
            setIncomingCalls((prev: any) =>
              prev.filter((c: any) => c.callId !== p.call.id),
            );
          }
          /* Item-3 — the durable re-ring-on-reconnect backstop is removed: real
             two-machine testing shows the ephemeral callRing (card + audio +
             missed-log) survives backgrounding fine, so re-presenting from the
             'call' event addressed a non-problem. The Part-C push-click surface
             path is kept (SW handlers + the /calls present on a push-opened tab). */
        },
        callRing: (p) => {
          if (!p || !p.callId || !p.initiatorId) return;
          /* 31.1 (item 7a) — POSITIVE gate: the ring popup renders ONLY for
             the invitee this copy is addressed to (the server fires one
             callRing per invitee, recipientId = that invitee; the initiator
             is never addressed). The old initiator-exclusion let every
             OTHER non-invitee through — the hub's admin overlay delivers
             ALL per-recipient ephemeral copies to admin streams, so an
             admin tab popped the ring for calls it was not invited to and
             Accept 403'd ("not invited to this call"). Number() on both
             sides forecloses the string-id drift class (31.1 item 2). */
          if (Number(p.recipientId) !== Number(currentUser)) return;
          setIncomingCalls((prev: any) =>
            prev.some((c: any) => c.callId === p.callId)
              ? prev
              : [
                  ...prev,
                  {
                    ...p,
                    deadline:
                      Date.now() + (Number(p.ringSeconds) || 40) * 1000,
                  },
                ],
          );
          // Corr-3 (item 11) — centralized + instrumented (see maybeRingNotification).
          console.debug("[ringNotif] callRing (ephemeral) for call", p.callId);
          maybeRingNotification(p.callId, p.initiatorId, p.startedAt);
        },
        /* Phase 31 (C3) — engine alerts: ephemeral toast, 25 s, dedupe by
           the engine's fire identity. Join affordance when a joinUrl rides
           along.
           Brief 35 (stage 3) — this handler still does not touch the bell,
           and now it must not: the engine wrote the durable row itself,
           inside the FiredAlert claim, and pokes for it separately. Adding
           the toast to the bell here would produce a SECOND, client-invented
           copy of a row that already exists — which is the "three different
           lists" impression this slice exists to end. */
        alert: (p) => {
          if (!p || !p.kind || p.entityId == null) return;
          const uid = `${p.kind}:${p.entity}:${p.entityId}:${p.date || ""}T${p.time || ""}`;
          /* Brief 42 §1.1 — NO `expiresAt`. This is the one line the ruling
             turns on: the toast is pushed with no lifetime, the sweeper
             (App.tsx:2166) skips anything carrying none, and the row therefore stays
             on screen until the Bell is opened. The dedupe by `uid` above is
             what keeps that from doubling — the same fire re-delivered over a
             reconnect still lands as one toast. */
          setAlertToasts((prev: any) =>
            prev.some((a: any) => a.uid === uid) ? prev : [...prev, { ...p, uid }],
          );
        },
        callAnswer: (p) => {
          if (!p || !p.callId) return;
          /* 31.1 (item 7) — same addressed-recipient gate as callRing:
             admins receive every overlay copy; without this an admin tab
             toasted answer/decline notices for calls it never made. */
          if (Number(p.recipientId) !== Number(currentUser)) return;
          const who = (coLiveRef.current || []).find((c: any) => c.id === p.by);
          const name = who ? pname(who) : "?";
          if (p.action === "declined") {
            toast(
              p.availableFrom
                ? t("calls.decline_msg", {
                    name,
                    time: p.availableFrom,
                    fallback: `${name} can't take this call right now, but will be available from ${p.availableFrom}`,
                  })
                : t("calls.declined_toast", {
                    name,
                    fallback: `${name} declined the call`,
                  }),
              "error",
            );
          } else if (p.action === "answered") {
            toast(
              t("calls.answered_toast", {
                name,
                fallback: `${name} answered — joining the call`,
              }),
            );
          }
        },
        /* Phase 39 — WebRTC signalling push (offer/answer/ICE/bye). The server
           already delivers recipient-exact; we re-check defensively, then nudge
           the session to pull (the ordered, de-duped apply path). */
        callSignal: (p) => {
          if (!p || Number(p.recipientId) !== Number(currentUser)) return;
          const s = rtcSessionRef.current;
          if (s && Number(p.callId) === Number(s.callId)) { s.onPush(p); return; }
          /* 72 — A SHARE LEG IS A SECOND SESSION ON A SECOND callId, and its
             signalling rides this same family. Without this arm every offer,
             answer and candidate for a leg would fall through to the warning
             below and negotiate only via the 40 ms durable pull — which works,
             and is exactly the slow path Phase 42 Lever A was built to avoid. */
          const sh = rtcShareRef.current;
          if (sh && Number(p.callId) === Number(sh.callId)) { sh.onPush(p); return; }
          // Phase 42 — no live session for this call yet (e.g. a ring-time offer
          // that beat the pre-warm session into existence). The row is durably
          // stored, so the session's initial pull recovers it once it opens — but
          // log LOUD so a future "offer into a void" fails visibly, not silently
          // (the silent drop is what hid the ring-time deadlock).
          console.warn(`[lxcall] callSignal for call ${p.callId} (${p.kind}) arrived with no matching session — deferring to the durable pull`);
        },
        /* 72 §3.5 — a screen-share LEG opened, changed roster, or ended.
           EPHEMERAL and recipient-exact (server/src/lib/callService.js's
           `publishLeg`), so it reaches only people who are in the leg and never
           an admin by overlay — 31.1 item 8's rule, and the reason the family is
           ephemeral rather than persistent.
           The recipient gate is re-checked here anyway, for the reason
           `callRing`'s POSITIVE gate exists (31.1 item 7a, App.tsx's callRing
           handler above): an overlay copy of an addressed event is precisely how
           the ring popup once fired for calls nobody was invited to. Defence in
           depth, and named as such rather than presented as the fix. */
        callLeg: (p) => {
          if (!p || !p.leg || p.leg.id == null) return;
          if (Number(p.recipientId) !== Number(currentUser)) return;
          const leg = p.leg;
          const held = rtcShareRef.current;
          const isMine = held && Number(held.callId) === Number(leg.id);
          if (p.action === "end") {
            if (isMine) endShareSession();
            return;
          }
          if (Number(leg.sharerId) === Number(currentUser)) {
            // My own share coming back to me. The session already exists (the
            // click built it); the only thing an event can carry that I need is
            // a CHANGED ROSTER — somebody left the conversation mid-share, so
            // they leave my star too.
            setShareLeg(leg);
            if (isMine) held.setPeers(leg.participantIds, leg.participantIds);
            return;
          }
          if (isMine) { setShareLeg(leg); return; } // roster update on a leg I receive
          openReceivedLeg(leg);
        },
        deploy: (p) => {
          if (!p || !p.buildId) return;
          if (bootBuildIdRef.current && p.buildId !== bootBuildIdRef.current) {
            setNewVersion(p.buildId);
          }
        },
      },
      onStatus: (st) => {
        // Phase 30 (F) — every (re)open compares the server's build to the
        // one this tab loaded with; pm2 reload drops the stream, so the
        // reconnect lands here within seconds of a deploy.
        if (st === "open") {
          checkVersion();
          // Phase 39 — a reconnect may have dropped ephemeral signalling
          // pushes; pull the durable mailbox so live negotiation catches up.
          if (rtcSessionRef.current) { try { rtcSessionRef.current.pull(); } catch { /* ignore */ } }
          // 72 — the share leg has its own mailbox on its own callId, and a leg
          // mid-negotiation across a reconnect needs the same catch-up. (An
          // already-connected leg needs nothing: its media is peer-to-peer.)
          if (rtcShareRef.current) { try { rtcShareRef.current.pull(); } catch { /* ignore */ } }
          // Item-3 — the durable re-ring-on-reconnect /calls re-pull is removed
          // (the ephemeral ring survives backgrounding in real testing). The
          // push-click surface path remains via the mount fetch + SW message.
        }
      },
      /* Round 14 (C84) — while the pop-out column is open, the stream's
         stall watchdog + reconnect timers ALSO arm on the pop-out window:
         a Document-PiP window is always visible, so Chrome never throttles
         or freezes its timers — the live pipeline survives a backgrounded
         main window, which is exactly the state a user watching the
         pop-out is in. (popOutWinRef is declared below in this component;
         the closure runs long after mount, so the reference is live.) */
      getTimerHost: () =>
        popOutWinRef.current && !popOutWinRef.current.closed
          ? popOutWinRef.current
          : window,
    });
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, currentUser]);
  const dismissedReminders = Array.isArray(prefs.dismissedReminders)
    ? prefs.dismissedReminders
    : [];
  const setDismissedReminders = (v: any) =>
    setPref(
      "dismissedReminders",
      typeof v === "function" ? v(dismissedReminders) : v,
    );
  const [dailyOverview, setDailyOverview] = useState<any>(false);
  const [discardDlg, setDiscardDlg] = useState<any>(null);
  // 58 §5b — the firm's schedule axis, resolved ONCE and handed down. Six
  // surfaces draw a week; one resolution means they cannot disagree about what
  // a week is. With no setting stored it returns today's constants exactly.
  const schedAxis = resolveScheduleAxis(firmSettings);
  /* 133 §4 — the three calendar-display switches, resolved HERE for the same
     reason `schedAxis` is: four surfaces read them (the two 14-day strips, the
     agenda's list and its calendar) and one resolution is what stops them
     disagreeing. With the column NULL it returns the defaults, which reproduce
     133 §2 and 132 §7 exactly — so a firm that never opens the panel is
     unaffected. */
  const calDisp = resolveCalendarDisplay(firmSettings as { calendarDisplay?: unknown } | null);
  /* 132 §4/§6/§7 — the firm's holidays, resolved HERE for the same reason
     `schedAxis` is: one answer, handed to every surface, so the strips and
     the agenda cannot disagree about what a holiday is. Unmemoised, like its
     neighbour and like everything else in this file — the work is ten date
     computations per year asked about, and the lookup caches per year within
     a render pass.
     NAMES come through `pickEntryLabel`, the cascade every taxonomy label
     uses, so the seed's fr/nl rows are what a French or Dutch reader sees:
     §9 rules holiday names DATA, not UI strings with their own keys. */
  const _holidayTax = useTaxonomy("legal_holiday").entries;
  const _countryTax = useTaxonomy("country").entries;
  const holidays = makeHolidayLookup(
    holidayDefsFrom(_holidayTax, (e) => pickEntryLabel(e as never)),
    observedCountries(
      (firmSettings as any)?.holidayCountries,
      (firmSettings as any)?.firm?.country,
      (v) => resolveTaxonomyEntry(v as never, _countryTax as never),
    ),
  );
  const workHours = prefs.notifWindow || { start: "08:00", end: "19:00" };
  const setWorkHours = (v: any) =>
    setPref("notifWindow", typeof v === "function" ? v(workHours) : v);
  const notifSentRef = useRef<Set<unknown>>(new Set());
  const [gSearchQ, setGSearchQ] = useState<any>("");
  const [searchDlg, setSearchDlg] = useState<any>(false);
  usePopoverAutoClose({
    sideFly,
    searchDlg,
    setSideFly,
    setSearchDlg,
    sideCol,
    sideMob,
  });
  const searchDlgRef = useRef<any>(null);
  const searchDlgInputRef = useRef<any>(null);
  const [sDlgQ, setSDlgQ] = useState<any>("");
  const [sDlgFilters, setSDlgFilters] = useState<any>({});
  const [sDlgMulti, setSDlgMulti] = useState<any>(false);
  const [sDlgMore, setSDlgMore] = useState<any>({});
  const agViewMode = prefs.agendaView || "list";
  const setAgViewMode = (v: any) =>
    setPref("agendaView", typeof v === "function" ? v(agViewMode) : v);
  const agTypeFilt =
    prefs.agendaTypeFilter != null ? prefs.agendaTypeFilter : null;
  const setAgTypeFilt = (v: any) =>
    setPref("agendaTypeFilter", typeof v === "function" ? v(agTypeFilt) : v);
  const agDateRange = prefs.agendaDateRange || "month";
  const setAgDateRange = (v: any) =>
    setPref("agendaDateRange", typeof v === "function" ? v(agDateRange) : v);
  const [agMulti, setAgMulti] = useState<any>(false);
  const listSort = prefs.listSort || {};
  const setListSort = (v: any) =>
    setPref("listSort", typeof v === "function" ? v(listSort) : v);
  const [listPage, setListPage] = useState<any>({});
  const sideCaseChatRef = useRef<any>(null);
  const [caseChatInput2, setCaseChatInput2] = useState<any>("");
  // Phase 27 — running timers persist to localStorage so a reload/crash
  // restores them. startedAt is an absolute epoch, so a still-running timer's
  // accrued time correctly includes the reload gap on rehydrate.
  const [activeTimers, setActiveTimers] = useState<any>(() => {
    try {
      const arr = JSON.parse(localStorage.getItem("lx:activeTimers") || "[]");
      if (!Array.isArray(arr)) return [];
      /* BRIEF 91 §3a — `t.taskId != null` was the whole membership test, so an
         EVENT subtask's timer (no parent task — it carries `eventId`) would
         have been dropped on the next reload with its accrued time. The test is
         "does this row name something": a task, or an event's subtask. A row
         naming neither is the corrupt/legacy shape this filter was written for
         and is still discarded. */
      /* 116 §3 — and an EVENT'S OWN timer names neither: it carries `eventId`
         and nothing else, so the two-way test discarded it on the next reload
         with its accrued time. Same reasoning as 91 §3a's, one kind wider. */
      const clean = arr.filter(
        (t) =>
          t && (t.taskId != null || t.subtaskId != null || t.eventId != null),
      );
      // Defensive single-runner invariant: a healthy persisted set has at most
      // one running timer (its absolute startedAt correctly accrues the offline
      // gap). If a corrupt/legacy state carries several paused:false timers,
      // keep the first running and freeze the rest to their stored elapsed so
      // they can't double-accrue overlapping wall-clock after rehydrate.
      let runningSeen = false;
      return clean.map((t) => {
        if (!t.paused) {
          if (runningSeen) return { ...t, paused: true };
          runningSeen = true;
        }
        return t;
      });
    } catch {
      return [];
    }
  });
  const [focusedTimerIdx, setFocusedTimerIdx] = useState<any>(() => {
    try {
      const v = parseInt(localStorage.getItem("lx:focusedTimerIdx") || "0", 10);
      return Number.isFinite(v) && v >= 0 ? v : 0;
    } catch {
      return 0;
    }
  });
  const activeTimerRef = useRef<any>(null);
  const [globalPfMod, setGlobalPfMod] = useState<any>(null);
  const [clListLocked, setClListLocked] = useState<any>(true);
  /* BRIEF 108 §5 — AN UNLOCKED LOCK RE-LOCKS WHEN THE PAGE IS LEFT. These two
     live at App altitude, and App never unmounts, so they never reset — the
     "seems random" half of the report: every view-level lock resets with its
     view (the section map mounts exactly one view, so switching sections
     unmounts the old one), while these two survived everything. One effect,
     keyed on the two navigations the rule names: the active section
     (setSection, both call paths land here) and the viewed matter (detailId
     covers case A → case B without a section change). Implemented as an
     effect rather than scattered onClicks, per the brief. */
  useEffect(() => {
    setPartiesLocked(true);
    setClListLocked(true);
  }, [section, detailId]);
  const [clImportFile2, setClImportFile2] = useState<any>(null);
  const clFileRef2 = useRef<any>(null);
  const [juImportFile, setJuImportFile] = useState<any>(null);
  const juFileRef = useRef<any>(null);
  const [orphanDlg, setOrphanDlg] = useState<any>(false);
  // Hotfix-B (Part B) — folder-backfill button busy flag.
  const [backfillBusy, setBackfillBusy] = useState<any>(false);
  // Hotfix-B correction (ITEM 2) — in-app provisioning modal (replaces the old
  // native count-prompt). provisionDlg = open flag; provisionChecked = the set
  // of client ids the user ticked for folder creation (hard-capped at 50).
  const [provisionDlg, setProvisionDlg] = useState<any>(false);
  const [provisionChecked, setProvisionChecked] = useState<any>(() => new Set());
  // 211 §12 — the clients' subfolder check (a folder that lacks its template's subfolders);
  // 212 §27 — and the matters', from the case lists: the kind whose check is open
  const [subfolderCheck, setSubfolderCheck] = useState<false | "client" | "case">(false);
  /* 176 §3.3 — THE MODAL'S SCOPE, and it exists because the repair 176 §3.2
     wired into the client-file pane opened a FIRM-WIDE list. Found by Laurent on
     staging: `provisionCandidateList` returns every folderless client in the
     firm and the modal prints each one's name, e-mail and LINKED MATTER
     (:16622, `linkedCaseLabel`) — so a repair clicked inside one matter showed
     a collaborator the firm's other clients and which cases they are on.

     `null` = firm-wide, the clients-list behaviour, unchanged. An array of
     client ids NARROWS the candidate list and never widens it: the existing
     filter still runs first and the scope only intersects with what it
     produced. Cleared when the modal closes (the effect at :5487), so the next
     unscoped open cannot inherit a stale scope. */
  const [provisionScope, setProvisionScope] = useState<number[] | null>(null);
  // Hotfix-B (Part A) — "Database cleanup" modal. dbCleanupChecked is the
  // checkbox source of truth (initialized on open to pre-check non-survivor
  // duplicates). The DEFERRED hard-delete lives at App scope so closing the
  // modal / navigating away neither strands nor cancels it: clRef mirrors the
  // freshest cl so the 20s-later commit removes exactly the captured ids (a
  // concurrent add during the window is not collaterally deleted), and
  // pendingClDelRef { timer, ids } drives commit-on-expiry (Undo cancels).
  const [dbCleanupChecked, setDbCleanupChecked] = useState<any>(() => new Set());
  const [pendingClDel, setPendingClDel] = useState<any>(null); // { count, seq } — Undo toast
  const pendingClDelRef = useRef<any>(null);
  const pendingClDelSeq = useRef<any>(0);
  const clRef = useRef<any>(clients);
  clRef.current = clients;
  /* Phase 29 (items 2/3) — pop-out column state. popOutWinRef holds the
     pop-out window (Document PiP or window.open fallback); popOutCtr is the
     container element inside its document that the live <CompactColumn/> is
     portaled into (state, so the portal mounts/unmounts with it). colInApp
     is the PHONE mode: at <600px the app opens INTO the column on every
     launch (deliberately not persisted) and the toggle flips column ↔ full
     app; phones never open a window. */
  const popOutWinRef = useRef<any>(null);
  const [popOutCtr, setPopOutCtr] = useState<any>(null);
  /* 74 — THE SHARER'S CALL, IN AN ALWAYS-ON-TOP WINDOW. Same two refs, same
     mechanism (`_openPopOutWindow`), different content: the call surface rather
     than the column. It exists because of a fact no CSS can change — a sharer is
     by definition looking at the thing he is sharing, and nothing inside a
     browser tab can put that tab in front of another application's window. */
  const callPopWinRef = useRef<Window | null>(null);
  const [callPopCtr, setCallPopCtr] = useState<HTMLDivElement | null>(null);
  /* 186 A.3a [RULED 186 (2)] — THE CHAT POP-OUTS ARE THE THIRD AND FOURTH
     CONTAINERS OF THIS ONE MECHANISM (`_openPopOutWindow` below): the same
     Document PiP window with the `window.open` fallback, the app's stylesheet
     and font cloned in, the theme mirrored per commit — and the LIVE sidebar
     chat portaled into it (beside the column's portal), never a second
     implementation of it. Same two refs per surface as the column and the
     call; the general chat and the case chat each have their own, and the
     sidebar TITLE toggles them (`toggleChatPopOut`). */
  const gChatPopWinRef = useRef<Window | null>(null);
  const [gChatPopCtr, setGChatPopCtr] = useState<HTMLDivElement | null>(null);
  const cChatPopWinRef = useRef<Window | null>(null);
  const [cChatPopCtr, setCChatPopCtr] = useState<HTMLDivElement | null>(null);
  /* The case chat the sidebar row WOULD show — the popped-out case chat is that
     row's chat, so it follows the current matter and closes when there is none
     (its poll below). Read once, typed, off the two `any` lists. */
  const _chatPopId: unknown = detailId;
  let _chatPopDid: ChatPopDid = null;
  if (typeof _chatPopId === "number" || typeof _chatPopId === "string") {
    if (section === "ca" && (cases as { id: number }[]).some((c) => c.id === _chatPopId)) _chatPopDid = _chatPopId;
    if (section === "cc" && (collectiveCases as { id: number }[]).some((c) => c.id === _chatPopId))
      _chatPopDid = "cc:" + String(_chatPopId);
  }
  const [colInApp, setColInApp] = useState<any>(
    () => typeof window !== "undefined" && window.innerWidth < 600,
  );
  const [timerLimitDlg, setTimerLimitDlg] = useState<any>(null);
  /* Round 15 (C94a) — taskId → ts of the last observed AUTO flip to
     "inprogress" (C82b: timer start / task-bound time log, local fire or
     SSE echo). TkStatusTag consults it to swallow a pill click that raced
     the flip; entries age out (pruned in the SSE handler). */
  const recentAutoOngoingRef = useRef<any>(new Map());
  const [timerDd, setTimerDd] = useState<any>(false);
  const timerDdRef = useRef<any>(null);
  const maxTimers = prefs.maxTimers != null ? prefs.maxTimers : 5;
  const setMaxTimers = (v: any) =>
    setPref("maxTimers", typeof v === "function" ? v(maxTimers) : v);
  const timerRound = prefs.timerRound != null ? prefs.timerRound : 5;
  const setTimerRound = (v: any) =>
    setPref("timerRound", typeof v === "function" ? v(timerRound) : v);
  // Backward-compatible shim: activeTimer = focused timer (or first)
  const activeTimer =
    activeTimers.length > 0
      ? activeTimers[focusedTimerIdx] || activeTimers[0]
      : null;
  const setActiveTimer = (v: any) => {
    if (v === null) {
      setActiveTimers((ts: any) => ts.filter((_: any, i: any) => i !== focusedTimerIdx));
      setFocusedTimerIdx(0);
    } else {
      setActiveTimers((ts: any) => {
        /* 116 §3 — the triple. `taskId` alone matched the FIRST row with that
           id, so two subtask timers on one task already aliased here (the
           collision `timerKey` names) and two event timers would have aliased
           on `undefined === undefined`. */
        const idx = ts.findIndex((t: any) => sameTimer(t, v));
        if (idx >= 0) {
          const nts = [...ts];
          nts[idx] = v;
          return nts;
        }
        return ts;
      });
    }
  };
  /* 116 §3 — `opts.eventId` was already here for 91 §3a's event SUBTASK timers;
     what is new is that it may arrive WITHOUT a subtaskId, which is an event's
     own timer. Nothing else about the signature changes — a sibling
     `startEventTimer` would have been a second door onto one room, and the
     least-churn form is the argument that already exists. */
  const startTimer = (taskId?: any, subtaskId?: any, opts?: any) => {
    /* Round 14 (C82b) — AUTO-ONGOING: any timer start/resume on a "todo"
       task (its subtasks included) silently advances it to "inprogress",
       server-side: the endpoint chronicles the flip with this user as actor
       and pushes the row over SSE (the echo updates this client too — no
       optimistic write here). Fired only when a timer will actually run for
       this key: an existing timer resumes regardless, a NEW one starts only
       under the max-timers cap (the limit dialog starts nothing). The server
       no-ops on anything but todo, so re-fires are harmless. */
    {
      /* 116 §3 — THE TASK-ONLY SIDE EFFECT IS SKIPPED FOR AN EVENT TIMER, and
         explicitly rather than incidentally: `tasks.find(x => x.id ===
         undefined)` already answered undefined, so the flip never fired, but a
         guard that works by accident is one refactor away from firing. An
         agenda event has no status to advance. */
      const _key = {
        taskId: taskId ?? null,
        eventId: (opts && opts.eventId) || null,
        subtaskId: subtaskId ?? null,
      };
      const _t0 = taskId != null ? tasks.find((x: any) => x.id === taskId) : null;
      const _hasKey = activeTimers.some((t2: any) => sameTimer(t2, _key));
      if (
        _t0 &&
        (_t0.status === "todo" || _t0.status == null) &&
        (_hasKey || activeTimers.length < maxTimers)
      ) {
        /* Round 15 (C94a) — record the flip locally too: the SSE echo can
           lose a footrace against the user's next click on this pill. */
        recentAutoOngoingRef.current.set(taskId, Date.now());
        autoOngoingTask(taskId).catch(() => {});
      }
    }
    keepScroll(() => {
      setActiveTimers((prev: any) => {
        const _stid = subtaskId || null;
        // Phase 27 Stage-3 — ONE boundary timestamp for the whole operation.
        // The outgoing (running) timer is frozen at `now` and the incoming one
        // starts at the SAME `now`, so the boundary instant is counted exactly
        // once: no gap, no overlap. A previously running timer accrues its
        // exact [startedAt, now] interval and nothing else, so toggle count has
        // zero effect on total accrued time.
        const now = Date.now();
        // Freeze EVERY running timer (one-at-a-time invariant). Raw ms, no
        // rounding — the 5-min round-up lives only at the Log step.
        const frozen = prev.map((t: any) =>
          t.paused
            ? t
            : { ...t, paused: true, elapsed: t.elapsed + (now - t.startedAt) },
        );
        /* 116 §3 — the triple, so a second event's play button starts a second
           timer instead of resuming the first one's row. */
        const _eid = (opts && opts.eventId) || null;
        const existIdx = frozen.findIndex((t: any) =>
          sameTimer(t, { taskId: taskId ?? null, eventId: _eid, subtaskId: _stid }),
        );
        if (existIdx >= 0) {
          const wasRunning = !prev[existIdx].paused;
          setFocusedTimerIdx(existIdx);
          if (wasRunning) {
            // Re-click the running one => everything is now paused; nothing
            // accrues.
            return frozen;
          }
          // Was paused => resume just this one (all others frozen above), using
          // the same boundary `now` as the freeze.
          const nts = [...frozen];
          nts[existIdx] = { ...nts[existIdx], paused: false, startedAt: now };
          if (!pomGetStart()) {
            pomSetStart(now);
            pomSetPings(0);
          }
          return nts;
        }
        if (prev.length >= maxTimers) {
          /* 116 §3 — THE PENDING START IS A KEY NOW, not a bare task id. §3
             names "max-timers logic" among the places the triple has to reach,
             and this is it: after "Close & Log" the swap re-issues this start,
             and a bare `taskId` cannot express an event (undefined) — it would
             have minted a (undefined, null, null) row. It could not express a
             SUBTASK either, which is a task-side defect this inherits and
             fixes on the way: hitting the cap while starting a subtask timer
             swapped in the TASK-level one. The old `pendingTaskId` field is
             GONE rather than kept beside it — nothing reads it once the swap
             takes the key, and a field written and never read is the 114 §3
             class (a prop that looks load-bearing and is not). */
          setTimerLimitDlg({
            pendingKey: {
              taskId: taskId ?? null,
              eventId: _eid,
              subtaskId: _stid,
            },
          });
          return prev;
        }
        const t = {
          taskId,
          subtaskId: _stid,
          /* BRIEF 91 §3a — an EVENT subtask's timer names its event here; a
             task's timer carries null and every `(taskId, subtaskId)`
             comparison in this engine is untouched by the extra field.
             116 §3 — and an EVENT'S OWN timer names its event here with NO
             subtask, which is the shape that made the pair insufficient: every
             comparison in this engine is `sameTimer` now. `taskId` and
             `eventId` are mutually exclusive by construction (the two callers
             pass one or the other); a persisted row from before this brief
             simply lacks the field and reads as null. */
          eventId: _eid,
          startedAt: now,
          elapsed: 0,
          paused: false,
        };
        setFocusedTimerIdx(frozen.length);
        activeTimerRef.current = t;
        // Start the pom cycle on first timer; it then runs continuously on
        // wall-clock regardless of pause state.
        if (!pomGetStart()) {
          pomSetStart(now);
          pomSetPings(0);
        }
        return [...frozen, t];
      });
      /* Phase 29 — auto-open the pop-out column (now hosting the timer) on
         desktop, the old openTimerPip behavior. Phones never open a window;
         flipping the phone view to the in-app column here would also yank
         the user out of the task list they just started a timer from, so
         phone width is a no-op. Item-4 fix — opts.noPopout suppresses this for
         HEADER-timer interactions (pause/resume/switch) so the header timer is
         usable IN PLACE without being thrown into the pop-out. */
      if (
        !(opts && opts.noPopout) &&
        !(typeof window !== "undefined" && window.innerWidth < 600)
      )
        openPopOut();
    });
  };
  /* ── BRIEF 116 §4 [RULED] — JOIN & TIME, IN ONE PLACE ──────────────────────
     A fired reminder for an event carrying a `joinUrl` gains ONE action: open
     the meeting AND start that event's timer (§3). Two surfaces render it — the
     bell's persisted rows and the reminder toast — so the ACT lives here rather
     than being spelled twice; the surfaces differ in where the id comes from,
     never in what the click does.
     The open is the JoinBtn family's own guarded one — `_blank` + `noopener`,
     the same pair the toast's plain Join already used (an opener-less tab is
     what keeps the meeting page off `window.opener`).
     ORDER MATTERS, and it is the deliberate one: the tab is opened FIRST, from
     inside the click, because a popup blocker judges by how far the call sits
     from the user gesture. `startTimer` is synchronous state, so it costs the
     open nothing to go second. */
  const joinAndTime = (
    eventId: number | null | undefined,
    joinUrl: string | null | undefined,
  ) => {
    if (joinUrl) window.open(joinUrl, "_blank", "noopener");
    if (eventId != null) startTimer(null, null, { eventId });
  };
  const pauseTimer = (taskId: any) => {
    keepScroll(() => {
      setActiveTimers((prev: any) => {
        /* 116 §3 — the same predicate as every other lookup in this engine.
           Measured: this branch is DEAD today (the only live call is
           `pauseTimer()` with no argument, CompactColumn.tsx:452, which takes
           the focused index), and it is corrected rather than left because
           `t.taskId === taskId` matched the FIRST row with that id — which can
           be one of that task's SUBTASK timers, not the task's own. */
        const idx =
          taskId != null
            ? prev.findIndex((t: any) => sameTimer(t, { taskId }))
            : focusedTimerIdx;
        const tm = prev[idx];
        if (!tm || tm.paused) return prev;
        const now = Date.now();
        // Keep raw milliseconds — the 5-min round-up lives only in the Log step.
        const el = tm.elapsed + (now - tm.startedAt);
        const nts = [...prev];
        nts[idx] = { ...tm, paused: true, elapsed: el };
        return nts;
      });
    });
  };
  const stopQueueRef = useRef<any>([]);
  /* Phase 29 Stage-3 C12 — stopping a timer opens the CANONICAL time-entry
     modal directly (the bare "Log timer" dialog is gone). Seed = exactly
     what that dialog passed to om("te"): minutes = roundUp5(elapsed) with
     the 5-min floor (computed by the callers), case/type/description from
     the task (subtask text appended; an explicit subtask wins, else the
     first open one is picked up), date today, _fromTask/_fromSubtask
     prefill flags. timerTeRef marks "this te modal belongs to the stop
     flow" so the [mod] effect below can pump the stop-all queue (one
     canonical modal after another) or start a swap-pending timer when the
     modal closes — saved OR cancelled (cancel = no entry; the timer is
     already removed, unchanged semantics). */
  const timerTeRef = useRef<any>(null);
  const openTimerLogTe = ({ taskId, subtaskId, eventId, mins, pendingKey }: any) => {
    const tsk = tasks.find((x: any) => x.id === taskId);
    /* BRIEF 91 §3a — AN EVENT SUBTASK'S TIMER STOPS ONTO A REAL ENTRY.
       This function seeded every field from `tsk`, so a timer whose parent is
       an agenda event (no taskId) produced a time entry with no matter, no
       activity type and no description — silently, on the money path. `_evt` is
       the same resolution for the other parent kind, and it is consulted ONLY
       when there is no task, so nothing about a task timer moves.
       `TimeEntry` has no taskId column (schema.prisma:818) — `subtaskId` is the
       real binding and it is parent-agnostic, which is why the entry needs
       nothing else invented for it. */
    const _evt =
      !tsk && (eventId != null || subtaskId != null)
        ? (agendaEvents || []).find(
            (e: any) =>
              e &&
              (eventId != null
                ? e.id === eventId
                : Array.isArray(e.subtasks) &&
                  e.subtasks.some((s2: any) => s2 && s2.id === subtaskId)),
          ) || null
        : null;
    const _par = tsk || _evt;
    const today = new Date().toISOString().slice(0, 10);
    let _stid = subtaskId || null;
    let _stx = null;
    if (_stid && _par && Array.isArray(_par.subtasks)) {
      _stx = _par.subtasks.find((s2: any) => s2.id === _stid) || null;
    } else if (!_stid && _par && Array.isArray(_par.subtasks)) {
      const _open = _par.subtasks.find((s2: any) => !s2.done);
      if (_open) {
        _stid = _open.id;
        _stx = _open;
      }
    }
    const _tt = _par?.title || "";
    /* C19 — no "X — Y" composition: the case lives in its own field. A
       task-level timer seeds the TASK title (even when a subtask is
       auto-linked for attribution); an explicit subtask timer seeds the
       subtask text. */
    const _desc = subtaskId && _stx ? _stx.text || "" : _tt;
    timerTeRef.current = { pending: pendingKey || null };
    openModal("te", {
      caId: _par?.caseId || "",
      /* Round 15 (C91 family) — cc-bound tasks bind their collective case on
         the timer-stop log too (this path predated C87 and bound nothing). */
      ccId: _par?.ccId || "",
      coId: tsk?.assignedCoId || currentUser || "",
      date: today,
      type: _par?.type || "",
      desc: _desc,
      billed: false,
      min: mins,
      /* Round 16 (C97 belt) — the TIMER knows its taskId even when the task
         row is absent from the viewer's tk (retracted delegation, list churn):
         carry the id itself, so the create body always ships the taskId hint
         and the server-side case adoption can bind the entry regardless.
         §3a — an EVENT subtask's timer has no task to hint at, and must not
         invent one: `_fromTask` is omitted entirely there rather than sent as
         `true`, which is a "there is a task, I just cannot name it" claim. */
      ...(_evt && !tsk ? {} : { _fromTask: tsk?.id ?? taskId ?? true }),
      ...(_stid ? { subtaskId: _stid, _fromSubtask: 1 } : {}),
    });
  };
  const stopAllTimers = () => {
    setActiveTimers((prev: any) => {
      const now = Date.now();
      const queue = prev.map((tm: any) => {
        const el = tm.paused ? tm.elapsed : tm.elapsed + (now - tm.startedAt);
        return {
          taskId: tm.taskId,
          subtaskId: tm.subtaskId || null,
          // §3a — the parent kind rides the queue too; without it a queued
          // event-subtask timer would log the caseless entry this brief fixed.
          eventId: tm.eventId || null,
          elapsed: el,
          mins: Math.max(5, roundUp5(el)),
        };
      });
      if (queue.length === 0) return prev;
      const first = queue[0];
      stopQueueRef.current = queue.slice(1);
      openTimerLogTe({
        taskId: first.taskId,
        subtaskId: first.subtaskId || null,
        eventId: first.eventId || null,
        mins: first.mins,
      });
      setFocusedTimerIdx(0);
      return [];
    });
  };
  // C12 — queue pump: when the stop flow's te modal closes (save or cancel),
  // open the next queued canonical modal, or start the swap-pending timer.
  useEffect(() => {
    if (activeModal !== null || !timerTeRef.current) return;
    const { pending } = timerTeRef.current;
    timerTeRef.current = null;
    if (pending) {
      /* 116 §3 — the pending start is a KEY, so the swap resumes the thing
         the user actually clicked: an event, a subtask, or a task. */
      setTimeout(
        () =>
          startTimer(pending.taskId ?? undefined, pending.subtaskId || undefined, {
            eventId: pending.eventId || null,
          }),
        50,
      );
      return;
    }
    if (stopQueueRef.current.length > 0) {
      const next = stopQueueRef.current[0];
      stopQueueRef.current = stopQueueRef.current.slice(1);
      setTimeout(
        () =>
          openTimerLogTe({
            taskId: next.taskId,
            subtaskId: next.subtaskId || null,
            eventId: next.eventId || null,
            mins: next.mins,
          }),
        50,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal]);
  const getTimerElapsed = (tm?: any) => {
    const t = tm || activeTimer;
    if (!t) return 0;
    if (t.paused) return t.elapsed;
    return t.elapsed + (Date.now() - t.startedAt);
  };
  const roundUp5 = (ms: any) => {
    const inc = timerRound || 5;
    const raw = ms / 1000 / 60;
    return raw <= 0 ? 0 : Math.ceil(raw / inc) * inc;
  };
  // A5's factory sits HERE, below `openTimerLogTe` and `roundUp5`, and not
  // where the two handlers stood. The originals were arrows that read those
  // two names when CALLED; a factory reads them when CONSTRUCTED, so leaving
  // the call where the handlers were would hit the temporal dead zone on
  // every render. `focusedTimerIdx` is a useState value and constant within
  // a render, so passing it by value is what the closure did.
  // A5 (brief 65) — both handlers are one `stop` in src/lib/timerStops; the
  // three things that differed between them are its three arguments.
  const { stopTimerAsk, stopTimerForSwap } = makeTimerStops({
    setActiveTimers,
    setFocusedTimerIdx,
    setTimerLimitDlg,
    openTimerLogTe,
    roundUp5,
    focusedTimerIdx,
  });
  const getTimerDisplay = () => fmtElapsed(getTimerElapsed());
  activeTimerRef.current = activeTimer;
  /* Phase 29 (item 2) — the Phase-27 postMessage/string-injection PiP
     machinery (pipCSS/pipHTML/setupPipDoc, sendTimerFull/sendTimerTick, the
     500ms tick interval and the window "message" handler — including the
     dead navCase message, which never had a handler) is gone. The pop-out
     now hosts a LIVE React portal (renderColumn below); elapsed readouts
     self-tick via <SelfTick> inside CompactColumn. What remains here is
     close detection: Document PiP windows fire "pagehide" (wired in
     openPopOut); the window.open fallback can only be caught by the 500ms
     closed-poll. */
  useEffect(() => {
    if (!popOutCtr) return;
    const iv = setInterval(() => {
      if (!popOutWinRef.current || popOutWinRef.current.closed) {
        popOutWinRef.current = null;
        setPopOutCtr(null);
      }
    }, 500);
    return () => clearInterval(iv);
  }, [popOutCtr]);
  // 74 — the same close detection for the call's window. Document PiP fires
  // `pagehide` (wired at open); the `window.open` fallback can only be caught by
  // this poll, and §5 requires that closing it end nothing but itself.
  useEffect(() => {
    if (!callPopCtr) return;
    const iv = setInterval(() => {
      if (!callPopWinRef.current || callPopWinRef.current.closed) {
        callPopWinRef.current = null;
        setCallPopCtr(null);
      }
    }, 500);
    return () => clearInterval(iv);
  }, [callPopCtr]);
  /* 186 A.3a — the same close detection for the two chat windows, one poll. It
     keys on the MATTER (`_chatPopDid`) and reads the two window REFS, never its
     own state: an effect that lists a state in its deps and sets it in its body
     re-enters itself, and with a cleanup that is the fatal shape
     (scripts/lint/effect-sets-its-own-dep.js, brief 78). A ref that is null is
     a chat that is not popped out, so the tick costs two null checks. */
  useEffect(() => {
    const iv = setInterval(() => {
      const gw = gChatPopWinRef.current;
      if (gw && gw.closed) {
        gChatPopWinRef.current = null;
        setGChatPopCtr(null);
      }
      /* The popped-out case chat is the sidebar row's chat: when the sidebar
         would show none (the matter left), the window closes with the row. */
      const cw = cChatPopWinRef.current;
      if (cw && (cw.closed || _chatPopDid == null)) {
        if (!cw.closed) cw.close();
        cChatPopWinRef.current = null;
        setCChatPopCtr(null);
      }
    }, 500);
    return () => {
      clearInterval(iv);
    };
  }, [_chatPopDid]);
  useEffect(() => {
    if (!timerDd) return;
    const h = (e: any) => {
      if (timerDdRef.current && !timerDdRef.current.contains(e.target))
        setTimerDd(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [timerDd]);
  /* ── 206 §8.4 [RULED — Laurent: "fix the surfaced items"] — THE TIMER'S TYPE
     DROPDOWN GETS AN ESCAPE, AND ONLY THEN A REGISTRATION ───────────────────

     205 §6 lifted this box over an open pane (`Z.picker`) and 205 §5
     DELIBERATELY did not register it as "painted above the pane", for a reason
     that was correct at the time and is recorded in `lib/paneStacking.ts`: it
     had NO Escape handler at all — it closed on an outside mousedown and
     nothing else — so registering it would have made Escape do NOTHING while
     it was open, which is worse than closing the pane behind it. The order was
     the finding: a surface cannot own a key it does not answer.

     SO THE CLOSE COMES FIRST AND THE REGISTRATION FOLLOWS IT, in that order,
     and they are one effect because they are one fact: this dropdown is open,
     it answers Escape, therefore it owns Escape.

     ON `document`, LIKE THE OUTSIDE-MOUSEDOWN ABOVE IT, and not on `window`:
     the two panes listen on `document` too (App's Eisenhower effect,
     BentoDialog), and 205 §5 measured that a keydown reaches `document` before
     `window` — so a `window` listener here could not beat them however it was
     written. It does not need to: `isAbovePane()` is what the panes ask, and
     `useAbovePane` below is what answers. `stopPropagation` is still called,
     so the chain in `useAppHotkeys` does not also act on the same press.

     THE MENUSELECT COMPARISON, and why this one needs the registry when
     « Viewing as » does not: MenuSelect's Escape is a REACT handler, and React
     delegates keydown at the root container — a descendant of `document` — so
     its `stopPropagation()` stops the pane's listener from ever running. This
     box's dropdown has no React key handler and its trigger is not focused
     after a click, so there is nothing to delegate; a real `document` listener
     plus the registry is the shape that fits it. */
  useEffect(() => {
    if (!timerDd) return undefined;
    const h = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setTimerDd(false);
    };
    /* `globalThis.document` and not the bare global: `no-undef` does not know
       `document` under this config, and the ratchet may never rise. Identical
       target, identical phase — which is the whole of the mechanism above. */
    globalThis.document.addEventListener("keydown", h);
    return () => { globalThis.document.removeEventListener("keydown", h); };
  }, [timerDd]);
  /* The other half: while it is open it is painted above any open pane
     (205 §6 gave it `Z.picker`), and now that it CLOSES on Escape it may say
     so — a pane reads this count at the keypress and stands down. */
  useAbovePane(!!timerDd);
  /* Phase 29 (items 2/3) — open the pop-out column. The pop-out document is
     a LIVE React surface: the main tree portals <CompactColumn/> into a
     container div inside it, so app state, i18n context (a language switch
     updates an open pop-out) and theme stay live. Both Phase-27 open paths
     are preserved: Document PiP first, window.open fallback. The app
     stylesheet + Google-font links are cloned into the new document at open;
     theme vars / zoom / app font / theme-dark class are mirrored per commit
     by the sync effect below. Modals/dialogs NEVER render in this document —
     rMod() and every dialog stay in the main tree. */
  const _colOpeningRef = useRef<any>(false);
  /* ── 74 §4 — ONE MECHANISM, WIDENED, NOT A SECOND ONE ───────────────────────
     The brief is explicit: `:3856` is the pop-out mechanism and if it needs
     widening, widen it. It did — brief 74 needs a SECOND surface (the call, in
     front of whatever the sharer is looking at) and the column already owned
     every line of this. So the part that is about WINDOWS rather than about the
     column moved into this helper, and both callers use it: Document PiP first,
     `window.open` fallback, the app stylesheet and font link cloned into the new
     document, a container div created and returned. Nothing about it is new
     behaviour — the column's own open is now four lines of intent over the same
     body it always ran.
     THE ONE-PiP-WINDOW LIMIT, MEASURED (§3 flags it UNVERIFIED and asks for the
     measurement first). In a real Chromium — headless, via playwright:
       · `documentPictureInPicture` is present and `documentPictureInPicture.window`
         is a SINGLE accessor: after a second `requestWindow()` it points at the
         SECOND window and no longer at the first. **A page can address exactly
         one PiP window at a time**, which is the fact a design has to obey.
       · a second `requestWindow()` in a FRESH gesture succeeds and does not
         throw; whether the displaced window's pixels go away is NOT measurable
         here, because a headless browser has no window manager (the first
         object read `closed: false` afterwards, which proves nothing either way).
       · `requestWindow()` needs its OWN user activation: a second call later in
         the SAME gesture threw `NotAllowedError: Document PiP requires user
         activation`.
     So the behaviour chosen is the one the accessor forces: **the call's pop-out
     takes the window, and the column's is closed first.** Not queued (there is
     nothing to queue on) and not refused (the call is the urgent one — the
     sharer cannot see it at all, which is the whole brief). */
  /* ── 74q — THE POP-OUT WINDOW TRACE ─────────────────────────────────────────
     Every open, close, resize and geometry read of a pop-out, with a timestamp, the
     CALLER, and the window's own numbers at that instant. Six readings of screenshots
     were wrong before this existed; a screenshot shows a window's state and never the
     event that changed it, and the events that matter here are a close nobody clicked
     and a resize the browser refused.
     UNCONDITIONAL, and that is deliberate. It is a handful of lines per share, it is
     the only instrument that can settle this class, and gating it behind a flag would
     mean the next occurrence produces nothing again — the same argument 70 §11bis made
     for its stall warning and 74o (a) for its identity log.
     `screenX`/`screenY` are read as well as the size: 74p's whole finding was that the
     MOVE came from a close-and-reopen rather than from a move, and a trace that logged
     size alone could not have distinguished those. */
  const _popTrace = (what: string, caller: string, win?: Window | null) => {
    let geom = "";
    try {
      if (win && !win.closed) {
        geom = ` ${win.innerWidth}×${win.innerHeight} at ${win.screenX},${win.screenY}`;
      } else if (win) {
        geom = " (closed)";
      }
    } catch (e) { geom = ` (geometry unreadable: ${(e as { name?: string }).name || "error"})`; }
    /* 74v — THE TAB'S OWN innerWidth RIDES ALONG, because it is the quantity the
       call window's geometry used to be a live function of and the one thing this
       box cannot measure across a real share boundary (`getDisplayMedia` is
       `NotSupportedError` headless). It is the OPENER's width even when the line
       describes the pop-out — that is the point: this script runs in the opener's
       realm, so this is the `vw` the layout reads. Logged at every share boundary,
       so the before/after is read off the console rather than reasoned about. */
    let main = "";
    try { main = ` · tab ${window.innerWidth}×${window.innerHeight}`; } catch { /* ignore */ }
    console.info(`[popout ${new Date().toISOString().slice(11, 23)}] ${what} — ${caller}${geom}${main}`);
  };
  /* 209 §27 [RULED 21] — WIDENED, NOT FORKED (74 §4's rule for this door): a
     surface may ask for an ORDINARY window (`pip: false`) at a place of its own
     (`left`). The chats do: the page's one PiP window is the column's (74 §4,
     74d), so a chat that took it closed the column — ruling 21 has them coexist.
     `window.open` runs before any await, inside the click's own activation. */
  const _openPopOutWindow = async (
    opts: { id: string; width: number; height: number; onClosed: () => void; pip?: boolean; left?: number },
  ): Promise<{ win: Window; ctr: HTMLDivElement } | null> => {
    let win: Window | null = null;
    try {
      if (opts.pip !== false && "documentPictureInPicture" in window) {
        // Bound to a local first: assigning the API's untyped result straight into
        // a `Window | null` leaves it un-narrowed, and widening the declaration to
        // `any` to dodge that is the thing the ratchet exists to refuse.
        const pipWin = await documentPictureInPicture.requestWindow({ width: opts.width, height: opts.height });
        pipWin.addEventListener("pagehide", opts.onClosed);
        win = pipWin;
      }
    } catch {
      win = null;
    }
    if (!win) {
      _popTrace(opts.pip === false ? `an ordinary window, as asked` : `requestWindow FAILED or unavailable, falling back to window.open`, opts.id);
      // 213B §9 [RULED 42] — the right edge of the APP'S OWN screen (src/lib/popOutPlace.ts), not of the first one
      win = window.open(
        "",
        opts.id,
        "width=" + opts.width + ",height=" + opts.height
          + ",left=" + (opts.left ?? rightEdgeLeft(appScreen(window), opts.width))
          + ",top=20,alwaysRaised=yes",
      );
      if (!win) return null;
    }
    try {
      const doc = win.document;
      doc.head.querySelectorAll("style,link").forEach((n: any) => n.remove());
      document.head
        .querySelectorAll('style,link[rel="stylesheet"],#lx-font-link')
        .forEach((n) => doc.head.appendChild(doc.importNode(n, true)));
      doc.body.innerHTML = "";
      const ctr = doc.createElement("div");
      ctr.id = opts.id;
      ctr.style.height = "calc(100dvh / var(--ui-zoom, 1))";
      doc.body.appendChild(ctr);
      // What was ASKED for against what was GIVEN — the one comparison that says
      // whether Chrome honoured the request or substituted remembered bounds (74m).
      _popTrace(`opened (requested ${opts.width}×${opts.height})`, opts.id, win);
      // 206D §5.1 — into the one registry the page's unload closes (module scope).
      for (const w of POP_OUT_WINDOWS) if (w.closed) POP_OUT_WINDOWS.delete(w);
      POP_OUT_WINDOWS.add(win);
      return { win, ctr };
    } catch {
      try { win.close(); } catch { /* ignore */ }
      return null;
    }
  };
  const openPopOut = async () => {
    if (popOutWinRef.current && !popOutWinRef.current.closed) {
      try {
        popOutWinRef.current.focus();
      } catch {}
      return;
    }
    if (_colOpeningRef.current) return;
    _colOpeningRef.current = true;
    /* Default open size (INITIAL only — per C37 nothing resizes/moves the
       window after open; the user can still resize manually. C50 carves
       out the single exception: the column's fold toggle resizes the
       HEIGHT inside its own click gesture — see CompactColumn.jsx).
       Round 14 (C83i) — DETERMINISTIC request, zoom math made explicit:
       window px are VISUAL px while the column lays out in CSS px under the
       mirrored documentElement zoom (the C53-verified model), so the WIDTH
       request scales by the current --ui-zoom — the column always opens
       with the same 330 CSS px of content (the C41/C70 layout constant)
       instead of squeezing at higher zoom levels. The HEIGHT request is
       always the full screen.availHeight — the maximum available — with no
       fudge subtraction and no variable floor: one constant request per
       (screen, zoom) configuration. Chrome clamps a Document-PiP window to
       its max overlay size (C44) — the clamp is accepted; the window.open
       fallback honors the full height. */
    /* 151E §2(b) — the width request scales by the POP-OUT's own zoom (the
       pinned xlarge step), not the main app's: the column always opens with
       the same 330 CSS px of content under the zoom it will actually
       render at. */
    const W = Math.round(330 * POPOUT_ZOOM),
      H = (window.screen && window.screen.availHeight) || 1080;
    const opened = await _openPopOutWindow({
      id: "lx-col-root",
      width: W,
      height: H,
      onClosed: () => { popOutWinRef.current = null; setPopOutCtr(null); },
    });
    if (opened) {
      popOutWinRef.current = opened.win;
      setPopOutCtr(opened.ctr);
    } else {
      popOutWinRef.current = null;
      setPopOutCtr(null);
    }
    _colOpeningRef.current = false;
  };
  const closePopOut = () => {
    if (popOutWinRef.current && !popOutWinRef.current.closed) {
      try {
        popOutWinRef.current.close();
      } catch {}
    }
    popOutWinRef.current = null;
    setPopOutCtr(null);
  };
  /* 74 §3 — the call's pop-out. Sized for faces and a control row, not for a
     column: four tiles at 150px plus the row. The column's 330px width constant
     is the column's; this one is its own and says so.
     IT TAKES THE PiP WINDOW FROM THE COLUMN, because the measurement above says
     a page can address only one. The column is NOT auto-restored afterwards, and
     that has a measured reason rather than a lazy one: `requestWindow()` needs
     its OWN user activation, so a restore triggered by the share ending would be
     refused by the browser. The column is one click away in the sidebar, where it
     always was. */
  const _callPopOpeningRef = useRef(false);
  const openCallPopOut = async () => {
    if (callPopWinRef.current && !callPopWinRef.current.closed) {
      _popTrace("already open → focus only, nothing resized", "openCallPopOut", callPopWinRef.current);
      try { callPopWinRef.current.focus(); } catch { /* ignore */ }
      return true;
    }
    _popTrace("open requested", "openCallPopOut");
    if (_callPopOpeningRef.current) return false;
    _callPopOpeningRef.current = true;
    try {
      /* ── 74d RULED — THE COLUMN IS EVICTED, AND IT DOES NOT COME BACK ─────────
         The compact column KEEPS this mechanism rather than moving to the
         `window.open` fallback. A page is given exactly ONE Document-PiP window
         (measured, 74: `documentPictureInPicture.window` is a single accessor), so
         two surfaces that both want it must take turns, and this line is the turn
         changing hands.
         WHY NOT `window.open` FOR THE COLUMN: it would gain a title bar, and pay
         popup blocking and the loss of always-on-top for it. The column's whole
         value is being visible beside another application, which is exactly what
         always-on-top provides and what a blocked popup does not provide at all.
         The title is not worth those two.
         AND THE COLUMN IS NOT RESTORED when the call's pop-out closes. That is the
         accepted consequence, recorded here rather than left to be discovered as a
         bug: restoring it would mean re-opening a PiP window with no user gesture
         — refused by the same rule that governs every other resize and open in
         this feature — so it could only ever be attempted, not guaranteed. An
         eviction the user can undo with one click is an annoyance; a surface that
         sometimes reappears and sometimes does not is worse. */
      if (popOutWinRef.current && !popOutWinRef.current.closed) closePopOut();
      const _z = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
      /* 74l (b) — OPEN AT THE SURFACE'S OWN SIZE. `360 × 380` were constants that
         predated the seven-control row and the drawers under it, and re-deriving
         them by hand would only postpone the next time they went stale. The tab's
         call window measures itself and reports; this opens at that, scaled by the
         same zoom factor the column's request uses (window px are visual px while
         the surface lays out in CSS px). The fallback is used only if the pop-out is
         somehow asked for before the surface has ever rendered — it cannot make the
         window right, and it is not pretending to. */
      const m = callSurfaceSizeRef.current;
      const opened = await _openPopOutWindow({
        id: "lx-call-root",
        width: Math.round((m ? m.width : 360) * _z),
        height: Math.round((m ? m.height : 380) * _z),
        onClosed: () => { _popTrace("pagehide (closed by the browser or the user)", "onClosed"); callPopWinRef.current = null; setCallPopCtr(null); },
      });
      if (!opened) { _popTrace("open FAILED (no window)", "openCallPopOut"); return false; }
      callPopWinRef.current = opened.win;
      setCallPopCtr(opened.ctr);
      return true;
    } finally {
      _callPopOpeningRef.current = false;
    }
  };
  const closeCallPopOut = () => {
    // The caller is the point: 74p's "move" was a close from a path nobody clicked.
    _popTrace("CLOSE", new Error().stack?.split("\n")[2]?.trim() || "unknown caller", callPopWinRef.current);
    if (callPopWinRef.current && !callPopWinRef.current.closed) {
      try { callPopWinRef.current.close(); } catch { /* ignore */ }
    }
    callPopWinRef.current = null;
    setCallPopCtr(null);
  };
  /* ── 186 A.3a — THE CHAT POP-OUTS, through the same door ────────────────────
     209 §27 [RULED 21] REPLACES WHAT STOOD HERE: « a chat takes the PiP window
     the way the call takes it from the column — whatever else holds it is
     closed FIRST … RULED 186 (13): the general chat, the case chat and the
     column replace each other ». The column and a chat now COEXIST, side by
     side: a chat opens as an ordinary window beside the column (`pip: false`,
     below), and only the other chat is closed. The open still runs on the
     click's own task: with `pip: false` the `window.open` inside
     `_openPopOutWindow` precedes any await (A.2's constraint).
     186 A.6 (a) — A CHAT ASKS FOR EXACTLY WHAT THE COLUMN ASKS FOR: the same
     width and height expressions as `openPopOut` above (330 CSS px of content
     under the pinned POPOUT_ZOOM, the full screen.availHeight), and the theme
     mirror below pins its document to POPOUT_ZOOM as it pins the column's. The
     first cut sized it as the retired window was (350 × 500 at the main root's
     zoom, the call's arithmetic); Laurent's glance ruled the column's geometry.
     What is ASKED is the app's; what is GIVEN is the browser's — Chrome
     substitutes remembered bounds for a window it has seen before (74m), and
     `_openPopOutWindow`'s trace prints both so the two are never confused. */
  const closeChatPopOut = (kind: "g" | "c") => {
    const winRef = kind === "g" ? gChatPopWinRef : cChatPopWinRef;
    const setCtr = kind === "g" ? setGChatPopCtr : setCChatPopCtr;
    const w = winRef.current;
    if (w && !w.closed) w.close();
    winRef.current = null;
    setCtr(null);
  };
  const _chatPopOpeningRef = useRef(false);
  const openChatPopOut = async (kind: "g" | "c") => {
    const winRef = kind === "g" ? gChatPopWinRef : cChatPopWinRef;
    const setCtr = kind === "g" ? setGChatPopCtr : setCChatPopCtr;
    const held = winRef.current;
    if (held && !held.closed) {
      held.focus();
      return;
    }
    if (_chatPopOpeningRef.current) return;
    _chatPopOpeningRef.current = true;
    try {
      /* 209 §27 [RULED 21] — THE COLUMN AND A CHAT COEXIST, side by side: neither
         closes the other (186's (13), « replace each other », is replaced). The
         chat no longer takes the page's one PiP window — the column keeps it —
         so it opens as an ordinary window, as tall, BESIDE the column, to its
         left: where the column's window is when it is open, else where the
         column opens (its window.open place, `availWidth − width − 20`), so
         whichever opens first, they stand side by side. In Chrome it is not
         always on top, as the column is; in Safari both were ordinary windows
         already, at one place, covering each other. With the PiP window no
         longer taken, the call's pop-out is not closed either — the reason it
         was is gone; the call still takes the PiP window from the column (74d).
         The two chats still share one window. */
      closeChatPopOut(kind === "g" ? "c" : "g");
      const W = Math.round(330 * POPOUT_ZOOM),
        H = (window.screen && window.screen.availHeight) || 1080;
      const colWin = popOutWinRef.current && !popOutWinRef.current.closed ? popOutWinRef.current : null;
      /* 213B §9 [RULED 42] — beside the column's window, to its left, ONLY when
         that window is open; else at the right edge of the app's own screen,
         20 px in (src/lib/popOutPlace.ts — the arithmetic the recording runs). */
      const opened = await _openPopOutWindow({
        id: kind === "g" ? "lexcrm_gchat" : "lexcrm_cchat",
        width: W,
        height: H,
        pip: false,
        left: chatLeft(appScreen(window), W, popOutLeftOf(colWin)),
        onClosed: () => { winRef.current = null; setCtr(null); },
      });
      if (opened) {
        winRef.current = opened.win;
        setCtr(opened.ctr);
      } else {
        winRef.current = null;
        setCtr(null);
      }
    } finally {
      _chatPopOpeningRef.current = false;
    }
  };
  /* 206D §5.2 — THE TOGGLE ASKS THE WINDOW, NOT THE STATE. CENSUS (WebKit,
     `scripts/drive-206d-glance.js --part=closepath`): a chat pop-out closed from
     its own side IS noticed — the 500 ms poll above hands the chat back in ~440 ms
     and the next title click opens a new window. But inside those 500 ms the
     container state still says "popped", and a title clicked then (`--fast`)
     closed a window that no longer existed: nothing opened, the chat was only
     handed back — measured on the case chat. The window's own `closed` is true
     at once, so it decides: a popped chat whose window is gone is opened again. */
  const toggleChatPopOut = (kind: "g" | "c") => {
    const w = (kind === "g" ? gChatPopWinRef : cChatPopWinRef).current;
    if ((kind === "g" ? gChatPopCtr : cChatPopCtr) && w && !w.closed) closeChatPopOut(kind);
    else void openChatPopOut(kind);
  };
  /* 74q — THE MIDDLE STEP OF THE SEQUENCE, RECORDED AS A NON-EVENT. 74p removed the
     focus-close, so clicking into the tab should now do NOTHING to the pop-out — and a
     trace that simply fell silent could not tell "nothing happened" from "nothing was
     watching". This says the focus arrived and names the window's geometry at that
     moment, so the step before and the step after are comparable. */
  useEffect(() => {
    if (!callPopCtr) return undefined;
    const onFocus = () => _popTrace("main window focused — pop-out untouched (74p)", "window.focus", callPopWinRef.current);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [callPopCtr]);
  /* ── 74p — THE FOCUS-CLOSE IS GONE, AND IT WAS THE "MOVE" ───────────────────
     §3 (amended) said the pop-out lives until the CALL ends, and then permitted one
     thing on top: it MAY close once the share has stopped and the main window
     regains focus. That permission is what produced the symptom Laurent photographed,
     one step removed from its apparent cause:
       stop the share → click back into the tab → the pop-out closes, silently →
       start another share → a NEW window opens, at Chrome's position and at today's
       measurement.
     Experienced as "starting a share moved and reshaped my window", while the actual
     event was a close nobody asked for. **74p's invariant — opening, closing,
     starting or stopping a share never changes the pop-out's size or position — is
     therefore satisfied by NOT CLOSING, and it cannot be satisfied any other way.**
     MEASURED, which is why there is no alternative: `requestWindow` takes width and
     height ONLY — there is no position in it — and it CONSUMES the transient
     activation, so `moveTo` (which exists, and does not throw on a gesture) cannot be
     called at open to restore a remembered position. A reopened window therefore goes
     wherever Chrome puts it, always.
     WHAT REPLACES THE PERMISSION, so nobody is trapped: the pop-out lives until the
     call ends (§3 amended, unchanged), and the moment the share stops the tab shows
     74f's marker — "The call is in the floating window", with "Bring it back here" —
     because that marker is suppressed only for a CAPTURER (74h) and a stopped share
     is no longer capturing. So the way back is a visible control the user chooses,
     rather than a window that vanished when they looked away. */
  /* Stage-3 round-5 (C37) — the temporary window-resize mechanism is gone:
     it reshaped and drifted the pop-out on every open/close. Panels opened
     inside the pop-out now anchor UNDER their trigger and fit within the
     current surface (see CompactColumn). The pop-out window's size and
     position are never touched programmatically anymore. */
  /* The single column toggle (sidebar .SF button + the column's own chrome):
     phone width flips the in-app column; desktop opens/closes the floating
     pop-out. These are the only entry points besides startTimer's
     auto-open — the old header pop-out button is gone. */
  const toggleColumn = () => {
    if (typeof window !== "undefined" && window.innerWidth < 600) {
      closePopOut();
      setColInApp((v: any) => !v);
      return;
    }
    if (popOutCtr) closePopOut();
    else openPopOut();
  };
  /* Mirror main-document presentation onto the pop-out document after every
     commit: theme CSS vars + --ui-zoom + --app-font live as inline style on
     documentElement, the dark flag as body.theme-dark, and the user font as
     the #lx-font-link href. Attribute string compares keep this cheap, and
     running per-commit guarantees it fires after the theme/zoom/font effects
     above on the same render. */
  useEffect(() => {
    /* 74 — BOTH pop-out documents, not just the column's. A second surface that
       did not get this would render with no theme vars, no zoom and no font — and
       the failure would look like a styling bug rather than a missing mirror. One
       loop over the containers that exist keeps the two from drifting apart. */
    // 186 A.3a — and the two chat pop-outs, the same way.
    for (const _ctr of [popOutCtr, callPopCtr, gChatPopCtr, cChatPopCtr]) {
    if (!_ctr) continue;
    try {
      const doc = _ctr.ownerDocument;
      const st = document.documentElement.getAttribute("style") || "";
      /* 151E §2(b) — after the copy, the COLUMN pop-out's two zoom
         declarations are overridden to the xlarge step, always. The CALL
         pop-out keeps the mirrored zoom untouched (142's call geometry is
         tuned against the main app's). The compare runs on the cached
         SOURCE string, not the live attribute — the override would defeat
         a live compare and re-write every commit.
         186 A.6 (a), RULED 186 (13) — the two CHAT pop-outs are pinned the
         way the column is: they ask for the column's geometry, so they
         render at the column's zoom. Only the call keeps the mirrored one. */
      if (_mirrorSrc.get(doc) !== st) {
        doc.documentElement.setAttribute("style", st);
        if (_ctr === popOutCtr || _ctr === gChatPopCtr || _ctr === cChatPopCtr) {
          /* 204 §3 [RULED] — the app's OWN effective zoom, read off the main
             root before the pin overwrites the copy: the column's section
             headings scale by appZoom / POPOUT_ZOOM so that, under the
             pinned root zoom, they render at the size the app chose. The
             rows and the timer stay at the pin (see POPOUT_ZOOM). The main
             document never carries this var: there the root zoom IS the
             app's size and the headings already follow it. */
          const appZoom = parseFloat(document.documentElement.style.getPropertyValue("--ui-zoom")) || 1;
          doc.documentElement.style.zoom = String(POPOUT_ZOOM);
          doc.documentElement.style.setProperty("--ui-zoom", String(POPOUT_ZOOM));
          doc.documentElement.style.setProperty("--col-title-scale", String(appZoom / POPOUT_ZOOM));
        }
        _mirrorSrc.set(doc, st);
      }
      if (doc.body.className !== document.body.className)
        doc.body.className = document.body.className;
      /* Stage-3 C22 — the pop-out document gets the same configured title
         + theme-color meta as the main document. NOTE what browsers honor:
         Chrome's Document-PiP title bar always shows the page ORIGIN (an
         anti-spoofing mandate) and paints its own bar — title/color there
         are browser-controlled; the window.open FALLBACK pop-up does show
         document.title. We set both anyway for whatever each surface
         honors now or later. */
      const ttl =
        firmSettings?.windowTitle ||
        firmName ||
        t("app.product_name", { fallback: "Legal Practice Manager" });
      if (doc.title !== ttl) doc.title = ttl;
      const mainMeta = document.querySelector('meta[name="theme-color"]');
      if (mainMeta) {
        let pm2 = doc.querySelector('meta[name="theme-color"]');
        if (!pm2) {
          pm2 = doc.createElement("meta");
          pm2.setAttribute("name", "theme-color");
          doc.head.appendChild(pm2);
        }
        if (pm2.getAttribute("content") !== mainMeta.getAttribute("content"))
          pm2.setAttribute("content", mainMeta.getAttribute("content"));
      }
      const mf = document.getElementById("lx-font-link");
      if (mf) {
        let pf = doc.getElementById("lx-font-link");
        if (!pf) {
          pf = doc.createElement("link");
          pf.id = "lx-font-link";
          pf.rel = "stylesheet";
          doc.head.appendChild(pf);
        }
        if (pf.getAttribute("href") !== mf.getAttribute("href"))
          pf.setAttribute("href", mf.getAttribute("href"));
      }
    } catch { /* a closing window's document goes away mid-write */ }
    }
  });
  const pomGet = (k: any, def: any) => {
    try {
      const v = localStorage.getItem(k);
      return v != null ? +v : def;
    } catch {
      return def;
    }
  };
  const pomSet = (k: any, v: any) => {
    try {
      localStorage.setItem(k, String(v));
    } catch {}
  };
  const pomGetStart = () => pomGet("lx:pomCycleStart", 0);
  const pomGetPings = () => pomGet("lx:pomPingCount", 0);
  const pomSetStart = (v: any) => pomSet("lx:pomCycleStart", v);
  const pomSetPings = (v: any) => pomSet("lx:pomPingCount", v);
  const playPing = (freq = 880, dur = 0.2, vol = 0.15) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + dur);
      setTimeout(() => ctx.close(), 600);
    } catch {}
  };
  const pomNotify = (msg: any) => {
    if ("Notification" in window && Notification.permission === "granted")
      new Notification(
        (firmName ||
          t("app.product_name", { fallback: "Legal Practice Manager" })) +
          " " +
          t("app.timer_suffix", { fallback: "Timer" }),
        {
        body: msg,
        tag: "pom-" + Date.now(),
      });
  };
  const getPomElapsed = () => {
    const s = pomGetStart();
    if (!s) return 0;
    return Date.now() - s;
  };
  // Flicker fix (a): the live elapsed displays now self-tick via <SelfTick>
  // (task rows + the timer-widget readout), so the former App-level 5s
  // setTimerTick interval — which re-rendered the entire App (all rows + any
  // open modal) every 5s — has been removed. `timerTick` state was never read.
  //
  // Phase 27 — pomodoro is GLOBAL, CONTINUOUS and ALERT-ONLY. Once a cycle
  // starts (first timer), it counts down on wall-clock continuously — even
  // when every timer is paused — and re-arms at each interval boundary,
  // chiming + sending a desktop Notification ONCE per boundary. No auto-pause
  // of timers, no escalating ping ladder. The cycle is cleared only when the
  // tray is empty (every timer stopped/logged), so the next session starts
  // fresh. Single 1s interval, reads from localStorage, no React state deps.
  useEffect(() => {
    const iv = setInterval(() => {
      const timers = activeTimersRef2.current || [];
      // Empty tray => no active work session: clear the cycle and bail.
      if (timers.length === 0) {
        if (pomGetStart()) {
          pomSetStart(0);
          pomSetPings(0);
        }
        return;
      }
      const cycleStart = pomGetStart();
      if (!cycleStart) {
        // Active session but no cycle (e.g. timers rehydrated from an older
        // localStorage state) — start one now.
        pomSetStart(Date.now());
        pomSetPings(0);
        return;
      }
      /* 147 §1.6 — `(pomInterval || 20)` STOOD HERE AND IS THE DEFECT. The
         guard on the next line has been in this file since Phase 27 and was
         UNREACHABLE: zero became twenty one line above it, so a user who
         wanted the pomodoro off had no way to say so — and `lx:activeTimers`
         is persisted, so one timer left paused in the tray pinged every 20
         minutes for ever, across reloads, with the app not in front. Now 0
         means off and the guard is what it always said it was. */
      const intMs = pomInterval * 60 * 1000;
      if (intMs <= 0) return;
      const boundary = Math.floor((Date.now() - cycleStart) / intMs);
      const pings = pomGetPings();
      if (boundary > pings) {
        // One (or, after a reload/long idle, several) interval boundaries
        // crossed — alert once and catch the counter up.
        pomSetPings(boundary);
        if (!pomMuted) playPing(880, 0.2, 0.15);
        pomNotify(
          t("app.pom.break_body", withCount(pomInterval, "min", { fallback:
              "Time for a break! You've been working for {min} minutes.",
          })),
        );
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [pomInterval, pomMuted]);
  // Keep a ref to activeTimers for the interval to read without causing effect re-runs
  const activeTimersRef2 = useRef<any>(activeTimers);
  activeTimersRef2.current = activeTimers;
  // Phase 27 — persist the running-timer set + focus so a reload restores them.
  useEffect(() => {
    try {
      localStorage.setItem("lx:activeTimers", JSON.stringify(activeTimers));
      localStorage.setItem("lx:focusedTimerIdx", String(focusedTimerIdx));
    } catch {}
  }, [activeTimers, focusedTimerIdx]);
  // Close any open pop-out window before the page unloads so a reload doesn't
  // leave an orphaned popup (the app loses its ref on mount). The portaled
  // column dies with the main page anyway; this just closes the shell.
  /* 206D §5.1 [THE BUG — Laurent: "sometimes a stale window was left open when
     one clicked either title, so one had two chats at the same time instead of
     toggling"] — EVERY POP-OUT, NOT THE COLUMN'S ALONE, AND ON `pagehide` AS WELL
     AS `beforeunload`. This handler closed `popOutWinRef` only: the two chat
     pop-outs (186 A.3a) and the call's (74) were never added. Document PiP
     windows close with their opener anyway; a `window.open` window — Safari's
     only kind — does not, and its content is a portal of the page that just
     died: it stays on screen, FROZEN, and the new page (its pop-out state empty
     at mount) does not know it exists. REPRODUCED IN WEBKIT
     (`scripts/drive-206d-glance.js --part=reload`): the general chat popped out,
     the main page reloaded — the old page fired `beforeunload` AND
     `pagehide(persisted=false)`, the pop-out stayed open with its title drawn,
     the case chat's title then opened a second one beside it, and the general
     chat's title re-acquired the frozen window by its name.
     NOT ONE MORE LINE PER WINDOW: the handler closes the registry
     `_openPopOutWindow` writes (`POP_OUT_WINDOWS`, module scope). `pagehide`
     too, because Safari fires it where `beforeunload` is not fired (iOS never
     fires `beforeunload`; a page entering the back-forward cache gets only
     `pagehide`) — and a page restored from that cache finds its windows closed
     and the 500 ms close polls (above) hand each surface back. A second close
     is a no-op. */
  useEffect(() => {
    const onUnload = () => {
      for (const w of POP_OUT_WINDOWS) {
        try {
          if (!w.closed) w.close();
        } catch { /* ignore */ }
      }
      POP_OUT_WINDOWS.clear();
    };
    globalThis.addEventListener("beforeunload", onUnload);
    globalThis.addEventListener("pagehide", onUnload);
    return () => {
      globalThis.removeEventListener("beforeunload", onUnload);
      globalThis.removeEventListener("pagehide", onUnload);
    };
  }, []);
  const gSearchRef = useRef<any>(null);
  const bellRef = useRef<any>(null);
  const [headerH, setHeaderH] = useState<number>(60);
  /* 190 glance round 2 [F] (ruled: correct it) — THIS NEVER MEASURED. The
     first render of App is the LoginView (:13420) or the loading splash
     (:13429), neither of which holds `.header`, so a `[]`-dep effect found
     nothing, returned, and the 60 default stood for the whole session: the
     matrix's `top: 59px` against a 57.84 CSS px header — flush by
     coincidence (a 1.4px gap at xlarge), measured 2026-09-15. Two fixes in
     one: the effect re-runs when the gate opens (`authed`, `loading`), and it
     reads `offsetHeight` — CSS px at any zoom — where a rect is VISUAL px
     under the root zoom (the 142 family) and would have put the matrix 15px
     low at xlarge the day the measurement started working. */
  useLayoutEffect(() => {
    const h = document.querySelector<HTMLElement>(".header");
    if (!h) return;
    const m = () => setHeaderH(h.offsetHeight);
    m();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", m);
      return () => window.removeEventListener("resize", m);
    }
    const ro = new ResizeObserver(m);
    ro.observe(h);
    return () => ro.disconnect();
  }, [authed, loading]);
  /* 206E §1.1 [RULED — Laurent: "a MY bento or eisenhower should open NEXT to
     the side bar (irrespective of whether it is open or closed): GUI wise, this
     avoids a step to close the bento/eisenhower with its header icon when one
     wants to jump to another section immediately: saves one click"] — THE
     SIDEBAR'S RIGHT EDGE IS THE PANES' LEFT ONE. ONE measurement, here, handed
     to both frames the way `headerH` is (BentoDialog.tsx:10-12): the aside's
     `offsetWidth`, CSS px at any zoom — the unit a frame's `left` is written in.
     MEASURED in both engines under the xlarge root zoom (16/13): a 210 px box
     reads `offsetWidth` 210, a fixed box at `left: 210px` lands exactly on its
     visual right edge (258.45), and a rect reads visual px (the 142 family).
     The sidebar always starts at the window's left edge, so its width IS its
     right edge in every state the census found: open (`210px`, `max-width:
     25vw`), folded (`.col`, 50), the phone drawer (`.mob`, `position: fixed`
     at left 0, 210 — it is on screen, so the panes stand beside it), and
     `.colHide` (the phone's in-app column: `display: none`, 0 — nothing at
     all). A ResizeObserver follows it through the hamburger's fold, every frame
     of `.sidebar`'s `transition: width`, and a window resize — only while a
     pane may show (`bentoOpen || eisenOpen`, a superset of `_paneOpen` that
     skips the permission, which is not declared yet here), so the fold's
     animation re-renders nothing when no pane is there to follow it.
     `flushSync` IN THE OBSERVER, AND IT IS MEASURED: an observer's callback
     runs after layout and before paint, but a plain `setState` from it is a
     default-priority render that lands in a LATER task — after this frame has
     painted. Sampled every animation frame (`drive-206e-glance.js
     --part=anim`), the frame's edge trailed the sidebar's by exactly one frame
     through the whole 0.2 s transition, up to 47.8 px apart. Committed inside
     the callback, the edge is in place before the frame paints. The first
     reading below is a plain `setState`: a layout effect is already before
     paint, and `flushSync` inside an effect is what React refuses. */
  const sidebarRef = useRef<HTMLElement | null>(null);
  const [sidebarEdge, setSidebarEdge] = useState<number>(0);
  const _paneMayShow = bentoOpen || eisenOpen;
  useLayoutEffect(() => {
    if (!_paneMayShow) return undefined;
    const el = sidebarRef.current;
    if (!el) return undefined;
    const m = (): void => {
      setSidebarEdge(el.offsetWidth);
    };
    m();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      flushSync(m);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [authed, loading, _paneMayShow]);
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (localStorage.getItem("lx:quoteDate") === today) return;
    } catch {}
    // Quote of the day is sourced from the local fallback set only — the old
    // remote quotable.io fetch was removed (dead/unreliable third-party call).
    const fb = getFallbackQuote();
    setTodayQuote(fb);
    try {
      localStorage.setItem("lx:quoteToday", JSON.stringify(fb));
      localStorage.setItem("lx:quoteDate", today);
    } catch {}
  }, []);

  useEffect(() => {
    if (!authed) return;
    (async () => {
      // First-login handshake: ship any legacy lx:* PERSONAL keys to the server
      // for one-shot ingestion into Collaborator.preferences, then prime the
      // usePreferences cache so subsequent mounts skip the redundant GET. Both
      // calls run before any other boot loads to guarantee no consumer reads stale
      // {} prefs (the ld2 splash gate already prevents view rendering until this
      // IIFE completes).
      try {
        const _ingested = await ingestLegacyPrefs();
        if (_ingested) seedPreferences(_ingested);
        else await primePreferences();
      } catch (e: any) {
        console.warn(
          "[prefs] handshake/load failed; UI will use bundled defaults:",
          e && e.message,
        );
      }
      try {
        await primeRoleLabels();
      } catch (e: any) {
        console.warn(
          "[roleLabels] load failed; falling back to Role.name:",
          e && e.message,
        );
      }
      // Phase M+2b: prime the permissions hook (singleton fetches /api/permissions/me).
      // Errors don't block boot — can() falls through to deny.
      try {
        await primePermissions();
      } catch (e: any) {
        console.warn(
          "[permissions] load failed; can() will deny until refetch:",
          e && e.message,
        );
      }
      /* 210 §1 — EACH LIST ASKED ONCE, in the order the boot always asked
         them; the ones that fail are asked again after the tasks (two more
         asks, a second apart) and, still failing, marked as not loaded:
         nothing takes their place. `_apiOr` stood here — any failure, or an
         answer that was not an array, became the local copy `ld` read, or
         `[]` (src/lib/listLoad.ts: the census of those copies, and the rule). */
      const _failedFirst: ListSource[] = [];
      const _first = async (key: string): Promise<unknown[]> => {
        const src = _listSources.get(key);
        if (!src) return [];
        const a = await askList(src, 1, LIST_ASKS);
        if (a.ok) return a.rows;
        _failedFirst.push(src);
        return [];
      };
      setClients(await _first(SK.cl));
      // Gate 4 C6, widened by 58 §2 row 15 — opt into the server-side
      // latest-invoice join on BOTH matter lists, so the one shared column
      // declaration has a value to render on either. The firm-wide `inv`
      // array (loaded below) could compute the same thing client-side; the
      // server flag is for forward-compat with eventual list pagination,
      // where the firm-wide load goes away.
      setCases(await _first(SK.ca));
      setCollectiveCases(await _first(SK.cc));
      setOpponents(await _first(SK.op));
      setOpponentLawyers(await _first(SK.ocu));
      setExperts(await _first(SK.ex));
      setJurisdictions(await _first(SK.ju));
      setCollaborators(await _first(SK.co));
      setTimeEntries(await _first(SK.te));
      setAgendaEvents(await _first(SK.ag));
      setContactPersons(await _first(SK.cp));
      setOpponentContacts(await _first(SK.oc));
      setMediators(await _first(SK.me));
      setArbiters(await _first(SK.ar));
      setAdditionalOpponents(await _first(SK.op2));
      const _bks = (await _first(SK.books)) as { id: number; borrowedById?: unknown; borrowedDate?: string | null }[];
      setBooks(_bks);
      setInvoices(await _first(SK.inv));
      (async () => {
        try {
          const tdMark = new Date().toISOString().slice(0, 10);
          for (const b of _bks) {
            if (b.borrowedById == null || !b.borrowedDate) continue;
            const bd = new Date(b.borrowedDate);
            const td = new Date(tdMark);
            const days = Math.floor(((td as any) - (bd as any)) / (1000 * 60 * 60 * 24));
            if (days < 2) continue;
            const k = "lx:bookNotif:" + b.id;
            try {
              const last = await window.storage.get(k);
              if (last && last.value === tdMark) continue;
            } catch (e) {}
            try {
              await window.storage.set(k, tdMark);
            } catch (e) {}
          }
        } catch (e) {}
      })();
      /* Phase 30 (D-2) — sender-folder maps + external task-type maps are
         FirmSettings columns now (they were localStorage-only firm config —
         RQ6 class C). Server wins; a legacy lx:sfm/lx:xtm value seeds the
         server once when the column is still empty (the PUT is gated, so a
         non-privileged boot just falls back silently until a privileged
         user's first boot ingests it). */
      {
        const _localSf = await ld(SK.sfm, []);
        const _localXt = await ld(SK.xtm, DEFAULT_EXT_TYPES);
        setSfMaps(_localSf);
        setExtTypeMaps(_localXt);
        try {
          const _fsMaps = (await getFirmSettings()) as FirmSettingsShape | null;
          if (Array.isArray(_fsMaps?.senderFolderMaps)) {
            setSfMaps(_fsMaps.senderFolderMaps);
          } else if (Array.isArray(_localSf) && _localSf.length) {
            updateFirmSettings({ senderFolderMaps: _localSf }).catch(() => {});
          }
          if (Array.isArray(_fsMaps?.externalTaskTypeMaps)) {
            setExtTypeMaps(_fsMaps.externalTaskTypeMaps);
          } else if (Array.isArray(_localXt) && _localXt.length) {
            updateFirmSettings({ externalTaskTypeMaps: _localXt }).catch(
              () => {},
            );
          }
        } catch {
          /* offline boot keeps the local fallback */
        }
      }
      {
        const _bootCu = await ld(SK.cu, null);
        const _authIdent = _readAuthIdentity();
        sCurrentUser(
          presentedIdentity({
            simulation: _decodeSimulation(),
            authIdentity: _authIdent,
            storedSelection: _bootCu,
          }),
        );
      }
      setTasks(await _first(SK.tk));
      if (_failedFirst.length) settleLists(await loadLists(_failedFirst, { from: 2 }));
      try {
        const _fs = (await getFirmSettings()) as FirmSettingsShape | null;
        setFirmSettings(_fs);
        setFirmLogo(_fs?.logo || null);
        const _fn = _fs?.firm?.name || "Legal Practice Manager";
        setFirmName(_fn);
        setFirmNameCache(_fn);
      } catch (e: any) {
        console.warn(
          "[firmSettings] load failed, falling back to defaults:",
          e?.message || e,
        );
        setFirmSettings(null);
        setFirmLogo(null);
        setFirmName("Legal Practice Manager");
        setFirmNameCache("Legal Practice Manager");
      }
      try {
        const _themes = await api.get("/themes");
        setThemes(Array.isArray(_themes) ? _themes : []);
      } catch (e: any) {
        console.warn("[themes] load failed:", e?.message || e);
        setThemes([]);
      }
      try {
        const _ch = (await getAllChatMessages()) as ChatBundleShape | null;
        const _cc = _ch?.ccChatMsgs || {};
        const _ccKeyed = Object.fromEntries(
          Object.entries(_cc).map(([k, v]) => ["cc:" + k, v]),
        );
        setChatMsgs({ ...(_ch?.chatMsgs || {}), ..._ccKeyed });
        setGChat(_ch?.gChat || []);
      } catch (e: any) {
        console.warn("[chat] load failed, falling back:", e?.message || e);
        setChatMsgs(await ld("lx:chat", {}));
        setGChat(await ld("lx:gchat", []));
      }
      if ("Notification" in window && Notification.permission === "default")
        Notification.requestPermission();
      const todCheck = new Date().toISOString().slice(0, 10);
      const lastShown = await ld("lx:dailylast", "");
      if (lastShown !== todCheck) {
        sv("lx:dailylast", todCheck);
        setDailyOverview(true);
      }
      try {
        setChatRead((await getChatReadReceipts()) || {});
      } catch (e: any) {
        console.warn("[chatRead] load failed, falling back:", e?.message || e);
        setChatRead(await ld("lx:chatrd", {}));
      }
      try {
        await window.storage.delete("lx:ol");
      } catch {}
      try {
        await window.storage.delete("lx:ctheme");
      } catch {}
      try {
        await window.storage.delete("lx:fname");
      } catch {}
      /* 210 §1 — the pre-rename migration that stood here rewrote an old
         `lx:ca` copy (olIds → ocuIds) and set it IN PLACE of the list the
         server had just answered: a copy nothing else reads any more
         (src/lib/listLoad.ts's census) is never a list. */
      /* Round 18 (C103) — the unconditional /reports/cdd fetch left this
         boot effect: it fired for EVERY authenticated user, and although the
         JS catch was silent, the browser still logs the network 403 — the
         console noise C96 was meant to remove but never shipped (no C96
         commit exists; the C95→C97 numbering gap). The fetch now lives in
         its own effect below, gated by the SAME rule the server applies
         (lateral.reportsCDD via the permissions engine) and DEFERRED until
         /api/permissions/me has resolved, so the predicate can never race
         the role data. */
      setLoading(false);
    })();
  }, [authed]);

  /* 152 §3 — THE SINGLE-ROW UNDO RESTORES THE ROW, NOT A LIKENESS.
     Four surfaces delete a time entry with an undo — Dashboard's own 5s
     window, CaseView's and CollectiveCaseView's `confirmAndUndo`, and
     InvoiceView's confirm — and all four go through the SAME two engine ops:
     `ent.d(id)` to remove, `ent.c(row)` to put it back. So the cure belongs on
     the pair, not written out four times in four hosts.

     `d` keeps the signed snapshot the DELETE route now answers with, keyed by
     the id it just removed. `c` asks, for a row carrying a server id, whether
     that id has a snapshot waiting: if it has, the undo IS a restore — the
     verbatim row, its id, its createdAt, its provenance columns and its
     released BilledEmail rows — and if it has not, it is an ordinary create,
     byte-for-byte what every other row does.

     NO FALLBACK when a restore is refused. Quietly creating a likeness after
     the server declined the restore is the exact defect this fixes; the
     rejection propagates to the engine's own error path, which reports it.
     The token is consumed on use, and the server re-checks the HMAC, the TTL
     and the per-row ladder regardless of what this map holds.

     The map holds a PROMISE, not a token, and it is stored BEFORE the request
     goes out. Undo is a button that appears the instant the row leaves the
     list, so a click can outrun the DELETE's own response; keying on the
     in-flight promise means the create waits for the token instead of
     racing it to a likeness. */
  const _teRestoreTokens = useRef(new Map<number, Promise<any>>());
  const _teDelete = (id: any) => {
    const key = Number(id);
    const req = deleteTimeEntry(id);
    // Stored arm never rejects — the engine's own error path owns the failure.
    _teRestoreTokens.current.set(
      key,
      req.then((res: any) => (res && res.restore) || null).catch(() => null),
    );
    return req;
  };
  const _teCreate = async (row: any) => {
    const key = row && row.id != null ? Number(row.id) : NaN;
    const pending = Number.isFinite(key) ? _teRestoreTokens.current.get(key) : null;
    if (!pending) return createTimeEntry(row);
    _teRestoreTokens.current.delete(key);
    const tok = await pending;
    if (!tok) return createTimeEntry(row); // the delete itself failed; nothing to restore
    const out: any = await restoreTimeEntry(tok);
    const back = out && Array.isArray(out.restored) ? out.restored[0] : null;
    if (back) return back;
    const why = out && Array.isArray(out.skipped) && out.skipped[0] ? out.skipped[0].code : "generic.server_error";
    throw new Error(why);
  };

  const _apiEnt = {
    [SK.cl]: { c: createClient, u: updateClient, d: deleteClient },
    // Phase 28 (item C/D) — the normal "delete" UX archives (soft, reversible);
    // it removes the row from the live list (archived rows are hidden by default).
    // Hard purge happens only from the Archive view via purgeCase/purgeCollectiveCase.
    [SK.ca]: { c: createCase, u: updateCase, d: archiveCase },
    [SK.cc]: {
      c: createCollectiveCase,
      u: updateCollectiveCase,
      d: archiveCollectiveCase,
    },
    [SK.op]: { c: createOpponent, u: updateOpponent, d: deleteOpponent },
    [SK.ocu]: {
      c: createOpponentLawyer,
      u: updateOpponentLawyer,
      d: deleteOpponentLawyer,
    },
    [SK.ex]: { c: createExpert, u: updateExpert, d: deleteExpert },
    [SK.ju]: {
      c: createJurisdiction,
      u: updateJurisdiction,
      d: deleteJurisdiction,
    },
    [SK.co]: {
      c: createCollaborator,
      u: updateCollaborator,
      d: deleteCollaborator,
    },
    // 152 §3 — the delete keeps the signed snapshot, the create spends it. See
    // the pair defined above `_apiEnt`.
    [SK.te]: { c: _teCreate, u: updateTimeEntry, d: _teDelete },
    [SK.ag]: {
      c: createAgendaEvent,
      u: updateAgendaEvent,
      d: archiveAgendaEvent,
    },
    [SK.tk]: { c: createTask, u: updateTask, d: archiveTask },
    [SK.books]: { c: createBook, u: updateBook, d: deleteBook },
    [SK.cp]: {
      c: createContactPerson,
      u: updateContactPerson,
      d: deleteContactPerson,
    },
    [SK.oc]: {
      c: createOpponentContact,
      u: updateOpponentContact,
      d: deleteOpponentContact,
    },
    [SK.me]: { c: createMediator, u: updateMediator, d: deleteMediator },
    [SK.ar]: { c: createArbiter, u: updateArbiter, d: deleteArbiter },
    [SK.op2]: {
      c: createAdditionalOpponent,
      u: updateAdditionalOpponent,
      d: deleteAdditionalOpponent,
    },
  };
  // the shape src/api.ts throws (message minted from the srvErr row; code from
  // the envelope, null when absent) — read through it, never off `any`
  interface MoveRefusal { code?: string | null; message?: string }
  const _apiErr = (op: any, e: any) => {
    try {
      let m;
      if (e && e.code === "case.folder.outside_library") {
        m = t("toast.folder.outside_library", {
          fallback: "⚠ Selected folder is outside the configured library.",
        });
      } else if ((e as MoveRefusal | null)?.code === "case.folder.not_found") {
        /* 212 §18 — the stop: an address SharePoint has nothing at is refused, and the
           refusal names the address tried; nothing was written. Read through the
           refusal's shape, never off `any`. */
        const _nf = (e as { body?: { address?: unknown } }).body;
        const _addr = typeof _nf?.address === "string" ? _nf.address : "";
        m = t("toast.folder.not_found", {
          address: _addr,
          fallback: "⚠ No folder at this address in SharePoint: " + _addr + " — nothing was saved.",
        });
      } else if (e && e.code === "case.folder.already_claimed") {
        const _ref = (e.body && e.body.conflictingCaseRef) || "";
        m = t("toast.folder.already_claimed", {
          ref: _ref,
          fallback:
            "⚠ This folder is already linked to " +
            (_ref || "another case") +
            ".",
        });
        /* Corr-2 (item 8) — the generic event modal saves meeting legs
           through the s() engine; its structured failures toast translated
           (the server is the race backstop behind the modal's pre-checks). */
      } else if (e && e.code === "reservation.conflict") {
        m = t("modal.meeting.room_conflict_block", {
          fallback:
            "Room conflict — pick another room or time, or bump the existing reservation.",
        });
      } else if (e && e.code === "reservation.bad_slots") {
        m = t("modal.meeting.room_window", {
          fallback:
            "Rooms are reservable 07:00–20:00 — adjust the meeting time.",
        });
      } else if (e && e.code === "meeting.bad_link") {
        m = t("modal.meeting.bad_link", {
          fallback: "The meeting link must be a full http(s):// address",
        });
      } else if (e && e.code === "meeting.time_required") {
        m = t("modal.ag.time_required", {
          fallback: "An online meeting needs a date and a time",
        });
      } else if (e && e.code === "m365.scope_missing") {
        m = t("modal.meeting.scope_missing", {
          fallback:
            "Your Microsoft 365 connection lacks calendar permissions — reconnect M365 in Settings.",
        });
      } else if (e && e.code === "m365.not_connected") {
        m = t("modal.meeting.m365_not_connected", {
          fallback:
            "Microsoft 365 is not connected — connect it in Settings to create meetings.",
        });
      } else if (e && e.code === "collaborator.delete_blocked") {
        // Owed-fixes — the collaborator DELETE 1B guard refuses (409) when the
        // collaborator is still referenced by firm records; the s() engine
        // restores the optimistically-removed row and routes the error here.
        // Mirror client.delete_blocked: surface the localized reason as a toast.
        //
        // 05k §1 — the 409 now carries a per-group breakdown, so the toast says
        // WHAT to clear rather than only that something blocks. `blocking` is
        // read defensively: an older server, or a proxy that drops the body,
        // still gets the bare sentence it always got.
        const _blocked = e.body?.blocking;
        const _parts = _blocked
          ? _BLOCK_GROUP_ORDER.filter((k) => _blocked[k] > 0).map((k) =>
              t("collaborator.blocked." + k, {
                count: _blocked[k],
                fallback: _BLOCK_GROUP_FALLBACK_EN[k],
              }),
            )
          : [];
        m =
          t("collaborator.delete_blocked", {
            // 75 §3 — 05j exists now, so the refusal finally names the way
            // forward (mirrors client.delete_blocked's "archive it instead").
            fallback:
              "This collaborator is referenced by existing firm records and cannot be deleted — archive them instead.",
          }) + (_parts.length ? " — " + _parts.join(" · ") : "");
      } else if (/^(task|agenda)\.move_/.test((e as MoveRefusal).code ?? "")) {
        // 185 (1) — the three move refusals carry the sentence ALONE: no
        // "API update failed:" prefix. `e.message` is already the resolved
        // sentence in the reader's language (src/api.ts mints it from the
        // srvErr row, else the server's own words).
        m = (e as MoveRefusal).message ?? "";
      } else if ((e as { code?: unknown } | null)?.code === "case.language_required") {
        // 209 §5 [RULED 18] — the refusal's own sentence (« Choisissez d'abord la
        // langue du dossier ») and the case form one click away.
        const refusal = e as { body?: unknown; message?: string };
        const action = languageRefusalAction(refusal.body, String(tImperative("case.language.open_form", { fallback: "Open the case form" })));
        // 209 §25 — through the toast, which owns the one clock (its ten seconds)
        toast(refusal.message ?? "", "error", { action });
        return;
      } else {
        m = t("app.api_op_failed", {
          op,
          msg: e.message || String(e),
          fallback: "API {op} failed: {msg}",
        });
      }
      toast(m, "error"); // 209 §25 — the toast's own clock, not a second one
    } catch {}
  };
  const _stableStringify = (v: any) => {
    const seen = new WeakSet();
    const walk = (x: any): any => {
      if (x === null || typeof x !== "object") return x;
      if (seen.has(x)) return null;
      seen.add(x);
      if (Array.isArray(x)) return x.map(walk);
      const o: any = {};
      for (const k2 of Object.keys(x).sort()) o[k2] = walk(x[k2]);
      return o;
    };
    return JSON.stringify(walk(v));
  };
  /* Phase 30 (D-3) — the engine now (a) ROLLS BACK failed creates (the temp
     row is removed instead of lingering as an un-retried zombie that 404s on
     edit) and failed deletes (the row is restored), keeping the C74/C77
     update rollback; (b) RETURNS an outcome promise ({ ok, failed }) so the
     sv* helpers toast success only AFTER the server confirmed (no more
     success-then-error); (c) drops the C94d 60s failed-update cooldown,
     which reverted re-attempts client-side WITHOUT a network call — a
     retry now actually retries. */
  /* LEGAL-LANE (fix 1) — (d) an OPTIONAL per-row callback. The engine already
     knew when each row settled; it just never said so, and the aggregate
     outcome promise resolves only once every op is done. A caller that wants
     to show "Importing… 7/15" cannot derive 7 from that. `opts.onRow` is
     invoked once per settled op with { ok, op, id, item, error }, so progress
     is the SERVER's count of confirmed rows, not a client-side guess.
     `opts.onStart(total)` reports the DENOMINATOR the same way — the engine
     owns the prev/next diff, so it is the only thing that can count the ops
     exactly. A caller re-deriving it would be guessing at the engine's own
     rules (stable-stringify equality, the _pendingCreates hash skip).
     Widening the engine rather than reimplementing it in the importer: the
     rollback, temp-id swap and dedupe all stay in one place (the duplicate-
     retry that stood beside them went at 207 §5). Callers that pass no opts
     are byte-for-byte unaffected. */
  /* 207B §5 — THE LIST SYNC'S ONE SAVE IN FLIGHT PER ROW (src/lib/saveQueue.mjs):
     an inline field saves every keystroke through `persist` — a collective-case
     member's Quantum cell among them — and the answers came back over the
     typing (« 98765 » typed, 95 kept: MEASURED, answers held 400 ms, both
     engines). A row's updates now leave one at a time, the latest waiting; the
     queue is dropped once idle. */
  const _rowSaveQueuesRef = useRef(new Map<string, SaveQueue<Record<string, unknown>, { sent: Record<string, unknown>; saved: Record<string, unknown> | null }>>());
  const _rowQueue = (k: unknown, id: unknown, update: (id: unknown, item: Record<string, unknown>) => Promise<Record<string, unknown> | null>) => {
    const key = `${String(k)}:${String(id)}`;
    const qs = _rowSaveQueuesRef.current;
    let q = qs.get(key);
    if (!q) {
      q = createSaveQueue(
        (item: Record<string, unknown>) => update(id, item).then((saved) => ({ sent: item, saved })),
        (_waiting, next) => next,
        () => { qs.delete(key); },
      );
      qs.set(key, q);
    }
    return q;
  };
  /* 207B §5 — A ROW FROM THE SERVER THAT IS NOT ITS SAVE'S ANSWER — the live
     stream's echo of a save (the typing tab receives its own, as the pane
     does), a matter's re-read — never replaces what was typed and not yet
     answered: the latest row queued for it is laid over it. MEASURED: with the
     answers held 150 ms, « 98765 » typed into the Quantum cell was kept as 9875
     — the client row the stream published for « 987 » landed after the « 6 ».
     Its own answer, when it lands, merges field by field (`mergeAnswerInto`). */
  const keepTyped = <T extends { id?: unknown }>(k: unknown, row: T): T => {
    const q = row && row.id != null ? _rowSaveQueuesRef.current.get(`${String(k)}:${String(row.id)}`) : undefined;
    const typed = q ? (q.waiting() ?? q.inflight()) : null;
    return typed ? ({ ...row, ...typed } as T) : row;
  };
  const persist = (k: any, d: any, fn: any, opts?: any) => {
    const ent = _apiEnt[k];
    /* 04g §2 — FIELDS THAT RIDE THE CREATE REQUEST AND NEVER THE STORE.
       Until this, one object served as both the optimistic state row and the
       POST body, which is right for every field the server echoes back and
       wrong for the first one it must never echo: a password. Carried on the
       row it would be diffed, stringified into the _pendingCreates hash below,
       and held in the entity array until the server's answer replaced it.
       Merged here it reaches the body alone. Create-only by construction —
       there is no update twin, because PUT already has its own password path
       (PasswordField's own request) and needs none. */
    const _createExtra = opts && opts.createExtra ? opts.createExtra : null;
    const _onRow = opts && typeof opts.onRow === "function" ? opts.onRow : null;
    /* 111 §5b — fires with the SERVER row after a create's id-swap. The link
       chain needs the real id (the optimistic temp id is `tempId()`'s — 207
       §1.2, negative, never a server id — and must never reach the store);
       additive, nothing else consumes it. */
    const _onCreatedRowCb =
      opts && typeof opts.onCreatedRow === "function" ? opts.onCreatedRow : null;
    const _onStart =
      opts && typeof opts.onStart === "function" ? opts.onStart : null;
    const _row = (op: any, id: any, item: any, ok: any, error?: any) => {
      if (!_onRow) return;
      try {
        _onRow({ ok, op, id, item, error });
      } catch {
        /* a reporting callback must never break a save */
      }
    };
    if (ent && Array.isArray(d)) {
      let _resolveOutcome: any;
      const outcome = new Promise((r) => {
        _resolveOutcome = r;
      });
      fn((prev: any) => {
        const ops = [];
        const p = Array.isArray(prev) ? prev : [];
        const pm = new Map(p.map((x) => [x.id, x]));
        const nm = new Map(d.map((x) => [x.id, x]));
        for (const [id, oldRow] of pm) {
          if (!nm.has(id)) {
            ops.push(
              ent.d(id).then(
                () => {
                  _row("delete", id, oldRow, true);
                  return true;
                },
                (e) => {
                  // Restore the optimistically-removed row.
                  fn((arr: any) =>
                    arr.some((x: any) => x.id === id) ? arr : [...arr, oldRow],
                  );
                  _apiErr("delete", e);
                  _row("delete", id, oldRow, false, e);
                  return false;
                },
              ),
            );
          }
        }
        for (const [id, item] of nm) {
          const old = pm.get(id);
          if (!old) {
            const _hash = k + ":" + _stableStringify(item);
            if (_pendingCreates.has(_hash)) continue;
            _pendingCreates.add(_hash);
            const _done = () => {
              _pendingCreates.delete(_hash);
            };
            /* 207 §5 — THE "ALREADY EXISTS → UPDATE IT AT ITS TEMPORARY ID"
               FALLBACK IS GONE. It read a create refused with « unique » /
               « already exists » / « 409 » in its message as a row that was
               already there, and PUT the item AT ITS TEMPORARY ID. No temporary
               id ever named the existing row (the server assigns its own), so
               the PUT could only miss — or, while temporary ids were
               `max + 1`, land on a DIFFERENT row that happened to hold that
               number. `POST /clients` cannot answer that way at all (no unique
               constraint; its refusals are 400 `generic.invalid_payload`, a
               P2002 would be the global 500 « Server error »); the one answer
               in the whole `_apiEnt` surface that matched is `POST
               /time-entries`' 409 « A time entry for this email already exists
               on this case. » (routes/timeEntries.js, and only for a reader
               whose words are English — its French and Dutch rows match
               nothing), whose PUT at `max + 1` could overwrite another person's
               time entry. Under §1's rule a temporary id is negative and names
               no row, so the branch could only 404 and leave the waiting row
               behind. A refused create is now what every other refused create
               is: the row is removed and the server's sentence is shown. */
            /* Phase 29 (item 5) — the swap drops any duplicate of the server id:
               see `swapCreatedRow` (src/lib/savedRows, extracted at 207 §1.3). */
            const _onCreated = (saved: any) => {
              if (_onCreatedRowCb) {
                try {
                  _onCreatedRowCb(saved);
                } catch {
                  /* a reporting callback must never break a save */
                }
              }
              if (saved && saved.id != null && saved.id !== id) {
                fn((arr: any) => swapCreatedRow(arr, id, saved));
              } else if (saved) {
                // A9 (brief 65) — src/lib/savedRows. The id-SWAP branch above
                // is NOT this: it replaces the row and dedupes.
                fn((arr: any) => mergeSavedRow(arr, id, saved));
              }
              toastInjectedSubtasks(saved, toast, t);
              const _fu = folderUncheckedWords(saved, t); // 212 §18 — an address stored unasked, said so
              if (_fu) toast(_fu, "warning");
            };
            ops.push(
              ent
                .c(_createExtra ? { ...item, ..._createExtra } : item)
                .then((saved) => {
                  _done();
                  _onCreated(saved);
                  _row("create", id, item, true);
                  return true;
                })
                .catch((e) => {
                  _done();
                  /* Phase 30 (D-3) — failed create: remove the temp-id row.
                     It was never retried and a later edit PUT it to a
                     nonexistent id; the toast explains, the row goes. */
                  fn((arr: any) => arr.filter((x: any) => x.id !== id));
                  _apiErr("create", e);
                  _row("create", id, item, false, e);
                  return false;
                }),
            );
          } else if (_stableStringify(old) !== _stableStringify(item)) {
            /* 207B §5 — through the row's queue (one in flight, the latest
               waiting), and the answer merged over the row as it stands NOW: a
               field changed after its request left keeps what was typed
               (`mergeAnswerInto`, src/lib/savedRows.ts). */
            ops.push(
              _rowQueue(k, id, (i2, it) => ent.u(i2, it) as Promise<Record<string, unknown> | null>)
                .run(item)
                .then(({ sent, saved }) => {
                  if (saved) fn((arr: any) => mergeAnswerInto(arr, id, sent, saved));
                  toastInjectedSubtasks(saved, toast, t);
                  const _fu = folderUncheckedWords(saved, t); // 212 §18 — an address stored unasked, said so
                  if (_fu) toast(_fu, "warning");
                  _row("update", id, item, true);
                  return true;
                })
                .catch((e) => {
                  /* Round 13 (C74/C77) — ROLL BACK the optimistic update on a
                     failed PUT: the pre-save row (`old`, captured above) is
                     restored so the UI never keeps showing a change the
                     server refused. The error toast stays as the explanation. */
                  fn((arr: any) => arr.map((x: any) => (x.id === id ? old : x)));
                  _apiErr("update", e);
                  _row("update", id, item, false, e);
                  return false;
                }),
            );
          }
        }
        /* Deferred to a microtask on purpose: this runs INSIDE fn()'s state
           updater, and a setState from there is not safe. The ops array is
           already complete, so the count is final either way — and because the
           callback SETS the total rather than accumulating it, a StrictMode
           double-invoke of the updater is harmless. */
        if (_onStart) {
          const n = ops.length;
          Promise.resolve().then(() => {
            try {
              _onStart(n);
            } catch {
              /* a reporting callback must never break a save */
            }
          });
        }
        Promise.all(ops).then((flags) => {
          const failed = flags.filter((x) => x === false).length;
          _resolveOutcome({ ok: failed === 0, failed, total: flags.length });
        });
        return d;
      });
      return outcome;
    }
    fn(d);
    sv(k, d);
    return Promise.resolve({ ok: true, failed: 0, total: 0 });
  };
  const toast = makeToast(setActiveToast);
  /* Phase 30 (D-3) — window bridge (same pattern as __gcSend) so components
     without a toast prop (SubtaskChecklist, TaskDocsPane, ScheduleView,
     CCV inline chat) can surface failures instead of console-only warns. */
  useEffect(() => {
    window.__lxToast = toast;
    return () => {
      if (window.__lxToast === toast) delete window.__lxToast;
    };
  });
  if (typeof window !== "undefined") window.lxToast = toast;
  const keepScroll = (fn: any) => {
    const el = document.querySelector(".content");
    const y = el ? el.scrollTop : 0;
    const wy = window.scrollY;
    fn();
    requestAnimationFrame(() => {
      if (el) el.scrollTop = y;
      window.scrollTo(0, wy);
    });
  };

  // Hotfix-B (Part A) — current orphan population: clients linked to no case or
  // collective-case. Exactly the filter the old Orphan Clients tool used.
  const orphanClientList = () =>
    clients.filter(
      (c: any) =>
        cases.every((x: any) => !(x.clIds || []).includes(c.id)) &&
        (collectiveCases || []).every(
          (x: any) => !(x.clientLinks || []).some((lk: any) => lk.clId === c.id),
        ),
    );

  // Duplicate analysis (analyzeOrphanClients) is the pure, browser-and-node
  // testable rule in lib/orphanDedup.mjs — imported at module top. Shared by the
  // modal render (flags) and the on-open pre-check; the server mirrors it.

  // Deferred hard-delete engine. NOTHING is removed for 20s; Undo cancels
  // entirely (defer-then-commit — the opposite of useConfirmAndUndo's
  // commit-then-reverse). Lives at App scope, so navigation / modal close leave
  // it running: it commits on expiry (chosen resolution = COMMIT, not cancel).
  const PENDING_CL_DEL_MS = 20000;
  const cancelPendingClDel = () => {
    const h = pendingClDelRef.current;
    if (h && h.timer) clearTimeout(h.timer);
    pendingClDelRef.current = null;
    setPendingClDel(null);
  };
  // Commit the captured hard delete against the FRESHEST cl (via clRef) so only
  // the captured ids are removed. Routing through s(SK.cl, …) preserves existing
  // delete semantics (per-row DELETE /api/clients/:id → server best-effort
  // folder cleanup).
  const commitPendingClDel = () => {
    const h = pendingClDelRef.current;
    if (!h) return;
    if (h.timer) clearTimeout(h.timer);
    const ids = h.ids;
    pendingClDelRef.current = null;
    setPendingClDel(null);
    const latest = Array.isArray(clRef.current) ? clRef.current : [];
    persist(
      SK.cl,
      latest.filter((c) => !ids.has(c.id)),
      setClients,
    );
    toast(
      t("toast.deleted_clients", {
        count: ids.size,
        fallback: `Deleted ${ids.size} client${ids.size === 1 ? "" : "s"}`,
      }),
    );
  };
  // Single-slot: a second schedule commits the first immediately (its window
  // closes early), matching the existing confirmAndUndo single-slot convention.
  const scheduleClDelete = (idArr: any) => {
    const arr = Array.from(idArr || []);
    if (!arr.length) return;
    if (pendingClDelRef.current) commitPendingClDel();
    const ids = new Set(arr);
    const timer = setTimeout(commitPendingClDel, PENDING_CL_DEL_MS);
    pendingClDelRef.current = { timer, ids };
    setPendingClDel({ count: ids.size, seq: ++pendingClDelSeq.current });
  };
  // On modal open, pre-check non-survivor duplicates (survivor + neutral orphans
  // start unchecked). Keyed on orphanDlg only so it does not clobber the user's
  // manual checkbox edits while the modal stays open.
  useEffect(() => {
    if (!orphanDlg) return;
    const { preChecked } = analyzeOrphanClients(orphanClientList());
    setDbCleanupChecked(new Set(preChecked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orphanDlg]);

  // Hotfix-B correction (ITEM 2) — clients eligible for folder provisioning:
  // folderless (clientRootFolder NULL) MINUS the (name+email) duplicate
  // non-survivors the cleanup modal would delete (no point creating a folder for
  // a row about to be removed). This mirrors the server's candidate set exactly
  // (deletionBoundDuplicateIds over the orphan population). Sorted by id ASC — a
  // STABLE, deterministic order so select-all's "first 50" is reproducible across
  // runs and matches the server's id-ordered batching.
  /* 176 §3.3 — `scope` is a PARAMETER and not a state read, deliberately.
     `openProvisionModal` has to know whether the scoped list is empty BEFORE it
     sets any state, and a state read there would see the PREVIOUS scope. The
     default keeps every other caller — the modal body — reading the scope the
     opener stored. */
  const provisionCandidateList = (scope: number[] | null = provisionScope) => {
    const { preChecked } = analyzeOrphanClients(orphanClientList());
    /* `null` is "no scope asked" and widens to the firm. An EMPTY ARRAY is a
       scope that names nothing, and it selects nothing — it must not fall back
       to the firm-wide list. That distinction is not academic: `[NaN]` from a
       caller normalises to `[]`, and treating `[]` as "unscoped" would turn one
       bad id into the leak this section exists to close. Fail closed. */
    const only = scope ? new Set(scope.map(Number)) : null;
    return clients
      .filter((c: any) => !c.clientRootFolder && !preChecked.has(c.id))
      /* The scope INTERSECTS, after the fact. It cannot re-add a client the
         line above excluded — a folder that already exists, or a duplicate the
         cleanup modal would delete — which is what makes this a narrowing and
         not a second candidate rule. */
      .filter((c: any) => only == null || only.has(Number(c.id)))
      .sort((a: any, b: any) => a.id - b.id);
  };
  // 176 §3.3 — THIS EFFECT OWNS THE CLOSE, AND ONLY THE CLOSE. Clearing the
  // scope here is what stops the next open from the clients list inheriting the
  // narrowing a matter's repair set.
  //
  // The ticked set moved to `openProvisionModal`, which is the ONE place that
  // opens this modal (`setProvisionDlg(true)` appears exactly once, measured).
  // That is not a stylistic move: the opener already holds the candidate list it
  // computed to decide whether to toast, so the pre-tick costs nothing there,
  // and an effect that pre-ticked would have had to read `provisionScope` and
  // `provisionCandidateList` — deps it must not re-run on, because re-running
  // while the modal is open would clobber the user's own ticks. Keeping them out
  // of the body keeps `[provisionDlg]` an HONEST dependency list rather than a
  // suppressed one: `scripts/lint/ratchets.js` caps this file's suppression
  // directives at 19 and they may never grow, so "the sibling above does it"
  // was not available as a reason. (That ratchet is a TEXT SCAN, measured: the
  // first version of this note SPELLED the directive and the counter stayed at
  // 20 with the directive itself already gone. A comment about a suppression
  // counts as one.)
  useEffect(() => {
    if (provisionDlg) return;
    setProvisionScope(null);
  }, [provisionDlg]);
  // saveFirmSettings — single write path for the firm-settings singleton.
  // Sends the patch to PUT /api/firm-settings, then mirrors lx:logo for the
  // few old read paths still pulling the logo from localStorage. The lx:fname
  // mirror was retired in Gate 4c; firmNameSlug() now reads from the
  // _firmNameCache module variable in utils.js.
  /* Phase 30 (D-2/D-3) — server-backed map saves: optimistic set, rollback +
     error toast on failure, success toast only after the server confirms. */
  const _saveFirmMaps = (field: any, value: any, setter: any, prevValue: any) => {
    setter(value);
    updateFirmSettings({ [field]: value })
      /* 209 §25 — these two set the toast with NO clock at all: they stayed until
         another toast's clock happened to close them. Through the toast now. */
      .then(() => toast(t("toast.saved", { fallback: "Saved" })))
      .catch((e) => {
        setter(prevValue);
        toast(
          t("err.save_failed", {
            fallback: "Save failed — your change was not stored",
          }) + (e?.message ? ` (${e.message})` : ""),
          "error",
        );
      });
  };
  const saveSfMaps = (u: any) => _saveFirmMaps("senderFolderMaps", u, setSfMaps, sfMaps);
  const saveExtTypeMaps = (u: any) =>
    _saveFirmMaps("externalTaskTypeMaps", u, setExtTypeMaps, extTypeMaps);
  /* 207B §5 [A DEFECT — PROD HAS IT TOO] — WHAT IS TYPED IS WHAT IS KEPT, AT THE
     ONE PLACE EVERY FIELD OF THE SETTINGS PANES GOES THROUGH. Every keystroke
     saves at once; the answer to an earlier keystroke landed in `firmSettings`,
     the pane's effect copied it over its local copy, and the keys typed
     meanwhile were lost — « Louizalaan 54 bus 3 » kept as « Loul b3 » (MEASURED,
     answers held 400 ms, both engines); and two saves in flight could finish in
     either order, the older stored. Now: ONE save in flight, the patches typed
     meanwhile folded into one that leaves as soon as it is back
     (src/lib/firmSettingsSaves.ts over src/lib/saveQueue.mjs — no new delay;
     one queue for the page, not the pane's, so closing the pane loses
     nothing); and a row from the server — a save's answer, or the config
     echo's re-read, which the typing tab receives too (measured) — lands
     through `takeFirmSettings`: never older than the row already held
     (`updatedAt`), with what was typed after its request left laid over it
     (`firmSettingsTyped()`). The pane lays the same over its own copy. */
  function takeFirmSettings(row: FirmSettingsShape | null): FirmSettingsShape | null {
    if (!row) return null;
    const at = (x: { updatedAt?: string } | null) => (x?.updatedAt ? Date.parse(x.updatedAt) : NaN);
    if (at(firmSettingsNowRef.current) > at(row as { updatedAt?: string })) return null;
    const merged: FirmSettingsShape = { ...row, ...firmSettingsTyped() };
    setFirmSettings(merged);
    return merged;
  }
  const saveFirmSettings = async (patch: any) => {
    try {
      const updated = (await firmSettingsSaves.run(patch as Record<string, unknown>)) as FirmSettingsShape | null;
      const merged = takeFirmSettings(updated);
      if (!merged) return updated;
      if (Object.prototype.hasOwnProperty.call(patch, "logo")) {
        setFirmLogo(merged.logo || null);
        sv("lx:logo", merged.logo || null);
      }
      if (
        patch &&
        patch.firm &&
        Object.prototype.hasOwnProperty.call(patch.firm, "name")
      ) {
        const nm = merged.firm?.name || "Legal Practice Manager";
        setFirmName(nm);
        setFirmNameCache(nm);
      }
      return updated;
    } catch (e) {
      _apiErr("firm settings update", e);
      throw e;
    }
  };
  // applyFirmSettings — apply an ALREADY-PERSISTED firm-settings row to local
  // state WITHOUT a second PUT. Panels that PUT their own field and hand back
  // the fresh full row (e.g. FolderTemplatesPanel) call this via onUpdated.
  // Echoing that full row back through saveFirmSettings re-PUT the ~1 MB
  // proformaTemplate blob → exceeded nginx's default 1 MB body limit → 413
  // (pre-existing bug). The caller's own field-PUT already saved the change; we
  // only refresh local state here. `removedExtras` (a propagation hint, not a
  // column) is stripped so it never leaks into firmSettings state.
  const applyFirmSettings = (res: any) => {
    if (!res || typeof res !== "object") return;
    const { removedExtras, ...row } = res;
    setFirmSettings(row);
  };
  // Phase auth-c2 — the legacy hasPermission(permId) bridge has been
  // removed. Views read directly via usePermissions().can(sectionKey,
  // {caseId|ccId}). _appPerms + _appIsAdmin are kept as locals here for
  // the few inline App.jsx call sites that consult them (see references
  // to _appPerms / _appIsAdmin in this file).
  const _appPerms = usePermissions();
  const _appIsAdmin = !!_appPerms.viewer?.isAdmin;
  // Phase 43 CORRECTION P2 (item A / D28 / D35) — amount-due visibility gate.
  // Reuses the EXISTING per-matter financial-visibility right (caseView.financials
  // — the same axis the server enforces via isFinancialVisible + shapeInvoice), so
  // NO new section key / route / plumbing. Per-matter: the NUMBER renders only for
  // an authorized viewer (RP-of-matter / financial-or-proforma delegate / admin);
  // everyone still sees the LABEL. canSeeAnyDue is the firm-level flag that gates
  // the amount-due FILTER axis + whether the list column is offered at all.
  const canSeeMatterDue = (caseId: any, ccId: any) =>
    _appIsAdmin ||
    (!!_appPerms.viewer &&
      _appPerms.can(
        "caseView.financials.view",
        caseId != null ? { caseId } : { ccId },
      ));
  const canSeeAnyDue =
    _appIsAdmin ||
    (!!_appPerms.viewer && _appPerms.can("caseView.financials.view"));
  /* 212 §27 [RULED 30] — THE MATTERS' CHECK IN THE CASE LISTS, shown to whoever
     §28's route serves (docs/212-deploy-note.md §8.7): the case template's
     right, or `caseView.documents.link` on one matter the person sees —
     clientCan over the two lists, the route's scope term for term. Handed to
     both lists; the window is the clients' one mount below, given the kind. */
  const canCheckMatterFolders =
    _appPerms.can("lateral.caseFolderTemplate.edit") ||
    (cases as readonly { id: number }[]).some((c) => _appPerms.can("caseView.documents.link", { caseId: c.id })) ||
    (collectiveCases as readonly { id: number }[]).some((c) => _appPerms.can("caseView.documents.link", { ccId: c.id }));
  const onMatterFolderCheck = canCheckMatterFolders ? () => { setSubfolderCheck("case"); } : undefined;
  /* Phase 31 (C2) — internal calls: gate, log fetch, presence poll, actions.
     canCalls mirrors the server's lateral.calls single-bit gate (default
     TRUE for all roles; admin overlay). */
  const canCalls =
    !!currentUser &&
    !!_appPerms.viewer &&
    (_appIsAdmin || _appPerms.can("lateral.calls"));
  /* 176 §3.4 [RULED — Laurent, 2026-09-10, closing §3.3 M1] — CAN THIS VIEWER
     CREATE A PERSON. `canCalls` above is the shape, copied and not invented.

     WHY THIS RIGHT: `POST /clients/backfill-folders` already requires
     `lateral.persons.create` (`clients.js:272`), so gating the button on it
     stops the UI offering what the server would refuse — and a collaborator who
     cannot create a client can no longer browse the firm's folderless list with
     each row's name, e-mail and linked matter (the §3.3 M1 disclosure, which
     §3.3's scope did NOT close because it lives on the clients list, not in the
     matter pane).

     NO SERVER CHANGE: the server is already correct. This is the button
     catching up with it. */
  const canCreatePersons =
    !!currentUser &&
    !!_appPerms.viewer &&
    (_appIsAdmin || _appPerms.can("lateral.persons.create"));
  coLiveRef.current = collaborators;
  useEffect(() => {
    if (!authed || !canCalls) return;
    void primeCallPush(); // 208 §14 — before any ring: does this browser hold a push subscription, registered for this user?
    api
      .get("/calls")
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        setCalls(rows);
        // Corr-4 (PART B) — present any ring still active for me on (re)load,
        // e.g. a tab opened by clicking a Web Push notification.
        rows.forEach(presentRingIfActive);
      })
      .catch(() => {});
  }, [authed, canCalls]);
  // Corr-4 (PART C) — the SW posts 'lx-call-accept' when the user clicks a Web
  // Push call notification (it has already focused/opened this tab); re-pull the
  // call log and surface the ring card so they land on the accept.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
    const onMsg = (ev: any) => {
      if (ev && ev.data && ev.data.type === "lx-call-accept") {
        api
          .get("/calls")
          .then((rows) => {
            if (!Array.isArray(rows)) return;
            setCalls(rows);
            rows.forEach(presentRingIfActive);
          })
          .catch(() => {});
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Presence while a calls panel is open (30 s refresh; the heartbeat POST
  // is app-wide and pre-existing). Corr-2 (item 4) — pullPresence is shared
  // with the compact column's inline panel, which runs its own interval.
  const pullPresence = () =>
    api
      .get("/presence")
      .then((rows) => {
        const ids = Array.isArray(rows) ? rows.map((r) => r.id) : [];
        if (Array.isArray(rows)) setPresenceIds(ids);
        setPresenceLoaded(true);
        return ids; // Corr (item 7) — the panel awaits this for a click before load
      })
      .catch(() => presenceIds);
  useEffect(() => {
    // Corr-3 (item 2) — also poll presence while a call window is open, so the
    // in-call "Add someone" list shows accurate green/grey dots.
    if (!callPanelOpen && !callWin) return;
    pullPresence();
    const iv = setInterval(pullPresence, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callPanelOpen, callWin]);
  // The ringer follows the incoming stack: ring while at least one call is
  // live, stop the moment the stack drains (answer/decline/timeout).
  useEffect(() => {
    if (incomingCalls.length > 0) {
      startRinger();
      loadCallWindow().catch(() => undefined); // 208 §1 — the window arrives during the ring; a failure speaks when it mounts
    } else stopRinger();
    return () => stopRinger();
  }, [incomingCalls.length]);
  // Local visual countdown: drop a ring card when its deadline passes (the
  // server sweep is authoritative and confirms via the 'call' update event).
  useEffect(() => {
    if (!incomingCalls.length) return;
    const iv = setInterval(() => {
      const now = Date.now();
      setIncomingCalls((prev: any) => prev.filter((c: any) => c.deadline > now));
    }, 1000);
    return () => clearInterval(iv);
  }, [incomingCalls.length]);
  // 163 §B.3 — the /calls/* endpoints answer with the updated call row.
  type CallEnvelope = { call?: { id: number; [k: string]: unknown } | null };
  const upsertCallRow = (row: any, action: any) => {
    if (!row || row.id == null) return;
    setCalls((prev: any) => {
      if (action === "delete") return prev.filter((x: any) => x.id !== row.id);
      const has = prev.some((x: any) => x.id === row.id);
      const next = has
        ? prev.map((x: any) => (x.id === row.id ? row : x))
        : [row, ...prev];
      // Archived rows leave the default log view (GET parity).
      return next.filter((x: any) => !x.archived);
    });
  };
  callsRef.current = calls;
  /* ── Phase 39 — in-app WebRTC call session plumbing ────────────────────────
     The persistent 'call' event (participant outcomes + initiatorLeftAt) is the
     authoritative roster that drives the mesh; the in-app window opens on the
     first answer (caller) / on accept (callee) and tears down when I leave or
     the call drains below two people. */
  const mediaErrToast = (err: any) =>
    err === "denied"
      ? t("calls.media.denied_short", { fallback: "Allow camera & microphone access to start a call." })
      : err === "no-device"
        ? t("calls.media.no_device_short", { fallback: "No camera or microphone found." })
        : err === "in-use"
          ? t("calls.media.in_use_short", { fallback: "Your camera or microphone is in use by another app." })
          : t("calls.media.error_short", { fallback: "Couldn't access your camera or microphone." });
  /* 72 — tear the LEG session down locally, and release the capture. Defined
     before `endCallSession` because that funnel calls it: ending the
     conversation ends its legs (§3.6), and a leg that outlived its parent on
     this side would leave a still frame of somebody's screen in a window with
     no call behind it.
     Idempotent, and called from four independent paths — the button, the
     track's `ended` event (§4), the server's 'end' event, and the
     conversation's own teardown. `destroy()` stops the session's own tracks
     too; stopping through the ref as well costs nothing and covers the case
     where the session was never built. */
  const endShareSession = () => {
    _popTrace("--- SHARE: session torn down ---", "endShareSession");
    const s = rtcShareRef.current;
    rtcShareRef.current = null;
    const st = shareStreamRef.current;
    shareStreamRef.current = null;
    if (st) { try { st.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ } }
    // 74y (addendum) — the count AFTER a teardown is the one that says whether the
    // teardown did its job. Anything still live here was not this session's to stop.
    _captureCensus("after teardown");
    if (s) { try { s.destroy(); } catch { /* ignore */ } }
    setShareLeg(null);
    setShareView(null);
    setShareLost(false);
    /* 74 §3 AMENDED — STOPPING A SHARE MOVES THE CALL NOWHERE, and this line used
       to close the pop-out here. §3 originally said the call surface "comes back
       when the share ends", and that assumed the sharer is back at the browser by
       then. He is not: he is still looking at the window he was sharing, so
       handing the call back to the tab hands it to a window he cannot see and has
       to hunt for WHILE STILL ON CAMERA. The pop-out therefore survives the share
       and lives until the CALL ends — see `closeCallPopOut`'s callers and the
       focus effect beside it. */
  };
  /* 73 §2 — THE RETRY, and it is deliberately ONE USER-INITIATED ATTEMPT rather
     than a loop. §6 forbids retrying until it works: a retry with no named cause
     spends the harness 72b bought. What a person gets instead is what brief 70
     gave the stalled call — told, and able to act. Only the WATCHER can do this:
     re-opening a receiving leg costs nothing (no gesture, no device), whereas the
     sharer's retry would need a fresh `getDisplayMedia` gesture and the browser's
     picker again, which is what Stop-and-share-again already is. */
  const retryReceivedLeg = () => {
    const leg = shareLeg;
    if (!leg || Number(leg.sharerId) === Number(meRef.current)) return;
    const s = rtcShareRef.current;
    rtcShareRef.current = null;
    if (s) { try { s.destroy(); } catch { /* ignore */ } }
    setShareView(null);
    setShareLost(false);
    openReceivedLeg(leg);
  };
  const endCallSession = () => {
    const s = rtcSessionRef.current;
    rtcSessionRef.current = null;
    rtcPrewarmRef.current = null;
    endShareSession(); // 72 §3.6 — a leg never outlives its parent.
    /* 74 §3 AMENDED — THE CALL ENDING IS WHAT CLOSES THE POP-OUT, not the share
       ending. It is stated here and in `hangupCall` rather than left inside
       `endShareSession`, because a window that outlives its call would sit in
       front of everything the user does next with nothing in it. */
    closeCallPopOut();
    // Release the ring-time pre-warmed camera if it was never promoted to a live
    // call (decline / timeout / caller-cancel) — no device left open.
    const pm = rtcPrewarmMediaRef.current;
    rtcPrewarmMediaRef.current = null;
    if (pm) Promise.resolve(pm).then((m) => { try { if (m && m.stream) m.stream.getTracks().forEach((t: any) => t.stop()); } catch { /* ignore */ } }).catch(() => {});
    if (s) { try { s.destroy(); } catch { /* ignore */ } }
    setCallWin(null);
    setCallView(null);
    setCallMediaErr(null);
  };
  // Phase 42 (Lever C) — two rosters from the authoritative call row.
  //  • MESH: everyone we hold a media-less PC to — the non-left initiator + every
  //    RINGING or ANSWERED invitee (minus me). Including ringing invitees is what
  //    lets the connection pre-warm before accept.
  //  • ARMED: the subset we actually SEND media to — only once BOTH ends are
  //    committed. I am committed if I'm the initiator or my own outcome is
  //    'answered'; a peer is committed if it's the non-left initiator or an
  //    'answered' invitee. Until then no track is attached (the privacy invariant).
  const iAmCommitted = (callRow: any, me: any) =>
    Number(callRow.initiatorId) === me ||
    (callRow.participants || []).some(
      (p: any) => Number(p.collaboratorId) === me && p.outcome === "answered",
    );
  // A17 (brief 65) — src/lib/ringTargets. The two things that differed are the
  // outcomes that count and whether I have to have accepted first.
  const computeMeshTargets = (callRow: any) =>
    pickRingTargets(callRow, Number(meRef.current), {
      outcomes: ["answered", "ringing"],
    });
  const computeArmedTargets = (callRow: any) =>
    pickRingTargets(callRow, Number(meRef.current), {
      outcomes: ["answered"],
      gate: iAmCommitted,
    });
  const syncCallSession = (callRow: any) => {
    const s = rtcSessionRef.current;
    if (!s || !callRow || Number(s.callId) !== Number(callRow.id)) return;
    const me = Number(meRef.current);
    const myPart = (callRow.participants || []).find((p: any) => Number(p.collaboratorId) === me);
    const iAmInitiator = Number(callRow.initiatorId) === me;
    const iLeft = iAmInitiator
      ? !!callRow.initiatorLeftAt
      : myPart && ["declined", "missed", "left"].includes(myPart.outcome);
    if (callRow.status === "canceled" || iLeft) { endCallSession(); return; }
    const mesh = computeMeshTargets(callRow);
    const armed = computeArmedTargets(callRow);
    s.setPeers(mesh, armed);
    // Once I'm committed this is no longer a pre-warm session.
    if (iAmCommitted(callRow, me)) rtcPrewarmRef.current = null;
    if (armed.length > 0) {
      // Someone we're live with (media flowing) → land in the window. For the
      // caller that's the first answer; for the callee it's their own accept.
      const windowOpen = !!callWin && Number(callWin.callId) === Number(callRow.id);
      if (!windowOpen) {
        setOutgoingCall(null);
        setCallWin({ callId: Number(callRow.id) });
      }
      return;
    }
    // No armed peer yet. Keep the session while it's legitimately pending — the
    // caller waiting on a first answer (status 'ringing'), OR a recipient pre-
    // warming during their own ring. Otherwise the call has drained → tear down
    // (releasing any pre-acquired camera/mic). The window stays CLOSED: a media-
    // less pre-warm connection must never surface before accept.
    const pending =
      callRow.status === "ringing" || Number(rtcPrewarmRef.current) === Number(callRow.id);
    if (!pending) endCallSession();
  };
  // Create the media session (after media is acquired in the click gesture).
  // `iceServersP` (Phase 42, Lever B) lets the caller kick off the TURN-credential
  // fetch in PARALLEL with acquireLocalMedia / POST-respond rather than
  // serializing it here; it falls back to fetching now when not supplied.
  const beginSession = async (callId?: any, media?: any, callRow?: any, openWindowNow?: any, iceServersP?: any) => {
    if (rtcSessionRef.current) { try { rtcSessionRef.current.destroy(); } catch { /* ignore */ } rtcSessionRef.current = null; }
    rtcPrewarmRef.current = null;
    setCallMediaErr(media && !media.stream ? media.error || "error" : null);
    const iceServers = await (iceServersP || fetchIceServers());
    callPerfMark(callId, "turn-creds-returned");
    const sess = createCallSession({
      callId,
      selfId: meRef.current,
      localStream: media ? media.stream : null,
      iceServers,
      onChange: setCallView,
    });
    rtcSessionRef.current = sess;
    setCallView(sess.getState());
    // Reconcile against the freshest row (an answer may have landed during setup).
    const latest = callsRef.current.find((c: any) => Number(c.id) === Number(callId)) || callRow;
    if (openWindowNow && (!callWin || Number(callWin.callId) !== Number(callId))) setCallWin({ callId: Number(callId) });
    if (latest) syncCallSession(latest);
  };
  // Phase 42 (Lever C) — stand up a MEDIA-LESS session the moment a call starts
  // ringing for me, so its peer connection negotiates + reaches connected DURING
  // the ring. No getUserMedia, no tracks: accept then only flips media on
  // (reneg-free). Only pre-warms when I'm idle (no live/pre-warming session).
  const startPrewarm = async (callId: any) => {
    if (!canCalls || rtcSessionRef.current) return;
    const cid = Number(callId);
    if (!Number.isFinite(cid) || cid <= 0) return;
    rtcPrewarmRef.current = cid;
    callPerfMark(cid, "ring-prewarm");
    // Pre-warm the CAMERA now (in parallel with the ICE fetch) so its device-open
    // latency is off the accept path. The resulting track is held in the ref —
    // OFF the session stream and OFF every sender — so it is NOT transmitted to
    // the caller and NOT shown in the recipient's self-view until accept promotes
    // it (setLocalMedia). The corrected invariant allows the device to open here;
    // it forbids only sending/showing pre-accept. endCallSession releases it if
    // the ring resolves without an accept.
    if (!rtcPrewarmMediaRef.current) {
      rtcPrewarmMediaRef.current = acquireLocalMedia({ userId: currentUser }).catch(() => ({ stream: null, error: "error" })); // 204 §9 — the remembered devices
    }
    try {
      const iceServers = await fetchIceServers();
      callPerfMark(cid, "turn-creds-returned");
      // A newer call may have superseded / an accept may have consumed this ring
      // while we fetched creds — bail if we're no longer the pending pre-warm.
      if (rtcSessionRef.current || Number(rtcPrewarmRef.current) !== cid) return;
      const sess = createCallSession({
        callId: cid,
        selfId: meRef.current,
        localStream: null, // media-less until accept — the privacy invariant
        iceServers,
        onChange: setCallView,
      });
      rtcSessionRef.current = sess;
      // Mesh to the initiator (+ any already-answered peer) with NO armed targets
      // → the PC connects carrying no media, and the window stays closed.
      const row = callsRef.current.find((c: any) => Number(c.id) === cid);
      if (row) syncCallSession(row);
      else {
        const ring = incomingCalls.find((c: any) => Number(c.callId) === cid);
        const initId = ring ? Number(ring.initiatorId) : NaN;
        sess.setPeers(Number.isFinite(initId) && initId > 0 ? [initId] : [], []);
      }
    } catch (e) {
      console.warn("[calls] pre-warm failed:", e);
      if (Number(rtcPrewarmRef.current) === cid) {
        if (rtcSessionRef.current) endCallSession();
        else rtcPrewarmRef.current = null;
      }
    }
  };
  /* ── 72 — SHARING A WINDOW IN A CALL ───────────────────────────────────────
     Three entry points and they are deliberately asymmetric, because sharing
     and receiving are not the same act:
       startShare       the sharer's click. A user gesture, a device (well, a
                        surface) opened, media SENT.
       openReceivedLeg  the receiver's client, off the server's event. No
                        gesture, no permission, no device, nothing sent.
       stopShare        the sharer stops — from the button, or from the OS.
     What makes the privacy claim structural rather than careful:
     webrtcCall.ts:26-28 — no track is added outside `armPeer`, and `armPeer`
     runs only for ARMED ids. A receiver passes NONE, and its session is built
     with `localStream: null`, so there is nothing to arm and no path by which a
     receiving leg could send anything. It is not a check that could be
     forgotten; it is an absence. */
  const startShare = async (callId: number) => {
    if (shareBusy || rtcShareRef.current) return;
    const md = navigator.mediaDevices as DisplayCapableMediaDevices | undefined;
    /* THE ONE HARD LIMIT, SAID TO THE PERSON RATHER THAN ONLY IN A PR BODY:
       iOS and iPadOS Safari have never supported getDisplayMedia and there is
       no fallback. A partner on an iPad can RECEIVE a shared window and can
       never send one. */
    if (!md || typeof md.getDisplayMedia !== "function") {
      toast(
        t("calls.share.unsupported", {
          fallback: "This device can't share a window. Sharing works on a computer — iPhone and iPad can only receive.",
        }),
        "error",
      );
      return;
    }
    setShareBusy(true);
    let stream: MediaStream | null = null;
    try {
      /* §3.3 — WHOLE-SCREEN SHARING IS NEVER OFFERED, by ruling. So a partner
         cannot expose another client's open file, a notification or a second
         monitor by mispicking. `monitorTypeSurfaces: "exclude"` is the ONLY
         exclusion the API actually enforces; `displaySurface` is a HINT a
         browser may ignore and is deliberately not leaned on. And this is NOT
         claimed as a guarantee: where a browser ignores the constraint the
         picker simply offers its usual choices — it degrades to the standard
         picker rather than failing closed.
         Audio is out of scope (§3.4): unsupported or partial in Safari and
         Firefox, and nobody has asked for it. `audio: false` says so.
         THIS CALL MUST BE INSIDE THE CLICK'S GESTURE — getDisplayMedia refuses
         outside one — and it comes BEFORE the POST, so cancelling the picker
         never reaches the server and leaves the call untouched. */
      _popTrace("--- SHARE: picker opened (getDisplayMedia awaiting) ---", "startShare");
      // 74y (addendum) — BEFORE the new capture exists, so an older live one is
      // named while it is still the only explanation on the table.
      _captureCensus("before the picker");
      /* ── 74ab — THE CAPTURE KEEPS CHROME'S DEFAULT FOCUS BEHAVIOUR ────────────
         RULED, and recorded as an ABSENCE rather than as a defect. At capture start
         Chrome raises the surface it is capturing, and the window it raised has been
         the wrong one. WHICH window is raised is not addressable from a page — the
         same wall as 74h and 74i (e), third instance of the family — so the only
         choice available is raise or do-not-raise, never raise-correctly. The one
         lever, `CaptureController.setFocusBehavior()`, is exactly that binary
         (`focus-captured-surface` / `no-focus-change`) and would have to be decided
         HERE, at the call, because it is finalised at capture start.
         RAISING IS KEPT. It is correct for a single-window application and
         sometimes correct otherwise, and a wrong raise costs one click — the same
         single click that suppressing it would cost EVERY time, including all the
         times it was right. So no `CaptureController` is constructed and no
         `controller` is passed; the absence is the decision, and this comment is
         where it is written down so the next reader does not re-derive it as a bug. */
      stream = await md.getDisplayMedia({ video: { monitorTypeSurfaces: "exclude" }, audio: false });
      _popTrace("--- SHARE: capture acquired; the click's activation is now spent ---", "startShare");
      // 74y — recorded HERE, at the resolution, before a session exists to colour it.
      sharePickedRef.current = { trackId: (stream.getVideoTracks()[0] || null)?.id || null, sessionTag: null };
      _registerCapture(stream);
      _captureCensus("after the picker resolved");
      const d = (await api.post(`/calls/${callId}/share`, {})) as { leg?: ShareLeg | null } | null;
      const leg: ShareLeg | null = d && d.leg ? d.leg : null;
      if (!leg || leg.id == null) throw new Error("no leg in the share response");
      shareStreamRef.current = stream;
      const iceServers = await fetchIceServers();
      const sess = createCallSession({
        callId: leg.id,
        selfId: meRef.current,
        localStream: stream,
        iceServers,
        onChange: setShareView,
        /* 73 §2 — A SHARER RECEIVES NOTHING, AND SAYING SO IS WHAT KEEPS THE NEW
           WATCH HONEST. The receivers on a leg send no media at all, so a
           sharer's peers never fire `ontrack` and that is correct. Without this
           the connected-and-carrying-nothing watch would fire on every healthy
           share — on the one session most likely to be under a magnifying glass. */
        expectRemoteMedia: false,
      });
      rtcShareRef.current = sess;
      /* 74y — the pair is completed here and NOWHERE ELSE. From this line on,
         "what should be on the capturer's screen" is a fixed (track, session)
         pair that no later session can overwrite without going through the
         picker again. */
      sharePickedRef.current = { ...sharePickedRef.current, sessionTag: sess.getState().sessionTag };
      setShareLeg(leg);
      setShareView(sess.getState());
      // THE MESH IS A STAR, NOT A MESH. The sharer meshes to every receiver and
      // ARMS every one of them; receivers mesh only to the sharer and arm
      // nobody, so no receiver ever holds a connection to another receiver.
      sess.setPeers(leg.participantIds, leg.participantIds);
      /* §4 — THE DEFECT THIS SHIPS WITH IF NOBODY LOOKS FOR IT. macOS and
         Windows both put a stop control in the system UI, and the browser
         exposes one of its own. Using either simply ENDS the track: no callback
         fires unless one is registered, and the far side would freeze on the
         last frame — a still image of a partner's screen, persisting in a call.
         Same shape as brief 41 §6's device-switch bullet and the same reason:
         state that lives on a track, not on a session. */
      /* §4's OS-stop path, and 74o (a)'s correction to it: the listener names ITS
         OWN leg, and `stopShare` now checks that against the live session — so this
         firing because WE stopped the track (a normal stop, or a later share
         replacing this one) can no longer tear down a share it does not belong to. */
      const vt = stream.getVideoTracks()[0];
      if (vt) vt.addEventListener("ended", () => { stopShare(leg.id); });
      /* ── 74 §3 — THE CALL GOES IN FRONT, AND THE ORDER HERE IS THE WHOLE
         RISK MANAGEMENT. §3 rules that the sharer's call surface goes into the
         pop-out WHEN A SHARE STARTS, so it is attempted here rather than left to
         a click. But `requestWindow()` needs its own user activation, and whether
         a SUCCESSFUL `getDisplayMedia()` consumes the gesture's activation could
         NOT be measured on this box — headless Chromium answers
         `NotSupportedError: Not supported` to `getDisplayMedia`, so the
         interaction is unverifiable here.
         Given that, the ordering is chosen so the unmeasured risk falls on the
         CHEAPER thing: the share is acquired FIRST and the window attempted
         after. If activation has survived, §3's automatic behaviour happens. If
         it has not, the only loss is the window — and `Keep call in front` on the
         share bar takes one click, in its own fresh gesture, which is
         activation-safe by construction. The reverse order would have risked the
         SHARE itself, which is not a trade worth making for one click.
         Not awaited: the share must not wait on a window, and a refusal must not
         reach the share's own catch. */
      /* 74l (b) — ONE FRAME BEFORE OPENING, so the tab has committed the SHARED
         drawer and the measurement INCLUDES it. Without this the pop-out opened at
         a height measured before that drawer existed, and the thumbnail was clipped
         by the window edge — which is what Laurent saw. A frame is well inside the
         ~5 s transient-activation window, so it does not change whether the
         auto-open is permitted (74c); it only changes what it opens at. */
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      openCallPopOut().then((ok) => {
        if (!ok) console.info("[calls] the call pop-out was refused (most likely a consumed user activation) — 'Keep call in front' opens it in its own gesture");
      }).catch(() => {});
    } catch (err) {
      if (stream) { try { stream.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ } }
      shareStreamRef.current = null;
      const e = err as ShareApiError;
      /* Cancelling the picker throws NotAllowedError, and that is not an error
         to report: the person changed their mind. Everything else is. Either
         way THE CALL IS UNAFFECTED — brief 41 §6 ruled that a failed blur was
         no reason to lose a call, and neither is this. */
      if (e && e.name === "NotAllowedError") return;
      console.error("[calls] share failed:", err);
      // `sharerId` rides the response BODY, which is where src/api.ts:186-188
      // puts everything past `code` — reading `e.sharerId` would have been
      // silently undefined and always fallen through to the nameless sentence.
      const sid = e && e.body ? e.body.sharerId : undefined;
      // `collaborators` is `useState<any>([])`, so its element type has to be
      // stated here rather than inferred — and `pname` is all this needs of it.
      const who: { id?: number } | null = sid != null
        ? (collaborators.find((c: { id?: number }) => Number(c.id) === Number(sid)) || null)
        : null;
      toast(
        e?.code === "call.share_taken"
          ? (who
            /* `{name}` and not a template literal: `interpolate()` fills a
               FALLBACK too (src/i18n.tsx:70), so writing it this way makes the
               fallback and the seeded English row byte-identical instead of two
               sentences that can drift. */
            ? t("calls.share.taken_by", { name: pname(who), fallback: "{name} is already sharing a window." })
            : t("calls.share.taken", { fallback: "Someone is already sharing a window." }))
          : e?.code === "call.too_late"
            ? t("calls.too_late", { fallback: "This call already ended" })
            : t("calls.share.failed", { fallback: "Couldn't start sharing." }),
        "error",
      );
    } finally {
      setShareBusy(false);
    }
  };
  /* Stop sharing. LOCAL TEARDOWN FIRST, then tell the server: the OS-stop path
     arrives here with the track already dead, and a failed POST must not be
     able to leave a dead tile on our own screen. The receivers learn from the
     server's 'end' event — and if that never reaches them, their peer
     connection closing does (see the orphan effect below). */
  /* 74o (a) — IT TEARS DOWN ONLY ITS OWN LEG, and that was a real defect rather
     than a tidiness point. This called `endShareSession()` unconditionally, so a
     stale `ended` listener — one left on a PREVIOUS capture's track, which fires the
     moment that track is stopped — destroyed whatever share was current instead of
     the one it belonged to. Stop share A and start share B quickly and A's listener
     could kill B's session while B's leg stayed open on the server: the receiver
     goes on getting one capture while the tab's state belongs to another, which is
     exactly the divergence 74o (a) describes.
     The leg id is now checked against the live session before anything is torn
     down, and the server is still told about the leg named — a stale stop must not
     leave a leg open just because it no longer owns the session. */
  const stopShare = async (legId?: number) => {
    _popTrace(`--- SHARE: stop requested (leg ${legId != null ? legId : "current"}) ---`, "stopShare");
    const s = rtcShareRef.current;
    const id = legId != null ? Number(legId) : (s ? Number(s.callId) : null);
    if (id == null || !Number.isFinite(id)) return;
    if (!s || Number(s.callId) === id) endShareSession();
    else console.info(`[calls] stop for leg ${id} arrived while leg ${s.callId} is live — ending that leg only, session untouched`);
    try { await api.post(`/calls/${id}/share/end`, {}); }
    catch (e) { console.error("[calls] stop share failed:", e); }
  };
  /* A window is being shared WITH me. No gesture and no permission are needed,
     because receiving opens no device — the sharer's click (§3.2) is the only
     user gesture in the whole feature. */
  const openReceivedLeg = async (leg: ShareLeg) => {
    if (!canCalls || !leg) return;
    const id = Number(leg.id);
    if (!Number.isFinite(id) || id <= 0) return;
    // One share at a time (§5). A leg with a different id supersedes whatever
    // we hold, so exactly one leg session is ever alive.
    if (rtcShareRef.current && Number(rtcShareRef.current.callId) !== id) endShareSession();
    if (rtcShareRef.current) return;
    setShareLeg(leg);
    setShareLost(false);
    try {
      const iceServers = await fetchIceServers();
      if (rtcShareRef.current) return; // a race we lost; the other one stands
      const sess = createCallSession({
        callId: id,
        selfId: meRef.current,
        localStream: null, // receive-only, and by construction: nothing to arm
        iceServers,
        /* THE ORPHANED RECEIVER, WATCHED WHERE THE TRANSPORT SPEAKS RATHER THAN
           IN AN EFFECT. A received leg is normally closed by the server's 'end'
           event; this covers the two paths that produce no event at all — the
           sharer's tab crashing, and a /share/end POST that never landed. The
           peer connection closes either way, webrtcCall's fail timer drops the
           peer after 9 s, and an empty peer list on a RECEIVING leg means the
           sharer is gone.
           It cannot misfire at open: `createCallSession` emits nothing at
           construction, and `setPeers` below runs synchronously, so the first
           snapshot this ever sees already carries the one peer. */
        onChange: (st: ShareView) => {
          setShareView(st);
          /* 73 §2 — #357 ENDED THE LEG HERE, AND THAT WAS THE WRONG READING.
             An empty peer list on a receiving leg means the transport gave up on
             the sharer — after `failed` for 9 s — and #357 treated it as "the
             sharer is gone" and closed the tile with no explanation. Those are
             two different events and only the SERVER knows which: a real
             departure arrives as `callLeg` end. So this now RAISES A FAILURE the
             person can see and retry, and the leg is closed only by the event
             that actually means it. */
          if ((st.peers || []).length === 0) setShareLost(true);
        },
      });
      rtcShareRef.current = sess;
      setShareView(sess.getState());
      // Mesh to the SHARER only, and no armed ids at all.
      sess.setPeers([Number(leg.sharerId)], []);
    } catch (e) {
      console.warn("[calls] could not open the shared window:", e);
      endShareSession();
    }
  };
  // Add colleague(s) to the live call (server enforces the 4-cap too).
  const addToCall = async (inviteeIds: any) => {
    const s = rtcSessionRef.current;
    if (!s) return;
    const me = Number(meRef.current);
    const ids = [...new Set((Array.isArray(inviteeIds) ? inviteeIds : []).map(Number).filter((n) => Number.isFinite(n) && n > 0 && n !== me))];
    if (!ids.length) return;
    // Prevent the 5th seat client-side (the server enforces it too): count who
    // currently occupies a seat (initiator unless left + ringing/answered).
    const row = callsRef.current.find((c: any) => Number(c.id) === Number(s.callId));
    const seats = new Set();
    if (row) {
      if (!row.initiatorLeftAt) seats.add(Number(row.initiatorId));
      for (const p of row.participants || []) if (p.outcome === "ringing" || p.outcome === "answered") seats.add(Number(p.collaboratorId));
    }
    const room = Math.max(0, 4 - seats.size);
    if (room <= 0) {
      toast(t("calls.too_many", { fallback: "A call can include at most 4 people." }), "error");
      return;
    }
    const capped = ids.slice(0, room);
    try {
      const d = (await api.post(`/calls/${s.callId}/invite`, { inviteeIds: capped })) as CallEnvelope | null;
      if (d?.call) { upsertCallRow(d.call, "update"); syncCallSession(d.call); }
    } catch (e: any) {
      console.error("[calls] invite failed:", e);
      toast(
        e?.code === "call.too_many"
          ? t("calls.too_many", { fallback: "A call can include at most 4 people." })
          : e?.code === "call.too_late"
            ? t("calls.too_late", { fallback: "This call already ended" })
            : t("calls.add_failed", { fallback: "Couldn't add to the call" }),
        "error",
      );
    }
  };
  const hangupCall = () => {
    const s = rtcSessionRef.current;
    rtcSessionRef.current = null;
    /* 72 §3.6 — MY SHARE GOES WITH MY CALL, AND IT GOES FIRST. The server ends
       every leg of a drained conversation (`endLegsOf`, off /leave below), but
       the person hanging up must not watch their own shared window sit there
       for a round trip — and if they were the SHARER, this is also what stops
       the capture. This does NOT tell the far side: /leave does that. */
    const sh = rtcShareRef.current;
    const sharing = !!sh && !!shareLeg && Number(shareLeg.sharerId) === Number(meRef.current);
    const legId = sh && sharing ? Number(sh.callId) : null;
    endShareSession();
    closeCallPopOut(); // 74 §3 AMENDED — the call is over, so the window is too
    if (s) { try { s.hangup(); } catch { /* ignore */ } } // POSTs /leave + tears down local
    if (legId != null) { api.post(`/calls/${legId}/share/end`, {}).catch(() => {}); }
    setCallWin(null);
    setCallView(null);
    setCallMediaErr(null);
  };
  // Click-to-call / click-to-add affordance on the collaborator rosters: ring a
  // colleague (new call) or pull them into the call I'm already in.
  const onCallColleague = (coId: any) => {
    const id = Number(coId);
    if (!Number.isFinite(id) || id <= 0 || id === Number(currentUser)) return;
    if (!canCalls) return;
    if (rtcSessionRef.current) addToCall([id]);
    else doCall([id]);
  };
  // Corr (D1) — the caller confirmed an OFFLINE colleague: record a missed call
  // on their side (and ours) with NO ring and NO media. The server publishes the
  // row to both logs; we upsert ours so the log updates immediately.
  const notifyMissed = async (coId: any) => {
    const id = Number(coId);
    if (!Number.isFinite(id) || id <= 0 || id === Number(currentUser)) return;
    if (!canCalls) return;
    const c = collaborators.find((x: any) => x.id === id);
    try {
      const d = (await api.post("/calls/missed", { inviteeId: id })) as CallEnvelope | null;
      if (d?.call) upsertCallRow(d.call, "create");
      toast(
        t("calls.missed_logged", {
          name: c ? pname(c) : "",
          fallback: `Missed call logged for ${c ? pname(c) : "your colleague"}`,
        }),
      );
    } catch {
      toast(t("calls.missed_failed", { fallback: "Couldn't log the missed call" }), "error");
    }
  };
  // Corr — the call-window device cog picked a mic/camera: re-acquire with those
  // device ids and hot-swap the tracks into the live session (replaceTrack needs
  // no renegotiation). Falls back to audio-only if the chosen camera fails.
  const switchCallDevices = async ({ audioDeviceId, videoDeviceId }: any) => {
    const s = rtcSessionRef.current;
    if (!s) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        video: videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true,
      });
      await s.replaceLocalStream(stream);
    } catch {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true,
        });
        await s.replaceLocalStream(stream);
      } catch {
        toast(mediaErrToast("error"), "error");
      }
    }
  };
  // Corr (Section 2) — the firm roster as case-free attendees: internal
  // colleagues with an email, alphabetical, none pre-checked.
  const buildInternalRoster = () =>
    choosableCo(collaborators)
      .filter((c: any) => c.id !== currentUser && c.email && (!c.coType || c.coType === "internal"))
      .sort((a: any, b: any) => String(pname(a)).localeCompare(String(pname(b)), undefined, { sensitivity: "base" }))
      /* 122 §2 — `group: ""` because this roster has no families to name: every
         row is a colleague, and the merge files each one under §5's (a) or (b)
         from his `coId`. The empty string is the "no caller family" case the
         merge falls back from, not a heading. */
      .map((c: any) => ({
        id: c.id,
        name: pname(c),
        email: c.email,
        checked: false,
        group: "",
      }));
  // Corr (Section 2) — open the three Parties-pane modals with NO case attached:
  // firm-roster attendees, "Internal meeting" title default, two-column picker.
  // A6 (brief 65) — the time arithmetic and the constant tail live in
  // src/lib/meetingSeed; what stays here is what identifies THIS meeting.
  /* BRIEF 120 §3 — the third entry point onto the ONE modal. No matter at all,
     so the roster is the firm's own people and the case combobox opens on its
     `modal.ag.general_no_case` arm; `_internal` is what tells the picker to
     render the flat `ColleagueCheckGrid` instead of the grouped list, and what
     tells the roster effect to leave this list alone (no matter can produce
     it). `caseObj`/`kind` are gone with the dialog that read them. */
  const openInternalMeeting = () => {
    openModal(
      "ag",
      meetingSeed(
        {
          _internal: true,
          /* 122 §2 — merged at the seed, because the roster effect deliberately
             leaves an internal list alone (no matter can produce it) and would
             therefore never give these rows their key, their `coId` or their
             section. `firm: []` keeps `buildInternalRoster`'s own filter as the
             whole population — the current user and every external coType stay
             out, which is what makes it an INTERNAL meeting. */
          _attendees: meetingRosterFor({} as SeedableEvent, {
            cast: buildInternalRoster(),
            firm: [],
          }).attendees,
          title: t("calls.internal_meeting", { fallback: "Internal meeting" }),
        },
        mtgDefLoc,
        /* 123 §3 — the firm's own first meeting type, not the literal "REU". */
        firstMeetingTypeCode(evtEntries),
        /* 124 §1 — and the mode that type declares, from the fold above. */
        evDefaultMode(firstMeetingTypeCode(evtEntries)),
      ),
    );
  };
  /* ── BRIEF 119 §1c — "SAVE & INVITE": SAVE THE EVENT, THEN OPEN THE DIALOG ──
     The flow the whole brief is for. The event is saved with `deferMeeting`
     (§1b) so its own Graph leg stands down, and the Schedule Meeting dialog then
     opens SEEDED FROM THAT EVENT — matter, title, date, start/end, platform,
     notes — carrying the row's id, so its "Create Meeting" posts `eventId` and
     the route UPDATES the row instead of creating a second one (§1a).

     THE TITLE IS THE ROW'S OWN, PREFIX AND ALL. `POST /agenda/meeting`
     recomposes `${evType}: ${title}` when it writes back, so handing it a title
     that already carries the code would double it. `stripTypePrefix`'s rule,
     applied on the way OUT: the prefix comes off only when it equals this
     event's own type code (117 §9's ruling, which is tighter than a shape test
     and is why "URGENT: hearing" keeps its first word).

     THE ROSTER IS §5's, over `matterCast`'s cast — so clients and other parties
     appear, unchecked, beside the pre-checked case team, and anyone ALREADY on
     the event is pre-checked from the row's own stored addressees rather than
     from a guess.

     120 §3 — the paragraph above described `_mtgSeedFromEvent`, which is gone
     with the dialog it seeded. What it says about the ROSTER is still exactly
     true and is now this function's; what it said about opening a second
     surface is not, and has been removed rather than left standing. */
  /* ── BRIEF 120 §3 — THE ROSTER, LIFTED OUT OF THE SEED ──────────────────────
     Extraction only: every line below stood inside the dialog's seed and is
     unchanged. It moves because §3 puts the CHECKBOXES in the event modal, and
     the modal needs the same roster the dialog was seeded with — over the same
     matter, with the same pre-checks. Table 5's words for it: "its roster +
     pre-check logic MOVES, it does not vanish".

     It answers a DIFFERENT question from `eventPeople`, and 120's Table 3 said
     otherwise. `eventPeople` is who is ON the event; this is the matter's CAST
     — who COULD be invited — marked with who already is. 122 §2 merged the two
     into one list rather than choosing between them, which is the union those
     two questions were always owed. `src/lib/meetingAttendees.ts:89-95` states the split in its own
     words; what the two share is `groupPeopleByMatter`, the GROUPING, not the
     producer. Recorded in /tmp/prospects/found.txt (D3-1). */
  /* 122 §2 — `over` lets a CALLER's own cast be merged rather than re-derived,
     which is the `_attendeesFor` adoption path's whole reason for existing (a
     collective case's families are not resolvable here), and lets the internal
     meeting keep its deliberately narrowed roster as the entire population by
     passing `firm: []` — otherwise the firm-wide third population would put
     back the current user and the externals `buildInternalRoster` filters out. */
  const meetingRosterFor = (
    row: SeedableEvent,
    over?: { cast?: readonly MeetingAttendee[]; firm?: readonly unknown[] },
  ) => {
    const cs4 =
      cases.find((c: { id?: number }) => c.id === +(row.caId ?? row.caseId ?? 0)) ||
      null;
    const cc4 = cs4
      ? null
      : collectiveCases.find(
          (c: { id?: number }) => c.id === +(row.ccId ?? row.caId ?? 0),
        ) || null;
    const matter: MatterWithCast | null = (cs4 || cc4) as MatterWithCast | null;
    const teamIds = teamIdSet(
      (matter?.caseCollaborators || []).map((x) => x.coId),
    );
    /* The cast, from the ONE producer (119's §1c pre-step). A matter-less event
       has no parties, and an empty cast is a roster of the team alone — which is
       correct rather than a special case. */
    const cast = matterCast(
      {
        clIds: matter?.clIds,
        cpIds: matter?.cpIds,
        opIds: matter?.opIds,
        ocIds: matter?.ocIds,
        ocuIds: matter?.ocuIds,
        exIds: matter?.exIds,
        meIds: matter?.meIds,
        arIds: matter?.arIds,
        op2Ids: matter?.op2Ids,
      },
      {
        clients,
        contactPersons,
        opponents,
        opponentContacts,
        opponentLawyers,
        experts,
        mediators,
        arbiters,
        additionalOpponents,
      },
    );
    /* Already invited = the addresses the row has recorded (119 §6). On a first
       pass there are none and the roster opens exactly as the parties pane's
       does; on a second (§4) it opens with them ticked. */
    const invited = new Set<string>(
      (Array.isArray(row.outlookAttendees) ? row.outlookAttendees : [])
        .map((a: OutlookAttendee) => String(a?.email || "").trim().toLowerCase())
        .filter(Boolean),
    );
    const teamPersons = [...teamIds]
      .map((id) => collaborators.find((c: { id?: number }) => c.id === id))
      .filter(Boolean);
    /* 122 §1 — THE LABELS ARE REAL NOW, AND THIS IS THE FOURTH CALL SITE.
       §1's census names three (CaseView twice, CollectiveCaseView once); this
       is the fourth, and it was passing the placeholders `"team"` and `"cast"`
       — legitimately, because `meetingRoster` discarded them. §1 makes the
       label of a group of non-collaborators its SECTION HEADING, so the
       placeholders would have rendered a heading reading literally "cast".
       The families are CaseView's, because the id families read above are
       CaseView's. (A COLLECTIVE case resolves to the team alone here — the
       limitation recorded at the `_attendeesFor` effect — so §4's committee
       label has no population to reach at this call site and is not duplicated
       into it.)

       ── BRIEF 123 §4 — AND THE PARENTHESIS ABOVE IS STILL TRUE, WHICH IS WHY
       THE LABEL IS FIXED ANYWAY. §4 measured it: on a collective case the cast
       here IS the team alone (0 rows under any client heading), because
       `matterCast` reads `clIds` and a CollectiveCase keeps its members in
       `clientLinks`. The heading below is therefore a label on a group that is
       always EMPTY on a cc today — a wrong word nobody can currently see.

       It is corrected because it is the word that becomes visible the moment
       that limitation is lifted, and because "no population to reach" is not a
       reason for two routes to disagree about the same matter. `clientsFamilyKey`
       is the one derivation all three builders ask (lib/matterLabel), so they
       cannot answer differently again. */
    const castRows = meetingRoster(
      [
        { label: "team", persons: teamPersons, precheck: true, isCollaborator: true },
        {
          label: t(clientsFamilyKey(!!cc4)),
          persons: [...cast.cls, ...cast.cps],
          precheck: false,
        },
        {
          label: t("case.parties.opponents"),
          persons: [...cast.ops, ...cast.ocs],
          precheck: false,
        },
        { label: t("case.parties.opp_counsel"), persons: cast.ols, precheck: false },
        {
          label: t("case.parties.third_parties"),
          persons: [...cast.exs, ...cast.mes, ...cast.ars, ...cast.op2s],
          precheck: false,
        },
      ],
      pname,
      teamIds,
      invited,
      (k: PeopleGroupKey) => t(PEOPLE_GROUP_KEY[k]),
    );
    /* ── BRIEF 122 §2 — THE THREE POPULATIONS BECOME ONE LIST ────────────────
       The cast above is one of three. `eventPeople` supplies the second (§6's
       response status, and any external address that was invited but is not in
       the cast — it exists NOWHERE else). `choosableCo` supplies the third: the
       firm's whole roster, which is what the "Should attend" multi-select
       offered, and dropping it would take away the ability to put an event on a
       colleague's agenda when he is neither on the case team nor already
       invited. §2's rule is that nothing displayed today stops being displayed.

       `isNew` is what keeps §3a honest: on a new event the team's pre-check is
       the default, on an existing one the STORED `assigned` is the truth, so a
       colleague unticked and removed does not return ticked at the next open. */
    const attendees = mergeAttendeeRows({
      cast: over?.cast ?? castRows,
      people: eventPeople(
        teamIds,
        row.assignedCollaborators,
        row.outlookAttendees,
        collaborators,
        pname,
      ),
      firm: (over?.firm ??
        choosableCo(collaborators, row.assignedCollaborators)) as {
        id?: number | null;
        email?: string | null;
      }[],
      collaborators: collaborators as { id?: number | null; email?: string | null }[],
      teamIds,
      assigned: row.assignedCollaborators,
      invited,
      isNew: !row.id,
      name: pname,
      label: (k: PeopleGroupKey) => t(PEOPLE_GROUP_KEY[k]),
    });
    return { matter, isCc: !!cc4, attendees };
  };
  /* ── BRIEF 137 §4(d)/(e) — `saveEventThenInvite` STOOD HERE, AND ITS ROAD
     IS RETIRED WITH THE BUTTON THAT WAS ITS ONLY DOOR ─────────────────────────
     119 §1c made it a road and 120 §3 made it the mint itself: save the event
     with `deferMeeting`, then POST `/agenda/meeting` (or `/agenda/:id/
     attendees` on an already-minted row) with the ticked roster, so that Graph
     mailed everybody at save. That is exactly what §4 reverses — "who is on the
     event decides whether mail leaves, never which button was pressed" — and
     with the second primary withdrawn (§4d) nothing reached this at all.

     WHAT IT DID THAT IS NOT LOST, enumerated rather than assumed, because a
     253-line deletion is where behaviour goes missing quietly:

       · the SAVE — `chainEvent(null)` does it, and does it better: this
         called `saveAgendaEvent` directly and never recorded the link chain's
         pairwise links.
       · the INVITATION — `openInviteDraft` (§4c), from the same ticked roster,
         as one Outlook draft the user sends himself.
       · the ROOM CONFLICT TOAST — MEASURED, not assumed: the generic save's
         own error arm (`:5122`) already translates `reservation.conflict`,
         and `POST /agenda` / `PUT /agenda/:id` raise the identical 409 from
         the identical `reservationCore` helpers. This was the second spelling
         of that toast, not its only one.
       · the CONFLICT PANEL inside the modal — still driven by the live
         pick-time pre-check (`setMtgRoomConflicts` at `:6899-6925`), which is
         what populates it while the room and the time are being chosen. What
         is gone is the post-save re-population on a server-side RACE; that
         case now reports through the toast above instead of the panel.
       · the TOASTS — `saveAgendaEvent` already fires `eventSaveWords`'s
         `toastPlainKey` on every save. The `toastInviteKey` half is what §4
         retires.

     AND ONE THING THAT IS LOST, SAID PLAINLY RATHER THAN GLOSSED: the ZOOM and
     GOOGLE MEET tab-opening shortcuts. `saveEventThenInvite` was their ONLY
     call site (censused: `zoom.us/j/new` and `calendar.google.com` appear
     nowhere else in `src/`), so an event on those two platforms no longer
     opens a tab at save. It never created anything in LexCRM either way — the
     "Meeting link (optional)" field is how such an event gets its link, and
     that field is untouched. The Meet leg in particular pre-filled a Google
     Calendar invitation WITH THE ATTENDEE ADDRESSES, which is precisely the
     app-initiated invitation §4 exists to remove. Reported in the PR body so
     the shortcut can be asked for back deliberately rather than restored by
     accident.
     `mtgBusy` GOES WITH IT: it was this function's re-entry guard against a
     double-click minting two Graph meetings, and there is no mint. The create's
     own guard, `agCreateInFlight` (`saveAgendaEvent`), is untouched and is what
     stops a double save. */
  const openInternalPoll = () =>
    setDoodleDlg({
      internal: true,
      caseObj: null,
      attendees: buildInternalRoster(),
      title: t("calls.internal_meeting", { fallback: "Internal meeting" }),
    });
  const openInternalEmail = () =>
    setMailDlg({
      internal: true,
      caseObj: null,
      label: t("calls.colleagues", { fallback: "colleagues" }),
      persons: buildInternalRoster(),
      ccCols: [],
    });
  // Reconcile the mesh whenever the call log updates (every 'call' SSE event
  // upserts a row → this fires → the session adds/removes peers / opens / ends).
  useEffect(() => {
    const s = rtcSessionRef.current;
    if (!s) return;
    const row = calls.find((c: any) => Number(c.id) === Number(s.callId));
    if (row) syncCallSession(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, callWin]);
  // Caller fallback: open the window the moment a peer we're LIVE with attaches
  // (armed = media flowing), even if the answered roster update was missed
  // during setup. Phase 42: gate on `armed` so a ring-time pre-warm peer (media-
  // less) never surfaces the window before accept.
  useEffect(() => {
    if (!rtcSessionRef.current || callWin) return;
    if (callView && callView.peers && callView.peers.some((p: any) => p.armed)) {
      setOutgoingCall(null);
      setCallWin({ callId: Number(rtcSessionRef.current.callId) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callView, callWin]);
  // Phase 42 (Lever C) — drive ring-time pre-warm off the incoming-ring set.
  // Start a media-less pre-warm for the active ring; tear it down if that ring
  // ends WITHOUT an accept (decline / timeout / caller-cancel), so no dangling
  // peer or leaked media survives. Legacy Teams rings never pre-warm.
  useEffect(() => {
    if (!canCalls) return;
    const cid = rtcPrewarmRef.current;
    if (cid != null) {
      // A pre-warm is in flight → tear down if its ring is gone and I never
      // accepted (accept clears rtcPrewarmRef; an answered row clears it too).
      const stillRinging = incomingCalls.some((c: any) => Number(c.callId) === Number(cid));
      if (!stillRinging) {
        const row = callsRef.current.find((c: any) => Number(c.id) === Number(cid));
        if (!(row && iAmCommitted(row, Number(meRef.current)))) endCallSession();
      }
      return;
    }
    // Idle → pre-warm the newest ring if I'm not already in a call.
    if (rtcSessionRef.current) return;
    const ring = incomingCalls[incomingCalls.length - 1];
    if (ring && ring.callId != null && ring.medium !== "teams") startPrewarm(ring.callId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingCalls, canCalls]);
  // Initiate (Phase 39): IN-APP WebRTC, no Teams. Acquire camera+mic in the
  // click gesture, create the log row + ring invitees, then hold the session;
  // the caller lands in the floating call window on the first answer (the
  // waiting modal stays for the ringing phase — REUSED unchanged). If a call is
  // already live, "call these" means ADD them to it (the 4-cap is enforced
  // server-side and the UI prevents a 5th seat). fromPopOut tags the initiating
  // host so the waiting card renders where the call was started.
  const doCall = async (inviteeIds?: any, subject?: any, fromPopOut?: any) => {
    if (callBusy) return;
    /* 31.1 (item 2) — normalize to positive numeric ids, drop self, dedupe. */
    const ids = [
      ...new Set(
        (Array.isArray(inviteeIds) ? inviteeIds : [])
          .map(Number)
          .filter(
            (n) => Number.isFinite(n) && n > 0 && n !== Number(currentUser),
          ),
      ),
    ];
    if (!ids.length) {
      toast(
        t("calls.no_selection", {
          fallback: "Select one or more colleagues to call",
        }),
        "error",
      );
      return;
    }
    // Already in a call → this is an add, not a new call.
    if (rtcSessionRef.current) { addToCall(ids); return; }
    setCallBusy(true);
    let media = null;
    try {
      // Camera+mic permission prompt rides this click (graceful degrade to
      // audio-only). No usable device at all → actionable toast, no call.
      media = await acquireLocalMedia({ userId: currentUser }); // 204 §9 — the remembered devices
      if (!media.stream) {
        toast(mediaErrToast(media.error), "error");
        return;
      }
      const d = (await api.post("/calls", { inviteeIds: ids, subject })) as CallEnvelope | null;
      if (d?.call) {
        upsertCallRow(d.call, "create");
        callPerfMark(d.call.id, "initiate");
        setOutgoingCall({ callId: d.call.id, host: fromPopOut ? "popout" : "main" });
        await beginSession(d.call.id, media, d.call, false); // window opens on first answer
      } else if (media.stream) {
        media.stream.getTracks().forEach((tr) => tr.stop());
      }
      setCallPanelOpen(false);
    } catch (e: any) {
      if (media && media.stream) { try { media.stream.getTracks().forEach((tr) => tr.stop()); } catch { /* ignore */ } }
      /* Never surface raw transport text; map known codes, else generic. */
      console.error("[calls] initiate failed:", e);
      const msg =
        e?.code === "call.too_many"
          ? t("calls.too_many", { fallback: "A call can include at most 4 people." })
          : // 179 A.2.2 — the server's refusal under view-as. `e.message` is the
            // sentence api.ts already resolved through `srvErr.<code>` in the
            // reader's language, not transport text.
            e?.code === "auth.simulated_read_only"
            ? e.message
            : t("calls.call_failed", { fallback: "Could not start the call" });
      toast(msg, "error");
    } finally {
      setCallBusy(false);
    }
  };
  /* 31.1 (item 6) — caller-side abort: flips every still-ringing invitee
     to missed and the call to 'canceled' (server publishes the update —
     their ring popups dismiss live). Close the modal only on success: a
     409 means an accept/decline/timeout won the race, and the live row
     already shows that outcome in the modal. */
  const abortCall = async (callId: any) => {
    try {
      const d = (await api.post(`/calls/${callId}/cancel`, {})) as CallEnvelope | null;
      if (d?.call) upsertCallRow(d.call, "update");
      setOutgoingCall(null);
    } catch (e) {
      console.error("[calls] cancel failed:", e);
    }
  };
  // Auto-close the waiting modal when the call is canceled from elsewhere
  // (another tab's abort arriving over the stream).
  useEffect(() => {
    if (!outgoingCall) return;
    const row = calls.find((c: any) => c.id === outgoingCall.callId);
    if (row && row.status === "canceled") setOutgoingCall(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calls, outgoingCall]);
  // Stream-loss insurance: if nothing resolved the modal by ring-window +
  // grace (the server sweep flips the row at 40 s), refetch the log once —
  // the modal resolves from the fetched row instead of hanging on
  // "Waiting…" forever.
  useEffect(() => {
    if (!outgoingCall) return;
    const tm = setTimeout(() => {
      api
        .get("/calls")
        .then((rows) => Array.isArray(rows) && setCalls(rows))
        .catch(() => {});
    }, 55000);
    return () => clearTimeout(tm);
  }, [outgoingCall]);
  // Invitee response. Accept opens the join URL; decline sends the
  // availability time which the caller sees live and the log keeps.
  /* 31.1 (item 9b) — hostWin is the GESTURE's window (the pop-out ring
     banner passes its PiP window): the placeholder tab must open from the
     window holding the click's transient activation, or the popup blocker
     can eat it. Main-window callers omit it. */
  const respondFailToast = (e: any) =>
    toast(
      e?.code === "call.too_late"
        ? t("calls.too_late", { fallback: "This call already ended" })
        : // 179 A.2.2 — the server's refusal under view-as, already resolved
          // to the reader's language by api.ts (`srvErr.<code>`).
          e?.code === "auth.simulated_read_only"
          ? e.message
          : t("calls.respond_failed", { fallback: "Could not send your response" }),
      "error",
    );
  const respondCall = async (callId?: any, action?: any, availableFrom?: any, hostWin?: any) => {
    // ── Decline: send availability, dismiss the ring (no media, no window). ──
    if (action !== "accept") {
      // Phase 42 — a declined ring may hold a media-less pre-warm session; drop it.
      if (Number(rtcPrewarmRef.current) === Number(callId)) endCallSession();
      try {
        await api.post(`/calls/${callId}/respond`, { action, availableFrom: availableFrom || undefined });
      } catch (e) {
        console.error("[calls] respond failed:", e);
        respondFailToast(e);
      }
      setIncomingCalls((prev: any) => prev.filter((c: any) => c.callId !== callId));
      setCallDeclineFor(null);
      return;
    }
    // ── Accept ──
    const ring = incomingCalls.find((c: any) => Number(c.callId) === Number(callId));
    // Legacy Teams calls (medium 'teams', only pre-Phase-39 in-flight rows)
    // keep the open-the-joinUrl behaviour; everything new is WebRTC.
    if (ring && ring.medium === "teams") {
      // Defensive: a Teams ring surfaced without a medium field could have
      // pre-warmed a WebRTC session that will never connect — drop it.
      if (Number(rtcPrewarmRef.current) === Number(callId)) endCallSession();
      const w = (hostWin || window).open("about:blank", "_blank");
      try {
        const d = (await api.post(`/calls/${callId}/respond`, { action: "accept" })) as { joinUrl?: string } | null;
        if (d?.joinUrl && w) w.location = d.joinUrl;
        else if (w) w.close();
      } catch (e) {
        try { w && w.close(); } catch { /* ignore */ }
        console.error("[calls] respond failed:", e);
        respondFailToast(e);
      }
      setIncomingCalls((prev: any) => prev.filter((c: any) => c.callId !== callId));
      setCallDeclineFor(null);
      return;
    }
    // WebRTC accept. Phase 42: acquire camera+mic, POST the accept, and (only
    // when we must build a session cold) fetch TURN creds — all in PARALLEL
    // instead of serially (Lever B). If the ring already PRE-WARMED a media-less
    // session (Lever C), accept just flips our media into the already-connected
    // PC — no new session, no TURN fetch, no offer/answer at accept.
    callPerfMark(callId, "accept-click");
    const prewarmed =
      !!rtcSessionRef.current &&
      Number(rtcSessionRef.current.callId) === Number(callId) &&
      Number(rtcPrewarmRef.current) === Number(callId);
    rtcPrewarmRef.current = null; // accepting → no longer a pre-warm
    // Use the ring-time pre-warmed camera if we have it (device already open →
    // near-instant flip + self-view); else acquire now. Consume the ref so
    // endCallSession won't stop the track we're about to promote to the call.
    const preMediaP = rtcPrewarmMediaRef.current;
    rtcPrewarmMediaRef.current = null;
    const mediaP = preMediaP || acquireLocalMedia({ userId: currentUser }).catch(() => ({ stream: null, error: "error" })); // 204 §9
    const respondP = api.post(`/calls/${callId}/respond`, { action: "accept" });
    const iceP = prewarmed ? null : fetchIceServers(); // cold path only; runs in parallel
    let media = null;
    try {
      media = await mediaP;
      callPerfMark(callId, "media-acquired");
      if (prewarmed && rtcSessionRef.current) {
        // Flip media into the connected pre-warm session and optimistically arm
        // the initiator (always committed) over it — without waiting for respond.
        setCallMediaErr(media && !media.stream ? media.error || "error" : null);
        rtcSessionRef.current.setLocalMedia(media ? media.stream : null);
        const cur = callsRef.current.find((c: any) => Number(c.id) === Number(callId));
        if (cur) {
          const me = Number(meRef.current);
          syncCallSession({
            ...cur,
            participants: (cur.participants || []).map((p: any) =>
              Number(p.collaboratorId) === me ? { ...p, outcome: "answered" } : p,
            ),
          });
        }
        const d = (await respondP) as CallEnvelope | null;
        callPerfMark(callId, "respond-returned");
        if (d?.call) { upsertCallRow(d.call, "update"); syncCallSession(d.call); }
      } else {
        const d = (await respondP) as CallEnvelope | null;
        callPerfMark(callId, "respond-returned");
        if (d?.call) {
          upsertCallRow(d.call, "update");
          await beginSession(callId, media, d.call, true, iceP);
        } else if (media && media.stream) {
          media.stream.getTracks().forEach((tr: any) => tr.stop());
        }
      }
    } catch (e) {
      if (media && media.stream) { try { media.stream.getTracks().forEach((tr: any) => tr.stop()); } catch { /* ignore */ } }
      // Accept failed (e.g. call.too_late) — tear down any pre-warm / half-built
      // session for this call so no dangling peer or flowing media survives the
      // optimistic arm above.
      if (rtcSessionRef.current && Number(rtcSessionRef.current.callId) === Number(callId)) endCallSession();
      console.error("[calls] respond failed:", e);
      respondFailToast(e);
    }
    setIncomingCalls((prev: any) => prev.filter((c: any) => c.callId !== callId));
    setCallDeclineFor(null);
  };
  /* Phase 31 (C4) — meeting modal: room picker data + pick-time
     availability. Slots are the 07:00–20:00 half-hour SCHED encoding
     (server mirror: lib/reservationCore.timeToSlot). */
  /* 137 §4(d) — `mtgBusy` STOOD HERE. It was `saveEventThenInvite`'s re-entry
     guard against a double-click minting two Graph meetings; there is no mint,
     and the create's own guard (`agCreateInFlight`, in `saveAgendaEvent`) is
     what stops a double save. Zero readers remained after the road went. */
  const [mtgRooms, setMtgRooms] = useState<any>(null); // null = not fetched yet
  const [mtgRoomConflicts, setMtgRoomConflicts] = useState<any>(null);
  const mtgTimeToSlot = (hm?: any, ceil?: any) => {
    const m2 = /^(\d{1,2}):(\d{2})$/.exec(String(hm || ""));
    if (!m2) return null;
    const mins = (Number(m2[1]) - SCHED_START) * 60 + Number(m2[2]);
    const slot = ceil ? Math.ceil(mins / 30) : Math.floor(mins / 30);
    return slot < 0 || slot > 26 ? null : slot;
  };
  // "HH:mm" + 60 min (clamped to the day) — the client's default meeting end.
  const hmPlus60 = (hm: any) => {
    const m2 = /^(\d{1,2}):(\d{2})$/.exec(String(hm || ""));
    if (!m2) return "";
    const mins = Math.min(23 * 60 + 59, Number(m2[1]) * 60 + Number(m2[2]) + 60);
    return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  };
  /* Corr-2 (item 8) — ONE availability probe drives BOTH meeting surfaces
     (the Parties-pane meeting modal and the generic event modal's REU leg);
     only one of the two is ever open, so the rooms/conflicts state is
     shared. The generic modal's probe excludes the event's OWN reservation
     (editing a meeting must not conflict with itself). */
  /* 123 §3 — TWO FACTS WHERE ONE `agMeetKind` STOOD. `agIsMeeting` is the
     TYPE's question (§1) and `agMode` is the EVENT's (§2); the old single value
     answered both at once and could answer neither for a type it did not know.
     `agMode` is null when the type is not a meeting type, so every `=== "person"`
     / `=== "online"` below is false for an ordinary event exactly as
     `=== "room"` / `=== "virtual"` used to be. */
  const agIsMeeting = activeModal === "ag" && evIsMeeting(form.type);
  /* 124 §1 — the toggle opens on the TYPE's declared default when the form has
     not answered yet, which is precisely a new event (and an old row whose type
     was flagged a meeting type after 123 §2's backfill ran, so it holds no
     answer either). Touching the toggle writes `form.meetingMode` and this
     stops consulting the type at all. */
  const agMode: MeetingMode | null = agIsMeeting
    ? meetingModeFor(form, evDefaultMode(form.type))
    : null;
  /* ── BRIEF 120 §3 — AND A LATENT DEFECT GOES WITH THE SURFACE (Table 5) ─────
     This branched on `mtgDlg && mtgDlg.mode === "person"` FIRST and on
     `agMeetKind === "room"` second. The dialog's arm carried
     `excludeEventId: null`; the modal's carries `form.id` — so editing a
     meeting through the dialog CONFLICTED WITH ITS OWN reservation, and the
     only reason it was not visible everywhere is that the dialog's arm won
     whenever the dialog was open. With the dialog gone the second arm is the
     whole probe, and the exclusion is now unconditional. Nothing was fixed
     here: a defect disappeared because the surface that carried it did. */
  const roomProbe =
    agMode === "person"
      ? {
          active: true,
          placeId: form.roomPlaceId,
          date: form.date,
          start: form.time || "",
          end: form.endTime || (form.time ? hmPlus60(form.time) : ""),
          excludeEventId: form.id || null,
        }
      : { active: false };
  useEffect(() => {
    if (!roomProbe.active || mtgRooms !== null) return;
    api
      .get("/places")
      .then((rows) =>
        setMtgRooms(Array.isArray(rows) ? rows.filter((p) => !p.archived) : []),
      )
      .catch(() => setMtgRooms([])); // 403 (no lateral.places bit) → no picker
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomProbe.active]);
  useEffect(() => {
    if (!roomProbe.active || !roomProbe.placeId || !roomProbe.date) {
      setMtgRoomConflicts(null);
      return;
    }
    let dead = false;
    const s2 = mtgTimeToSlot(roomProbe.start);
    const e2 = mtgTimeToSlot(roomProbe.end, true);
    if (s2 == null || e2 == null || e2 <= s2) {
      setMtgRoomConflicts([{ badWindow: true }]);
      return;
    }
    api
      .get(
        `/reservations?from=${roomProbe.date}&to=${roomProbe.date}&placeId=${roomProbe.placeId}`,
      )
      .then((rows) => {
        if (dead) return;
        const cf = (Array.isArray(rows) ? rows : []).filter(
          (r) =>
            s2 < r.endSlot &&
            r.startSlot < e2 &&
            (!roomProbe.excludeEventId ||
              r.eventId !== roomProbe.excludeEventId),
        );
        setMtgRoomConflicts(cf.length ? cf : null);
      })
      .catch(() => {
        if (!dead) setMtgRoomConflicts(null);
      });
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    roomProbe.active,
    roomProbe.placeId,
    roomProbe.date,
    roomProbe.start,
    roomProbe.end,
    roomProbe.excludeEventId,
  ]);
  const canBumpRooms =
    _appIsAdmin || (_appPerms.viewer && _appPerms.can("lateral.places.override"));
  /* Corr-2 (item 8) — one-shot prefill when the generic modal opens on an
     EXISTING meeting event: PHYSICAL → resolve the bound reservation into the
     room picker (the _roomLoaded flag also gates the release-on-save, so a
     fast save before this resolves can never strip a binding silently);
     ONLINE → infer platform + pasted link from the persisted row.
     123 §3 — the two arms were "REU" and "VIR"; they are the MODE now, read off
     the row through the same `evMode` every other site uses. */
  useEffect(() => {
    if (activeModal !== "ag" || !agIsMeeting || !form.id || form._meetInit) return;
    setForm((p: any) => ({
      ...p,
      _meetInit: true,
      ...(evMode(p) === "online"
        ? {
            platform:
              p.platform ||
              (p.organizerEventId
                ? "teams"
                : p.joinUrl
                  ? /meet\.google/i.test(p.joinUrl)
                    ? "gmeet"
                    : "zoom"
                  : ""),
            meetingUrl:
              p.meetingUrl !== undefined
                ? p.meetingUrl
                : !p.organizerEventId && p.joinUrl
                  ? p.joinUrl
                  : "",
          }
        : {}),
    }));
    if (agMode === "person" && form.date) {
      const evId = form.id;
      api
        .get(`/reservations?from=${form.date}&to=${form.date}`)
        .then((rows) => {
          const own = (Array.isArray(rows) ? rows : []).find(
            (r) => r.eventId === evId,
          );
          setForm((p: any) =>
            p.id === evId
              ? {
                  ...p,
                  _roomLoaded: true,
                  ...(own && p.roomPlaceId == null
                    ? { roomPlaceId: own.placeId }
                    : {}),
                }
              : p,
          );
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, agIsMeeting, agMode, form.id]);
  /* ── BRIEF 120 §3 — THE ROSTER FOLLOWS THE MATTER, WHILE THE MODAL IS OPEN ──
     The dialog was seeded once, at open, because it was opened FROM a matter and
     could never change it. The event modal CAN: its case combobox is editable
     unless `_caseFixed`, so a roster fixed at open would list the first matter's
     parties against the second matter's event.

     Keyed on the matter alone, so it fires when the matter changes and not when
     a box is ticked — the ticks are `_attendees`, which is deliberately not in
     the key. Existing ticks survive a matter change BY ADDRESS: somebody who is
     on both matters stays as the user left him, and everybody else arrives at
     the producer's own pre-check (team ticked, parties not).

     The INTERNAL entry is skipped outright: its roster is the firm's people,
     handed in by `openInternalMeeting`, and no matter can produce it. */
  const _agMatterKey =
    activeModal === "ag" ? `${form.caId ?? ""}|${form.ccId ?? ""}` : "";
  useEffect(() => {
    if (activeModal !== "ag" || form._internal) return;
    setForm(
      (
        p: SeedableEvent & {
          _attendees?: MeetingAttendee[];
          _attendeesFor?: string;
          _attendeesBase?: string[];
        },
      ) => {
        const key = `${p.caId ?? ""}|${p.ccId ?? ""}`;
        if (p._attendeesFor === key) return p;
        /* ── A CALLER'S OWN ROSTER IS ADOPTED, NEVER RE-DERIVED ───────────────
           MEASURED, and it is the finding that stopped this being one line.
           For a COLLECTIVE case the pane's cast is not the row's own scalar id
           lists: `CollectiveCaseView.tsx:4161-4172` reads clients off
           `cs.clientLinks`, and opponents / opposing counsel / additional
           opponents off the derived `ccIds` — four of the nine families. This
           function reads `matter.clIds`/`opIds`/… like the case pane does, so
           re-deriving a collective roster here would hand back the TEAM ALONE
           and silently drop the collective meeting's clients and opponents.

           So the pane that knows its own id sources passes `_attendees`, and
           this adopts it: an un-stamped roster is a caller's, and only the
           stamp (`_attendeesFor`, the matter it was built for) licenses a
           rebuild. Rebuilds then happen exactly when the user picks a
           DIFFERENT matter in the combobox, which is the case this effect
           exists for.

           KNOWN AND PRE-EXISTING, surfaced rather than fixed: picking a
           COLLECTIVE case from the modal's own combobox yields the team alone,
           because App cannot resolve `clientLinks`/`ccIds`. That is what the
           dialog's seed always did for a cc event on "Save & invite"; this
           brief does not widen it. */
        /* 122 §2 — AN ADOPTED ROSTER IS STILL MERGED. Adoption is about the
           CAST, which App cannot re-derive for a collective case; it was never
           about the other two populations. So the caller's cast is handed to
           the merge as the cast, and the event's own people and the firm's
           roster join it here — otherwise a parties-pane event would show the
           matter's parties and lose both the response status and every
           colleague off the case team. */
        const built = p._attendees && p._attendeesFor == null
          ? meetingRosterFor(p, { cast: p._attendees }).attendees
          : meetingRosterFor(p).attendees;
        /* Ticks survive a matter change BY IDENTITY — the merge's own key, so a
           collaborator with no address is carried like everybody else where the
           old address-keyed map dropped him into a single "" bucket. */
        const was = new Map<string, boolean>(
          (p._attendees || []).map((a) => [
            (a as AttendeeRow).key ??
              attendeeKey(null, String(a.email || "")),
            !!a.checked,
          ]),
        );
        const rows = built.map((a) =>
          was.has(a.key) ? { ...a, checked: !!was.get(a.key) } : a,
        );
        return {
          ...p,
          _attendeesFor: key,
          _attendees: rows,
          /* §2 — "`assignedCollaborators` is what the ticked TEAM/FIRM rows
             produce." Re-derived with the list so the two cannot disagree.
             Nobody is lost by this: the firm's whole roster is in the
             population, so an assigned colleague always has a row to be
             ticked on. */
          assignedCollaborators: assignedFromRows(rows),
          /* 122 §3b — WHO WAS ON THE EVENT WHEN THIS MODAL OPENED. The
             confirmation's counts come from the ACTUAL diff, and this is what
             they are measured against: the check state the user was shown, not
             a re-reading of the row. `??` and not `=` — the roster rebuilds
             when the matter changes, and the baseline is a fact about the
             EVENT, so it is captured once per open and never moved under the
             user's feet. A new event never has one that matters: §3b does not
             fire without `form.id`. */
          _attendeesBase:
            p._attendeesBase ??
            rows.filter((r) => r.checked).map((r) => r.key),
        };
      },
    );
    /* No `exhaustive-deps` disable here, deliberately: this repo configures no
       eslint, so the directive would suppress nothing — and `ratchets.js` counts
       it as debt against App.tsx's level of 19, which it is already at. */
  }, [activeModal, _agMatterKey]);
  /* Phase 31 (C4) — Outlook-import surfacing: count the user's NEW (not
     yet imported) calendar events over the next 14 days, once per boot.
     409 m365.not_connected (staging / unconnected users) stays silent.

     ── 119 §3 — AND "ONCE PER BOOT" WAS THE WHOLE OF IT ──────────────────
     Everything else §3 asks for already existed: the route classifies
     NEW/ALREADY (routes/agenda.js:1051-1141), the bell carries the count
     (:10830 below) and the Agenda view prompts (:16428). What was missing
     is liveness — a user who accepted an invitation at 09:05 learned about
     it at tomorrow's login. The count is now re-taken on a timer AND on the
     hide→show cycle already wired below for missed calls, which is exactly
     the moment somebody comes back from their mailbox.

     CLIENT-SIDE, NEVER A SERVER CRON (RULED): the route calls Graph as
     `req.user` (`m365.searchUserCalendar(req.user.id, …)`), so a server
     sweep would mean holding and refreshing every collaborator's Microsoft
     token to poll on their behalf. Not for this feature.

     THE GUARD IS 110 §2's SHAPE — an in-flight flag plus a `lastAttempt`
     cooldown (`src/hooks/useTaxonomy.ts:53-58` and `:251-260`), which is
     where this tree's pair actually lives. §3 cites "the 117 §2 cooldown
     pattern"; 117 §2 is the subtask status pill and carries no cooldown at
     all, so the coordinate is corrected rather than followed. */
  const [outlookNewCount, setOutlookNewCount] = useState<any>(0);
  const [agAutoImport, setAgAutoImport] = useState<any>(false);
  /* The popup carries the count that raised it; `null` is "not on screen".
     `…Seen` is what the last COMPLETED search found, so a rise is measured
     against the previous answer rather than against zero — otherwise every
     boot with a full mailbox would raise one. */
  const [outlookPopup, setOutlookPopup] = useState<number | null>(null);
  const outlookPopupMutedRef = useRef<boolean>(false);
  const outlookSeenCountRef = useRef<number | null>(null);
  const outlookInFlightRef = useRef<boolean>(false);
  const outlookLastAttemptRef = useRef<number>(0);
  const runOutlookOrphanCheck = useCallback(() => {
    if (!authed || !currentUser) return;
    if (outlookInFlightRef.current) return;
    if (Date.now() - outlookLastAttemptRef.current < OUTLOOK_ORPHAN_MIN_GAP_MS)
      return;
    outlookInFlightRef.current = true;
    outlookLastAttemptRef.current = Date.now();
    const from = new Date();
    const to = new Date(Date.now() + 14 * 24 * 3600 * 1000);
    api
      .get(
        `/agenda/outlook/search?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      )
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        const n = rows.filter((r: any) => r.classification === "NEW").length;
        setOutlookNewCount(n);
        const prev = outlookSeenCountRef.current;
        outlookSeenCountRef.current = n;
        /* The FIRST answer of a session sets the baseline and raises nothing:
           the bell entry and the Daily Overview already carry the standing
           count, and a popup on top of them at boot is one fact rendered
           three times. */
        if (prev != null && n > prev && !outlookPopupMutedRef.current)
          setOutlookPopup(n);
      })
      /* 409 m365.not_connected is the ordinary state of a user who never
         connected M365, and it stays as silent here as it was at boot. */
      .catch(() => {})
      .finally(() => {
        outlookInFlightRef.current = false;
      });
  }, [authed, currentUser]);
  /* The hide→show handler below is dep-less by design (it reads its counts
     through refs), so it reaches this callback through one too. */
  const outlookCheckRef = useRef<any>(null);
  outlookCheckRef.current = runOutlookOrphanCheck;
  useEffect(() => {
    if (!authed || !currentUser) return;
    runOutlookOrphanCheck();
    const iv = setInterval(runOutlookOrphanCheck, OUTLOOK_ORPHAN_POLL_MS);
    return () => clearInterval(iv);
  }, [authed, currentUser, runOutlookOrphanCheck]);
  // RED badge = missed calls newer than the last panel open.
  const missedCallCount = canCalls
    ? calls.filter((c: any) => {
        const mine = (c.participants || []).find(
          (p: any) => p.collaboratorId === currentUser,
        );
        if (!mine || mine.outcome !== "missed") return false;
        return !callsSeenAt || (c.startedAt || "") > callsSeenAt;
      }).length
    : 0;
  /* Item-7 — when the user RETURNS to LexCRM (focus/visibility regained) AND a
     call was missed DURING the absence (the missed count rose since focus was
     last lost), auto-open the comms panel so the call log surfaces the misses —
     not only the red badge. Guarded to a real hide→show cycle with an increase,
     so an ordinary refocus (nothing missed) never auto-opens. */
  const missedCallCountRef = useRef<any>(0);
  missedCallCountRef.current = missedCallCount;
  const missedAtHideRef = useRef<any>(0);
  const wasHiddenRef = useRef<any>(false);
  useEffect(() => {
    const onHide = () => {
      wasHiddenRef.current = true;
      missedAtHideRef.current = missedCallCountRef.current;
    };
    const onShow = () => {
      if (
        wasHiddenRef.current &&
        missedCallCountRef.current > missedAtHideRef.current
      ) {
        setCallPanelOpen(true);
        setCallsSeenAt(new Date().toISOString());
      }
      /* 119 §3 — the second reader of the SAME cycle, rather than a second
         set of listeners on the same three events. A real hide→show only:
         coming back from the mailbox is the moment an Outlook invitation
         has just been accepted, and an ordinary in-app blur is not. The
         cooldown inside the check is what makes this safe to call on every
         such cycle. */
      if (wasHiddenRef.current) outlookCheckRef.current?.();
      /* 210 §1 — the third reader of the same cycle: a list that could not
         load is asked again when the window comes back (a real hide→show,
         as the Outlook check). */
      if (wasHiddenRef.current) _listsRetryRef.current?.();
      wasHiddenRef.current = false;
    };
    const onVis = () => (document.hidden ? onHide() : onShow());
    window.addEventListener("blur", onHide);
    window.addEventListener("focus", onShow);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("blur", onHide);
      window.removeEventListener("focus", onShow);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openCallPanel = () => {
    setCallPanelOpen((v: any) => {
      const next = !v;
      if (next) {
        setCallsSeenAt(new Date().toISOString());
        // Corr-4 (PART C) — arm Web Push the moment the user first engages with
        // calling while present (this click is the gesture permission needs), so
        // a LATER backgrounded incoming call can wake the tab. Once-guarded;
        // silent fallback if denied/unsupported.
        armCallPush();
      }
      return next;
    });
  };
  /* Round 18 (C103) — the CDD notifications rollup, fetched ONCE per
     session and only for viewers the server will actually allow. The
     server's rule is rpCan(viewer, 'lateral.reportsCDD') (admin overrides
     inside the engine); the client mirrors it through the SAME source of
     truth — /api/permissions/me via usePermissions — and DEFERS until that
     payload has arrived (viewer != null), so the predicate can never race
     the role data: an admin's first paint no longer skips the fetch, and a
     denied role never fires it (no 403 in the console). If /permissions/me
     itself failed, viewer stays null and the rollup is simply skipped —
     CDD notifications need the API anyway. */
  const _cddFetchedRef = useRef<any>(false);
  useEffect(() => {
    if (!authed) {
      _cddFetchedRef.current = false;
      return;
    }
    if (_cddFetchedRef.current || !_appPerms.viewer) return;
    _cddFetchedRef.current = true;
    if (!(_appIsAdmin || _appPerms.can("lateral.reportsCDD"))) return;
    api
      .get("/reports/cdd")
      .then((d) => {
        if (d && typeof d === "object") setCddReportData(d);
      })
      .catch(() => {
        /* transient failure — notifications simply stay absent */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, _appPerms.viewer, _appIsAdmin]);
  /* 111 §5b — the save-fn opts (persist's onCreatedRow passthrough), typed
     once here for both entity save fns. */
  type SaveRowOpts = {
    onCreatedRow?: (row: { id?: number | string } | null) => void;
  };
  const saveTask = (fd2: any, opts?: SaveRowOpts) => {
    if (!fd2.title) return false;
    if (fd2.id) {
      const orig = tasks.find((t: any) => t.id === fd2.id);
      if (orig && orig.status === "archived") return false;
      /* 112 §6 — TWO ARCHIVE MECHANISMS EXISTED ON TASKS and this is where
         they converge. The Phase-28 `archived` boolean is the house one: the
         engine's delete op IS archiveTask (App.tsx SK.tk d:), ArchiveView
         lists it, restore works. But several affordances (the not-own row
         Archive button, the status dropdown's "Archived" entry) wrote
         `status: "archived"` instead — a string the server stores and no
         surface ever retires, so the row stayed on every live list forever:
         the glance's "archived items ANYwhere" sighting. A save that SETS
         status 'archived' now MEANS the archive: the row leaves the array and
         the engine fires the real archive route. The status string is never
         written, so a later restore from ArchiveView brings the row back with
         the live status it had. */
      if (orig && fd2.status === "archived") {
        keepScroll(() => {
          persist(SK.tk, tasks.filter((t: { id: number }) => t.id !== fd2.id), setTasks);
        });
        return true;
      }
    }
    /* Round 17 (C102) — _timerHold rides the completion-pend object only;
       it must never reach the task row / PUT body. */
    /* 184 B.4 — `_dropEml` (the dropped File / Message-ID) is `chainGo`'s
       second act, never a row field and never on the PUT/POST body. */
    const { _caseFixed: _cf, _timerHold: _th, ...fd2c } = withoutDropEml(fd2);
    const _coId = fd2c.assignedCoId ? +fd2c.assignedCoId : null;
    const td = {
      ...fd2c,
      /* 184 A.1 — MEASURED: the picker has written `ccId: ""` for a case pick
         (and `caseId: ""` for a cc pick) since 131 §3, and only `caseId` was
         normalised here (`fd2c.caseId ? +fd2c.caseId : null`) — so the PUT
         carried `""` to an `Int?` column and Prisma refused it (500
         `generic.unhandled`, reproduced on staging). The field was hidden
         inside every matter, which is why no move ever reached the wire. Now
         that it shows everywhere, BOTH ids take the one normaliser. */
      caseId: matterIdOrNull(fd2c.caseId),
      ccId: matterIdOrNull(fd2c.ccId),
      assignedCoId: _coId,
      assigned: _coId ? [_coId] : [],
      priority: fd2c.priority || null,
      status: fd2c.status || "todo",
    };
    let newTkArr;
    if (fd2.id) {
      const newTk2 = { ...(tasks.find((t: any) => t.id === fd2.id) || {}), ...td };
      newTkArr = tasks.map((t: any) => (t.id === fd2.id ? newTk2 : t));
    } else {
      newTkArr = [
        ...tasks,
        { ...td, id: tempId(), createdBy: td.createdBy || currentUser },
      ];
    }
    keepScroll(() => {
      /* 111 §5b — opts (onCreatedRow) threads through for the link chain. */
      persist(SK.tk, newTkArr, setTasks, opts);
    });
    return true;
  };
  /* Round 17 (C102) — completion ↔ timer link (was NEVER WIRED: the
     completion prompts and the timer-stop plumbing grew as separate paths,
     so completing a timed entity left its timer ticking and the offered log
     at 0 minutes). HOLD pauses the entity's own timer the moment a
     completion prompt opens and snapshots its elapsed; RELEASE either
     resumes it to its prior state (Cancel / revert — only if it was
     running, honouring the one-at-a-time invariant by freezing any other
     running timer first, exactly like startTimer) or CLEARS it (the entity
     is done: "just finish", or the te modal's save via _consumeTimer).
     Exact-key matching only — a task completion consumes the TASK-level
     timer, a subtask completion consumes THAT subtask's timer. */
  const holdEntityTimer = (taskId: any, subtaskId: any) => {
    const _key = subtaskId || null;
    const tm = activeTimers.find(
      (t2: any) => t2.taskId === taskId && (t2.subtaskId || null) === _key,
    );
    if (!tm) return null;
    const wasRunning = !tm.paused;
    const elapsed = tm.paused
      ? tm.elapsed
      : tm.elapsed + (Date.now() - tm.startedAt);
    if (wasRunning) {
      setActiveTimers((prev: any) =>
        prev.map((t2: any) =>
          t2.taskId === taskId &&
          (t2.subtaskId || null) === _key &&
          !t2.paused
            ? {
                ...t2,
                paused: true,
                elapsed: t2.elapsed + (Date.now() - t2.startedAt),
              }
            : t2,
        ),
      );
    }
    return { taskId, subtaskId: _key, wasRunning, elapsed };
  };
  const releaseEntityTimer = (hold: any, mode: any) => {
    if (!hold) return;
    if (mode === "clear") {
      // A5 (brief 65) — src/lib/timerStops. `hold.subtaskId` is passed exactly
      // as it stood: normalising it inside the helper would change this side.
      dropTimerRow(
        { setActiveTimers, setFocusedTimerIdx, count: activeTimers.length },
        hold.taskId,
        hold.subtaskId,
      );
      return;
    }
    if (hold.wasRunning) {
      const now = Date.now();
      setActiveTimers((prev: any) =>
        prev.map((t2: any) => {
          const isHeld =
            t2.taskId === hold.taskId &&
            (t2.subtaskId || null) === hold.subtaskId;
          if (isHeld && t2.paused)
            return { ...t2, paused: false, startedAt: now };
          // one-at-a-time: anything else running freezes at the boundary.
          if (!isHeld && !t2.paused)
            return {
              ...t2,
              paused: true,
              elapsed: t2.elapsed + (now - t2.startedAt),
            };
          return t2;
        }),
      );
    }
  };
  // Phase 29 C61 — canonical subtask-done hand-off: every checklist surface
  // (task modal, CV/CCV rows, compact column) funnels here so the SAME
  // complete-style prompt + time-log offer runs app-wide.
  /* Round 15 (C91) — the prompt CARRIES ITS OWN te payload, computed here at
     trigger time from the CANONICAL tk row (hosts may hand a derived
     snapshot; a pop-out → main hand-off must not depend on whatever ambient
     state the main window holds when the button is finally clicked). The
     "Log time" button just opens the modal with this object. */
  const askSubtaskDone = (task: any, subtask: any) => {
    const row =
      (task && task.id != null && tasks.find((x: any) => x.id === task.id)) ||
      task ||
      {};
    const _desc = subtask
      ? (row.title ? row.title + " — " : "") + (subtask.text || "")
      : row.title || "";
    const _hasCase = row.caseId != null || row.ccId != null;
    /* Round 17 (C102) — the completed entity's OWN timer pauses now and
       rides the ask: Log time prefills its minutes (timer-stop rounding)
       and consumes it on save; "just finish" clears it; Cancel resumes. */
    const _hold = holdEntityTimer(row.id, subtask ? subtask.id : null);
    setSubtaskDoneAsk({
      task: row,
      subtask,
      _timerHold: _hold,
      tePayload: {
        caId: row.caseId || "",
        ccId: row.ccId || "",
        coId: currentUser || "",
        date: new Date().toISOString().slice(0, 10),
        type: row.type || "",
        desc: _desc,
        billed: false,
        min: _hold ? Math.max(5, roundUp5(_hold.elapsed)) : 0,
        _fromTask: row.id || true,
        ...(_hold
          ? {
              _consumeTimer: {
                taskId: _hold.taskId,
                subtaskId: _hold.subtaskId,
              },
            }
          : {}),
        ...(_hasCase
          ? {
              _caseFixed: true,
              _caseTitle: row.caseTitle || row.ccTitle || "",
              _caseRef: row.caseRef || row.ccRef || "",
            }
          : {}),
        ...(subtask ? { _fromSubtask: 1, subtaskId: subtask.id } : {}),
      },
    });
  };
  /* Round 13 (C76) — Cancel on the subtask-done prompt REVERTS the trigger:
     the tick already persisted (the checklist PUTs before the prompt), so
     cancelling unticks it server-side and rolls the row back in state. The
     C68a carve-out accepts the {done:false} patch from the delegatee too. */
  const cancelSubtaskDone = () => {
    const a = subtaskDoneAsk;
    setSubtaskDoneAsk(null);
    /* Round 17 (C102d) — Cancel restores the held timer to its prior
       state (resume only if it was running when the prompt opened); the
       untick below stays subtask-variant-only. */
    if (a && a._timerHold) releaseEntityTimer(a._timerHold, "resume");
    if (!a || !a.subtask || !a.subtask.id) return;
    updateSubtask(a.subtask.id, { done: false })
      .then(() => {
        setTasks((prev: any) =>
          prev.map((x: any) =>
            a.task && x.id === a.task.id
              ? {
                  ...x,
                  subtasks: (x.subtasks || []).map((s2: any) =>
                    s2.id === a.subtask.id ? { ...s2, done: false } : s2,
                  ),
                }
              : x,
          ),
        );
      })
      .catch((e) => {
        toast(
          t("app.api_op_failed", {
            op: "update",
            msg: e.message || String(e),
            fallback: "API {op} failed: {msg}",
          }),
          "error",
        );
      });
  };
  const delTk = (taskId: any) => {
    persist(
      SK.tk,
      tasks.filter((x: any) => x.id !== taskId),
      setTasks,
    );
    toast(t("toast.deleted", { fallback: "Deleted" }));
  };
  /* B.6 Phase B — nav() untangled: a ROUTER step (record the leave-context,
     switch section/detail) followed by the named RESET SHAPE below — same
     resets, same order, same keys as the pre-refactor closure body, proven
     by replaying the 464-transition characterization recording
     (/tmp/b6/nav_recording.json) against this implementation. */
  // Reset every per-section transient the six regions read (search box,
  // delete-confirm chrome, CaseView client editors + display toggles).
  const navResetTransients = () => {
    setQuery("");
    setDocsQ(""); // 195 §C.3b — the Documents box, reset with the rest
    setConfirmDelete(null);
    // 190 round 6 [Z] — the two lists leave their Bento on every navigation
    setCaMode("list");
    setCcMode("list");
    sExpCl(false);
    sClSrch("");
    sShowEarlier(false);
    setCollStg({});
    sShowDesc(false);
    setCaTesel(new Set());
    setPartiesOpen({});
  };
  // Sidebar case-chat rail: opens on entering a case detail, closes
  // otherwise; the draft clears on every transition.
  const navSyncCaseChat = (x: any, id: any) => {
    if (x !== "ca" || !id) {
      setSideCaseChat(false);
      setCaseChatInput2("");
    } else {
      setSideCaseChat(true);
      setCaseChatInput2("");
    }
  };
  // Sidebar persons-group chrome: entering a persons subsection opens its
  // group (and the Persons cluster); any other target closes the subgroups.
  const navSyncPersonsGroups = (x: any) => {
    setNavGrp((prev: any) => {
      const _pg: Record<string, any> = {
        co2int: "collabs",
        co2ext: "collabs",
        cl: "clients",
        cp: "clients",
        op: "opponents",
        ocu: "opponents",
        oc: "opponents",
        ex: "third",
        me: "third",
        ar: "third",
        op2: "third",
      };
      const pg = _pg[x];
      return pg
        ? {
            ...prev,
            persons: true,
            collabs: false,
            clients: false,
            opponents: false,
            third: false,
            [pg]: true,
          }
        : { ...prev, clients: false, opponents: false, third: false };
    });
  };
  // Entering the general chat stamps the local read time and pushes the
  // server receipt.
  const navMarkGchatRead = (x: any) => {
    if (x === "gchat" && currentUser) {
      const nk = "gchat:" + currentUser;
      const ts = Date.now();
      setChatRead((p: any) => ({ ...p, [nk]: ts }));
      upsertChatReadReceipt({
        caseId: null,
        lastReadAt: new Date(ts).toISOString(),
      }).catch((e) => _apiErr("chat read", e));
    }
  };
  /* 206 §7 — `id` WIDENED, and it is a correction rather than a convenience.
     `id = null` made TypeScript infer the parameter's type as exactly `null`,
     which is wrong at every call site that passes one (`nav("ca", c.id)`); it
     went unnoticed because every caller reaches it through an `any`, so the
     checker had nothing to compare. The moment §7's bell arm typed its
     payload, three honest calls became type ERRORS against a signature that
     was the thing at fault. Widening a primitive to the union its call sites
     actually pass is THE FOUR RULES #2 and #4; it is strictly more permissive,
     so no existing caller can break. */
  /* 207C §4 [RULED 2026-09-24, ruling 8] — A SECTION OPENS AT ITS TOP, its
     header in sight. The app's one scroll pane, `.content`, kept the offset the
     previous screen was left at: nothing in src/ ever set it back. Now each new
     screen sets it as it is laid out, by ONE decision (src/lib/paneScroll.ts
     `paneOnArrival`): a section opened, and a record opened, at the top; back
     from a record to its list, the list where it was left. The pane's offset is
     followed as it scrolls (a ref — no render), and `nav()` says when a move
     was a section being opened. */
  const contentRef = useRef<HTMLDivElement | null>(null);
  const paneTopRef = useRef(0);
  const paneKeptRef = useRef<Record<string, number>>({});
  const paneByNavRef = useRef(false);
  const paneScreenRef = useRef<PaneScreen>({ section: String(section), detailId: detailId as unknown });
  const nav = (x: any, id: number | string | null = null) => {
    // P21-zero — a surface this firm's product does not have is not somewhere
    // you can go. Every LINK to one is gone once the four render sites share
    // the predicate, so this is the backstop for the paths that are not links:
    // a deep link, a restored section, a jump from code. It lands on the
    // dashboard rather than refusing, because there is nothing to explain —
    // for this firm the section does not exist.
    if (_appPerms.hiddenSurface(x)) {
      x = "dash";
      id = null;
    }
    // 207C §4 — a section opened (the sidebar, a jump): only when the screen moves.
    paneByNavRef.current = x !== section || id !== detailId;
    if ((section === "ca" || section === "cc") && detailId)
      setLastCaseCtx({ type: section, id: detailId });
    setSection(x);
    setDetailId(id);
    navResetTransients();
    navSyncCaseChat(x, id);
    navSyncPersonsGroups(x);
    setListPage({});
    navMarkGchatRead(x);
  };
  /* 207C §4 — the pane on arrival: runs as the new screen is laid out, before
     the moves that scroll to a row (they wait 200 ms and retry — src/utils.ts),
     so those still land on their row. */
  useLayoutEffect(() => {
    const prev = paneScreenRef.current;
    const next: PaneScreen = { section: String(section), detailId: detailId as unknown };
    paneScreenRef.current = next;
    const byNav = paneByNavRef.current;
    paneByNavRef.current = false;
    const el = contentRef.current;
    if (!el || (prev.section === next.section && prev.detailId === next.detailId)) return;
    const move = paneOnArrival(prev, next, byNav, paneTopRef.current, paneKeptRef.current);
    if (move.keep) paneKeptRef.current = { ...paneKeptRef.current, [move.keep[0]]: move.keep[1] };
    el.scrollTop = move.top;
    paneTopRef.current = el.scrollTop;
  }, [section, detailId]);
  /* Round 14 (C85) — CASE-JUMP GUARD for tap-through navigation (task/event
     title clicks, Eisenhower jumps, matrix task clicks — pop-out and main
     window alike): assignment/delegation can put a row in a viewer's lists
     whose case sits OUTSIDE his server-scoped ca/cc — jumping there used to
     land on a blank CV. The guard checks the viewer's ACTUAL case/cc
     visibility (his loaded lists) and replaces the dead jump with a toast.
     Returns whether navigation ran, so callers skip scroll-queueing on
     refusal. */
  const tryNavCase = (kind: any, id: any) => {
    const ok =
      kind === "ca" ? cases.some((c: any) => c.id === id) : collectiveCases.some((c: any) => c.id === id);
    if (!ok) {
      toast(
        t("nav.case_no_access", {
          fallback:
            "You don't have access to this casefile — ask the Responsible Partner",
        }),
        "warning",
      );
      return false;
    }
    nav(kind, id);
    return true;
  };
  /* Brief 42 §2 — clicking an alert row in the bell landed on the case but
     left the item unmarked, so the user arrived at a page and still had to
     hunt for the thing the alert was about.
     THE HIGHLIGHT MECHANISM ALREADY EXISTED AND IS REUSED, NOT REINVENTED
     (§2 asked for exactly that, and to stop if none existed). `queueScrollToEv`
     / `queueScrollToTk` (src/utils.ts:29,74) park an id in localStorage; the
     destination view consumes it on arrival, scrolls the row into view and
     adds the `.evHl` class. It is the same glow every other path into CV/CCV
     uses — PlacesView's `openLinkedEvent`, the Dashboard rows, AgendaView, and
     the derived task notifications a few hundred lines below this file's
     persisted branch.
     The payload names its own target: the engine sets `entity` ('agenda' |
     'task') and `entityId` on every fire it writes
     (server/src/lib/alertEngine.js). So this asks the row what it is about
     rather than knowing the type — the same shape the general arm was built
     on in stage 3.
     C85 CONTRACT, and it is the reason the queue sits after the nav rather
     than beside it: the id is parked ONLY when navigation actually ran. A row
     naming a case the viewer cannot open must leave nothing behind, or the
     parked id fires on some unrelated later arrival. */
  const navAlertPayload = (p: AlertPayload) => {
    const kind = p.caseId != null ? "ca" : p.ccId != null ? "cc" : null;
    if (!kind) return;
    if (!tryNavCase(kind, kind === "ca" ? p.caseId : p.ccId)) return;
    if (p.entityId == null) return;
    if (p.entity === "task") queueScrollToTk(p.entityId);
    else if (p.entity === "agenda") queueScrollToEv(p.entityId);
  };
  /* Brief 42 §3 — the DERIVED agenda notifications (the "ev-" upcoming-event
     source and the "agr-" reminder source) opened the event's EDITING MODAL
     when clicked. They now show the item in context in CV/CCV, highlighted,
     like every other row in this bell.
     DATED, NOT JUST REPAIRED (§3 required this, and the answer overturns the
     brief's framing): it is NOT a regression and #277 is cleared. Both call
     sites were born with `om("ag", a)` — "ev-" at 8a79db5 and "agr-" at
     0613e68, both 2026-04-07 — and a walk of every commit that has touched
     this file since finds exactly three changes to either line, all three
     cosmetic: #51 reformatted the minified source, #120 renamed `om` to
     `openModal`. The semantics never moved. See the PR body.
     The shape is the app's existing single-event mechanism, taken from
     PlacesView's `openLinkedEvent` (src/views/PlacesView.tsx:1794) rather
     than invented here: navigate to the owning case / collective case, queue
     the scroll-to-highlight, and fall back to the global agenda for a
     firm-wide event that belongs to no case.
     `caId` OR `caseId`, because an agenda event carries both spellings in
     this tree depending on where it came from — the same defensive read
     CompactColumn's `navEvent` makes (src/views/CompactColumn.tsx:214), and
     `caId` is what the `ref` lookup beside both call sites already reads.
     C85 CONTRACT: the highlight is queued only when navigation actually ran. */
  const navAgendaEvent = (a: AgendaEventLike) => {
    const cid = a.caId != null ? a.caId : a.caseId;
    if (cid != null) {
      if (tryNavCase("ca", cid) && a.id != null) queueScrollToEv(a.id);
    } else if (a.ccId != null) {
      if (tryNavCase("cc", a.ccId) && a.id != null) queueScrollToEv(a.id);
    } else {
      if (a.id != null) queueScrollToEv(a.id);
      nav("ag");
    }
  };
  const uqf = (k: any, v: any) => setQuickAddForm((p: any) => ({ ...p, [k]: v }));
  /* 207 §13 — the quick add opens on a fresh form (`cqm` cleared it) and every
     kind it adds shows the address block, so it starts with the firm's country
     too; closing is unchanged. */
  const openQuickAdd = (m: unknown) => {
    if (m) setQuickAddForm(firmCountryNow ? { country: firmCountryNow } : {});
    setQuickAddModal(m);
  };
  const cqm = () => {
    setQuickAddModal(null);
    setQuickAddForm({});
  };
  // M+3.7.2 §H — await the POST and use the server-assigned id before
  // triggering the case PUT. The previous fire-and-forget shape raced the
  // case PUT past the entity create; the case junction createMany then
  // 500'd on a contactPersonId (etc.) that hadn't been inserted yet.
  /* 214 §2 [RULED 51] — `partyIds`: a counsel added from a matter stands for the parties ticked in the quick add;
     they are seated on the LINK (the case's `ocuLinks`, a collective case's element), never on the person's row. */
  const saveQuickAddParty = async (sk: any, arr: any, fn: any, d: any, ext: any, retKey: any, partyIds: readonly number[] = []) => {
    if (!d.lastName && !d.companyName && !d.name) {
      toast(t("toast.name_required", { fallback: "⚠ Name is required" }));
      return;
    }
    const ent = _apiEnt[sk];
    const payload = { ...d, ...ext };
    delete payload.id;
    let saved;
    try {
      saved = ent ? await ent.c(payload) : { ...payload, id: nid(arr) };
      if (!saved || saved.id == null) throw new Error("create returned no id");
    } catch (e: any) {
      toast(
        "⚠ " +
          (e.message || t("toast.add_failed", { fallback: "Add failed" })),
      );
      return;
    }
    const newId: unknown = (saved as { id?: unknown }).id; // 214 §2 — read once, typed
    fn((prev: any) => appendSavedRow(prev, saved));
    if (quickAddModal.caseId) {
      const _caseObjCols = ["clIds", "opIds", "ocuIds", "op2Ids"];
      persist(
        SK.ca,
        cases.map((c: any) => {
          if (c.id !== quickAddModal.caseId) return c;
          const upd = { ...c, [retKey]: [...(c[retKey] || []), saved.id] };
          if (_caseObjCols.includes(retKey)) {
            const _rk = retKey.replace(/Ids$/, "Roles");
            upd[_rk] = { ...(c[_rk] || {}), [saved.id]: [] };
          }
          return withNewCounselLink(upd, retKey, newId, partyIds); // 214 §2 — the ticked parties on the new link
        }),
        setCases,
      );
    } else if (quickAddModal.ccId) {
      const _objCols = ["opIds", "ocuIds", "op2Ids"];
      const _add = _objCols.includes(retKey)
        ? newCcElement(newId, retKey, partyIds) // 214 §2 — the ticked parties on the new element
        : saved.id;
      persist(
        SK.cc,
        collectiveCases.map((c: any) =>
          c.id === quickAddModal.ccId
            ? { ...c, [retKey]: [...(c[retKey] || []), _add] }
            : c,
        ),
        setCollectiveCases,
      );
    } else {
      setForm((p: any) => ({ ...p, [retKey]: [...(p[retKey] || []), saved.id] }));
    }
    cqm();
    toast(t("toast.added", { fallback: "Added" }));
  };
  const getRecentCases = (userId: any) => {
    try {
      const raw = localStorage.getItem("lx:recentCases:" + userId);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  };
  const addRecentCase = (userId: any, caseId: any) => {
    if (!userId || !caseId) return;
    try {
      const prev = getRecentCases(userId);
      const next = [+caseId, ...prev.filter((id: any) => id !== +caseId)].slice(
        0,
        10,
      );
      localStorage.setItem("lx:recentCases:" + userId, JSON.stringify(next));
    } catch {}
  };
  /* ── BRIEF 05 STEP 5/6 (via brief 124 §5) — THE EMAIL DROP ────────────────
     ARMED exactly when a matter is open, which is the layer's whole gate: the
     brief's `(section === "ca" || section === "cc") && detailId`, verbatim.
     Disarmed the layer does not prevent `dragover` at all, so the Eisenhower
     drag is untouched — glance check 1, and the one the brief calls most
     important.

     The PREFILL KEYS live HERE, next to `openModal`, because the arms they
     have to match are here. Every one was verified present against the tree
     before a line of this was written (124 §5: a key that no longer exists
     after 122 and 123 is a STOP, not a guess). */
  const _dropKind = section === "cc" ? "cc" : "ca";
  /* ── BRIEF 126 §10 — DRAG AND DROP WORKS ON EVERY SCREEN ──────────────────
     [RULED] Laurent: "drag and drop should work across every screen of the app;
     the only difference when dropping in a case is the case prefill —
     otherwise not prefilled, one needs to choose."

     WHAT WAS ACTUALLY CASE-SCOPED, measured, because the brief tagged this
     [UNVERIFIED]: the LAYER was never the problem. `EmailDropLayer` has mounted
     ONCE on the app shell since brief 05 step 5, and it READS NOTHING OFF ITS
     CONTAINER — it listens on `window`, and its props are `armed` plus four
     callbacks. The matter reached it through exactly two seams:
       (a) `armed`, which was `(section === "ca" || section === "cc") && detailId`;
       (b) the two resolver callbacks' closure over `(_dropKind, detailId)`,
           which chose a MATTER-SCOPED ROUTE — and that was the real fence, not
           the layer. Outside a matter there was nowhere to send the email.
     Nothing else. No container, no section, no lock, no selection.

     So (a) becomes always-armed and (b) gains a third destination:
     `/api/emails/resolve-drop` (server/src/routes/emailDrop.js). The overlay's
     own sentence is the third thing the matter reached, and it now has two.

     `_dropMatterId` REPLACES the bare `detailId` at every drop site. It is null
     off a matter, and that null is what makes the prefills empty and the route
     matterless — one value, read in four places, rather than four places each
     re-deciding whether there is a matter. */
  const _dropMatterId =
    (section === "ca" || section === "cc") && detailId ? detailId : null;
  const _dropArmed = true;
  /* ── BRIEF 125 §5 — THE TIME-ENTRY CHOICE TAKES THE BILLING GATE ──────────
     Laurent: "the timelog option should only be available for users who can
     bulk-timelog emails in the Emails section of CV/CCV — same word count
     method, same gate, one follows the other."

     THE KEY IS DERIVED, NOT CHOSEN. `CaseEmailsSection.tsx:152` computes the
     gate on the existing bulk email billing as
     `can("caseView.emails.billing", kind === "cc" ? { ccId } : { caseId })`,
     and this is that call with this screen's matter. Same key, same scope
     shape, same permission machinery.

     WHAT IT LOOKS LIKE WITHOUT IT: two choices, not a disabled third (§5's own
     words). The disabled state is reserved for the OTHER question — "we could
     not compute a number" — which is a different sentence and already has one.

     AND A MEASUREMENT THAT MAKES THIS GATE A BACKSTOP RATHER THAN THE DOOR:
     the route that PRODUCES the descriptor is already behind the same key —
     `caseEmails.js:273` and `collectiveCaseEmails.js:231` both mount
     `/resolve-drop` under `requireSection('caseView.emails.billing')`. So a
     user who fails this gate cannot resolve a dropped email at all today and
     never reaches the chooser. The client gate is still the right thing and is
     still built: it is defence in depth, it is where the answer belongs if that
     route's gate is ever relaxed (a separate, security-relevant ruling), and it
     is what makes Laurent's sentence true of the SCREEN rather than only of the
     wire. Reported in the PR rather than discovered later.

     ── BRIEF 126 §10 — AND WHICH MATTER IT IS ASKED AGAINST WHEN THERE IS NONE

     §10 asks the question outright and says to STOP rather than invent a global
     permission. MEASURED, and it is not a stop, because BOTH PLANES ALREADY
     DEFINE THE UNSCOPED ASK AND ANSWER IT THE SAME WAY:

       client  `clientCan` (hooks/usePermissions.ts:70-103) — hidden → false;
               admin → true; the per-case grant arm is entered only
               `if (caseId != null)` / `if (ccId != null)`; otherwise
               `return data.defaults?.[key] === true`.
       server  `canActOnCase` (lib/permissions.js:152-167) — the same three
               steps, and its `caseInScope` term is guarded by
               `(caseId != null || ccId != null)`, so it too falls to the
               viewer's role default.

     THE ROLE DEFAULT IS NOT A NEW PERMISSION. A per-case grant is by
     construction an EXCEPTION attached to a case; with no case there is no
     exception to apply, and what is left is the base the exception was
     overriding. It is strictly no wider than the in-matter answer for a viewer
     holding no grants — and NARROWER for one who holds an ALLOW grant on case
     X: three choices there, two here. That is correct. They may bill on X, not
     in general.

     The tree even has the NAME for it: `requireSection(key, { scope: 'lateral' })`,
     "firm-global — no case scoping" (lib/permissions.js:251), which is what the
     matterless route carries. Same key as the twins. Nothing widened. */
  const _dropScope = _dropMatterId
    ? _dropKind === "cc"
      ? { ccId: _dropMatterId }
      : { caseId: _dropMatterId }
    : {};
  const _dropCanBill = !!_appPerms?.can?.("caseView.emails.billing", _dropScope);
  /* 129 §2/§5 — THE DOCUMENT SHAPE'S FOURTH CHOICE TAKES ITS OWN KEY, and the
     key is READ OFF THE ROUTE that would refuse it rather than chosen:
     `documentBilling.js:473` mounts `/bill` behind
     `requireSection('caseView.documents.billing', { scope })`. It is NOT the
     email key — a firm may let somebody bill correspondence and not documents,
     and the two sections are separate rows in `sectionRegistry.js`. Same
     doctrine as 125 §5: without it the chooser offers three, not a disabled
     fourth.

     UNLIKE the email gate, this one is NOT a backstop: nothing upstream of the
     chooser asks it. A document drop reaches the chooser through the classifier
     and the upload route, whose gate is `caseView.documents.edit` (§3) — so
     `documents.billing` is asked HERE for the first time, and the server asks it
     again at `/bill`. */
  const _dropCanBillDocs = !!_appPerms?.can?.("caseView.documents.billing", _dropScope);
  /* 129 §3 — the key `matterFiles.js:152` mounts the upload behind. Every one
     of the four choices begins with that upload, so this decides whether the
     document chooser may open AT ALL. */
  const _dropCanFile = !!_appPerms?.can?.("caseView.documents.edit", _dropScope);
  /* ── BRIEF 129 §1/§2 — ONE STATE, A DISCRIMINATED UNION ───────────────────
     §2 asks the question the 127 verdict left open: a second dropped SHAPE
     needs either a discriminated union in this state or a second state beside
     it. **One state, and the union is why.**

     The invariant is "at most one chooser is open", and a single `null`-able
     state ENFORCES that where two states can only be relied upon to maintain
     it: `setDropSubject(x)` is total, while `setEmailDrop(x); setDocDrop(null)`
     is a pair of calls with an illegal state between them and a third caller
     who forgets the second half. Everything downstream is the same shape too —
     one open, one close (`onClose`), one `_dropChoose` that reads the subject
     and clears it — so two states would fork three call sites to vary one
     field.

     THE STATE'S SECOND ARM ARRIVES IN §1 rather than in §2, because §1 is what
     makes two kinds exist: the moment `EmailDropLayer` can hand back a
     document, something has to hold it. §2 is where the CHOOSER grows its
     second shape; this is where the app grows somewhere to put it.

     The document arm holds the FILE ITSELF, not a descriptor. There is nothing
     to resolve — an email needs a server round trip to become a subject, a
     document is already one — and the bytes are needed later anyway, by the
     upload (§3) and by its numbered-version retry (127 §2). Re-asking the OS
     for them would be a second file dialog for a question already answered.

     ── BRIEF 130 §1 — AND THIS IS WHERE THE BYTES ARE HELD ──────────────────

     §1's fence: "holds the bytes until the matter is known. No upload to an
     `:id` that does not exist." They are held HERE, in the state that already
     held them — a `File` handle in React state, never on disk, never on the
     wire. Nothing is uploaded, so there is nothing to clean up: closing the
     chooser is `setDropSubject(null)` and the handle is garbage. A cancelled
     drop files nothing and leaves nothing, which is the same outcome the
     cancel has had since 129 §2.

     `choice` is the SECOND FIELD of the document arm and it is what makes one
     state enough for a two-step dialog: `null` means "the four choices are on
     screen", set means "the act is chosen and the matter is the question". It
     is on the union rather than beside it for 129 §2's own reason — a second
     state is a pair of calls with an illegal state between them, and here the
     illegal state is a matter question with no file behind it. */
  type DropSubject =
    /* 184 B.4 — `emlFile`: the dropped `.eml` itself, held as the document arm
       holds its File; it leaves this state only through `_dropEml` on the
       task/event arms of `_dropChoose`. */
    | { kind: "email"; email: EmailDropDescriptor; emlFile?: File | null }
    | {
        /* 216 §5 [RULED 74] — EVERY document of the drop, through one chooser */
        kind: "document";
        files: File[];
        choice: DocumentDropChoice | null;
      };
  /* 130 §1 — THE MATTER A DOCUMENT DROP IS AIMED AT, as a value rather than as
     two closures. On a matter it is `(_dropKind, _dropMatterId)` and nothing
     has changed; off one it is what the chooser's second step answered. It is
     threaded through the upload, the conflict retry and the after-filing arms
     rather than read from the closure at each, because the closure is the one
     thing that is right on a matter and wrong off it. */
  type DropTarget = { kind: MatterKind; id: number };
  const [dropSubject, setDropSubject] = useState<DropSubject | null>(null);
  const _dropEmail = dropSubject?.kind === "email" ? dropSubject.email : null;
  /* 216 §5 [RULED 74; ADVISOR] — ONE SUBJECT ON SCREEN, THE REST IN LINE. A drop of several files makes several
     subjects — the documents together (one chooser), then each e-mail — and the chooser holds one. What arrives
     while one is open waits here and opens when it closes, in the order it arrived: the documents first. */
  const _dropQueue = useRef<DropSubject[]>([]);
  const _dropOpenRef = useRef<DropSubject | null>(null);
  _dropOpenRef.current = dropSubject;
  const _dropOpen = useCallback((s: DropSubject) => {
    if (_dropOpenRef.current) _dropQueue.current.push(s);
    else setDropSubject(s);
  }, []);
  /* … and held while the documents are being filed (the window, the uploads, the conflict dialog) and while what
     they opened is still on screen (the task or event form, the billing window): the e-mails' choosers open once
     the documents are DONE, not the moment their choice is made. */
  const [dropBusy, setDropBusy] = useState(false);
  // (the line's effect is declared below `docBill`, which it reads — see « THE LINE OPENS » there)

  /* 126 §9 — THE M365 ANSWER IS SETTLED BEFORE THE CHOOSER OPENS, and that is
     why it is awaited here rather than asked later. `_dropDocs` runs
     SYNCHRONOUSLY inside `_dropChoose`, so it can only read a cached answer;
     resolving the drop is the moment that answer becomes relevant, and it is
     already a round trip. `m365Connected()` is cached for the session, so this
     costs one request per session and rides in parallel with the resolve. */
  /* 209 §5 [RULED 18] — A LANGUAGE REFUSAL IS AN ANSWER, NOT A FAILURE. The
     matter has no language, and the refusal's toast says so with the case form
     one click away (`_apiErr`); the layer then reports the resolve as failed,
     and its « could not be read » would cover that toast at once. So the
     resolve notes that it has already spoken, and `_dropFailed` stays quiet
     for that one drop. */
  const _dropTold = useRef(false);
  const _dropResolveFile = useCallback(
    async (file: File) => {
      try {
        const [d] = await Promise.all([
          resolveDroppedEmailFile(_dropKind, _dropMatterId, file),
          m365Connected(),
        ]);
        return d as EmailDropDescriptor;
      } catch (e) {
        _dropTold.current = (e as { code?: unknown } | null)?.code === "case.language_required";
        _apiErr("email drop", e);
        return null;
      }
    },
    [_dropKind, _dropMatterId],
  );
  const _dropResolveId = useCallback(
    async (id: string) => {
      try {
        const [d] = await Promise.all([
          resolveDroppedEmailById(_dropKind, _dropMatterId, id),
          m365Connected(),
        ]);
        return d as EmailDropDescriptor;
      } catch (e) {
        _dropTold.current = (e as { code?: unknown } | null)?.code === "case.language_required";
        _apiErr("email drop", e);
        return null;
      }
    },
    [_dropKind, _dropMatterId],
  );
  /* THE SUBJECT-ONLY BRANCH. An OWA list row carries EWS ids and no translator
     exists in this tree, so there is nothing to resolve: the chooser opens on
     a descriptor whose `autoMinutes` is null, which is the single field that
     disables the time-entry option and makes it say why. The number is NOT
     invented — an invented minute count is indistinguishable, on the screen and
     in the invoice, from one the billing library computed. */
  const _dropSubjectOnly = useCallback((subject: string, moreRows: number) => {
    // 216 §5 [ADVISOR] — an Outlook-on-the-web drag of several rows: only the first can be read
    if (moreRows > 0) {
      toast(t("emailDrop.owa_first_only", { fallback: "Only the first message can be read from Outlook on the web; drop the others one by one." }), "warning");
    }
    _dropOpen({
      kind: "email",
      email: {
        internetMessageId: null, subject, fromAddress: null, fromName: null,
        sentDate: null, webLink: null, wordCount: null, autoMinutes: null,
        activityType: null, description: null, alreadyBilled: false,
      },
    });
  }, [_dropOpen, t]); // 216 §5 — the sentence reads the live dictionary (a stale closure said it in English)
  const _dropResolved = useCallback((email: EmailDropDescriptor, file?: File) => {
    _dropOpen({ kind: "email", email, emlFile: file ?? null });
  }, [_dropOpen]);
  const _dropFailed = useCallback(() => {
    if (_dropTold.current) {
      _dropTold.current = false; // 209 §5 — the refusal has spoken (above)
      return;
    }
    toast(t("emailDrop.resolve_failed"), "error");
  }, [t]);
  /* ── BRIEF 129 §1 — THE FILE WAS NOT AN EMAIL, AND WHAT EACH ANSWER GETS ───
     The layer classified before it resolved (its own branch (1)); this decides
     what the two non-email answers deserve. Both are SENTENCES, because §7's
     rule is that a lawyer reads them while holding a file.

     `unknown` REFUSES, and it is deliberately narrow — `lib/dropFileKind.ts`
     has exactly two members in it, an empty file and an OLE2 container that
     would not name itself, and each gets its own reason. It is never the
     residue: a `.txt`, a `.csv` and a photograph are all documents, and the
     classifier says so.

     ── BRIEF 130 §1 — AND THE SECOND REFUSAL IS GONE ────────────────────────

     [RULED] Laurent, 24 Aug 2026, REVERSING his own earlier ruling (127 §3,
     129 §3): "refusal — it should not be: choice of 4 actions should be there,
     only to force the choice of a case IN THE MODAL (one must be able eg to be
     on dashboard, and empty an inbox with files for different cases from
     there, without back and forth to the app: works like that for email drag
     and drop)."

     So `document` off a matter no longer refuses — it ASKS. The chooser opens
     wherever the file was dropped, with all four choices, and the matter is
     the question the flow puts BEFORE the folder picker rather than the reason
     the flow never started. `docDrop.matterless` keeps its seeded rows (a
     translation row is never deleted) and loses its only consumer, exactly as
     `emailDrop.notes.email_id` did in 125 §4.

     THE PERMISSION REFUSAL STAYS, and off a matter it is the ROLE DEFAULT —
     `_dropScope` is `{}` there, which `clientCan` answers from `defaults[key]`
     (126 §10's ruling). The per-case answer is asked again the moment a matter
     IS known, in `_dropDocMatter`. */
  const _dropDocuments = useCallback(
    (files: File[], unknown: { file: File; verdict: DropFileVerdict }[]) => {
      /* 216 §5 [ADVISOR] — the files that are neither (empty, or unclassified) are NAMED, in one sentence; the
         others are filed. A drop of one such file alone keeps 129 §1's two reasons. */
      if (unknown.length === 1 && files.length === 0) {
        const verdict = unknown[0].verdict;
        toast(
          verdict.kind === "unknown" && verdict.why === "empty"
            ? t("docDrop.empty", { fallback: "That file is empty — there is nothing to file." })
            : t("docDrop.unclassified", {
                fallback:
                  "This file does not say whether it is an e-mail or a document, so it was not filed. Save it from Outlook and drop it again, or add it from the matter's Documents.",
              }),
          "error",
        );
        return;
      }
      if (unknown.length) {
        toast(
          t("docDrop.not_filed_named", {
            names: unknown.map((u) => u.file.name).join(", "),
            fallback: `Not filed — ${unknown.map((u) => u.file.name).join(", ")}: an empty file, or one that does not say what it is.`,
          }),
          "warning",
        );
      }
      if (!files.length) return;
      /* 129 §3 — AND THE SAME RULE FOR THE PERMISSION, because it is the same
         rule: a chooser that cannot complete must not open. All four choices
         begin by uploading, and `matterFiles.js:152` mounts that upload behind
         `requireSection('caseView.documents.edit')` — so without it every
         button in the dialog would 403. Read off the route that refuses, not
         chosen; and REFUSED here rather than degraded, because §3's fence is
         "it refuses; it does not degrade". */
      if (!_dropCanFile) {
        toast(
          t("docDrop.not_allowed", {
            fallback: "You are not allowed to add documents to this matter.",
          }),
          "error",
        );
        return;
      }
      _dropOpen({ kind: "document", files, choice: null });
    },
    [_dropCanFile, _dropOpen, t],
  );

  /* THE NOTES BLOCK, both (i) and (ii), ruled by Laurent and generated through
     `t()`. Labels are keys; the values are data. A line whose value is absent
     is OMITTED rather than printed as an empty label.

     BRIEF 125 §4(2) — THE ID CAME OUT OF HERE. It read
     `emailDrop.notes.email_id: <...@host>` and Laurent's objection is exact:
     "just the mail ID in Notes, which doesn't belong there since it's
     unusable". Notes is prose a human reads; a Message-ID is an identifier a
     machine resolves, and putting it here made it neither. It is a COLUMN now
     — `Task.emailGraphId` / `AgendaEvent.emailGraphId`, beside the one
     `TimeEntry` has always had — and `OpenEmailButton` is what turns it back
     into something clickable.

     EVERY OTHER LINE STAYS, which is the other half of the ruling: date,
     sender and subject are for a human and belong in prose.
     `emailDrop.notes.email_id` keeps its seeded rows (nothing is deleted from
     a translation table) and loses its only consumer.

     ── BRIEF 126 §8 — AND THE WORD COUNT GOES THE SAME WAY ──────────────────

     [RULED] Laurent: "do not show word count in Notes from tasks and Events,
     that's only for time entries." The count exists to compute billable
     minutes; on a task or an event it is a number with no consumer, sitting in
     prose a human reads.

     MEASURED, AND THE BRIEF'S "ALL THREE" IS TWO: `_dropNotes` has exactly two
     call sites, the `tk` arm and the `ag` arm. THE TIME ENTRY NEVER GOT A NOTE
     AT ALL — its arm passes `desc` (the composed "correspondent – subject")
     and `min: d.autoMinutes`. So "keep it for the time entry" needed nothing
     kept: the count is already there, in the only form it was ever for, as the
     minutes `blocksAndMinutes(wordCount, …)` computed from it server-side.

     WHAT IS NOT TOUCHED, said so it is not read as an oversight: the CHOOSER
     still shows "Words: N" above the three buttons (EmailDropChooser.tsx:148).
     That is not a Note — it is the summary of the email being placed, and it
     is what the time-entry option's minutes are derived from. Laurent's words
     name Notes, and this § does not widen past them.

     `emailDrop.notes.words` keeps its seeded rows and loses its only consumer,
     exactly as `emailDrop.notes.email_id` did in 125 §4. */
  const _dropNotes = (d: EmailDropDescriptor) => {
    const ddmmyyyy = d.sentDate
      ? (() => { const x = new Date(d.sentDate as string);
                 return Number.isNaN(x.getTime()) ? null
                   : `${String(x.getDate()).padStart(2, "0")}${String(x.getMonth() + 1).padStart(2, "0")}${x.getFullYear()}`; })()
      : null;
    return [
      ddmmyyyy ? t("emailDrop.notes.of", { date: ddmmyyyy }) : null,
      d.fromName || d.fromAddress
        ? `${t("emailDrop.notes.sent_by")}: ${d.fromName || d.fromAddress}` : null,
      d.subject ? `${t("emailDrop.notes.subject")}: ${d.subject}` : null,
    ].filter(Boolean).join("\n");
  };
  /* THE DOCUMENT LINK, shared by (i) and (ii), as a PRE-SAVE ROW.
     `TaskDoc` and `AgendaEventDoc` are both `{ label, url }`; `TaskDocsPane`
     already holds pre-save rows locally and both modals already wire
     `onLocalChange={(arr) => updateField("docs", arr)}`; the task create
     flushes them and — since brief 05 step 1, already in the tree — so does the
     event create.

     ── BRIEF 126 §9 — THE ROW IS ADDED ALWAYS ───────────────────────────────

     [RULED] Laurent: "the email link should just be added as a document,
     triggering an icon and as such appear when the drawer is opened." So this
     becomes the ONLY affordance and 125's bespoke button retires.

     It used to be OMITTED ENTIRELY when `webLink` was null — which is exactly
     the case Laurent hit, and is why 125 grew a button beside the pane. Now:

       webLink known    the link, as since brief 05. Nothing to resolve.
       no link, id      `message:<id>` — the Message-ID, which is what 125 §4
                        ruled is the durable half ("a webLink rots, a
                        Message-ID does not"), and is the STRING THE DROP LAYER
                        ITSELF PARSED off `text/uri-list` on the way in. Opened
                        through `lib/emailLink.ts`, which resolves on demand
                        and carries every one of 125's refusal messages.
       neither          no row. The subject-only OWA branch has no id this tree
                        can translate, so there is nothing a row could ever
                        open — and a document that cannot be opened is worse
                        than no document.

     `openDocHref` IS UNTOUCHED and still opens `http(s)`/`www.` only; the
     readers ask `isEmailDocUrl()` first. Widening that guard remains a separate,
     security-relevant ruling.

     ── AND §7's RULE LANDS HERE, AT THE ONE MOMENT IT CAN ────────────────────

     §7 removed the affordance when M365 is not connected. Under §9 the
     affordance is this row, and a row is written once and read forever — so
     hiding a stored document on today's connection state would be a document
     that flickers. The honest place is the WRITE: a row that would carry only
     a `message:` URI is not written on a box with no M365 account, because
     nothing could ever resolve it. `m365ConnectedNow()` reads the answer §7
     already cached; `null` (not yet asked) is treated as not-connected here,
     which is the same fail-closed choice §7's button makes. A row written
     while connected keeps working, and says its own named reason if the
     account later goes away. */
  const _dropDocs = (d: EmailDropDescriptor) => {
    const _label = t("emailDrop.doc_label", { fallback: "Email in Outlook" });
    if (d.webLink) return { docs: [{ label: _label, url: d.webLink }] };
    if (d.internetMessageId && m365ConnectedNow() === true)
      return { docs: [{ label: _label, url: emailDocUrl(d.internetMessageId) }] };
    return {};
  };
  /* ── BRIEF 125 §2 — THE MATTER ARRIVES UNDER THE NAME THE MODAL READS ─────
     Laurent: "when I choose New Task it doesn't prefill the case (modifiable),
     unlike New Event where it works."

     MEASURED, AND IT WAS ONE WORD. This answered `{ caId }` for every arm. The
     `ag` arm survives that because `openModal`'s ag branch reads `e?.caId`
     explicitly and the modal's own matter field reads `form.caId`
     (AgendaEventModal.tsx:604-608); the `te` arm survives it because
     TimeEntryModal reads `form.caId` (:106, :273, :402). The `tk` arm takes the
     plain `setForm(e ? normFormDates(e) : {})` — no alias translation anywhere
     on its path — and TaskModal reads `form.caseId` (:212-213 for the field,
     :151 on save). The value arrived under a name that modal never looks at.

     WHICH SPELLING IS CANONICAL FOR A TASK: `caseId`, and it is not a
     preference. It is the column (schema.prisma `model Task`), it is what the
     modal's field binds and what its save sends, and `tasks.js` declares NO
     `caId` alias — so `caseId` is the only spelling that survives the whole
     path. The event and the time entry are `caId`-canonical for the mirror-image
     reason: the ag picker WRITES `caId` (AgendaEventModal.tsx:754) and
     `timeEntries.js:99` declares `caId -> caseId` in its ALIASES map.

     THREE MODALS, THREE NAMES, ONE PLACE THAT KNOWS. That is the shape of the
     cure for the `caId`/`caseId`/`_ci` drift B.4 exists to end: not a fourth
     spelling taught to a modal, but the one caller that opens all three saying
     what each of them calls it.

     THE `cc` SIDE IS `ccId` FOR ALL THREE and needs no target: TaskModal reads
     `form.ccId` (:152) and TimeEntryModal reads it (:106-107, :405). The ag
     modal WAS the exception — a KNOWN, CARRIED drift rather than this §'s: its
     picker wrote `caId` for a collective case too, so a cc-bound drop reached
     the WIRE correctly (`ccId` rides `fdata` through `saveAgendaEvent`) and
     showed a blank matter field. 184 A.1 repaired the picker (it reads
     `caId || ccId` and writes both by kind), so the cc-bound event drop now
     shows its matter as the other two do. */
  /* WRITTEN AS PROPERTY NAMES, NOT AS A MAP OF STRINGS, and the gate is why:
     a `{ tk: "caseId", … }` lookup puts the three field names on the wire as
     STRING LITERALS, which `i18n-total`'s prose-map detector reads as words a
     human might see. They are neither prose nor an identifier some other system
     parses — they are the keys of the object being built, so they are written
     as keys. Same three names, same one place, and nothing to exempt. */
  /* 126 §10 — AND NO MATTER IS A LEGAL ANSWER. Off a matter every arm carries
     NOTHING, so the field opens editable and empty and the user chooses — which
     is already legal on all three: 125 §2 made the task arm's matter editable
     (no `_caseFixed`), the event arm has carried `_caseFixed: false` since brief
     05 step 2, and the time entry's matter has always been a required, editable
     field (`field.case_required`). Returning `{}` rather than a null-valued key
     is deliberate: `openModal`'s ag branches test `e?.caId || e?.ccId`, and a
     present-but-null key would take the matter-bound arm and seed a title off a
     matter that is not there. */
  /* 130 §1 — AND THE MATTER CAN NOW ARRIVE FROM THE DROP ITSELF. `at` is the
     matter the document drop asked for when the screen had none; the email
     drop passes nothing and keeps 126 §10's matterless `{}` exactly. One
     function, because the three prefill spellings are the same three whether
     the matter came from the URL or from a question. */
  const _dropMatter = (target: "tk" | "ag" | "te", at?: DropTarget | null) => {
    const kind = at ? at.kind : _dropKind;
    const id = at ? at.id : _dropMatterId;
    if (!id) return {};
    if (kind === "cc") return { ccId: id };
    return target === "tk" ? { caseId: id } : { caId: id };
  };

  const _dropChoose = (choice: "task" | "event" | "time") => {
    const d = _dropEmail;
    const emlFile = dropSubject?.kind === "email" ? (dropSubject.emlFile ?? null) : null;
    setDropSubject(null);
    if (!d) return;
    const day = d.sentDate ? String(d.sentDate).slice(0, 10) : null;
    /* 184 B.4 [RULED 184 (ii)] — THE E-MAIL ITSELF FOLLOWS THE TASK OR EVENT.
       `_dropEml` carries the dropped File (branch (1)) or the RFC Message-ID
       (the `message:` URI branch) to the save; `chainGo` files it as `.eml`
       in the matter's e-mail folder AFTER the row exists (its second act) and
       links it beside the Outlook link `_dropDocs` seeds. A subject-only drop
       (an OWA list row: no bytes, no id) carries nothing and files nothing.
       NEVER on the time-entry arm — an entry records and bills; it does not
       hold a document. A transient key of the `_timerHold` class: both saves
       strip it before the row and the body. */
    const _dropEml =
      emlFile || d.internetMessageId
        ? { _dropEml: { file: emlFile, internetMessageId: d.internetMessageId ?? null } }
        : {};
    /* BRIEF 125 §4(1) — THE ID, STORED, ON ALL THREE. `egid` is the one word
       every entity now takes: `timeEntries.js:99` has always aliased it,
       `tasks.js`'s `mapTaskIn` and `agenda.js`'s `ALIASES` now do too, and all
       three routes mirror it back on read. Omitted when the descriptor has no
       id — the subject-only branch (an OWA list row, whose EWS ids nothing in
       this tree can translate) genuinely has nothing to store, and writing an
       empty string would make a row claim a provenance it does not have. */
    const _dropEgid = d.internetMessageId ? { egid: d.internetMessageId } : {};
    if (choice === "task") {
      /* NO `_caseFixed` — the matter is filled and EDITABLE (Laurent's ruling).
         The `tk` arm takes the plain `setForm(e ? normFormDates(e) : {})`, so
         omitting the key is all that is needed. */
      openModal("tk", {
        ..._dropMatter("tk"), title: "", notes: _dropNotes(d),
        ..._dropEgid, ..._dropDocs(d), ..._dropEml,
      });
      return;
    }
    if (choice === "event") {
      /* `_caseFixed: false` EXPLICITLY — the `ag` arm forces `true` for every
         other caller, and step 2's opt-out (`e._caseFixed === false ? false :
         true`, App.tsx) is what lets this one out. */
      openModal("ag", {
        ..._dropMatter("ag"), _caseFixed: false, title: "", notes: _dropNotes(d),
        ...(day ? { date: day } : {}), ..._dropEgid, ..._dropDocs(d), ..._dropEml,
      });
      return;
    }
    /* (iii) TIME ENTRY, in the FE aliases `timeEntries.js`'s ALIASES map
       declares. `egid` IS `emailGraphId`, and that alias is the whole reason
       double-billing is impossible: TimeEntry carries
       `@@unique([caseId, emailGraphId, collaboratorId])` and its ccId twin, and
       the route already turns a P2002 into a friendly 409. `alreadyBilled` only
       warns BEFORE the modal opens.

       NOT routed through App's `saveTimeEntry`, which has zero callers and is
       dead; the time modal's own save handles both matter kinds. */
    openModal("te", {
      ..._dropMatter("te"), coId: currentUser, _lockCo: true,
      ...(day ? { date: day } : {}),
      min: d.autoMinutes, desc: d.description, type: d.activityType,
      egid: d.internetMessageId, mark: "unmarked",
    });
  };
  /* ── BRIEF 129 §3 — A: JUST FILE IT ───────────────────────────────────────
     The act every one of Laurent's four choices begins with, written once.
     Three steps and none of them is new machinery:

       1. ASK WHERE. `openOneDrivePicker` (`OneDrivePicker.tsx`) — the 127
          verdict's measured answer to "which of the two browsers is the right
          instrument": it returns a FILE or a FOLDER, it is clamped SERVER-side
          by `caseId`/`ccId` (Phase 23, and the floor is never the client's
          `basePath`), and it is already the imperative `openX() -> Promise`
          this app calls from seven places. `DocumentBillingModal`'s tree is a
          billing browser bound to its own modal's state and is not one.

          AND THE FOLDER IS ASKED FOR, unlike 127 §1's pane button, which files
          into the matter's root without asking. That is not an inconsistency —
          it is that §'s own stated distinction: "§5's drop offers a folder
          browser because FILING is what that flow is for; this button is a
          quick attach inside a task or an event." Filing a lawyer's document is
          the act where the folder IS the decision.

       2. UPLOAD. `POST /api/{cases|collective-cases}/:id/files/upload`, built
          in 127 §1, which already takes `subPath` — so this § needed a folder
          VALUE and not a new endpoint.

       3. THE COLLISION IS ALREADY RULED. 127 §2: never overwrite; cancel, or a
          numbered version. Reused by mounting its dialog, not by re-deciding
          it — `MatterFileConflictDialog` is the same presentation the pane
          shows, and the retry differs from the first attempt in one argument.

     A FILE PICK IS READ AS ITS FOLDER. The picker's footer offers both, and
     "select this file" as a DESTINATION is a mis-click with an obvious meaning:
     the folder the user was looking at when they made it. So the last segment
     comes off. Refusing here would be pedantry about a control this § does not
     own.

     `subPath === null` REFUSES, and that is the one place this is strict: null
     means the browse floor was not knowable, `""` means the matter's root, and
     filing a document in the wrong folder because the two were collapsed is
     exactly the failure a document-management system may not have. */
  /* THE CONFLICT STATE CARRIES THE WHOLE PENDING ACT, not just the bytes: the
     destination the user already chose, and the CHOICE they already made. A
     dialog that only remembered the file would answer "yes, number it" and then
     have to ask for the folder again and forget that this was "file it & bill
     it" — the two questions the user has already answered. */
  /* 216 §6 [RULED 75] — and the ROOT the one window chose: the matter's folder (`matter`), or a linked client's
     (`client:<id>`); the upload and the billing window carry it. */
  type DropPlace = { subPath: string; folderId: string | null; folderName: string | null; root: string };
  /* 129 §5 — the billing browser, opened FROM A DROP. Held as the matter's id
     plus a starting position rather than as an open flag, because there is
     nothing else App needs to know: the modal takes `cs` for its URL base and
     resolves everything else itself. */
  const [docBill, setDocBill] = useState<
    { id: number; kind: string; start: DocumentBillingStart } | null
  >(null);
  /* 216 §5 — THE LINE OPENS: the next subject waiting opens when no chooser is up, the documents are not being
     filed, and what they opened (the task or event form, the billing window) has closed. Declared after `docBill`,
     which it reads. */
  useEffect(() => {
    if (dropSubject !== null || dropBusy || activeModal || docBill) return;
    const next = _dropQueue.current.shift();
    if (!next) return;
    /* The functional form: this effect runs after the commit that emptied the subject, and any direct set in
       between re-runs it with a non-null closure — so `cur` is null here; the form says so rather than assuming. */
    setDropSubject((cur) => cur ?? next);
  }, [dropSubject, dropBusy, activeModal, docBill]);
  /* 130 §1 — AND THE MATTER JOINS THAT LIST, for the same reason the other two
     are on it: off a matter the retry has no closure to read it back from, and
     a numbered-version retry that filed into a different matter from the
     attempt it is retrying would be the worst bug this flow could have. */
  /* 216 §5 [ADVISOR — never overwrite in silence] — the collision dialog answers a QUESTION the filing loop
     awaits: « version » (127 §2's numbered one) or « skip » (this file is left), one file at a time, so a drop of
     several files asks for each that collides and files the rest. */
  const [docConflict, setDocConflict] = useState<
    | (MatterFileConflictState & {
        answer: (a: "version" | "skip") => void;
      })
    | null
  >(null);
  const [docFiling, setDocFiling] = useState(false);
  const _dropUpload = async (
    file: File,
    place: DropPlace,
    onConflict: "fail" | "version",
    at: DropTarget,
  ): Promise<MatterFileUpload | null | "conflict"> => {
    setDocFiling(true);
    try {
      const out = await uploadMatterFile(at.kind, at.id, file, { subPath: place.subPath, onConflict, root: place.root });
      /* 130-A §N(b)(ii) — ONE SENTENCE PER REASON, on the other kind of rename.
         `renamed` was a boolean covering the numbered-version case alone, and
         a name the drive would not take was changed in silence. Three arms
         now, because the reader has to know which happened: a collision is
         somebody else's file, an illegal character is their own name, and
         "both" is the one that would otherwise be told half. */
      toast(
        out.renamed === "name"
          ? t("docDrop.filed_cleaned", {
              name: out.name,
              original: out.originalName || "",
              fallback: `Filed as “${out.name}” — Microsoft 365 does not accept every character in “${out.originalName || ""}”.`,
            })
          : out.renamed === "both"
            ? t("docDrop.filed_cleaned_renamed", {
                name: out.name,
                original: out.originalName || "",
                fallback: `Filed as “${out.name}” — “${out.originalName || ""}” has characters Microsoft 365 will not take, and a file of that name was already here.`,
              })
            : out.renamed
              ? t("docDrop.filed_renamed", {
                  name: out.name,
                  fallback: `Filed as “${out.name}” — a file of that name was already here.`,
                })
              : t("docDrop.filed", { name: out.name, fallback: `Filed: ${out.name}` }),
      );
      return out;
    } catch (e) {
      /* Typed rather than `any` — the ratchet may only shrink, and `api.ts`
         states this exact shape for a multipart call's rejection. */
      const err = e as Error & { body?: Partial<MatterFileConflict> | null };
      if (err.body && err.body.code === "matterFiles.name_conflict") {
        /* NOT a failure and NOT a success: the question goes to the loop, which asks the dialog and resumes. */
        const name = String(err.body.name || file.name);
        const suggestion = String(err.body.suggestion || "");
        /* the question is not a filing: the dialog's own button reads `busy` off the flag, so it goes down
           while the answer is awaited (the drive found it disabled otherwise) */
        setDocFiling(false);
        const answer = await new Promise<"version" | "skip">((resolve) => {
          setDocConflict({ file, name, suggestion, answer: resolve });
        });
        setDocConflict(null);
        setDocFiling(true);
        if (answer === "skip") return null;
        return "conflict";
      }
      /* The server's own sentence, already resolved through `srvErrText` — so
         `m365.not_connected` and `matterFiles.no_matter_folder` arrive in the
         reader's language rather than as a status code. */
      toast("⚠ " + (err.message || t("docDrop.file_failed", { fallback: "The document could not be filed." })), "error");
      return null;
    } finally {
      setDocFiling(false);
    }
  };
  /* ── BRIEF 129 §2 — THE DOCUMENT SHAPE'S DISPATCHER ───────────────────────
     Laurent's four choices share a PREFIX — every one of them begins "File
     it" — so the act is written once above and the four arms differ only in
     what happens after the bytes have landed:

       file    §3 — nothing after. The document is filed and the drop is done.
       task    §4 — the ordinary Task modal, matter prefilled, the filed
       event        document riding the `docs:` key the email drop already uses.
       bill    §5 — `DocumentBillingModal`, opened on the folder it was filed
                    in with the file already chosen.

     The chooser is closed FIRST and unconditionally, exactly as `_dropChoose`
     does: the folder picker mounts its own React root at zIndex 400 and a
     dialog left underneath it would be a second modal nobody asked for. */
  /* ── BRIEF 129 §4 — B AND C: THE MODALS STILL DO NOT CHANGE ───────────────
     127 §1 made this literally true, and this § is the proof rather than a
     promise: not one line of `TaskModal`, `AgendaEventModal` or `TaskDocsPane`
     changes here. The drop opens the ORDINARY modal, prefilled with the matter
     and the document it has just filed, through prefill keys that already
     exist.

     HOW THE BRIEF'S SENTENCE IS READ, said plainly because it is the one place
     this build interprets rather than follows. §4 says the modal opens
     "prefilled with the matter and nothing else, and the user attaches through
     the pane's second button". Taken at its most literal that would file the
     bytes and then leave the user to go and find them again — and all four of
     Laurent's choices begin "File it", so B and C cannot mean "file it and then
     do not attach it". It is read as "no INVENTED fields": no title, no notes,
     no dates, nothing the app would have to make up. The filed document rides
     the SAME `docs:` key `_dropDocs` already uses for the email, which is
     precisely why the modals do not change.

     AND THE ROW IS OMITTED WHEN THERE IS NO LINK, which is 126 §9's rule
     applied to the other kind: `TaskDoc.url` and `AgendaEventDoc.url` are
     `String` NOT NULL, and "a document that cannot be opened is worse than no
     document". A Graph upload that answered without a `webUrl` still filed the
     bytes — the toast said so — so the modal opens on the matter alone rather
     than carrying a row that opens nothing.

     "CHOOSE AN EXISTING TASK" IS RECORDED, NOT BUILT. The 127 verdict measured
     it: `openModal(t, e)` (App.tsx) seeds the form from `e`, so handing it a
     real row opens that row for EDITING — but nothing in the tree lets a drop
     ASK WHICH existing task, and answering that here would put a second
     item-picker in the tree. §4's fence forbids exactly that. */
  /* 216 §5 [RULED 74] — EVERY document filed rides on: all of them in `docs`, all of them ticked. */
  const _dropFiledDocs = (filed: MatterFileUpload[]) => {
    const docs = filed.filter((f) => f.webUrl).map((f) => ({ label: f.name, url: f.webUrl as string }));
    return docs.length ? { docs } : {};
  };
  const _dropAfterFiling = (
    choice: DocumentDropChoice,
    filed: MatterFileUpload[],
    place: DropPlace,
    at: DropTarget,
  ) => {
    if (choice === "task") {
      /* NO `_caseFixed` — 125 §2's ruling: the matter is filled and EDITABLE,
         and the `tk` arm takes the plain `setForm`, so omitting the key is all
         that is needed. `_dropMatter("tk")` answers `caseId`, which is the only
         spelling that survives TaskModal's whole path.

         130 §1 — AND THIS IS WHERE LAURENT'S SENTENCE LANDS. "Force the choice
         of a case IN THE MODAL": the modal opens with `DOSSIER` FILLED with
         the matter the drop asked for, and editable, exactly as an in-matter
         drop has opened it since 125 §2. The choice was forced one step
         earlier because the bytes could not wait for it (see `_dropDocChoose`);
         the field is the same field and it is still the user's. */
      openModal("tk", { ..._dropMatter("tk", at), title: "", ..._dropFiledDocs(filed) });
      return;
    }
    if (choice === "event") {
      /* `_caseFixed: false` EXPLICITLY — the `ag` arm forces `true` for every
         other caller and brief 05 step 2's opt-out is what lets this one out. */
      openModal("ag", {
        ..._dropMatter("ag", at), _caseFixed: false, title: "", ..._dropFiledDocs(filed),
      });
      return;
    }
    /* ── BRIEF 129 §5 — D: BILLING A DROPPED DOCUMENT ───────────────────────
       §5: "a filed document enters that flow with the file already chosen.
       Re-implement nothing — not the word count, not the modes, not the
       minutes, not the dedup." So none of it is: this opens
       `DocumentBillingModal` (Phase 12, correction R1) on the folder the
       document was just filed into, with that document's own drive item id
       already ticked. Everything after that is the modal's, unchanged — the
       three modes, the lazy `/extract` on expand, the soft "billed previously"
       chip, and the authoritative server-side recompute at `/bill`.

       THE TWO IDS ARE NOT MINTED HERE. `place.folderId` is what the drop's own
       folder picker returned from `/auth/microsoft/browse`, and `filed.id` is
       what the upload route handed back for the bytes it had just written.
       Both are drive item ids in the drive `/documents/list` resolves from the
       same `caseRootFolder`, which is why they address.

       THE PROVENANCE NEEDS NO COLUMN. `/bill` writes `TimeEntry.docItemId` —
       one of `EMISSION_PROVENANCE_KEYS` (`moduleHooks.js:168`), deliberately
       NOT unique, because "multi-year cases re-read docs" where an email is
       deduped hard. Nothing on this path touches that, and §6 is what makes it
       clickable. */
    if (choice === "bill") {
      setDocBill({
        id: at.id,
        /* `DocumentBillingModal`'s own word for a collective case is "cc" and
           its word for a case is "case" — not `MatterKind`'s "ca". Translated
           here rather than widened there: the modal's prop has had this
           spelling since Phase 12 and four other callers pass it. */
        kind: at.kind === "cc" ? "cc" : "case",
        /* 216 §6 [RULED 75] — the billing window opens on the folder the documents were filed in, whichever root:
           by its PATH under that root (the one window hands a path, not a drive id) [ADVISOR]. */
        start: { folderId: place.folderId, folderName: place.folderName, itemId: null, itemIds: filed.map((f) => f.id).filter((x): x is string => !!x), root: place.root, path: place.subPath },
      });
    }
  };
  /* ── BRIEF 130 §1 — THE ACT IS CHOSEN; THE MATTER MAY STILL BE MISSING ─────
     On a matter this is byte-for-byte 129 §3's flow and asks nothing extra —
     §1's "on a matter it must **not** ask". Off one, the chooser STAYS OPEN on
     its second step and the file stays in `dropSubject`; `_dropDocMatter` is
     what resumes it.

     WHY THE MATTER IS ASKED HERE AND NOT LEFT TO B/C/D's OWN `DOSSIER` FIELD —
     the brief proposed the latter ("held and uploaded on save") and the tree
     refuses it on its own ruled grounds. Three measurements:

       1. **D HAS NO `DOSSIER` FIELD.** `DocumentBillingModal`'s props are
          `open · onClose · cs · kind · toast · onBilled` (its own header,
          DocumentBillingModal.tsx:25-31) and the matter appears exactly once,
          as `cs.id` in the URL base (:140). It is a per-matter browser, not a
          form. The brief's screenshot is *Ajouter une prestation* — the EMAIL
          chooser's time-entry modal, a different dialog.
       2. **UPLOAD-ON-SAVE INVERTS 129 §3's RULING.** "A chooser that cannot
          complete must not open." Filing after the row is written means the
          upload's four real failures — M365 not connected, no matter folder,
          a name conflict the user then cancels, a cancelled folder picker —
          each leave a task or an event that EXISTS with nothing attached,
          having been asked for "File it & add to a task". Asking first keeps
          the invariant this flow was built on: nothing is created until the
          bytes have landed.
       3. **THE FOLDER IS PART OF THE ACT.** 129 §3: "Filing a lawyer's
          document is the act where the folder IS the decision." On save, the
          folder picker and 127 §2's conflict dialog would both open AFTER the
          modal closed — two dialogs behind a Save button.

     What the brief asked for is still what happens: the four choices are
     there, the matter is forced, and B and C then open their own modal with
     `DOSSIER` filled and EDITABLE. Nothing in either modal changed — 129 §4's
     "not one line" still holds. */
  const _dropDocChoose = async (choice: DocumentDropChoice) => {
    const s = dropSubject;
    if (!s || s.kind !== "document") return;
    if (_dropMatterId == null) {
      setDropSubject({ ...s, choice });
      return;
    }
    setDropSubject(null);
    await _dropDocFiles(s.files, choice, {
      kind: _dropKind === "cc" ? "cc" : "ca",
      id: _dropMatterId,
    });
  };
  /* THE SECOND STEP'S ANSWER. `caseView.documents.edit` is asked AGAIN, and
     this is the first time it can be asked properly: off a matter `_dropScope`
     is `{}` and `clientCan` answers from the role default (126 §10), which is
     the base a per-case DENY grant would be overriding. So a viewer who may
     file in general and not in THIS matter is refused here rather than by a
     403 after the folder picker — read off the same route as 129 §3, with the
     same sentence. */
  /* 131 §6 — THE STEP OFFERED `Dossiers` AND HID `Dossiers collectifs`, which
     is the defect Laurent glanced. The kind now arrives with the id, and it has
     to: the permission is scoped `{ccId}` rather than `{caseId}` (the shape
     `:7969` already states for the email twin), and `_dropDocFile` forks the
     upload on `at.kind` — it always could, and was only ever handed "ca". */
  const _dropDocMatter = async (matterId: number, kind: "ca" | "cc" = "ca") => {
    const s = dropSubject;
    if (!s || s.kind !== "document" || !s.choice) return;
    const scope = kind === "cc" ? { ccId: matterId } : { caseId: matterId };
    if (!_appPerms?.can?.("caseView.documents.edit", scope)) {
      toast(
        t("docDrop.not_allowed", {
          fallback: "You are not allowed to add documents to this matter.",
        }),
        "error",
      );
      return;
    }
    setDropSubject(null);
    await _dropDocFiles(s.files, s.choice, { kind, id: matterId });
  };
  /* 216 §5 [RULED 74] — EVERY document of the drop: one window (one root, one folder), then each is filed — a
     name that collides asks 127 §2's dialog for THAT file —, then the task or the event opens with all of them, or
     the billing window with all of them ticked. */
  const _dropDocFiles = async (
    files: File[],
    choice: DocumentDropChoice,
    at: DropTarget,
  ) => {
    /* 216 §6 [RULED 75] — THE ONE WINDOW: the e-mail attachments window's browser (breadcrumb, subfolders,
       « Nouveau dossier »), with the matter's folder or a linked client's at its top (FileDestinationModal.tsx),
       where the general SharePoint browser stood — no « Nouveau dossier », « Remonter » stopped at the matter's
       folder. It hands a root and a path, never a drive id: the billing window opens by that path. */
    setDropBusy(true);
    try {
      const picked = await openFileDestination({ kind: at.kind, id: at.id, canCreateFolder: _dropCanFile });
      if (!picked) return; // cancelled — say nothing, the user just closed it
      const dest: DropPlace = { subPath: picked.subPath, folderId: null, folderName: picked.folderName, root: picked.root };
      const filed: MatterFileUpload[] = [];
      for (const file of files) {
        let out = await _dropUpload(file, dest, "fail", at);
        if (out === "conflict") out = await _dropUpload(file, dest, "version", at);
        if (out && out !== "conflict") filed.push(out);
      }
      if (filed.length) _dropAfterFiling(choice, filed, dest, at);
    } finally {
      setDropBusy(false);
    }
  };

  /* 207 §13 [ADVISOR — Laurent overrules at the glance] — A NEW client or party
     starts with the FIRM's country filled in: its CODE, a real value saved with
     the record and changeable in the form. The person modals that carry a
     country field; a record being EDITED, or a seed that already names a
     country, is never touched. */
  const NEW_PERSON_WITH_COUNTRY = new Set(["cl", "ocu", "ex", "me", "ar", "op2", "cp"]);
  const firmCountryNow = firmCountryCode(firmSettings?.firm, (v) => resolveTaxonomyEntry(v as never, countryEntries as never));
  const openModal = (t: any, e: any = null) => {
    setActiveModal(t);
    if (t === "ca" && (!e || (e.isProspect && !e.id))) {
      /* The ref this modal offers is what usually BECOMES the ref — the create
         route reads `ref || generateRefTx(…)` — so it is generated from the
         firm's own pattern (FirmSettings.caseRefPattern), through the SAME
         pure grammar the server allocates with. Two copies of a numbering
         format is how client and server come to disagree, which is exactly
         what happened to the collective case.

         BRIEF 04 UNIT 2 — AND WHICH PATTERN IS THE TAG'S ANSWER, on both ends.
         A prospect pre-fills from `prospectRefPattern` because the server will
         allocate from it; offering a `2026/NNN` here for a row the create route
         is about to number `0/NNNN` would be the same client/server
         disagreement in a new place. The arm widened to accept a seed rather
         than only `!e`: `openModal("ca", { isProspect: true })` is how unit 4's
         second create button will open this modal, and until it exists the new
         branch is unreachable (`!e` is every caller today, and it still takes
         the ordinary series). `nextRefFrom` needs no matter filter — it scans
         `rows` for the pattern's own PREFIX, and "0/" selects exactly the
         prospect refs, which is the same thing the server's `startsWith` does. */
      const prospect = !!(e && e.isProspect);
      setForm({
        ref: nextRefFrom(
          prospect ? firmSettings?.prospectRefPattern : firmSettings?.caseRefPattern,
          cases,
        ),
        status: "active",
        ...(prospect ? { isProspect: true } : {}),
      });
    } else if (t === "ag" && e?.id) {
      setForm({
        ...normFormDates(e),
        _locManual: true,
        _caseFixed: e._caseFixed || false,
      });
    } else if (t === "ag" && (e?.caId || e?.ccId) && !e?.id) {
      /* BRIEF 117 §8 STOOD HERE AND IS WITHDRAWN IN PLACE (191 D.0, carried):
         a new event on a matter is NO LONGER called after the matter. The
         stored title stays the event's own — TITRE opens empty and required —
         and the matter travels AT THE WIRE instead: `m365.typedSubject`
         composes Outlook's subject as `CODE: <first word of the matter's
         title> · <title>` (server/src/lib/m365.js), and the import strips it
         back on the same licence (recordings/191-outlook-subject). What this
         branch still does is what it did besides the seed: a case-bound new
         event takes the case's collaborators as attendees, and the matter
         field is fixed (`_caseFixed`) unless the caller says otherwise. The
         collective half keeps the condition widened to either id (117 §8) so
         a cc-bound new event lands here and not in the bare `else`. */
      /* Written out rather than reached for with `any`: the ratchet may only
         ever shrink, and it refused this branch's first version. */
      type _MatterRow = {
        id?: number;
        caseCollaborators?: { coId?: number }[];
      };
      const _rows: _MatterRow[] = e?.caId ? cases : collectiveCases;
      const _m = _rows.find((c) => c.id === (e?.caId || e?.ccId));
      const acs = e?.caId
        ? _m?.caseCollaborators?.map((x) => x.coId) || []
        : null;
      setForm({
        ...normFormDates(e),
        ...(acs ? { assignedCollaborators: acs } : {}),
        /* BRIEF 05 STEP 2 — A PREFILL MAY UNLOCK THE MATTER FIELD.
           Every caller that reaches this arm today wants the matter fixed: it
           was opened FROM that matter. The email-drop feature does not —
           Laurent ruled the matter modifiable there — so the arm honours an
           explicit opt-out and nothing else. Backward-compatible by
           construction: `undefined` still yields `true`, so no existing call
           site moves, and only a caller that says `_caseFixed: false` out loud
           gets an editable field.

           THE BRIEF'S TEXT DOES NOT PASTE HERE, and the difference matters.
           Step 2 quotes this arm as `t === "ag" && e?.caId && !e?.id` assigning
           a bare `_caseFixed: true`; brief 117 §8 has since widened the
           condition to either id and turned the assignment into this
           conditional spread. The replacement goes INSIDE the spread's object,
           never in place of it — a cc-bound new event gets NO `_caseFixed` key
           today, which is what makes ITS matter field editable, and hoisting
           the property out would lock a field that is open now. */
        ...(e?.caId ? { _caseFixed: e._caseFixed === false ? false : true } : {}),
      });
    } else {
      const seed = e ? normFormDates(e) : {};
      const isNew = !e || e.id == null;
      const fresh = NEW_PERSON_WITH_COUNTRY.has(t) && isNew && (seed.country == null || seed.country === "");
      setForm(withClientFormLang(t, isNew, fresh && firmCountryNow ? { ...seed, country: firmCountryNow } : seed)); // 207 §13 · 207B §6 · 207C §6
    }
  };
  /* 209 §5 [RULED 18] — THE CASE FORM, ONE CLICK AWAY. A text that needs the
     case's language and finds none is refused (`case.language_required`); the
     refusal's toast carries an action that asks for the matter's form
     (src/lib/caseLanguage.ts). A case's form is this component's; a collective
     case's is its view's — mounted there, it opens its own; met elsewhere, the
     app navigates to it and the view takes the waiting request on arrival. */
  // The handler reads the app as it is NOW (kept current after every render) and
  // is subscribed once (the effect below).
  const matterFormCtx = useRef({ cases: cases as unknown, section: section as unknown, detailId: detailId as unknown, openModal, nav });
  useEffect(() => {
    matterFormCtx.current = { cases: cases as unknown, section: section as unknown, detailId: detailId as unknown, openModal, nav };
  });
  useEffect(() => {
    const h = (ev: Event) => {
      const m = (ev as CustomEvent<MatterRef>).detail;
      const x = matterFormCtx.current;
      if (m.caseId) {
        const row = (x.cases as { id: number }[]).find((c) => c.id === m.caseId);
        if (row) x.openModal("ca", row);
      } else if (m.ccId && !(x.section === "cc" && Number(x.detailId) === m.ccId)) {
        setPendingMatterForm(m);
        x.nav("cc", m.ccId);
      }
    };
    globalThis.addEventListener(OPEN_MATTER_FORM, h);
    return () => { globalThis.removeEventListener(OPEN_MATTER_FORM, h); };
  }, []);
  const cm = () => {
    setActiveModal(null);
    setForm({});
    /* 111 §5b — a modal close ends the chain session (Cancel / plain Save /
       any other exit). chainGo re-arms the ref AFTER calling cm when the
       chain continues, so the transition survives this line. (Declared below
       cm but initialized before any handler can run — safe closure.) */
    linkChainRef.current = null;
    /* 04g §2 — a create password typed and then CANCELLED must not survive the
       modal. `_apiEnt[SK.co].c` reads-and-clears, so a value left armed here
       would be injected into whatever collaborator was created next. Closing
       is the only other exit from the modal, so this is the other half of that
       clear. */
    coCreatePwRef.current = "";
    /* 120 §2 — the create-in-flight guard is scoped to the OPEN form. A closed
       modal's outstanding request must not refuse the next modal's save. */
    agCreateInFlight.current = false;
    /* ── BRIEF 125 §3 — THE STALE "GÉNÉRAL (SANS DOSSIER)", AT ITS CAUSE ─────
       Laurent, on the glance of #430: "when choosing New Event, a 'general —
       without file' field stays stale and obstructs the view — this may be a
       wider problem, check the origin." His screenshot shows the matter
       combobox's menu row floating over the TYPE select beneath it.

       IT IS NOT `Combobox.tsx`. The brief guessed a shared `Combobox` menu that
       never unmounted; measured, the event modal's matter field is not a
       `Combobox` at all — it is an open-coded input plus an absolutely
       positioned menu (AgendaEventModal.tsx:596-780) driven by THIS
       App-scope state, and `Combobox` has six callers none of which is this
       one. So the fix could not have lived there either.

       THE CAUSE IS THE SENTINEL, AND IT WAS WRONG AT BOTH ENDS. The menu
       renders on `agCaseQ != null` (:628). `null` means closed; `""` means open
       on the recents list. The state was INITIALISED to `""` (:1957) and RESET
       here to `""` — both the OPEN value — so the menu was painted the moment
       the field mounted, on every single open, and only an explicit dismiss
       inside the modal (Escape, or picking a row, :624/:687/:728) ever closed
       it. Closing the modal armed it again for the next one.

       THE SIBLING IS THE PROOF IT IS A SLIP AND NOT A DESIGN: `teCaseQ`, one
       line below the declaration and one line below this reset, has the same
       shape, states the contract in its own comment since C18, and resets to
       `null`. Two pickers, adjacent lines, opposite sentinels.

       WHY IT IS NOT PATCHED AT THE CALL SITE. The field renders whenever
       `!form._caseFixed`, which is 8 of the 11 `openModal("ag", …)` call sites
       — every EDIT of an existing event (the ag arm assigns
       `_caseFixed: e._caseFixed || false`, and no stored row carries the key)
       from AgendaView :854/:2056, CaseView :3896, CompactColumn :368 and
       Dashboard :924; both matterless creates (AgendaView :1604,
       popoverChrome :125); and the email drop, which is the one Laurent hit.
       Only the three callers that pass `_caseFixed: true` were spared
       (CaseAgendaSection :273, CaseEventRow :894, CaseView :1468). An
       `sAgCaseQ(null)` added to the drop's own arm would have fixed the report
       and left the other seven. */
    sAgCaseQ(null);
    sTeCaseQ(null);
    setEisenPickerOpen(false);
  };
  const updateField = (k: any, v: any) => setForm((p: any) => ({ ...p, [k]: v }));
  // The firm's ladder, for every `ITEM_LEVEL[x]` read in the body below. This
  // used to SHADOW a hardcoded map of the same name; there is no longer a
  // hardcoded map to shadow, and no PRI_CYCLE either — its click-through had
  // been dead in all three hosts long before this slice.
  const ITEM_LEVEL = itemLevelMap(itemLevelList);

  const locIcon = (loc: any) => {
    const st = {
      display: "inline-block",
      verticalAlign: "middle",
      flexShrink: 0,
    };
    if ((loc || "").toLowerCase() === "online")
      return (
        <svg
          style={st}
          width="12"
          height="10"
          viewBox="0 0 12 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.1"
        >
          <rect x=".5" y=".5" width="11" height="7" rx="1" />
          <line x1="6" y1="7.5" x2="6" y2="9" />
          <line x1="3.5" y1="9.5" x2="8.5" y2="9.5" />
        </svg>
      );
    return (
      <svg
        style={st}
        width="8"
        height="11"
        viewBox="0 0 8 11"
        fill="currentColor"
      >
        <path d="M4 0C1.79 0 0 1.79 0 4c0 3 4 7 4 7s4-4 4-7c0-2.21-1.79-4-4-4zm0 5.5C3.17 5.5 2.5 4.83 2.5 4S3.17 2.5 4 2.5 5.5 3.17 5.5 4 4.83 5.5 4 5.5z" />
      </svg>
    );
  };
  /* BRIEF 95 §2c — THE MATTER'S RESPONSIBLE PARTNER, FOR A ROW THAT HAS NO
     PRINCIPAL OF ITS OWN.
     Brief 94 §2 left this stack sorting the viewer last because an event has no
     principal. It has one by INHERITANCE — the responsible partner of the case
     it belongs to — and that is a better answer than "every attendee is equal".
     HOW IT ARRIVES: a SECOND ARGUMENT, and it is the EVENT rather than the
     case. All three call sites (AgendaView.tsx:594, Dashboard.tsx:1226,
     CaseEventRow.tsx:270) already hold `ev`; only two of them hold the matter
     lists, and `CaseEventRow` holds neither. `coAvs` is defined inside App and
     already closes over `cases` and `collectiveCases`, so resolving it HERE
     needs no state threaded to any caller — which is what §5's 2c clause
     forbids. It also puts the `caId`/`caseId` double spelling (navAgendaEvent's
     own defensive read, :6835, and CompactColumn's at :214) in one place
     instead of three.
     `responsiblePartners` is read exactly as App.tsx:7797 and
     matterListCfg.tsx:204 read it: an array of collaborator ids on the matter. */
  const _evRpIds = (ev: EvMatterRef | null | undefined): number[] => {
    if (!ev) return [];
    const cid = ev.caId != null ? ev.caId : ev.caseId;
    if (cid != null)
      return (
        (cases as MatterRps[]).find((c) => c.id === cid)?.responsiblePartners ||
        []
      );
    if (ev.ccId != null)
      return (
        (collectiveCases as MatterRps[]).find((c) => c.id === ev.ccId)
          ?.responsiblePartners || []
      );
    return [];
  };
  /* 119 §5 — THE MATTER'S TEAM, which is what separates group (a) from group
     (b). Same two-ledger resolve as `_evRpIds` above and for the same reason:
     an agenda event carries either spelling of its case id, or a `ccId`. */
  const _evTeamIds = (ev: EvMatterRef | null | undefined) => {
    if (!ev) return teamIdSet([]);
    const cid = ev.caId != null ? ev.caId : ev.caseId;
    const m =
      cid != null
        ? cases.find((c: { id?: number }) => c.id === cid)
        : ev.ccId != null
          ? collectiveCases.find((c: { id?: number }) => c.id === ev.ccId)
          : null;
    return teamIdSet(
      ((m as { caseCollaborators?: { coId?: number }[] } | null)?.caseCollaborators || [])
        .map((x) => x.coId),
    );
  };
  /* ── BRIEF 116 §2 [RULED] — AT MOST TWO BADGES, AND NEVER A SHIFTING STACK ──
     Laurent: a row shows the collaborator assigned to it, and the CURRENT USER
     if a subtask of that row is his. Everybody else is named in this area's
     `title`. The stack this replaced grew with the row's people and took the
     row's dates with it (measured: three attendees drew 54px here, and a
     six-badge task row stretched its whole column to 106).

     THE PRINCIPAL IS THE ONE 95 §2c ALREADY RULED — the attending responsible
     partner, which that brief gave the event "by INHERITANCE" and sorted to the
     head of this stack. With no RP attending it is `ids[0]`, which is not a new
     notion either: it is what the task wire shape has always called the
     principal (`assigned[0]`, server/src/lib/taskShape.js:23) applied to the
     same column (AgendaEvent.assigned, aliased `assignedCollaborators`).

     AND 95 §2c's SECOND ORDER DISSOLVES. It kept two — RP-first (earlier in
     front, hole on the LEFT) and today's viewer-last (later in front, hole on
     the RIGHT) — because with every attendee equal there was nothing to raise.
     A two-slot stack whose slots MEAN something has one order by construction:
     principal, then the viewer. So the right-hand cut and the viewer-last sort
     go, and this stack takes the task row's geometry always. */
  const coAvs = (ids: any, ev?: EvMatterRef | null) => {
    const _all: number[] = Array.isArray(ids)
      ? (ids as unknown[]).filter((x): x is number => x != null)
      : [];
    /* §2c — only the RPs who are actually ATTENDING. An RP who is not on the
       event is not in this stack and there is nothing to raise. */
    const _rp = new Set<number>(
      _evRpIds(ev).filter((id: number) => _all.includes(id)),
    );
    /* §2 — badge 1. The first attending RP (95 §2c's head), else the first
       attendee. Null when the event has nobody, which is a real state: the
       area still renders, at its reserved width, so a row with no attendees
       and a row with two put their titles in the same place. */
    const _principal: number | null = _all.find((id) => _rp.has(id)) ?? _all[0] ?? null;
    /* §2 — badge 2. The viewer, when a live subtask of THIS EVENT is assigned
       to him and he is not already the principal. He need not be an attendee:
       the rule is about holding work on the row, which is exactly what the task
       side means by it.
       BRIEF 117 §4 — AND BEING ON THE EVENT COUNTS TOO. That clause is what the
       loss was: measured on 2026/001, whose case names no responsible partner,
       so every event's principal is `assigned[0]` and the viewer — an attendee
       of seven of its ten events, holder of a subtask on none — had no badge on
       any of them. `viewerInRow` is the union; see src/lib/badgeStack.ts. Still
       at most two badges, still the same reserved width. */
    const _viewerSub = viewerInRow(
      _all,
      ev && ev.subtasks,
      currentUser,
      _principal,
    );
    /* §2 — the tail is the tooltip: every attendee, in the order the stack drew
       them, comma-joined and resolved exactly as the badges resolve.
       BRIEF 117 §3 — AND THE TOOLTIP BECOMES A PANEL. `assigneeTip`'s one
       comma-joined string could not say who is an attendee and who holds a
       subtask; §3 wants both, sectioned, one line each. `badgeSections` is the
       same dedupe-and-resolve split in two, in the same file, so the two hosts
       still share one derivation. The native `title` goes with the string it
       carried: a tooltip and a panel over one element is worse than either.
       EVENTS take "Should attend" where a task takes "Main task" — §3's own
       words and its own key — and reuse the subtasks key. */
    const _sections = badgeSections(
      _all,
      ev && ev.subtasks,
      (id: number) => collaborators.find((x: { id?: number }) => x.id === id),
      (c: NameableCo) => ({ initials: coIni(c), name: pname(c) }),
    );
    /* ── 119 §5 — THE ATTENDEE SECTION BECOMES THE THREE NAMED GROUPS ───────
       §5's legibility rule binds this panel as well as the modal, and the SAME
       grouping function answers it — the panel simply hands it the same
       population the modal's overview builds (`eventPeople`), so the two cannot
       disagree about who is a third party.

       "Should attend" becomes up to three headings; SUBTASKS STAYS. 117 §3 put
       it there to answer a different question — how is this person involved,
       not which constituency are they — and 118's own note on that section is
       that somebody who is both appears in both, because the honest answer is
       "twice". Folding it into the three would delete that.

       A TASK ROW IS UNTOUCHED (components/CaseTaskRow.tsx keeps
       `badgeSections`): a task has no external addressees and no meeting, so the
       three groups would always collapse to at most two and the rename would buy
       nothing. §5's subject is an EVENT's people. */
    const _evTeam = _evTeamIds(ev);
    const _evGroups = groupPeopleByMatter(
      eventPeople(
        _evTeam,
        _all,
        ev && ev.outlookAttendees,
        collaborators,
        pname,
      ),
      _evTeam,
    );
    const _panelSections = [
      ..._evGroups.map((g) => ({
        label: t(PEOPLE_GROUP_KEY[g.key]),
        people: g.people.map((r) => ({
          initials:
            r.coId == null
              ? externalInitials(r.name || r.email)
              : coIni(collaborators.find((x: { id?: number }) => x.id === r.coId)),
          name: r.name,
        })),
      })),
      {
        label: t("badges.section_subtasks", { fallback: "Subtasks" }),
        people: _sections.subs,
      },
    ];
    const _slots: { id: number; role: "principal" | "viewer" }[] = [];
    if (_principal != null) _slots.push({ id: _principal, role: "principal" });
    if (_viewerSub && currentUser != null)
      _slots.push({ id: currentUser, role: "viewer" });
    /* 75 §3 (A.8) — every attendee archived means the record lost its only
       people: earmark with the ?-badge, beside the greyed badges, here in the
       ONE renderer every event surface calls. */
    const _allArch = soleArchived(
      _all.map((id: number) => collaborators.find((x: { id?: number }) => x.id === id)),
    );
    return (
      /* BRIEF 108 §7 — the agenda EVENT row renders this stack inline in its
         title flex row (AgendaView.tsx:625) with no reserved column, unlike
         the task row's `width: 26, flexShrink: 0` wrapper (:884). Without
         `flexShrink: 0` here a tight row compresses the discs and the badges
         draw truncated. Declared in the ONE renderer so every event surface
         inherits it.
         116 §2 — and it takes the two-slot RESERVATION with it, for the same
         "declared once, inherited by every host" reason: an event card has no
         badge column to widen (114 measured that), so the area itself is the
         column. `minWidth`, so the 75 §3 earmark beside it is never clipped. */
      <BadgeHoverArea
        sections={_panelSections}
        style={{
          display: "inline-flex",
          alignItems: "center",
          /* BRIEF 139 §1.3 — FLUSH, not centred, and the divergence from
             `CaseTaskRow.tsx:827` is the point rather than an oversight. That
             badge sits in a REAL TABLE CELL (`width: BADGE_AREA_W + 4`, :622)
             and centring in a column cell is correct. This one does not: 114
             measured that an event card has no badge column to widen, so 116 §2
             made the AREA the column (that sentence is at :9035) — and the
             centring travelled with the
             reservation into a place with nothing to centre in. MEASURED: the
             area is 38 (`BADGE_SIZE * 2 - BADGE_OVERLAP`, badgeStack.ts:94) and
             a single badge is 22, so centred left ~8px of dead air on each side.
             Flush, the reserved 16 falls entirely to the RIGHT, where the second
             badge would go. Nothing downstream moves: the area still reserves
             its `minWidth`, so the timer and the title keep their x and the
             reservation's whole purpose — one title x down the list — is
             untouched. */
          justifyContent: "flex-start",
          flexShrink: 0,
          minWidth: BADGE_AREA_W,
        }}
      >
        {/* 116 §2 — THE EM-DASH MOVES IN HERE, and it has to: this area now
            RESERVES its width, so a card with nobody on it would otherwise put
            an empty 38px box next to the dash its host draws beside it
            (CaseEventRow.tsx:406). One owner, one box, three states. The dash
            loses its `canEdit` gate on the way — the task row's own
            no-principal branch has always drawn one unconditionally
            (CaseTaskRow.tsx:816), so this is the two kinds agreeing rather than
            a new rule; the CLICK affordance is unchanged and still the host's.
            AgendaView's event rows gain the dash they never had, named in the
            PR as a visible change. */}
        {_slots.length === 0 && (
          <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>—</span>
        )}
        {_slots.map(({ id }, i) => {
          const c = collaborators.find((x: any) => x.id === id);
          if (!c) return null;
          const isMe = id === currentUser;
          /* Phase 31 (C5) — shared InitialsBadge (photo-capable); the
             stacked-overlay specifics (negative margin, outline, z-order,
             hairline border) ride the style override. */
          return (
            <InitialsBadge
              key={id}
              person={c}
              isMe={isMe}
              /* 118 §3 — this stack sits inside `BadgeHoverArea`; the panel
                 carries the names now. See SharedUI's `hideTitle`. */
              hideTitle
              /* BRIEF 90 §3c — ONE BADGE SIZE, THE LARGER. The agenda's stacked
                 avatars were 18/8 here and the Tasks list renders
                 InitialsBadge with no size at all (CaseTaskRow.tsx:563), i.e.
                 the component's own 20/9. Taking the larger makes 20/9 the one
                 size, and it is the DEFAULT — so the tasks side needed no change
                 and cannot drift from this one by omission.
                 The ring is NOT changed and did not need to be: InitialsBadge
                 has always drawn it `isMe ? … : "none"` (SharedUI.tsx:335, :364),
                 and the override below repeats the same condition. Only the
                 viewer's badge was ever circled. */
              /* BRIEF 95 §2a — 20 -> 22, THE SIZE THE OTHER TWO RENDERERS
                 ALREADY AGREE ON (matterListCfg.tsx:308, CaseTaskRow.tsx:631).
                 90 §3c took "the larger" of 18 and 20 and made 20 the one size;
                 it was reading `InitialsBadge`'s default rather than the two
                 stacks that actually draw a stack, both of which are 22/6. Three
                 numbers move together or the mask lands off the disc it cuts —
                 the `size=` prop, the `marginLeft`, and `stackCut`'s `size`. */
              size={22}
              fontSize={9}
              /* 116 §2 — NO PER-BADGE `title`. The area's tooltip names the
                 whole list and the badges are the only ink in it, so a title
                 here would be the one a hover actually reaches and the list
                 would never show. The names are not lost — they moved up one
                 element, which is the point of the ruling. */
              /* BRIEF 94 §1a/§1b — ONE RING, AND IT IS THE VIEWER'S.
                 The `--br` border and the `--bd` outline are gone: both were
                 ring-shaped devices on EVERY badge, in tokens that name a
                 surface this stack does not sit on (measured, light theme:
                 `--bd` is rgb(240,242,245) against an agenda card, a task row
                 and a translucent drawer). `boxShadow: isMe` is untouched and is
                 now the only ring anything draws — so a row of two colleagues,
                 neither of them the viewer, carries none.
                 The separation the outline used to give is cut rather than
                 painted (`stackCut`, src/lib/badgeStack.ts): the badge behind is
                 masked where its neighbour covers it, and the surface shows
                 through the hole whatever the surface happens to be.
                 94 §2 — the ORDER here was deliberately unchanged: an event had
                 no principal, so the viewer sorted last and `zIndex: 2` kept
                 them in front. BRIEF 95 §2c gives it one where the matter has a
                 responsible partner among the attendees, and the two orders
                 carry their own hole side with them — LEFT when the stack
                 paints earlier-in-front (the RP is first), RIGHT when it paints
                 later-in-front (today's order, unchanged).
                 116 §2 — ONE ORDER NOW, so ONE hole side. The slots are
                 [principal][viewer] by construction, the principal paints in
                 front, and the second badge is cut on its LEFT — the task row's
                 geometry, which is what 95 §2c already used whenever the event
                 had a principal at all. */
              style={{
                boxShadow: isMe ? BADGE_SELF_RING : "none",
                marginLeft: i > 0 ? -BADGE_OVERLAP : 0,
                zIndex: Math.max(1, 9 - i),
                position: "relative",
                ...(i > 0
                  ? stackCut({ size: BADGE_SIZE, overlap: BADGE_OVERLAP, side: "left" })
                  : null),
              }}
            />
          );
        })}
        {/* 116 §2 — THE "+N" CHIP IS GONE, and its removal is a DECISION the
            brief enumerates rather than a side effect: the tail is carried by
            this area's `title`, and a chip would be a third thing in a
            two-slot area. If a surface is ever found where the loss is acute
            the replacement is muted TEXT, never a third badge. */}
        {/* §2a — the earmark takes the stack's size with it. It is drawn BESIDE
            the badges, so leaving it at 18/8 next to 22/9 discs would have been
            the one thing in the row that did not match; both sibling renderers
            already draw it at 22/9 (matterListCfg.tsx:332,
            CaseTaskRow.tsx:688). */}
        {_allArch && (
          <ArchivedHoleBadge size={22} fontSize={9} style={{ marginLeft: 2 }} />
        )}
      </BadgeHoverArea>
    );
  };
  const toggleDL = () => {
    const v = !showDL;
    sShowDL(v);
  };
  const DLBtn = () => (
    <button
      className="btn"
      style={{
        padding: "2px 8px",
        fontSize: FS.s10,
        borderRadius: RAD.r20,
        background: showDL ? "var(--gs)" : "var(--bc)",
        color: showDL ? "var(--gn)" : "var(--tm)",
        border: "none",
      }}
      onClick={toggleDL}
    >
      {showDL ? `● ${t("agenda.deadlines")}` : `○ ${t("agenda.deadlines")}`}
    </button>
  );
  /* BRIEF 118 §4 — `genAgTitle` IS GONE, and its removal is the § rather than
     a tidy-up. It was the SECOND composer of a matter's name — `CODE: firstWord
     - ref`, first word only, hyphen — against 117 §8's `matterEventTitle`, which
     is the whole title with the house em dash. Two composers of one idea, and
     the one that ran last won: the type <select> called this on every change and
     replaced the prefill (AgendaEventModal.tsx). Its only two call sites were
     that handler and the case picker beside it.
     191 D.0 (carried) — AND THE PREFILL ITSELF IS WITHDRAWN: neither the
     open-modal arm above nor the case picker seeds a title any more. The
     matter travels at the wire — `m365.typedSubject` composes `CODE: <first
     word of the matter's title> · <title>` for Outlook, and the import strips
     it back — so a stored title is the event's own and only ever the user's.
     `matterEventTitle` stays for its one remaining reader, the email-drop
     chooser's label (EmailDropChooser.tsx). */
  /* P20a — the autofilled location is now event_type taxonomy DATA
     (meta.location), resolved by the shared resolveEventLocation. This fn is
     down to WIRING: look the spec up, hand the resolver the case's linked
     place and the firm room. The "case's place" is its jurisdiction today —
     the generic seam the linkedPlace mode names. */
  const genAgLocation = (typeCode: any, caseId: any) => {
    if (!typeCode) return "";
    const cs = caseId ? cases.find((c: any) => c.id === +caseId) : null;
    const place = cs?.juId
      ? jurisdictions.find((x: any) => x.id === cs.juId)
      : null;
    return resolveEventLocation(evtLocations[typeCode], {
      place,
      firmRoom: mtgDefLoc,
    });
  };
  /* 157A §8 RULED (2026-08-31, DIRECTION CORRECTED) — `asColTag` renders this
     chip as the POPOUT'S MODEL, `.colTag` (App.css:2929), which is the timer
     chip in CompactColumn's Minuteur section and was right all along. An
     earlier pass reconciled this chip to `.tag` on the grounds that its inline
     metrics already equalled `.tag`'s; that was true and beside the point,
     because the standard the popout's sections have to agree on is the timer
     chip, not `.tag`.
     ONLY THE PER-TYPE COLOUR STAYS, and it stays INLINE because it is both
     meaning and DYNAMIC — it comes from the event-type taxonomy, so no static
     class can carry it, and §8's own rule is that a colour variant carrying
     meaning survives the reconciliation. Being inline it also outranks
     `.colTag:hover`, so the tint does not flash to `--bh` under the pointer.
     Everything else — size, weight, padding, radius, monospace face — comes
     from the class, which is the whole point of there being one.
     THE FLAG MUST REACH THE MIRROR TOO. `CaseEventRow` sizes `TypeTagBox` by
     rendering the longest code through this same function (118 §8 / 139 §1:
     "so the mirror cannot drift from what it mirrors"). A visible chip at
     `.colTag`'s metrics against a mirror at the old ones would size the box
     from the wrong chip, so :167 passes `reduced` exactly as :402 and :827 do. */
  const etTag = (code: any, asColTag?: boolean) => {
    const t = evtTypes.find((x: any) => x.code === code) || {
      code: code || "?",
      color: CT.c_8892a4,
    };
    const r = parseInt(t.color.slice(1, 3), 16),
      g = parseInt(t.color.slice(3, 5), 16),
      b = parseInt(t.color.slice(5, 7), 16);
    /* BRIEF 118 §8 — the chip lives inside a fixed box on the event surfaces
       (`TypeTagBox`), and §8's instruction for a code wider than the box is
       that it ELLIPSISES rather than widening. These three do nothing wherever
       the chip is unconstrained, which is every other caller. */
    const clip = {
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
    } as const;
    if (asColTag) {
      return (
        <span
          className="colTag"
          style={{
            background: `rgba(${r},${g},${b},.15)`,
            color: t.color,
            cursor: "default",
            ...clip,
          }}
        >
          {t.code}
        </span>
      );
    }
    return (
      <span
        style={{
          display: "inline-block",
          fontSize: FS.s9_5,
          padding: "2px 6px",
          borderRadius: RAD.r4,
          background: `rgba(${r},${g},${b},.15)`,
          color: t.color,
          fontWeight: 500,
          whiteSpace: "nowrap",
          ...clip,
        }}
      >
        {t.code}
      </span>
    );
  };
  const cf2 = (t: any, id: any) =>
    cases.filter((c: any) => {
      if (t === "cl") return (c.clIds || []).includes(id);
      if (t === "op") return (c.opIds || []).includes(id);
      if (t === "ocu") return (c.ocuIds || []).includes(id);
      if (t === "ex") return (c.exIds || []).includes(id);
      if (t === "ju") return c.juId === id;
      if (t === "co")
        return (c.caseCollaborators || []).some((x: any) => x.coId === id);
      if (t === "cp") return (c.cpIds || []).includes(id);
      if (t === "oc") return (c.ocIds || []).includes(id);
      if (t === "me") return (c.meIds || []).includes(id);
      if (t === "ar") return (c.arIds || []).includes(id);
      if (t === "op2") return (c.op2Ids || []).includes(id);
      return false;
    });
  // P17.1a (F1) — the browser effR resolver was removed: the effective rate is
  // now server-authoritative (server/src/lib/caseFinancials.js collabRates, via
  // the shared makeEffR). The two team-table rate echoes read the served map.
  // docs/hooks-v0.md §5.7.
  /* 200 §E — `SEC_TASKS` (["ADMIN","CORR","EXCORR","PREP","TEL"]) STOOD HERE
     and is GONE: the secretarial activity list is the `activity_code`
     taxonomy's own `meta.secretarial` flag now (SettingsTaxonomies, read by
     TimeEntryModal off `tkTypes`), so no vertical carries this one's codes. */
  const isSecRole = (c: any) => billsAtZeroCollab(c);
  const lp = (et: any, id: any) => {
    const cs = cf2(et, id);
    return cs.length ? (
      cs.map((c: any) => (
        <span key={c.id} className="lib-pill" onClick={() => nav("ca", c.id)}>
          {c.ref} ›
        </span>
      ))
    ) : (
      <span style={{ color: "var(--tm)", fontSize: FS.s9 }}>
        {t("common.none", { fallback: "None" })}
      </span>
    );
  };

  // CRUD
  /* 04g §2 — `opts` is forwarded verbatim to persist(); its `createExtra` is
     how a field reaches the CREATE request without entering the row. Optional
     and unused by every existing caller. */
  const saveEntity = (
    sk: any,
    arr: any,
    fn: any,
    d: any,
    ext = {},
    opts?: SaveEntityOpts,
  ) => {
    if (!d.name && !d.companyName && !d.lastName && !d.ref) {
      toast(t("toast.name_required", { fallback: "⚠ Name is required" }));
      return;
    }
    /* Phase 30 (D-3) — success toasts fire only after the server confirmed
       (the engine's outcome promise); failures already rollback + toast. */
    if (d.id) {
      persist(
        sk,
        arr.map((i: any) => (i.id === d.id ? { ...i, ...d, ...ext } : i)),
        fn,
      ).then((r: any) => {
        if (r.ok) toast(t("toast.updated", { fallback: "Updated" }));
      });
    } else {
      persist(sk, [...arr, { ...d, ...ext, id: tempId() }], fn, opts).then(
        (r: any) => {
          if (r.ok) toast(t("toast.added", { fallback: "Added" }));
        },
      );
    }
    cm();
  };

  // Phase 43 (item 5) — save-time assistive CONFLICT / DUPLICATE warnings.
  // New-adds only. COI (D21/D23/D24): adding an opponent or additional-opponent
  // (NOT counsel/expert/mediator/arbiter) whose NAME matches a client in the
  // archived-INCLUSIVE pool — name-only, accent+space normalized. Duplicate: a
  // new client matching an existing client on name AND email. window.confirm →
  // OK proceeds with the save (svGen), Cancel aborts. Purely assistive — never an
  // authoritative "no conflict" clearance.
  const saveEntityGuarded = (sk: any, arr: any, fn: any, d: any, ext = {}) => {
    const proceed = () => saveEntity(sk, arr, fn, d, ext);
    // Phase 43 CORRECTION (items 2 + 5) — new-adds only. Matcher is the shared
    // normalized SUBSTRING name match (item 5, so "helios" ↔ "Helios NV"); the
    // warning is an APP-styled OK/Cancel dialog (item 2), not window.confirm.
    // OK proceeds with the save, Cancel aborts. Assistive, never authoritative.
    if (!d.id) {
      const party = { ...d, ...ext };
      let msg = null;
      if ((sk === SK.op || sk === SK.op2) && findNameMatches(party, clients).length) {
        msg = t("party.warn.may_be_client", {
          fallback: "That adversary may have been a client",
        });
      } else if (sk === SK.cl && findNameMatches(party, clients).length) {
        msg = t("client.warn.already_client", {
          fallback: "That person already seems to be a client",
        });
      }
      if (msg) {
        setCoiPrompt({ msg, onConfirm: proceed });
        return;
      }
    }
    proceed();
  };

  const saveCase = () => {
    const d = form;
    console.log("svCa called, form data:", JSON.stringify(d));
    if (!d.title || !d.ref) {
      console.log("Case validation failed: title=", d.title, "ref=", d.ref);
      return;
    }
    const cd = {
      ...d,
      clIds: d.clIds || [],
      opIds: d.opIds || [],
      ocuIds: d.ocuIds || [],
      exIds: d.exIds || [],
      cpIds: (d.cpIds || []).filter((id: any) => contactPersons.some((p: any) => p.id === id)),
      juId: d.juId ? +d.juId : null,
      docs: d.docs || [],
      status: d.status || "active",
    };
    if (d.id) {
      persist(
        SK.ca,
        cases.map((c: any) => (c.id === d.id ? { ...c, ...cd } : c)),
        setCases,
      ).then((r: any) => {
        if (r.ok) toast(t("toast.case_updated", { fallback: "Case updated" }));
      });
    } else {
      persist(SK.ca, [...cases, { ...cd, id: tempId() }], setCases).then((r: any) => {
        if (r.ok) toast(t("toast.case_added", { fallback: "Case added" }));
      });
    }
    cm();
  };
  const saveTimeEntry = () => {
    const d = form;
    if (!d.caId || !d.coId || !d.min) return;
    const e = {
      ...d,
      caId: +d.caId,
      coId: +d.coId,
      min: Math.round(+(d.min || 0)), // 175 §1 — de-rounded with the live editors (this fn has no callers, :8201)
      billed: d.billed || false,
    };
    const _teSave = d.id
      ? persist(
          SK.te,
          timeEntries.map((t: any) => (t.id === d.id ? { ...t, ...e } : t)),
          setTimeEntries,
        )
      : persist(SK.te, [...timeEntries, { ...e, id: tempId() }], setTimeEntries);
    _teSave.then((r: any) => {
      if (r.ok)
        toast(
          d.id
            ? t("toast.updated", { fallback: "Updated" })
            : t("toast.logged", { fallback: "Logged" }),
        );
    });
    cm();
  };
  /* Phase 29 (item 1.iii) — reusable event save. The only LIVE event-save
     logic used to be the ag modal's onS closure (the old svAg here was dead
     code with zero callers); the compact column's quick-add needs the same
     path, so the closure body now lives in this function and the modal calls
     it. Behavior-neutral for the modal: same validation, same UI-only field
     strip, same skipTemplates-on-create, same toast. Returns true when saved
     so callers can close the modal / clear the quick-add input. */
  /* ── BRIEF 120 §2 — ONE CLICK, ONE ROW, EVEN WHILE THE CREATE IS IN FLIGHT ──
     §2 asked which of three things "Save & EXIT" does after a "Save & invite".
     MEASURED at the wire: (c), a SECOND CREATE — two `POST /api/agenda`, two
     rows for one event, 118 §6b's defect arriving by another door. §1 removes
     the door it came through, but not the window: `saveAgendaEvent` returns as
     soon as it has queued the persist, so for one round trip the modal is still
     mounted, `form.id` is still empty, and every primary on it still takes the
     CREATE branch. Driven with the POST held 2500ms, §1 alone still yielded two
     rows.

     THE BRIEF NAMES THE CAUSE AS "the form taking the saved row's id", AND THAT
     IS THE ONE FIX THAT CANNOT WORK HERE: at the moment of the second click the
     id does not exist yet — it is what the outstanding request will return — and
     writing it back when it arrives would land AFTER `cm()` has cleared the
     form, resurrecting a closed modal's state. So the guard is the create's own
     flight, which is the thing that is actually true at the second click.

     Only the CREATE branch is guarded: a second PUT rewrites a row, it does not
     mint one. Cleared when the persist settles and again in `cm()`, so the next
     modal — a spawn, a quick-add, anything — opens unarmed. The refusal is
     silent, exactly as a double-submit refusal is: it returns the same `false`
     an empty title returns, and `chainGo` then leaves the modal open rather
     than closing it on a save that did not happen. */
  const agCreateInFlight = useRef(false);
  /* 195 §B.0 — the Archive's "tell me when the restore has saved": set by the
     opener (the Archive's Restaurer on an event), read ONCE by the save's
     restore arm below — captured before the modal closes, since `cm()` runs
     before the PUT resolves — and cleared by both. */
  const archiveRestoreRef = useRef<Dispatch<unknown> | null>(null);
  const saveAgendaEvent = (d: any, opts?: SaveRowOpts) => {
    if (!d.title || !d.date) return false;
    /* 195 §B — the id read ONCE (the ratchet counts every read off `any`) */
    const rowId = (d.id ?? null) as number | null;
    if (!rowId && agCreateInFlight.current) return false;
    /* Corr-2 (item 8) — the meeting-machinery UI fields are REQUEST fields,
       not row fields: they ride the POST/PUT body (the route reads them and
       persists only their consequences — joinUrl, the reservation) and are
       stripped back out of client state after the save resolves. */
    /* 184 B.4 — `_dropEml` (the dropped e-mail's File / Message-ID) is
       `chainGo`'s second act, stripped first for the reason every name in this
       destructure is. */
    const {
      _locManual: _,
      /* BRIEF 124 §1 — the same class as `_locManual` beside it: a form-local
         flag saying the user has ANSWERED the mode, so the type's declared
         default stops being applied. Never a row key — the ANSWER is
         `meetingMode`, and that is what the wire carries. */
      _modeManual: _mm,
      _caseFixed: _cf2,
      templatesOn: _to,
      _meetInit: _mi,
      _roomLoaded: _rl,
      roomPlaceId: _rp,
      roomOverride: _ro,
      platform: _pl,
      meetingUrl: _mu,
      /* BRIEF 117 §6b — was a REQUEST field, exactly like the four above it.
         137 §4(b) RETIRED IT: no route reads it any more (Graph never carries
         attendees), and no screen has set it since 120 §3. The strip stays as
         a row-shape guard — it is not a column and must not become one — but
         nothing re-adds it to the request below. */
      inviteeIds: _inv,
      /* BRIEF 119 §1b — the same class again: a REQUEST field the route reads to
         decide whether to mint, never a row key. "Save & invite" sets it so the
         event's own Graph leg stands down and the dialog that opens next is the
         one meeting this event gets. */
      deferMeeting: _dm,
      /* BRIEF 120 §3 — the merged modal's invitee roster and its internal flag.
         UI state, not row fields and not even request fields: the invitation
         travels on `POST /agenda/meeting`, never on the event's own save. They
         are stripped here for the reason every name above them is — everything
         this destructure does not catch is spread into `i2`, which becomes both
         the client row and the POST body. */
      _attendees: _at,
      _attendeesFor: _af,
      /* 122 §3b — the baseline the send confirmation measures its counts
         against. UI state in the same class as the two above it: it says what
         the user was SHOWN when the modal opened, which is not a row field and
         not a request field. */
      _attendeesBase: _ab,
      _internal: _in,
      /* 195 §B.0 — the Archive's opener marks the form: this row is ARCHIVED
         and its save is its restore. A form flag, never a row key. */
      _restoreFromArchive: _rfa,
      ...fdata
    } = withoutDropEml(d);
    /* 123 §3 — the two facts, in the ONE place every event save passes through.
       `mode` is null for an event whose type is not one of the firm's meeting
       types, so every branch below is false for an ordinary event exactly as
       the old `kind` was — and `meetingMode` is sent EXPLICITLY on both roads:
       the mode the toggle chose when the type is a meeting type, and null when
       it is not, which is what releases the mode on a type-flip away from one.
       (`DEFAULT_MEETING_MODE` is the read-side default; writing it down is what
       makes a new event's row say Physical rather than leave every future
       reader to infer it.) */
    const isMeeting = evIsMeeting(d.type);
    /* 124 §1 — the SAME derivation the toggle displays (`evMode`), so what the
       user saw is what gets written. Reading `meetingModeOf` here while the
       modal read the type's default would save Physical on an event whose
       toggle said Online — the exact divergence 123 §2 gave the column to
       prevent. */
    const mode = isMeeting ? evMode(d) : null;
    const plat =
      mode === "online"
        ? _pl ||
          (mtgPlatforms.teams ? "teams" : mtgPlatforms.zoom ? "zoom" : "gmeet")
        : null;
    const link =
      mode === "online" && plat !== "teams"
        ? String(_mu ?? "").trim()
        : undefined;
    const i2 = {
      ...fdata,
      /* 184 A.1 — the collective half, normalised as `caId` always was
         (`d.caId ? +d.caId : null`): the modal's picker now writes `ccId: ""`
         for a case pick (its twin in TaskModal always has), and `""` is not a
         value an `Int?` column takes. One normaliser for both. */
      caId: matterIdOrNull(d.caId),
      ccId: matterIdOrNull(d.ccId),
      ...(rowId ? {} : { skipTemplates: true }),
      meetingMode: isMeeting ? mode || DEFAULT_MEETING_MODE : null,
      /* room: {placeId,override} = ensure · null = release · absent =
         untouched. Release on edit only after the prefill resolved
         (_roomLoaded) so a fast save can never strip a binding it has not
         seen; a flip to ONLINE (or off a meeting type) releases via the
         mode!=person branch, which is what the old kind!=room branch did for a
         flip away from REU. */
      ...(mode === "person"
        ? _rp
          ? { room: { placeId: +_rp, override: !!_ro } }
          : rowId && _rl
            ? { room: null }
            : {}
        : rowId
          ? { room: null }
          : {}),
      ...(mode === "online" && !rowId ? { platform: plat } : {}),
      /* 117 §6b's `inviteeIds` IS NO LONGER SENT — 137 §4(b) removed the only
         thing that read it (`routes/agenda.js`'s Teams leg), because Graph
         mails an attendee array at save and invitations now follow the people
         on the event into ONE Outlook draft (§4c). It had already been dead
         since 120 §3 removed the only screen that set `form.inviteeIds`, so
         this guard has been false on every save for eighteen briefs. The
         destructure above still strips the name, which is a row-shape guard
         and costs nothing. */
      ...(link !== undefined && (rowId || link) ? { meetingUrl: link } : {}),
      /* §1b — sent only on the CREATE path, which is the only branch that mints
         (routes/agenda.js:478). On an edit the flag has nothing to skip and
         sending it would be noise the route would have to explain. */
      ...(_dm && !rowId ? { deferMeeting: true } : {}),
    };
    /* ── 195 §B.0 [RULED — Laurent, 2026-09-18, choosing (b)] — RESTORING AN
       ARCHIVED EVENT IS THIS SAVE. The row is not in `agendaEvents` (the live
       list holds no archived row), so the diff engine below could neither
       update it nor be allowed to create it; the ordinary PUT is sent
       DIRECTLY with `archived: false` beside the same body every edit sends,
       and the route makes the un-archive and the edit ONE row write, then
       mints the calendar copy on its own late-create arm (routes/agenda.js).
       ONE call: a failure leaves the row archived and untouched, the toast
       says so, and Restaurer can be pressed again. On success the row joins
       the live list here and the Archive drops it (the ref). Cancel never
       reaches this function, so nothing moves until he saves. The toast and
       the request-field strip below are the ordinary save's, shared. */
    let outcome: Promise<unknown>;
    if (_rfa && rowId) {
      const done = archiveRestoreRef.current;
      archiveRestoreRef.current = null;
      outcome = updateAgendaEvent(rowId, { ...i2, archived: false }).then(
        (served: unknown) => {
          const row = (served && typeof served === "object" && "id" in served ? served : { ...i2, id: rowId, archived: false }) as { id: number };
          setAgendaEvents((prev: unknown) => mergeRestoredRow(prev, row));
          if (done) done(row);
          return { ok: true };
        },
        (e: unknown) => {
          _apiErr("update", e);
          return { ok: false };
        },
      );
    } else {
      let newAg;
      if (rowId) {
        newAg = agendaEvents.map((a: any) => (a.id === rowId ? { ...a, ...i2 } : a));
      } else {
        newAg = [...agendaEvents, { ...i2, id: tempId() }];
      }
      /* 111 §5b — opts (onCreatedRow) threads through for the link chain. */
      if (!rowId) agCreateInFlight.current = true;
      outcome = persist(SK.ag, newAg, setAgendaEvents, opts).finally(() => {
        // §2 — settled, not resolved: a create that FAILED must not leave the
        // form permanently unable to try again.
        if (!rowId) agCreateInFlight.current = false;
      });
    }
    outcome.then((r: any) => {
      if (r.ok) {
        /* 123 §5 — "Updated" / "Added" were the GENERIC modal toasts, shared
           with every other entity, and they said nothing about what was saved.
           An event's own words, and the same derivation the SAVE BUTTON reads
           (`eventSaveWords`) so a button and its toast cannot describe two
           different acts. `toast.updated`/`toast.added` stay seeded and keep
           their other call sites.
           137 §4(d) — `hasMeeting`, `mode` and `app` are gone from the
           argument: they only ever separated the invite wording, and there is
           one act now. */
        toast(t(eventSaveWords({ isNew: !rowId }).toastPlainKey));
        // Strip the request-only fields the update-merge kept on the row.
        setAgendaEvents((prev: any) =>
          prev.map(
            ({
              room: _r2,
              platform: _p2,
              meetingUrl: _m2,
              inviteeIds: _i2,
              deferMeeting: _dm2,
              ...rest
            }: any) => rest,
          ),
        );
      }
    });
    return true;
  };
  /* Phase 10 — unified stage save. `f.ccId` routes the stage to a collective
   case; otherwise `f._ci` routes it to a Case. `f._si` = stage id on edit. */
  /* 109 §7d — the save's core, extracted so "Save and create Event" can run
     the SAME save and then act on the SAVED row. The id was always computed
     BEFORE the persist (`d._si || nid(...)`), so surfacing the row is a
     return statement, not new machinery. Returns null when validation stops
     the save (the caller then leaves the modal open, exactly as before). */
  const saveStageCore = () => {
    const d = form;
    if (!d.type) return null;
    /* 111 §4c — TYPE is the only required field. The startDate refusal that
       stood here is dropped: the write path below already defaults every
       other field (`|| ""` / null), so a dateless stage saves clean. The
       `toast.start_date_required` key stays seeded (D28). */
    const isCc = !!d.ccId;
    if (!isCc && !d._ci) return null;
    const ci = +(isCc ? d.ccId : d._ci);
    const pool = isCc ? collectiveCases : cases;
    const stg = {
      id: d._si || nid(pool.flatMap((c: any) => c.stages || [])),
      type: d.type,
      registerNumber: d.registerNumber || "",
      juId: d.juId ? +d.juId : null,
      startDate: d.startDate || "",
      by: d.by || "",
      notes: d.notes || "",
    };
    const apply = (arr: any) =>
      arr.map((c: any) =>
        c.id === ci
          ? {
              ...c,
              stages: d._si
                ? (c.stages || []).map((st: any) =>
                    st.id === d._si ? { ...st, ...stg } : st,
                  )
                : [...(c.stages || []), stg],
            }
          : c,
      );
    (isCc ? persist(SK.cc, apply(collectiveCases), setCollectiveCases) : persist(SK.ca, apply(cases), setCases)).then((r: any) => {
      if (r.ok) toast(t("toast.saved", { fallback: "Saved" }));
    });
    return {
      stg,
      ci,
      isCc,
      docs:
        pool.find((c: { id?: number; docs?: unknown[] }) => c.id === ci)
          ?.docs || [],
    };
  };
  /* 111 §5b — `saveStage` retired: the stage modal's Save is chainStage(null)
     now (identical when un-chained — saveStageCore + cm — and link-aware when
     the modal is part of a chain session). */
  /* ── 111 §5b — THE LINK CHAIN ─────────────────────────────────────────────
     (109 §7d's saveStageAndEvent generalized: every Stage/Task/Event modal
     can spawn every kind, and the chain records ALL-PAIRWISE links.)

     A chain SESSION carries the set of items it has touched, seeded with the
     item whose modal started it; every chained save links the NEW item to
     EVERY item already in the set (S → E → T yields S–E, S–T AND E–T), then
     joins it. The set dies with the chain: Cancel or a plain Save ends it,
     and a later chain starts fresh (RULED 2026-08-21). No sync ever —
     prefill only (RULED, v-e).

     The id problem, and why spawn accepts a promise: a created task/event
     gets its REAL id from the server (persist's id-swap); the optimistic nid
     guess must never reach the store. `onCreatedRow` (persist, this brief)
     resolves it; a stage's id is client-minted and is sync. The pairwise
     POSTs ride the resolved id; a user cannot reach the next modal's save
     before the previous create returns in practice, and a link that fails
     (offline) warns rather than blocking the save it decorates. */
  const linkChainRef = useRef<null | {
    matterKind: "ca" | "cc";
    matterId: number;
    items: { k: "stage" | "task" | "event"; id: number }[];
  }>(null);
  type MatterLite = { id: number; itemLinks?: unknown };
  type ChainRow = {
    id?: number;
    title?: string;
    dueDate?: string;
    date?: string;
    [k: string]: unknown;
  };
  const _linkPatchMatter = (
    matterKind: "ca" | "cc",
    matterId: number,
    itemLinks: ItemLink[],
  ) => {
    if (matterKind === "cc")
      setCollectiveCases((prev: MatterLite[]) =>
        prev.map((c) => (c.id === matterId ? { ...c, itemLinks } : c)),
      );
    else
      setCases((prev: MatterLite[]) =>
        prev.map((c) => (c.id === matterId ? { ...c, itemLinks } : c)),
      );
  };
  /* The matter a modal form is bound to — task forms carry caseId/ccId, event
     forms caId/ccId, stage forms _ci/ccId. Null = unbound (no chain). */
  const _linkMatterOf = (f: {
    ccId?: number | string | null;
    caseId?: number | string | null;
    caId?: number | string | null;
    _ci?: number | string | null;
  }): { matterKind: "ca" | "cc"; matterId: number } | null =>
    f.ccId
      ? { matterKind: "cc", matterId: +f.ccId }
      : f.caseId || f.caId || f._ci
        ? { matterKind: "ca", matterId: +(f.caseId || f.caId || f._ci || 0) }
        : null;
  const _linkRowsOfMatter = (
    m: { matterKind: string; matterId: number } | null,
  ): ItemLink[] => {
    if (!m) return [];
    const pool: MatterLite[] = m.matterKind === "cc" ? collectiveCases : cases;
    const row = pool.find((c) => c.id === m.matterId);
    return Array.isArray(row?.itemLinks) ? (row.itemLinks as ItemLink[]) : [];
  };
  /* §5b's prefill map (the design's small map): stage→X rides stageAutofill
     (4e's date rule: an absent startDate fills NOTHING — the event opens
     dateless) + the stageId stamp (the 109 pill binding, kept beside the
     store per §5a's (iii)); task/event→X prefills title, matter and
     date-if-present; →stage takes only matter + date-as-startDate (a stage
     has no title). */
  const _chainPrefill = (
    next: "stage" | "task" | "event",
    fromK: "stage" | "task" | "event",
    row: ChainRow,
    matter: { matterKind: "ca" | "cc"; matterId: number },
    docs: unknown[],
  ) => {
    const out: Record<string, unknown> =
      next === "stage"
        ? matter.matterKind === "cc"
          ? { ccId: matter.matterId }
          : { _ci: matter.matterId }
        : {
            ...(matter.matterKind === "cc"
              ? { ccId: matter.matterId }
              : next === "event"
                ? { caId: matter.matterId }
                : { caseId: matter.matterId }),
            _caseFixed: true,
          };
    if (fromK === "stage" && next !== "stage") {
      const _fill = stageAutofill(
        row as Parameters<typeof stageAutofill>[0],
        next,
        jurisdictions,
        docs as Parameters<typeof stageAutofill>[3],
        caseStageEntries as readonly StageTaxEntry[],
      );
      /* 166 §D — A NEW TASK IS NOT NAMED FOR IT. See the note on the `else`
         arm below: Laurent's ruling is that a task OPENS WITH AN EMPTY TITLE,
         whatever the chain came from, so the stage's composed name (109 §7e —
         type name + register number) reaches a chained EVENT and no longer
         reaches a chained task. Every other field `stageAutofill` returns —
         dueDate, location, notes, the stage's filed documents — is untouched.
         `StageLinkField.tsx:190`, the OTHER caller, is deliberately not
         changed: there a user has picked a stage in the "linked stage" field
         and asked to be filled from it, which is an act, not a modal
         opening. */
      if (next === "task") delete _fill.title;
      Object.assign(
        out,
        _fill,
        { stageId: row.id },
        next === "event" ? { _stageSync: "linked" } : {},
      );
    } else if (fromK !== "stage") {
      const date = String((fromK === "task" ? row.dueDate : row.date) || "").slice(0, 10);
      if (next === "stage") {
        if (date) out.startDate = date;
      } else {
        /* ── 166 §D — A NEW TASK IS NOT NAMED AFTER ITS MATTER ──────────────
           MEASURED, and this line is the site. Laurent's screenshot showed a
           New task modal on a collective case opening titled
           "Bricart & c — CC-2026/003" — `matterEventTitle`'s exact shape
           (`matterLabel.ts:41`, title — ref). Two places in the tree composed
           that string into a form's title and BOTH were event modals: the `ag`
           arm's `_seedTitle` (App.tsx:8858, brief 117 §8 — a new event on a
           matter WAS named after the matter) and the event modal's own matter
           picker (`AgendaEventModal.tsx:642`) — both withdrawn by 191 D.0 (the
           matter travels at the wire now). No `openModal("tk", …)` call
           site passes a title at all — censused, seven of them, and two pass
           `title: ""` out loud — and the `tk` arm falls to the bare
           `setForm(e ? normFormDates(e) : {})`. So the only way an event's
           name reached a task was this line, copying the source row's title
           across the chain.

           Laurent's ruling, 2026-09-04: events are named after their matter,
           tasks are not — a task opens with an empty title, and the save
           already refuses an empty one (`TaskModal.tsx:165`). The DATE prefill
           stays: it is the chain's whole point. */
        if (row.title && next !== "task") out.title = row.title;
        if (date) out[next === "task" ? "dueDate" : "date"] = date;
      }
    }
    return out;
  };
  const LINK_MODAL_KEY = { stage: "stg", task: "tk", event: "ag" } as const;
  /* The one chain move: save the current modal's item, record its pairwise
     links if this modal is part of a session, then either open the next
     modal (session continues, this item in the set) or end the session.
     `save()` returns null when its own validation refused (the modal stays
     open, exactly as before), else the saved row's identity — id sync for
     stages, a promise off onCreatedRow for created tasks/events. */
  /* 184 B.4 — THE `.eml`, THE SECOND ACT. Runs inside `chainGo`'s async leg
     once the created row's real id is known (`saved.idSync ?? await
     saved.idAsync` — 111 §5b's `onCreatedRow`), so the task or event exists
     either way and the filing is never a condition of the first act. On 201
     the doc row joins the parent's `docs` in state by id (the server also
     publishes the parent; the id check keeps a live upsert from doubling it);
     on any failure ONE toast — the Outlook link `_dropDocs` seeded is already
     on the row, and the sentence says so. */
  const fileDroppedEml = async (
    kind: "task" | "event",
    id: number,
    dropEml: DropEml,
  ) => {
    try {
      const row: EmlDocRow = await postEmlDoc(kind, id, dropEml);
      const append = (docs: unknown[] | undefined): unknown[] => {
        const list = Array.isArray(docs) ? docs : [];
        return list.some((d) => (d as { id?: unknown } | null)?.id === row.id) ? list : [...list, row];
      };
      const patch = (prev: DocParentRow[]): DocParentRow[] =>
        prev.map((x) => (x.id === id ? { ...x, docs: append(x.docs) } : x));
      if (kind === "task") setTasks(patch);
      else setAgendaEvents(patch);
    } catch {
      toast(
        (t as TFn)("emailDrop.eml_not_filed", {
          fallback:
            "The e-mail file could not be saved in the casefile — only the Outlook link was added",
        }),
        "error",
      );
    }
  };
  const chainGo = (
    selfK: "stage" | "task" | "event",
    form_: { _chain?: unknown; _dropEml?: DropEml | null },
    next: "stage" | "task" | "event" | null,
    save: () => null | {
      idSync?: number;
      idAsync?: Promise<number | null>;
      row: ChainRow;
      docs?: unknown[];
      matter: { matterKind: "ca" | "cc"; matterId: number } | null;
    },
  ): boolean => {
    const saved = save();
    if (!saved) return false;
    const matter = saved.matter;
    const sess =
      form_._chain &&
      matter &&
      linkChainRef.current &&
      linkChainRef.current.matterId === matter.matterId &&
      linkChainRef.current.matterKind === matter.matterKind
        ? linkChainRef.current
        : null;
    const nextSess =
      next && matter
        ? sess || { matterKind: matter.matterKind, matterId: matter.matterId, items: [] }
        : null;
    (async () => {
      if (!matter) return;
      const id = saved.idSync != null ? saved.idSync : await saved.idAsync;
      if (id == null) return;
      /* 184 B.4 — the dropped e-mail's `.eml`, filed under the row that now
         exists. Fire-and-forget beside the link leg: neither waits for the
         other, and a refused filing cannot undo a recorded link. */
      if ((selfK === "task" || selfK === "event") && form_._dropEml) {
        void fileDroppedEml(selfK, id, form_._dropEml);
      }
      const item = { k: selfK, id: Number(id) };
      if (sess) {
        for (const prior of sess.items) {
          try {
            const r = await createItemLink(matter.matterKind, matter.matterId, item, prior);
            if (r && r.itemLinks) _linkPatchMatter(matter.matterKind, matter.matterId, r.itemLinks);
          } catch (e) {
            console.warn(
              "[linkChain] link failed:",
              e instanceof Error ? e.message : String(e),
            );
          }
        }
      }
      if (nextSess) nextSess.items.push(item);
    })();
    cm();
    linkChainRef.current = nextSess;
    if (next && matter) {
      openModal(LINK_MODAL_KEY[next], {
        ..._chainPrefill(next, selfK, saved.row, matter, saved.docs || []),
        _chain: 1,
      });
    }
    return true;
  };
  /* Unlink all — confirm-free, Undo toast (the house removal shape:
     useConfirmAndUndo with label:null skips the dialog). The removed rows
     come back from the API; Undo re-POSTs each pair. */
  const _linkUndoStash = useRef<ItemLink[] | null>(null);
  const unlinkAllOf = (
    item: { k: "stage" | "task" | "event"; id: number },
    matter: { matterKind: "ca" | "cc"; matterId: number },
  ) => {
    confirmAndUndo({
      label: null,
      toastName: t("links.unlinked_toast", { fallback: "Links removed" }),
      onConfirm: async () => {
        try {
          const r = await unlinkItemLinks(matter.matterKind, matter.matterId, item);
          _linkUndoStash.current = r.removed || [];
          _linkPatchMatter(matter.matterKind, matter.matterId, r.itemLinks || []);
        } catch (e) {
          toast(e instanceof Error ? e.message : String(e), "error");
        }
      },
      undo: {
        snapshot: null,
        restore: async () => {
          const rows = _linkUndoStash.current || [];
          for (const lk of rows) {
            try {
              const r = await createItemLink(matter.matterKind, matter.matterId, lk.a, lk.b);
              if (r && r.itemLinks) _linkPatchMatter(matter.matterKind, matter.matterId, r.itemLinks);
            } catch (e) {
              console.warn(
                "[linkChain] undo failed:",
                e instanceof Error ? e.message : String(e),
              );
            }
          }
        },
      },
    });
  };
  /* The stage modal's chain handlers (the task/event ones live at their
     modals' call sites — their validation is modal-local). */
  const chainStage = (next: "stage" | "task" | "event" | null) =>
    chainGo("stage", form, next, () => {
      const r = saveStageCore();
      if (!r) return null;
      return {
        idSync: r.stg.id,
        row: r.stg,
        docs: r.docs,
        matter: { matterKind: r.isCc ? "cc" : "ca", matterId: r.ci },
      };
    });
  /* The task chain: saveTask's own title guard refuses, the modal's toast is
     mirrored here so the refusal still speaks; a created task's id arrives
     via onCreatedRow (15s guard so an offline create cannot strand the
     promise). The completion-pend flow (edit → completed) stays the modal's
     and BEATS a chain click — the prompt path closes the modal on its own
     terms, so a spawn during completion falls back to the plain flow
     (enumerated). */
  const chainTask = (next: "stage" | "task" | "event" | null) =>
    chainGo("task", form, next, () => {
      if (!form.title) {
        toast(t("toast.title_required", { fallback: "⚠ Title is required" }));
        return null;
      }
      const matter = _linkMatterOf(form);
      if (form.id) {
        if (!saveTask(form)) return null;
        return { idSync: +form.id, row: form, matter };
      }
      let resolveId: (v: number | null) => void = () => {};
      const idAsync = new Promise<number | null>((res) => (resolveId = res));
      const ok = saveTask(
        { ...form, skipTemplates: true },
        {
          onCreatedRow: (row) =>
            resolveId(row && row.id != null ? Number(row.id) : null),
        },
      );
      if (!ok) return null;
      setTimeout(() => resolveId(null), 15000);
      return { idAsync, row: form, matter };
    });
  /* The event chain: the modal runs its meeting guards FIRST (they stay
     modal-local, beside the state they read), then hands over here. */
  /* `onSaved` — BRIEF 137 §4(c). The invitation draft is composed AFTER the
     save, from the row's own id, and this is the one place that knows it on
     both branches: sync on an edit, through `onCreatedRow` on a create.

     IT HANGS HERE AND NOT ON `chainGo` because `chainGo`'s id resolution is
     inside its link leg and returns early on a matter-less event — and a
     matter-less event is exactly the internal meeting, which must still be
     able to answer "no externals, no draft" rather than never being asked.

     ONE SAVE, so it also closes an asymmetry §4(d) would otherwise carry
     forward: the retired "Save & invite" called `saveAgendaEvent` directly and
     never recorded the chain's pairwise links, while the plain save did. There
     is one road now and it does both. */
  const chainEvent = (
    next: "stage" | "task" | "event" | null,
    onSaved?: (id: number) => void,
  ) =>
    chainGo("event", form, next, () => {
      const matter = _linkMatterOf(form);
      if (form.id) {
        if (!saveAgendaEvent(form)) return null;
        onSaved?.(+form.id);
        return { idSync: +form.id, row: form, matter };
      }
      let resolveId: (v: number | null) => void = () => {};
      const idAsync = new Promise<number | null>((res) => (resolveId = res));
      const ok = saveAgendaEvent(form, {
        onCreatedRow: (row) => {
          const id = row && row.id != null ? Number(row.id) : null;
          resolveId(id);
          if (id != null) onSaved?.(id);
        },
      });
      if (!ok) return null;
      setTimeout(() => resolveId(null), 15000);
      return { idAsync, row: form, matter };
    });
  /* ── BRIEF 137 §4(c) — THE DRAFT, AFTER THE SAVE ───────────────────────────
     `POST /agenda/:id/invite-draft` composes one Outlook draft in the user's
     OWN Drafts — externals in `to`, colleagues in `cc`, the event's .ics
     attached — and returns its `webLink`. LexCRM never sends it.

     `win` IS OPENED BY THE CALLER, SYNCHRONOUSLY, INSIDE THE CLICK. That is
     `runEmailReply`'s shape (`lib/emailArchiveClient.ts:92-111`) and the reason
     is popup blockers: a `window.open` after an await has lost the user
     activation that authorised it. Here there are two awaits between the click
     and the link — the save's persist and this POST — which makes the
     placeholder more necessary, not less. On any failure the placeholder is
     closed, so a refused draft leaves no orphan tab.

     THE MODAL HAS ALREADY CLOSED by the time this resolves (`chainGo` calls
     `cm()` synchronously), which is why the feedback is a toast and a tab
     rather than anything on the form. */
  const openInviteDraft = async (
    eventId: number,
    rcpt: { to: string[]; cc: string[] },
    win: Window | null,
  ) => {
    try {
      const d = (await api.post(`/agenda/${eventId}/invite-draft`, rcpt)) as {
        webLink?: string | null;
      } | null;
      if (d && d.webLink) {
        if (win) win.location.href = d.webLink;
        else window.open(d.webLink, "_blank", "noopener");
        return;
      }
      /* A 200 with no link is not a success worth a tab. It is the shape the
         stub returns if it is ever changed to withhold one, and closing is the
         honest answer to "there is nothing to show". */
      try {
        win?.close();
      } catch {
        /* a window the browser already closed */
      }
    } catch (err: unknown) {
      try {
        win?.close();
      } catch {
        /* a window the browser already closed */
      }
      const e = (err || {}) as { code?: string; message?: string };
      if (e.code === "m365.not_connected") {
        toast(
          t("modal.meeting.m365_not_connected", {
            fallback:
              "Microsoft 365 is not connected — connect it in Settings to create meetings.",
          }),
          "error",
        );
        return;
      }
      console.error("[agenda] invitation draft failed:", err);
      /* `e.message` is already the resolved `srvErr.<code>` sentence (api.ts
         :202-209), so the key below is the last resort rather than the usual
         path. THE EVENT IS SAVED EITHER WAY — the draft is a separate act and
         its failure must not read as a lost event. */
      toast(
        e.message ||
          t("modal.meeting.draft_failed", {
            fallback: "The event was saved, but the invitation draft could not be created",
          }),
        "error",
      );
    }
  };
  /* Per-modal footer state: Unlink all lights only when the OPEN item already
     has links in its matter's store (a NEW item has no id and no links);
     the spawn buttons grey out on an unbound form — the store is
     matter-scoped, so an item bound to no matter has nowhere to record.
     112 §7 — "Link…" (the Links manager) disables on the UNSAVED form like
     Unlink all (no id, nothing to link) but NOT on linklessness: a saved
     item with zero links is exactly the one the manager exists for. */
  const linkStateFor = (k: "stage" | "task" | "event") => {
    const item =
      k === "stage"
        ? form._si
          ? { k: "stage" as const, id: +form._si }
          : null
        : form.id
          ? { k, id: +form.id }
          : null;
    const matter = _linkMatterOf(form);
    const has =
      !!item &&
      !!matter &&
      _linkRowsOfMatter(matter).some(
        (l) =>
          (l.a.k === item.k && +l.a.id === item.id) ||
          (l.b.k === item.k && +l.b.id === item.id),
      );
    return {
      unlinkDisabled: !has,
      spawnDisabled: !matter,
      onUnlinkAll: () => {
        if (item && matter) unlinkAllOf(item, matter);
      },
      linkDisabled: !item || !matter,
      onLinkManager: () => {
        if (item && matter) setLinksMgr({ item, matter });
      },
    };
  };
  /* 112 §7 — the Links manager's target: the open item + its matter. Set by
     the footer's "Link…", cleared on close. The dialog itself is dumb; the
     resolution (labels as the pills resolve them, candidates minus self /
     already-linked / archived) and both writes live here beside the other
     link plumbing. */
  const [linksMgr, setLinksMgr] = useState<{
    item: { k: "stage" | "task" | "event"; id: number };
    matter: { matterKind: "ca" | "cc"; matterId: number };
  } | null>(null);
  const saveDocument = () => {
    const d = form;
    if (!d.name || !d.link || !d._ci) return;
    const ci = +d._ci;
    const doc = {
      id: d._di || nid(cases.flatMap((c: any) => c.docs || [])),
      name: d.name,
      link: d.link,
      date: d.date || new Date().toISOString().slice(0, 10),
      type: d.type || "other",
    };
    const newCa = cases.map((c: any) =>
      c.id === ci
        ? {
            ...c,
            docs: d._di
              ? (c.docs || []).map((dd: any) =>
                  dd.id === d._di ? { ...doc, id: dd.id } : dd,
                )
              : [...(c.docs || []), doc],
          }
        : c,
    );
    persist(SK.ca, newCa, setCases).then((r: any) => {
      if (r.ok) toast(t("toast.saved", { fallback: "Saved" }));
    });
    cm();
  };

  const del = (sk: any, arr: any, fn: any, id: any) => {
    persist(
      sk,
      arr.filter((i: any) => i.id !== id),
      fn,
    ).then((r: any) => {
      if (r.ok) toast(t("toast.deleted", { fallback: "Deleted" }));
    });
    setDetailId(null);
    setConfirmDelete(null);
  };

  // Phase 36 (D) — guarded client delete. The server refuses (409) a hard
  // delete when the client is linked (cases / invoices / …) or its folder holds
  // content, and we offer archive instead. We bypass the generic s() engine so
  // the 409 surfaces as a structured choice rather than a restore-and-toast.
  const delClient = async (id: any) => {
    setConfirmDelete(null);
    try {
      await deleteClient(id);
      setClients((prev: any) => (Array.isArray(prev) ? prev.filter((c) => c.id !== id) : prev));
      toast(t("toast.deleted", { fallback: "Deleted" }));
      setDetailId(null);
    } catch (e: any) {
      if (e?.code === "client.delete_blocked" || e?.status === 409) {
        setClBlocked({ id, body: e?.body || null });
      } else {
        _apiErr("delete", e);
      }
    }
  };

  const doArchiveClient = async (id: any) => {
    try {
      const updated = (await archiveClient(id)) as Record<string, unknown>;
      setClients((prev: any) =>
        Array.isArray(prev)
          ? prev.map((c) => (c.id === id ? { ...c, ...updated } : c))
          : prev,
      );
      toast(t("toast.archived", { fallback: "Archived" }));
      setDetailId(null);
    } catch (e) {
      _apiErr("archive", e);
    }
    setClBlocked(null);
  };

  /* 189 §H — the client the one-to-one template modal is open on, or null. */
  const [mailTplClient, setMailTplClient] = useState<{ id: number; name?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null } | null>(null);
  const doUnarchiveClient = async (id: any) => {
    try {
      const updated = (await unarchiveClient(id)) as Record<string, unknown>;
      setClients((prev: any) =>
        Array.isArray(prev)
          ? prev.map((c) => (c.id === id ? { ...c, ...updated } : c))
          : prev,
      );
      toast(t("toast.unarchived", { fallback: "Unarchived" }));
    } catch (e) {
      _apiErr("unarchive", e);
    }
  };

  // Hotfix-B (Part B) — provision SharePoint folders for clients whose
  // clientRootFolder is NULL. Server-side the run is capped (<=50), spaced +
  // Retry-After-aware, re-runnable + idempotent; on staging (no M365) it returns
  // provisioned:0 / failed:N and creates nothing, still proving the route, the
  // limit, the NULL-folder selection and the duplicate-skip. The actual run lives
  // in doProvisionFolders below; this opener just gates the modal.
  //
  // Hotfix-B correction (ITEM 2) — OPEN the in-app provisioning modal (the old
  // native prompt() is gone). The provisionable set is folderless clients minus
  // (name+email) duplicate non-survivors; if that set is empty there is nothing
  // to do, so we toast instead of opening an empty modal.
  //
  // 176 §3.3 — AND IT TAKES A SCOPE. `restrictIds` absent (or not an array) is
  // the firm-wide list this has always opened; an array of client ids narrows
  // it to those, so the client-file pane's repair shows the one client its row
  // is about and not the firm's others.
  //
  // THE `Array.isArray` GUARD IS LOAD-BEARING, not defensive noise. The
  // clients-list button below is an `onClick`, and React hands a handler its
  // MouseEvent as the first argument — so `onClick={openProvisionModal}` would
  // silently pass an event where a scope is expected. That call site is written
  // `() => openProvisionModal()` for exactly this reason, and this line is what
  // keeps a future one from re-introducing it as an empty modal nobody can
  // explain.
  const openProvisionModal = (restrictIds?: unknown) => {
    /* Whether a scope was ASKED FOR is `Array.isArray`, and nothing else. An
       array that normalises to empty stays an empty scope — it does NOT become
       `null` — so a caller that asked to narrow can never accidentally widen. */
    const scope = Array.isArray(restrictIds)
      ? [...new Set(restrictIds.map(Number).filter((n) => Number.isFinite(n)))]
      : null;
    /* Asked with the scope, not from state: `setProvisionScope` below has not
       committed yet, so a state read here would answer about the PREVIOUS
       open. A scoped list can legitimately be empty — the client got a folder
       between render and click — and the existing sentence is right for that. */
    const list = provisionCandidateList(scope);
    if (!list.length) {
      /* 211 §12 — every client has a folder: the repair is the check that looks
         inside them (a scoped repair names one client, and its folder is there). */
      if (!scope) {
        setSubfolderCheck("client");
        return;
      }
      toast(
        t("toast.no_missing_folders", {
          fallback: "No clients are missing a folder.",
        }),
      );
      return;
    }
    setProvisionScope(scope);
    /* The ticked set is decided HERE, because this is the only thing that opens
       the modal. Unscoped starts empty, exactly as it always has — a prior run's
       ids, now provisioned and off the list, never carry over. A SCOPED open
       starts fully ticked: the scope IS the user's selection, since they clicked
       one row's repair rather than picking from a list. `list` is the one the
       toast test above already computed, so nothing is recomputed. */
    setProvisionChecked(scope ? new Set(list.map((c: any) => c.id)) : new Set());
    setProvisionDlg(true);
  };
  // Run the backfill on the SELECTED client ids (through the Part C throttle,
  // server-side). Clamped to 50 defensively; the modal already caps selection.
  // Phase 41 (D8b — fire-and-poll) — completion handler shared by the terminal
  // 'backfill'/'done' SSE poke (normal path) and the defensive sync-summary path
  // (an older server that still returns the summary in the response). This is
  // the ONLY place backfillBusy is cleared on a normal run: the launch control
  // stays disabled until the batch actually FINISHES server-side.
  const onBackfillComplete = (summary: any) => {
    if (!summary) return;
    setBackfillBusy(false);
    toast(
      t("toast.backfill_done", {
        provisioned: summary.provisioned,
        throttled: summary.throttled,
        failed: summary.failed,
        skipped: summary.skippedDuplicates || 0,
        remaining: summary.remaining,
        fallback: `Folders: ${summary.provisioned} provisioned, ${summary.throttled} throttled, ${summary.failed} failed, ${summary.skippedDuplicates || 0} duplicate(s) skipped — ${summary.remaining} still missing.`,
      }),
      summary.failed || summary.throttled ? "warning" : "success",
    );
    // 211 §11 — folders made without all their subfolders, after their retry
    const sfm = Number((summary as { subfoldersMissing?: unknown }).subfoldersMissing ?? 0);
    if (sfm > 0)
      toast(
        (t as TFn)("toast.backfill_subfolders_missing", {
          count: sfm,
          fallback: String(sfm) + " client folder(s) were made without all their subfolders — check them with « Check the subfolders ».",
        }),
        "warning",
      );
  };

  const doProvisionFolders = async (ids: any) => {
    const arr = (Array.isArray(ids) ? ids : [])
      .filter((n) => Number.isFinite(n))
      .slice(0, 50);
    if (!arr.length) return;
    setBackfillBusy(true);
    toast(
      t("toast.backfill_started", {
        count: arr.length,
        fallback: `Provisioning ${arr.length} folder(s)… spaced to avoid throttling, this can take a while.`,
      }),
      "warning",
    );
    try {
      // Phase 41 — the route ACKs immediately (202 {started:true}); it no longer
      // holds the connection through the whole run, so a mid-run nginx 504 can no
      // longer surface as a false failure. Completion — and the ONLY normal
      // backfillBusy reset + summary toast — arrives via the terminal
      // 'backfill'/'done' SSE poke (onBackfillComplete), so we DELIBERATELY do
      // NOT clear busy here: the control stays disabled until the batch finishes
      // server-side, which is what prevents a second overlapping batch. Progress
      // meanwhile is the live count on the button (party/cl SSE row-updates).
      const r = (await backfillClientFolders({ ids: arr })) as { started?: boolean; provisioned?: unknown } | null;
      // Defensive: an older server that still returns a terminal summary
      // synchronously (no {started:true}) — treat that as completion.
      if (r && r.started !== true && r.provisioned !== undefined) {
        onBackfillComplete(r);
      }
    } catch (e) {
      // A REAL launch failure only: the pre-ack validation (401/403/400/500)
      // rejects BEFORE any provisioning starts, so re-enabling here cannot
      // overlap a live run. The 504-mid-run class no longer occurs.
      setBackfillBusy(false);
      _apiErr("backfill", e);
    }
  };

  // Phase 41 (D8b) — resolve a fire-and-poll backfill on its terminal SSE poke,
  // filtered to the launching user (the hub also delivers it to admins by
  // overlay). The poke is non-ephemeral → it replays across a stream reconnect,
  // so a brief drop between launch and completion still resolves the button.
  useEffect(() => {
    const onSse = (e: any) => {
      const p = e && e.detail;
      if (!p || p.type !== "backfill" || p.action !== "done") return;
      if (Number(p.recipientId) !== Number(currentUser)) return;
      onBackfillComplete(p.summary);
    };
    window.addEventListener("lx:sse", onSse);
    return () => window.removeEventListener("lx:sse", onSse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Quick-create sub-modal (stacks on top of case modal)
  /* 137 §4(a) — `mkIcsDate` and `evToVevent` STOOD HERE and are now
     `src/lib/icsEvent.mjs`, imported at the top of this file: §4 attaches an
     .ics to the draft the SERVER composes, and its fence is that there must
     not be a second composer. The import keeps this name in scope, so the
     three `evToVevent={evToVevent}` prop sites below are untouched.
     `downloadIcs` is the browser-only half named by §4(a) and stays — a Blob,
     an object URL and a synthetic click are not things the server has. */
  const downloadIcs = (content: any, filename: any) => {
    const blob = new Blob([icsCalendar(content)], { type: "text/calendar" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  // Modals
  const renderModals = () => {
    if (!activeModal) return null;
    const {
      _fl,
      _mt,
      F,
      S,
      T,
      TitSel,
      BarSel,
      BarAssoc,
      PF,
      NA,
    } = makeModalFields({
      t,
      form,
      updateField,
      titlesList,
      titleEntries,
      barsList,
      barEntries,
    });
    switch (activeModal) {
      case "cl":
        return (
          <ClientModal
            F={F}
            NA={NA}
            PF={PF}
            S={S}
            T={T}
            _fl={_fl}
            _mt={_mt}
            clientTypesList={clientTypesList}
            clients={clients}
            cm={cm}
            confirmAndUndo={confirmAndUndo}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            saveEntityGuarded={saveEntityGuarded}
            setClients={setClients}
            setForm={setForm}
            t={t}
            toast={toast}
            updateField={updateField}
          />
        );
      case "op":
        return (
          <OpponentModal
            F={F}
            NA={NA}
            PF={PF}
            S={S}
            T={T}
            _mt={_mt}
            clientTypesList={clientTypesList}
            clients={clients}
            cm={cm}
            form={form}
            opponents={opponents}
            saveEntityGuarded={saveEntityGuarded}
            setOpponents={setOpponents}
            t={t}
          />
        );
      case "ocu":
        return (
          <OpposingCounselModal
            BarSel={BarSel}
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            cm={cm}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            opponentLawyers={opponentLawyers}
            saveEntity={saveEntity}
            setOpponentLawyers={setOpponentLawyers}
            t={t}
            updateField={updateField}
          />
        );
      case "ex":
        return (
          <ExpertModal
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            cm={cm}
            countriesList={countriesList}
            countryEntries={countryEntries}
            experts={experts}
            form={form}
            saveEntity={saveEntity}
            setExperts={setExperts}
            t={t}
            updateField={updateField}
          />
        );
      case "me":
        return (
          <MediatorModal
            BarAssoc={BarAssoc}
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            cm={cm}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            mediators={mediators}
            saveEntity={saveEntity}
            setMediators={setMediators}
            t={t}
            updateField={updateField}
          />
        );
      case "ar":
        return (
          <ArbiterModal
            BarAssoc={BarAssoc}
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            arbiters={arbiters}
            cm={cm}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            saveEntity={saveEntity}
            setArbiters={setArbiters}
            t={t}
            updateField={updateField}
          />
        );
      case "op2":
        return (
          <AdditionalPartyModal
            BarAssoc={BarAssoc}
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            additionalOpponents={additionalOpponents}
            clients={clients}
            cm={cm}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            saveEntityGuarded={saveEntityGuarded}
            setAdditionalOpponents={setAdditionalOpponents}
            t={t}
            updateField={updateField}
          />
        );
      case "ju":
        return (
          <JurisdictionModal
            jurisdictions={jurisdictions}
            F={F}
            _mt={_mt}
            cm={cm}
            form={form}
            saveEntity={saveEntity}
            setJurisdictions={setJurisdictions}
          />
        );
      case "co": {
        /* Round 13 (C74c) — LOCKED field matrix in the UI: a non-admin
           (editing his own record — EList hides foreign rows' edit for
           non-admins) gets every admin-only field read-only; only
           Initials + Phone stay editable (password rides its own flow).
           The server enforces the same matrix; this just stops the form
           from offering edits that can only 403. */
        return (
          <CollaboratorModal
            F={F}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            _appIsAdmin={_appIsAdmin}
            _appPerms={_appPerms}
            _coCreatePwPayload={_coCreatePwPayload}
            _m365Configured={_m365Configured}
            _roleRows={_roleRows}
            _setCoCreatePw={_setCoCreatePw}
            barEntries={barEntries}
            barsList={barsList}
            cm={cm}
            coCreatePwRef={coCreatePwRef}
            collaborators={collaborators}
            currentUser={currentUser}
            form={form}
            saveEntity={saveEntity}
            setCollaborators={setCollaborators}
            setForm={setForm}
            t={t}
            toast={toast}
            updateField={updateField}
          />
        );
      }
      case "ca":
        return (
          <CaseModal
            jurisdictions={jurisdictions}
            F={F}
            S={S}
            T={T}
            _fl={_fl}
            _mt={_mt}
            _appIsAdmin={_appIsAdmin}
            _appPerms={_appPerms}
            caseModalCalc={caseModalCalc}
            caseStatusesList={caseStatusesList}
            caseTypeEntries={caseTypeEntries}
            caseTypesList={caseTypesList}
            cases={cases}
            clients={clients as unknown}
            cm={cm}
            collaborators={collaborators}
            collectiveCases={collectiveCases}
            confirmAndUndo={confirmAndUndo}
            contactPersons={contactPersons}
            form={form}
            persist={persist}
            setCases={setCases}
            setForm={setForm}
            t={t}
            toast={toast}
            updateField={updateField}
          />
        );
      case "cp":
        return (
          <ContactPersonModal
            F={F}
            NA={NA}
            T={T}
            TitSel={TitSel}
            _fl={_fl}
            _mt={_mt}
            barEntries={barEntries as TaxList}
            barsList={barsList as readonly FieldSelectOption[]}
            clients={clients}
            cm={cm}
            contactPersons={contactPersons}
            contactRoleEntries={contactRoleEntries}
            contactRolesList={contactRolesList}
            countriesList={countriesList}
            countryEntries={countryEntries}
            form={form}
            saveEntity={saveEntity}
            setContactPersons={setContactPersons}
            t={t}
            updateField={updateField}
          />
        );
      case "oc":
        return (
          <OpponentContactModal
            F={F}
            NA={NA}
            TitSel={TitSel}
            _mt={_mt}
            cm={cm}
            form={form}
            opponentContacts={opponentContacts}
            saveEntity={saveEntity}
            setOpponentContacts={setOpponentContacts}
            t={t}
          />
        );
      case "te": {
        return (
          <TimeEntryModal
            _fl={_fl}
            _appIsAdmin={_appIsAdmin}
            activeTimers={activeTimers}
            addRecentCase={addRecentCase}
            cases={cases}
            cm={cm}
            collaborators={collaborators}
            collectiveCases={collectiveCases}
            currentUser={currentUser}
            form={form}
            getRecentCases={getRecentCases}
            isSecRole={isSecRole}
            persist={persist}
            sTeCaseQ={sTeCaseQ}
            saveTask={saveTask}
            setActiveTimers={setActiveTimers}
            setFocusedTimerIdx={setFocusedTimerIdx}
            setForm={setForm}
            setTimeEntries={setTimeEntries}
            setTkCompletePend={setTkCompletePend}
            t={t}
            tasks={tasks}
            teCaseQ={teCaseQ}
            timeEntries={timeEntries}
            tkCompletePend={tkCompletePend}
            tkTypes={tkTypes}
            toast={toast}
            updateField={updateField}
          />
        );
      }
      // 144 §4 — the bulk time-entry modal. Opened from TimeEntryTable's bulk
      // bar via om("teBulk", { _teIds }); field state lives in the modal, only
      // the id payload rides `form`. Renders here, in the main window, like
      // every other modal (rMod is the one modal surface).
      //
      // 145 §1 — and now a completion callback rides beside the ids. The undo
      // window belongs on the HOST's single confirmAndUndo slot (the one the
      // bulk delete already uses), not on App's, so the table hands its own
      // handler down through om() and gets the result back. `openModal` reaches
      // this payload through the plain `setForm(normFormDates(e))` arm, a
      // shallow spread that carries a function through untouched.
      case "teBulk":
        return (
          <BulkTimeEntryModal
            form={form}
            cm={cm}
            t={t}
            toast={toast}
            _appIsAdmin={_appIsAdmin}
            collaborators={collaborators}
            tkTypes={tkTypes}
            cases={cases}
            collectiveCases={collectiveCases}
            getRecentCases={getRecentCases}
            currentUser={currentUser}
            onDone={form._onDone}
          />
        );
      case "tk":
        return (
          <TaskModal
            caseStageEntries={caseStageEntries}
            getRecentCases={getRecentCases}
            F={F}
            S={S}
            T={T}
            _fl={_fl}
            _mt={_mt}
            _appIsAdmin={_appIsAdmin}
            _appPerms={_appPerms}
            actEntries={actEntries}
            askSubtaskDone={askSubtaskDone}
            jurisdictions={jurisdictions}
            cases={cases}
            cm={cm}
            collaborators={collaborators}
            collectiveCases={collectiveCases}
            currentUser={currentUser}
            eisenPickerOpen={eisenPickerOpen}
            form={form}
            holdEntityTimer={holdEntityTimer}
            itemLevelEntries={itemLevelEntries}
            openModal={openModal}
            reminderOptsList={reminderOptsList}
            saveTask={saveTask}
            chainTask={chainTask}
            linkState={linkStateFor("task")}
            setEisenPickerOpen={setEisenPickerOpen}
            setForm={setForm}
            setTasks={setTasks}
            setTkCompletePend={setTkCompletePend}
            t={t}
            taskStatusesList={taskStatusesList}
            tasks={tasks}
            tkTypes={tkTypes}
            toast={toast}
            updateField={updateField}
          />
        );
      case "ag":
        return (
          <AgendaEventModal
            caseStageEntries={caseStageEntries}
            _appIsAdmin={_appIsAdmin}
            _appPerms={_appPerms}
            S={S}
            T={T}
            _fl={_fl}
            _mt={_mt}
            agCaseQ={agCaseQ}
            // 123 §3 — two facts where `EV_MEETING_KIND` + `agMeetKind` were
            // threaded. The map is gone; `agIsMeeting` is the TYPE's answer and
            // `agMode` the EVENT's.
            agIsMeeting={agIsMeeting}
            agMode={agMode}
            // 124 §1 — and the mode a TYPE declares, from the same fold.
            agDefaultMode={evDefaultMode}
            agendaEvents={agendaEvents}
            canBumpRooms={canBumpRooms}
            jurisdictions={jurisdictions}
            cases={cases}
            cm={cm}
            collaborators={collaborators}
            collectiveCases={collectiveCases}
            currentUser={currentUser}
            evtEntries={evtEntries}
            evtTypes={evtTypes}
            form={form}
            genAgLocation={genAgLocation}
            getRecentCases={getRecentCases}
            hmPlus60={hmPlus60}
            itemLevelEntries={itemLevelEntries}
            mtgPlatforms={mtgPlatforms}
            mtgRoomConflicts={mtgRoomConflicts}
            mtgRooms={mtgRooms}
            reminderOptsList={reminderOptsList}
            eventStatusEntries={eventStatusEntries}
            sAgCaseQ={sAgCaseQ}
            saveAgendaEvent={saveAgendaEvent}
            /* 137 §4(c) — the one road that mails anybody. `onSaveInvite`
               stood here and is withdrawn with 119 §2's second primary. */
            onInviteDraft={openInviteDraft}
            chainEvent={chainEvent}
            /* 137 §4 — `askConfirm` stood here for 122 §3b's send-update
               confirmation, which warned that Outlook MIGHT re-notify everyone
               when an already-live meeting's attendee list changed. Nothing in
               this app changes a Graph attendee list any more, so the question
               has no act to be about. The COI prompt itself (`setCoiPrompt`) is
               unchanged and keeps its other callers. */
            linkState={linkStateFor("event")}
            setAgendaEvents={setAgendaEvents}
            setForm={setForm}
            t={t}
            toast={toast}
            updateField={updateField}
          />
        );
      case "stg":
        return (
          <StageModal
            jurisdictions={jurisdictions}
            F={F}
            T={T}
            _mt={_mt}
            caseStageEntries={caseStageEntries}
            cm={cm}
            form={form}
            partyRoleTax={partyRoleTax}
            chainStage={chainStage}
            linkState={linkStateFor("stage")}
            stageTypesList={stageTypesList}
            t={t}
            updateField={updateField}
          />
        );
      default:
        return null;
    }
  };

  // Views
  // 57a-i / 67 §3 — DERIVED, not written. This was
  // `["todo","inprogress","waiting"]`, a second declaration of the fact
  // `isTerminalStatus` owns via `meta.terminal` on the status's own taxonomy
  // row. The two agreed only on today's six codes; a firm adding a seventh got
  // a task open in one surface and not in another, with no code change
  // anywhere. `openStatusCodes` filters the tenant's own ladder through that
  // one predicate, and falls back to the built-in six when the taxonomy has
  // not loaded — so the answer is unchanged on the day this lands.
  const OPEN_ST = openStatusCodes(taskStatusEntries);
  /* BRIEF 136 §1 [RULED] — THE PILL IS A MENU NOW, AND C45/C58 ARE WITHDRAWN.
     What stood here said: "'waiting' left the cycle with the delegation
     redesign (C58) … Cancelled / archived remain reachable only via the lock /
     archive affordances, never from the pill." Laurent, 25 Aug 2026: "the
     status pill when clicked should show menu with all options, user to choose
     one, EXCEPT archived." The full withdrawal — C45, C58 and C94a, with the
     code they governed — is in StatusPill.tsx, beside the mechanism that
     replaces it. `archived` is excluded by name; §2 is why. */
  const TK_PICKABLE = pickableStatusCodes(taskStatusEntries);
  const TkStatusTag = (task: any, readonly: any) => {
    const st = task.status || "todo";
    const cfg = getTaskStatusMeta(st, taskStatusEntries);
    /* Round 14 (C82c) — ASSIGNMENT-AWARE interactivity, replacing the
       round-13 case-visibility test (C77a): the pill is a live control for
       the task's EXPLICIT assignees (an off-team explicit assignee must be
       able to set his own task's status — the server's C82c status-only
       carve-out accepts exactly that), for admin, for the case/cc RP and
       for matrix-edit holders. A DELEGATION-INDUCED-only viewer (and any
       other bystander) gets the inert display variant — he completes via
       HIS subtask, never the parent status. */
    /* Round 15 (C94d) — the matrix-edit arm is SCOPE-GATED now: clientCan
       resolves the role's tasks.edit bit with no notion of case scope, so a
       delegation-induced-only viewer whose ROLE carries the bit got a live
       pill on an off-team case — the server then 403'd the flip (the C82c
       gate composes the bit with case scope). The client's best scope proxy
       is its server-scoped ca/cc lists (the C77a test), applied to the
       matrix arm ONLY: explicit assignees, admin and RP arms are unchanged,
       and caseless tasks stay firm-global. */
    const _caseInScope =
      task.caseId == null && task.ccId == null
        ? true
        : task.caseId != null
          ? cases.some((c: any) => c.id === task.caseId)
          : collectiveCases.some((c: any) => c.id === task.ccId);
    const _canSetStatus =
      !!_appIsAdmin ||
      (currentUser != null &&
        (task.assignedCoId === currentUser ||
          (Array.isArray(task.assigned) &&
            task.assigned.includes(currentUser)))) ||
      (currentUser != null &&
        (task.caseId != null
          ? (
              cases.find((c: any) => c.id === task.caseId)?.responsiblePartners || []
            ).includes(currentUser)
          : task.ccId != null
            ? (
                collectiveCases.find((c: any) => c.id === task.ccId)?.responsiblePartners || []
              ).includes(currentUser)
            : false)) ||
      (_caseInScope &&
        _appPerms.can("caseView.tasks.edit", {
          caseId: task.caseId,
          ccId: task.ccId,
        }));
    if (!_canSetStatus) readonly = true;
    /* BRIEF 117 §2a — THE MARKUP IS `StatusPill` NOW, both arms. Everything
       above and below this line is task-specific and stays: the C82c/C94d
       rights test, the completion prompt, `saveTask`. The pill itself is
       shared with the SUBTASK row, which §2a rules must wear "the same status
       pill mechanism … do not build a second pill" — and since 136 §1 the MENU
       is shared with it too. (This sentence named `TK_CYCLE` and the C94a
       swallow until §1 withdrew both.) */
    /* 136 §1 — AND AN ARCHIVED ROW IS INERT, which the cycle could hide and a
       menu cannot. `saveTask` refuses every write to a row whose stored status
       is "archived" (:7496, `return false`), so a live pill there would offer
       five choices and silently take none. Legacy rows only: since 112 §6 the
       status string is never written, the Phase-28 boolean is the archive. */
    if (readonly || st === "archived")
      return <StatusPill cfg={cfg} minWidth={STATUS_PILL_W} />;
    /* BRIEF 136 §1 [RULED] — THE PILL OPENS A MENU OF EVERY STATUS EXCEPT
       `archived`, and one click sets the one the user chose. What the cycle
       carried, this carries:
         · the COMPLETION PROMPT, on entering "completed" — a MAIN-window
           dialog (C25/C26) that owns the commit, so cancelling leaves the task
           where it was and the time-log offer is unchanged;
         · the RIGHTS test above (C82c/C94d) — untouched, and it is what makes
           the readonly arm above the answer for an off-team viewer;
         · ONE MECHANISM — the menu lives in `StatusPill`, so CV, CCV, the
           Dashboard, the compact column and the subtask row share it and no
           call site grows a second pill.
       C94a's 4s swallow is GONE with the cycle that needed it; StatusPill.tsx
       carries that withdrawal and its reason. */
    const commit = (code: string, srcDoc: Document) => {
      if (code === st) return;
      if (code === "completed") {
        /* C25/C26 — the complete-task prompt opens in the MAIN window only
           (rMod/dialogs never render in the pop-out document); pull the main
           window forward when the pick was made in the pop-out. The pill
           reports the document its menu was in, because since 136 §1 the menu
           is portalled into that document rather than into this one. */
        if (srcDoc && srcDoc !== document) {
          try {
            window.focus();
          } catch {}
        }
        /* C91 — pend the CANONICAL row (host lists may hand the pill a
           derived snapshot): the completion prompt's te payload reads
           case/type/title from this object.
           Round 17 (C102) — the task's own (task-level) timer pauses with the
           prompt and rides the pend for the prompt's four outcomes. */
        setTkCompletePend({
          ...((task.id != null && tasks.find((x: any) => x.id === task.id)) ||
            task),
          _timerHold: holdEntityTimer(task.id, null),
        });
      } else
        saveTask({
          ...task,
          status: code,
          /* Round 15 (C94c) — LEAVING "completed" clears the stamp a completion
             set; the field is client-state only (the server pick drops it).
             136 §1 — it was written for the cycle's one exit (completed →
             todo) and now covers every exit the menu offers, which is the same
             rule applied to a wider set of transitions. */
          ...(task.completedDate != null && code !== "completed"
            ? { completedDate: null }
            : {}),
        });
    };
    return (
      <StatusPill
        cfg={cfg}
        current={st}
        minWidth={STATUS_PILL_W}
        options={TK_PICKABLE.map((code: string) => ({
          code,
          cfg: getTaskStatusMeta(code, taskStatusEntries),
        }))}
        onPick={commit}
      />
    );
  };
  const renderTaskDeleteDialog = () => {
    if (!tkDelConfirm) return null;
    const tk = tasks.find((x: any) => x.id === tkDelConfirm);
    return (
      <ModalLayout
        title={t("modal.tk.delete_title", { fallback: "Delete task" })}
        showX={false}
        rawBody
        footer={null}
        maxWidth={340}
      >
          <div style={{ padding: "16px 20px" }}>
            <p style={{ fontSize: FS.s12, fontWeight: 500, marginBottom: 6 }}>
              {tk?.title}
            </p>
            <p style={{ fontSize: FS.s11, color: "var(--tm)", marginBottom: 20 }}>
              {t("modal.tk.delete_body", {
                fallback: "This task will be permanently deleted. Are you sure?",
              })}
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                alignItems: "stretch",
              }}
            >
              <button className="modal-cancel" onClick={() => setTkDelConfirm(null)}>
                {t("common.cancel", { fallback: "Cancel" })}
              </button>
              <button
                className="modal-delete"
                onClick={() => {
                  delTk(tkDelConfirm);
                  setTkDelConfirm(null);
                }}
              >
                {t("common.delete", { fallback: "Delete" })}
              </button>
            </div>
          </div>
      </ModalLayout>
    );
  };

  /* 190 §C surface 1 — the user Bento's inputs: the matrix's own filter
     (EisenhowerDialog.tsx:112–116) over the `any`-typed stores, narrowed ONCE
     at this seam (185's method: a cast from `any` is free, every `.prop` read
     off it is not). Sits after `tryNavCase` (declared above) and before the
     view wrappers that follow. */
  const bentoUser = currentUser as number | null;
  const bentoTasks: EisenCardTask[] = (tasks as EisenCardTask[]).filter(
    (tk) => (tk.assignedCoId === bentoUser || !bentoUser) && taskIsOpen(tk, taskStatusEntries as unknown),
  );
  const bentoCases = cases as { id: number; title?: string; itemLinks?: unknown }[];
  const bentoCcs = collectiveCases as { id: number; itemLinks?: unknown }[];
  const bentoTypes = tkTypes as { code?: string | null; desc?: string }[];
  const bentoT = t as TFn;
  const CaView = () => (
    <CaViewComponent
      tryNavCase={tryNavCase}
      did={detailId}
      sDid={setDetailId}
      userMatrixQuadrant={userMatrixQuadrant} // 190 round 6 [T] — the case Bento's card edge is the task's quadrant
      caMode={caMode}
      setCaMode={setCaMode}
      ca={cases}
      sCa={setCases}
      cl={clients}
      sCl={setClients}
      op={opponents}
      ocu={opponentLawyers}
      ex={experts}
      cp={contactPersons}
      oc={opponentContacts}
      me={mediators}
      ar={arbiters}
      op2={additionalOpponents}
      co={collaborators}
      ju={jurisdictions}
      te={timeEntries}
      sTe={setTimeEntries}
      ag={agendaEvents}
      sAg={setAgendaEvents}
      tk={tasks}
      sTk={setTasks}
      tkTypes={tkTypes}
      evtTypes={evtTypes}
      agTypeFilt={agTypeFilt}
      schedAxis={schedAxis}
      holidays={holidays}
      calDisp={calDisp}
      setAgTypeFilt={setAgTypeFilt}
      cf={caseFilter}
      sCf={setCaseFilter}
      cdl={confirmDelete}
      sCdl={setConfirmDelete}
      f={form}
      sF={setForm}
      mod={activeModal}
      showDL={showDL}
      sShowDL={sShowDL}
      lookback={lookback}
      showEarlier={showEarlier}
      sShowEarlier={sShowEarlier}
      showDesc={showDesc}
      sShowDesc={sShowDesc}
      currentUser={currentUser}
      onCallColleague={canCalls ? onCallColleague : undefined}
      callActive={!!callWin}
      partiesLocked={partiesLocked}
      setPartiesLocked={setPartiesLocked}
      partiesOpen={partiesOpen}
      setPartiesOpen={setPartiesOpen}
      undoEntry={undoEntry}
      setUndoEntry={setUndoEntry}
      collStg={collStg}
      setCollStg={setCollStg}
      expCl={expCl}
      sExpCl={sExpCl}
      clSrch={clSrch}
      sClSrch={sClSrch}
      caTesel={caTesel}
      setCaTesel={setCaTesel}
      agMy={agMy}
      sAgMy={sAgMy}
      caSort={caSort}
      setCaSort={setCaSort}
      tkStatusFilt={tkStatusFilt}
      setTkStatusFilt={setTkStatusFilt}
      chatOpen={chatOpen}
      setChatOpen={setChatOpen}
      chatMsgs={chatMsgs}
      setChatMsgs={setChatMsgs}
      chatInput={chatInput}
      setChatInput={setChatInput}
      chatType={chatType}
      setChatType={setChatType}
      chatSearch={chatSearch}
      setChatSearch={setChatSearch}
      chatMention={chatMention}
      setChatMention={setChatMention}
      chatMentionQ={chatMentionQ}
      setChatMentionQ={setChatMentionQ}
      chatRead={chatRead}
      setChatRead={setChatRead}
      gChat={gChat}
      setGChat={setGChat}
      tkDelConfirm={tkDelConfirm}
      setTkDelConfirm={setTkDelConfirm}
      activeTimer={activeTimer}
      activeTimers={activeTimers}
      setMailDlg={setMailDlg}
      mtgDefLoc={mtgDefLoc}
      setDoodleDlg={setDoodleDlg}
      permMx={permMx}
      chatRef={chatRef}
      undoTRef={undoTRef}
      s={persist}
      nav={nav}
      om={openModal}
      cm={cm}
      del={del}
      toast={toast}
      etTag={etTag}
      evtLongestCode={evtLongestCode}
      coAvs={coAvs}
      toggleDL={toggleDL}
      locIcon={locIcon}
      svTkData={saveTask}
      onSubtaskDone={askSubtaskDone}
      delTk={delTk}
      TkStatusTag={TkStatusTag}
      startTimer={startTimer}
      getTimerDisplay={getTimerDisplay}
      evToVevent={evToVevent}
      downloadIcs={downloadIcs}
      keepScroll={keepScroll}
      sQm={openQuickAdd} /* 207 §13 — opens on the firm's country */
      sQf={setQuickAddForm}
      OPEN_ST={OPEN_ST}
      MultiSelect={MultiSelect}
      linkTypesList={linkTypesList}
      caseTypesList={caseTypesList}
      caseStatusesList={caseStatusesList}
      docStatusesList={docStatusesList}
      stageTypesList={stageTypesList}
      taskStatusesList={taskStatusesList}
      itemLevelList={itemLevelList}
      reminderOptsList={reminderOptsList}
      setPartyDetailDlg={setPartyDetailDlg}
      inv={invoices}
      sInv={setInvoices}
      firmSettings={firmSettings}
      /* 176 §3.2 — App's `openProvisionModal` (:10169) reaching the client-file
         pane's no-folder row. ONE prop, threaded the way firmSettings already
         is; §3.2 rules a prop is the price rather than a second provisioning
         path. The modal itself renders in the top-level JSX (:16581), not
         under `renderContent`, so it opens from a matter unchanged. */
      onProvisionFolders={openProvisionModal}
      onFolderCheck={onMatterFolderCheck} /* 212 §27 — the matters' check, when the person may run it */
      partyRoleEntries={partyRoleTax.entries}
      /* 174 §5 — the email section's matter-choice modal picks over BOTH
         lists and reads the same recents every other MatterPicker host does. */
      cc={collectiveCases}
      getRecentCases={getRecentCases}
    />
  );
  const CcView = () => (
    <CollectiveCaseView
      /* 86 Phase 2 step 7 — the tenant's own open-status ladder, the same value
         CaseView has had since :8538. CCV's team pane spelled the predicate
         inline instead, which is the fifth spelling taskCounters.ts:137 names. */
      OPEN_ST={OPEN_ST}
      ccMode={ccMode}
      setCcMode={setCcMode}
      /* brief 86 Phase 2 step 3 — click-to-call reaches the collective case */
      onCallColleague={canCalls ? onCallColleague : undefined}
      callActive={!!callWin}
      tryNavCase={tryNavCase}
      ca={cases}
      caseTypesList={caseTypesList}
      caseStatusesList={caseStatusesList}
      docStatusesList={docStatusesList}
      stageTypesList={stageTypesList}
      taskStatusesList={taskStatusesList}
      itemLevelList={itemLevelList}
      reminderOptsList={reminderOptsList}
      cc={collectiveCases}
      sCc={setCollectiveCases}
      cl={clients}
      sCl={setClients}
      did={detailId}
      sDid={setDetailId}
      userMatrixQuadrant={userMatrixQuadrant} // 190 round 6 [T] — the case Bento's card edge is the task's quadrant
      op={opponents}
      ocu={opponentLawyers}
      ex={experts}
      cp={contactPersons}
      oc={opponentContacts}
      me={mediators}
      ar={arbiters}
      op2={additionalOpponents}
      co={collaborators}
      ju={jurisdictions}
      te={timeEntries}
      sTe={setTimeEntries}
      ag={agendaEvents}
      sAg={setAgendaEvents}
      tk={tasks}
      sTk={setTasks}
      tkTypes={tkTypes}
      evtTypes={evtTypes}
      agTypeFilt={agTypeFilt}
      schedAxis={schedAxis}
      holidays={holidays}
      calDisp={calDisp}
      setAgTypeFilt={setAgTypeFilt}
      currentUser={currentUser}
      s={persist}
      nav={nav}
      toast={toast}
      pname={pname}
      /* Round 15 (C90) — CollectiveCaseView has consumed coIni since the
         round-12 delegation-visibility work (RP initials + assignee badges
         in the cc list), but the prop was never wired here: the first cc
         row carrying a responsible partner or assignees crashed the list
         render ("coIni is not a function" inside the tbody map — the
         blank-screen report). CaseView is unaffected (it imports coIni
         from utils directly). */
      coIni={coIni}
      SK={SK}
      I={I}
      nid={nid}
      sv={sv}
      chatMsgs={chatMsgs}
      setChatMsgs={setChatMsgs}
      chatRead={chatRead}
      setChatRead={setChatRead}
      setMailDlg={setMailDlg}
      setDoodleDlg={setDoodleDlg}
      mtgDefLoc={mtgDefLoc}
      linkTypesList={linkTypesList}
      setPartyDetailDlg={setPartyDetailDlg}
      activeTimers={activeTimers}
      startTimer={startTimer}
      inv={invoices}
      sInv={setInvoices}
      globalOm={openModal}
      MultiSelect={MultiSelect}
      sQm={openQuickAdd} /* 207 §13 — opens on the firm's country */
      sQf={setQuickAddForm}
      partiesLocked={partiesLocked}
      setPartiesLocked={setPartiesLocked}
      partiesOpen={partiesOpen}
      setPartiesOpen={setPartiesOpen}
      TkStatusTag={TkStatusTag}
      firmSettings={firmSettings}
      onProvisionFolders={openProvisionModal} /* 176 §3.2 — the CCV twin */
      onFolderCheck={onMatterFolderCheck} /* 212 §27 — the matters' check, its twin */
      partyRoleEntries={partyRoleTax.entries}
      svTkData={saveTask}
      onSubtaskDone={askSubtaskDone}
      etTag={etTag}
      evtLongestCode={evtLongestCode}
      coAvs={coAvs}
      locIcon={locIcon}
      /* 174 §5 — as CaseView: the matter-choice modal's recents. */
      getRecentCases={getRecentCases}
    />
  );


  const AgView = () => (
    <AgViewComponent
      /* 132 §7 — the agenda draws the same non-working days the strips do, so
         it takes the same two answers App already resolved. */
      schedAxis={schedAxis}
      holidays={holidays}
      calDisp={calDisp}
      autoOpenImport={agAutoImport}
      onAutoImportConsumed={() => setAgAutoImport(false)}
      tryNavCase={tryNavCase}
      currentUser={currentUser}
      co={collaborators}
      ag={agendaEvents}
      sAg={setAgendaEvents}
      ca={cases}
      cc={collectiveCases}
      agTypeFilt={agTypeFilt}
      setAgTypeFilt={setAgTypeFilt}
      agDateRange={agDateRange}
      setAgDateRange={setAgDateRange}
      agViewMode={agViewMode}
      setAgViewMode={setAgViewMode}
      agMy={agMy}
      sAgMy={sAgMy}
      agCoFilt={agCoFilt}
      sAgCoFilt={sAgCoFilt}
      af={agendaFilter}
      sAf={setAgendaFilter}
      showEarlier={showEarlier}
      sShowEarlier={sShowEarlier}
      agMulti={agMulti}
      setAgMulti={setAgMulti}
      s={persist}
      nav={nav}
      om={openModal}
      toast={toast}
      etTag={etTag}
      evtLongestCode={evtLongestCode}
      coAvs={coAvs}
      locIcon={locIcon}
      /* 216 addendum 6 §10 (4) — `evToVevent`, `downloadIcs`, `activeTimers`,
         `startTimer`, `svTkData` and `setTkDelConfirm` left this mount: the
         Agenda page reads none of them since addendum 4 §8 (the .ics
         download, the timer, the archive and the delete left its lines).
         216 addendum 7 §11 (4) — and the seven the view destructured without
         reading (`DLBtn`, `activeTimer`, `delTk`, `getTimerDisplay`, `showDL`,
         `lookback`, `ju`) leave it too. */
      evtTypes={evtTypes}
      tk={tasks}
      tkTypes={tkTypes}
      TkStatusTag={TkStatusTag}
    />
  );

  const DocView = () => {
    const all = cases.flatMap((c: any) =>
      (c.docs || []).map((d: any) => ({ ...d, caId: c.id, caRef: c.ref })),
    );
    /* 195 §C.3b — the filter as it was, over this screen's own text */
    const fi = all.filter((d: any) =>
      (d.name + d.caRef).toLowerCase().includes(docsQ.toLowerCase()),
    );
    return (
      <div className="table-wrap">
        {/* 195 §C.3b [RULED C.0] — the Documents screen owns its search box,
            leading the table it filters (158 §8). */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <div className="search-box" style={{ width: 240 }}>
            <Search size={12} style={{ color: "var(--tm)", flexShrink: 0 }} />
            <input
              placeholder={t("common.search_placeholder")}
              value={docsQ}
              onChange={(e) => setDocsQ(e.target.value)}
            />
          </div>
        </div>
        <Table>
          <thead>
            <tr>
              <th>{t("common.document", { fallback: "Document" })}</th>
              <th>{t("field.case", { fallback: "Case" })}</th>
              <th>{t("common.type", { fallback: "Type" })}</th>
              <th>{t("common.status", { fallback: "Status" })}</th>
              <th>{t("common.date", { fallback: "Date" })}</th>
              <th>{t("common.link", { fallback: "Link" })}</th>
            </tr>
          </thead>
          <tbody>
            {fi.map((d: any) => {
              const ftI = (FT_INFO as any)[d.fileType || detFT(d.name)];
              const stI = (docStatusesList || []).find(
                (x: any) => x.v === (d.status || ""),
              );
              return (
                <tr key={`${d.caId}-${d.id}`}>
                  <td>
                    <span
                      style={{
                        fontSize: FS.s8_5,
                        fontWeight: 700,
                        color: CT.c_fff,
                        background: ftI.c,
                        borderRadius: RAD.r3,
                        padding: "1px 4px",
                        marginRight: 5,
                      }}
                    >
                      {ftI.l}
                    </span>
                    <span className="name-cell">{d.name}</span>
                  </td>
                  <td>
                    <span className="link" onClick={() => nav("ca", d.caId)}>
                      {d.caRef}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`tag ${dcatTag(d.type)}`}
                    >
                      {dcatLabel(d.type)}
                    </span>
                  </td>
                  <td>
                    {stI ? (
                      <span
                        style={{ fontSize: FS.s9_5, fontWeight: 600, color: stI.c }}
                      >
                        {stI.l}
                      </span>
                    ) : (
                      <span style={{ color: "var(--tm)", fontSize: FS.s9_5 }}>
                        —
                      </span>
                    )}
                  </td>
                  <td className="mono">{fd(d.date)}</td>
                  <td>
                    <a
                      href={d.link}
                      target="_blank"
                      rel="noopener"
                      className="lib-pill"
                      onClick={(e) => e.stopPropagation()}
                    >
                      ↗ {t("common.open", { fallback: "Open" })}
                    </a>
                  </td>
                </tr>
              );
            })}{" "}
          </tbody>
        </Table>
      </div>
    );
  };

  const SettingsView = () => (
    <SettingsViewComponent
      settingsFocus={settingsFocus}
      onSettingsFocusDone={() => setSettingsFocus(null)}
      cc={collectiveCases}
      sCc={setCollectiveCases}
      cl={clients}
      sCl={setClients}
      ca={cases}
      sCa={setCases}
      op={opponents}
      sOp={setOpponents}
      ocu={opponentLawyers}
      sOcu={setOpponentLawyers}
      ex={experts}
      sEx={setExperts}
      ju={jurisdictions}
      sJu={setJurisdictions}
      co={collaborators}
      sCo={setCollaborators}
      te={timeEntries}
      sTe={setTimeEntries}
      ag={agendaEvents}
      sAg={setAgendaEvents}
      cp={contactPersons}
      sCp={setContactPersons}
      oc={opponentContacts}
      sOc={setOpponentContacts}
      evtTypes={evtTypes}
      tkTypes={tkTypes}
      currentUser={currentUser}
      sCurrentUser={sCurrentUser}
      lookback={lookback}
      sLookback={sLookback}
      nwh={nwh}
      sNwh={sNwh}
      permMx={permMx}
      sPermMx={sPermMx}
      sfMaps={sfMaps}
      setSfMaps={setSfMaps}
      saveSfMaps={saveSfMaps}
      extTypeMaps={extTypeMaps}
      setExtTypeMaps={setExtTypeMaps}
      saveExtTypeMaps={saveExtTypeMaps}
      emailClient={emailClient}
      setEmailClient={setEmailClient}
      mtgApp={mtgApp}
      setMtgApp={setMtgApp}
      mtgPlatforms={mtgPlatforms}
      setMtgPlatforms={setMtgPlatforms}
      mtgDefLoc={mtgDefLoc}
      setMtgDefLoc={setMtgDefLoc}
      pollTool={pollTool}
      setPollTool={setPollTool}
      pollCustomUrl={pollCustomUrl}
      setPollCustomUrl={setPollCustomUrl}
      workHours={workHours}
      setWorkHours={setWorkHours}
      pomInterval={pomInterval}
      setPomInterval={setPomInterval}
      pomMuted={pomMuted}
      setPomMuted={setPomMuted}
      sfForm={sfForm}
      setSfForm={setSfForm}
      xtForm={xtForm}
      setXtForm={setXtForm}
      etForm={etForm}
      sEtForm={sEtForm}
      tkForm={tkForm}
      setTkForm={setTkForm}
      fRef={fRef}
      s={persist}
      toast={toast}
      caseTypesList={caseTypesList}
      docTypesList={docTypesList}
      titlesList={titlesList}
      tk={tasks}
      clientTypesList={clientTypesList}
      caseStatusesList={caseStatusesList}
      barsList={barsList}
      countriesList={countriesList}
      docStatusesList={docStatusesList}
      me={mediators}
      ar={arbiters}
      op2={additionalOpponents}
      theme={theme}
      setTheme={setTheme}
      personalTheme={personalTheme}
      setPersonalTheme={setPersonalTheme}
      textSize={textSize}
      setTextSize={setTextSize}
      autoNightSwitch={autoNightSwitch}
      setAutoNightSwitch={setAutoNightSwitch}
      dayStart={dayStart}
      setDayStart={setDayStart}
      nightStart={nightStart}
      setNightStart={setNightStart}
      themes={themes}
      setThemes={setThemes}
      firmLogo={firmLogo}
      setFirmLogo={setFirmLogo}
      firmName={firmName}
      setFirmName={setFirmName}
      firmSettings={firmSettings}
      saveFirmSettings={saveFirmSettings}
      applyFirmSettings={applyFirmSettings}
      appFont={appFont}
      setAppFont={setAppFont}
      linkTypesList={linkTypesList}
      stageTypesList={stageTypesList}
      taskStatusesList={taskStatusesList}
      itemLevelList={itemLevelList}
      reminderOptsList={reminderOptsList}
      maxTimers={maxTimers}
      setMaxTimers={setMaxTimers}
      timerRound={timerRound}
      setTimerRound={setTimerRound}
    />
  );

  const ecfg: Record<string, any> = buildEcfg({
    clients,
    opponents,
    opponentLawyers,
    experts,
    jurisdictions,
    collaborators,
    contactPersons,
    opponentContacts,
    mediators,
    arbiters,
    additionalOpponents,
    clientTypeEntries,
    contactRoleEntries, // 214 §4
    barEntries: barEntries as TaxList,
    customFieldColsCl, // 189 §B
    /* 195 §C.3a — the thirteen lists' own search box (ecfg.tsx declares it,
       EntityList renders it): the text is `query`, App-owned as before for
       the one component the thirteen share, reset by every navigation
       (navResetTransients); a keystroke resets the list's own page as the
       page-level box did. */
    listSearch: {
      value: query,
      change: ([key, v]: [string, string]) => {
        setQuery(v);
        setListPage((p: Record<string, number>) => ({ ...p, [key]: 0, ca: 0 }));
      },
    },
    _appPerms,
    _appIsAdmin,
  });
  /* B.6 drain — the Escape/Alt hotkey chain (extracted to
     hooks/popoverChrome.ts). Called AFTER the ecfg construction because the
     hook args (ecfg, openModal, cm, cancelSubtaskDone, releaseEntityTimer)
     are evaluated at call time — the old inline effect only reached them
     lazily through its closure. */
  useAppHotkeys({
    searchDlg,
    setSearchDlg,
    discardDlg,
    setDiscardDlg,
    sideFly,
    setSideFly,
    subtaskDoneAsk,
    cancelSubtaskDone,
    tkCompletePend,
    setTkCompletePend,
    releaseEntityTimer,
    notifOpen,
    setNotifOpen,
    activeModal,
    form,
    cm,
    mailDlg,
    setMailDlg,
    doodleDlg,
    setDoodleDlg,
    dailyOverview,
    setDailyOverview,
    gSearchQ,
    setGSearchQ,
    ecfg,
    section,
    openModal,
    currentUser,
    setSDlgQ,
    setSDlgMore,
  });

  const gChatUnread = currentUser
    ? (() => {
        const lr = chatRead["gchat:" + currentUser] || 0;
        return gChat.filter((m: any) => m.ts > lr && m.author !== currentUser)
          .length;
      })()
    : 0;
  /* Phase 29 (items 1/2/3) — ONE render helper for both column hosts: the
     phone in-app container and the desktop pop-out portal. CompactColumn is
     a module-scope import (stable identity — Bug A rule), so this thin
     call-site helper is safe. */
  const renderColumn = (inPop: any) => (
    <CompactColumn
      inPopOut={inPop}
      onToggle={toggleColumn}
      activeTimers={activeTimers}
      focusedTimerIdx={focusedTimerIdx}
      startTimer={startTimer}
      pauseTimer={pauseTimer}
      stopTimerAsk={stopTimerAsk}
      stopAllTimers={stopAllTimers}
      pomInterval={pomInterval}
      getPomElapsed={getPomElapsed}
      tk={tasks}
      sTk={setTasks}
      ag={agendaEvents}
      sAg={setAgendaEvents}
      ca={cases}
      cc={collectiveCases}
      co={collaborators}
      OPEN_ST={OPEN_ST}
      getRecentCases={getRecentCases}
      currentUser={currentUser}
      om={openModal}
      nav={nav}
      tryNavCase={tryNavCase}
      svTkData={saveTask}
      onSubtaskDone={askSubtaskDone}
      tkTypes={tkTypes}
      evtTypes={evtTypes}
      itemLevelList={itemLevelList}
      priMap={ITEM_LEVEL}
      TkStatusTag={TkStatusTag}
      etTag={etTag}
      evtLongestCode={evtLongestCode}
      locIcon={locIcon}
      coAvs={coAvs}
      MultiSelect={MultiSelect}
      s={persist}
      SK={SK}
      permIsAdmin={_appIsAdmin}
      userMatrix={userMatrix}
      userMatrixQuadrant={userMatrixQuadrant}
      userMatrixSetQuadrant={userMatrixSetQuadrant}
      notifications={notifications}
      unreadNotifCount={unreadNotifs.length}
      bellNewCount={bellNew}
      onBellShown={markBellSeen}
      /* 209 §27 (d) — the pop-out's bell is the main one: what a bell row needs */
      evtEntries={evtEntries}
      dismissNotif={dismissNotif}
      joinAndTime={joinAndTime}
      notifRead={notifRead}
      markNotifRead={markNotifRead}
      markAllRead={markAllRead}
      gChat={gChat}
      setGChat={setGChat}
      gChatInput={gChatInput}
      setGChatInput={setGChatInput}
      gChatLinkCase={gChatLinkCase}
      setGChatLinkCase={setGChatLinkCase}
      gChatLinkKind={gChatLinkKind}
      setGChatLinkKind={setGChatLinkKind}
      chatMsgs={chatMsgs}
      setChatMsgs={setChatMsgs}
      chatRead={chatRead}
      setChatRead={setChatRead}
      gChatUnread={gChatUnread}
      firmName={firmName}
      /* Phase 31 (C2) — phone surface in the column header + the incoming
         ring banner (the pop-out's popup). Corr-2 (item 4) — the panel
         renders INLINE in the column (both hosts); doCall/presence state
         stay App-owned and ride down as props. */
      canCalls={canCalls}
      missedCallCount={missedCallCount}
      presenceIds={presenceIds}
      presenceLoaded={presenceLoaded}
      calls={calls}
      /* 31.1 (item 9) — the column closure knows its host: a call started
         here is hosted here (the pop-out banner) instead of bouncing to
         the main window. */
      onCall={(ids: any) => doCall(ids, undefined, inPop)}
      onNotifyMissed={notifyMissed}
      callActive={!!callWin || !!outgoingCall}
      onScheduleMeeting={openInternalMeeting}
      onSchedulePoll={openInternalPoll}
      onSendEmail={openInternalEmail}
      outgoingCall={outgoingCall}
      onAbortCall={abortCall}
      onCloseOutgoing={() => setOutgoingCall(null)}
      callBusy={callBusy}
      onPullPresence={pullPresence}
      incomingCalls={incomingCalls}
      alertToasts={alertToasts}
      onDismissAlert={dismissAlertToast}
      onCallAccept={(callId: any, win: any) => respondCall(callId, "accept", undefined, win)}
      onCallDecline={(callId: any) => {
        try {
          window.focus();
        } catch {
          /* ignore */
        }
        const d = new Date(Date.now() + 30 * 60000);
        d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
        setCallDeclineFor({
          callId,
          time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        });
      }}
    />
  );
  const navI = [
    { k: "dash", l: t("nav.dashboard"), i: LayoutTemplate },
    { k: "ag", l: t("nav.agenda"), c: agendaEvents.length, i: CalendarClock },
    {
      k: "gchat",
      l: t("nav.general_chat"),
      c: gChatUnread || undefined,
      i: MessagesSquare,
    },
    /* 189 §H — campaigns: newsletters over a segment of the firm's clients,
       gated on lateral.campaigns.view through SURFACE_SECTION_KEY. */
    { k: "campaigns", l: t("nav.campaigns", { fallback: "Campaigns" }), i: Send },
    {
      k: "ca",
      l: t("nav.cases"),
      /* 139 §3.2 — THE ONE EXCLUDE SITE, and the §3.1 grep cannot see it.
         §3.2 names three answers; TOGGLE and INCLUDE both land among the 24
         `ca={cases}` prop sites, but EXCLUDE does not — every one of those 24
         hands the array to something that looks a matter up, lists it, or
         totals money over it. The assertion §3.2 describes word for word —
         "this firm has N open matters" — is not passed as a prop at all. It is
         this number, beside "Cases" in the sidebar. A prospect is not an open
         matter, so it is not counted here.

         The badge and the list agree: with §2's toggle the list's own segments
         read All / Cases / Prospects, and this number is the "Cases" segment.

         ITS SIBLING `ca:` IS DELIBERATELY NOT FILTERED. That is the ARCHIVED
         count, a disclosure count for the archive drawer — and the drawer
         still lists archived prospects. Excluding them from the badge while
         its own list shows them would put a badge in disagreement with the
         thing it counts, which is a defect, not a pass. */
      c: (true /* M+2b: server-filtered */
        ? cases.filter((c: any) => c.status !== "archived" && !c.isProspect)
        : cases.filter(
            (c: any) =>
              c.status !== "archived" &&
              !c.isProspect &&
              (c.caseCollaborators || []).some((x: any) => x.coId === currentUser),
          )
      ).length,
      ca: cases.filter((c: any) => c.status === "archived").length,
      i: BookUser,
    },
    {
      k: "cc",
      l: t("nav.collective_cases"),
      c: collectiveCases.length || undefined,
      i: Bus,
    },
    { k: "ju", l: t("nav.jurisdictions"), c: jurisdictions.length, i: Landmark },
    {
      k: "co2int",
      l: t("nav.internal"),
      c: collaborators.filter((c: any) => !c.coType || c.coType === "internal").length,
      i: UsersRound,
    },
    {
      k: "co2ext",
      l: t("nav.external"),
      c: collaborators.filter((c: any) => c.coType === "external").length,
      i: UserCircle,
    },
    { k: "cl", l: t("nav.clients"), c: clients.length, i: UsersRound },
    { k: "cp", l: t("nav.contact_persons"), c: contactPersons.length, i: UserRoundPlus },
    { k: "op", l: t("nav.opponents"), c: opponents.length, i: UserX },
    { k: "ocu", l: t("nav.opponent_counsel"), c: opponentLawyers.length, i: Scale },
    { k: "oc", l: t("nav.opponent_contacts"), c: opponentContacts.length, i: UserPlus },
    { k: "ex", l: t("nav.experts"), c: experts.length, i: Scale },
    { k: "me", l: t("nav.mediators"), c: mediators.length, i: Scale },
    { k: "ar", l: t("nav.arbiters"), c: arbiters.length, i: Scale },
    { k: "op2", l: t("nav.other_persons"), c: additionalOpponents.length, i: UserPen },
    { k: "sched", l: t("nav.schedule"), i: ClipboardClock },
    { k: "places", l: t("nav.places", { fallback: "Places" }), i: MapPin },
    { k: "lib", l: t("nav.library"), c: books.length, i: LibraryBig },
    {
      k: "external",
      l: t("nav.externalLinks"),
      c: extLinks.length || undefined,
      i: Globe,
    },
    { k: "costs", l: t("nav.costs"), i: HandCoins },
    {
      k: "invoices",
      l: t("nav.invoices"),
      c: invoices.filter((i: any) => i.paymentStatus !== "cancelled").length || undefined,
      i: ReceiptEuro,
    },
    { k: "reports", l: t("nav.reports"), i: BarChart3 },
    { k: "archive", l: t("nav.archive", { fallback: "Archive" }), i: Archive },
    { k: "fleet", l: t("nav.fleet", { fallback: "Fleet" }), i: ServerCog }, // BRIEF 162 — gated below on isCockpit && fleet.view
    { k: "settings", l: t("nav.settings"), i: Settings },
  ].filter((n) => {
    // P21-zero — THE VISIBILITY TERM, ahead of everything else, because
    // everything else below is an authorization question and this one is not.
    // The `if (_appIsAdmin) return true` on the next line is exactly the
    // short-circuit usePermissions.hidden() was written for (its own comment
    // says so): it skips can(), and can() is where the P20f check lives — so
    // the firm's own admin was the one viewer who still saw a Jurisdictions
    // row after the pack hid jurisdictions. Hiding is the one rule an
    // administrator does not override.
    // BRIEF 162 corr(1) — OFF A COCKPIT THE FLEET SECTION DOES NOT EXIST, admin
    // included. This must sit ABOVE the `if (_appIsAdmin) return true`
    // short-circuit: that line is why the fix's first version failed Laurent's
    // glance — an admin on staging (isCockpit false) reached the overlay before
    // the fleet gate and saw the row. isCockpit is an instance fact
    // (/api/version, OPERATOR_TARGETS), the one hide an administrator does not
    // override, exactly like hiddenSurface above.
    if (n.k === "fleet" && !isCockpit) return false;
    if (_appPerms.hiddenSurface(n.k)) return false;
    if (_appIsAdmin) return true;
    if (n.k === "dash") return true;
    // Phase auth-c2 Stage 3 — Settings is universal. The page itself
    // renders Personal Preferences for everyone, with business
    // subsections gated per-panel. The coarse lateral.settingsBusiness
    // gate is retired; the page entry is reachable by all authenticated
    // users.
    if (n.k === "settings") return true;
    if (n.k === "costs")
      return (
        _appPerms.viewer?.isRpOfAnyCase === true ||
        _appPerms.can("lateral.costsAndFees")
      );
    if (n.k === "reports")
      return (
        _appPerms.can("lateral.reportsFinancial") ||
        _appPerms.can("lateral.reportsOperational")
      );
    // BRIEF 162 corr(1) — on a cockpit (the !isCockpit case returned false
    // above, before the admin bypass) the fleet section is ORDINARY: the admin
    // saw it via the short-circuit already, and a non-admin sees it iff it holds
    // fleet.view.
    if (n.k === "fleet") return _appPerms.can("fleet.view");
    // P21-zero — ONE MAP. This was a private `_navPK` copy of the
    // surface->section-key mapping, which made the sidebar the only surface
    // that knew it: the page mount, the search chips and the report tiles each
    // had to invent their own answer, and three of them got it wrong. The map
    // now lives in hooks/sectionMeta.ts and all four read it.
    const pk = SURFACE_SECTION_KEY[n.k];
    return pk ? _appPerms.can(pk) : false;
  }).map((n) => ({
    ...n,
    /* 210 §1 — the row's count as drawn, here and in the Persons flyout: its
       number, or — while the list it counts could not load — the words and
       « Réessayer » (ListNotLoaded's badge). Never « 0 ». */
    cn: (
      <ListNotLoaded lists={NAV_COUNT_LISTS[n.k] ?? []} variant="badge">
        {n.c !== undefined && <span className="badge">{n.c}</span>}
      </ListNotLoaded>
    ),
  }));
  /* Phase 29 (item 4) — lifted from the sidebar nav IIFE so the persons
     flyout panel (portaled to body, outside the <aside>) shares the same
     definition as the in-bar cluster. */
  const personsGrps = [
    {
      id: "collabs",
      label: t("nav.collaborators"),
      keys: ["co2int", "co2ext"],
    },
    { id: "clients", label: t("nav.clients"), keys: ["cl", "cp"] },
    { id: "opponents", label: t("nav.opponents"), keys: ["op", "ocu", "oc"] },
    { id: "third", label: t("nav.third_parties"), keys: ["ex", "me", "ar", "op2"] },
  ];
  /* Phase 29 (item 4) — theme toggle control, lifted out of the pinned .SF
     footer (which now holds the column toggle). It renders as a nav row in
     the scrollable middle opening a sideways flyout (inline row in the
     <600px drawer). Push / session-escape / auto resolution is unchanged —
     only the placement moved. */
  const themeCtl = (() => {
    const _now = new Date();
    const _pushOn =
      firmSettings?.customPushUntil &&
      new Date(firmSettings.customPushUntil) > _now;
    const dayId = firmSettings?.dayThemeId;
    const nightId = firmSettings?.nightThemeId;
    const customId = firmSettings?.customThemeId;
    // Phase 44 D6 — the Personal slot is enabled whenever a durable pick exists
    // and still resolves to a real theme (mirrors Custom being enabled whenever
    // customThemeId is set), independent of which slot is currently active.
    const personalPickValid =
      Number.isFinite(personalTheme) &&
      themes.some((t2: any) => t2.id === personalTheme);
    const effAuto =
      _pushOn && sessionAuto !== null ? sessionAuto : autoNightSwitch;
    let activePos;
    if (effAuto) activePos = "auto";
    else if (_pushOn) {
      activePos =
        sessionTheme === dayId
          ? "day"
          : sessionTheme === nightId
            ? "night"
            : "custom";
    } else {
      // Phase 44 — a 5th "personal" position: prefs.theme holds a real theme id
      // that is NOT one of the three firm slots (set via ThemeManager "Use this
      // theme"). Selecting any firm slot / auto rewrites prefs.theme and clears
      // it (D1/D5). One state (prefs.theme), two views (sidebar + card).
      const _pt = prefs.theme;
      activePos =
        _pt === dayId
          ? "day"
          : _pt === nightId
            ? "night"
            : _pt === customId
              ? "custom"
              : Number.isFinite(_pt) && themes.some((t2: any) => t2.id === _pt)
                ? "personal"
                : "night";
    }
    const pickToggle = (mode: any) => {
      if (mode === "auto") {
        if (_pushOn) {
          setSessionAuto(true);
          setSessionTheme(null);
        } else {
          setAutoNightSwitch(true);
          setSessionTheme(null);
          setSessionAuto(null);
        }
        return;
      }
      if (mode === "personal") {
        // Apply the DURABLE personal pick (not the live active theme); suspends
        // auto. Disabled in the UI when no valid pick exists, so pid is set.
        const pid = personalPickValid ? personalTheme : null;
        if (!pid) return;
        if (_pushOn) {
          setSessionAuto(false);
          setSessionTheme(pid);
        } else {
          setAutoNightSwitch(false);
          setSessionAuto(null);
          setTheme(pid);
          setSessionTheme(null);
        }
        return;
      }
      const tid =
        mode === "day" ? dayId : mode === "night" ? nightId : customId;
      if (!tid) return;
      if (_pushOn) {
        setSessionAuto(false);
        setSessionTheme(tid);
      } else {
        setAutoNightSwitch(false);
        setSessionAuto(null);
        setTheme(tid);
        setSessionTheme(null);
      }
    };
    const _MODES = [
      { k: "day", I: Sun, lbl: t("sidebar.theme.day") },
      { k: "night", I: Moon, lbl: t("sidebar.theme.night") },
      { k: "custom", I: Palette, lbl: t("sidebar.theme.custom") },
      { k: "auto", I: Clock, lbl: t("sidebar.theme.auto") },
      {
        k: "personal",
        I: UserCircle,
        lbl: t("sidebar.theme.personal", { fallback: "Personal theme" }),
      },
    ];
    const modeButtons = _MODES.map(({ k, I: Ic, lbl }) => {
      const on = activePos === k;
      const tid =
        k === "day"
          ? dayId
          : k === "night"
            ? nightId
            : k === "custom"
              ? customId
              : null;
      const disabled =
        k === "auto"
          ? false
          : k === "personal"
            ? !personalPickValid
            : !tid;
      return (
        <button
          key={k}
          type="button"
          onClick={() => pickToggle(k)}
          disabled={disabled}
          title={lbl}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 24,
            padding: 0,
            background: "transparent",
            border: "none",
            borderRadius: RAD.r5,
            color: on ? "var(--ac)" : "var(--tm)",
            opacity: disabled ? 0.3 : 1,
            cursor: disabled ? "default" : "pointer",
            transition: "color .12s",
          }}
          onMouseEnter={(e) =>
            !on && !disabled && (e.currentTarget.style.color = "var(--ts)")
          }
          onMouseLeave={(e) =>
            !on && !disabled && (e.currentTarget.style.color = "var(--tm)")
          }
        >
          <Ic size={14} />
        </button>
      );
    });
    const pushNote = _pushOn
      ? (() => {
          const _ct = themes.find((t2: any) => t2.id === firmSettings?.customThemeId);
          const _nm = _ct?.name || t("sidebar.theme.custom_default");
          const _d = new Date(firmSettings.customPushUntil);
          const _df = `${String(_d.getDate()).padStart(2, "0")}/${String(_d.getMonth() + 1).padStart(2, "0")}/${_d.getFullYear()}`;
          return (
            <div
              style={{
                fontSize: FS.s9,
                color: "var(--am)",
                fontStyle: "italic",
                marginBottom: 4,
                opacity: 0.85,
              }}
            >
              {t("sidebar.theme.active_until", { name: _nm, date: _df })}{" "}
              <span style={{ color: "var(--tm)", fontStyle: "normal" }}>
                {t("sidebar.theme.firm_wide")}
              </span>
            </div>
          );
        })()
      : null;
    const personalNote =
      activePos === "personal"
        ? (
            <div
              style={{
                fontSize: FS.s9,
                color: "var(--am)",
                fontStyle: "italic",
                marginBottom: 4,
                opacity: 0.85,
              }}
            >
              {t("sidebar.theme.personal_active", {
                fallback: "Personal theme active — auto day/night suspended",
              })}
            </div>
          )
        : null;
    return { activePos, modeButtons, pushNote, personalNote };
  })();
  const curL = navI.find((n) => n.k === section)?.l || "";
  /* 195 §C.3e — the `ag` arm that stood here ("New Event") is gone: MEASURED,
     the actions row never rendered on the Agenda section (the deleted chain
     excluded "ag"), so the arm drew nothing, and AgendaView has its own New
     Event button. Under the derived condition it would have drawn one. */
  const addB = ecfg[section]
    ? { l: t("common.add", { fallback: "Add" }), m: ecfg[section].am }
    : section === "docs"
      ? { l: t("addb.add_doc", { fallback: "Add Doc" }), m: "doc" }
      : null;
  const GChatView = () => (
    <GChatViewComponent
      tryNavCase={tryNavCase}
      gChat={gChat}
      setGChat={setGChat}
      gChatInput={gChatInput}
      setGChatInput={setGChatInput}
      gChatType={gChatType}
      setGChatType={setGChatType}
      gChatSearch={gChatSearch}
      setGChatSearch={setGChatSearch}
      gChatMention={gChatMention}
      setGChatMention={setGChatMention}
      gChatMentionQ={gChatMentionQ}
      setGChatMentionQ={setGChatMentionQ}
      gChatRef={gChatRef}
      gChatLinkCase={gChatLinkCase}
      setGChatLinkCase={setGChatLinkCase}
      gChatLinkKind={gChatLinkKind}
      setGChatLinkKind={setGChatLinkKind}
      chatMsgs={chatMsgs}
      setChatMsgs={setChatMsgs}
      chatRead={chatRead}
      setChatRead={setChatRead}
      co={collaborators}
      ca={cases}
      cc={collectiveCases}
      getRecentCases={getRecentCases}
      currentUser={currentUser}
      nav={nav}
    />
  );
  /* B.6 Phase B — the section switch as a named router: one view entry per
     section key (the old renderContent if-chain, branch bodies verbatim),
     with the ecfg -> EntityList entity-list fallback behind it. Section
     keys and ecfg keys are disjoint, so map-first + fallback preserves the
     original first-match order; thunks keep each branch lazy exactly as
     the if-chain did. */
  const SECTION_VIEWS: Record<string, () => any> = {
    dash: () => (
        <DashComponent
          firmSettings={firmSettings} /* 198 §C — the e-mail desk's and the time-entry table's axis input (D.3) */
          ca={cases}
          co={collaborators}
          te={timeEntries}
          sTe={setTimeEntries}
          ag={agendaEvents}
          sAg={setAgendaEvents}
          tk={tasks}
          sTk={setTasks}
          tkTypes={tkTypes}
          evtTypes={evtTypes}
          agTypeFilt={agTypeFilt}
          schedAxis={schedAxis}
          holidays={holidays}
          calDisp={calDisp}
          setAgTypeFilt={setAgTypeFilt}
          currentUser={currentUser}
          showDL={showDL}
          nwh={nwh}
          dTeP={dTeP}
          setDTeP={setDTeP}
          tePeriod={tePeriod}
          setTePeriod={setTePeriod}
          activeTimer={activeTimer}
          activeTimers={activeTimers}
          chatMsgs={chatMsgs}
          gChat={gChat}
          chatRead={chatRead}
          setChatOpen={setChatOpen}
          setTkDelConfirm={setTkDelConfirm}
          s={persist}
          nav={nav}
          om={openModal}
          toast={toast}
          etTag={etTag}
          evtLongestCode={evtLongestCode}
          coAvs={coAvs}
          locIcon={locIcon}
          svTkData={saveTask}
          delTk={delTk}
          TkStatusTag={TkStatusTag}
          startTimer={startTimer}
          getTimerDisplay={getTimerDisplay}
          evToVevent={evToVevent}
          downloadIcs={downloadIcs}
          keepScroll={keepScroll}
          DLBtn={DLBtn}
          OPEN_ST={OPEN_ST}
          setEisenOpen={setEisenOpen}
          userMatrix={userMatrix}
          userMatrixSetQuadrant={userMatrixSetQuadrant}
          cc={collectiveCases}
          inv={invoices}
          onSubtaskDone={askSubtaskDone}
          tryNavCase={tryNavCase}
          /* 174 §4 — the email desk's matter picker needs the same recents
             `MatterPicker` reads everywhere else. Already passed to the five
             131 hosts; this is the sixth. */
          getRecentCases={getRecentCases}
        />
    ),
    gchat: () => GChatView(),
    campaigns: () => <LoadedOnUse load={loadCampaigns} inline>{(m) => <m.default toast={toast} />}</LoadedOnUse>, // 189 §H · 208 §1 — loads when the section opens
    ca: () => CaView(),
    cc: () => CcView(),
    costs: () => (
        <CoView
          _appIsAdmin={_appIsAdmin}
          _appPerms={_appPerms}
          t={t}
          firmSettings={firmSettings} /* 198 §C — the shared period picker's axis input (D.3) */
          currentUser={currentUser} /* 196 §C.2 — the badge's `isMe`, the list's own (buildMatterListCfg's currentUser) */
          cases={cases}
          collectiveCases={collectiveCases}
          timeEntries={timeEntries}
          collaborators={collaborators}
          invoices={invoices}
          nav={nav}
          caseTypesList={caseTypesList}
          caseTypeEntries={caseTypeEntries}
          caseStatusEntries={caseStatusEntries}
          dayTheme={dayThemeVarsOf(themes, firmSettings)} /* 203-glance §C.1 — the documents' palette derives from the firm's DAY theme */
          appFont={appFont} /* 203-glance-2 §C — the documents wear the exporting user's face */
        />
    ),
    ag: () => AgView(),
    docs: () => DocView(),
    archive: () => (
        /* 195 §A — the Archive renders the matter's own cards in their archived
           mode, so it takes what the cards take: the taxonomy chips and their
           longest code, the badge renderer, the location icon, the level
           ladder, the status pill's renderer — App's own closures, passed
           through and narrowed at the screen's own seam. */
        <ArchiveView
          sCa={setCases}
          sCc={setCollectiveCases}
          sTk={setTasks}
          sAg={setAgendaEvents}
          sTe={setTimeEntries}
          nav={nav}
          toast={toast}
          ca={cases as MatterRow[]}
          cc={collectiveCases as MatterRow[]}
          co={collaborators as unknown}
          currentUser={currentUser as unknown}
          tkTypes={tkTypes as unknown}
          evtTypes={evtTypes as unknown}
          etTag={etTag}
          evtLongestCode={evtLongestCode}
          coAvs={coAvs}
          locIcon={locIcon}
          priMap={ITEM_LEVEL}
          itemLevelList={itemLevelList as unknown}
          TkStatusTag={TkStatusTag}
          /* 195 §B.0 — Restaurer on an EVENT opens the event modal prefilled
             from the archived row (the `ag` arm with an id, App.tsx openModal);
             nothing is un-archived until he saves — the save's restore arm
             (saveAgendaEvent) does it in one PUT and calls `done`. */
          restoreEvent={([row, done]) => {
            archiveRestoreRef.current = done;
            openModal("ag", { ...row, archived: false, _caseFixed: true, _restoreFromArchive: true });
          }}
        />
    ),
    fleet: () => <FleetView />, // BRIEF 162 — the operator cockpit (fetches its own data from /api/fleet)
    settings: () => SettingsView(),
    sched: () => (
        <ScheduleView
          schedAxis={schedAxis}
          tryNavCase={tryNavCase}
          co={collaborators}
          sCo={setCollaborators}
          currentUser={currentUser}
          ag={agendaEvents}
          ca={cases}
          cc={collectiveCases} // 191 §C — the overlay's matter name
          nav={nav}
          evtTypes={evtTypes}
          s={persist}
          toast={toast}
          onCallColleague={canCalls ? onCallColleague : undefined}
          callActive={!!callWin}
        />
    ),
    places: () => (
        <PlacesView
          schedAxis={schedAxis}
          tryNavCase={tryNavCase}
          co={collaborators}
          ag={agendaEvents}
          currentUser={currentUser}
          toast={toast}
          viewerCanOverride={_appPerms.can("lateral.places.override")}
          nav={nav}
        />
    ),
    reports: () => (
        <ReportsView
          ca={cases}
          cc={collectiveCases}
          cl={clients}
          co={collaborators}
          op={opponents}
          ocu={opponentLawyers}
          ex={experts}
          ju={jurisdictions}
          te={timeEntries}
          ag={agendaEvents}
          tk={tasks}
          inv={invoices}
          currentUser={currentUser}
          tkTypes={tkTypes}
          evtTypes={evtTypes}
          nav={nav}
          firmSettings={firmSettings}
          dayTheme={dayThemeVarsOf(themes, firmSettings)} /* 203-glance §C.1 — the documents' palette derives from the firm's DAY theme */
          appFont={appFont} /* 203-glance-2 §C — the documents wear the exporting user's face */
        />
    ),
    invoices: () => (
        <>
          <InvoiceView
            inv={invoices}
            sInv={setInvoices}
            ca={cases}
            cc={collectiveCases}
            cl={clients}
            co={collaborators}
            te={timeEntries}
            sTe={setTimeEntries}
            tkTypes={tkTypes}
            currentUser={currentUser}
            s={persist}
            toast={toast}
            nav={nav}
            om={openModal}
            lastCaseCtx={lastCaseCtx}
            onViewInvoice={(id: any) => setGlobalPfMod({ invoiceId: id })}
            onCreateProForma={(payload: any) => setGlobalPfMod(payload)}
            firmSettings={firmSettings}
          />
          {globalPfMod && (
            <ProFormaModal
              key={globalPfMod?.invoiceId ? `pf-view-${globalPfMod.invoiceId}` : "pf-create"}
              pfMod={globalPfMod}
              invoiceId={globalPfMod?.invoiceId || null}
              onClose={() => setGlobalPfMod(null)}
              onRemake={(p: any) => setGlobalPfMod(p)}
              onRestored={(r: any) => {
                if (r && r.id != null) {
                  setInvoices((arr: any) => {
                    const ex = (arr || []).some((i: any) => i.id === r.id);
                    return ex ? arr : [r, ...(arr || [])];
                  });
                }
                /* 153 §2 — the `trashRefreshKey` bump that stood here is gone
                   with InvoiceView's trash list: it existed only to make that
                   list re-fetch and drop the restored row, and there is no
                   such list. Splicing the row into `invoices` above is the
                   whole of what a restore has to do to this screen. */
              }}
              te={timeEntries}
              sTe={setTimeEntries}
              ca={cases}
              cc={collectiveCases}
              cl={clients}
              co={collaborators}
              inv={invoices}
              sInv={setInvoices}
              s={persist}
              toast={toast}
              tkTypes={tkTypes}
              tk={tasks}
              sTk={setTasks}
              currentUser={currentUser}
              firmSettings={firmSettings}
              emailClient={emailClient}
            />
          )}
        </>
    ),
    lib: () => (
        <LibraryView
          books={books}
          setBooks={setBooks}
          co={collaborators}
          currentUser={currentUser}
          toast={toast}
          pname={pname}
          sv={sv}
          s={persist}
        />
    ),
    external: () => <ExternalView />,
  };
  const renderContent = () => {
    // P21-zero — the PAGE MOUNT, on the same predicate as the nav row that
    // used to be the only thing standing in front of it. Nothing here ever
    // asked: SECTION_VIEWS / ecfg rendered whatever `section` held, so the
    // hidden page loaded in full the moment anything set it. Same answer as an
    // unknown section — null — because that is what "this firm does not have
    // it" means.
    if (_appPerms.hiddenSurface(section)) return null;
    /* 210 §1 — a section drawn from a list that could not load says so in
       its place, with « Réessayer » — never « 0 », never an empty list. */
    const _secLists = SECTION_LISTS[String(section)];
    if (_secLists?.some((k) => k in listsNotLoaded)) return <ListNotLoaded lists={_secLists} />;
    const view = SECTION_VIEWS[section];
    if (view) return view();
    if (ecfg[section])
      return (
        <EntityList
          schedAxis={schedAxis}
          {...ecfg[section]}
          detailId={detailId}
          setDetailId={setDetailId}
          confirmDelete={confirmDelete}
          setConfirmDelete={setConfirmDelete}
          clBlocked={clBlocked}
          setClBlocked={setClBlocked}
          t={t}
          toast={toast}
          _appIsAdmin={_appIsAdmin}
          _appPerms={_appPerms}
          currentUser={currentUser}
          openModal={openModal}
          doArchiveClient={doArchiveClient}
          doUnarchiveClient={doUnarchiveClient}
          openMailTemplate={setMailTplClient}
          delClient={delClient}
          del={del}
          persist={persist}
          nav={nav}
          lp={lp}
          cf2={cf2}
          composeTo={composeTo}
          copyText={copyText}
          canSeeMatterDue={canSeeMatterDue}
          canSeeAnyDue={canSeeAnyDue}
          caseStatusesList={caseStatusesList}
          listSort={listSort}
          setListSort={setListSort}
          clFilter={clFilter}
          query={query}
          cases={cases}
          collectiveCases={collectiveCases}
          invoices={invoices}
          clients={clients}
          setClients={setClients}
          opponents={opponents}
          setOpponents={setOpponents}
          opponentLawyers={opponentLawyers}
          setOpponentLawyers={setOpponentLawyers}
          experts={experts}
          setExperts={setExperts}
          jurisdictions={jurisdictions}
          setJurisdictions={setJurisdictions}
          collaborators={collaborators}
          setCollaborators={setCollaborators}
          contactPersons={contactPersons}
          setContactPersons={setContactPersons}
          opponentContacts={opponentContacts}
          setOpponentContacts={setOpponentContacts}
          mediators={mediators}
          setMediators={setMediators}
          arbiters={arbiters}
          setArbiters={setArbiters}
          additionalOpponents={additionalOpponents}
          setAdditionalOpponents={setAdditionalOpponents}
        />
      );
    return null;
  };

  const notifications = (() => {
    if (!currentUser) return [];
    const n: any[] = [];
    // 213B §10 [RULED 43] — the tasks whose standing « En retard » row is built below: while it stands, the task's own
    // reminder (`tkr-`, and its persisted copy among the `db-` rows) is not shown — one row per late task.
    const lateTaskIds = new Set<number>();
    const todStr = new Date().toISOString().slice(0, 10);
    const tomD = new Date();
    tomD.setDate(tomD.getDate() + 1);
    const tomStr = tomD.toISOString().slice(0, 10);
    /* Phase 31 (C4) — surface the manual Outlook import: a bell entry when
       the user's calendar has NEW (not-yet-imported) events in the next 14
       days. Click opens the existing choice-list import on the Agenda view
       — the import itself stays manual-confirm. */
    if (outlookNewCount > 0 && !dismissedReminders.includes("outlook-import")) {
      n.push({
        id: "outlook-import",
        _bell: standingPlace(false), // 209 §26 — recomputed each day: today's
        /* Brief 45 §2 — NO `_item`, and this is the source the brief asked to
           be named. It is not about an item: it is a COUNT across every
           not-yet-imported Outlook event, so there is no id to collapse on and
           inventing one would fold unrelated events together. It stays
           uncollapsed, which is the brief's instruction for exactly this case. */
        type: "reminder",
        icon: <CalendarClock size={17} />,
        msg: t("notif.outlook_import", {
          count: outlookNewCount,
          fallback: `${outlookNewCount} Outlook event${outlookNewCount === 1 ? "" : "s"} to import`,
        }),
        ref: "",
        ts: todStr,
        dismissId: "outlook-import",
        nav: () => {
          setAgAutoImport(true);
          nav("ag");
        },
      });
    }
    // C63 — mention matching follows the same source as the badges.
    const myIni2 = coIni(collaborators.find((x: any) => x.id === currentUser));
    tasks.filter(
      (task: any) =>
        task.assignedCoId === currentUser &&
        task.dueDate &&
        task.dueDate < todStr &&
        taskIsOpen(task, taskStatusEntries),
    ).forEach((task: any) => {
      const cs2 = resolveMatter(task, cases, collectiveCases); // 191 §C — a cc task's ref too
      { const lid = taskIdOf(task); if (lid != null) lateTaskIds.add(lid); } // 213B §10 [RULED 43]
      n.push({
        id: "ovd-" + task.id,
        _bell: standingPlace(true), // 209 §26 — late: the overdue group, as it stood
        /* RULED 2026-08-07 by Laurent — NO `_item`, AND THIS IS AN EXEMPTION
           RATHER THAN AN OVERSIGHT. Say so here, because a missing `_item`
           beside fourteen siblings that have one reads as a line somebody
           forgot.
           AN OVERDUE MARKER IS A STANDING STATE, NOT A NOTIFICATION ABOUT A
           FIRING. Every other source on the `task:` key announces that
           something HAPPENED — a reminder fired, the task was delegated, a
           timer has been running. This one announces that something IS TRUE,
           and stays true until the task is done: its own filter is
           `dueDate < today && status not in (completed, cancelled, archived)`,
           which is a predicate over the task's state and not an event.
           So it does not compete with the item's most recent notification, and
           collapsing it against one was a real loss of signal — a task stopped
           saying "Overdue" because somebody started a timer on it or it was
           delegated, and it dropped out of the daily overview's Overdue
           section with it.
           Being unkeyed cuts BOTH ways and both are wanted: this row is never
           suppressed, and it never suppresses. An overdue task that also fired
           a reminder shows two rows — the standing state and the latest
           firing — which is the shape the ruling describes.
           SCOPE, STATED SO IT IS NOT WIDENED BY ACCIDENT: the exemption is
           THIS source. Five other sources render `type: "overdue"` under some
           condition (`st-`, `tkr-`, `agr-`, `book-sec-`, the `cdd-*` rows) and
           are NOT exempt — each of those IS a firing, gated on a reminder date
           or an elapsed count, and `st-` could not collide with anything
           anyway because no second source writes a `subtask:` key.
           213B §10 [RULED 43, Laurent 29/09] — AMENDED FOR A TASK'S OWN
           REMINDER: « two rows » no longer describes a late task. Once a task
           is late, the reminder it fired leaves the bell — its `tkr-` row is
           not built and its persisted `task_reminder` row is skipped
           (`lateTaskIds`, above) — and this standing row alone says it. The
           exemption stands otherwise: this row is never suppressed by a
           delegation, a timer or any other firing. */
        type: "overdue",
        /* 126 §2 — WHAT CLEARS IT, stated by the row. Its own filter, four
           lines up, is the answer: `dueDate < today && taskIsOpen(...)`, so
           doing the task — or cancelling it, archiving it, or moving its date
           — is what makes this row stop being true. */
        _clears: "task",
        icon: <AlertTriangle size={17} />,
        msg: t("notif.task.overdue", {
          title: task.title,
          fallback: "Overdue: " + task.title,
        }),
        ref: cs2?.ref || "",
        ts: task.dueDate,
        /* Item-8a — glow the target task on arrival (queueScrollToTk), reusing
           the Agenda-click mechanism. tryNavCase enforces access (item 1) — a
           refused nav glows nothing. */
        nav: () => {
          if (task.caseId && tryNavCase("ca", task.caseId)) queueScrollToTk(task.id);
          else if (task.ccId && tryNavCase("cc", task.ccId)) queueScrollToTk(task.id);
        },
      });
    });
    /* Phase M+7 — subtasks coming due. Aggregates undone subtasks belonging to tasks assigned to the current user. Overdue → red 'overdue', due today → 'event', within 3 days → 'reminder'. Beyond 3 days are not surfaced (parent-task badge handles those). */ tasks.filter(
      (task: any) =>
        task.assignedCoId === currentUser &&
        Array.isArray(task.subtasks) &&
        task.subtasks.length > 0 &&
        taskIsOpen(task, taskStatusEntries),
    ).forEach((task: any) => {
      const cs2 = resolveMatter(task, cases, collectiveCases); // 191 §C
      task.subtasks.forEach((st: any) => {
        // Round 13 (C75) — archived subtasks never reach the bell.
        if (st.done || st.archived || !st.dueDate) return;
        const dd = String(st.dueDate).slice(0, 10);
        if (dd > todStr) {
          const diffMs = new Date(dd).getTime() - new Date(todStr).getTime();
          const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
          if (diffDays > 3) return;
        }
        if (dismissedReminders.includes("st-" + st.id)) return;
        const overdue = dd < todStr;
        const dueToday = dd === todStr;
        const _stKey = overdue
          ? "notif.subtask.overdue"
          : dueToday
            ? "notif.subtask.due_today"
            : "notif.subtask.due_soon";
        const _stFb =
          (overdue
            ? "Subtask overdue: "
            : dueToday
              ? "Subtask due today: "
              : "Subtask soon: ") +
          (task.title ? task.title + " — " : "") +
          (st.text || "");
        n.push({
          id: "st-" + st.id,
          // 209 §26 — late, else today's: due today, or due soon and recomputed each day
          _bell: standingPlace(overdue),
          /* The SUBTASK is the item, not its parent task: two subtasks of one
             task are two things to do and must not collapse into one row. */
          _item: "subtask:" + st.id,
          type: overdue ? "overdue" : dueToday ? "event" : "reminder",
          icon: overdue ? (
            <AlertTriangle size={17} />
          ) : (
            <ClipboardClock size={17} />
          ),
          msg: t(_stKey, {
            task: task.title || "",
            subtask: st.text || "",
            fallback: _stFb,
          }),
          ref: cs2?.ref || "",
          ts: dd,
          dismissId: "st-" + st.id,
          nav: () => {
            if (task.caseId && tryNavCase("ca", task.caseId)) queueScrollToTk(task.id); else if (task.ccId && tryNavCase("cc", task.ccId)) queueScrollToTk(task.id);
          },
        });
      });
    });
    agendaEvents.filter((a: any) => {
      const d = a.date?.slice(0, 10);
      return (
        d &&
        (d === todStr || d === tomStr) &&
        (!(a.assignedCollaborators || []).length ||
          (a.assignedCollaborators || []).includes(currentUser))
      );
    }).forEach((a: any) => {
      const cs2 = resolveMatter({ caseId: a.caId, ccId: a.ccId }, cases, collectiveCases); // 191 §C
      const isToday = a.date?.slice(0, 10) === todStr;
      const _day = isToday
        ? t("notif.event.today", { fallback: "Today" })
        : t("notif.event.tomorrow", { fallback: "Tomorrow" });
      n.push({
        id: "ev-" + a.id,
        _bell: eventPlace(a.date, todStr, tomStr), // 209 §26 — today's; tomorrow's after them (addendum 6)
        _item: "agenda:" + a.id,
        type: "event",
        /* 126 §2 — the filter above is `d === todStr || d === tomStr`. Nothing
           the reader DOES clears this one; the calendar does, when the day
           goes by (or when the event is moved or deleted). Said plainly rather
           than left to look like an oversight beside a row with a button. */
        _clears: "event",
        icon: <Calendar size={17} />,
        msg: _day + ": " + (a.type ? a.type + ": " : "") + a.title,
        ref: cs2?.ref || "",
        ts: a.date,
        /* Brief 42 §3 — was `openModal("ag", a)`: the edit modal. Now the
           item in context, highlighted. See `navAgendaEvent` (App.tsx:6735). */
        nav: () => navAgendaEvent(a),
      });
    });
    Object.entries(chatMsgs).forEach(([cid, msgs]: any) => {
      const lr = chatRead[`${cid}:${currentUser}`] || 0;
      msgs
        // A21 (brief 65) — src/lib/mentionFilter.
        .filter((m: any) => isUnreadMention(m, lr, currentUser, myIni2))
        .forEach((m: any) => {
          const cs2 = cases.find((c: any) => c.id === +cid);
          /* BRIEF 102 §1a — KEPT, second branch: this is a PREVIEW, not a
             layout truncation. The snippet is composed INTO a sentence
             ("Mentioned: …") and the bell row that renders it wraps freely
             (App.tsx:11577, `lineHeight: 1.45`, no `overflow`), so there is no
             box to ellipsise against and nothing widens by removing the cut —
             a whole chat message would simply land in the notification list.
             Same at :10088 for the general chat. */
          const _snip = m.text.length > 40 ? m.text.slice(0, 40) + "…" : m.text;
          n.push({
            id: "chat-" + m.id,
            // 209 §26 — a mention of today; an older one leaves the bell (its chat keeps it unread)
            _bell: firedPlace(new Date(m.ts).toISOString(), todStr),
            /* The MESSAGE is the item. Two mentions in one case are two things
               said to you, and the later one does not supersede the earlier. */
            _item: "chatmsg:" + m.id,
            type: "chat",
            // 126 §2 — the filter is `isUnreadMention`. Reading it is the cure.
            _clears: "read",
            icon: <MessageCircle size={17} />,
            msg: t("notif.chat.mentioned", {
              snippet: _snip,
              fallback: "Mentioned: " + _snip,
            }),
            ref: cs2?.ref || "",
            ts: new Date(m.ts).toISOString().slice(0, 10),
            nav: () => {
              /* Round 19 (C104) — chat keys can be cc-scoped ("cc:<id>"):
                 the old +cid coerced those to NaN and dead-jumped into
                 /cases/NaN. Parse the key and run the C85 guard either way
                 (the viewer may hold mentions from chats whose case sits
                 outside his scoped lists — SSE delivers them regardless). */
              const _ccKey = String(cid).startsWith("cc:");
              const _ok = _ccKey
                ? tryNavCase("cc", +String(cid).slice(3))
                : tryNavCase("ca", +cid);
              if (_ok) setChatOpen(true);
            },
          });
        });
    });
    const glr = chatRead["gchat:" + currentUser] || 0;
    gChat
      // A21 (brief 65) — the same filter, against the general chat's own mark.
      .filter((m: any) => isUnreadMention(m, glr, currentUser, myIni2))
      .forEach((m: any) => {
        const _snip = m.text.length > 40 ? m.text.slice(0, 40) + "…" : m.text;
        n.push({
          id: "gchat-" + m.id,
          _bell: firedPlace(new Date(m.ts).toISOString(), todStr), // 209 §26
          _item: "gchatmsg:" + m.id,
          type: "chat",
          // 126 §2 — the same filter against the general chat's own mark.
          _clears: "read",
          icon: <MessagesSquare size={17} />,
          msg: t("notif.chat.general", {
            snippet: _snip,
            fallback: "General: " + _snip,
          }),
          ref: "",
          ts: new Date(m.ts).toISOString().slice(0, 10),
          nav: () => nav("gchat"),
        });
      });
    /* 126 §1 — THE "Delegated to you" ROW IS GONE, AND IT NEVER FIRED.
       Its predicate read `task.delegatedFromId`, a field that existed nowhere
       else in the tree: not in `server/prisma/schema.prisma`, not on the wire,
       not in `shared/`, and with no writer anywhere in `server/`. The docs that
       proposed it say so themselves — `docs/phases/M3_gate2_recon.md:431`,
       "**not in schema**, no FK link". So the condition was always false and
       the row has not rendered since C58 (:10368) ruled the C48 waiting handoff
       DELETED and delegation to be subtask assignment.
       Not to be confused with `Task.delegatedAssigned` / wire `delegatedCoIds`
       (schema.prisma:1219, maintained server-side) — that plane is live and is
       what C60/C65 read. It was never this row's input.
       `notif.task.delegated` stays SEEDED AND UNREAD, per D28: nothing is
       deleted from a translation table. */
    /* 209 §26 — THE RUNNING TIMER'S ROW ("Timer running 20 min: …") LEFT THE BELL:
       the timer has its own place (the column, whose hourglass view leads with
       it). It was built here and nowhere else, so it goes whole — and with it
       its Mac notification, which only ever came from this row (208 §15: the
       bell tells the Mac what is new). `notif.timer.running` keeps its rows. */
    tasks.filter(
      (task: any) =>
        task.assignedCoId === currentUser &&
        task.dueDate &&
        task.reminder &&
        task.reminder !== "none" &&
        taskIsOpen(task, taskStatusEntries) &&
        /* 213B §10 [RULED 43] — ONE ROW PER LATE TASK: while the task's standing
           « En retard » row stands (`ovd-`, above — the same assignee, the same
           openness, the due date passed), the reminder it fired is not shown
           again as a second « En retard » dated the reminder's day. Before the
           due date the reminder shows as before. */
        !lateTaskIds.has(taskIdOf(task) ?? NaN),
    ).forEach((task: any) => {
      const rd = reminderDate(task.dueDate, task.reminder);
      if (
        rd &&
        rd <= todStr &&
        !dismissedReminders.includes("tkr-" + task.id)
      ) {
        const cs2 = resolveMatter(task, cases, collectiveCases); // 191 §C
        const overdue = task.dueDate < todStr;
        const dueToday = task.dueDate === todStr;
        const _tkKey = overdue
          ? "notif.task.overdue"
          : dueToday
            ? "notif.task.due_today"
            : "notif.task.reminder";
        const _tkFb =
          (overdue ? "Overdue: " : dueToday ? "Due today: " : "Reminder: ") +
          task.title;
        n.push({
          id: "tkr-" + task.id,
          // 209 §26 — late with its task; today's when the task is due today or it fired today
          _bell: reminderPlace({ late: overdue, dueToday, firedOn: rd }, todStr),
          _item: "task:" + task.id,
          type: overdue ? "overdue" : dueToday ? "event" : "reminder",
          icon: overdue ? <AlertTriangle size={17} /> : <Bell size={17} />,
          msg: t(_tkKey, { title: task.title, fallback: _tkFb }),
          ref: cs2?.ref || "",
          ts: rd,
          dismissId: "tkr-" + task.id,
          nav: () => {
            if (task.caseId && tryNavCase("ca", task.caseId)) queueScrollToTk(task.id); else if (task.ccId && tryNavCase("cc", task.ccId)) queueScrollToTk(task.id);
          },
        });
      }
    });
    agendaEvents.filter((a: any) => {
      const ass = a.assignedCollaborators || [];
      return (
        a.date &&
        a.reminder &&
        a.reminder !== "none" &&
        (!ass.length || ass.includes(currentUser))
      );
    }).forEach((a: any) => {
      const rd = reminderDate(a.date, a.reminder);
      if (rd && rd <= todStr && !dismissedReminders.includes("agr-" + a.id)) {
        const cs2 = resolveMatter({ caseId: a.caId, ccId: a.ccId }, cases, collectiveCases); // 191 §C
        const overdue = a.date < todStr;
        const dueToday = a.date?.slice(0, 10) === todStr;
        const _agKey = overdue
          ? "notif.event.reminder_overdue"
          : dueToday
            ? "notif.event.reminder_today"
            : "notif.event.reminder";
        const _agTitle = (a.type ? a.type + ": " : "") + a.title;
        const _agFb =
          (overdue ? "Overdue: " : dueToday ? "Today: " : "Reminder: ") +
          _agTitle;
        n.push({
          id: "agr-" + a.id,
          _bell: reminderPlace({ late: overdue, dueToday, firedOn: rd }, todStr), // 209 §26
          _item: "agenda:" + a.id,
          type: overdue ? "overdue" : dueToday ? "event" : "reminder",
          icon: overdue ? <AlertTriangle size={17} /> : <Calendar size={17} />,
          msg: t(_agKey, { title: _agTitle, fallback: _agFb }),
          ref: cs2?.ref || "",
          ts: rd,
          dismissId: "agr-" + a.id,
          /* Brief 42 §3 — was `openModal("ag", a)`: the edit modal. Now the
             item in context, highlighted. See `navAgendaEvent` (App.tsx:6735). */
          nav: () => navAgendaEvent(a),
        });
      }
    });
    books
      .filter((b: any) => b.borrowedById != null && b.borrowedDate)
      .forEach((b: any) => {
        const bd = new Date(b.borrowedDate);
        const td = new Date(todStr);
        const days = Math.floor(((td as any) - (bd as any)) / (1000 * 60 * 60 * 24));
        if (days < 2) return;
        const myRole = collaborators.find((x: any) => x.id === currentUser)?.role;
        const isBorrower = b.borrowedById === currentUser;
        const isSec = myRole === "Executive Secretary";
        if (days >= 2 && isBorrower) {
          n.push({
            id: "book-bw-" + b.id,
            _bell: standingPlace(days >= 3), // 209 §26 — day 2 today's, day 3 on late
            _item: "book:" + b.id,
            type: days >= 3 ? "overdue" : "reminder",
            /* 126 §2 — AND THE REASON THIS IS NOT DERIVED FROM `type`. This
               row is `"overdue"` above three days and `"reminder"` below it,
               and BOTH are cured by returning the book — while `ovd-`, also
               `"overdue"`, is cured by doing a task. One field per row is the
               only reading that can be right about both. */
            _clears: "book",
            icon: <Book size={11} />,
            msg: t("notif.book.return_today", {
              title: b.title,
              fallback: "Please bring back " + b.title + " today",
            }),
            ref: "",
            ts: todStr,
            nav: () => nav("lib"),
          });
        }
        if (days >= 3 && isSec) {
          const bw = collaborators.find((x: any) => x.id === b.borrowedById);
          const bwName = bw
            ? pname(bw)
            : t("notif.fallback.someone", { fallback: "Someone" });
          const bdFmt = b.borrowedDate.split("-").reverse().join("/");
          n.push({
            id: "book-sec-" + b.id,
            _bell: standingPlace(true), // 209 §26
            _item: "book:" + b.id,
            type: "overdue",
            // 126 §2 — the secretary's copy of the same fact, same cure.
            _clears: "book",
            icon: <Book size={11} />,
            msg: t("notif.book.secretary_followup", {
              borrower: bwName,
              title: b.title,
              date: bdFmt,
              fallback:
                bwName +
                " borrowed " +
                b.title +
                " on " +
                bdFmt +
                " and it is not back yet, please follow up",
            }),
            ref: "",
            ts: todStr,
            nav: () => nav("lib"),
          });
        }
      });
    /* Phase 3 Stage 3 fix #8 — CDD expiry notifications. Three sources:
   (a) ContactPerson.idDocumentExpiresAt within 30 days (data already in cp cache);
   (b) /api/reports/cdd `expiring` rollup (review-due rows, within 7-day cushion);
   (c) PEP re-screening 12-month threshold is deferred — would need per-client
       CDD state fetch and isn't wired in this round.
   Dismissal keys follow the "cdd-{rowKind}-{caseId|clientId}" convention. */
    const _cdd30 = new Date();
    _cdd30.setDate(_cdd30.getDate() + 30);
    const _cdd30Str = _cdd30.toISOString().slice(0, 10);
    (contactPersons || []).forEach((person: any) => {
      const exp = person?.idDocumentExpiresAt
        ? String(person.idDocumentExpiresAt).slice(0, 10)
        : null;
      if (!exp || exp > _cdd30Str) return;
      const dk = "cdd-id-" + person.id;
      if (dismissedReminders.includes(dk)) return;
      const ovd = exp < todStr;
      const clRow = clients.find((x: any) => x.id === person.clientId);
      const _clFb = t("notif.fallback.client", { fallback: "client" });
      const clName = clRow
        ? clRow.companyName ||
          [clRow.firstName, clRow.lastName].filter(Boolean).join(" ") ||
          clRow.email ||
          _clFb
        : "";
      const _pName =
        ([person.firstName, person.lastName].filter(Boolean).join(" ") ||
          t("notif.fallback.contact", { fallback: "contact" })) +
        (clName ? " (" + clName + ")" : "");
      const _cddKey = ovd ? "notif.cdd.id_expired" : "notif.cdd.id_expiring";
      const _cddFb = (ovd ? "ID expired: " : "ID expiring: ") + _pName;
      n.push({
        id: dk,
        _bell: standingPlace(ovd), // 209 §26 — expiring today's (recomputed each day), expired late
        _item: "contactperson:" + person.id,
        type: ovd ? "overdue" : "reminder",
        icon: ovd ? <AlertTriangle size={11} /> : <Clock size={11} />,
        msg: t(_cddKey, { person: _pName, fallback: _cddFb }),
        ref: "",
        ts: exp,
        dismissId: dk,
        nav: () => {
          if (person.clientId) nav("cl", person.clientId);
        },
      });
    });
    /* Round 2 #10 — natural-person Client ID expiry. Symmetrical to the
   ContactPerson loop above but reads Client.idDocumentExpiresAt directly
   for individual-type clients (no ContactPerson row needed). */
    (clients || []).forEach((client: any) => {
      if ((client.type || "").toLowerCase() === "company") return;
      const exp = client?.idDocumentExpiresAt
        ? String(client.idDocumentExpiresAt).slice(0, 10)
        : null;
      if (!exp || exp > _cdd30Str) return;
      const dk = "cdd-id-cl-" + client.id;
      if (dismissedReminders.includes(dk)) return;
      const ovd = exp < todStr;
      const clName =
        [client.firstName, client.lastName].filter(Boolean).join(" ") ||
        client.companyName ||
        client.email ||
        t("notif.fallback.client", { fallback: "client" });
      const _ckey = ovd
        ? "notif.cdd.client_id_expired"
        : "notif.cdd.client_id_expiring";
      const _cfb =
        (ovd ? "Client ID expired: " : "Client ID expiring: ") + clName;
      n.push({
        id: dk,
        _bell: standingPlace(ovd), // 209 §26
        _item: "client:" + client.id,
        type: ovd ? "overdue" : "reminder",
        icon: ovd ? <AlertTriangle size={11} /> : <Clock size={11} />,
        msg: t(_ckey, { name: clName, fallback: _cfb }),
        ref: "",
        ts: exp,
        dismissId: dk,
        nav: () => nav("cl", client.id),
      });
    });
    const _cdd7 = new Date();
    _cdd7.setDate(_cdd7.getDate() + 7);
    const _cdd7Str = _cdd7.toISOString().slice(0, 10);
    ((cddReportData && cddReportData.expiring) || []).forEach((item: any) => {
      const due = item?.dueAt ? String(item.dueAt).slice(0, 10) : null;
      if (!due || due > _cdd7Str) return;
      const dk =
        "cdd-" +
        (item.kind || "review") +
        "-" +
        (item.caseId || item.clientId || "");
      if (dismissedReminders.includes(dk)) return;
      const ovd = due < todStr;
      const lbl =
        item.ref ||
        item.name ||
        (item.kind === "client_review"
          ? t("notif.fallback.client", { fallback: "client" })
          : t("notif.fallback.case", { fallback: "case" }));
      const _rkey = ovd ? "notif.cdd.review_overdue" : "notif.cdd.review_due";
      const _rfb = (ovd ? "CDD review overdue: " : "CDD review due: ") + lbl;
      n.push({
        id: dk,
        _bell: standingPlace(ovd), // 209 §26 — a review coming due today's, overdue late
        /* Named from the payload, never parsed back out of `dk`: the key is
           built from `kind` too, so the id alone cannot say which of the two
           this row is about. A row naming neither stays uncollapsed. */
        _item:
          item.caseId != null
            ? "case:" + item.caseId
            : item.clientId != null
              ? "client:" + item.clientId
              : undefined,
        type: ovd ? "overdue" : "reminder",
        icon: ovd ? <AlertTriangle size={11} /> : <Clock size={11} />,
        msg: t(_rkey, { label: lbl, fallback: _rfb }),
        ref: item.ref || "",
        ts: due,
        dismissId: dk,
        nav: () => {
          // C-1 — CDD rows can reference off-scope cases: C85 guard.
          if (item.caseId) tryNavCase("ca", item.caseId);
          else if (item.clientId) nav("cl", item.clientId);
        },
      });
    });
    // Phase 19 — persisted notifications branch (first persisted source).
    // Brief 42 follow-on — THE "db-" PREFIX IS AN IDENTITY, NOT A ROUTE, and
    // this comment used to say the opposite ("ID prefix 'db-' lets
    // markNotifRead() route to the API patch path"). Nothing routes on it any
    // more: read and dismiss both ask `_persistedDbId`, the sidecar below, and
    // the prefix exists only to keep the id unique against the derived
    // sources. A prefix test is a fact the row already states, re-derived —
    // and re-deriving it at three sites is the defect this closes.
    // Read state for these items is row.readAt (server-side); we treat
    // a non-null readAt as "already read" and drop them from the unread
    // set — they still render in the inbox UI but never pop a desktop
    // alert and never count toward the badge.
    //
    // Brief 35 (stage 2) — ANY type renders, so this branch can carry a kind
    // of row nobody has taught it about. The three place.* arms below are
    // untouched; what changed is the arm that catches everything else. It
    // used to set the message to the raw type string and leave the two
    // defaults above it in place — a Bell icon and a click that navigated to
    // PLACES, which is the wrong door for a row about an agenda event or a
    // task, and a door at all for a row that should not offer one. It now
    // reads the same fields the derived sources read (title, case context)
    // out of the payload, and navigates by what the payload names or not at
    // all. Nothing writes an unknown type yet — stage 3 is the first.
    (persistedNotifs || []).forEach((row: any) => {
      const p = row.payload || {};
      let msg = "";
      let icon = <Bell size={11} />;
      let onNav: (() => void) | undefined = () => nav("places");
      let ref = "";
      if (row.type === "place.bump") {
        msg = t("notif.place.bumped", {
          fallback: "Your reservation was overridden",
          date: p.date,
          start: p.startSlot != null ? p.startSlot : "",
        });
        icon = <AlertTriangle size={11} />;
      } else if (row.type === "place.reserve_notify") {
        msg = t("notif.place.reserve", {
          fallback: "Place reserved — you were notified",
          date: p.date,
        });
        icon = <Bell size={11} />;
      } else if (row.type === "place.edit") {
        const _pr = p.prevStartSlot != null ? `${slotToTime(p.prevStartSlot)}–${slotToTime(p.prevEndSlot)}` : "";
        const _nw = p.startSlot != null ? `${slotToTime(p.startSlot)}–${slotToTime(p.endSlot)}` : "";
        msg = t("notif.place.edit", {
          fallback:
            `Your reservation on ${p.date} was changed` + (_pr && _nw ? ` (${_pr} → ${_nw})` : ""),
          date: p.date,
        });
        icon = <AlertTriangle size={11} />;
      } else if (row.type === "client.folder_failed" || row.type === "case.folder_failed") {
        /* ── 206 §7 [RULED — Laurent: "both"] — THE MESSAGE HALF ───────────
           "whoever created the client is told, then and there, that the folder
           was not created. Not a toast that vanishes unread if you can avoid
           it; it has to survive being clicked past."
           A toast already existed (175 §3.3) and is KEPT — it is the "then and
           there". This is the half that survives: a persisted `Notification`
           row, which the bell keeps until it is read and which `readAt` /
           `dismissedAt` already track. Nothing new was built for it; the
           census found the channel already there and already rendered.
           TWO TYPES, ONE ARM, because they are one event on two entities —
           exactly the reasoning 176 §2.2 used to give the client and case
           folder creations ONE key. The payload names the row (`clientId` or
           `caseId`/`ccId`) and carries the folder NAME the server resolved, so
           the bell prints what was attempted rather than an id.
           THE TWO REASONS ARE TWO SENTENCES, not one with a substitution: a
           throttle clears by itself and is worth retrying, a terminal failure
           is a configuration problem and is not. That is the distinction 205
           §8.1 put in the log line, and the person reading the bell needs it
           more than the person reading the log. Literal keys, never computed
           (brief 36). */
        /* NARROWED AT THE SEAM (179): `row.payload` crosses the wire untyped,
           so this arm reads it ONCE through the shape the server publishes
           (`lib/folderNotice.js`'s three call sites) and every field below is
           a real type. */
        const fp = p as { name?: string; reason?: string; clientId?: number; caseId?: number; ccId?: number };
        const _nm = typeof fp.name === "string" && fp.name ? fp.name : "";
        const _subject = _nm === "" ? "a new record" : _nm;
        msg =
          fp.reason === "throttled"
            ? t("notif.folder.failed_throttled", {
                fallback:
                  "The SharePoint folder for " + _subject + " was not created — Microsoft was busy. Retry it from the clients list (Provision missing folders).",
                name: _nm,
              })
            : t("notif.folder.failed", {
                fallback:
                  "The SharePoint folder for " + _subject + " was not created. Retry it from the clients list (Provision missing folders).",
                name: _nm,
              });
        icon = <AlertTriangle size={11} />;
        /* The door is the row itself where the payload names one, and NO door
           at all otherwise — the rule the 128 §3 arm below established: a row
           that cannot say where to go stays inert rather than sending the
           reader to the wrong screen.
           An if-ladder and not a ternary chain: three outcomes nested as
           conditionals is exactly `sonarjs/no-nested-conditional`, and the
           ladder reads as the three doors it is. */
        const _clId = fp.clientId;
        const _ccId = fp.ccId;
        const _caId = fp.caseId;
        onNav = undefined;
        if (row.type === "client.folder_failed") {
          if (_clId != null) onNav = () => { nav("cl", _clId); };
        } else if (_ccId != null) {
          onNav = () => { nav("cc", _ccId); };
        } else if (_caId != null) {
          onNav = () => { nav("ca", _caId); };
        }
      } else if (["client.subfolders_missing", "case.subfolders_missing"].includes(row.type)) {
        /* 211 §11 — the durable half of a subfolder still missing after its
           retry (lib/folderNotice.js `reportMissingSubfolders`): the folder was
           made, the names it lacks are said, and the door is the row. */
        const sp = p as { name?: string; missing?: string[]; clientId?: number; caseId?: number; ccId?: number };
        const _args = { name: sp.name ?? "", list: (Array.isArray(sp.missing) ? sp.missing : []).join(", ") };
        msg =
          sp.clientId != null
            ? (t as TFn)("clients.subfolders_missing", { ..._args, fallback: "The folder of {name} was made without {list}. Check the subfolders from the clients list — Provision missing folders." })
            : (t as TFn)("cases.subfolders_missing", { ..._args, fallback: "The folder of {name} was made without {list}. Check the subfolders from Settings — Folder templates." });
        icon = <AlertTriangle size={11} />;
        onNav = undefined;
        if (sp.clientId != null) onNav = () => { nav("cl", sp.clientId); };
        else if (sp.ccId != null) onNav = () => { nav("cc", sp.ccId); };
        else if (sp.caseId != null) onNav = () => { nav("ca", sp.caseId); };
      } else if (row.type === "agenda.deadlines_assigned") {
        /* Brief 46 §5 — WHAT YOU ARE NOW CARRYING. Written when someone becomes
           the assignee of an event that carries an alert schedule, because the
           rung claim is per (event, lead, date) and knows nothing about the
           recipient set: a colleague added after that morning's rung fired is
           never told about it.

           IT STATES THE TRUE TIME REMAINING AND NEVER A RUNG THAT ALREADY
           FIRED — the ruling's first constraint, and the reason the earlier
           version was declined. `daysLeft` is computed server-side at the
           moment of assignment, so a person handed a matter on day 20 reads
           "In 20 days", never "In 60 days". `alerts.days_left` is the SAME key
           the rungs themselves render, so the two surfaces cannot drift into
           different words for the same fact.

           ONE ROW, EVERY DEADLINE. `items` is already sorted soonest-first by
           the server, so the sentence reads as a priority list. */
        const _its = Array.isArray(p.items) ? p.items : [];
        msg =
          t("alerts.assigned_brief", { count: _its.length }) +
          (_its.length
            ? ": " +
              _its
                .map(
                  (i: any) =>
                    (i.type ? i.type + ": " : "") +
                    (i.title || "") +
                    " — " +
                    t("alerts.days_left", { count: i.daysLeft }),
                )
                .join(" · ")
            : "");
        icon = <Calendar size={17} />;
        const _dcs = resolveMatter(p, cases, collectiveCases); // 191 C.2b — its own expression, moved onto the one resolver
        ref = _dcs?.ref || "";
        /* Navigates through the SOONEST item, which the server put in the
           payload's own `entityId`/`caseId`. When the items span more than one
           case the server leaves the scope null and this stays undefined — the
           row is then inert rather than pointing at a casefile that only part
           of its contents belongs to. */
        onNav =
          p.caseId != null || p.ccId != null
            ? () => {
                navAlertPayload(p);
              }
            : undefined;
      } else if (row.type.startsWith("alert.")) {
        /* Brief 35 (stage 3) — the engine's alerts, now durable. This renders
           THE SAME PAYLOAD the toast renders (App.tsx:20535-20605) from THE
           SAME four label keys, so the bell row and the toast are two
           renderings of one row rather than two descriptions of one event.
           The label composition follows the derived agenda source above
           (`_day + ": " + type + ": " + title`) — one convention for a bell
           row, not a second one for this type. */
        const _al =
          /* Brief 46 §2 — A LATE ROW NEVER RENDERS AS A COUNTDOWN, and this
             branch is first for that reason. The engine's catch-up fires a
             window it missed while the process was down; by the time it
             arrives the deadline may be nearer than the alert says, or past.
             Rendering "In 7 days" over a limitation period that expired
             yesterday is the one thing worse than the silence this fixes, so
             the label states what it is — an alert that should have reached
             you on a named morning — and the row's type and title say what it
             is about. `shouldHaveFiredOn` is the stamp lateStamp() writes
             (alertEngine.js:230). */
          p.late
            ? t("alerts.late", { on: fd(p.shouldHaveFiredOn) })
            : p.kind === "event_lead"
            ? t("alerts.lead", withCount(p.leadMinutes, "n", {  }))
            : /* Brief 46 §1 — the all-day lead. `count` and not `n`, because
                 the i18n layer's own plural rule keys on `vars.count`
                 (i18n.tsx:54-56): 1 resolves `alerts.days_left_one`
                 ("Tomorrow"), 0 resolves `_zero` ("Today"), everything else
                 the plural row. The number is READ FROM THE PAYLOAD, never
                 recomputed here — the same discipline `leadMinutes` earned
                 when `alerts.t15` came to lie about its own constant. No
                 `fallback:` literal on this branch: gate 1's rule is that no
                 new literal is written in any language, English included. */
              p.kind === "event_allday_lead"
              ? t("alerts.days_left", { count: p.leadDays })
              : p.kind === "event_t0"
                ? t("alerts.t0", { fallback: "Starting now" })
                : p.kind === "task_reminder"
                  ? t("alerts.task_reminder", { fallback: "Task reminder" })
                  : /* 182 C.2 — the silence alarm reads its two numbers out of the
                       payload, as the leads do. No `fallback:` — seeded ×3. */
                    p.kind === "silence"
                    ? t("alerts.silence", withCount(p.days, "days", { date: fd(p.since) }))
                    : t("alerts.event_reminder", { fallback: "Event reminder" });
        msg =
          _al +
          (p.time ? " · " + p.time : "") +
          ": " +
          (p.type ? p.type + ": " : "") +
          (p.title || "");
        /* Same icon rule as the toast: a task rings a Bell, everything else
           is a Calendar. Size 17 to sit with the derived rows it now joins. */
        icon = p.entity === "task" ? <Bell size={17} /> : <Calendar size={17} />;
        const _acs = resolveMatter(p, cases, collectiveCases); // 191 C.2b — its own expression, moved onto the one resolver
        ref = _acs?.ref || "";
        /* Brief 42 §2 — navigate AND highlight, through `navAlertPayload`
           (App.tsx:6121). Still `undefined` when the payload names no case at all, so
           such a row stays inert rather than wrong: the click handler guards
           with `typeof n.nav === "function"`. A payload carrying no entity /
           entityId simply navigates, exactly as this did before. */
        onNav =
          p.caseId != null || p.ccId != null
            ? () => {
                navAlertPayload(p);
              }
            : undefined;
      } else if (row.type === "auth.record_read") {
        /* ── 128 §3 — THE ALARM GETS ITS WORDS ────────────────────────────
           [MEASURED at 9f5c3da3] `auth.record_read` is a TYPE, not a missing
           i18n key: `authAttempts.js:266` writes it and `:164` reads it back.
           Laurent's bell showed three rows whose ENTIRE TITLE was that string,
           because no arm claimed the type and the general arm below printed it
           (its payload carries no `title`).

           WHY THIS IS BIGGER THAN ITS SIZE. This is brief 81 §6's ruling
           working exactly as designed — "an administrator's read of somebody
           else's record is ITSELF RECORDED, and APPEARS IN THAT PERSON'S OWN
           VIEW… an audit trail with a blind spot at its most sensitive screen
           has the hole exactly where it matters." It fired sixteen times on
           staging (measured) and told the person nothing, because the notice
           had no words. THE AUDIT TRAIL WORKED AND THE ALARM WAS MUTE.

           NO NEW DATA IS NEEDED and none is invented: the payload already
           carries `byId`, `byName` and `at`. The writer falls back
           name → email → null (`authAttempts.js:268`), so this falls back too
           — a null `byName` gets the anonymous sentence rather than the word
           "null", and never a guess at who it was.

           THE TIME IS FORMATTED THE WAY `SignInLine` FORMATS ITS OWN, and
           that is deliberate rather than incidental: these are two sentences
           about the same subject on the same panel, and a reader comparing
           "your previous sign-in was on X" with "somebody read your record on
           Y" must not have to reconcile two date formats to do it. There is no
           shared date+time helper in this tree (eight sites each call
           `toLocaleString(locale, …)` with their own options — surfaced, not
           fixed here), so this matches the sibling rather than minting a
           ninth convention.

           `nav` GOES TO SETTINGS, WHICH IS WHERE THE RECORD IS
           (`SettingsView.tsx:3608`, "Your sign-ins"). The general arm's
           default is `nav("places")`, which for this row would have sent a
           person worried about their account to the room-booking screen. */
        const _who =
          typeof p.byName === "string" && p.byName ? p.byName : null;
        const _when = p.at
          ? new Date(p.at).toLocaleString(locale, {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";
        msg = _who
          ? t("notif.auth.record_read", {
              by: _who,
              at: _when,
              fallback: `${_who} viewed your sign-in record on ${_when}`,
            })
          : t("notif.auth.record_read_anon", {
              at: _when,
              fallback: `An administrator viewed your sign-in record on ${_when}`,
            });
        icon = <Lock size={17} />;
        onNav = () => nav("settings");
      } else {
        /* The general arm. Payload-driven and type-agnostic: it asks the row
           what it is about rather than knowing the type. `title` is the field
           every derived source already renders as its message, and caseId /
           ccId are how they resolve their case ref — so a row written by any
           future producer inherits the bell's existing shape by naming those.
           Size 17 matches the twelve derived sources rather than the 11 the
           place.* rows use; these sit among the derived rows, not among the
           place ones. Navigation goes through tryNavCase, which is the
           access-enforcing primitive the derived sources use — a row naming a
           case the viewer cannot open must not silently open it. A payload
           with no case at all carries NO nav: the click handler already
           guards with `typeof n.nav === "function"`, so the row is inert
           rather than wrong.

           ── 128 §3, SECOND HALF — THE CLASS IS CLOSED ────────────────────
           The line below used to end `: row.type`, and that is how
           `auth.record_read` reached Laurent's screen as a title. The rule
           §3 states is absolute: A PERSISTED NOTIFICATION WHOSE TYPE HAS NO
           RENDERING MUST NOT PRINT THE TYPE. Three candidates, enumerated
           because §3 asks for the decision:

             (a) A GENERIC SENTENCE. The row still appears, still carries its
                 date, its case ref and its click, still counts toward the
                 badge — the reader learns that something about their account
                 or matter happened and when, and loses only an identifier
                 that told them nothing they could act on. RULED.
             (b) OMISSION — drop the row. REFUSED, and it is the tempting one.
                 A notice the server wrote and the client silently discards is
                 the SAME defect as this § in a worse form: the alarm goes
                 from mute to absent, and nobody ever finds out a producer is
                 un-rendered. §3's own finding is the argument against it.
             (c) THE TYPE AS A SMALL TECHNICAL TELL beside a generic title.
                 REFUSED: it relocates the leak instead of closing it. A
                 `<area>.<event>` identifier is not something a lawyer can act
                 on, in any position on the row.

           AND THE DEVELOPER IS TOLD, ONCE PER TYPE PER SESSION. "The audit
           trail worked and the alarm was mute" is this whole §; a fallback
           that hides an un-rendered producer from the people who could give
           it words would be the same silence one level down. `_untypedSeen`
           is module-scope, so a render loop cannot flood the console.

           CENSUS at 9f5c3da3 — `notify()` (lib/notify.js) is the ONLY writer,
           5 call sites, 10 types, and after this commit 10 of 10 render. The
           table is in the commit message. TODAY THIS ARM IS REACHED BY ONE
           ROW ON THIS BOX: tenant 2's `CANARY Notification.type`, the
           tenancy canary — so it is a net rather than dead code, and it is
           the shape 125 §5 ruled on ("a gate can be unreachable today and
           still be the right gate"). */
        msg =
          typeof p.title === "string" && p.title
            ? p.title
            : (() => {
                if (!_untypedSeen.has(row.type)) {
                  _untypedSeen.add(row.type);
                  /* No suppression comment above this call. The tree ships no
                     eslint config, `console` is used unguarded elsewhere
                     (`utils.ts:50`), and the ratchet that counts those comments
                     per file may only shrink. */
                  console.warn(
                    `[notifications] no rendering for type "${row.type}" — the row shows the generic sentence. Give it words in App.tsx's persisted branch.`,
                  );
                }
                return t("notif.untyped", { fallback: "Notification" });
              })();
        icon = <Bell size={17} />;
        const _pcs = resolveMatter(p, cases, collectiveCases); // 191 C.2b — its own expression, moved onto the one resolver
        ref = _pcs?.ref || "";
        /* Brief 42 §2 — navigate AND highlight, through `navAlertPayload`
           (App.tsx:6121). Still `undefined` when the payload names no case at all, so
           such a row stays inert rather than wrong: the click handler guards
           with `typeof n.nav === "function"`. A payload carrying no entity /
           entityId simply navigates, exactly as this did before. */
        onNav =
          p.caseId != null || p.ccId != null
            ? () => {
                navAlertPayload(p);
              }
            : undefined;
      }
      /* 213B §10 [RULED 43] — the persisted copy of a late task's own reminder
         (the engine's `task_reminder`, the one task kind it fires) leaves the
         bell with the `tkr-` row: the standing « En retard » row alone says it. */
      if (isLateTaskReminder(p, lateTaskIds)) return;
      n.push({
        id: "db-" + row.id,
        _bell: firedPlace(row.createdAt, todStr), // 209 §26 — a notice written today; older ones leave the bell
        /* Brief 45 §2 — THE COLLAPSE KEY IS THE ITEM, AND A PERSISTED ROW
           ALREADY STATES ITS ITEM. `entity`/`entityId` are written by the alert
           engine on every fire — all four kinds, verified in
           server/src/lib/alertEngine.js — so this is read, never inferred from
           the type string or the id prefix. `place.*` rows and any future
           payload naming no entity get NO key and stay uncollapsed, which is
           the brief's instruction rather than a guess. */
        _item:
          typeof p.entity === "string" && p.entity && p.entityId != null
            ? p.entity + ":" + p.entityId
            : undefined,
        type: row.type,
        icon,
        msg,
        ref,
        ts: row.createdAt,
        nav: onNav,
        dismissId: "db-" + row.id,
        // Sidecar — markAllRead's bulk API call uses this; per-item
        // read flips also key off it.
        _persistedDbId: row.id,
        _alreadyRead: !!row.readAt,
        /* Brief 42 follow-on §2 — THE ROW'S PARENT CONTEXT, STATED RATHER
           THAN INFERRED. The bell's context block (case title + type badge)
           recovers its parent by parsing the id prefix — it knows
           ovd|tkr|del|ev|agr|st, and "db-" fell through, so an alert showed no
           case in the bell while the SAME row named its case in the login
           modal through `ref`. Two mechanisms for one fact across two of the
           three surfaces, which is the thing this family exists to end.
           A persisted row does not need inferring: the payload already says
           what it is about, and it is the payload `ref` above was resolved
           from. So the row carries its context and the block reads it — the
           block itself is unchanged and there is no second renderer. `kind`
           picks which badge style applies (task vs event); null for a
           `place.*` row, which names no case and correctly shows nothing. */
        _ctx: {
          kind: p.entity === "task" ? "task" : p.entity === "agenda" ? "event" : null,
          /* 116 §4 — WHICH event, not just that it is one. The payload already
             states it (`_item` above is built from this very pair), so §4's
             "reuse that resolution" is literally this field rather than a
             second lookup: the bell needs the id to find the row's `joinUrl`
             and to name the timer it starts. */
          entityId: p.entityId != null ? p.entityId : null,
          // Gated on `entity` so a code can never exist without a kind to
          // style it: the badge renders only for task/event, and a payload
          // with neither must contribute nothing rather than an empty row.
          typeCode: p.entity && typeof p.type === "string" ? p.type : "",
          caseId: p.caseId != null ? p.caseId : null,
          ccId: p.ccId != null ? p.ccId : null,
        },
      });
    });
    /* 209 §26 — THE ROWS THAT LEAVE THE BELL LEAVE HERE, at the source (45 §2's
       one list: the bell, the Daily Overview, the pop-out, the Mac and the
       badge all read this array) and BEFORE the collapse below — so a row that
       leaves can never be the survivor that hides, under its item, a row that
       stays (a timer or yesterday's alert over today's reminder). */
    const kept = bellRows(n);
    kept.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
    /* ── Brief 45 §2 — ONE BELL ROW PER ITEM ──────────────────────────────
       RULED 2026-08-07 by Laurent, off staging: one agenda event produced
       "Dans 60 minutes · 09:37: …" and "Aujourd'hui: …" as two rows, and
       earlier the same day "Dans 15 minutes" and "Commence maintenant". The
       bell shows the LAST notification for an item, and only that one.

       THE COLLAPSE KEY IS `_item`, NEVER THE SOURCE AND NEVER `type`. The two
       rows Laurent saw come from DIFFERENT MECHANISMS — one is a persisted
       `alert.*` row written by the engine inside its FiredAlert claim, the
       other is one of the twelve derived sources computed right here — and a
       key that named the mechanism would have collapsed neither pair. Each
       row states its own item where it is built, above; nothing here parses an
       id prefix or sniffs a type, because a fact the row already states must
       not be re-derived at a second site (the rule brief 42's follow-on
       earned when `"db-"` was being tested at three of them).

       IT IS COLLAPSED HERE, AT THE SOURCE, so there is ONE list. The unread
       count, the bell, the pop-out column, the desktop-notification loop and
       the daily overview all read this array, and the brief's constraint —
       "the unread count must agree with the list" — is met by there being
       nothing for them to disagree about. Collapsing in the renderer and
       leaving the count to its own filter is precisely yesterday's
       two-mechanisms-one-fact defect wearing a new hat.

       THE SURVIVOR IS STRICTLY THE MOST RECENT `ts`, WITH NO EXCEPTIONS, AND
       ITS ONE BAD CASE IS A CHOICE RATHER THAN A DEFECT. Ruled 2026-08-07 by
       Laurent, after the case was measured and put to him. `ts` is the only
       "when" this data has, and it is not one thing: a persisted row carries
       `createdAt`, a real firing time, while a derived row carries THE ITEM'S
       OWN DATE. So when a derived row's date is in the FUTURE it outranks a
       real firing that happened in the past.
       THE CASE, NAMED, so the next reader does not file it as a bug and
       "fix" it: an agenda event TOMORROW whose reminder fires TODAY shows
       "Demain: …" — the general row — and not the later, more imminent
       "Event reminder". `"2026-08-08"` sorts above `"2026-08-07T08:00:00Z"`
       and that is correct string ordering doing exactly what it says.
       LAURENT CHOSE ONE RULE WITH NO EXCEPTIONS OVER A BETTER ANSWER WITH A
       SPECIAL CASE. A second time plane — a real "when was this surfaced" on
       every derived row — is the only honest fix and it is a larger ruling
       than this unit carries. Do not add a per-source tiebreak here; that is
       the special case the ruling declined.

       A row with NO `_item` is never collapsed, and there are now two REASONS
       for that, which is why the field's absence is commented at every site
       that lacks it.
       CANNOT name an item: the Outlook-import row (a count across many events,
       not an item) and any persisted row whose payload names no entity — every
       `place.*` row today. Left uncollapsed rather than keyed on a guess, per
       the brief.
       EXEMPT, deliberately: the `ovd-` overdue marker, ruled 2026-08-07. Its
       argument is at its own push site above and is not repeated here.

       The survivor CARRIES its superseded siblings in `_superseded`, and that
       is what makes reading or dismissing it clear the ITEM rather than the
       row. A superseded row that came back after a refresh would be the same
       defect wearing a third hat. */
    const _seen = new Map();
    const _collapsed = [];
    for (const row of kept) {
      if (!row._item) {
        _collapsed.push(row);
        continue;
      }
      const first = _seen.get(row._item);
      if (first) first._superseded.push(row);
      else {
        row._superseded = [];
        _seen.set(row._item, row);
        _collapsed.push(row);
      }
    }
    return _collapsed;
  })();
  // Unread = (derived items not in notifRead) ∪ (persisted items with
  // readAt == null). Persisted rows track read state server-side; their
  // ID is "db-<row.id>" and they are NOT mirrored into prefs.notifRead.
  // Brief 42 follow-on — this now asks `isNotifRead`, the ONE predicate, and
  // so does the bell row and the compact column. The count and the row
  // disagreeing about one fact is precisely the defect being closed.
  const unreadNotifs = notifications.filter((n) => !isNotifRead(n, notifRead));
  /* 209 §26 (addendum 6) — THE BADGE COUNTS WHAT IS NEW SINCE A BELL WAS LAST
     OPENED, not what is unread. Opening a bell — the header's or the pop-out's —
     records the rows it shows (`prefs.bellSeen`, beside `notifRead` and
     `dismissedReminders`: the user's preferences, which every device re-reads on
     the SSE `prefs` poke), and the badge counts the rows shown since that were
     not among them. A row's unread styling and « Tout marquer comme lu » still
     read `unreadNotifs`. */
  const bellSeen: unknown[] = Array.isArray(prefs.bellSeen) ? prefs.bellSeen : [];
  const bellNew = bellNewCount(notifications, bellSeen);
  const markBellSeen = () => {
    const ids = bellIds(notifications);
    if (ids.length === bellSeen.length && ids.every((x, i) => x === String(bellSeen[i]))) return;
    setPref("bellSeen", ids);
  };
  // While the header's bell is open, what it shows is seen (a row arriving meanwhile too).
  useEffect(() => {
    if (notifOpen) markBellSeen();
  });
  /* Takes the ROW, not the id. Brief 42 follow-on: the previous signature
     took an id and recovered the row's nature by testing for a `"db-"`
     prefix, which meant every caller that wanted the same answer had to
     re-derive it the same way — the second mechanism this defect is made of.
     The row already states what it is (`_persistedDbId`), so it is asked. */
  /* Brief 45 §2 — READING CLEARS THE ITEM, NOT THE ROW. A collapsed survivor
     stands for every row of its item, so marking it read must mark them all;
     otherwise a superseded sibling is still unread in storage, and the day the
     survivor is dismissed it walks back into the bell as an unread row nobody
     has seen since. That is the same defect wearing a different hat, which the
     brief names as the thing not to build.
     BOTH PLANES, because an item's rows can span both — the measured case is
     exactly one persisted alert plus one derived row for one event — and each
     plane is cleared its own way: the server for a persisted row, the
     `prefs.notifRead` id list for a derived one. */
  const notifItemRows = (n: NotifRow | null | undefined): NotifRow[] =>
    n ? [n, ...(n._superseded || [])] : [];
  const markNotifRead = (n: NotifRow | null | undefined) => {
    const rows = notifItemRows(n);
    // Persisted plane: PATCH the server, optimistically flip the local rows.
    // Phase 30 (D-3) — a failed PATCH re-pulls the server state (rollback)
    // instead of leaving the row looking read forever.
    const dbIds = rows
      .map((r) => r._persistedDbId)
      .filter((x) => x != null);
    if (dbIds.length) {
      dbIds.forEach((dbId) => {
        api.patch(`/notifications/${dbId}/read`).catch(() => {
          fetchPersistedNotifs();
        });
      });
      setPersistedNotifs((rows2: any) =>
        rows2.map((r: any) =>
          dbIds.includes(r.id) ? { ...r, readAt: r.readAt || new Date().toISOString() } : r,
        ),
      );
    }
    // Derived plane: the id list. A row on the persisted plane has no entry
    // here and must not get one — its read state is the server's.
    const derivedIds = rows
      .filter((r) => r._persistedDbId == null && r.id != null)
      .map((r) => r.id)
      .filter((id) => !notifRead.includes(id));
    if (derivedIds.length) setNotifRead([...notifRead, ...derivedIds]);
  };
  /* Brief 42 follow-on — "Ignorer", for BOTH planes, in one place.
     `dismissedReminders` is a client-side list the DERIVED sources consult
     when they build themselves; the persisted branch does not consult it and
     must not, because a server-backed row is not un-built by a local array.
     So pushing `"db-N"` into it did nothing whatsoever — the row was rebuilt
     from `persistedNotifs` on the very next render, which is exactly the
     "Ignorer does nothing" symptom.
     A persisted row's durable equivalent of dismissing is MARKING IT READ:
     the same server fact the count already reads, so it survives a reload,
     reaches every device, and dims the row rather than hiding a row the other
     two surfaces would still show. */
  /* ── 128 §2 — AND THAT LAST SENTENCE WAS THE DEFECT, NOT THE DESIGN ────────
     Laurent, glancing #432: "says dismiss but stale button."

     The paragraph above is an accurate description of what the tree could do
     in August: `notifications.js` had FOUR verbs and none of them removed
     anything, so "the durable equivalent of dismissing" was the best available
     answer rather than the right one. 126 §6 then renamed the control to
     Remove / Retirer / Verwijderen and the gap became a LIE ON A BUTTON — the
     word promised a deletion the server could not perform, and the row dimmed
     and stayed.

     `PATCH /notifications/:id/dismiss` is the missing verb (128 §2; the
     migration header carries the full enumeration of why a COLUMN and not the
     `dismissedReminders` blob, and why not a DELETE). `GET /me` no longer
     serves a dismissed row, so this is a removal the reader can trust: it
     survives a reload and it reaches every device, which is what the other
     plane has always done.

     THE TWO PLANES NOW DO THE SAME THING, and that is the point of the §.
     They still do it by different means — a derived id stops the builder
     building, a persisted id stops the server serving — and the routing below
     is unchanged, because `_persistedDbId` is still the fact that says which
     means applies. What changed is that the reader no longer has to care. */
  /* Brief 45 §2 — DISMISSING CLEARS THE ITEM TOO, and for the same reason
     reading does. A collapsed item's rows can sit on both planes at once, so
     this no longer picks a plane off the survivor and stops: it clears every
     row of the item on whichever plane that row lives. `markNotifRead` already
     walks `_superseded` and does the persisted half, so the persisted rows are
     handed to it whole; the derived half is the `dismissId` list, and a
     derived row that carries no `dismissId` (ovd / del / chat / timer — the
     sources that were never dismissible) contributes nothing rather than
     an undefined entry. */
  /* 128 §2 — ONE ACT, TWO ARITIES, and the arity is why this took a list.
     The per-row control and the Daily Overview's "Remove all" are the same
     act, and the honest way to say so is one implementation. Looping the
     single-row form would ALSO have been wrong rather than merely duplicated:
     `dismissedReminders` is derived from the `prefs` captured by THIS render's
     closure, so N synchronous calls each write `[...that same array, ...their
     own ids]` and the last one silently drops the other N-1. Batching both
     planes once is what makes the bulk form correct, not just tidy. */
  const dismissNotifs = (list: (NotifRow | null | undefined)[]) => {
    /* Every row of every item, not just the survivors: brief 45 §2's reason
       is unchanged — a superseded sibling left un-dismissed walks back into
       the bell the moment its survivor goes. The `Set`s make the batch safe by
       construction rather than by an argument about the collapse. */
    const rows = list.flatMap((n) => notifItemRows(n));
    /* The persisted half, through the verb 128 §2 added. OPTIMISTIC REMOVAL,
       ROLLED BACK BY RE-PULL on failure — the Phase 30 (D-3) shape
       `markNotifRead` uses one function up, for the same reason: a row that
       vanished on a request that failed is a row the user believes is gone.
       It is dropped from `persistedNotifs` rather than flagged, because that
       is exactly what the next `GET /me` will do — the route no longer
       returns a dismissed row at all. */
    const dbIds = [
      ...new Set(rows.map((r) => r._persistedDbId).filter((x) => x != null)),
    ];
    if (dbIds.length) {
      dbIds.forEach((dbId) => {
        api.patch(`/notifications/${dbId}/dismiss`).catch(() => {
          fetchPersistedNotifs();
        });
      });
      setPersistedNotifs((rows2: any) =>
        rows2.filter((r: any) => !dbIds.includes(r.id)),
      );
    }
    /* The derived half, unchanged: the `dismissId` list the twelve builders
       consult. A derived row carrying no `dismissId` (ovd / del / chat /
       timer — the sources that were never dismissible) contributes nothing
       rather than an undefined entry. */
    const ids = [
      ...new Set(
        rows
          .filter((r) => r._persistedDbId == null && r.dismissId)
          .map((r) => r.dismissId),
      ),
    ].filter((id) => !dismissedReminders.includes(id));
    if (ids.length) setDismissedReminders([...dismissedReminders, ...ids]);
  };
  const dismissNotif = (n: NotifRow | null | undefined) => dismissNotifs([n]);
  const markAllRead = () => {
    // Persisted bulk read + derived bulk read (the legacy prefs path).
    if (persistedNotifs.some((r: any) => !r.readAt)) {
      api.post("/notifications/me/mark-all-read").catch(() => {
        fetchPersistedNotifs(); // Phase 30 (D-3) — rollback via re-pull.
      });
      setPersistedNotifs((rows: any) =>
        rows.map((r: any) => (r.readAt ? r : { ...r, readAt: new Date().toISOString() })),
      );
    }
    /* Brief 45 §2 — "everything" includes the rows the collapse hid. Reading
       only the survivors would leave every superseded sibling unread in prefs,
       and "mark all read" would be the one action in the bell that does not
       do what it says. */
    const derivedIds = notifications
      .flatMap((n) => notifItemRows(n))
      .filter((r) => !r._persistedDbId)
      .map((r) => r.id);
    setNotifRead(derivedIds);
  };
  /* 208 §15 — THE BELL TELLS THE MAC WHAT IS NEW, NOT WHAT IS UNREAD. It told
     it what was unread: this block ran in the render body and, during working
     hours, made every unread bell item not yet in `notifSentRef` a system
     notification — and that set is new and empty with every page, so each page
     that opened sent the whole unread backlog at once (Laurent's Notification
     Center, prod 24/09: a burst stamped with one minute, « Commence maintenant
     · 09:00 » among it, hours late), titled « Legal Practice Manager », the
     starting value of `firmName` — the firm's own name not loaded yet.
     THE RULE: the Mac hears of a bell item that ARRIVES while the page is open —
     [ADVISOR] first seen after the page's first load of the bell; what is already
     unread when the page opens stays in the bell. The first load is the page's
     data in (`loading` over — the firm's settings among it, so the title is the
     firm's name) and the bell's persisted rows answered once; what the page
     learns within BELL_SETTLE_MS of that (a source answering late — the Outlook
     count) was unread when it opened too. An item that arrives outside the
     working hours is seen, and never sent later: the hours opening is not an
     arrival. An effect now (a notification is a side effect), after each
     render as the render-body block ran: `bellToMac` never sends one twice. */
  const bellFirstLoadAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!currentUser || loading || !persistedAnswered) return;
    const now5 = new Date();
    const { arrived: fresh, firstLoadAt } = bellToMac({ unread: unreadNotifs, seen: notifSentRef.current, firstLoadAt: bellFirstLoadAtRef.current, now: now5.getTime(), settleMs: BELL_SETTLE_MS });
    bellFirstLoadAtRef.current = firstLoadAt;
    if (!fresh.length || !("Notification" in globalThis.window) || Notification.permission !== "granted") return;
    const nowMin = now5.getHours() * 60 + now5.getMinutes();
    const [sh, sm] = (workHours.start || "08:00").split(":").map(Number);
    const [eh, em] = (workHours.end || "19:00").split(":").map(Number);
    if (isWeekendDow(now5.getDay()) || nowMin < sh * 60 + sm || nowMin > eh * 60 + em) return;
    const title = String(firmName || t("app.product_name", { fallback: "Legal Practice Manager" }));
    for (const n of fresh) {
      const notif = new Notification(title + (n.ref ? " — " + n.ref : ""), { body: n.msg, tag: n.id });
      notif.onclick = () => {
        globalThis.window.focus();
        markNotifRead(n);
        n.nav();
        notif.close();
      };
    }
  });
  if (!authed)
    return (
      <LoginView
        onLogin={() => {
          setAuthed(true);
          setLoading(true);
        }}
        firmName={firmName}
        firmLogo={firmLogo}
      />
    );
  if (loading)
    return (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            background: "var(--bd)",
            color: "var(--tm)",
            fontFamily: "'DM Sans',sans-serif",
          }}
        >
          {t("common.loading")}
        </div>
      </>
    );

  /* 186 A.3a [RULED 186 (2)] — the two sidebar chats' props, defined ONCE for
     the sidebar mount and the pop-out portal: one component, two hosts, the
     same values (the case mounts add their own `did`, `ca`). */
  const _genChatProps = {
    tryNavCase, gChat, setGChat, gChatInput, setGChatInput, gChatLinkCase, setGChatLinkCase,
    gChatLinkKind, setGChatLinkKind, chatMsgs, setChatMsgs, chatRead, setChatRead,
    co: collaborators, ca: cases, cc: collectiveCases, getRecentCases, currentUser, nav,
    setSideChat,
  };
  const _caseChatProps = {
    chatMsgs, setChatMsgs, chatRead, setChatRead, co: collaborators, currentUser,
    caseChatInput2, setCaseChatInput2, sideCaseChatRef,
  };
  /* 206C §2 — THE CHAT TITLE, ONE RENDERING. The sidebar's three chat rows —
     the general chat's, a case's, a collective case's — MOVED here out of the
     sidebar block, so a second host can mount the same title instead of a copy
     of it. EXTRACT, DON'T IMPROVE (rule 3): this move changes no DOM — the
     rows' outerHTML is byte-identical before and after it, unread badges
     included (`scripts/drive-206c-glance.js --part=chatrows [--unread]`).
     `caseChatHead` is the two case rows' data half: for a case (`isCc`
     false) or a collective case, the name the row prints and its unread
     count, or null when no such matter is in the list — the rows' own
     `find`, their own read-mark key and their own cut (15 characters for a
     case, 20 for a collective case). `chatTitleRow` is the rendering all
     three rows shared; `col` is the sidebar's folded state. */
  const caseChatHead = (isCc: boolean, id: unknown): { label: string; unread: number } | null => {
    const row: { id: number; title: string } | undefined = isCc
      ? collectiveCases.find((c: any) => c.id === id)
      : cases.find((c: any) => c.id === id);
    if (!row) return null;
    const key = isCc ? "cc:" + String(row.id) : row.id;
    const msgs = chatMsgs[key] || [];
    const lastRead = chatRead[`${key}:${currentUser}`] || 0;
    const unread = currentUser
      ? msgs.filter((m: any) => m.ts > lastRead && m.author !== currentUser).length
      : 0;
    /* BRIEF 102 §1a — KEPT, and §7's first stop is why. There is no CSS
       ellipsis to inherit here: `.nav-label` declares none (App.css:155, :193
       are its only rules and both are `display`), and it cannot be given one
       from this call site — the label sits inside a `<span>` that is a flex
       item of `.nav-item` with the default `min-width: auto`, so nothing
       shrinks until that row is made a bounded flex. That is a change to every
       sidebar row, not to this one. The same holds for the collective chat's. */
    const cut = isCc ? 20 : 15;
    const title = row.title.length > cut ? row.title.slice(0, cut) + "…" : row.title;
    return { label: t("sidebar.case_chat_title", { title }), unread };
  };
  const chatTitleRow = (kind: "g" | "c", label: string, unread: number, col: boolean) => (
    <div
      className="nav-item"
      style={{ cursor: "pointer" }}
      /* 186 A.6 (c) — collapsed, the row is an icon and the tooltip is its only
         label: the chat's NAME (what A.1's tree showed). Expanded, the name is
         on screen and the hover says what the click does (186 (8)). */
      title={col ? label : t("chat.toggle_window")}
      role="button"
      tabIndex={0}
      onClick={() => { toggleChatPopOut(kind); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleChatPopOut(kind);
        }
      }}
    >
      <span>
        {kind === "g" ? (
          <MessagesSquare
            size={19}
            style={{
              display: "inline",
              verticalAlign: "middle",
              marginRight: col ? 0 : 4,
            }}
          />
        ) : (
          <MessageSquareCheck
            size={19}
            style={{
              display: "inline",
              verticalAlign: "middle",
              marginRight: col ? 0 : 4,
            }}
          />
        )}
        <span className="nav-label">{label}</span>
      </span>
      {unread > 0 && kind === "g" && <span className="badge">{unread}</span>}
      {unread > 0 && kind === "c" && (
        <span
          style={{
            fontSize: FS.s8,
            fontWeight: 700,
            color: CT.c_fff,
            background: "var(--rd)",
            borderRadius: RAD.r8,
            padding: "0 4px",
            lineHeight: "14px",
            minWidth: 14,
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {unread}
        </span>
      )}
    </div>
  );
  /* 206C §2 [RULED — Laurent: "now the pop-out is headless: add a header with
     the title « General chat » / « XXX case chat » (same font, icon etc.)" —
     and — "when one clicks on it, it closes the pop-out and returns to the
     in-app version (mirrors the same behaviour of the in-app title of the
     chats)"] — THE POP-OUT'S TITLE IS THE SIDEBAR ROW ITSELF, `chatTitleRow`:
     the same text (the same keys, the case named by the same cut), icon,
     colour and badge, and the same click and Enter/Space — `toggleChatPopOut`
     with the pop-out's own kind, which closes this window and hands the chat
     back to the sidebar. Nothing is designed here, and no key is minted.
     ONE WRAPPER, AND IT IS WHAT MAKES IT THE SAME FONT: the two chat pop-outs
     are pinned at POPOUT_ZOOM (the column's xlarge step, 186 A.6 (a)), so the
     same row would render larger than the sidebar's at every other text size.
     `zoom: var(--col-title-scale)` — the ratio the mirror already writes on
     these two roots (appZoom / POPOUT_ZOOM) for the column's section titles,
     204 P3 §3 [RULED]: pop-out titles follow the app's size — scales row,
     icon and badge together back to the app's own size. `col` is false: a
     window is never the folded rail.
     206D §3 [RULED — Laurent: "the title must be bigger in the pop-outs, to
     follow the fact that the messages' text is bigger there too — around the
     same size as the « Message » field down under, to balance" — and, on
     206C's (a), "make it much bigger"] — THE WRAPPER BECOMES A CLASS
     (`.chatPopHead`, App.css), which sizes the row and the title separately:
     the title's line — icon, text and badge — as tall as the « Message… »
     field, and the row around it 25 % roomier than it was. The figures and
     their measurement are in App.css beside the two rules. */
  const chatPopHead = (row: React.ReactNode) => <div className="chatPopHead">{row}</div>;
  // The general chat's title for both its hosts — the sidebar row (folded or not) and its pop-out.
  const gChatTitle = (col: boolean) => chatTitleRow("g", t("nav.general_chat"), gChatUnread, col);
  // The case chat pop-out's matter is the sidebar row's (`_chatPopDid`), so its title is too.
  const _chatPopCaseHead = _chatPopDid != null ? caseChatHead(section === "cc", detailId) : null;

  /* 204 §1 [RULED — Laurent: "a modal opened while a full-screen pane is open
     renders ABOVE that pane, and the pane stays open behind it"] — WHETHER A
     PANE IS OPEN, told once to every ModalLayout (lib/paneStacking.ts). The
     two full-screen panes are the two names of `headerOverlay` (one owner,
     :1472); the matrix renders only under its permission (the mount below
     gates it the same way), so a denied "eisen" lifts nothing.
     206E §1.3 — AND NOT WHILE THE PHONE'S IN-APP COLUMN IS SHOWN. The column
     is opened by the sidebar's footer toggle under 600 px and replaces the
     whole app (`.colHide` on the sidebar AND on `.main`, whose header then
     measures 0): MEASURED before this line, a pane open at that moment kept
     painting over it — at left 0 and top −1, « Back to app » under it and
     unclickable. The pane is not closed — the toggle leaves it open, as every
     click in the sidebar that is not a section does — it is not SHOWN (both
     mounts below read the same condition), and « Back to app » shows it again. */
  const _paneOpen = !colInApp && (bentoOpen || (eisenOpen && _appPerms.can("surface.eisenhower.user")));
  /* 206E §1.3 [RULED — Laurent: "… saves one click"] — A SECTION CHOSEN IN THE
     SIDEBAR SHOWS THAT SECTION AND CLOSES THE PANE: the sidebar's section rows
     (`NI2` below) and the Persons flyout's (SideFlyPanel), which are the same
     sections one step further in. CENSUS of everything else the sidebar lets
     you click — each LEAVES THE PANE OPEN, and what it opens shows above it:
     the hamburger (the pane follows the fold, §1.1); the firm's name (its
     website, a new tab); « Persons » and « Theme » (SideFlyPanel — lifted over
     an open pane there, and it owns Escape while it stands); « Search » (the
     search popover, already over a pane since 205 §6); the general chat's and
     a case chat's title rows (a pop-out WINDOW); the inline chats themselves
     (their pickers already lift over a pane through `useMenuLevel`); the
     footer's column toggle (a pop-out window on a desktop; the phone's in-app
     column, above). The sidebar holds no timer and no group header (`grpDef`
     is empty). */
  const navFromSidebar = (k: unknown): void => { // the rows' own `n.k` is off the `any` nav list
    nav(k);
    setHeaderOverlay(null);
  };

  return (
    /* 210 §1 — every screen reads which lists could not load, and asks again
       (src/lib/listLoad.ts). */
    <ListLoadContext.Provider value={_listLoad}>
    {/* 157 §2 — the app's ONE confirm, published so the surfaces that used to
       call window.confirm can reach it without a prop-drill. Same instance,
       same dialog (rendered once, below); only the reach is new. */}
    <PaneOpenContext.Provider value={_paneOpen}>
    <ConfirmProvider confirm={confirmAndUndo}>
      {/* BRIEF 05 STEP 5 — mounted ONCE on the app shell. It always was; 126
          §10 is what makes the mount matter, because the ARM and the ROUTE were
          the case-scoped halves and both are now answered per-screen. Renders
          nothing at all until a drag enters. */}
      <EmailDropLayer
        armed={_dropArmed}
        matterBound={!!_dropMatterId}
        onResolveFile={_dropResolveFile}
        onDocuments={_dropDocuments}
        onResolveMessageId={_dropResolveId}
        onSubjectOnly={_dropSubjectOnly}
        onResolved={_dropResolved}
        onFailed={_dropFailed}
      />
      {/* 129 §2 — ONE COMPONENT, TWO SHAPES, ONE STATE. The union is passed
          straight through: the chooser branches on the same discriminant the
          app stores, so there is no second place that can disagree about which
          drop is open. */}
      {dropSubject && (
        <EmailDropChooser
          subject={dropSubject}
          canBillEmails={_dropCanBill}
          canBillDocuments={_dropCanBillDocs}
          onChoose={_dropChoose}
          onChooseDocument={_dropDocChoose}
          /* 130 §1 — THE SECOND STEP'S POPULATION, and it is CASES ONLY. The
             brief's own instruction on measuring it: "If it offers only cases,
             say so and stop — widening it is a separate ruling, not this §."
             Measured, and the answer is per-modal rather than uniform:
             `TaskModal.tsx:207-227`'s `DOSSIER` is a plain `<select>` over
             `cases` alone and cannot represent a collective case at all, while
             `AgendaEventModal.tsx:631` and `TimeEntryModal.tsx:499` both offer
             `[...cases, ...collectiveCases]`. Offering a collective case here
             would send "File it & add to a task" straight into the state 129
             already surfaced and did not fix — a cc-bound drop displays no
             matter in either modal. Archived matters are filtered out, which is
             the filter the Task modal's own select applies. */
          matters={[
            ...cases
              .filter((c: any) => c.status !== "archived")
              .map((c: any) => ({ ...c, kind: "ca" as const })),
            ...collectiveCases
              .filter((c: any) => c.status !== "archived")
              .map((c: any) => ({ ...c, kind: "cc" as const })),
          ].sort((a: any, b: any) =>
            (a.title || "").localeCompare(b.title || ""),
          )}
          onChooseMatter={_dropDocMatter}
          /* The act is un-chosen and the file stays put — the union's `choice`
             field is the whole of "which step am I on", so going back is
             clearing it. */
          onBack={() =>
            setDropSubject((p) =>
              p && p.kind === "document" ? { ...p, choice: null } : p,
            )
          }
          onClose={() => setDropSubject(null)}
        />
      )}
      {/* 129 §3 — 127 §2's collision dialog, SECOND MOUNT. The retry differs
          from the first attempt in one argument, and it resumes the choice the
          user already made rather than restarting the drop. */}
      {docConflict && (
        <MatterFileConflictDialog
          conflict={docConflict}
          busy={docFiling}
          onVersion={() => {
            docConflict.answer("version");
          }}
          onCancel={() => {
            docConflict.answer("skip");
          }}
        />
      )}
      {/* 129 §5 — the SECOND MOUNT of the billing browser. Same component,
          same props `CaseDocumentsSection.tsx:961` passes, plus the starting
          position. `cs` is `{ id }` because that is all the modal reads off it
          (`urlBase`, :112-115) — passing a whole matter row would imply it
          reads more. */}
      {docBill && (
        <Suspense fallback={null}>
          <DocumentBillingModal
            open
            onClose={() => setDocBill(null)}
            cs={{ id: docBill.id }}
            kind={docBill.kind}
            startAt={docBill.start}
            toast={toast}
            onBilled={() => window.dispatchEvent(new CustomEvent("lx:reload-time-entries"))}
          />
        </Suspense>
      )}
      <div className="app">
        {/* BRIEF 98 §1 — SEPARATE FROM THE UPDATE BANNER, AND DELIBERATELY
            WITHOUT A RELOAD BUTTON. Reloading fetches the same new bundle from
            nginx and leaves the old API exactly where it was, so offering it
            would be an action that cannot work — the remedy is `pm2 restart
            lexcrm-staging`, and it belongs to whoever runs the box.
            BRIEF 112 §5 (RULED 2026-08-21) — AN OPERATOR INSTRUMENT, NOT A
            STAFF MESSAGE. Two consequences, and the second half of 98 §1's
            "not dismissable" rationale is overruled by the ruling itself:
            (a) it renders only for isAdmin viewers — the remedy belongs to
            whoever runs the box, and so does the warning;
            (b) it gains a dismiss (×) that silences it for the SESSION. The
            state is MEMORY (React state), not sessionStorage, and the choice
            is forced by the ruling's own parenthetical: the dismissal must
            NOT survive a reload (after a reload the condition being true
            again is worth seeing) — and sessionStorage DOES survive reloads;
            it clears on tab close, which is the wrong boundary. Plain state
            dies with the document, which is exactly the window ruled.
            The detection is untouched. */}
        {apiStale && _appIsAdmin && !staleDismissed && (
          <div className="UpdateBanner UpdateBannerStale" role="alert">
            <span>
              {/* BRIEF 106 §2d — THE SENTENCE CHANGES WITH THE MEANING, AND
                  IT NEEDED TO. It read "This page is newer than the server
                  answering it", which described the BUNDLE skew: under §2c
                  this banner no longer fires for that, and the page may be
                  byte-identical to the one the server booted with. What is
                  true now is about the server alone.
                  AND IT IS A NEW KEY, per the 2026-08-19 ruling: the seed is
                  `createMany({ skipDuplicates: true })`, so editing
                  `deploy.api_stale`'s rows would have changed nothing on any
                  database that already has them — the file would say the
                  sentence was fixed while every screen kept the old one. The
                  old rows stay, unread. */}
              {t("deploy.server_stale", {
                fallback:
                  "The server's code has changed since this process started. Until the API is restarted, parts of the app may fail in ways that look like data errors.",
              })}
            </span>
            <button
              type="button"
              className="UpdateBannerClose"
              aria-label={t("common.dismiss", { fallback: "Dismiss" })}
              onClick={() => setStaleDismissed(true)}
            >
              ×
            </button>
          </div>
        )}
        {bundleStale && _appIsAdmin && !bundleDismissed && (
          <div className="UpdateBanner UpdateBannerStale" role="alert">
            <span>
              {t("deploy.bundle_stale", {
                fallback:
                  "What you are looking at is NOT the current code — the bundle is older than the source. Reloading will not help; it needs a rebuild (npm run build).",
              })}
            </span>
            <button
              type="button"
              className="UpdateBannerClose"
              aria-label={t("common.dismiss", { fallback: "Dismiss" })}
              onClick={() => setBundleDismissed(true)}
            >
              ×
            </button>
          </div>
        )}
        {newVersion && (
          <div className="UpdateBanner" role="status">
            <span>
              {t("deploy.new_version", {
                fallback: "A new version of LexCRM is available.",
              })}
            </span>
            <button type="button" onClick={() => window.location.reload()}>
              {t("deploy.reload", { fallback: "Reload" })}
            </button>
            <button
              type="button"
              className="UpdateBannerClose"
              aria-label={t("common.dismiss", { fallback: "Dismiss" })}
              onClick={() => setNewVersion(null)}
            >
              ×
            </button>
          </div>
        )}
        {/* 77 §4a — the demo warning, stacked UNDER the simulation banner when
            both are up (that one owns top: 0). A person viewing-as inside a
            demo tenant is being told two different things and needs both. */}
        {_demoTenant && <DemoBanner top={simulation ? 26 : 0} />}
        {simulation &&
          (() => {
            const _su = collaborators.find((c: any) => c.id === simulation.simulatedUserId);
            const _su_nm = _su
              ? pname(_su)
              : "user #" + simulation.simulatedUserId;
            const _su_role = _su?.role ? roleLabel(_su.role) : "";
            return (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: Z.top,
                  padding: "6px 12px",
                  background: "rgba(251,191,36,.95)",
                  color: CT.c_1a1a1a,
                  fontSize: FS.s11,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,.4)",
                }}
              >
                <span>
                  {t("simulation.viewing_as")} {_su_nm.toUpperCase()}
                  {_su_role ? " — " + _su_role : ""}
                </span>
                <button
                  type="button"
                  onClick={endSimulation}
                  style={{
                    padding: "2px 10px",
                    fontSize: FS.s10,
                    fontWeight: 700,
                    background: CT.c_1a1a1a,
                    color: CT.c_fbbf24,
                    border: "none",
                    borderRadius: RAD.r4,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {t("simulation.exit")}
                </button>
              </div>
            );
          })()}
        {sideMob && <div className="mobile-overlay" onClick={() => setSideMob(false)} />}
        <aside
          ref={sidebarRef}
          className={`sidebar${sideCol && !sideMob ? " col" : ""}${sideMob ? " mob" : ""}${colInApp ? " colHide" : ""}`}
          data-theme={theme}
        >
          <div
            className="sidebar-brand"
            style={{
              padding: "12px 14px",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              overflow: "hidden",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <button
                className="hamburger"
                onClick={toggleSideCol}
                title={sideCol ? t("sidebar.expand") : t("sidebar.collapse")}
              >
                <Menu size={16} />
              </button>
              {firmLogo && !sideCol && (
                <img
                  src={firmLogo}
                  alt=""
                  style={{
                    maxHeight: 24,
                    maxWidth: 60,
                    objectFit: "contain",
                    flexShrink: 0,
                  }}
                />
              )}
              {/* BRIEF 92 §2 — THE NAME LINKS TO THE FIRM'S WEBSITE, AND THE
                  ANCHOR GOES INSIDE THE <h1> RATHER THAN AROUND IT.
                  §2b asks that the link not cost the element what it carries.
                  Inside, it costs nothing by construction: `flex: 1`,
                  `minWidth: 0` and the three ellipsis properties stay on the
                  same box they were written for, and `.sidebar.col
                  .sidebar-brand h1::first-letter` (App.css) still selects the
                  first letter of the block — a pseudo-element that reads
                  through inline children, so the collapsed rail's monogram is
                  unaffected. Wrapping the <h1> would have moved every one of
                  those onto a new flex child.
                  `firmWebsiteHref` returns null for unset, blank and refused
                  values alike, so "no empty anchor, no dead cursor" is the
                  shape of the value rather than a second test here.
                  New tab: the firm's site is not this app, and a same-tab
                  navigation would discard an open modal's unsaved form. */}
              <h1
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {(() => {
                  const _label =
                    firmName ||
                    t("app.product_name", {
                      fallback: "Legal Practice Manager",
                    });
                  const _site = firmWebsiteHref(firmSettings?.firm?.website);
                  return _site ? (
                    <a href={_site} target="_blank" rel="noopener noreferrer">
                      {_label}
                    </a>
                  ) : (
                    _label
                  );
                })()}
              </h1>
            </div>
            {/* P21-zero round 3 — the PRODUCT line honours windowTitle. The
                <h1> above is the FIRM's name; this line is what the product
                calls itself, and it was a bare literal — the one surface a
                pack could not reach. So the core tenant's tab (:1135) and its
                installed-app manifest (routes/manifest.js:42) both read
                "Practice Manager" while the sidebar still said LEGAL PRACTICE
                MANAGER. Same precedence as those two MINUS the firm-name rung:
                falling back to firmName here would only repeat the <h1>. A
                NULL windowTitle — WATT, and every tenant that never set one —
                is the literal, unchanged. */}
            <p className="sidebar-subtitle">
              {firmSettings?.windowTitle ||
                t("app.product_name", {
                  fallback: "Legal Practice Manager",
                })}
              <span className="sidebar-credit">
                {t("app.credit", { fallback: "© Laurent Arnauts" })}
              </span>
            </p>
          </div>
          <div className="sidebar-scroll">
            <nav className="sidebar-nav">
              {(() => {
                const isCol = !!sideCol && !sideMob; // `sideCol` is off the `any` prefs object
                const grpDef: any[] = [];
                const grpKeys = new Set(grpDef.flatMap((g) => g.keys));
                /* Phase 29 — personsGrps lifted to App scope (shared with
                   the sideways flyout panel rendered outside this aside). */
                const personsKeys = new Set(personsGrps.flatMap((g) => g.keys));
                const NI2 = (n?: any, icSize?: any) => (
                  <div
                    key={n.k}
                    className={`nav-item ${section === n.k ? "on" : ""}`}
                    onClick={() => {
                      navFromSidebar(n.k); // 206E §1.3 — and the pane closes
                      if (sideMob) setSideMob(false);
                    }}
                    title={isCol ? n.l : ""}
                  >
                    <span>
                      {n.i && (
                        <n.i
                          size={icSize || 19}
                          style={{
                            display: "inline",
                            verticalAlign: "middle",
                            marginRight: isCol ? 0 : 4,
                          }}
                        />
                      )}
                      <span
                        className="nav-label"
                        style={
                          n.k === "dash"
                            ? { fontWeight: 700, textTransform: "uppercase" }
                            : undefined
                        }
                      >
                        {n.l}
                      </span>
                    </span>
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      {n.cn /* 210 §1 — the number, or the words (navI) */}
                    </span>
                  </div>
                );
                const out: any[] = [];
                let grpInserted = new Set();
                let personsInserted = false;
                navI.forEach((n) => {
                  if (personsKeys.has(n.k)) {
                    if (!personsInserted) {
                      personsInserted = true;
                      out.push(
                        <div key="persons">
                          <div
                            className={
                              "nav-item" + (sideFly?.id === "persons" ? " on" : "")
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              /* Stage-3 C24 — one centered popover in every
                                 mode (expanded / rail / drawer); the old
                                 drawer vertical fallback is gone. */
                              openSideFly("persons");
                            }}
                            title={isCol ? t("nav.persons") : ""}
                          >
                            <span>
                              <Users
                                size={19}
                                style={{
                                  display: "inline",
                                  verticalAlign: "middle",
                                  marginRight: isCol ? 0 : 4,
                                }}
                              />
                              <span className="nav-label">{t("nav.persons")}</span>
                            </span>
                            <span
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <span
                                className="nav-label"
                                style={{ color: "var(--tm)", flexShrink: 0 }}
                              >
                                <ChevronRight size={11} />
                              </span>
                            </span>
                          </div>
                        </div>,
                      );
                    }
                    return;
                  }
                  if (grpKeys.has(n.k)) {
                    const g = grpDef.find((g2) => g2.keys.includes(n.k));
                    if (g && !grpInserted.has(g.id)) {
                      grpInserted.add(g.id);
                      const items = g.keys
                        .map((k: any) => navI.find((x) => x.k === k))
                        .filter(Boolean);
                      const total = items.reduce((s: any, x: any) => s + (x.c || 0), 0);
                      const isOpen = navGrp[g.id];
                      const hasActive = items.some((x: any) => x.k === section);
                      out.push(
                        <div key={g.id}>
                          <div
                            className="nav-group-header"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "6px 14px",
                              cursor: "pointer",
                              color: "var(--tm)",
                              fontSize: FS.s10_5,
                              fontWeight: 600,
                              userSelect: "none",
                            }}
                            onClick={() =>
                              setNavGrp((p: any) => ({ ...p, [g.id]: !p[g.id] }))
                            }
                            title={isCol ? g.label : ""}
                          >
                            <span className="nav-group-count">
                              {isOpen || hasActive ? (
                                <ChevronDown
                                  size={10}
                                  style={{ flexShrink: 0 }}
                                />
                              ) : (
                                <ChevronRight
                                  size={10}
                                  style={{ flexShrink: 0 }}
                                />
                              )}
                            </span>
                            <span className="nav-group-label" style={{ flex: 1 }}>
                              {g.label}
                            </span>
                            {total > 0 && <span className="badge">{total}</span>}
                          </div>
                          {(isOpen || hasActive || isCol) && (
                            <div className="nav-group-children" style={{ paddingLeft: 10 }}>
                              {items.map((n2: any) => NI2(n2))}
                            </div>
                          )}
                        </div>,
                      );
                    }
                  } else if (n.k === "gchat") {
                    out.push(
                      <div key="gchat">
                        {/* 206C §2 — the row, moved to `chatTitleRow` above. */}
                        {gChatTitle(isCol)}
                        {!gChatPopCtr && <SidebarGenChat {..._genChatProps} />}
                      </div>,
                    );
                  } else {
                    out.push(NI2(n));
                  }
                });
                const _ccIdx = out.findIndex((x) => x && x.key === "cc");
                const _chatPos = _ccIdx >= 0 ? _ccIdx + 1 : out.length;
                const _chatItems = [];
                /* 206C §2 — the two case rows' name and unread count come from
                   `caseChatHead`, their rendering from `chatTitleRow` (both
                   above, moved there unchanged). */
                if (section === "ca" && detailId) {
                  const scHead = caseChatHead(false, detailId);
                  if (scHead) {
                    _chatItems.push(
                      <div key="sidecasechat">
                        {chatTitleRow("c", scHead.label, scHead.unread, isCol)}
                        {!cChatPopCtr && (
                          <SidebarCaseChat {..._caseChatProps} did={detailId} />
                        )}
                      </div>,
                    );
                  }
                }
                if (section === "cc" && detailId) {
                  const ccHead = caseChatHead(true, detailId);
                  if (ccHead) {
                    _chatItems.push(
                      <div key="sideccchat">
                        {chatTitleRow("c", ccHead.label, ccHead.unread, isCol)}
                        {!cChatPopCtr && (
                          <SidebarCaseChat {..._caseChatProps} did={"cc:" + String(detailId)} />
                        )}
                      </div>,
                    );
                  }
                }
                if (_chatItems.length) out.splice(_chatPos, 0, ..._chatItems);
                const _canSearchNav =
                  _appIsAdmin || _appPerms.can("lateral.search");
                if (_canSearchNav) {
                  const searchItem = (
                    <div
                      key="gsearch"
                      className="nav-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setSearchDlg(true);
                        setSDlgQ("");
                        setSDlgMore({});
                      }}
                      title={isCol ? t("nav.search") : ""}
                    >
                      <span>
                        <SearchCheck
                          size={19}
                          style={{
                            display: "inline",
                            verticalAlign: "middle",
                            marginRight: isCol ? 0 : 4,
                          }}
                        />
                        <span className="nav-label">{t("nav.search")}</span>
                      </span>
                      {/* Stage-3 C6 — empty right-side span mirroring NI2's
                          two-children structure: in the collapsed rail the
                          flex gap centers the children as a GROUP, so a
                          single-child row sat ~4px right of every other
                          icon's axis. */}
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      ></span>
                    </div>
                  );
                  const settingsIdx = out.findIndex(
                    (x) => x.key === "settings",
                  );
                  if (settingsIdx >= 0) {
                    out.splice(settingsIdx + 1, 0, searchItem);
                  } else {
                    out.push(searchItem);
                  }
                }
                /* Phase 29 (item 4, Stage-3 C24) — the theme toggle lives in
                   the scrollable middle (the pinned .SF slot below holds the
                   column toggle) and opens the unified CENTERED popover in
                   every mode. */
                out.push(
                  <div key="themeRow">
                    <div
                      className={
                        "nav-item" + (sideFly?.id === "theme" ? " on" : "")
                      }
                      style={{ cursor: "pointer" }}
                      onClick={() => openSideFly("theme")}
                      title={
                        isCol
                          ? t("sidebar.theme.label", { fallback: "Theme" })
                          : ""
                      }
                    >
                      <span>
                        <Palette
                          size={19}
                          style={{
                            display: "inline",
                            verticalAlign: "middle",
                            marginRight: isCol ? 0 : 4,
                          }}
                        />
                        <span className="nav-label">
                          {t("sidebar.theme.label", { fallback: "Theme" })}
                        </span>
                      </span>
                      <span style={{ display: "flex", alignItems: "center" }}>
                        <span
                          className="nav-label"
                          style={{ color: "var(--tm)", flexShrink: 0 }}
                        >
                          <ChevronRight size={11} />
                        </span>
                      </span>
                    </div>
                  </div>,
                );
                return out;
              })()}
            </nav>
          </div>
          <div className="sidebar-footer">
            {/* Phase 29 (item 4, Stage-3 C3/C4/C5) — pinned bottom slot: the
                column toggle, visually a twin of the hamburger (same .hb
                class, size-16 icon, same left alignment/padding in its
                zone; the zone is delimited by the same 1px rule as the .SB
                header). Desktop: opens/closes the floating pop-out column;
                phone: flips the in-app column. */}
            <button
              type="button"
              className={"hamburger" + (popOutCtr || colInApp ? " ton" : "")}
              style={{ margin: 0 }}
              onClick={toggleColumn}
              title={t("col.toggle.title", { fallback: "Compact panel" })}
            >
              {/* Corr-3 (item 10) — bolder/stronger popout arrow (was too
                  subtle): larger glyph + thicker stroke. */}
              <SquareArrowOutUpRight size={19} strokeWidth={2.5} />
            </button>
          </div>
        </aside>
        {/* Phase 29 (item 3) — phone mode: the app opens INTO the column.
            The sidebar + main view stay mounted but hidden (.colHide), so
            toggling back to the full app preserves every view state; modals
            and dialogs are siblings of .M and keep rendering normally. */}
        {colInApp && <div className="CColWrap">{renderColumn(false)}</div>}
        {/* Phase 29 (item 4) — sideways flyout panels (theme / persons),
            portaled to body so aside.S's overflow:hidden can't clip them.
            Suppressed in the <600px drawer (vertical fallback in-bar). */}
        {/* Stage-3 C24 — Persons / Theme as the unified CENTERED popover:
            blurred .OV backdrop (outside-click closes), .PopPanel chrome
            shared with the Search popover. Works identically from the
            expanded bar, the collapsed rail and the <600px drawer. */}
        <SideFlyPanel
          sideFly={sideFly}
          setSideFly={setSideFly}
          t={t}
          themeCtl={themeCtl}
          personsGrps={personsGrps}
          navI={navI}
          nav={navFromSidebar} // 206E §1.3 — a Persons section closes the pane too
        />
        {/* 190 round 8 [AK] — the provider hands the footer slot's node to
            every host under .main (CountersStrip portals the strip into it). */}
        <ContentFooterSlot.Provider value={contentFooter}>
        <div className={"main" + (colInApp ? " colHide" : "")} data-theme={theme}>
          <div className="header">
            <h2>{curL}</h2>
            <div className="header-actions">
              {activeTimer &&
                !popOutCtr &&
                (() => {
                  // BRIEF 91 §3a — resolves a task OR an event's subtask.
                  const at = timerSubject(activeTimer, tasks, agendaEvents)
                    ?.parent as any;
                  /* 191 §C — the matter through the one resolver (the subject's
                     parent carries caseId / ccId for a task and an event alike). */
                  const cs2h = resolveMatter(at, cases, collectiveCases);
                  // Phase 27 — no hard 12-char slice: the CSS ellipsis span
                  // (maxWidth + text-overflow) truncates ONLY when space-
                  // constrained, so short titles show in full.
                  const csTrunc = cs2h ? cs2h.title : "";
                  /* BRIEF 102 §1a — AND THIS ONE WAS ALREADY DEAD. The three
                     lines above cured the CASE title in Phase 27; the TASK
                     title's 25-character cut sat here for the rest of that
                     session and was never rendered — `trunc` had no reader in
                     this file and `tTitle` had no reader but `trunc`. The cut
                     the header actually shows is `csTrunc`'s, above, and it is
                     the CSS one. Deleting the pair changes no pixel. */
                  const intMs = pomInterval * 60 * 1000;
                  const pomR = 7;
                  const pomCirc = 2 * Math.PI * pomR;
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        marginLeft: "auto",
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                    >
                      {/* 147 §1.6 — A RING FOR A DISABLED COUNTDOWN IS A LIE.
                          This site carried no `|| 20`, so with the interval at
                          0 it did not fall back to twenty — it drew an EMPTY
                          ring that never fills, for a countdown that will
                          never fire. When the pomodoro is off the ring is
                          ABSENT, not frozen; the timer's own elapsed readout
                          and its controls beside it are untouched. */}
                      {intMs > 0 && (
                      <SelfTick active={!activeTimer.paused}>
                        {() => {
                          const _pomEl = getPomElapsed();
                          const _pomProg = (_pomEl % intMs) / intMs;
                          return (
                            <svg
                              width={18}
                              height={18}
                              style={{ flexShrink: 0 }}
                            >
                              <circle
                                cx={9}
                                cy={9}
                                r={pomR}
                                fill="none"
                                stroke="var(--br)"
                                strokeWidth={2}
                              />
                              <circle
                                cx={9}
                                cy={9}
                                r={pomR}
                                fill="none"
                                stroke={
                                  activeTimer.paused
                                    ? "var(--am)"
                                    : "var(--gn)"
                                }
                                strokeWidth={2}
                                strokeDasharray={pomCirc}
                                strokeDashoffset={pomCirc * (1 - _pomProg)}
                                strokeLinecap="round"
                                transform="rotate(-90 9 9)"
                              />
                            </svg>
                          );
                        }}
                      </SelfTick>
                      )}
                      <span
                        className={activeTimer.paused ? undefined : "tmPulse"}
                        style={{
                          fontSize: FS.s10,
                          color: activeTimer.paused ? "var(--am)" : "var(--gn)",
                          fontWeight: 600,
                          fontFamily: "'JetBrains Mono',monospace",
                          flexShrink: 0,
                        }}
                      >
                        <SelfTick active={!activeTimer.paused}>
                          {() => getTimerDisplay()}
                        </SelfTick>
                      </span>
                      <button
                        className="btn"
                        style={{
                          padding: "2px 6px",
                          background: "none",
                          border: "none",
                          color: activeTimer.paused ? "var(--am)" : "var(--gn)",
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                        /* Item-4 fix — pause/resume IN PLACE (pass the subtask
                           id so a subtask timer toggles itself, and noPopout so
                           the header doesn't throw the user into the pop-out). */
                        /* 116 §3 — and the PARENT KIND with it: without
                           `eventId` this resume sends (null, null, null) for an
                           event timer and (null, S, null) for an event
                           SUBTASK's, neither of which is the row it means. */
                        onClick={() =>
                          startTimer(activeTimer.taskId, activeTimer.subtaskId, {
                            noPopout: true,
                            eventId: activeTimer.eventId || null,
                          })
                        }
                      >
                        {activeTimer.paused ? (
                          <Play size={I.s} />
                        ) : (
                          <Pause size={I.s} />
                        )}
                      </button>
                      <button
                        className="btn"
                        style={{
                          padding: "2px 6px",
                          background: "none",
                          border: "none",
                          color: "var(--rd)",
                          cursor: "pointer",
                          flexShrink: 0,
                          opacity: 0.7,
                        }}
                        /* Item-4 fix — was onClick={stopTimerAsk}, which passed
                           the click EVENT as taskId → findIndex(-1) → no-op.
                           Pass the focused timer's id so Stop works in place. */
                        onClick={() => stopTimerAsk(activeTimer.taskId)}
                      >
                        <Square size={I.s} />
                      </button>
                      <div ref={timerDdRef} style={{ position: "relative" }}>
                        <button
                          style={{
                            fontSize: FS.s9,
                            padding: "1px 5px",
                            borderRadius: RAD.r3,
                            background: "var(--as)",
                            color: "var(--tp)",
                            fontFamily: "'JetBrains Mono',monospace",
                            fontWeight: 600,
                            border: "none",
                            cursor: "pointer",
                          }}
                          onClick={() => setTimerDd((v: any) => !v)}
                        >
                          {at?.type || "—"}
                        </button>
                        {timerDd && (
                          <div
                            style={{
                              position: "absolute",
                              top: "100%",
                              right: 0,
                              marginTop: 4,
                              background: "var(--bc)",
                              border: "1px solid var(--br)",
                              borderRadius: RAD.r8,
                              boxShadow: "0 6px 20px rgba(0,0,0,.6)",
                              /* 205 §6 — THE TIMER'S TYPE DROPDOWN, OVER AN OPEN
                                 PANE. CENSUSED: this one is NOT portalled — it is a
                                 plain `position: absolute` box inside `.header`,
                                 which carries no position, no transform and no
                                 z-index and so establishes NO stacking context; the
                                 box therefore competes in the root context, where it
                                 carried `Z.pane` — THE PANE'S OWN FIGURE. At equal
                                 z-index the later element in the DOM wins, and both
                                 panes come after the header (BentoDialog is portalled
                                 to body, EisenhowerDialog renders late in App's
                                 tree), so it lost every time. `Z.picker` (10000) is
                                 the rung the Bento template picker already takes for
                                 precisely this — a header control's layer over the
                                 9999 overlay — and it stays under `Z.paneModal`
                                 (10001). Unconditional, like the picker's: with no
                                 pane open nothing else in the header sits between
                                 9999 and 10001, so the relationship to every other
                                 layer is unchanged. */
                              zIndex: Z.picker,
                              minWidth: 160,
                              overflow: "hidden",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {activeTimers.map((tm: any, i: any) => {
                              // BRIEF 91 §3a — task OR event subtask.
                              const tsk2 = timerSubject(
                                tm,
                                tasks,
                                agendaEvents,
                              )?.parent as any;
                              const cs3 = resolveMatter(tsk2, cases, collectiveCases); // 191 §C
                              const isFocused = i === focusedTimerIdx;
                              return (
                                <div
                                  /* §3a — `tm.taskId` was ALREADY a collision
                                     here: two subtask timers on one task are
                                     two rows with one key. */
                                  key={timerKey(tm)}
                                  style={{
                                    padding: "5px 8px",
                                    cursor: "pointer",
                                    fontSize: FS.s10,
                                    borderBottom:
                                      "1px solid rgba(255,255,255,.04)",
                                    background: isFocused
                                      ? "var(--as)"
                                      : "transparent",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                  }}
                                  onMouseEnter={(e) =>
                                    !isFocused &&
                                    (e.currentTarget.style.background =
                                      "var(--bh)")
                                  }
                                  onMouseLeave={(e) =>
                                    !isFocused &&
                                    (e.currentTarget.style.background =
                                      "transparent")
                                  }
                                  onClick={() => {
                                    // Phase 27 Stage-3 — focusing a timer must
                                    // route through startTimer so it pauses all
                                    // others and becomes the single accruing
                                    // timer. A bare setFocusedTimerIdx left the
                                    // previously-running timer accruing in the
                                    // background (one-at-a-time violation).
                                    // Item-4 fix — noPopout: switching the focused
                                    // timer from the header stays in the header.
                                    // 116 §3 — carry the parent kind, as the
                                    // pop-out's own switch tag does (91 §3a).
                                    startTimer(tm.taskId, tm.subtaskId, {
                                      noPopout: true,
                                      eventId: tm.eventId || null,
                                    });
                                    setTimerDd(false);
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: FS.s9,
                                      padding: "1px 4px",
                                      borderRadius: RAD.r3,
                                      background: "rgba(74,124,255,.2)",
                                      color: "var(--tp)",
                                      fontFamily: "'JetBrains Mono',monospace",
                                      fontWeight: 600,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {tsk2?.type || "—"}
                                  </span>
                                  <div
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: "hidden",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: FS.s8,
                                        color: "var(--tm)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {cs3 ? cs3.title : ""}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: FS.s9_5,
                                        color: isFocused
                                          ? "var(--ac)"
                                          : "var(--ts)",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {tsk2?.title || t("header.task")}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            <div
                              style={{
                                padding: "5px 8px",
                                cursor: "pointer",
                                fontSize: FS.s10,
                                color: "var(--ac)",
                                borderTop: "1px solid var(--br)",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "var(--bh)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                              onClick={() => {
                                setTimerDd(false);
                                nav("dash");
                              }}
                            >
                              {t("header.timer.new")}
                            </div>
                          </div>
                        )}
                      </div>
                      {csTrunc && (
                        <span
                          style={{
                            fontSize: FS.s9,
                            color: "var(--tm)",
                            flexShrink: 0,
                            maxWidth: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {csTrunc}
                        </span>
                      )}
                    </div>
                  );
                })()}
              {/* 190 §E.1 — the header matrix carries its own registry key
                  (`surface.eisenhower.user`): hidden firm-wide or withheld from
                  a role, the button and the dialog are gone together. */}
              {_appPerms.can("surface.eisenhower.user") && (
              <button
                type="button"
                title={t("header.matrix.title")}
                onClick={() => setEisenOpen((v: any) => !v)}
                /* 190 round 6 [AA] — the icon GLOWS while its overlay is open:
                   the lock's own three declarations (SharedUI OPEN_MODE_GLOW),
                   the way out, as the lock is. */
                style={{
                  background: eisenOpen ? OPEN_MODE_GLOW.background : "none",
                  border: eisenOpen ? OPEN_MODE_GLOW.border : "none",
                  animation: eisenOpen ? OPEN_MODE_GLOW.animation : undefined,
                  color: "var(--tp)",
                  cursor: "pointer",
                  padding: "2px",
                  marginLeft: 4,
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: RAD.r6,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,.08)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = eisenOpen ? OPEN_MODE_GLOW.background : "none")
                }
              >
                <span
                  style={{
                    fontSize: FS.s12,
                    fontWeight: 600,
                    color: "var(--tp)",
                    marginRight: 2,
                  }}
                >
                  {t("header.my")}
                </span>
                <Grid2x2 size={21} color={eisenOpen ? OPEN_MODE_ICON : "var(--tp)"} />
              </button>
              )}
              {/* 190 §C surface 1 — the user Bento: the matrix button's shape,
                  one icon over. Gated by its own registry key (190 §E.1). */}
              {_appPerms.can("surface.bento.user") && (
                <button
                  type="button"
                  title={(t as TFn)("header.bento.title", { fallback: "My Bento" })}
                  onClick={() => { setBentoOpen((v: boolean) => !v); }}
                  /* [AA] — glows while My Bento is open (the lock's declarations) */
                  style={{
                    background: bentoOpen ? OPEN_MODE_GLOW.background : "none",
                    border: bentoOpen ? OPEN_MODE_GLOW.border : "none",
                    animation: bentoOpen ? OPEN_MODE_GLOW.animation : undefined,
                    color: "var(--tp)",
                    cursor: "pointer",
                    padding: "2px",
                    marginLeft: 4,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: RAD.r6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = bentoOpen ? OPEN_MODE_GLOW.background : "none"; }}
                >
                  <SquareKanban size={21} color={bentoOpen ? OPEN_MODE_ICON : "var(--tp)"} />
                </button>
              )}
              {/* 190 [I] — the template picker, in the icon's row, right of
                  it, while the Bento is open (over the 9999 overlay). */}
              {_appPerms.can("surface.bento.user") && bentoOpen && (
                <BentoPicker bento={userBento} id="bento-template" zIndex={Z.picker} />
              )}
              {currentUser && (
                <div
                  style={{ position: "relative", marginLeft: 4, flexShrink: 0 }}
                >
                  <button
                    ref={bellRef}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--tp)",
                      cursor: "pointer",
                      padding: "2px",
                      position: "relative",
                    }}
                    onClick={() => {
                      /* Brief 42 §1.5 — OPENING the Bell clears every alert
                         toast then on screen. On open only: not on close, not
                         per item. This is the routine §0's ruling buys — the
                         toast has no other exit, so the user's only way out of
                         the stack is a look at the list holding the same rows.
                         Cleared OUTSIDE the setNotifOpen updater on purpose: a
                         setState called from inside another updater runs twice
                         under StrictMode's double-invoke. `notifOpen` is the
                         committed value here, so `!notifOpen` is "opening". */
                      if (!notifOpen) setAlertToasts([]);
                      /* 119 §3 — the orphan card is a NUDGE TOWARD THIS LIST,
                         so opening the list retires it: it would be odd for one
                         card in the stack to survive the sweep that clears its
                         siblings. CLOSED, not muted — a later rise in the count
                         is a new fact and earns a new nudge. */
                      if (!notifOpen) setOutlookPopup(null);
                      setNotifOpen((v: any) => !v);
                    }}
                  >
                    <Bell size={21} />
                    {/* Phase 31 (C3) — badge color re-spec: Bell counter is
                        GREEN (reminders/alerts); phone stays RED; chat
                        counters stay RED. */}
                    {/* 209 §26 — new since the bell was last opened (addendum 6) */}
                    {bellNew > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -4,
                          right: -6,
                          fontSize: FS.s8,
                          fontWeight: 700,
                          color: CT.c_fff,
                          background: "var(--gn)",
                          borderRadius: RAD.r8,
                          padding: "0 4px",
                          lineHeight: "14px",
                          minWidth: 14,
                          textAlign: "center",
                        }}
                      >
                        {bellNew}
                      </span>
                    )}
                  </button>
                  {notifOpen &&
                    (() => {
                      const __r = bellRef.current?.getBoundingClientRect();
                      /* 151E §1(b) — the panel never outgrows the viewport,
                         and every coordinate converts by the root zoom ONCE
                         (142 §1: rects are visual px, CSS px are layout).
                         Measured before: fixed maxHeight 600 rendered 738
                         visual from a 75px top in an 800px window — 13.5px
                         off the bottom, more at larger text sizes. The cap
                         is the layout viewport minus the panel's own top
                         minus 12px; content scrolls inside, the 151D §2(d)
                         discs say so. */
                      const __z =
                        parseFloat(
                          String(
                            getComputedStyle(document.documentElement).zoom || "1",
                          ),
                        ) || 1;
                      const __top = ((__r ? __r.bottom : 60) + 8) / __z;
                      const __right = Math.max(
                        8,
                        (window.innerWidth -
                          (__r ? __r.right : window.innerWidth - 20)) / __z,
                      );
                      const __maxH = Math.max(
                        200,
                        window.innerHeight / __z - __top - 12,
                      );
                      return createPortal(
                        <>
                          <div
                            style={{
                              position: "fixed",
                              inset: 0,
                              zIndex: Z.topBackdrop,
                            }}
                            onClick={() => setNotifOpen(false)}
                          />
                          <div
                            ref={bellHint.ref}
                            style={{
                              position: "fixed",
                              top: __top,
                              right: __right,
                              width: 448,
                              maxHeight: __maxH,
                              overflowY: "auto",
                              background: "var(--bc)",
                              border: "1px solid var(--br)",
                              borderRadius: RAD.r10,
                              boxShadow: "0 8px 28px rgba(0,0,0,.7)",
                              zIndex: Z.topMenu,
                              /* 204 §5 [RULED] — the communications popup's
                                 own inset (CallsPanel's, POPUP_PAD), not this
                                 box's `4`: "OVERDUE", the quote and the
                                 sign-in line sat 5 px from the border. */
                              padding: POPUP_PAD,
                            }}
                          >
                            {bellHint.above && <ScrollHint dir="up" />}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "6px 8px",
                                borderBottom: "1px solid var(--br)",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: FS.s13,
                                  fontWeight: 600,
                                  color: "var(--tp)",
                                  textTransform: "uppercase",
                                  letterSpacing: ".3px",
                                }}
                              >
                                {t("notif.title")}
                              </span>
                              {unreadNotifs.length > 0 && (
                                <button
                                  style={{
                                    fontSize: FS.s9,
                                    background: "none",
                                    border: "none",
                                    color: "var(--ac)",
                                    cursor: "pointer",
                                    fontFamily: "inherit",
                                  }}
                                  onClick={markAllRead}
                                >
                                  {t("notif.mark_all_read")}
                                </button>
                              )}
                            </div>
                            {/* 126 §3 — THE DAY'S QUOTE, ON TOP. It was the
                                last thing in this frame, below twenty rows in
                                a panel that caps at 600px — which is to say it
                                was read by nobody. Same producer, same block,
                                now a component the Daily Overview mounts too
                                (`components/DayQuote.tsx`). */}
                            <DayQuote quote={todayQuote} variant="popup" />
                            {notifications.length === 0 && (
                              <p
                                style={{
                                  fontSize: FS.s10_5,
                                  color: "var(--tm)",
                                  textAlign: "center",
                                  padding: "16px 8px",
                                }}
                              >
                                {t("notif.empty")}
                              </p>
                            )}
                            {/* ── 128 §4 — THE BELL TAKES THE OVERVIEW'S SHAPE
                                [RULED — Laurent] "couldn't Bell and Today be
                                the same — based on Today now, which is better
                                than bell?" Yes, and Today's is the one that
                                wins.

                                This was `notifications.slice(0, 20).map(…)`:
                                one flat list, no headings, no counters. It is
                                now the SAME component the Daily Overview
                                mounts, over the SAME array, with 126 §4b's
                                same total `groupOf()` partition — one
                                presentation, two mounts, exactly as the ROW
                                already was (125 §6, `NotifRowView`).

                                THE 20-ROW CAP IS GONE, and that is a
                                consequence rather than a preference. §4 rules
                                that the LIST, its GROUPING, its COUNTERS and
                                its ROWS may not differ between the mounts — and
                                A COUNTER OVER A CAPPED LIST IS A LIE.
                                "REMINDERS (12)" over eight rows, because the
                                cap ate four, is worse than no counter at all:
                                it is a number the reader has no way to doubt.
                                Either both surfaces cap and both lie, or
                                neither does. This panel has scrolled since it
                                was written (`maxHeight: 600, overflowY: auto`,
                                above) and the overview scrolls since §1, so
                                neither needs a cap to stay on the screen.

                                WHAT STAYS THIS SURFACE'S OWN: the header and
                                "Mark all read" above, the 448px width, the
                                backdrop dismissal — chrome, not the list. */}
                            <NotifGroupedList
                              notifications={notifications}
                              row={{
                                isNotifReadFn: isNotifRead,
                                notifRead,
                                tasks,
                                agendaEvents,
                                cases,
                                collectiveCases,
                                evtEntries,
                                markNotifRead,
                                dismissNotif,
                                joinAndTime,
                                onActed: () => setNotifOpen(false),
                                t,
                              }}
                            />
                            {/* ── 126 §5 — THE CONNECTION LINE, AT THE FOOT ──
                                [RULED] "Votre connexion précédente date du…"
                                led both panels; it goes to the foot of both.
                                SAME STRING, SAME PRODUCER — one `SignInLine`,
                                which is 81 §7's own arrangement and the reason
                                neither surface can drift into showing the
                                CURRENT sign-in. Only where it sits changed, and
                                its rule moved from `borderBottom` to
                                `borderTop` with it, because a separator
                                separates from what is on the other side. */}
                            <SignInLine summary={signInSummary} variant="popup" />
                            {bellHint.below && <ScrollHint dir="down" />}
                          </div>
                        </>,
                        document.body,
                      );
                    })()}
                </div>
              )}
              {/* Phase 31 (C2) — phone surface: panel trigger + RED missed
                  badge (bell badge re-specs GREEN in C3; chat stays RED). */}
              {canCalls && (
                <div
                  style={{ position: "relative", marginLeft: 4, flexShrink: 0 }}
                >
                  <button
                    ref={callBtnRef}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--tp)",
                      cursor: "pointer",
                      padding: "2px",
                      position: "relative",
                    }}
                    title={t("calls.panel_title", { fallback: "Internal communication" })}
                    onClick={openCallPanel}
                  >
                    <Phone size={21} />
                    {/* Corr-2 (item 9) — phone badge = missed calls + general-
                        chat unread (general chat now lives in COMMS). Case-chat
                        @mentions stay on the Bell. */}
                    {missedCallCount + gChatUnread > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -4,
                          right: -6,
                          fontSize: FS.s8,
                          fontWeight: 700,
                          color: CT.c_fff,
                          background: "var(--rd)",
                          borderRadius: RAD.r8,
                          padding: "0 4px",
                          lineHeight: "14px",
                          minWidth: 14,
                          textAlign: "center",
                        }}
                      >
                        {missedCallCount + gChatUnread}
                      </span>
                    )}
                  </button>
                  {callPanelOpen && (
                    <CallsPanel
                      anchorRect={callBtnRef.current?.getBoundingClientRect()}
                      onClose={() => setCallPanelOpen(false)}
                      co={collaborators}
                      currentUser={currentUser}
                      presenceIds={presenceIds}
                      presenceLoaded={presenceLoaded}
                      onEnsurePresence={pullPresence}
                      calls={calls}
                      onCall={(ids: any) => doCall(ids)}
                      onNotifyMissed={notifyMissed}
                      callActive={!!callWin || !!outgoingCall}
                      onScheduleMeeting={openInternalMeeting}
                      onSchedulePoll={openInternalPoll}
                      onSendEmail={openInternalEmail}
                      chatSlot={
                        <SidebarGenChat
                          inCol
                          limit={4}
                          tryNavCase={tryNavCase}
                          gChat={gChat}
                          setGChat={setGChat}
                          gChatInput={gChatInput}
                          setGChatInput={setGChatInput}
                          gChatLinkCase={gChatLinkCase}
                          setGChatLinkCase={setGChatLinkCase}
                          gChatLinkKind={gChatLinkKind}
                          setGChatLinkKind={setGChatLinkKind}
                          chatMsgs={chatMsgs}
                          setChatMsgs={setChatMsgs}
                          chatRead={chatRead}
                          setChatRead={setChatRead}
                          co={collaborators}
                          ca={cases}
                          cc={collectiveCases}
                          getRecentCases={getRecentCases}
                          currentUser={currentUser}
                          nav={nav}
                        />
                      }
                      busy={callBusy}
                    />
                  )}
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginLeft: 4,
                  flexShrink: 0,
                }}
              >
                {(() => {
                  const _cu = collaborators.find((c: any) => c.id === currentUser);
                  const _ini = _cu ? coIni(_cu) : "—";
                  const _nm = _cu ? pname(_cu) : null;
                  // Gate the "Viewing as" switcher on the AUTHENTICATED user (authUserId),
                  // not on the simulated currentUser. Never re-derived from currentUser so
                  // admin keeps the switcher while impersonating non-admins.
                  const _authCo =
                    authUserId != null
                      ? collaborators.find((c: any) => c.id === authUserId)
                      : null;
                  const _authIsAdmin = _authCo?.isAdmin === true;
                  if (
                    typeof window !== "undefined" &&
                    !window.__lxAuthLogged &&
                    authUserId != null &&
                    collaborators.length
                  ) {
                    window.__lxAuthLogged = true;
                    console.log(
                      "[lx] header auth check: authUserId=",
                      authUserId,
                      "matched co=",
                      _authCo,
                      "_authIsAdmin=",
                      _authIsAdmin,
                    );
                  }
                  return (
                    <>
                      {_authIsAdmin ? (
                        <>
                          <span
                            style={{
                              fontSize: FS.s9,
                              color: "var(--tm)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t("header.viewing_as")}
                          </span>
                          <MenuSelect
                            style={{
                              fontSize: FS.s12,
                              fontWeight: 700,
                              background: "var(--bc)",
                              color: "var(--tp)",
                              border: "1px solid var(--br)",
                              borderRadius: RAD.r6,
                              padding: "2px 6px",
                              cursor: "pointer",
                            }}
                            value={
                              simulation
                                ? String(simulation.simulatedUserId)
                                : authUserId
                                  ? String(authUserId)
                                  : ""
                            }
                            onChange={(picked) => {
                              const v = picked ? +picked : null;
                              if (!v || v === authUserId) {
                                if (simulation) endSimulation();
                              } else {
                                startSimulation(v);
                              }
                            }}
                            title={_nm || ""}
                            options={[
                              [
                                authUserId ? String(authUserId) : "",
                                authUserId
                                  ? (() => {
                                      const _ac = collaborators.find(
                                        (c: any) => c.id === authUserId,
                                      );
                                      return _ac
                                        ? coIni(_ac) +
                                            " " +
                                            t("header.you_suffix")
                                        : "—";
                                    })()
                                  : "—",
                              ] as const,
                              /* 75 §4 — view-as offers only what /simulate
                                 will mint: active people (isActiveCo, the one
                                 place stricter than choosable — the server
                                 refuses a suspended or archived target). */
                              ...collaborators
                                .filter((c: any) => c.id !== authUserId && isActiveCo(c))
                                .map((c: any) => [String(c.id), coIni(c)] as const),
                            ]}
                          />
                        </>
                      ) : (
                        <span
                          style={{
                            fontSize: FS.s12,
                            fontWeight: 700,
                            color: "var(--tp)",
                            padding: "2px 6px",
                          }}
                          title={_nm || ""}
                        >
                          {_ini}
                        </span>
                      )}
                      <button
                        title={
                          _nm
                            ? t("header.sign_out_named", { name: _nm })
                            : t("header.sign_out")
                        }
                        onClick={() => {
                          confirmAndUndo({
                            // 161C §3 — its own verb, no object to name.
                            title: t("confirm.sign_out_title", {
                              fallback: "Sign out?",
                            }),
                            message: t("header.sign_out_confirm"),
                            confirmLabel: t("header.sign_out", {
                              fallback: "Sign out",
                            }),
                            onConfirm: doLogout,
                          });
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          background: "none",
                          border: "1px solid var(--br)",
                          color: "var(--tp)",
                          borderRadius: RAD.r6,
                          padding: "2px 6px",
                          fontSize: FS.s12,
                          fontWeight: 700,
                          cursor: "pointer",
                          marginLeft: 4,
                          fontFamily: "inherit",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "var(--bh)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "none")
                        }
                      >
                        <span>{_ini}</span>
                        <LogOut size={12} />
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <div
            className="content"
            ref={contentRef}
            onScroll={(e) => {
              paneTopRef.current = e.currentTarget.scrollTop; // 207C §4 — where the screen is left
            }}
          >
            {/* 196 §C.1 [RULED — Laurent, 2026-09-19] — the « Vue d'ensemble —
                dossiers et cabinet » heading that stood here under the page
                header is gone: the header already says what the screen is.
                Its key (`costs.section.cases_firm_overview`) keeps its rows,
                dead. */}
            {/* 195 §C.3e [RULED — Laurent, 2026-09-18: "shouldn't we ditch it
                everywhere, just keep the ones that work, autonomously?"] — THE
                PAGE-LEVEL SEARCH BOX IS GONE, and with it the chain of ten
                `section !== "…"` tests that decided whether to draw it — a
                hand-kept list of "this screen has its own search" whose
                eleventh name was 174 §7's cure for Factures and whose missing
                twelfth was Rapports. Every screen that searches owns its box
                now (the thirteen party lists through ecfg.tsx, Frais et
                honoraires, Documents, Archives; Rapports, Factures, Agenda and
                the two matter lists already did), so there is no place left
                where a screen's search can be forgotten. What stays of the row
                is the ACTIONS it also held (export, import, the add button(s),
                the lock), on the condition they actually need — a section
                with an add button, not on a detail — which is derived from
                `addB`, never a list of names. */}
            {addB &&
              !detailId && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                    flexWrap: "wrap",
                  }}
                >
                  {section === "cl" && !clListLocked && (
                    <>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "3px 9px", fontSize: FS.s10 }}
                        onClick={() => {
                          const rows = clients.map((c: any) => ({
                            Title: c.title || "",
                            FirstName: c.firstName || "",
                            LastName: c.lastName || "",
                            Type: c.type || "individual",
                            Email: c.email || "",
                            Phone: c.phone || "",
                            Street: c.street || "",
                            PostalCode: c.postalCode || "",
                            City: c.city || "",
                            Country: c.country || "",
                            VAT: c.vat || "",
                            "National Register": c.nationalRegisterId || "",
                            Language: storedClientLanguage(c), // 207C §6 — what is stored (it read `lang`, never sent); 209 §5 [RULED 18] — none: an empty cell (it wrote « fr »)
                            CompanyName: c.companyName || "",
                            Quantum: c.quantum || "",
                            Notes: c.notes || "",
                            // Phase 43 CORRECTION (item 7) — emit the 3 columns
                            // the importer reads, so export→edit→reimport
                            // round-trips (headers match findC2 aliases).
                            Case: c.formerCaseRef || "",
                            Archived: c.archived ? "yes" : "",
                            "Archived Date": c.archivedAt
                              ? String(c.archivedAt).slice(0, 10)
                              : "",
                          }));
                          // LEGAL-LANE (round 2, Excel sweep) — cap-guarded
                          // (Notes is free text and unbounded) and reported: a
                          // throw here used to die in the console.
                          void (async () => {
                          try {
                            const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
                            const ws = XLSX.utils.json_to_sheet(rows.map(xlsxRow));
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Clients");
                            XLSX.writeFile(
                              wb,
                              `Clients All ${new Date().toISOString().slice(0, 10)}.xlsx`, // 181 (ruling 1)
                            );
                            toast(
                              t("toast.exported_n_clients", {
                                count: clients.length,
                                fallback: "Exported " + clients.length + " clients",
                              }),
                            );
                          } catch (e: any) {
                            toast(
                              t("toast.export_failed", {
                                msg: e?.message || "unknown error",
                                fallback: "⚠ Export failed: {msg}",
                              }),
                            );
                          }
                          })();
                        }}
                      >
                        <Download
                          size={10}
                          style={{ marginRight: 3, verticalAlign: "middle" }}
                        />
                        {t("common.export", { fallback: "Export" })}
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "3px 9px", fontSize: FS.s10 }}
                        onClick={() => clFileRef2.current?.click()}
                      >
                        <Upload
                          size={10}
                          style={{ marginRight: 3, verticalAlign: "middle" }}
                        />
                        {t("common.import", { fallback: "Import" })}
                      </button>
                      <input
                        ref={clFileRef2}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          if (e.target.files?.[0])
                            setClImportFile2(e.target.files[0]);
                          e.target.value = "";
                        }}
                      />
                      {(() => {
                        const orphanCount = orphanClientList().length;
                        return (
                          <button
                            className="btn btn-ghost"
                            style={{
                              padding: "3px 9px",
                              fontSize: FS.s10,
                              color:
                                orphanCount > 0 ? "var(--am)" : "var(--tm)",
                              borderColor:
                                orphanCount > 0 ? "var(--am)" : undefined,
                            }}
                            onClick={() => setOrphanDlg(true)}
                            title={t("app.client.cleanup_tip", {
                              fallback:
                                "Review unlinked clients, flag duplicates (same name + email), and delete what you don't need",
                            })}
                          >
                            <Trash2
                              size={10}
                              style={{
                                marginRight: 3,
                                verticalAlign: "middle",
                              }}
                            />
                            {t("app.client.cleanup_btn", {
                              fallback: "Database cleanup",
                            })}
                            {orphanCount > 0 ? ` (${orphanCount})` : ""}
                          </button>
                        );
                      })()}
                      {(() => {
                        /* 176 §3.4 — THE GATE, beside the existing terms and
                           not merged into them. `section === "cl" &&
                           !clListLocked` (the enclosing guard) asks WHERE we
                           are and whether the list is open for editing;
                           `missing` asks whether there is anything to do;
                           `canCreatePersons` asks WHO. Three questions, three
                           terms.

                           The client-file pane's own repair (§3.2/§3.3) keeps
                           its separate gate — admin OR RP-of-this-matter —
                           deliberately unmerged: that one answers "may you
                           repair THIS matter's client", which is a per-matter
                           question this firm-wide right cannot express. */
                        if (!canCreatePersons) return null;
                        /* 212 §13 — ONE BUTTON, ONE WINDOW. « Vérifier les
                           dossiers » opens the whole check on every client — a
                           client with no folder, a folder not found at its
                           address, subfolders missing — one simulation, one
                           repair (FolderPropagationModal, purpose "fill"; the
                           server lists the folderless clients by the backfill's
                           own rule). Until 212 this button opened the provision
                           window when a client lacked a folder and the check
                           only as a secondary button inside it. The provision
                           window stays for a matter's client-file pane, which
                           repairs ONE client (176 §3.2–§3.3). */
                        return (
                          <button
                            className="btn btn-ghost"
                            disabled={backfillBusy}
                            style={{
                              padding: "3px 9px",
                              fontSize: FS.s10,
                              opacity: backfillBusy ? 0.6 : 1,
                              cursor: backfillBusy ? "wait" : "pointer",
                            }}
                            onClick={() => {
                              setSubfolderCheck("client");
                            }}
                            title={(t as TFn)("folder_check.button_tip", {
                              fallback:
                                "Every client: a folder missing, a folder not found at its address, subfolders missing — one simulation, one repair",
                            })}
                          >
                            <Folder
                              size={10}
                              style={{
                                marginRight: 3,
                                verticalAlign: "middle",
                              }}
                            />
                            {/* 212 §29 — Settings' words: whose folders */}
                            {(t as TFn)("folder_check.settings_clients", {
                              fallback: "Check the clients' folders",
                            })}
                          </button>
                        );
                      })()}
                    </>
                  )}
                  {section === "ju" && !clListLocked && (
                    <>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "3px 9px", fontSize: FS.s10 }}
                        onClick={() => {
                          const rows = jurisdictions.map((j: any) => ({
                            COURT: j.name || "",
                            ADDRESS: j.address || "",
                            "POSTAL CODE": j.postalCode || "",
                            CITY: j.city || "",
                            TEL: j.tel || "",
                            EMAIL: j.email || "",
                          }));
                          // LEGAL-LANE (round 2, Excel sweep) — guarded + reported.
                          void (async () => {
                          try {
                            const XLSX = await loadXlsx(); // 208 §1 — on first use; a load that fails is the catch's words
                            const ws = XLSX.utils.json_to_sheet(rows.map(xlsxRow), {
                              header: [
                                "COURT",
                                "ADDRESS",
                                "POSTAL CODE",
                                "CITY",
                                "TEL",
                                "EMAIL",
                              ],
                            });
                            const wb = XLSX.utils.book_new();
                            XLSX.utils.book_append_sheet(wb, ws, "Jurisdictions");
                            XLSX.writeFile(
                              wb,
                              `Jurisdictions ${new Date().toISOString().slice(0, 10)}.xlsx`, // 181 (ruling 1)
                            );
                            toast(
                              t("ju.toast.exported", {
                                count: jurisdictions.length,
                                fallback: "Exported " + jurisdictions.length + " courts",
                              }),
                            );
                          } catch (e: any) {
                            toast(
                              t("toast.export_failed", {
                                msg: e?.message || "unknown error",
                                fallback: "⚠ Export failed: {msg}",
                              }),
                            );
                          }
                          })();
                        }}
                      >
                        <Download
                          size={10}
                          style={{ marginRight: 3, verticalAlign: "middle" }}
                        />
                        {t("common.export", { fallback: "Export" })}
                      </button>
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "3px 9px", fontSize: FS.s10 }}
                        onClick={() => juFileRef.current?.click()}
                      >
                        <Upload
                          size={10}
                          style={{ marginRight: 3, verticalAlign: "middle" }}
                        />
                        {t("common.import", { fallback: "Import" })}
                      </button>
                      <input
                        ref={juFileRef}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          if (e.target.files?.[0])
                            setJuImportFile(e.target.files[0]);
                          e.target.value = "";
                        }}
                      />
                    </>
                  )}
                  {addB && !clListLocked && (
                    <button
                      className="btn btn-primary"
                      style={{
                        padding: "3px 9px",
                        fontSize: FS.s10,
                        marginLeft: "auto",
                      }}
                      onClick={() =>
                        openModal(
                          addB.m,
                          section === "co2int"
                            ? { coType: "internal", _lockCoType: true }
                            : section === "co2ext"
                              ? { coType: "external", _lockCoType: true }
                              : null,
                        )
                      }
                    >
                      + {addB.l}
                    </button>
                  )}
                  {/* 168A §1a (168 R7) — THE SECOND BUTTON, AND IT IS THE
                      MATTER LISTS' SHAPE. `Client.isProspect` has been written
                      since brief 04 unit 3 and 168 gave it a reader; nothing in
                      the app could CREATE one, because `POST /api/clients`
                      takes the flag off the body (`clients.js:322`) and no file
                      under `src/` sent it. This is the writer.

                      `openModal("cl", { isProspect: true })` — a SEED, not a
                      form: `openModal`'s bare else does `setForm(normFormDates(e))`
                      (App.tsx:8334), the modal hands `form` straight to
                      `saveEntityGuarded`, and `saveEntity` spreads it into
                      `persist` → `ent.c(item)` → `POST /clients` unpruned. So
                      the seed IS the body; nothing rides in `ext`. Measured, not
                      assumed.

                      `AddButton` and not a second hand-rolled `<button>`: the
                      "+" belongs to the component (brief 57 §2,
                      `scripts/lint/add-button-owns-the-plus.js`), and the label
                      is the seeded `matters.prospect.add.new` — the same
                      sentence the matter lists' own second button carries
                      (`matterListCfg.tsx:1172`), so no new key. The `padding`
                      is the sibling's, so the pair matches. Under the SAME gate:
                      one permission governs creating a person, tagged or not. */}
                  {addB && !clListLocked && section === "cl" && (
                    <AddButton
                      label={t("matters.prospect.add.new", {
                        fallback: "New prospect",
                      })}
                      onClick={() => openModal("cl", { isProspect: true })}
                      style={{ padding: "3px 9px" }}
                    />
                  )}
                  {/* 176 §1.2(c) — THE FOLDER'S NAME IS PERMANENT, third
                      surface, beside the lock that opens this list for
                      editing. Renaming a client here never renames its
                      SharePoint folder — the link points at the folder, not
                      at its name — and the clients list is where a rename is
                      typed, so this is where the sentence belongs.

                      `section === "cl"` as well as `!clListLocked`: this
                      header row is shared with `ju`, `co2int`, `co2ext` and
                      the other party lists (see the sibling guards at :15400
                      and :15557), and a CLIENT-folder sentence on the
                      jurisdictions list would be false. `clListLocked`
                      defaults to true (:3684), so the tip is silent until
                      someone deliberately unlocks. */}
                  {section === "cl" && !clListLocked && (
                    <InfoHint
                      title={t("folder.permanence_tip_clients", {
                        fallback:
                          "The SharePoint folder keeps the name it was created with. Renaming a client here never renames the folder — the link points at the folder itself, so it keeps working whatever the folder is called.",
                      })}
                    />
                  )}
                  {addB && (
                    <LockButton {...({} as any)}
                      locked={clListLocked}
                      onToggle={() => setClListLocked((v: any) => !v)}
                      style={
                        addB && !clListLocked
                          ? undefined
                          : { marginLeft: "auto" }
                      }
                    />
                  )}
                </div>
              )}
            {/* Phase 43 (item 1) — Clients-scoped structured filter bar. Its own
                row above the list; client-side over the loaded list via clPass in
                EList's `fi`. Also the manual conflict-check surface (arch axis
                reveals archived former clients). */}
            {section === "cl" &&
              !detailId &&
              (() => {
                const f = clFilter;
                const set = (k: any, v: any) => sClFilter((p: any) => ({ ...p, [k]: v }));
                // Phase 43 CORRECTION P3 (item 1) — match the app's standard
                // input styling (.fi: 8px radius + app border/bg) so the compact
                // filter controls aren't square-cornered browser defaults.
                const inS = {
                  fontSize: FS.s10,
                  padding: "2px 6px",
                  borderRadius: RAD.r8,
                  border: "1px solid var(--br)",
                  background: "var(--bi)",
                  color: "var(--tp)",
                };
                /* 168B §2b (RULING 3) — every axis the bar carries, so "Clear"
                   appears for any of them. `email` is gone with its input. */
                const dirty =
                  f.name ||
                  f.type !== "all" ||
                  f.country ||
                  f.caseName ||
                  f.caseLink !== "all" ||
                  f.status !== "all" ||
                  f.clientStatus !== "all" ||
                  f.statusFrom ||
                  f.statusTo ||
                  f.due !== "all" ||
                  f.arch !== "active";
                return (
                  /* 168B §2a (RULING 3) — the title on its OWN LINE directly
                     under the search bar, the inputs on the line(s) below,
                     wrapping, and "Clear" at the end of them. It was one
                     wrapping row with the title as its first chip, so the title
                     drifted into the middle of the controls as the row wrapped. */
                  <div
                    style={{
                      marginBottom: 12,
                      padding: "6px 8px",
                      border: "1px solid var(--bd)",
                      borderRadius: RAD.r6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: FS.s10,
                        color: "var(--tm)",
                        fontWeight: 600,
                        marginBottom: 6,
                      }}
                    >
                      {t("cl.filter.title", { fallback: "Filters" })}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                    <input autoComplete="off" className="no-contact-autofill" /* 207 §18 */
                      style={{ ...inS, width: 110 }}
                      placeholder={t("cl.filter.name", { fallback: "Name" })}
                      value={f.name}
                      onChange={(e) => set("name", e.target.value)}
                    />
                    {/* 168B §2b — NEW: the client TYPE, over the same
                        `client_type` taxonomy the Type column resolves
                        through, so the filter and the column cannot disagree
                        about the vocabulary. */}
                    <MenuSelect
                      style={inS}
                      value={f.type}
                      onChange={(v) => set("type", v)}
                      options={[
                        [
                          "all",
                          t("cl.filter.type_all", { fallback: "Any type" }),
                        ] as const,
                        ...(clientTypesList || []).map(
                          ([v, l]: any) => [String(v), String(l ?? "")] as const,
                        ),
                      ]}
                    />
                    <input
                      style={{ ...inS, width: 90 }}
                      placeholder={t("cl.filter.country", {
                        fallback: "Country",
                      })}
                      value={f.country}
                      onChange={(e) => set("country", e.target.value)}
                    />
                    <MenuSelect
                      style={inS}
                      value={f.caseLink}
                      onChange={(v) => set("caseLink", v)}
                      options={[
                        [
                          "all",
                          t("cl.filter.caselink_all", {
                            fallback: "Any case link",
                          }),
                        ],
                        [
                          "yes",
                          t("cl.filter.caselink_yes", { fallback: "Has a case" }),
                        ],
                        ["no", t("cl.filter.caselink_no", { fallback: "No case" })],
                      ]}
                    />
                    <input autoComplete="off" className="no-contact-autofill" /* 207 §18 */
                      style={{ ...inS, width: 120 }}
                      placeholder={t("cl.filter.case_name", {
                        fallback: "Case name",
                      })}
                      value={f.caseName}
                      onChange={(e) => set("caseName", e.target.value)}
                    />
                    <MenuSelect
                      style={inS}
                      value={f.status}
                      onChange={(v) => set("status", v)}
                      options={[
                        [
                          "all",
                          t("cl.filter.status_any_case", {
                            fallback: "Any case status",
                          }),
                        ] as const,
                        /* 108 §6b — the divider is the menu's own row now */
                        ...(caseStatusesList || []).map(
                          ([v, l]: any) => [String(v), String(l ?? "")] as const,
                        ),
                      ]}
                    />
                    {/* 168B §2b — NEW: the CLIENT's standing, the five link
                        values in lifecycle order, wearing the labels the
                        roster's own picker resolves (`cc.retainer.status.*`,
                        already seeded). Matched against `mostRecentStatus`,
                        which is what the Client status COLUMN shows. */}
                    <MenuSelect
                      style={inS}
                      value={f.clientStatus}
                      onChange={(v) => set("clientStatus", v)}
                      options={[
                        [
                          "all",
                          t("cl.filter.clientstatus_all", {
                            fallback: "Any client status",
                          }),
                        ] as const,
                        ...(KNOWN_STATUSES as string[]).map(
                          (v) => [v, linkStatusLabel(t, v)] as const,
                        ),
                      ]}
                    />
                    <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                      {t("cl.filter.status_between", {
                        fallback: "Client status set between",
                      })}
                    </span>
                    <input
                      type="date"
                      style={inS}
                      value={f.statusFrom}
                      onChange={(e) => set("statusFrom", e.target.value)}
                    />
                    <input
                      type="date"
                      style={inS}
                      value={f.statusTo}
                      onChange={(e) => set("statusTo", e.target.value)}
                    />
                    {/* Phase 43 CORRECTION P2 (item A) — the amount-due filter
                        axis is only available to viewers who can see figures
                        (canSeeAnyDue); hidden otherwise, since filtering on unseen
                        data is incoherent.
                        168B §2b — the THIRD arm is back. `rowPass` has always
                        answered "yes" (something due) and the bar offered only
                        two options, so that arm was unreachable. Item 9 removed
                        it as duplicating "Any amount due" — which is the LABEL
                        of the no-filter option, and that is the collision:
                        "any" means "do not filter", "something due" means the
                        client owes. Ruling 3's three states, named apart. */}
                    {canSeeAnyDue && (
                      <MenuSelect
                        style={inS}
                        value={f.due}
                        onChange={(v) => set("due", v)}
                        options={[
                          [
                            "all",
                            t("cl.filter.due_all", {
                              fallback: "Any amount due",
                            }),
                          ],
                          [
                            "yes",
                            t("cl.filter.due_yes", { fallback: "Something due" }),
                          ],
                          ["no", t("cl.filter.due_no", { fallback: "Nothing due" })],
                        ]}
                      />
                    )}
                    <MenuSelect
                      style={inS}
                      value={f.arch}
                      onChange={(v) => set("arch", v)}
                      /* Phase 43 CORRECTION (item 10 / D30) — client-VISIBILITY
                         axis (the client's archived flag), relabelled to not
                         collide with the case-STATUS "Archived" value above.
                         168B ruling 3 — and it is LAST. */
                      options={[
                        [
                          "active",
                          t("cl.filter.vis_active", {
                            fallback: "Active clients only",
                          }),
                        ],
                        [
                          "only",
                          t("cl.filter.vis_archived", {
                            fallback: "Archived clients only",
                          }),
                        ],
                        [
                          "incl",
                          t("cl.filter.vis_all", {
                            fallback: "All clients incl. archived",
                          }),
                        ],
                      ]}
                    />
                    {/* 168B GLANCE 3 [RULED Laurent 2026-09-05] — FILLED, not
                        ghost. MEASURED: it wore `btn btn-ghost`, which is
                        `background: none` with `--br` for its border and `--ts`
                        for its ink (App.css:454) — the same ground and nearly
                        the same border as the `.fi`-shaped inputs it sits
                        after, so at the end of a wrapping row of controls it
                        read as one more of them. `btn-primary` is the "+ Add"
                        shape: `--as` ground, `--ac` ink (App.css:444), which is
                        the reversal the ruling asks for. The padding stays this
                        bar's own — `AddButton` uses 2px 7px, one pixel apart,
                        and the button is not an add. */}
                    {dirty && (
                      <button
                        className="btn btn-primary"
                        style={{ padding: "2px 8px", fontSize: FS.s10 }}
                        onClick={() => sClFilter(CL_FILTER_DEFAULT)}
                      >
                        {t("cl.filter.clear", { fallback: "Clear" })}
                      </button>
                    )}
                    </div>
                  </div>
                );
              })()}
            {renderContent()}
          </div>
          {/* 190 round 8 [AK] — THE PAGE'S FLOOR: the one slot the counters
              strip pins into, a flex row under the scroller; empty, it takes
              no space (App.css `.contentFooter:empty`). Its node is state so
              a host mounting later still finds it through the provider. */}
          <div className="contentFooter" ref={setContentFooter} />
        </div>
        </ContentFooterSlot.Provider>
        {/* 190 §C surface 1 — the user Bento: my own open tasks (the matrix's
            own filter, EisenhowerDialog.tsx:112–116), per-user placements; a
            card click is the matrix's (tryNavCase + queueScrollToTk, close). */}
        <BentoDialog
          open={bentoOpen && !colInApp} // 206E §1.3 — never over the phone's in-app column
          onClose={closeBento}
          headerH={headerH}
          sidebarEdge={sidebarEdge} // 206E §1.1 — the frame starts where the sidebar ends
          surface="user"
          bento={userBento}
          quadrantOf={userMatrixQuadrant}
          title={bentoT("bento.my_title", { fallback: "My Bento" })}
          tasks={bentoTasks}
          cases={bentoCases}
          collectiveCases={bentoCcs}
          tkTypes={bentoTypes}
          canMove={_appPerms.can("surface.bento.user")}
          onCardClick={(tk) => {
            if (tk.caseId) {
              if (tryNavCase("ca", tk.caseId)) queueScrollToTk(tk.id);
            } else if (tk.ccId) {
              if (tryNavCase("cc", tk.ccId)) queueScrollToTk(tk.id);
            }
            setBentoOpen(false);
          }}
          t={bentoT}
        />
        {/* BRIEF 102 §2e — `ITEM_LEVEL` no longer passed: the dialog drew a
            level letter and no longer does, and its lookup went with it. */}
        <EisenhowerDialog
          cases={cases}
          collectiveCases={collectiveCases} // 111 §5c
          currentUser={currentUser}
          eisenDragId={eisenDragId}
          eisenOpen={eisenOpen && _appPerms.can("surface.eisenhower.user") && !colInApp} // 206E §1.3 — as `_paneOpen`
          eisenResetSnap={eisenResetSnap}
          headerH={headerH}
          sidebarEdge={sidebarEdge} // 206E §1.1 — the frame starts where the sidebar ends
          setEisenDragId={setEisenDragId}
          setEisenOpen={setEisenOpen}
          t={t}
          taskStatusEntries={taskStatusEntries}
          tasks={tasks}
          tkTypes={tkTypes}
          tryNavCase={tryNavCase}
          userMatrix={userMatrix}
          userMatrixQuadrant={userMatrixQuadrant}
          userMatrixReset={userMatrixReset}
          userMatrixSetQuadrant={userMatrixSetQuadrant}
          userMatrixUndoReset={userMatrixUndoReset}
        />
        {/* 112 §7 — the Links manager (ADDENDUM). Resolution and writes live
            here beside the rest of the link plumbing; the dialog is dumb.
            Labels resolve AS THE PILLS DO (stage: resolveTaxonomyLabel over
            case_stage; task/event: the row's title), kind-prefixed, dated
            through fd() where the item carries a date. Candidates: the SAME
            matter's stages + tasks + events, minus SELF, minus the item's
            already-linked partners (DIRECT links — linking deeper into one's
            own component stays legitimate, §3), minus archived rows (§6's
            rule; stage rows carry no archived flag). The × is the granular
            unlink "Unlink all" cannot express: one link, house removal shape
            (no confirm, Undo re-creates the pair). Every action commits at
            once — Close is done. */}
        {linksMgr &&
          (() => {
            const { item, matter } = linksMgr;
            const pool: MatterLite[] =
              matter.matterKind === "cc" ? collectiveCases : cases;
            const matterRow = pool.find((c) => c.id === matter.matterId);
            type S7Stage = { id: number; type?: string | null; startDate?: string | null };
            const _mrStages = (matterRow as { stages?: S7Stage[] } | undefined)?.stages;
            const stages: S7Stage[] = Array.isArray(_mrStages) ? _mrStages : [];
            const touches = (l: ItemLink) =>
              (l.a.k === item.k && +l.a.id === item.id) ||
              (l.b.k === item.k && +l.b.id === item.id);
            const links = _linkRowsOfMatter(matter).filter(touches);
            const otherOf = (l: ItemLink) =>
              l.a.k === item.k && +l.a.id === item.id ? l.b : l.a;
            const labelOfRef = (r: { k: string; id: number }): string => {
              if (r.k === "stage") {
                const stg = stages.find((s) => +s.id === +r.id);
                const name = stg
                  ? resolveTaxonomyLabel(stg.type, caseStageEntries) ||
                    String(stg.type || "")
                  : `#${r.id}`;
                const dated = stg?.startDate ? ` — ${fd(stg.startDate)}` : "";
                return `${t("links.kind_stage", { fallback: "Stage" })} · ${name}${dated}`;
              }
              if (r.k === "task") {
                const row = (tasks || []).find((x: { id: number }) => +x.id === +r.id);
                return `${t("links.kind_task", { fallback: "Task" })} · ${row?.title || `#${r.id}`}`;
              }
              const row = (agendaEvents || []).find((x: { id: number }) => +x.id === +r.id);
              const dated = row?.date ? ` — ${fd(row.date)}` : "";
              return `${t("links.kind_event", { fallback: "Event" })} · ${row?.title || `#${r.id}`}${dated}`;
            };
            const linkedKeys = new Set(
              links.map(otherOf).map((r) => `${r.k}-${+r.id}`),
            );
            linkedKeys.add(`${item.k}-${item.id}`);
            type S7Item = {
              id: number;
              ccId?: number | string | null;
              caseId?: number | string | null;
              archived?: boolean;
            };
            const onMatter = (x: S7Item) =>
              matter.matterKind === "cc"
                ? +(x.ccId ?? 0) === matter.matterId
                : +(x.caseId ?? 0) === matter.matterId;
            const candidates: { value: string; label: string }[] = [
              ...stages.map((s) => ({ k: "stage", id: +s.id })),
              ...(tasks || [])
                .filter((x: S7Item) => onMatter(x) && !x.archived)
                .map((x: S7Item) => ({ k: "task", id: +x.id })),
              ...(agendaEvents || [])
                .filter((x: S7Item) => onMatter(x) && !x.archived)
                .map((x: S7Item) => ({ k: "event", id: +x.id })),
            ]
              .filter((r) => !linkedKeys.has(`${r.k}-${r.id}`))
              .map((r) => ({ value: `${r.k}-${r.id}`, label: labelOfRef(r) }));
            return (
              <LinksManagerDialog
                links={links}
                labelOfOther={(l) => labelOfRef(otherOf(l))}
                options={candidates}
                /* 113 §4 — ONE BATCH, at the primary. The dialog stages;
                   this commits: every staged add POSTed, every staged removal
                   DELETEd, and the answer names ONLY what failed so the dialog
                   can keep exactly those rows staged and marked. The store is
                   patched from each reply's full itemLinks array (111 §5a's
                   shape), so the icons and the cluster move with no reload.
                   The per-removal Undo toast is gone WITH the immediate-delete
                   model it belonged to — staging is the second chance now, and
                   one toast could not unwind a mixed batch. */
                onCommit={async (adds, removes) => {
                  const failed: { adds: string[]; removes: number[] } = {
                    adds: [],
                    removes: [],
                  };
                  for (const key of adds) {
                    const m2 = /^(stage|task|event)-(\d+)$/.exec(key);
                    if (!m2) {
                      failed.adds.push(key);
                      continue;
                    }
                    try {
                      const r = await createItemLink(
                        matter.matterKind,
                        matter.matterId,
                        item,
                        { k: m2[1] as "stage" | "task" | "event", id: +m2[2] },
                      );
                      if (r && r.itemLinks)
                        _linkPatchMatter(matter.matterKind, matter.matterId, r.itemLinks);
                    } catch (e) {
                      failed.adds.push(key);
                      toast(e instanceof Error ? e.message : String(e), "error");
                    }
                  }
                  for (const l of removes) {
                    try {
                      const r = await deleteItemLink(
                        matter.matterKind,
                        matter.matterId,
                        l.id,
                      );
                      _linkPatchMatter(
                        matter.matterKind,
                        matter.matterId,
                        r.itemLinks || [],
                      );
                    } catch (e) {
                      failed.removes.push(l.id);
                      toast(e instanceof Error ? e.message : String(e), "error");
                    }
                  }
                  return failed;
                }}
                onClose={() => setLinksMgr(null)}
              />
            );
          })()}
        <PartyDetailDialog
          clients={clients}
          openModal={openModal}
          opponents={opponents}
          partyDetailDlg={partyDetailDlg}
          setPartyDetailDlg={setPartyDetailDlg}
          t={t}
          lang={lang}
        />
        <TaskPromptDialog
          activeModal={activeModal}
          currentUser={currentUser}
          openModal={openModal}
          releaseEntityTimer={releaseEntityTimer}
          roundUp5={roundUp5}
          saveTask={saveTask}
          setTkCompletePend={setTkCompletePend}
          t={t}
          tkCompletePend={tkCompletePend}
          toast={toast}
        />
        {renderTaskDeleteDialog()}
        <ConfirmDeleteDialog dlg={confirmDlg} onCancel={cancelConfirm} />
        {/* Phase 43 CORRECTION (item 2) — app-styled COI / duplicate confirm
            (replaces window.confirm). OK → deferred save, Cancel → abort. */}
        {coiPrompt && (
          <ModalLayout
            title={t("coi.dialog.title", { fallback: "Please confirm" })}
            showX={false}
            onClose={() => setCoiPrompt(null)}
            closeOnOverlay
            stopShellClicks
            zIndex={Z.popoverBackdrop}
            /* 166 §C — CHANGES WHAT RENDERS: the cap is below the house 480,
               so this confirm now IS 400 wide. */
            maxWidth={400}
            rawBody
            footer={
              <div className="modal-footer">
                <button
                  type="button"
                  className="modal-cancel"
                  onClick={() => setCoiPrompt(null)}
                >
                  {t("common.cancel", { fallback: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="modal-save"
                  onClick={() => {
                    const p = coiPrompt.onConfirm;
                    setCoiPrompt(null);
                    if (p) p();
                  }}
                >
                  {t("common.ok", { fallback: "OK" })}
                </button>
              </div>
            }
          >
              <div className="modal-body">
                <p style={{ fontSize: FS.s12, lineHeight: 1.5 }}>{coiPrompt.msg}</p>
              </div>
          </ModalLayout>
        )}
        <UndoToast toast={undoToast} onUndo={runUndo} />
        {/* Hotfix-B (Part A) — deferred client-delete Undo toast. App-scoped so
            it survives modal close / navigation; the 20s bar mirrors the commit
            timer. Undo → cancelPendingClDel (nothing deleted). */}
        {pendingClDel && (
          <div
            key={pendingClDel.seq}
            style={{
              position: "fixed",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px 11px",
              background: CT.c_f87171,
              borderRadius: RAD.r8,
              fontSize: FS.s11,
              zIndex: Z.popoverBackdrop,
              boxShadow: "0 6px 20px rgba(0,0,0,.4)",
              minWidth: 240,
            }}
          >
            <span
              style={{
                flex: 1,
                color: CT.c_fff,
                fontSize: FS.s10_5,
                fontWeight: 500,
              }}
            >
              {t("toast.deleting_clients", {
                count: pendingClDel.count,
                fallback: `Deleting ${pendingClDel.count} client${pendingClDel.count === 1 ? "" : "s"}…`,
              })}
            </span>
            <button
              type="button"
              style={{
                padding: "2px 10px",
                fontSize: FS.s10,
                background: "none",
                border: "1.5px solid rgba(255,255,255,.8)",
                color: CT.c_fff,
                borderRadius: RAD.r6,
                cursor: "pointer",
                fontWeight: 600,
                fontFamily: "inherit",
                flexShrink: 0,
              }}
              onClick={cancelPendingClDel}
            >
              {t("common.undo", { fallback: "Undo" })}
            </button>
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                height: 3,
                background: "rgba(255,255,255,.45)",
                animation: "ud 20s linear forwards",
              }}
            />
          </div>
        )}
        {juImportFile && (
          <JurisdictionImportModal
            juImportFile={juImportFile}
            setJuImportFile={setJuImportFile}
            jurisdictions={jurisdictions}
            setJurisdictions={setJurisdictions}
            t={t}
            toast={toast}
          />
        )}
        {clImportFile2 && (
          <ClientsImportModal
            clImportFile2={clImportFile2}
            setClImportFile2={setClImportFile2}
            clients={clients}
            setClients={setClients}
            persist={persist}
            toast={toast}
            t={t}
          />
        )}
        {/* AUTHORITY (155 §4): this checked-list dialog idiom (list +
            checkboxes + counts) is the original; provisionDlg below derives
            its shape — different act, shared idiom; a dialog-mechanics fix
            lands in BOTH. */}
        {orphanDlg &&
          (() => {
            const orphans = orphanClientList();
            const { dupIds, survivorIds } = analyzeOrphanClients(orphans);
            const checkedCount = orphans.filter((c: any) =>
              dbCleanupChecked.has(c.id),
            ).length;
            const dupCount = dupIds.size;
            const allChecked =
              orphans.length > 0 &&
              orphans.every((c: any) => dbCleanupChecked.has(c.id));
            const toggleRow = (id: any) =>
              setDbCleanupChecked((prev: any) => {
                const nx = new Set(prev);
                if (nx.has(id)) nx.delete(id);
                else nx.add(id);
                return nx;
              });
            const toggleAll = () =>
              setDbCleanupChecked(
                allChecked ? new Set() : new Set(orphans.map((c: any) => c.id)),
              );
            // Linked case (col iii): faithful lookup — this population is
            // un-linked clients, so it renders "—", but a real value would show
            // if the underlying linkage ever surfaced one.
            const linkedCaseLabel = (c: any) => {
              const k = cases.find((x: any) => (x.clIds || []).includes(c.id));
              if (k) return k.title || k.ref || k.name || `#${k.id}`;
              const g = (collectiveCases || []).find((x: any) =>
                (x.clientLinks || []).some((lk: any) => lk.clId === c.id),
              );
              if (g) return g.name || g.title || `CC-${g.id}`;
              return null;
            };
            const runDeleteSelection = () => {
              const ids = orphans
                .filter((c: any) => dbCleanupChecked.has(c.id))
                .map((c: any) => c.id);
              if (!ids.length) return;
              scheduleClDelete(ids);
              setOrphanDlg(false);
            };
            return (
              <ModalLayout
                title={t("app.client.cleanup_title", {
                  fallback: "Database cleanup",
                })}
                onClose={() => setOrphanDlg(false)}
                closeOnOverlay
                stopShellClicks
                /* 166 §C — the `92vw` cap was the GLOBAL guard restated
                   (`min(92vw, 100vw)` is 92vw with no `--modal-max`), and a
                   viewport cap is what the ruling retires. Dropped; the
                   pixel figure was already the width, and the `!important`
                   min() still holds it inside a small screen. Renders
                   identically. */
                width={720}
                rawBody
                footer={
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="modal-cancel"
                      onClick={() => setOrphanDlg(false)}
                    >
                      {t("common.close", { fallback: "Close" })}
                    </button>
                    {orphans.length > 0 && (
                      <button
                        type="button"
                        className="modal-save"
                        disabled={!checkedCount}
                        style={{
                          background: checkedCount ? "var(--rd)" : "var(--tm)",
                          opacity: checkedCount ? 1 : 0.6,
                          cursor: checkedCount ? "pointer" : "not-allowed",
                        }}
                        onClick={runDeleteSelection}
                      >
                        <Trash2 size={I.s} style={{ marginRight: 4 }} />
                        {t("app.client.delete_selection", {
                          fallback: "Delete selection",
                        })}
                        {checkedCount ? ` (${checkedCount})` : ""}
                      </button>
                    )}
                  </div>
                }
              >
                  <div className="modal-body">
                    {orphans.length === 0 ? (
                      <p
                        style={{
                          fontSize: FS.s11,
                          color: "var(--tm)",
                          textAlign: "center",
                          padding: "20px 0",
                        }}
                      >
                        ✓{" "}
                        {t("app.client.cleanup_none", {
                          fallback:
                            "No unlinked clients — every client is linked to at least one case.",
                        })}
                      </p>
                    ) : (
                      <>
                        <p
                          style={{
                            fontSize: FS.s11,
                            marginBottom: 10,
                            color: "var(--ts)",
                          }}
                        >
                          {t("app.client.cleanup_intro", {
                            count: orphans.length,
                            fallback: `${orphans.length} client(s) not linked to any case (regular or collective). Review and delete what you don't need.`,
                          })}
                          {dupCount > 0
                            ? " " +
                              t("app.client.cleanup_intro_dups", {
                                count: dupCount,
                                fallback: `${dupCount} look like duplicates (same name + email) and are pre-selected — one copy per group is kept.`,
                              })
                            : ""}
                        </p>
                        <div
                          className="table-wrap"
                          style={{ maxHeight: 360, overflowY: "auto" }}
                        >
                          <Table style={{ width: "100%" }}>
                            <thead>
                              <tr>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("common.name", { fallback: "Name" })}
                                </th>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("common.email", { fallback: "Email" })}
                                </th>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("app.client.linked_case", {
                                    fallback: "Linked case",
                                  })}
                                </th>
                                <th
                                  style={{ fontSize: FS.s9, textAlign: "center" }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={toggleAll}
                                    title={t("common.select_all", {
                                      fallback: "Select all / none",
                                    })}
                                  />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {orphans.map((c: any) => {
                                const isDup = dupIds.has(c.id);
                                const isSurvivor = survivorIds.has(c.id);
                                const lc = linkedCaseLabel(c);
                                return (
                                  <tr
                                    key={c.id}
                                    style={
                                      isDup && !isSurvivor
                                        ? {
                                            background:
                                              "rgba(248,113,113,.10)",
                                          }
                                        : undefined
                                    }
                                  >
                                    <td style={{ fontSize: FS.s10 }}>
                                      <span className="name-cell">{pname(c)}</span>
                                      {isDup ? (
                                        <span
                                          className="tag"
                                          style={{
                                            marginLeft: 6,
                                            fontSize: FS.s8_5,
                                            color: isSurvivor
                                              ? "var(--tm)"
                                              : CT.c_fff,
                                            background: isSurvivor
                                              ? "transparent"
                                              : "var(--rd)",
                                            padding: "0 4px",
                                            borderRadius: RAD.r3,
                                          }}
                                        >
                                          {isSurvivor
                                            ? t("app.client.dup_kept", {
                                                fallback: "duplicate (kept)",
                                              })
                                            : t("app.client.dup", {
                                                fallback: "duplicate",
                                              })}
                                        </span>
                                      ) : (
                                        <span
                                          className="tag"
                                          style={{
                                            marginLeft: 6,
                                            fontSize: FS.s8_5,
                                            color: "var(--tm)",
                                          }}
                                        >
                                          {t("app.client.orphan_no_case", {
                                            fallback: "orphan, no case",
                                          })}
                                        </span>
                                      )}
                                    </td>
                                    <td style={{ fontSize: FS.s10 }}>
                                      {c.email || "—"}
                                    </td>
                                    <td style={{ fontSize: FS.s10 }}>
                                      {lc || "—"}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      <input
                                        type="checkbox"
                                        checked={dbCleanupChecked.has(c.id)}
                                        onChange={() => toggleRow(c.id)}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </div>
              </ModalLayout>
            );
          })()}
        {/* 211 §12 — the clients' subfolder check, loaded on use; 212 §27 — and
            the matters', opened from either case list */}
        {subfolderCheck && (
          <Suspense fallback={null}>
            <FolderPropagationModal
              purpose="fill"
              kind={subfolderCheck}
              onClose={() => {
                setSubfolderCheck(false);
              }}
            />
          </Suspense>
        )}
        {/* AUTHORITY (155 §4): orphanDlg above — see its note. */}
        {provisionDlg &&
          (() => {
            const list = provisionCandidateList();
            const CAP = 50;
            const capCount = Math.min(list.length, CAP);
            const checkedInList = list.filter((c: any) =>
              provisionChecked.has(c.id),
            );
            const checkedCount = checkedInList.length;
            const allChecked = capCount > 0 && checkedCount >= capCount;
            const maxTip = t("app.client.provision_max_tip", {
              fallback: "Maximum 50 per turn",
            });
            const toggleRow = (id: any) =>
              setProvisionChecked((prev: any) => {
                const nx = new Set(prev);
                if (nx.has(id)) {
                  nx.delete(id);
                  return nx;
                }
                if (nx.size >= CAP) return prev; // 50-cap reached — no-op
                nx.add(id);
                return nx;
              });
            // Select-all toggles between "first 50 in display order" and none —
            // the list is id-sorted, so "first 50" is deterministic + reproducible.
            const toggleAll = () =>
              setProvisionChecked(
                checkedCount >= capCount
                  ? new Set()
                  : new Set(list.slice(0, CAP).map((c: any) => c.id)),
              );
            // Linked case (col iii): folderless clients CAN be linked, so this
            // resolves a real case / collective-case label when one exists.
            const linkedCaseLabel = (c: any) => {
              const k = cases.find((x: any) => (x.clIds || []).includes(c.id));
              if (k) return k.title || k.ref || k.name || `#${k.id}`;
              const g = (collectiveCases || []).find((x: any) =>
                (x.clientLinks || []).some((lk: any) => lk.clId === c.id),
              );
              if (g) return g.name || g.title || `CC-${g.id}`;
              return null;
            };
            /* 206C §4.1 [RULED — Laurent: "(c)"] — THE ROWS BRIEF 04's RULING 5
               OFFERS RATHER THAN PROVISIONS, MARKED. An archived client and a
               prospect are the two a folder is never made for automatically
               (the create path's own guard, `scheduleClientFolder`); here a
               human may still tick them — that is the affordance the ruling
               names — so the list says which they are. The chips are the
               client record's OWN marks, reused and not redrawn: the detail
               pane's « Archived » chip (`clientArchivedChip`, moved out of
               ecfg.tsx for this) and the Clients list's « Prospect » pill
               (`clientStandingPill`'s first arm). The row is read as
               `unknown` and narrowed, so an `any` list row costs nothing. */
            const marksOf = (c: unknown) => {
              const row: { archived?: unknown; isProspect?: unknown } =
                typeof c === "object" && c !== null ? c : {};
              const tt = t as TFn;
              return (
                <span style={{ display: "inline-flex", gap: 4 }}>
                  {row.archived === true && clientArchivedChip(tt)}
                  {row.isProspect === true &&
                    clientStandingPill({ standing: null, isProspect: true, t: tt, inheritedLabel: "" })}
                </span>
              );
            };
            const runProvision = () => {
              const ids = checkedInList.map((c: any) => c.id);
              if (!ids.length) return;
              setProvisionDlg(false);
              doProvisionFolders(ids);
            };
            return (
              <ModalLayout
                title={t("app.client.provision_title", {
                  fallback: "Provision client folders",
                })}
                onClose={() => setProvisionDlg(false)}
                closeOnOverlay
                stopShellClicks
                /* 166 §C — as the cleanup dialog above: the global guard,
                   restated. Dropped; identical render. */
                width={720}
                rawBody
                footer={
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="modal-cancel"
                      onClick={() => setProvisionDlg(false)}
                    >
                      {t("common.cancel", { fallback: "Cancel" })}
                    </button>
                    {/* 211 §12 — the clients that HAVE a folder: the check inside it */}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => {
                        setProvisionDlg(false);
                        setSubfolderCheck("client");
                      }}
                    >
                      {(t as TFn)("folder_fill.title", { fallback: "Check the subfolders" })}
                    </button>
                    {list.length > 0 && (
                      <button
                        type="button"
                        className="modal-save"
                        disabled={!checkedCount || backfillBusy}
                        style={{
                          opacity: checkedCount && !backfillBusy ? 1 : 0.6,
                          cursor:
                            checkedCount && !backfillBusy
                              ? "pointer"
                              : "not-allowed",
                        }}
                        onClick={runProvision}
                      >
                        <Folder size={I.s} style={{ marginRight: 4 }} />
                        {t("app.client.provision_confirm", {
                          fallback: "Provision",
                        })}
                        {checkedCount ? ` (${checkedCount})` : ""}
                      </button>
                    )}
                  </div>
                }
              >
                  <div className="modal-body">
                    {list.length === 0 ? (
                      <p
                        style={{
                          fontSize: FS.s11,
                          color: "var(--tm)",
                          textAlign: "center",
                          padding: "20px 0",
                        }}
                      >
                        ✓{" "}
                        {t("toast.no_missing_folders", {
                          fallback: "No clients are missing a folder.",
                        })}
                      </p>
                    ) : (
                      <>
                        <p
                          style={{
                            fontSize: FS.s11,
                            marginBottom: 4,
                            color: "var(--ts)",
                          }}
                        >
                          {t("app.client.provision_intro", {
                            fallback:
                              "The following clients have no individual file yet. Please select the clients for which such file should be created:",
                          })}
                        </p>
                        <p
                          style={{
                            fontSize: FS.s10,
                            marginBottom: 10,
                            color: "var(--tm)",
                            fontStyle: "italic",
                          }}
                        >
                          {t("app.client.provision_dups_excluded", {
                            fallback: "(Duplicates excluded)",
                          })}
                        </p>
                        <div
                          className="table-wrap"
                          style={{ maxHeight: 360, overflowY: "auto" }}
                        >
                          <Table style={{ width: "100%" }}>
                            <thead>
                              <tr>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("common.name", { fallback: "Name" })}
                                </th>
                                {/* 206C §4.1 — the marks, beside the name they qualify. */}
                                <th style={{ fontSize: FS.s9 }}>
                                  {(t as TFn)("common.status", { fallback: "Status" })}
                                </th>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("common.email", { fallback: "Email" })}
                                </th>
                                <th style={{ fontSize: FS.s9 }}>
                                  {t("app.client.linked_case", {
                                    fallback: "Linked case",
                                  })}
                                </th>
                                <th
                                  style={{ fontSize: FS.s9, textAlign: "center" }}
                                  title={maxTip}
                                >
                                  <input
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={toggleAll}
                                    title={maxTip}
                                  />
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {list.map((c: any) => {
                                const isChecked = provisionChecked.has(c.id);
                                const disabled =
                                  !isChecked && checkedCount >= CAP;
                                const lc = linkedCaseLabel(c);
                                return (
                                  <tr key={c.id}>
                                    <td style={{ fontSize: FS.s10 }}>
                                      <span className="name-cell">{pname(c)}</span>
                                    </td>
                                    <td style={{ fontSize: FS.s10 }}>{marksOf(c)}</td>
                                    <td style={{ fontSize: FS.s10 }}>
                                      {c.email || "—"}
                                    </td>
                                    <td style={{ fontSize: FS.s10 }}>
                                      {lc || "—"}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={disabled}
                                        onChange={() => toggleRow(c.id)}
                                        title={disabled ? maxTip : undefined}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </>
                    )}
                  </div>
              </ModalLayout>
            );
          })()}
        {timerLimitDlg && (
          <ModalLayout
            title={t("modal.timer_limit.title", withCount(maxTimers, "n", { fallback: "Maximum {n} timers allowed",
            }))}
            onClose={() => setTimerLimitDlg(null)}
            closeOnOverlay
            stopShellClicks
            /* 166 §C — CHANGES WHAT RENDERS: 480 → 420. */
            maxWidth={420}
            rawBody
            footer={
              <div className="modal-footer">
                <button
                  type="button"
                  className="modal-cancel"
                  onClick={() => setTimerLimitDlg(null)}
                >
                  {t("common.cancel", { fallback: "Cancel" })}
                </button>
              </div>
            }
          >
              <div className="modal-body">
                <p
                  style={{ fontSize: FS.s11, color: "var(--ts)", marginBottom: 10 }}
                >
                  {t("modal.timer_limit.prompt", {
                    fallback: "Please close one to continue:",
                  })}
                </p>
                {activeTimers.map((tm: any, i: any) => {
                  // BRIEF 91 §3a — task OR event subtask; same key fix.
                  const tsk2 = timerSubject(tm, tasks, agendaEvents)
                    ?.parent as any;
                  const cs2 = resolveMatter(tsk2, cases, collectiveCases); // 191 §C
                  const el = getTimerElapsed(tm);
                  return (
                    <div
                      key={timerKey(tm)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 0",
                        borderBottom: "1px solid var(--br)",
                      }}
                    >
                      {/* BRIEF 102 §1a/§1c — the cut goes and the CONTAINER
                          takes its job. A flex item is blockified, so this span
                          can carry `text-overflow` once it is allowed to shrink
                          (`minWidth: 0` — §1c's step two; flex items default to
                          `min-width: auto` and will not shrink below their
                          content). The elapsed time and the button beside it are
                          both `flexShrink: 0`, so this span is the row's only
                          shrink sink and the row stays one line at any width —
                          which the 20-char cut could not promise, since the TASK
                          title next to it was never cut at all. */}
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: FS.s11,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "var(--tp)" }}>
                          {tsk2?.title || t("header.task", { fallback: "Task" })}
                        </span>
                        {cs2 && (
                          <span
                            style={{
                              color: "var(--tm)",
                              marginLeft: 6,
                              fontSize: FS.s10,
                            }}
                          >
                            — {cs2.title}
                          </span>
                        )}
                      </span>
                      <span
                        style={{
                          fontSize: FS.s10,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: tm.paused ? "var(--am)" : "var(--gn)",
                          flexShrink: 0,
                        }}
                      >
                        {fmtElapsed(el)}
                      </span>
                      <button
                        /* 190 round 5 [P] addendum (Laurent, 2026-09-16) —
                           closing a timer and logging its time is not a
                           deletion; the affirmative family. */
                        className="btn btn-primary"
                        style={{
                          padding: "3px 10px",
                          fontSize: FS.s10,
                          flexShrink: 0,
                        }}
                        onClick={() =>
                          stopTimerForSwap(i, timerLimitDlg.pendingKey)
                        }
                      >
                        {t("modal.timer_limit.close_and_log", {
                          fallback: "Close & Log",
                        })}
                      </button>
                    </div>
                  );
                })}
              </div>
          </ModalLayout>
        )}
        {subtaskDoneAsk &&
          (() => {
            /* Phase 29 Stage-3 C9 — generalized done-prompt: a null subtask
               means a TASK was checked off (compact column); the subtask
               branch is byte-identical to the pre-existing behavior. Both
               open the canonical time-entry modal with the _fromTask /
               _fromSubtask prefills. */
            const tk2 = subtaskDoneAsk.task || {};
            const sx = subtaskDoneAsk.subtask || null;
            const today = new Date().toISOString().slice(0, 10);
            /* C61 (supersedes C19 for THIS prompt) — a checked subtask
               prefills "task title — subtask title"; a checked task keeps
               the task title only. The manual per-row "Log time" clock
               button keeps its C19 subtask-text-only prefill. */
            const _desc = sx
              ? (tk2.title ? tk2.title + " — " : "") + (sx.text || "")
              : tk2.title || "";
            /* Round 13 (C76) — Cancel (button, X, Escape, click-outside)
               REVERTS the trigger: the subtask gets UNTICKED (the checklist
               persisted the tick before prompting — cancelSubtaskDone PUTs
               {done:false} and rolls state back). The legacy null-subtask
               (task-done) variant has no persisted trigger to revert.
               Round 17 (C102d) — both variants funnel through
               cancelSubtaskDone now: it also RESUMES the held timer (the
               untick part stays guarded to the subtask variant inside). */
            const _cancel = cancelSubtaskDone;
            return (
              <ModalLayout
                title={
                  sx
                    ? t("app.subtask.done_prompt", {
                        fallback: "Subtask done",
                      })
                    : t("app.task.done_prompt", {
                        fallback: "Task done",
                      })
                }
                onClose={_cancel}
                closeOnOverlay
                stopShellClicks
                zIndex={Z.popoverBackdrop}
                /* 166 §C — CHANGES WHAT RENDERS: 480 → 380. */
                maxWidth={380}
                rawBody
                footer={null}
              >
                  <div style={{ padding: "16px 20px" }}>
                    <p
                      style={{
                        fontSize: FS.s11_5,
                        color: "var(--ts)",
                        marginBottom: 6,
                      }}
                    >
                      <strong>
                        {sx
                          ? sx.text ||
                            t("app.subtask_fallback", { fallback: "Subtask" })
                          : tk2.title ||
                            t("header.task", { fallback: "Task" })}
                      </strong>
                    </p>
                    <p
                      style={{
                        fontSize: FS.s11,
                        color: "var(--tm)",
                        marginBottom: 14,
                      }}
                    >
                      {sx
                        ? t("app.subtask.log_prompt", {
                            fallback: "Log time on this subtask?",
                          })
                        : t("app.task.log_prompt", {
                            fallback: "Log time on this task?",
                          })}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <button
                        className="modal-save"
                        onClick={() => {
                          /* Round 14 (C87) — case auto-bound (cc included),
                             locked; description stays exactly
                             "task title — subtask title".
                             Round 15 (C91) — the modal opens with the
                             payload the prompt CARRIES (built at trigger
                             time from the canonical row — see
                             askSubtaskDone); the inline build is only the
                             fallback for a legacy ask without one. */
                          openModal(
                            "te",
                            subtaskDoneAsk.tePayload || {
                              caId: tk2.caseId || "",
                              ccId: tk2.ccId || "",
                              coId: currentUser || "",
                              date: today,
                              type: tk2.type || "",
                              desc: _desc,
                              billed: false,
                              min: 0,
                              _fromTask: tk2.id || true,
                              ...(tk2.caseId != null || tk2.ccId != null
                                ? {
                                    _caseFixed: true,
                                    _caseTitle:
                                      tk2.caseTitle || tk2.ccTitle || "",
                                    _caseRef: tk2.caseRef || tk2.ccRef || "",
                                  }
                                : {}),
                              ...(sx
                                ? { _fromSubtask: 1, subtaskId: sx.id }
                                : {}),
                            },
                          );
                          setSubtaskDoneAsk(null);
                        }}
                      >
                        {t("modal.tk.log_time_btn", { fallback: "Add time entry" })}
                      </button>
                      <button
                        className="modal-cancel"
                        onClick={() => {
                          /* Round 17 (C102c) — the entity is done: its held
                             timer stops and clears (no entry). */
                          if (subtaskDoneAsk._timerHold)
                            releaseEntityTimer(
                              subtaskDoneAsk._timerHold,
                              "clear",
                            );
                          setSubtaskDoneAsk(null);
                        }}
                      >
                        {t("modal.tk.already_registered", {
                          fallback: "Already registered, just finish",
                        })}
                      </button>
                      <button className="modal-cancel" onClick={_cancel}>
                        {t("common.cancel", { fallback: "Cancel" })}
                      </button>
                    </div>
                  </div>
              </ModalLayout>
            );
          })()}
        {/* Phase 31 (C3) — alert toasts (T-15 / T-0 / reminders). Top-right
            stack, Join affordance when the event carries a join URL.
            Brief 42 §0/§1 — they no longer auto-dismiss, and they ALL stack:
            RULED 2026-08-06, chosen over a count and over a cap. Six alerts is
            six toasts and that weight is the intent — a version that softens
            itself on the busy morning is a version that does nothing on the
            morning it matters.
            ONE CONSTRAINT FOLLOWS FROM THAT RULING RATHER THAN QUALIFYING IT:
            THE STACK MUST NEVER COVER THE BELL. The Bell is now the only exit,
            so a stack that hid it would be a deadlock — the user could not
            perform the single action that clears the screen. Two things
            enforce it. The stack starts BELOW the Bell, measured off `bellRef`
            — the same rect the Bell's own dropdown anchors to (App.tsx:17869) —
            rather than the hardcoded 70 that stood here, which a taller header
            or a --ui-zoom step could out-grow silently. And it is bounded by
            the viewport with `overflowY: auto`, so an over-long stack scrolls
            INSIDE itself instead of running down over the chrome. */}
        {/* 119 §3 — THE ORPHAN POPUP RIDES THIS COLUMN, it does not build a
            second one. "Same manners as the existing reminders" is the
            brief's instruction, and the manners are this box: the rect under
            the bell, the shadow padding 108 §3 measured, the scroll bound,
            the pointer-events split. A card positioned by its own constants
            would be a second opinion about where a reminder appears. */}
        {(alertToasts.length > 0 || outlookPopup != null) &&
          (() => {
            const __ar = bellRef.current?.getBoundingClientRect();
            const __atop = __ar ? __ar.bottom + 8 : 70;
            /* Brief 42 §1.3 — the sentence that says why the toast will not
               leave. One key, three rows, written in this unit and seeded in
               en/fr/nl (server/scripts/data/translations_seed.json). NO
               `fallback:` literal, unlike its `alerts.*` siblings: §4 forbids
               writing this tooltip as a literal in any language, English
               included, so an unseeded box must show the bare key — a loud
               signal — rather than quietly render an English sentence nobody
               registered. */
            const _bellTip = t("alerts.close_via_bell_tip");
            return (
              <div
                style={{
                  /* BRIEF 108 §3 — THE SLAB WAS THE SHADOW, SLICED SQUARE BY
                     THIS BOX. `overflowY: auto` clips at the padding box, and
                     this wrapper's box was EXACTLY the card column's rect — so
                     each card's 24px-blur, .6-alpha shadow terminated in a
                     hard edge ON the cards' own perimeter: a straight-cornered
                     slab bleeding at all four corners of the rounded cards
                     (measured in the mirror with elementsFromPoint: no child
                     paints square; the only square paint is the clipped
                     shadow). The clip boundary now sits one shadow-reach away
                     from the paint: padding absorbs the shadow's ink (blur 24,
                     y-offset 8 → 16 top / 24 left / 32 bottom; 16 right runs
                     to the viewport edge, beyond which there is nothing to
                     slice), and the fixed coordinates compensate so the cards
                     render exactly where they always did. The scroll bound
                     stays — an over-long stack still scrolls inside itself. */
                  position: "fixed",
                  top: __atop - 16,
                  right: 0,
                  zIndex: Z.stack,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  maxWidth: 408,
                  maxHeight: `calc(100vh - ${__atop}px)`,
                  padding: "16px 16px 32px 32px",
                  overflowY: "auto",
                  /* The wrapper is invisible chrome and must not grow a click
                     shield as it grows padding: events pass through it; each
                     card re-enables itself below. Wheel-scrolling still works
                     — the cards are the hit targets and this box is their
                     nearest scrollable ancestor. */
                  pointerEvents: "none",
                }}
              >
            {alertToasts.map((a: any) => {
              /* Brief 42 follow-on — the kind no longer names the number,
                 so the LABEL reads the number out of the payload instead. It
                 can therefore never disagree with the engine again, which is
                 the same reason the identity stopped carrying it. No
                 `fallback:` literal: the constraint on this branch is that no
                 new literal is written in any language, English included. */
              const isLead = a.kind === "event_lead";
              const isT0 = a.kind === "event_t0";
              /* Brief 46 §1 — the all-day lead reads its number out of the
                 payload exactly as the minute lead does, through the same
                 keys the bell branch uses (App.tsx:16442). Two renderings of
                 one row, still. */
              const isDays = a.kind === "event_allday_lead";
              /* Brief 46 §2 — same rule as the bell branch above: a late row
                 states that it is late instead of counting down to something
                 that may already have happened. */
              const label = a.late
                ? t("alerts.late", { on: fd(a.shouldHaveFiredOn) })
                : isLead
                ? t("alerts.lead", withCount(a.leadMinutes, "n", {  }))
                : isDays
                  ? t("alerts.days_left", { count: a.leadDays })
                  : isT0
                    ? t("alerts.t0", { fallback: "Starting now" })
                    : a.kind === "task_reminder"
                      ? t("alerts.task_reminder", { fallback: "Task reminder" })
                      : /* 182 C.2 — same words as the bell branch above. */
                        a.kind === "silence"
                        ? t("alerts.silence", withCount(a.days, "days", { date: fd(a.since) }))
                        : t("alerts.event_reminder", {
                            fallback: "Event reminder",
                          });
              /* A deadline is red like a T-0, not amber like an hour's notice:
                 the countdown band in the case view is already red for `PRE`
                 and a toast that disagreed with it would be a third opinion. */
              const color = isT0 || isDays || a.late
                ? "var(--rd)"
                : isLead
                  ? "var(--am)"
                  : "var(--ac)";
              return (
                <div
                  key={a.uid}
                  style={{
                    display: "flex",
                    /* 213B §6 — the join buttons wrap onto a line of their own
                       under the words (below); the row may wrap. */
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 8,
                    background: "var(--bc)",
                    border: `1px solid ${color}`,
                    borderRadius: RAD.r10,
                    boxShadow: "0 8px 24px rgba(0,0,0,.6)",
                    padding: "9px 12px",
                    /* BRIEF 108 §3 — the rounded box clips its own children:
                       no future child can poke a square corner past the
                       radius. And the card re-enables the events its wrapper
                       now lets through. */
                    overflow: "hidden",
                    pointerEvents: "auto",
                  }}
                >
                  <span style={{ color, flexShrink: 0, display: "inline-flex" }}>
                    {a.entity === "task" ? (
                      <Bell size={15} />
                    ) : (
                      <Calendar size={15} />
                    )}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: FS.s9,
                        fontWeight: 700,
                        color,
                        textTransform: "uppercase",
                        letterSpacing: ".4px",
                      }}
                    >
                      {label}
                      {a.time ? ` · ${a.time}` : ""}
                    </div>
                    <div
                      style={{
                        fontSize: FS.s11_5,
                        fontWeight: 600,
                        color: "var(--tp)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={a.title}
                    >
                      {(a.type ? a.type + ": " : "") + a.title}
                    </div>
                    {/* Brief 42 §1.3 — the click half of "on hover and on
                        click, a tooltip". `whiteSpace: normal` because this
                        one is allowed to wrap; the title line above is
                        deliberately clipped to one line and must stay so. */}
                    {alertTipUid === a.uid && (
                      <div
                        role="tooltip"
                        style={{
                          marginTop: 4,
                          fontSize: FS.s10_5,
                          fontWeight: 600,
                          color: "var(--tm)",
                          whiteSpace: "normal",
                        }}
                      >
                        {_bellTip}
                      </div>
                    )}
                  </div>
                  {/* Brief 42 §1.2-1.4 — the ✕ is now a BELL, and it is a
                      SIGNPOST rather than a control. It names the only exit
                      the ruling leaves — open the Bell menu — and does NOTHING
                      else when clicked: no dismiss, no navigation, no opening
                      the menu on the user's behalf. Laurent: "do nothing else
                      on clicking that symbol."
                      It is the same Lucide `Bell` the menubar renders (App.tsx:17842),
                      taken from the one import at the head of this file rather
                      than a second import path for the same glyph.
                      HOVER says the sentence through `title`; CLICK reveals the
                      same sentence as a line inside the toast (above). A line
                      and not a floating bubble on purpose: the stack is an
                      `overflowY: auto` container, and CLAUDE.md's own note on
                      overflow-clipped popups says escaping one needs a portal.
                      One sentence does not earn a portal, and a line in flow
                      cannot be clipped. */}
                  <button
                    type="button"
                    title={_bellTip}
                    aria-label={_bellTip}
                    aria-expanded={alertTipUid === a.uid}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--tm)",
                      cursor: "pointer",
                      padding: 2,
                      flexShrink: 0,
                      display: "inline-flex",
                    }}
                    onClick={() =>
                      setAlertTipUid((u) => (u === a.uid ? null : a.uid))
                    }
                  >
                    <Bell size={13} />
                  </button>
                  {/* 213B §6 [ADVISOR] — THE JOIN BUTTONS ON A LINE OF THEIR OWN,
                      under the label and the title, which keep the card's full
                      width beside the icon with the bell at the top right. Two
                      buttons that may not shrink were squeezing the text block
                      to one letter (your words, 29/09: « the virtual meeting
                      start notification is a mess »). The words, the colours,
                      what each button does and what dismisses the card are
                      unchanged; the line starts under the words (the icon's
                      width and the gap) — my judgement. */}
                  {joinUrlOf(a) && (
                    <div style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: 8, paddingLeft: 23 }}>
                      {a.joinUrl && (
                        <button
                          type="button"
                          className="btn"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            fontSize: FS.s10_5,
                            fontWeight: 700,
                            background: "rgba(74,222,128,.14)",
                            color: "var(--gn)",
                            border: "1px solid var(--gn)",
                            borderRadius: RAD.r6,
                            flexShrink: 0,
                          }}
                          onClick={() => {
                            window.open(a.joinUrl, "_blank", "noopener");
                            dismissAlertToast(a.uid);
                          }}
                        >
                          <Video size={12} />
                          {t("alerts.join", { fallback: "Join" })}
                        </button>
                      )}
                      {/* 116 §4 — AND THE ONE THE BRIEF ADDS, BESIDE IT rather than
                          instead of it: §4 says the reminder GAINS an action, and
                          plain Join is a real choice (a meeting somebody attends
                          without billing it). Offered only when the payload names
                          an agenda event, because the timer needs an id and the
                          toast's `joinUrl` alone cannot supply one — a `place.*` or
                          task payload carrying a link therefore keeps plain Join
                          and gains nothing, which is correct rather than a gap.
                          NOT dismissed on click, deliberately: the toast is the
                          only place this alert lives until the Bell is opened
                          (brief 42 §1.1), and having joined is not having read. */}
                      {a.joinUrl && a.entity === "agenda" && a.entityId != null && (
                        <button
                          type="button"
                          className="btn"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            fontSize: FS.s10_5,
                            fontWeight: 700,
                            background: "rgba(74,222,128,.14)",
                            color: "var(--gn)",
                            border: "1px solid var(--gn)",
                            borderRadius: RAD.r6,
                            flexShrink: 0,
                          }}
                          onClick={() => joinAndTime(+a.entityId, a.joinUrl)}
                        >
                          <Video size={12} />
                          {t("alerts.join_and_time", { fallback: "Join & time" })}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {/* ── 119 §3 — THE NUDGE, AND WHY ITS DISMISSAL IS SESSION STATE ──
                §3 says to reuse "the `dismissedReminders` mechanism the bell
                entry already uses — do not invent a second one". MEASURED:
                `dismissedReminders` is not session state. It is a SERVER-BACKED
                preference (`prefs.dismissedReminders`, App.tsx:3582 →
                `usePreferences` → `PATCH /api/me/preferences`), so it is
                durable and reaches every device. Reusing it has exactly two
                shapes and §3's own acceptance criterion refutes both:

                  · the bell's own id, `"outlook-import"` — the bell entry is
                    built behind `!dismissedReminders.includes("outlook-import")`
                    (:10830 above), so dismissing the popup would DELETE the
                    bell entry. §3 requires the opposite: "dismissing it
                    silences it for the session WHILE THE BELL KEEPS THE ENTRY".
                  · a second, distinct id — durable too, so it silences the
                    popup FOREVER rather than for the session.

                So the mechanism named cannot produce the behaviour ruled beside
                it, and the dismissal is a ref that dies with the tab. The bell
                entry and its durable dismissal are untouched.

                NO NEW KEY, AND NO SEED ROW IS OWED. The eyebrow is the import
                dialog's own `outlookImport.title`, the sentence is the bell's
                own `notif.outlook_import` and the action is the Daily
                Overview's own `outlookImport.review_button` — one fact, one
                wording, wherever it is rendered. All three resolve in en/fr/nl
                today; the last two are absent from `translations_seed.json`
                and seeded by `server/scripts/seed_deploy_phase31_strings.js`
                :215-220 instead, which is worth knowing before reading that
                file as the catalogue (it is one seeder of several). Measured
                in the drive: the card renders "{count} événement(s) Outlook à
                importer" in a French app. */}
            {outlookPopup != null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "var(--bc)",
                  border: "1px solid var(--ac)",
                  borderRadius: RAD.r10,
                  boxShadow: "0 8px 24px rgba(0,0,0,.6)",
                  padding: "9px 12px",
                  overflow: "hidden",
                  pointerEvents: "auto",
                }}
              >
                <span
                  style={{
                    color: "var(--ac)",
                    flexShrink: 0,
                    display: "inline-flex",
                  }}
                >
                  <CalendarClock size={15} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: FS.s9,
                      fontWeight: 700,
                      color: "var(--ac)",
                      textTransform: "uppercase",
                      letterSpacing: ".4px",
                    }}
                  >
                    {t("outlookImport.title", {
                      fallback: "Import from Outlook",
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: FS.s11_5,
                      fontWeight: 600,
                      color: "var(--tp)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("notif.outlook_import", {
                      count: outlookPopup,
                      fallback: `${outlookPopup} Outlook event${outlookPopup === 1 ? "" : "s"} to import`,
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{
                    padding: "4px 10px",
                    fontSize: FS.s10_5,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                  onClick={() => {
                    /* Acting on it CLOSES but does not mute: the count drops
                       once the import runs, and a later rise deserves the same
                       nudge. Muting is what the ✕ means. */
                    setOutlookPopup(null);
                    setAgAutoImport(true);
                    nav("ag");
                  }}
                >
                  {t("outlookImport.review_button", {
                    fallback: "Review & import",
                  })}
                </button>
                <button
                  type="button"
                  title={t("notif.dismiss")}
                  aria-label={t("notif.dismiss")}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--tm)",
                    cursor: "pointer",
                    padding: 2,
                    flexShrink: 0,
                    display: "inline-flex",
                  }}
                  onClick={() => {
                    setOutlookPopup(null);
                    outlookPopupMutedRef.current = true;
                  }}
                >
                  <X size={13} />
                </button>
              </div>
            )}
              </div>
            );
          })()}
        {/* Phase 31 (C2) — incoming-call popups (main window; the pop-out
            renders its own banner inside CompactColumn). One card per live
            ring; Accept opens the Teams join URL, Decline opens the
            availability time picker. Cards self-expire on the local
            countdown; the server sweep is authoritative.
            31.1 (item 6) — the caller-side waiting card shares this exact
            wrapper (same size, same position): "Waiting for an answer…"
            with live outcome chips and a red Abort while ringing, then it
            resolves IN PLACE from the same persistent 'call' events the
            log consumes — answered flips it to a Join button (synchronous
            window.open inside the caller's click → popup-safe), declined
            surfaces the availability message, timeout reads "no answer". */}
        {(outgoingCall || incomingCalls.length > 0) && (
          <div
            style={{
              position: "fixed",
              top: 70,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: Z.top,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {outgoingCall &&
              (() => {
                /* 31.1 (item 9) — host routing: a pop-out-initiated card
                   lives in the pop-out's column banner; this main-window
                   card renders for main-initiated calls AND as the
                   fallback when the pop-out died mid-ring (the card must
                   never vanish with its host). */
                if (
                  outgoingCall.host === "popout" &&
                  popOutCtr &&
                  popOutWinRef.current &&
                  !popOutWinRef.current.closed
                )
                  return null;
                const oc = calls.find((c: any) => c.id === outgoingCall.callId);
                if (!oc || oc.status === "canceled") return null;
                const ringing = oc.status === "ringing";
                const OC_COLOR: Record<string, any> = {
                  answered: "var(--gn)",
                  declined: "var(--am)",
                  missed: "var(--rd)",
                  ringing: "var(--ac)",
                };
                const byP = (p: any) => collaborators.find((x: any) => x.id === p.collaboratorId);
                const answeredP = (oc.participants || []).filter(
                  (p: any) => p.outcome === "answered",
                );
                const declinedP = (oc.participants || []).filter(
                  (p: any) => p.outcome === "declined",
                );
                return (
                  <div
                    style={{
                      minWidth: 340,
                      maxWidth: 420,
                      background: "var(--bc)",
                      border: "1px solid var(--ac)",
                      borderRadius: RAD.r12,
                      boxShadow: "0 10px 36px rgba(0,0,0,.75)",
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          color:
                            oc.status === "answered"
                              ? "var(--gn)"
                              : oc.status === "ringing"
                                ? "var(--ac)"
                                : "var(--tm)",
                          animation: ringing
                            ? "p31ringPulse 1s ease-in-out infinite"
                            : "none",
                        }}
                      >
                        <PhoneOutgoing size={22} />
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: FS.s10,
                            color: "var(--tm)",
                            textTransform: "uppercase",
                            letterSpacing: ".4px",
                            fontWeight: 700,
                          }}
                        >
                          {oc.isCollective
                            ? t("calls.outgoing_collective", {
                                fallback: "Outgoing collective call",
                              })
                            : t("calls.outgoing", {
                                fallback: "Outgoing call",
                              })}
                        </div>
                        <div
                          style={{
                            fontSize: FS.s13,
                            fontWeight: 700,
                            color:
                              oc.status === "missed"
                                ? "var(--rd)"
                                : oc.status === "declined"
                                  ? "var(--am)"
                                  : "var(--tp)",
                          }}
                        >
                          {ringing
                            ? t("calls.waiting", {
                                fallback: "Waiting for an answer…",
                              })
                            : oc.status === "answered"
                              ? t("calls.answered_modal", {
                                  name: answeredP.length
                                    ? pname(byP(answeredP[0]) || {})
                                    : "?",
                                  fallback: `${answeredP.length ? pname(byP(answeredP[0]) || {}) : "?"} answered`,
                                })
                              : oc.status === "missed"
                                ? t("calls.no_answer", {
                                    fallback: "No answer",
                                  })
                                : t("calls.outcome.declined", {
                                    fallback: "declined",
                                  })}
                        </div>
                      </div>
                      <span
                        style={{
                          display: "inline-flex",
                          gap: 3,
                          flexShrink: 0,
                        }}
                      >
                        {(oc.participants || []).map((p: any) => {
                          const c2 = byP(p);
                          if (!c2) return null;
                          return (
                            <InitialsBadge {...({} as any)}
                              key={p.collaboratorId}
                              person={c2}
                              size={20}
                              fontSize={8.5}
                              title={pname(c2)}
                              style={{
                                boxShadow: `0 0 0 1.5px ${OC_COLOR[p.outcome] || "var(--br)"}`,
                              }}
                            />
                          );
                        })}
                      </span>
                    </div>
                    {/* Declined availability lines — the live row carries
                        each decliner's availableFrom. */}
                    {!ringing && declinedP.length > 0 && (
                      <div
                        style={{
                          fontSize: FS.s10_5,
                          color: "var(--ts)",
                          marginBottom: 8,
                        }}
                      >
                        {declinedP.map((p: any) => {
                          const c2 = byP(p);
                          const name = c2 ? pname(c2) : "?";
                          return (
                            <div key={p.collaboratorId}>
                              {p.availableFrom
                                ? t("calls.decline_msg", {
                                    name,
                                    time: p.availableFrom,
                                    fallback: `${name} can't take this call right now, but will be available from ${p.availableFrom}`,
                                  })
                                : t("calls.declined_toast", {
                                    name,
                                    fallback: `${name} declined the call`,
                                  })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Phase 42 — camera pre-warm reassurance: shown only while
                        ringing (pre-connection); the ringing card is dismissed on
                        answer, so it never shows once video is actually flowing. */}
                    {ringing && (
                      <div style={{ fontSize: FS.s10_5, color: "var(--tm)", marginBottom: 8 }}>
                        {t("calls.no_video_before_connect", { fallback: "No video sent before connection" })}
                      </div>
                    )}
                    {ringing ? (
                      <button
                        type="button"
                        className="btn"
                        style={{
                          width: "100%",
                          justifyContent: "center",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 10px",
                          fontSize: FS.s12,
                          fontWeight: 700,
                          background: "rgba(248,113,113,.12)",
                          color: "var(--rd)",
                          border: "1px solid var(--rd)",
                          borderRadius: RAD.r8,
                        }}
                        onClick={() => abortCall(oc.id)}
                      >
                        <PhoneOff size={14} />
                        {t("calls.abort", { fallback: "Abort" })}
                      </button>
                    ) : (
                      <div style={{ display: "flex", gap: 8 }}>
                        {oc.status === "answered" && oc.joinUrl && (
                          <button
                            type="button"
                            className="btn"
                            style={{
                              flex: 1,
                              justifyContent: "center",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "8px 10px",
                              fontSize: FS.s12,
                              fontWeight: 700,
                              background: "rgba(74,222,128,.14)",
                              color: "var(--gn)",
                              border: "1px solid var(--gn)",
                              borderRadius: RAD.r8,
                            }}
                            onClick={() => {
                              /* Synchronous open inside the click — the
                                 popup-safe leg of item 6(b). */
                              try {
                                window.open(oc.joinUrl, "_blank");
                              } catch {
                                /* ignore */
                              }
                              setOutgoingCall(null);
                            }}
                          >
                            <Phone size={14} />
                            {t("calls.join", { fallback: "Join the call" })}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{
                            flex: oc.status === "answered" ? "0 0 auto" : 1,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "8px 14px",
                            fontSize: FS.s12,
                          }}
                          onClick={() => setOutgoingCall(null)}
                        >
                          {t("common.close", { fallback: "Close" })}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            {incomingCalls.map((ic: any) => {
              const caller = collaborators.find((c: any) => c.id === ic.initiatorId);
              const callerName = caller ? pname(caller) : "?";
              const otherInvitees = (ic.inviteeIds || []).filter(
                (id: any) => id !== currentUser,
              );
              const secsLeft = Math.max(
                0,
                Math.ceil((ic.deadline - Date.now()) / 1000),
              );
              const declining =
                callDeclineFor && callDeclineFor.callId === ic.callId;
              const defTime = (() => {
                const d = new Date(Date.now() + 30 * 60000);
                d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
                return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
              })();
              return (
                <div
                  key={ic.callId}
                  style={{
                    minWidth: 340,
                    maxWidth: 420,
                    background: "var(--bc)",
                    border: "1px solid var(--ac)",
                    borderRadius: RAD.r12,
                    boxShadow: "0 10px 36px rgba(0,0,0,.75)",
                    padding: "14px 16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        color: "var(--gn)",
                        animation: "p31ringPulse 1s ease-in-out infinite",
                      }}
                    >
                      <PhoneIncoming size={22} />
                    </span>
                    {/* Phase 38 (CK1) — caller's photo on the incoming card,
                        reusing the photo-capable badge from the log / outgoing
                        card (initials fallback when no M365 photo). */}
                    {caller && (
                      <InitialsBadge {...({} as any)}
                        person={caller}
                        size={34}
                        fontSize={13}
                        title={callerName}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: FS.s10,
                          color: "var(--tm)",
                          textTransform: "uppercase",
                          letterSpacing: ".4px",
                          fontWeight: 700,
                        }}
                      >
                        {ic.isCollective
                          ? t("calls.incoming_collective", {
                              fallback: "Incoming collective call",
                            })
                          : t("calls.incoming", { fallback: "Incoming call" })}
                      </div>
                      <div
                        style={{
                          fontSize: FS.s15,
                          fontWeight: 700,
                          color: "var(--tp)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {callerName}
                      </div>
                      {otherInvitees.length > 0 && (
                        <div style={{ fontSize: FS.s10, color: "var(--ts)" }}>
                          {t("calls.with_others", {
                            fallback: "Also invited:",
                          })}{" "}
                          {otherInvitees
                            .map((id: any) => {
                              const c2 = collaborators.find((x: any) => x.id === id);
                              return c2 ? coIni(c2) : "?";
                            })
                            .join(", ")}
                        </div>
                      )}
                    </div>
                    <span
                      className="mono"
                      style={{
                        fontSize: FS.s11,
                        color: secsLeft <= 10 ? "var(--rd)" : "var(--tm)",
                        flexShrink: 0,
                      }}
                    >
                      {secsLeft}s
                    </span>
                  </div>
                  {/* Phase 42 — camera pre-warm reassurance: the recipient's
                      camera device opens on ring (LED on) but no video is sent or
                      shown until they accept. Only in the accept/decline view;
                      the whole ring card is gone once accepted (connected). */}
                  {!declining && (
                    <div style={{ fontSize: FS.s10_5, color: "var(--tm)", marginBottom: 8 }}>
                      {t("calls.no_video_before_connect", { fallback: "No video sent before connection" })}
                    </div>
                  )}
                  {!declining ? (
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          flex: 1,
                          justifyContent: "center",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 10px",
                          fontSize: FS.s12,
                          fontWeight: 700,
                          background: "rgba(74,222,128,.14)",
                          color: "var(--gn)",
                          border: "1px solid var(--gn)",
                          borderRadius: RAD.r8,
                        }}
                        onClick={() => respondCall(ic.callId, "accept")}
                      >
                        <Phone size={14} />
                        {t("calls.accept", { fallback: "Accept" })}
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          flex: 1,
                          justifyContent: "center",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 10px",
                          fontSize: FS.s12,
                          fontWeight: 700,
                          background: "rgba(248,113,113,.12)",
                          color: "var(--rd)",
                          border: "1px solid var(--rd)",
                          borderRadius: RAD.r8,
                        }}
                        onClick={() =>
                          setCallDeclineFor({ callId: ic.callId, time: defTime })
                        }
                      >
                        <PhoneOff size={14} />
                        {t("calls.decline", { fallback: "Decline" })}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div
                        style={{
                          fontSize: FS.s10_5,
                          color: "var(--ts)",
                          marginBottom: 6,
                        }}
                      >
                        {t("calls.decline_msg_first_person", {
                          time: callDeclineFor.time,
                          fallback: `I can't take this call right now, but will be available from ${callDeclineFor.time}`,
                        })}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <TimeField
                          value={callDeclineFor.time}
                          onChange={(v) =>
                            setCallDeclineFor((d: any) => ({
                              ...d,
                              time: v,
                            }))
                          }
                          style={{ width: 110, height: 30 }}
                        />
                        {[30, 60].map((m) => (
                          <button
                            key={m}
                            type="button"
                            className="btn btn-ghost"
                            style={{ padding: "4px 8px", fontSize: FS.s10 }}
                            onClick={() => {
                              const d = new Date(Date.now() + m * 60000);
                              d.setMinutes(
                                Math.ceil(d.getMinutes() / 5) * 5,
                                0,
                                0,
                              );
                              setCallDeclineFor((x: any) => ({
                                ...x,
                                time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
                              }));
                            }}
                          >
                            +{m}m
                          </button>
                        ))}
                        <span style={{ flex: 1 }} />
                        <button
                          type="button"
                          className="btn btn-ghost"
                          style={{ padding: "4px 10px", fontSize: FS.s10_5 }}
                          onClick={() => setCallDeclineFor(null)}
                        >
                          {t("common.cancel", { fallback: "Cancel" })}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ padding: "4px 12px", fontSize: FS.s10_5 }}
                          onClick={() =>
                            respondCall(
                              ic.callId,
                              "decline",
                              callDeclineFor.time,
                            )
                          }
                        >
                          {t("calls.send_decline", { fallback: "Send" })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {/* Phase 39 — the in-app WebRTC call window (floating, draggable). Lives
            in the main window so the peer connections + SSE signalling share one
            context; portals to <body> so position here is only for locality. */}
        {callWin && callView && (() => {
          const cr = calls.find((c: any) => Number(c.id) === Number(callWin.callId)) || null;
          return (
            /* 208 §1 — on use; a load that fails says so, beside « Hang up » */
            <LoadedOnUse load={loadCallWindow} onClose={hangupCall} closeLabel={(t as TFn)("calls.window.hangup", { fallback: "Hang up" })}>
            {(m) => (
            <m.default
              view={callView}
              callRow={cr}
              co={collaborators}
              currentUser={currentUser}
              mediaError={callMediaErr}
              onToggleMic={() => rtcSessionRef.current && rtcSessionRef.current.toggleMic()}
              onToggleCam={() => rtcSessionRef.current && rtcSessionRef.current.toggleCam()}
              onHangup={hangupCall}
              onAdd={(id: any) => addToCall([id])}
              onSwitchDevices={switchCallDevices}
              presenceIds={presenceIds}
              onNotifyMissed={notifyMissed}
              /* 72 — the share leg. Rendered only while it belongs to THIS
                 conversation: a leg is torn down with its parent, but the window
                 must not show a leg from a call it is not displaying even for a
                 frame. */
              shareLeg={shareLeg && Number(shareLeg.parentCallId) === Number(callWin.callId) ? shareLeg : null}
              shareView={shareView}
              sharePicked={sharePickedRef.current}
              shareBusy={shareBusy}
              onStartShare={() => startShare(Number(callWin.callId))}
              onStopShare={() => stopShare()}
              shareLost={shareLost}
              onRetryShare={retryReceivedLeg}
              /* 74f — exactly one call surface at a time: while the pop-out holds
                 the call, this instance renders a marker instead of a window (it
                 still renders the share surface, which has no control row). Taking
                 it back just closes the pop-out — the suppression is derived from
                 `callPopCtr`, so the in-page window returns by itself, and the
                 focus rule from the §3 amendment is untouched. */
              onMeasure={(sz) => { callSurfaceSizeRef.current = sz; }}
              onTrace={(what, caller) => _popTrace(what, `tab surface · ${caller}`)}
              handedOff={!!callPopCtr}
              onTakeBack={closeCallPopOut}
              poppedOut={!!callPopCtr}
              onPopOutCall={() => { if (callPopCtr) closeCallPopOut(); else openCallPopOut(); }}
            />
            )}
            </LoadedOnUse>
          );
        })()}
        {dailyOverview &&
          currentUser &&
          (() => {
            /* Resolved ONCE and handed to the shared list, so the two mounts
               cannot disagree about what "today" is across a midnight. 125 §6's
               `_tsDay` normalisation — the reason this is compared as a
               10-character day rather than as `n.ts` — moved to
               `NotifGroupedList` with the partition that needs it, and its
               argument travelled with it rather than being left here to rot
               beside code that no longer does the comparing. */
            /* ── BRIEF 126 §4 — ONE ORDERING, AND IT IS THE BELL'S ───────────
               Laurent: "not clear why Bell and Daily overview show in different
               order — should be identical, follow Bell."

               BOTH ORDERINGS, MEASURED at 61f38a39, because §4 asks for them:

               THE BELL is one flat list. Every builder pushes into `n`;
               `n.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""))`
               (App.tsx, the aggregator) orders it DESCENDING BY `ts` AS A
               STRING; the `_item` collapse keeps the first — i.e. the most
               recent — survivor of each item; `slice(0, 20)` caps it.

               THIS MODAL read that same array THREE TIMES, with three
               independent `filter()` calls. `filter` preserves source order, so
               WITHIN each group the order was ALREADY the bell's, and this
               commit does not change a single row's position relative to its
               neighbours. The difference Laurent saw is not a sort. It is that
               THE THREE GROUPS WERE NOT DISJOINT:

                 reminders = type === "reminder" || n.dismissId

               and a row that is `type: "overdue"` AND carries a `dismissId`
               satisfies both — every `st-`, `tkr-`, `agr-` and `cdd-*` row on
               its overdue branch, and any persisted `db-` row whose type is
               overdue. MEASURED IN THE BROWSER on the base build: "Subtask
               overdue: Smoke Case 01 — Task 10 — test" rendered under OVERDUE
               and AGAIN under REMINDERS, 72 lines apart. The same fact for
               `type: "event"` due today, which `dueToday` and `reminders` both
               claim.

               So ONE notification occupied TWO positions here and exactly one
               in the bell, which is a difference of ORDER in the only sense a
               reader experiences: where the thing is on the page.

               THE CURE IS THE PARTITION, in ONE pass over the bell's own
               array. First heading that matches wins, in the order the headings
               are printed. That makes within-group order the bell's BY
               CONSTRUCTION rather than by a property of `filter` nobody wrote
               down, and it makes `allDO` — which drives the empty state and
               "Dismiss all" — a list of rows rather than a list with repeats.

               THE GROUPING STAYS, per §4's fence: OVERDUE / DUE TODAY /
               REMINDERS are this surface's own and Laurent has not ruled them
               away.

               ── §4b, THE ADDENDUM: §4 WAS THE WEAKER CURE AND IS SUPERSEDED ──

               Laurent refused it: "why unify the notification ordering between
               Daily Overview and bell, while it could just take the very bell
               notification list itself, by deduping?" He is right, and the
               paragraph above is the evidence: unifying two orderings leaves
               TWO PRODUCERS AGREEING. Taking the list leaves ONE.

               §4's partition closed the "twice" direction. It left the other
               one open, and said so as if a fence protected it: A ROW MATCHING
               NO HEADING VANISHED. That is 125 §6's own DUE TODAY finding —
               where an event's `ts` shape made it match nothing and today's
               meetings were absent from the morning modal — and 125 fixed the
               INSTANCE while leaving the SHAPE that produced it. The next
               builder whose row matches no predicate disappears the same way,
               silently, and nothing says so.

               RULED: `notifications` IS the list. `allDO` is that array, not a
               concatenation of anything; the headings are a TOTAL PARTITION of
               it, computed by ONE function that returns for every row; and a
               DEFAULT group catches whatever no heading claims. `groupOf`'s
               return type is a four-member union with no `undefined` in it, so
               "every row lands somewhere" is checked by the compiler rather
               than remembered by a reader.

               WHAT THE DEFAULT CATCHES TODAY, measured on the live fixture: an
               `ev-` event that is neither today's nor overdue and carries no
               `dismissId` — "Tomorrow: REU: test case leaf — 2026/001". A chat
               mention and a running timer would land there too; this fixture
               has neither. IT IS NOT EMPTY, and it was the exact set of rows
               the bell showed and this modal did not.

               WHAT THE PARTITION CANNOT AND MUST NOT MERGE, said here because
               it looks like the same defect and is not: two DISTINCT rows about
               one task. The `ovd-` overdue marker is EXEMPT from the `_item`
               collapse by Laurent's ruling of 2026-08-07 (its argument sits at
               its push site), so a derived overdue marker and a persisted
               overdue alert about the same task both stand — in the bell as
               well as here. Merging them would reverse that ruling. */
            /* ── 128 §4 — THE PARTITION LEFT THIS RENDER BODY ────────────────
               `groupOf`, its four buckets and the `SEC`s that printed them are
               `components/NotifGroupedList.tsx` now, and the BELL MOUNTS THE
               SAME COMPONENT. Everything 126 §4b ruled is intact and is checked
               in exactly the same way — `groupOf`'s return type is still a
               four-member union with no `undefined` in it, so "every row lands
               somewhere" is still the compiler's job rather than a reader's.
               It moved for the reason §4 gives: the two surfaces had two
               renderers for one list, and that is what "based on Today now,
               which is better than bell" asks to end. */
            const allDO = notifications;
            /* BRIEF 125 §6 — everything a bell row needs, handed to all three
               groups verbatim. `onActed` is the ONE thing the two surfaces
               answer differently: a row that navigates or joins closes the
               menu in the bell, and closes THIS MODAL here. */
            const _secRow = {
              isNotifReadFn: isNotifRead,
              notifRead,
              tasks,
              agendaEvents,
              cases,
              collectiveCases,
              evtEntries,
              markNotifRead,
              dismissNotif,
              joinAndTime,
              onActed: () => setDailyOverview(false),
              t,
            };
            /* ── 81 §7 / register 81f — THE CLAUSE ────────────────────────────
               [RULED — Laurent, 2026-08-14] "The modal opens on an otherwise-
               empty morning ONLY when failedSince is not zero. THE
               PREVIOUS-LOGIN LINE ALONE DOES NOT FORCE IT OPEN — that would put
               it on screen every morning and turn it into wallpaper, which is
               your own argument about the zero count. A non-zero failure count
               DOES force it, because an empty task list is not a choice the
               reader made and the morning that line matters is likelier than
               not to be a quiet one."

               THREE STATES, NOT TWO, and the third is why `signInChecked`
               exists: while the answer is still in flight this returns null
               WITHOUT closing, so the day is not spent a moment before the
               count arrives. `lx:dailylast` is already stamped by then (:4552),
               so a modal closed here does not come back today.

               `setDailyOverview(false)` during render is pre-existing and is
               left alone — this calls it in strictly fewer cases than before. */
            const alarm = (signInSummary?.failedSince ?? 0) > 0;
            if (allDO.length === 0) {
              if (!signInChecked) return null;
              if (!alarm) {
                setDailyOverview(false);
                return null;
              }
            }
            return (
              <ModalLayout
                title={t("app.notif.daily_overview", {
                  fallback: "Daily Overview",
                })}
                onClose={() => setDailyOverview(false)}
                maxWidth={480}
                /* 205 §2.2 — THE FIFTH POP-OUT. Laurent's ruling names the
                   "login popup" among the five; 204 P3 §5 established that it
                   is THIS — the Daily Overview — and that its inset is the
                   modal shell's own 12/16, measured at 16 – 17 px to every
                   text. It takes the ruled quarter over that base, through a
                   class on `.modal-body` alone (App.css `.modal-body.popup-pad`,
                   all three width arms), because raising `.modal-body` itself
                   would move every modal in the app and no ruling asks that. */
                bodyClass="popup-pad"
                /* ── 128 §1 — THE OVERVIEW SCROLLS, THROUGH THE SCROLLER IT
                     WAS OPTING OUT OF ──────────────────────────────────────
                   Laurent: "All great — late, today, reminder sections, with
                   counters — except it should scroll (now partly hidden)."

                   [MEASURED at 9f5c3da3, in a browser, on the live fixture]
                   this mount rendered scrollHeight 1909 into clientHeight 750
                   — 1159px of content below the fold, no scrollbar, no wheel.
                   `.modal` is `display:flex; flex-direction:column;
                   max-height:calc((100dvh/var(--ui-zoom,1))*.9);
                   overflow:hidden` (App.css:945-981), and `rawBody` skips
                   `.modal-body` — which is the element carrying
                   `overflow-y:auto; flex:1 1 auto; min-height:0`
                   (App.css:1045-1050). Its replacement was a bare
                   `<div style={{padding:"16px 20px"}}>` at
                   `overflow-y:visible`, so `.modal`'s own
                   `overflow:hidden` did the clipping.

                   NO SCROLL CONTAINER IS INVENTED — §1 asks how the tree's
                   other tall modals do this, and the answer is that they do
                   not do anything: `ModalLayout` IS the shared pattern, and
                   it already reads header (`flex-shrink:0`) · `.modal-body`
                   (the scroller) · footer (`flex-shrink:0`), which is §1's
                   "the body scrolls; the header and the footer stay put",
                   verbatim. 26 files consume it and the tallest dialog in the
                   tree (Edit Case) is one of them. So the cure is to STOP
                   OPTING OUT: `rawBody` goes, `footer={null}` becomes the
                   real footer, and the action row moves into it from the
                   bottom of the body, where it was scrolling away under 1900px
                   of rows.

                   TWO THINGS ARRIVE WITH IT AND BOTH ARE THE POINT. The
                   controls stop being the last thing under the content, and
                   `useScrollHint` (SharedUI.tsx:771) — which measures
                   `.modal-body` and mounts the ▴/▾ discs — starts applying
                   here for the first time. That affordance already existed and
                   was simply unreachable from a `rawBody` shell, which is the
                   second half of "now partly hidden".

                   The body's padding becomes the house 12px/16px instead of
                   this mount's own 16px/20px. That is a consequence of joining
                   the shared shell, not a separate decision, and it is the
                   direction that makes this dialog agree with the other 26. */
                footer={
                  <div
                    className="modal-footer"
                    style={{
                      /* `.modal-footer` is `flex-direction:column;
                         align-items:stretch` (App.css:1051-1059) because most
                         footers are a stacked button column. This one is the
                         row it has always been — a checkbox pushed left by
                         `marginRight:auto`, two buttons on the right — so it
                         states the axis it needs rather than minting a class
                         nothing else would use. Every other declaration
                         (padding, the top rule, `flex-shrink:0`) is the
                         shell's. */
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 8,
                    }}
                  >
                    {/* ── MOVED, NOT IMPROVED (SESSION_RULES rule 3) ────────
                        The three controls below are byte-identical to what
                        stood at the foot of the body, re-indented. The gap the
                        "Remove all" comment admits — a persisted row's "db-N"
                        landing in `dismissedReminders` inertly — is left
                        exactly as it was; §2 is the commit that closes it. */}
                    <label
                      style={{
                        fontSize: FS.s9_5,
                        color: "var(--tm)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        marginRight: "auto",
                      }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            sv(
                              "lx:dailylast",
                              new Date().toISOString().slice(0, 10),
                            );
                          }
                        }}
                      />
                      {t("app.notif.hide_today", {
                        fallback: "Don't show again today",
                      })}
                    </label>
                    {allDO.filter((n) => n.dismissId).length > 0 && (
                      <button
                        className="btn btn-ghost"
                        style={{ padding: "4px 10px", fontSize: FS.s10 }}
                        onClick={() => {
                          /* Brief 45 §2 — through the item, not the row.
                             `allDO` is built from the collapsed list, so a
                             survivor here stands for its superseded siblings
                             too; dismissing only what is visible would leave
                             those siblings un-dismissed and let one walk back
                             into the bell the moment the survivor went. This
                             expands the same way read and dismiss do.

                             ── 128 §2 — IT GOES THROUGH `dismissNotif` NOW ──
                             The sentence that stood here admitted the gap this
                             § closes: "it still writes ONLY into
                             `dismissedReminders`… a persisted row's db-N lands
                             there inertly." That was true and it made the bulk
                             control quietly weaker than the per-row one, which
                             is the worst place for an asymmetry — a reader who
                             clicks "Remove all" and finds the alerts still
                             there learns not to trust either button.

                             So the bulk act IS the row's act: `dismissNotifs`
                             is the one implementation and `dismissNotif` is
                             `dismissNotifs([n])`. The `flatMap` the old code
                             needed is gone rather than moved — the expansion
                             happens inside, once, for the whole batch. The two
                             controls can no longer come to disagree about what
                             removing a row means, because there is nothing
                             left for them to disagree with. */
                          dismissNotifs(allDO);
                          setDailyOverview(false);
                        }}
                      >
                        {/* 126 §6 — the bulk form of the row's own act, so
                            it takes the row's own word. Leaving "Dismiss
                            all" here while the rows said "Remove" would be
                            two words for one act, on one screen. */}
                        {t("app.notif.remove_all", {
                          fallback: "Remove all",
                        })}
                      </button>
                    )}
                    <button
                      className="btn btn-primary"
                      style={{ padding: "4px 10px", fontSize: FS.s10 }}
                      onClick={() => setDailyOverview(false)}
                    >
                      {t("common.close", { fallback: "Close" })}
                    </button>
                  </div>
                }
              >
                    {/* 126 §3 — THE SAME QUOTE THE BELL SHOWS, and the SAME
                        COMPONENT: this modal had none at all, and writing a
                        second quote block here is precisely what §3 forbids.
                        It leads, because this is the panel that arrives on a
                        morning unbidden. */}
                    <DayQuote quote={todayQuote} variant="modal" />
                    {/* ── 128 §4 — ONE PRESENTATION, AND THIS IS THE MOUNT THE
                        BELL COPIED. The four headings, their colours and their
                        counters are `NotifGroupedList`'s now, over the SAME
                        `notifications` array the bell hands it. 125 §6 made the
                        ROW one component across the two surfaces; this makes
                        the LIST one. `_secRow` rides in as `row`, unchanged —
                        its `onActed` is still the ONE thing the two mounts
                        answer differently. */}
                    <NotifGroupedList
                      notifications={allDO}
                      row={_secRow}
                    />
                    {/* Phase 31 (C4) — Outlook-import prompt: jumps to the
                        existing choice-list import (manual confirm). */}
                    {outlookNewCount > 0 && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "7px 9px",
                          borderRadius: RAD.r6,
                          border: "1px solid var(--br)",
                          background: "var(--bd)",
                          marginBottom: 10,
                        }}
                      >
                        <CalendarClock
                          size={13}
                          style={{ color: "var(--ac)", flexShrink: 0 }}
                        />
                        <span
                          style={{
                            flex: 1,
                            fontSize: FS.s10_5,
                            color: "var(--tp)",
                          }}
                        >
                          {t("notif.outlook_import", {
                            count: outlookNewCount,
                            fallback: `${outlookNewCount} Outlook event${outlookNewCount === 1 ? "" : "s"} to import`,
                          })}
                        </span>
                        <button
                          className="btn btn-primary"
                          style={{ padding: "3px 10px", fontSize: FS.s10 }}
                          onClick={() => {
                            setDailyOverview(false);
                            setAgAutoImport(true);
                            nav("ag");
                          }}
                        >
                          {t("outlookImport.review_button", {
                            fallback: "Review & import",
                          })}
                        </button>
                      </div>
                    )}
                    {allDO.length === 0 && (
                      <p
                        style={{
                          fontSize: FS.s11,
                          color: "var(--tm)",
                          textAlign: "center",
                        }}
                      >
                        {t("app.notif.nothing_today", {
                          fallback: "Nothing for today!",
                        })}
                      </p>
                    )}
                    {/* ── 126 §5 — THE CONNECTION LINE, AT THE FOOT ───────────
                        [RULED] it led both panels and goes to the foot of both.
                        SAME STRING, SAME PRODUCER — the same `SignInLine` the
                        bell mounts, which is 81 §7's arrangement and the reason
                        neither surface can drift into showing the CURRENT
                        sign-in.

                        BELOW THE CONTENT, ABOVE THE ACTIONS. The row under this
                        is "Don't show again today" / "Dismiss all" / "Close" —
                        controls, not content — so the foot of what this modal
                        SAYS is here.

                        128 §1 — THAT ARRANGEMENT IS UNCHANGED AND IS NOW
                        STRUCTURAL. The three controls moved into the shell's
                        own `footer`, so "below the content, above the actions"
                        stopped being an ordering this block has to maintain and
                        became the difference between `.modal-body` and
                        `.modal-footer`. This line is still the last thing the
                        modal SAYS, and it now scrolls with the content it is
                        the foot OF, rather than pinning the controls beneath
                        1900px of rows nobody could reach.

                        81 §7's own caveat still stands and is not widened: the
                        modal returns null when there is nothing overdue, due or
                        reminded, so on a clean morning the line arrives through
                        the bell alone. */}
                    <SignInLine summary={signInSummary} variant="modal" />
              </ModalLayout>
            );
          })()}
        {doodleDlg &&
          (() => {
            const _localizeGroup = (g: any) => {
              const _key =
                "modal.attendees.group." +
                g.toLowerCase().replace(/[^a-z0-9]+/g, "_");
              const _v = t(_key);
              return _v === _key ? g : _v;
            };
            return (
              <ModalLayout
                title={t("modal.poll.title")}
                onClose={() => setDoodleDlg(null)}
                rawBody
                footer={null}
                maxWidth={480}
              >
                  <div style={{ padding: "16px 20px" }}>
                    <div className="field">
                      <label>{t("modal.field.title")}</label>
                      <input
                        className="field-input"
                        value={doodleDlg.title}
                        onChange={(e) =>
                          setDoodleDlg((d: any) => ({ ...d, title: e.target.value }))
                        }
                      />
                    </div>
                    <AttendeesPickerSection doodleDlg={doodleDlg} setDoodleDlg={setDoodleDlg} t={t} _localizeGroup={_localizeGroup} />
                    <div
                      style={{
                        fontSize: FS.s10,
                        marginTop: 8,
                        padding: "6px 8px",
                        background: "var(--bi)",
                        borderRadius: RAD.r6,
                        color: "var(--ts)",
                        wordBreak: "break-all",
                      }}
                    >
                      {doodleDlg.attendees
                        .filter((a: any) => a.checked)
                        .map((a: any) => a.email)
                        .join(", ") || t("modal.no_attendees_selected")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        alignItems: "stretch",
                        marginTop: 14,
                      }}
                    >
                      <button
                        type="button"
                        className="modal-cancel"
                        onClick={() => setDoodleDlg(null)}
                      >
                        {t("common.cancel")}
                      </button>
                      <button
                        type="button"
                        className="modal-save"
                        onClick={() => {
                          const emails = doodleDlg.attendees
                            .filter((a: any) => a.checked)
                            .map((a: any) => a.email)
                            .join(", ");
                          navigator.clipboard
                            .writeText(emails)
                            .then(() =>
                              toast(
                                t("toast.emails_copied", {
                                  fallback:
                                    "Emails copied — paste them in the poll tool",
                                }),
                              ),
                            )
                            .catch(() => {});
                          const subj = encodeURIComponent(doodleDlg.title);
                          const desc = encodeURIComponent(
                            doodleDlg.caseObj?.ref || "",
                          );
                          let url;
                          if (pollTool === "findtime")
                            url = "https://findtime.microsoft.com";
                          else if (pollTool === "calendly")
                            url = "https://calendly.com";
                          else if (pollTool === "custom")
                            url = pollCustomUrl || "https://doodle.com/create";
                          else
                            url = `https://doodle.com/create?title=${subj}&description=${desc}`;
                          window.open(url, "_blank");
                          setDoodleDlg(null);
                        }}
                      >
                        <Copy size={I.s} style={{ marginRight: 4 }} />
                        {t("modal.poll.copy_emails")}
                      </button>
                    </div>
                  </div>
              </ModalLayout>
            );
          })()}
        {mailDlg && (
          <ModalLayout
            title={t("modal.email.title", {
              label: mailDlg.label,
              fallback: "Email {label}",
            })}
            onClose={() => setMailDlg(null)}
            rawBody
            footer={null}
            maxWidth={500}
          >
              <div style={{ padding: "16px 20px" }}>
                {mailDlg.persons.length === 0 ? (
                  <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
                    {t("modal.email.no_addresses", {
                      fallback: "No email addresses available in this group.",
                    })}
                  </p>
                ) : (
                  <>
                    <div style={{ marginBottom: 10 }}>
                      {mailDlg.internal ? (
                        <ColleagueCheckGrid
                          items={mailDlg.persons}
                          onToggle={(i: any) =>
                            setMailDlg((d: any) => ({
                              ...d,
                              persons: d.persons.map((x: any, j: any) =>
                                j === i ? { ...x, checked: !x.checked } : x,
                              ),
                            }))
                          }
                          onToggleAll={(next: any) =>
                            setMailDlg((d: any) => ({
                              ...d,
                              persons: d.persons.map((x: any) => ({
                                ...x,
                                checked: next,
                              })),
                            }))
                          }
                        />
                      ) : (
                        mailDlg.persons.map((p: any, i: any) => (
                        <label
                          key={p.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 0",
                            borderBottom: "1px solid rgba(255,255,255,.04)",
                            fontSize: FS.s11,
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={p.checked}
                            onChange={() =>
                              setMailDlg((d: any) => ({
                                ...d,
                                persons: d.persons.map((x: any, j: any) =>
                                  j === i ? { ...x, checked: !x.checked } : x,
                                ),
                              }))
                            }
                          />
                          <span style={{ fontWeight: 500, flex: 1 }}>
                            {p.name}
                          </span>
                          <span style={{ color: "var(--tm)", fontSize: FS.s10 }}>
                            {p.email}
                          </span>
                        </label>
                      ))
                      )}
                    </div>
                    <div style={{ fontSize: FS.s10, marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: "var(--ts)" }}>
                        {t("modal.email.to", { fallback: "To" })}:{" "}
                      </span>
                      <span style={{ color: "var(--tp)" }}>
                        {mailDlg.persons
                          .filter((p: any) => p.checked)
                          .map((p: any) => p.email)
                          .join(", ") || "—"}
                      </span>
                    </div>
                    {/* Corr-2 (item 8) — the CC field is removed (stale). */}
                    <div style={{ fontSize: FS.s10, marginBottom: 12 }}>
                      <span style={{ fontWeight: 600, color: "var(--ts)" }}>
                        {t("modal.email.subject", { fallback: "Subject" })}:{" "}
                      </span>
                      <span style={{ color: "var(--tp)" }}>
                        {mailDlg.caseObj
                          ? `${mailDlg.caseObj.title} — ${mailDlg.caseObj.ref}`
                          : t("calls.internal_email_subject", {
                              fallback: "Internal message",
                            })}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        alignItems: "stretch",
                        marginTop: 14,
                      }}
                    >
                      <button className="modal-cancel" onClick={() => setMailDlg(null)}>
                        {t("common.cancel", { fallback: "Cancel" })}
                      </button>
                      <button
                        className="modal-save"
                        onClick={() => {
                          const toAddrs = mailDlg.persons
                            .filter((p: any) => p.checked)
                            .map((p: any) => p.email);
                          if (!toAddrs.length) return;
                          const subj = mailDlg.caseObj
                            ? mailDlg.caseObj.title + " — " + mailDlg.caseObj.ref
                            : t("calls.internal_email_subject", {
                                fallback: "Internal message",
                              });
                          const toStr = toAddrs.join(",");
                          /* Corr-2 (item 8) — CC removed (stale). */
                          const url = buildComposeUrl(
                            { to: toStr, cc: "", subject: subj, body: "" },
                            emailClient,
                          );
                          window.open(url, "_blank");
                          setMailDlg(null);
                          toast(
                            t("toast.email_opened", {
                              fallback: "Email opened",
                            }),
                          );
                        }}
                      >
                        <Mail
                          size={16}
                          color={CT.c_fff}
                          style={{ marginRight: 4 }}
                        />
                        {/* Corr-3 (item 9) — opens a draft/compose window, it
                            does not send → "Compose". */}
                        {t("calls.compose", { fallback: "Compose email" })}
                      </button>
                    </div>
                  </>
                )}
              </div>
          </ModalLayout>
        )}
        {renderModals()}
        {mailTplClient && (
          <ClientMailTemplateModal client={mailTplClient} onClose={() => { setMailTplClient(null); }} toast={toast} />
        )}
        <QuickAddModal
          _apiEnt={_apiEnt}
          additionalOpponents={additionalOpponents}
          arbiters={arbiters}
          barEntries={barEntries}
          barsList={barsList}
          cases={cases}
          clients={clients}
          collectiveCases={collectiveCases}
          contactPersons={contactPersons}
          contactRoleEntries={contactRoleEntries}
          contactRolesList={contactRolesList}
          countriesList={countriesList}
          countryEntries={countryEntries}
          cqm={cqm}
          experts={experts}
          mediators={mediators}
          opponentContacts={opponentContacts}
          opponentLawyers={opponentLawyers}
          opponents={opponents}
          persist={persist}
          quickAddForm={quickAddForm}
          quickAddModal={quickAddModal}
          saveQuickAddParty={saveQuickAddParty}
          setAdditionalOpponents={setAdditionalOpponents}
          setArbiters={setArbiters}
          setCases={setCases}
          setClients={setClients}
          setCoiPrompt={setCoiPrompt}
          setCollectiveCases={setCollectiveCases}
          setContactPersons={setContactPersons}
          setExperts={setExperts}
          setForm={setForm}
          setMediators={setMediators}
          setOpponentContacts={setOpponentContacts}
          setOpponentLawyers={setOpponentLawyers}
          setOpponents={setOpponents}
          t={t}
          titleEntries={titleEntries}
          titlesList={titlesList}
          toast={toast}
          uqf={uqf}
        />
        {/* Phase 29 (item 2) — the LIVE pop-out: the same column component,
            portaled into the pop-out document. React ≥17 attaches its event
            listeners to portal containers, so clicks inside the other
            document dispatch normally; context (i18n) flows from this tree. */}
        {popOutCtr && createPortal(renderColumn(true), popOutCtr)}
        {/* 186 A.3a [RULED 186 (2)] — the SAME sidebar chats, portaled into their
            pop-out documents (`popped` fills the window); the case chat renders for
            the matter the sidebar row would show (`_chatPopDid`). */}
        {gChatPopCtr && createPortal(
          <SidebarGenChat
            {..._genChatProps}
            popped
            head={chatPopHead(gChatTitle(false))}
          />,
          gChatPopCtr,
        )}
        {cChatPopCtr && _chatPopDid != null && createPortal(
          <SidebarCaseChat
            {..._caseChatProps}
            popped
            did={_chatPopDid}
            head={_chatPopCaseHead && chatPopHead(chatTitleRow("c", _chatPopCaseHead.label, _chatPopCaseHead.unread, false))}
          />,
          cChatPopCtr,
        )}
        {/* 74 §3 — the SAME CallWindow, portaled into the call pop-out's document
            in `inPopOut` mode: no portal of its own, no drag, no drawers, no share
            surface. React ≥17 attaches listeners to portal containers, so the
            controls in that other document work; i18n context flows from here. */}
        {callPopCtr && callWin && callView && createPortal(
          (() => {
            const cr = calls.find((c: { id?: number }) => Number(c.id) === Number(callWin.callId)) || null;
            return (
              <LoadedOnUse load={loadCallWindow} inline>
              {(m) => (
              <m.default
                inPopOut
                onTrace={(what, caller) => _popTrace(what, `popout surface · ${caller}`)}
                view={callView}
                callRow={cr}
                co={collaborators}
                currentUser={currentUser}
                mediaError={callMediaErr}
                onToggleMic={() => rtcSessionRef.current && rtcSessionRef.current.toggleMic()}
                onToggleCam={() => rtcSessionRef.current && rtcSessionRef.current.toggleCam()}
                onHangup={hangupCall}
                shareLeg={shareLeg && Number(shareLeg.parentCallId) === Number(callWin.callId) ? shareLeg : null}
                shareView={shareView}
              sharePicked={sharePickedRef.current}
                shareBusy={shareBusy}
                onStartShare={() => startShare(Number(callWin.callId))}
                onStopShare={() => stopShare()}
                shareLost={shareLost}
              />
              )}
              </LoadedOnUse>
            );
          })(),
          callPopCtr,
        )}
        {searchDlg && (
          <SearchPopover
            openSettingsCard={(pane: string) => {
              setSection("settings");
              setSettingsFocus({ pane, seq: Date.now() });
            }}
            sDlgQ={sDlgQ}
            setSDlgQ={setSDlgQ}
            setSearchDlg={setSearchDlg}
            searchDlgInputRef={searchDlgInputRef}
            sDlgFilters={sDlgFilters}
            setSDlgFilters={setSDlgFilters}
            sDlgMulti={sDlgMulti}
            setSDlgMulti={setSDlgMulti}
            sDlgMore={sDlgMore}
            setSDlgMore={setSDlgMore}
            currentUser={currentUser}
            _appIsAdmin={_appIsAdmin}
            _appPerms={_appPerms}
            cases={cases}
            collectiveCases={collectiveCases}
            clients={clients}
            opponents={opponents}
            opponentLawyers={opponentLawyers}
            experts={experts}
            collaborators={collaborators}
            jurisdictions={jurisdictions}
            agendaEvents={agendaEvents}
            tasks={tasks}
            timeEntries={timeEntries}
            chatMsgs={chatMsgs}
            evtTypes={evtTypes}
            nav={nav}
            setDetailId={setDetailId}
            tryNavCase={tryNavCase}
            t={t}
          />
        )}
        <ToastHost activeToast={activeToast} setActiveToast={setActiveToast} />
      </div>
    </ConfirmProvider>
    </PaneOpenContext.Provider>
    </ListLoadContext.Provider>
  );
}
